/* @ts-self-types="./lakko_ffi_wasm.d.ts" */

/**
 * Result of [`acceptCallInvite`]. Carries the fresh MLS
 * group, the Welcome bytes for the owner, the call_id,
 * and the call-key seed. The group is exposed as a
 * `Group` so the platform can continue MLS ops via the
 * existing wasm-bindgen surface; the seed is flat so it
 * feeds straight into `sframeDeriveParticipantKey`.
 */
export class AcceptedCallInvite {
    static __wrap(ptr) {
        const obj = Object.create(AcceptedCallInvite.prototype);
        obj.__wbg_ptr = ptr;
        AcceptedCallInviteFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AcceptedCallInviteFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_acceptedcallinvite_free(ptr, 0);
    }
    /**
     * 16-byte `call_id` from the capability.
     * @returns {Uint8Array}
     */
    get callId() {
        const ret = wasm.acceptedcallinvite_callId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * 32-byte `CallKey` seed. Feed into
     * `sframeDeriveParticipantKey` to get the per-leaf
     * SFrame key.
     * @returns {Uint8Array}
     */
    get callKeySeed() {
        const ret = wasm.acceptedcallinvite_callKeySeed(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Take ownership of the freshly-created MLS group
     * (joiner is founder at leaf 0). Wasm-bindgen one-
     * shot move — calling this twice panics; the
     * platform must capture the result on first call.
     * @returns {Group}
     */
    group() {
        const ret = wasm.acceptedcallinvite_group(this.__wbg_ptr);
        return Group.__wrap(ret);
    }
    /**
     * Joiner's leaf index — always 0 for the founder.
     * @returns {number}
     */
    get ownLeafIndex() {
        const ret = wasm.acceptedcallinvite_ownLeafIndex(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * MLS wire-format Welcome bytes for the owner to
     * run `Group.joinFromWelcome` against. Platform
     * delivers these out-of-band.
     * @returns {Uint8Array}
     */
    get welcomeForOwner() {
        const ret = wasm.acceptedcallinvite_welcomeForOwner(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) AcceptedCallInvite.prototype[Symbol.dispose] = AcceptedCallInvite.prototype.free;

/**
 * The fold's answer to *am I the acting committer right now?* wasm-bindgen
 * mirror of UniFFI's `ActingCommitter` /
 * [`lakko_contacts::committer_beacon::ActingCommitter`].
 *
 * # Why a flat tagged class rather than a sum type
 *
 * wasm-bindgen has no idiomatic story for a Rust enum carrying data, so this
 * follows [`crate::mls::ProposalRecord`]: a string [`Self::kind`]
 * discriminator plus a per-variant getter. The payload getter is named for its
 * own variant — [`Self::defer_to_rank`], `undefined` outside `"defer"` — and
 * deliberately **not** a shared slot in the `ParsedBody` style, because only
 * one variant has a payload and a shared name would invite reading it on the
 * others.
 *
 * A string tag also sidesteps the numeric-enum hazard this crate pins
 * elsewhere (`outgoing_owner_variants_are_never_zero`): there is no
 * discriminant for a missing value to coerce to.
 */
export class ActingCommitter {
    static __wrap(ptr) {
        const obj = Object.create(ActingCommitter.prototype);
        obj.__wbg_ptr = ptr;
        ActingCommitterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ActingCommitterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_actingcommitter_free(ptr, 0);
    }
    /**
     * The rank of the best-placed fresh observation, present **iff** `kind
     * === "defer"` and `undefined` for every other kind. Equal to this
     * device's own rank when it defers to a lower-keyed device of the same
     * account.
     *
     * Purely informational — the decision is already made — but it is what lets
     * a log line answer *deferring to whom*.
     * @returns {number | undefined}
     */
    get deferToRank() {
        const ret = wasm.actingcommitter_deferToRank(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * That observation's 32-byte signature key, present **iff** `kind ===
     * "defer"`. Never this device's own key.
     * @returns {Uint8Array | undefined}
     */
    get deferToSignatureKey() {
        const ret = wasm.actingcommitter_deferToSignatureKey(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * `true` only for `kind === "act"`. Convenience for the common call site;
     * the kind is still the thing to log.
     * @returns {boolean}
     */
    get isAct() {
        const ret = wasm.actingcommitter_isAct(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Discriminator: `"act" | "defer" | "not-a-candidate"`. Useful for
     * `switch` / `case` in TypeScript.
     *
     * * `"act"` — this device is the acting committer: it is a candidate and
     *   has heard no fresh beacon from any lower-ranked candidate, **including
     *   when it has heard nothing at all**, which means nobody better placed is
     *   running. Still only a licence to *attempt* the sweep: ADR-0045's
     *   stand-down journal re-read and the staged-commit rollback
     *   (`stagePendingProposals` / `mergeStagedSweep` / `abandonStagedSweep`)
     *   both remain required, because two candidates can still both reach it.
     * * `"defer"` — a better-placed candidate (a lower rank, or the same rank
     *   and a lower signature key — another device of the same account,
     *   ADR-0050 section 2.5a item 6) beaconed inside the freshness window;
     *   stand down and re-evaluate on the next poll. [`Self::defer_to_rank`]
     *   and [`Self::defer_to_signature_key`] name it.
     * * `"not-a-candidate"` — this device is not an owner or admin and can
     *   never sweep. Distinct from `"defer"`: there is nothing to re-evaluate,
     *   and no beacon this device should be emitting.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.actingcommitter_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) ActingCommitter.prototype[Symbol.dispose] = ActingCommitter.prototype.free;

/**
 * Result of [`Group::add_member`].
 *
 * Distribute `commit` to all existing members for processing; deliver
 * `welcome` to the newly-added member.
 */
export class AddMember {
    static __wrap(ptr) {
        const obj = Object.create(AddMember.prototype);
        obj.__wbg_ptr = ptr;
        AddMemberFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AddMemberFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_addmember_free(ptr, 0);
    }
    /**
     * Encoded MLS commit message — broadcast to existing members.
     * @returns {Uint8Array}
     */
    get commit() {
        const ret = wasm.addmember_commit(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Encoded MLS welcome message — sent to the joining member.
     * @returns {Uint8Array}
     */
    get welcome() {
        const ret = wasm.addmember_welcome(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) AddMember.prototype[Symbol.dispose] = AddMember.prototype.free;

/**
 * A decoded admission-seed envelope; the seed stays sealed.
 */
export class AdmissionSeedEnvelope {
    static __wrap(ptr) {
        const obj = Object.create(AdmissionSeedEnvelope.prototype);
        obj.__wbg_ptr = ptr;
        AdmissionSeedEnvelopeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AdmissionSeedEnvelopeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_admissionseedenvelope_free(ptr, 0);
    }
    /**
     * The 32-byte admission public key.
     * @returns {Uint8Array}
     */
    get admissionPublicKey() {
        const ret = wasm.admissionseedenvelope_admissionPublicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The admission-key version.
     * @returns {number}
     */
    get admissionVersion() {
        const ret = wasm.admissionseedenvelope_admissionVersion(this.__wbg_ptr);
        return ret;
    }
    /**
     * The 16-byte channel id.
     * @returns {Uint8Array}
     */
    get channelId() {
        const ret = wasm.admissionseedenvelope_channelId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The addressee's 32-byte account key.
     * @returns {Uint8Array}
     */
    get memberAccountKey() {
        const ret = wasm.admissionseedenvelope_memberAccountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) AdmissionSeedEnvelope.prototype[Symbol.dispose] = AdmissionSeedEnvelope.prototype.free;

/**
 * Which reactions a group's messages or a channel's posts take (ADR-0087
 * section 13a). Advisory: it decides what a client offers and shows.
 */
export class AllowedReactions {
    static __wrap(ptr) {
        const obj = Object.create(AllowedReactions.prototype);
        obj.__wbg_ptr = ptr;
        AllowedReactionsFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AllowedReactionsFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_allowedreactions_free(ptr, 0);
    }
    /**
     * Any reaction (the default).
     * @returns {AllowedReactions}
     */
    static all() {
        const ret = wasm.allowedreactions_all();
        return AllowedReactions.__wrap(ret);
    }
    /**
     * Whether `emoji` is allowed.
     * @param {string} emoji
     * @returns {boolean}
     */
    allows(emoji) {
        const ptr0 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.allowedreactions_allows(this.__wbg_ptr, ptr0, len0);
        return ret !== 0;
    }
    /**
     * The list of an `"only"` value; empty otherwise.
     * @returns {string[]}
     */
    get emoji() {
        const ret = wasm.allowedreactions_emoji(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * `"all"`, `"off"` or `"only"`.
     * @returns {string}
     */
    get mode() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.allowedreactions_mode(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * No reactions.
     * @returns {AllowedReactions}
     */
    static off() {
        const ret = wasm.allowedreactions_off();
        return AllowedReactions.__wrap(ret);
    }
    /**
     * Only these emoji, in the order a composer offers them.
     *
     * # Errors
     *
     * Throws `allowedReactions.<code>: ...` for an empty list, a repeat, an
     * entry that is not a reaction emoji, or more than
     * `allowedReactionsMax()`.
     * @param {string[]} emoji
     * @returns {AllowedReactions}
     */
    static only(emoji) {
        const ptr0 = passArrayJsValueToWasm0(emoji, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.allowedreactions_only(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AllowedReactions.__wrap(ret[0]);
    }
}
if (Symbol.dispose) AllowedReactions.prototype[Symbol.dispose] = AllowedReactions.prototype.free;

/**
 * One finalized token, ready to spend.
 */
export class AnonToken {
    static __wrap(ptr) {
        const obj = Object.create(AnonToken.prototype);
        obj.__wbg_ptr = ptr;
        AnonTokenFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AnonTokenFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_anontoken_free(ptr, 0);
    }
    /**
     * The context name it pays for (`discovery-lookup`, `ds-knock`, ...).
     * @returns {string}
     */
    get context() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.anontoken_context(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The issuance epoch (a `bigint`). Spend it while
     * `anonTokenIsLive(epoch, current)`.
     * @returns {bigint}
     */
    get epoch() {
        const ret = wasm.anontoken_epoch(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The `x-lakko-anon-token` header value (base64url, no padding). Store
     * it as is; it is the whole token.
     * @returns {string}
     */
    get headerValue() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.anontoken_headerValue(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) AnonToken.prototype[Symbol.dispose] = AnonToken.prototype.free;

/**
 * A batch of blinded token requests awaiting the issuer. Holds the blinding
 * factors: keep it in memory only, and finalize it once.
 */
export class AnonTokenRequest {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AnonTokenRequestFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_anontokenrequest_free(ptr, 0);
    }
    /**
     * The blinded elements, base64url without padding, in order.
     * @returns {string[]}
     */
    blindedElementsB64url() {
        const ret = wasm.anontokenrequest_blindedElementsB64url(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The context name this request is for.
     * @returns {string}
     */
    get context() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.anontokenrequest_context(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The number of tokens requested.
     * @returns {number}
     */
    get count() {
        const ret = wasm.anontokenrequest_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The epoch this request is for (a `bigint`).
     * @returns {bigint}
     */
    get epoch() {
        const ret = wasm.anontokenrequest_epoch(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Check the issuer's proof against `publicKeyB64url` (from the key
     * route) and unblind every evaluation into a token. All or nothing.
     * Throws `proof-verification: ...` when the issuer did not evaluate under
     * that key (discard the batch), `count-mismatch` / `bad-element` /
     * `bad-public-key` for a malformed response, `already-finalized` on a
     * second call.
     * @param {string[]} evaluations_b64url
     * @param {string} proof_b64url
     * @param {string} public_key_b64url
     * @returns {AnonToken[]}
     */
    finalize(evaluations_b64url, proof_b64url, public_key_b64url) {
        const ptr0 = passArrayJsValueToWasm0(evaluations_b64url, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(proof_b64url, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(public_key_b64url, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.anontokenrequest_finalize(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v4 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v4;
    }
    /**
     * The JSON body of `POST /auth/tokens`:
     * `{"context":..,"epoch":..,"blinded_elements":[..]}`.
     * @returns {string}
     */
    issueBodyJson() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.anontokenrequest_issueBodyJson(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Start a request for `count` tokens of `context` (a context name) at
     * `epoch`. Throws `unknown-context: ...` or `bad-batch-size: ...`.
     * @param {string} context
     * @param {bigint} epoch
     * @param {number} count
     */
    constructor(context, epoch, count) {
        const ptr0 = passStringToWasm0(context, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.anontokenrequest_new(ptr0, len0, epoch, count);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        AnonTokenRequestFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) AnonTokenRequest.prototype[Symbol.dispose] = AnonTokenRequest.prototype.free;

/**
 * One attachment reference inside an MLS Application message
 * envelope. Constructed via [`AttachmentRef::new`] on the send
 * path; obtained via [`ParsedEnvelope::attachments`] on the
 * receive path.
 *
 * ADR-0047 media metadata (`kind`, `durationMs`, `waveform`, `width`,
 * `height`, `thumb`) is optional and set after construction, so the
 * constructor's signature is unchanged:
 *
 * ```ts
 * const r = new AttachmentRef(blobId, key, nonce, "audio/mp4", size, "voice-1.m4a");
 * r.kind = "voice";
 * r.durationMs = 4200;
 * r.waveform = amplitudes; // Uint8Array, 1..=128 bytes
 * ```
 *
 * Out-of-bound values are not errors: `EnvelopeBuilder.build` drops them,
 * as every receiver would.
 */
export class AttachmentRef {
    static __wrap(ptr) {
        const obj = Object.create(AttachmentRef.prototype);
        obj.__wbg_ptr = ptr;
        AttachmentRefFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AttachmentRefFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_attachmentref_free(ptr, 0);
    }
    /**
     * Raw 32-byte SHA-256 blob id.
     * @returns {Uint8Array}
     */
    get blobId() {
        const ret = wasm.attachmentref_blobId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Remove the thumb.
     */
    clearThumb() {
        wasm.attachmentref_clearThumb(this.__wbg_ptr);
    }
    /**
     * ADR-0047 — playback length in ms. Survives only on a `voice`
     * (<= 15 min) or `video_note` (<= 60 s) ref.
     * @returns {number | undefined}
     */
    get durationMs() {
        const ret = wasm.attachmentref_durationMs(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * ADR-0047 — pixel height (1..=4096), `undefined` when absent.
     * @returns {number | undefined}
     */
    get height() {
        const ret = wasm.attachmentref_height(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * Raw 32-byte CEK.
     * @returns {Uint8Array}
     */
    get key() {
        const ret = wasm.attachmentref_key(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * ADR-0047 — `"voice"`, `"video_note"`, `"gif"`, another (unknown)
     * value that renders as a file, or `undefined` for a plain file.
     * Chooses the bubble only; inline playback is `inlineMediaType(mime)`.
     * @returns {string | undefined}
     */
    get kind() {
        const ret = wasm.attachmentref_kind(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Optional MIME type (e.g. `"image/jpeg"`). `undefined` when
     * not set.
     * @returns {string | undefined}
     */
    get mime() {
        const ret = wasm.attachmentref_mime(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Optional human-visible filename. `undefined` when not set.
     * @returns {string | undefined}
     */
    get name() {
        const ret = wasm.attachmentref_name(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Construct from raw byte fields + metadata. Field-length
     * validation happens at `composeEnvelope` time, not here —
     * keeping the constructor permissive lets callers build refs
     * incrementally (e.g. compute the SHA-256 separately from
     * the CEK derivation).
     * @param {Uint8Array} blob_id
     * @param {Uint8Array} key
     * @param {Uint8Array} nonce
     * @param {string | null | undefined} mime
     * @param {bigint} size
     * @param {string | null} [name]
     */
    constructor(blob_id, key, nonce, mime, size, name) {
        const ptr0 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(nonce, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(mime) ? 0 : passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        var ptr4 = isLikeNone(name) ? 0 : passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len4 = WASM_VECTOR_LEN;
        const ret = wasm.attachmentref_new(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, size, ptr4, len4);
        this.__wbg_ptr = ret;
        AttachmentRefFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Raw 12-byte AEAD nonce.
     * @returns {Uint8Array}
     */
    get nonce() {
        const ret = wasm.attachmentref_nonce(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Attach a copy of `thumb`. A method rather than a setter so the
     * caller's `ThumbRef` handle stays valid (a by-value setter would
     * consume it).
     * @param {ThumbRef} thumb
     */
    setThumb(thumb) {
        _assertClass(thumb, ThumbRef);
        wasm.attachmentref_setThumb(this.__wbg_ptr, thumb.__wbg_ptr);
    }
    /**
     * Set (or clear with `undefined`) `durationMs`.
     * @param {number | null} [duration_ms]
     */
    set durationMs(duration_ms) {
        wasm.attachmentref_set_durationMs(this.__wbg_ptr, isLikeNone(duration_ms) ? Number.MAX_SAFE_INTEGER : (duration_ms) >>> 0);
    }
    /**
     * Set (or clear with `undefined`) the pixel height.
     * @param {number | null} [height]
     */
    set height(height) {
        wasm.attachmentref_set_height(this.__wbg_ptr, isLikeNone(height) ? Number.MAX_SAFE_INTEGER : (height) >>> 0);
    }
    /**
     * Set (or clear with `undefined`) the media kind.
     * @param {string | null} [kind]
     */
    set kind(kind) {
        var ptr0 = isLikeNone(kind) ? 0 : passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        wasm.attachmentref_set_kind(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Set (or clear with `undefined`) the waveform bytes.
     * @param {Uint8Array | null} [waveform]
     */
    set waveform(waveform) {
        var ptr0 = isLikeNone(waveform) ? 0 : passArray8ToWasm0(waveform, wasm.__wbindgen_malloc);
        var len0 = WASM_VECTOR_LEN;
        wasm.attachmentref_set_waveform(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Set (or clear with `undefined`) the pixel width.
     * @param {number | null} [width]
     */
    set width(width) {
        wasm.attachmentref_set_width(this.__wbg_ptr, isLikeNone(width) ? Number.MAX_SAFE_INTEGER : (width) >>> 0);
    }
    /**
     * Plaintext size in bytes. JS represents `u64` as `BigInt` on
     * the wasm boundary.
     * @returns {bigint}
     */
    get size() {
        const ret = wasm.attachmentref_size(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * ADR-0047 — the poster-frame thumb (a copy), `undefined` when absent.
     * @returns {ThumbRef | undefined}
     */
    get thumb() {
        const ret = wasm.attachmentref_thumb(this.__wbg_ptr);
        return ret === 0 ? undefined : ThumbRef.__wrap(ret);
    }
    /**
     * ADR-0047 — raw waveform amplitude bytes (1..=128), `undefined` when
     * absent.
     * @returns {Uint8Array | undefined}
     */
    get waveform() {
        const ret = wasm.attachmentref_waveform(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * ADR-0047 — pixel width (1..=4096), `undefined` when absent.
     * @returns {number | undefined}
     */
    get width() {
        const ret = wasm.attachmentref_width(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
}
if (Symbol.dispose) AttachmentRef.prototype[Symbol.dispose] = AttachmentRef.prototype.free;

/**
 * Keeps one remote person's sound and picture together in a group call:
 * from what this browser's receivers report each interval, the target
 * playout delay (`RTCRtpReceiver.jitterBufferTarget`) to set on each.
 *
 * One per remote person. Every {@link avAlignReadingMs} (any interval up to
 * two seconds works): call `setAudio` and `setVideo` for each track that
 * exists with what its `inbound-rtp` statistics say for the interval, then
 * `read(nowMs)`, then read back what to set with `audioSetMs()` and
 * `videoSetMs(track)`. A track not reported since the last `read` counts
 * as not flowing. Nothing is allocated per call and only the `AvAlign`
 * itself needs `free()`.
 */
export class AvAlign {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AvAlignFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_avalign_free(ptr, 0);
    }
    /**
     * What the audio receiver was last told, milliseconds; `0` when
     * nothing.
     * @returns {number}
     */
    audioAskedMs() {
        const ret = wasm.avalign_audioAskedMs(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Why the audio target is what it is.
     * @returns {AvAudioReason}
     */
    audioReason() {
        const ret = wasm.avalign_audioReason(this.__wbg_ptr);
        return ret;
    }
    /**
     * The target to set on the audio receiver now, milliseconds;
     * `undefined` leaves it alone; `0` gives the target back
     * (`jitterBufferTarget = null`).
     * @returns {number | undefined}
     */
    audioSetMs() {
        const ret = wasm.avalign_audioSetMs(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * What the rule can achieve in this browser.
     * @returns {AvCapability}
     */
    capability() {
        const ret = wasm.avalign_capability(this.__wbg_ptr);
        return ret;
    }
    /**
     * A remote person's alignment in a browser that can (or cannot) set
     * `jitterBufferTarget` on an audio receiver and on a video receiver.
     * @param {boolean} audio_settable
     * @param {boolean} video_settable
     */
    constructor(audio_settable, video_settable) {
        const ret = wasm.avalign_new(audio_settable, video_settable);
        this.__wbg_ptr = ret;
        AvAlignFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Take the interval reported since the last `read`, at `nowMs` (one
     * millisecond clock, e.g. `performance.now()`), and decide. The answer
     * is read with the getters below until the next `read`.
     * @param {number} now_ms
     */
    read(now_ms) {
        wasm.avalign_read(this.__wbg_ptr, now_ms);
    }
    /**
     * The receivers were replaced: nothing has been told to the new ones.
     * The next `read` starts every track again and tells every receiver
     * its target.
     */
    receiversReplaced() {
        wasm.avalign_receiversReplaced(this.__wbg_ptr);
    }
    /**
     * The audio receiver's interval: whether packets arrived, the average
     * jitter-buffer delay in milliseconds (`jb_ms`; `undefined` when
     * nothing left the buffer) and the network-only need (`jb_net_ms`;
     * `undefined` where the browser reports none).
     * @param {boolean} flowing
     * @param {number | null} [delay_ms]
     * @param {number | null} [network_ms]
     */
    setAudio(flowing, delay_ms, network_ms) {
        wasm.avalign_setAudio(this.__wbg_ptr, flowing, !isLikeNone(delay_ms), isLikeNone(delay_ms) ? 0 : delay_ms, !isLikeNone(network_ms), isLikeNone(network_ms) ? 0 : network_ms);
    }
    /**
     * One video receiver's interval. `shown` is whether a tile draws this
     * track now; `engineSynced` is whether the browser synchronises it with
     * the audio itself (the main track, which shares the audio's stream
     * id).
     * @param {AvTrack} track
     * @param {boolean} flowing
     * @param {boolean} shown
     * @param {boolean} engine_synced
     * @param {number | null} [delay_ms]
     * @param {number | null} [network_ms]
     */
    setVideo(track, flowing, shown, engine_synced, delay_ms, network_ms) {
        wasm.avalign_setVideo(this.__wbg_ptr, track, flowing, shown, engine_synced, !isLikeNone(delay_ms), isLikeNone(delay_ms) ? 0 : delay_ms, !isLikeNone(network_ms), isLikeNone(network_ms) ? 0 : network_ms);
    }
    /**
     * What a video receiver was last told, milliseconds; `0` when nothing.
     * @param {AvTrack} track
     * @returns {number}
     */
    videoAskedMs(track) {
        const ret = wasm.avalign_videoAskedMs(this.__wbg_ptr, track);
        return ret >>> 0;
    }
    /**
     * Why a video target is what it is.
     * @param {AvTrack} track
     * @returns {AvVideoReason}
     */
    videoReason(track) {
        const ret = wasm.avalign_videoReason(this.__wbg_ptr, track);
        return ret;
    }
    /**
     * The target to set on a video receiver now, milliseconds; `undefined`
     * leaves it alone; `0` gives the target back.
     * @param {AvTrack} track
     * @returns {number | undefined}
     */
    videoSetMs(track) {
        const ret = wasm.avalign_videoSetMs(this.__wbg_ptr, track);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * A video track's measured delay minus the audio's, milliseconds:
     * positive when the picture is later than the sound; `undefined` when
     * either was not measured in the interval.
     * @param {AvTrack} track
     * @returns {number | undefined}
     */
    videoSkewMs(track) {
        const ret = wasm.avalign_videoSkewMs(this.__wbg_ptr, track);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
}
if (Symbol.dispose) AvAlign.prototype[Symbol.dispose] = AvAlign.prototype.free;

/**
 * Why the audio receiver's target is what it is.
 * @enum {0 | 1 | 2 | 3 | 4 | 5 | 6 | 7}
 */
export const AvAudioReason = Object.freeze({
    /**
     * No audio packets arrived; nothing is changed.
     */
    NotFlowing: 0, "0": "NotFlowing",
    /**
     * The audio flows but reported no delay; nothing is changed.
     */
    NoReading: 1, "1": "NoReading",
    /**
     * This browser cannot set an audio receiver's delay.
     */
    NoSetter: 2, "2": "NoSetter",
    /**
     * Nothing is asked of the audio.
     */
    Free: 3, "3": "Free",
    /**
     * The sound waits for a picture that needs more delay than it has.
     */
    WaitingForPicture: 4, "4": "WaitingForPicture",
    /**
     * The sound waits as long as it may; the picture is left late.
     */
    WaitBounded: 5, "5": "WaitBounded",
    /**
     * No picture needs the sound to wait any more; the target is coming
     * down.
     */
    Releasing: 6, "6": "Releasing",
    /**
     * The engine does not follow the audio target.
     */
    HintIgnored: 7, "7": "HintIgnored",
});

/**
 * What the rule can achieve in a browser, from which receivers' delays it
 * can set.
 * @enum {0 | 1 | 2 | 3}
 */
export const AvCapability = Object.freeze({
    /**
     * Both can be set.
     */
    Full: 0, "0": "Full",
    /**
     * Only video can be set: the audio cannot be delayed here, so a picture
     * that needs more delay than the sound has stays late.
     */
    PictureWaitsOnly: 1, "1": "PictureWaitsOnly",
    /**
     * Only audio can be set: a picture that runs ahead of the sound cannot
     * be held back here.
     */
    SoundWaitsOnly: 2, "2": "SoundWaitsOnly",
    /**
     * Neither can be set: the skew is measured and nothing is done.
     */
    ObserveOnly: 3, "3": "ObserveOnly",
});

/**
 * One of a remote person's three video tracks.
 * @enum {0 | 1 | 2}
 */
export const AvTrack = Object.freeze({
    /**
     * The main track (stream id `<peer>`).
     */
    Main: 0, "0": "Main",
    /**
     * The middle track (`<peer>~mid`).
     */
    Middle: 1, "1": "Middle",
    /**
     * The light track (`<peer>~light`).
     */
    Light: 2, "2": "Light",
});

/**
 * Why a video receiver's target is what it is.
 * @enum {0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}
 */
export const AvVideoReason = Object.freeze({
    /**
     * No packets arrived; the track is left alone.
     */
    NotFlowing: 0, "0": "NotFlowing",
    /**
     * The track flows but reported no delay; nothing is changed.
     */
    NoReading: 1, "1": "NoReading",
    /**
     * There is no measured audio delay to follow.
     */
    NoSound: 2, "2": "NoSound",
    /**
     * This browser cannot set a video receiver's delay.
     */
    NoSetter: 3, "3": "NoSetter",
    /**
     * The engine synchronises this track itself; it is only observed.
     */
    EngineSynced: 4, "4": "EngineSynced",
    /**
     * The track began to flow: it starts at the audio's delay.
     */
    Started: 5, "5": "Started",
    /**
     * The skew is inside the dead band.
     */
    Aligned: 6, "6": "Aligned",
    /**
     * The target is being raised: the picture waits for the sound.
     */
    Raising: 7, "7": "Raising",
    /**
     * The target is being lowered towards the sound.
     */
    Lowering: 8, "8": "Lowering",
    /**
     * The target stands and the receiver has not answered it yet.
     */
    Settling: 9, "9": "Settling",
    /**
     * The engine does not follow the target; the picture stays early.
     */
    HintIgnored: 10, "10": "HintIgnored",
    /**
     * The track needs more delay than the sound has; the sound waits.
     */
    SoundWaits: 11, "11": "SoundWaits",
    /**
     * The track is later than the sound and stays so.
     */
    PictureLate: 12, "12": "PictureLate",
});

/**
 * A reference to one encrypted avatar blob (ADR-0084): what
 * `encryptAttachment` returned for it, plus its plaintext size and side.
 */
export class AvatarRef {
    static __wrap(ptr) {
        const obj = Object.create(AvatarRef.prototype);
        obj.__wbg_ptr = ptr;
        AvatarRefFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AvatarRefFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_avatarref_free(ptr, 0);
    }
    /**
     * The 32-byte storage address (`SHA-256` of the ciphertext).
     * @returns {Uint8Array}
     */
    get blobId() {
        const ret = wasm.avatarref_blobId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 32-byte key. SECRET.
     * @returns {Uint8Array}
     */
    get key() {
        const ret = wasm.avatarref_key(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Build a reference: 32-byte blob id, 32-byte key (SECRET), 12-byte
     * nonce, plaintext size, and the side in pixels (the media is square).
     *
     * # Errors
     *
     * Throws when a byte field has the wrong length.
     * @param {Uint8Array} blob_id
     * @param {Uint8Array} key
     * @param {Uint8Array} nonce
     * @param {number} size
     * @param {number} side_px
     */
    constructor(blob_id, key, nonce, size, side_px) {
        const ptr0 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(nonce, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.avatarref_new(ptr0, len0, ptr1, len1, ptr2, len2, size, side_px);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        AvatarRefFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The 12-byte nonce.
     * @returns {Uint8Array}
     */
    get nonce() {
        const ret = wasm.avatarref_nonce(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Side in pixels.
     * @returns {number}
     */
    get side() {
        const ret = wasm.avatarref_side(this.__wbg_ptr);
        return ret;
    }
    /**
     * Plaintext size in bytes.
     * @returns {number}
     */
    get size() {
        const ret = wasm.avatarref_size(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) AvatarRef.prototype[Symbol.dispose] = AvatarRef.prototype.free;

/**
 * One row in the accepted-contacts list passed to
 * [`build_backup_payload`]. Plain JS-friendly fields; the wasm
 * boundary copies bytes in / out.
 *
 * We expose this as a wasm-bindgen `struct` (vs a plain JS
 * object) so the field-length contracts are enforced at the
 * boundary — wrong-length pubkeys fail constructor validation
 * instead of producing a corrupt payload further down the line.
 */
export class BackupContact {
    static __wrap(ptr) {
        const obj = Object.create(BackupContact.prototype);
        obj.__wbg_ptr = ptr;
        BackupContactFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof BackupContact)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        BackupContactFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_backupcontact_free(ptr, 0);
    }
    /**
     * Optional 16-byte account_id (ADR-0021); `undefined` when unknown.
     * @returns {Uint8Array | undefined}
     */
    get accountId() {
        const ret = wasm.backupcontact_accountId(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * User-chosen display label (UTF-8, may be empty).
     * @returns {string}
     */
    get displayName() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.backupcontact_displayName(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Raw 32-byte Ed25519 verifying key.
     * @returns {Uint8Array}
     */
    get ed25519Pubkey() {
        const ret = wasm.backupcontact_ed25519Pubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Construct a contact record. `ed25519_pubkey` and
     * `x25519_pubkey` must both be 32 bytes; `display_name` is
     * any UTF-8 string up to
     * `lakko_backup::payload::MAX_DISPLAY_NAME_LEN` bytes.
     *
     * # Errors
     *
     * `JsError` on wrong-length pubkey or over-cap display name.
     * @param {Uint8Array} ed25519_pubkey
     * @param {Uint8Array} x25519_pubkey
     * @param {string} display_name
     * @param {Uint8Array | null} [account_id]
     */
    constructor(ed25519_pubkey, x25519_pubkey, display_name, account_id) {
        const ptr0 = passArray8ToWasm0(ed25519_pubkey, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(x25519_pubkey, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(account_id) ? 0 : passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.backupcontact_new(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        BackupContactFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Raw 32-byte X25519 HPKE public key.
     * @returns {Uint8Array}
     */
    get x25519Pubkey() {
        const ret = wasm.backupcontact_x25519Pubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) BackupContact.prototype[Symbol.dispose] = BackupContact.prototype.free;

/**
 * The newest profile envelope this device holds from one other person
 * (ADR-0048), passed to [`build_backup_payload`] and returned by
 * `ParsedBackupPayload.receivedProfiles`, so a device restored with no other
 * device alive shows every sender with a name.
 */
export class BackupReceivedProfile {
    static __wrap(ptr) {
        const obj = Object.create(BackupReceivedProfile.prototype);
        obj.__wbg_ptr = ptr;
        BackupReceivedProfileFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof BackupReceivedProfile)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        BackupReceivedProfileFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_backupreceivedprofile_free(ptr, 0);
    }
    /**
     * The profile envelope bytes.
     * @returns {Uint8Array}
     */
    get envelope() {
        const ret = wasm.backupreceivedprofile_envelope(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `person_key` is the person's 32-byte account signature key, the key
     * the client stored the profile under at receipt; `envelope` is the
     * profile envelope exactly as received.
     *
     * # Errors
     *
     * `JsError` when `person_key` is not 32 bytes.
     * @param {Uint8Array} person_key
     * @param {Uint8Array} envelope
     */
    constructor(person_key, envelope) {
        const ptr0 = passArray8ToWasm0(person_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.backupreceivedprofile_new(ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        BackupReceivedProfileFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The person's 32-byte account signature key.
     * @returns {Uint8Array}
     */
    get personKey() {
        const ret = wasm.backupreceivedprofile_personKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) BackupReceivedProfile.prototype[Symbol.dispose] = BackupReceivedProfile.prototype.free;

/**
 * A channel's ban list (ADR-0087 section 13a). Carried as the plaintext of
 * an ordinary post; `classifyChannelPostPlaintext` returns it as kind
 * `"ban-list"`.
 */
export class BanList {
    static __wrap(ptr) {
        const obj = Object.create(BanList.prototype);
        obj.__wbg_ptr = ptr;
        BanListFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        BanListFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_banlist_free(ptr, 0);
    }
    /**
     * The banned author keys, concatenated (32 bytes each).
     * @returns {Uint8Array}
     */
    get authors() {
        const ret = wasm.banlist_authors(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Whether `authorKey` is banned.
     * @param {Uint8Array} author_key
     * @returns {boolean}
     */
    isBanned(author_key) {
        const ptr0 = passArray8ToWasm0(author_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.banlist_isBanned(this.__wbg_ptr, ptr0, len0);
        return ret !== 0;
    }
    /**
     * A list stamped `updatedAtMs` of the banned comment author keys,
     * concatenated (32 bytes each, oldest first).
     *
     * # Errors
     *
     * Throws `banList.badLength: ...`.
     * @param {bigint} updated_at_ms
     * @param {Uint8Array} authors
     */
    constructor(updated_at_ms, authors) {
        const ptr0 = passArray8ToWasm0(authors, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.banlist_new(updated_at_ms, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        BanListFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Publisher-clock UNIX milliseconds.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.banlist_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) BanList.prototype[Symbol.dispose] = BanList.prototype.free;

/**
 * One observed committer beacon, reduced to the three things
 * [`acting_committer_verdict`] reads. wasm-bindgen mirror of UniFFI's
 * `BeaconObservation` / [`lakko_contacts::committer_beacon::BeaconObservation`].
 *
 * **Every field is the observer's own re-derivation, never anything the
 * beacon claimed.** The payload is field-less: the sender comes from the MLS
 * sender leaf and the age from the DS row's server-assigned `created_at`, so a
 * sender can neither name itself nor assert its own freshness.
 *
 * # Why a constructible class rather than parallel arrays
 *
 * The fields belong to one observation, so they are carried as one object
 * — following [`crate::mls::ProposalRecord`], the house shape for a small
 * record crossing this boundary. Two parallel arrays (`ranks[]`, `ages[]`)
 * plus a sentinel for the absent rank would let the lengths diverge silently
 * and would need a magic number that `undefined` already expresses.
 */
export class BeaconObservation {
    static __unwrap(jsValue) {
        if (!(jsValue instanceof BeaconObservation)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        BeaconObservationFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_beaconobservation_free(ptr, 0);
    }
    /**
     * Age of the beacon's DS row in milliseconds (BigInt on the JS side).
     * @returns {bigint}
     */
    get ageMs() {
        const ret = wasm.beaconobservation_ageMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Build one observation from a beacon the caller has already decrypted and
     * classified.
     *
     * `rank` is the committer rank the observer **re-derived** for this
     * beacon's MLS-authenticated sender — `Group.committerRank(signatureKey)`
     * — or `undefined` when that sender is not a candidate at all (a leaf
     * that no longer resolves). Since ADR-0050 section 2.5a a plain member's
     * leaves ARE ranked, after the admins, because they beacon while they hold
     * a device admission; and since item 6 of that section every leaf of a
     * ranked account holds the account's rank.
     *
     * **Derive it at the moment the verdict is taken, never at the moment the
     * beacon arrived.** A rank is a *position* in the role table's canonical
     * order (`0` = owner, `k + 1` = the `k`-th admin), so promoting, demoting
     * or removing any admin renumbers every admin sorting after it; a rank
     * captured under one table cannot be compared against this device's own
     * rank, which is necessarily current. Persist the sender's **32-byte
     * signature key** with the row timestamp and rank it at fold time — never
     * a leaf index, which OpenMLS reuses for the next member added.
     *
     * `ageMs` is `now - created_at` in milliseconds on the DS row's
     * **server-assigned** timestamp, which every member reads identically; the
     * sender's own clock never enters it. Clamp a row that appears to come
     * from the future to `0` rather than wrapping — that is also the safe
     * direction, since an age that reads too fresh makes this device defer,
     * while one that reads too stale makes it act and risks a double sweep.
     *
     * `signatureKey` is the sender's 32-byte leaf signature key — the key the
     * MLS sender leaf resolved to at receipt, the same one ranked into
     * `rank`. It breaks the tie between devices of one account (ADR-0050
     * section 2.5a item 6): the account's lowest-keyed device whose beacon is
     * fresh acts.
     *
     * # Errors
     *
     * `JsError` when `signatureKey` is not exactly 32 bytes.
     * @param {number | null | undefined} rank
     * @param {bigint} age_ms
     * @param {Uint8Array} signature_key
     */
    constructor(rank, age_ms, signature_key) {
        const ptr0 = passArray8ToWasm0(signature_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.beaconobservation_new(isLikeNone(rank) ? Number.MAX_SAFE_INTEGER : (rank) >>> 0, age_ms, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        BeaconObservationFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The observer's re-derived committer rank for this beacon's sender, or
     * `undefined` when the sender is not a candidate.
     *
     * `undefined` and `0` are **not** the same thing: `0` is the owner. Because
     * `0` is falsy in JavaScript, `if (obs.rank)` is wrong in both directions.
     * @returns {number | undefined}
     */
    get rank() {
        const ret = wasm.beaconobservation_rank(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * The sender's 32-byte leaf signature key.
     * @returns {Uint8Array}
     */
    get signatureKey() {
        const ret = wasm.beaconobservation_signatureKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) BeaconObservation.prototype[Symbol.dispose] = BeaconObservation.prototype.free;

/**
 * Where a call is, as the collapsed bar of `docs/ux/calls.md` section 4.5
 * needs to see it.
 * @enum {0 | 1 | 2 | 3 | 4 | 5 | 6 | 7}
 */
export const CallBarPhase = Object.freeze({
    /**
     * No call at all.
     */
    Idle: 0, "0": "Idle",
    /**
     * An incoming call being offered, which nobody has answered.
     */
    Ringing: 1, "1": "Ringing",
    /**
     * Built, not yet on the wire.
     */
    Preparing: 2, "2": "Preparing",
    /**
     * Ringing out.
     */
    Calling: 3, "3": "Calling",
    /**
     * Answered; the transport is coming up.
     */
    Connecting: 4, "4": "Connecting",
    /**
     * Media is flowing.
     */
    Connected: 5, "5": "Connected",
    /**
     * The transport dropped and is being rebuilt.
     */
    Reconnecting: 6, "6": "Reconnecting",
    /**
     * Over.
     */
    Ended: 7, "7": "Ended",
});

/**
 * The share a call is showing now.
 */
export class CallCurrentScreenShare {
    static __wrap(ptr) {
        const obj = Object.create(CallCurrentScreenShare.prototype);
        obj.__wbg_ptr = ptr;
        CallCurrentScreenShareFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CallCurrentScreenShareFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_callcurrentscreenshare_free(ptr, 0);
    }
    /**
     * The sharer's account key (empty for this device's own share).
     * @returns {Uint8Array}
     */
    get accountKey() {
        const ret = wasm.callcurrentscreenshare_accountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Whether it is this device's own share.
     * @returns {boolean}
     */
    get isOwn() {
        const ret = wasm.callcurrentscreenshare_isOwn(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The sharer's peer id.
     * @returns {string}
     */
    get peerId() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.callcurrentscreenshare_peerId(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The share's sequence number.
     * @returns {bigint}
     */
    get seq() {
        const ret = wasm.callcurrentscreenshare_seq(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The sharer's leaf signature key.
     * @returns {Uint8Array}
     */
    get signerKey() {
        const ret = wasm.callcurrentscreenshare_signerKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Whether it carries sound.
     * @returns {boolean}
     */
    get withSound() {
        const ret = wasm.callcurrentscreenshare_withSound(this.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) CallCurrentScreenShare.prototype[Symbol.dispose] = CallCurrentScreenShare.prototype.free;

/**
 * How a group call's grid is drawn (`docs/ux/group-call-grid.md` section 4):
 * equal tiles filling the call area, with no scrolling.
 */
export class CallGrid {
    static __wrap(ptr) {
        const obj = Object.create(CallGrid.prototype);
        obj.__wbg_ptr = ptr;
        CallGridFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CallGridFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_callgrid_free(ptr, 0);
    }
    /**
     * Columns on a page.
     * @returns {number}
     */
    get cols() {
        const ret = wasm.callgrid_cols(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The space between tiles.
     * @returns {number}
     */
    get gap() {
        const ret = wasm.callgrid_gap(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Pages; 1 when everyone fits.
     * @returns {number}
     */
    get pages() {
        const ret = wasm.callgrid_pages(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Tiles on a full page.
     * @returns {number}
     */
    get perPage() {
        const ret = wasm.callgrid_perPage(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Rows on a full page.
     * @returns {number}
     */
    get rows() {
        const ret = wasm.callgrid_rows(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Height of every tile.
     * @returns {number}
     */
    get tileHeight() {
        const ret = wasm.callgrid_tileHeight(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Width of every tile.
     * @returns {number}
     */
    get tileWidth() {
        const ret = wasm.callgrid_tileWidth(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) CallGrid.prototype[Symbol.dispose] = CallGrid.prototype.free;

/**
 * Composed and verified call-invite capability (Phase
 * 3c3). Mirror of [`lakko_contacts::CallInviteCapability`].
 */
export class CallInviteCapability {
    static __wrap(ptr) {
        const obj = Object.create(CallInviteCapability.prototype);
        obj.__wbg_ptr = ptr;
        CallInviteCapabilityFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CallInviteCapabilityFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_callinvitecapability_free(ptr, 0);
    }
    /**
     * 16-byte random call id minted by the inviter.
     * @returns {Uint8Array}
     */
    get callId() {
        const ret = wasm.callinvitecapability_callId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Decode PLAINTEXT capability wire bytes and verify the Ed25519
     * signature. A link never carries plaintext — a joiner uses `openLink`.
     * @param {Uint8Array} wire
     * @returns {CallInviteCapability}
     */
    static decodeAndVerify(wire) {
        const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callinvitecapability_decodeAndVerify(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return CallInviteCapability.__wrap(ret[0]);
    }
    /**
     * UNIX milliseconds when the invite stops being valid.
     * `0` is the never-expires sentinel.
     * @returns {bigint}
     */
    get expiryMs() {
        const ret = wasm.callinvitecapability_expiryMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * `true` iff the capability has not expired at `nowMs`.
     * @param {bigint} now_ms
     * @returns {boolean}
     */
    isValidNow(now_ms) {
        const ret = wasm.callinvitecapability_isValidNow(this.__wbg_ptr, now_ms);
        return ret !== 0;
    }
    /**
     * Optional human-visible label.
     * @returns {string}
     */
    get label() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.callinvitecapability_label(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The link's `link` reach grant (ADR-0060 §6). SECRET — the joiner
     * writes its `ContactRequest` to the owner's mailbox with
     * `DsProofSigner.forReach(linkGrant.seed)`, then drops it.
     * @returns {ReachGrant}
     */
    get linkGrant() {
        const ret = wasm.callinvitecapability_linkGrant(this.__wbg_ptr);
        return ReachGrant.__wrap(ret);
    }
    /**
     * The link's 16-byte reach scope id (ADR-0060 §6).
     * @returns {Uint8Array}
     */
    get linkId() {
        const ret = wasm.callinvitecapability_linkId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Max-uses hint. `0` is the single-use sentinel.
     * @returns {number}
     */
    get maxUses() {
        const ret = wasm.callinvitecapability_maxUses(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * `true` when the call supports audio.
     * @returns {boolean}
     */
    get mediaAudio() {
        const ret = wasm.callinvitecapability_mediaAudio(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `true` when the call supports video.
     * @returns {boolean}
     */
    get mediaVideo() {
        const ret = wasm.callinvitecapability_mediaVideo(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Owner's one-time `KeyPackage` wire bytes.
     * @returns {Uint8Array}
     */
    get oneTimeKeyPackage() {
        const ret = wasm.callinvitecapability_oneTimeKeyPackage(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Joiner-side: open a `/c/` link's sealed blob with the 12-byte secret
     * from the URL fragment and verify the capability inside. Without the
     * secret a fetched blob is not a seat in the call. Does NOT check expiry.
     *
     * # Errors
     *
     * Throws an `Error` with `inviteLinkError: "damaged"` on any failure.
     * @param {Uint8Array} sealed
     * @param {Uint8Array} link_secret
     * @returns {CallInviteCapability}
     */
    static openLink(sealed, link_secret) {
        const ptr0 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(link_secret, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.callinvitecapability_openLink(ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return CallInviteCapability.__wrap(ret[0]);
    }
    /**
     * Inviter's `account_id` (16-byte UUIDv7).
     * @returns {Uint8Array}
     */
    get ownerAccountId() {
        const ret = wasm.callinvitecapability_ownerAccountId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Signal-style safety number derived from the owner's
     * long-term Ed25519 identity key. Joiner UI shows this
     * before Accept so the human cross-checks the digits
     * against what the owner shared OOB. Defends against
     * link-substitution attacks.
     * @returns {string}
     */
    get ownerFingerprint() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.callinvitecapability_ownerFingerprint(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Inviter's long-term Ed25519 verifying key (32 bytes).
     * @returns {Uint8Array}
     */
    get ownerSignaturePubkey() {
        const ret = wasm.callinvitecapability_ownerSignaturePubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The owner's mailbox key the link grant names, verified against the
     * owner's ACCOUNT key (the one-time KeyPackage's device binding, or the
     * leaf key of an unbound leaf) — ADR-0078 phase 1. A joiner writes under
     * the link grant only to this key; a throw means the link is damaged.
     *
     * # Errors
     *
     * Throws when the KeyPackage or either binding does not verify.
     * @returns {Uint8Array}
     */
    verifiedLinkGrantMailboxPk() {
        const ret = wasm.callinvitecapability_verifiedLinkGrantMailboxPk(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The plaintext capability wire — what `computeJoinerInviteTag` takes.
     * @returns {Uint8Array}
     */
    get wire() {
        const ret = wasm.callinvitecapability_wire(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) CallInviteCapability.prototype[Symbol.dispose] = CallInviteCapability.prototype.free;

/**
 * Per-call shared key — the 32-byte seed each participant
 * derives via the MLS exporter.
 */
export class CallKey {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CallKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_callkey_free(ptr, 0);
    }
    /**
     * Construct from a 32-byte exporter output. The caller
     * MUST have derived the seed via
     * `Group.exportSecret("lakko/call/v1", call_id, 32)`
     * — receivers reach the same bytes because the MLS
     * state machine guarantees it.
     *
     * # Errors
     *
     * `JsError` if `seed` is not exactly 32 bytes.
     * @param {Uint8Array} seed
     */
    constructor(seed) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callkey_new(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        CallKeyFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Derive the seal-side [`WasmParticipantKey`] for the supplied
     * `(sender_leaf, stream_index)` stream (ADR-0035). Keying on the composite
     * SFrame KID `(sender_leaf << 8) | stream_index` gives every one of a
     * participant's concurrent streams (audio, camera, screen-share, …) a
     * distinct key + nonce-salt space, so their independent counters cannot
     * reuse a `(key, nonce)` pair — the gap the ADR-0034 per-media-TYPE tag
     * left open for two same-kind streams.
     *
     * `stream_index` crosses as a `u32`; a value `> 255` fails closed (it would
     * overflow the KID's 8-bit stream field) rather than silently truncating.
     *
     * The key's frame counter starts at a uniformly random value in
     * `[0, 2^62)` (ADR-0069), so deriving a key again for the same
     * `(seed, sender_leaf, stream_index)` — a reload, a rejoin, a republished
     * track — is safe: two such keys reuse a nonce only if the counter ranges
     * they seal overlap, with probability at most `(L1 + L2) / 2^62`.
     *
     * # Errors
     *
     * `JsError` if `stream_index > 255`, or on HKDF failure (practically
     * unreachable with valid inputs).
     * @param {number} sender_leaf
     * @param {number} stream_index
     * @returns {ParticipantKey}
     */
    participantKey(sender_leaf, stream_index) {
        const ret = wasm.callkey_participantKey(this.__wbg_ptr, sender_leaf, stream_index);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ParticipantKey.__wrap(ret[0]);
    }
    /**
     * Borrow the raw 32-byte seed. Test / persistence
     * only.
     * @returns {Uint8Array}
     */
    seedBytes() {
        const ret = wasm.callkey_seedBytes(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) CallKey.prototype[Symbol.dispose] = CallKey.prototype.free;

/**
 * FFI wrapper over [`lakko_calls::CallKeyRing`] — the mid-call SFrame overlap
 * keyer (ADR-0029, G-18). Holds the current call key plus, during a rotation,
 * the previous one for the overlap window. `Mutex` because a mid-call rekey /
 * prune mutates the ring while the receive-side worker may open frames.
 */
export class CallKeyRing {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CallKeyRingFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_callkeyring_free(ptr, 0);
    }
    /**
     * `true` when a previous key is retained AND still inside its overlap
     * window at `now_ms`.
     * @param {bigint} now_ms
     * @returns {boolean}
     */
    hasLivePrevious(now_ms) {
        const ret = wasm.callkeyring_hasLivePrevious(this.__wbg_ptr, now_ms);
        return ret !== 0;
    }
    /**
     * Build a ring from a 32-byte call-key seed (the current epoch).
     *
     * # Errors
     *
     * `JsError` if `seed` is not exactly 32 bytes.
     * @param {Uint8Array} seed
     */
    constructor(seed) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callkeyring_new(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        CallKeyRingFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Open an RFC 9605 framed SFrame with the overlap fall-back: try the
     * current key for `kid` (ADR-0035; the composite `(sender_leaf,
     * stream_index)` identifier the worker read from the frame header), and on
     * an AEAD failure fall back to the retained previous key while its window
     * is live (ADR-0029). The receive-side path for the web worker across a
     * mid-call rekey. `kid` crosses to JS as a `BigInt`; the decryptor takes it
     * straight from the wire — no separate media discriminator.
     *
     * # Errors
     *
     * `JsError` on a malformed header, or on AEAD auth failure under both the
     * current and previous keys.
     * @param {bigint} kid
     * @param {Uint8Array} wire
     * @returns {Uint8Array}
     */
    openFramedWithOverlap(kid, wire) {
        const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callkeyring_openFramedWithOverlap(this.__wbg_ptr, kid, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Open a sealed media frame in the ADR-0071 layout with the overlap
     * fall-back (ADR-0029) and return the encoded frame
     * `clear_prefix || plaintext`. The KID is read from the frame's own
     * SFrame header, which sits before the trailer at the end of the frame;
     * the caller passes nothing but the bytes. The receive-side path for the
     * web worker.
     *
     * # Errors
     *
     * `JsError` on a malformed frame, or on AEAD authentication failure under
     * both the current and the previous key.
     * @param {Uint8Array} wire
     * @returns {Uint8Array}
     */
    openMediaFrameWithOverlap(wire) {
        const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callkeyring_openMediaFrameWithOverlap(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Derive the seal-side [`WasmParticipantKey`] for a `(sender_leaf,
     * stream_index)` stream from the current epoch (ADR-0035). Re-fetched after
     * a `rekeyFromSeedAt` to seal under the new key. Keying on the composite
     * SFrame KID `(sender_leaf << 8) | stream_index` keeps every one of a
     * participant's concurrent streams on a distinct key + nonce-salt space.
     * `stream_index` crosses as a `u32`; a value `> 255` fails closed rather
     * than silently truncating. The key starts at a random counter in
     * `[0, 2^62)` (ADR-0069).
     *
     * # Errors
     *
     * `JsError` if `stream_index > 255`, or on HKDF failure (practically
     * unreachable).
     * @param {number} sender_leaf
     * @param {number} stream_index
     * @returns {ParticipantKey}
     */
    participantKey(sender_leaf, stream_index) {
        const ret = wasm.callkeyring_participantKey(this.__wbg_ptr, sender_leaf, stream_index);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ParticipantKey.__wrap(ret[0]);
    }
    /**
     * Drop the retained previous key once it has expired at `now_ms`.
     * @param {bigint} now_ms
     */
    pruneExpired(now_ms) {
        wasm.callkeyring_pruneExpired(this.__wbg_ptr, now_ms);
    }
    /**
     * Rotate to a new-epoch seed on a mid-call MLS epoch change, retaining the
     * previous key until `now_ms + clamp(overlap_ms)` so in-flight frames
     * sealed under it still open (ADR-0008; C-8 / C-9 / C-10). `now_ms` +
     * `overlap_ms` cross to JS as `BigInt`.
     *
     * # Errors
     *
     * `JsError` if `seed` is not exactly 32 bytes.
     * @param {Uint8Array} seed
     * @param {bigint} now_ms
     * @param {bigint} overlap_ms
     */
    rekeyFromSeedAt(seed, now_ms, overlap_ms) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callkeyring_rekeyFromSeedAt(this.__wbg_ptr, ptr0, len0, now_ms, overlap_ms);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
}
if (Symbol.dispose) CallKeyRing.prototype[Symbol.dispose] = CallKeyRing.prototype.free;

/**
 * Stateful rolling-window smoothed quality sampler
 * (Phase 3a11). One instance per active call.
 *
 * `record` pushes a fresh sample and returns the
 * smoothed [`CallQualityScore`]; `latest` returns the
 * current smoothed score without pushing; `clear` resets
 * the window (on call end / SFU handoff).
 */
export class CallQualitySampler {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CallQualitySamplerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_callqualitysampler_free(ptr, 0);
    }
    /**
     * Drop every buffered sample.
     */
    clear() {
        wasm.callqualitysampler_clear(this.__wbg_ptr);
    }
    /**
     * Number of samples currently buffered.
     * @returns {number}
     */
    get count() {
        const ret = wasm.callqualitysampler_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Latest smoothed score without pushing a new sample.
     * Returns `null` when the sampler is empty.
     * @returns {CallQualityScore | undefined}
     */
    latest() {
        const ret = wasm.callqualitysampler_latest(this.__wbg_ptr);
        return ret === 0 ? undefined : CallQualityScore.__wrap(ret);
    }
    /**
     * Construct a sampler with a custom window size.
     * Sizes outside `[1, MAX_SAMPLE_WINDOW]` are clamped.
     * @param {number} window_size
     */
    constructor(window_size) {
        const ret = wasm.callqualitysampler_new(window_size);
        this.__wbg_ptr = ret;
        CallQualitySamplerFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Push a sample and return the smoothed score.
     * @param {number} rtt_ms
     * @param {number} jitter_ms
     * @param {number} loss_pct
     * @param {string} codec
     * @returns {CallQualityScore}
     */
    record(rtt_ms, jitter_ms, loss_pct, codec) {
        const ptr0 = passStringToWasm0(codec, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callqualitysampler_record(this.__wbg_ptr, rtt_ms, jitter_ms, loss_pct, ptr0, len0);
        return CallQualityScore.__wrap(ret);
    }
    /**
     * Effective window size after clamping.
     * @returns {number}
     */
    get windowSize() {
        const ret = wasm.callqualitysampler_windowSize(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) CallQualitySampler.prototype[Symbol.dispose] = CallQualitySampler.prototype.free;

/**
 * MOS estimation result. `level` is one of
 * `"excellent" | "good" | "fair" | "poor" | "bad"`.
 */
export class CallQualityScore {
    static __wrap(ptr) {
        const obj = Object.create(CallQualityScore.prototype);
        obj.__wbg_ptr = ptr;
        CallQualityScoreFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CallQualityScoreFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_callqualityscore_free(ptr, 0);
    }
    /**
     * One of `"excellent"` / `"good"` / `"fair"` /
     * `"poor"` / `"bad"`.
     * @returns {string}
     */
    get level() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.callqualityscore_level(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Mean Opinion Score in `[1.0, 4.5]`.
     * @returns {number}
     */
    get mos() {
        const ret = wasm.callqualityscore_mos(this.__wbg_ptr);
        return ret;
    }
    /**
     * E-model R-factor in `[0.0, 100.0]`.
     * @returns {number}
     */
    get rFactor() {
        const ret = wasm.callqualityscore_rFactor(this.__wbg_ptr);
        return ret;
    }
}
if (Symbol.dispose) CallQualityScore.prototype[Symbol.dispose] = CallQualityScore.prototype.free;

/**
 * The key the relay channel of one call epoch is sealed under.
 */
export class CallRelayKey {
    static __wrap(ptr) {
        const obj = Object.create(CallRelayKey.prototype);
        obj.__wbg_ptr = ptr;
        CallRelayKeyFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CallRelayKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_callrelaykey_free(ptr, 0);
    }
    /**
     * Derive the key from the 32-byte per-call seed of the current epoch.
     *
     * # Errors
     *
     * `seedLength` when the seed is not 32 bytes.
     * @param {Uint8Array} seed
     * @returns {CallRelayKey}
     */
    static fromCallSeed(seed) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callrelaykey_fromCallSeed(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return CallRelayKey.__wrap(ret[0]);
    }
    /**
     * Open a relay payload.
     *
     * # Errors
     *
     * `badLength`, `unknownVersion` or `notOpened`.
     * @param {Uint8Array} sealed
     * @returns {Uint8Array}
     */
    open(sealed) {
        const ptr0 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callrelaykey_open(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Seal a relay payload.
     *
     * # Errors
     *
     * `badLength` for a plaintext over the bound.
     * @param {Uint8Array} plaintext
     * @returns {Uint8Array}
     */
    seal(plaintext) {
        const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callrelaykey_seal(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
}
if (Symbol.dispose) CallRelayKey.prototype[Symbol.dispose] = CallRelayKey.prototype.free;

/**
 * What an opened relay plaintext is (flat: wasm-bindgen has no enums with
 * data).
 */
export class CallRelayPayload {
    static __wrap(ptr) {
        const obj = Object.create(CallRelayPayload.prototype);
        obj.__wbg_ptr = ptr;
        CallRelayPayloadFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CallRelayPayloadFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_callrelaypayload_free(ptr, 0);
    }
    /**
     * For `"peer-announcement"`: the whole announcement.
     * @returns {Uint8Array}
     */
    get announcement() {
        const ret = wasm.callrelaypayload_announcement(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * For `"media-state"`: whether the camera is on.
     * @returns {boolean}
     */
    get cameraOn() {
        const ret = wasm.callrelaypayload_cameraOn(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `"peer-announcement"`, `"media-state"`, `"screen-share"` or
     * `"unknown"`.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.callrelaypayload_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * For `"screen-share"`: the whole share state (ADR-0083).
     * @returns {Uint8Array}
     */
    get state() {
        const ret = wasm.callrelaypayload_state(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) CallRelayPayload.prototype[Symbol.dispose] = CallRelayPayload.prototype.free;

/**
 * The one-share-per-call fold of ADR-0083 (C-87), one per call.
 */
export class CallScreenShareState {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CallScreenShareStateFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_callscreensharestate_free(ptr, 0);
    }
    /**
     * The share to show, or `undefined`.
     * @returns {CallCurrentScreenShare | undefined}
     */
    current() {
        const ret = wasm.callscreensharestate_current(this.__wbg_ptr);
        return ret === 0 ? undefined : CallCurrentScreenShare.__wrap(ret);
    }
    /**
     * A fold for a device whose leaf signature key is `ownKey`.
     *
     * # Errors
     *
     * `keyLength` when the key is not 32 bytes.
     * @param {Uint8Array} own_key
     */
    constructor(own_key) {
        const ptr0 = passArray8ToWasm0(own_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callscreensharestate_new(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        CallScreenShareStateFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The seq a share this device starts now must carry.
     * @returns {bigint}
     */
    nextStartSeq() {
        const ret = wasm.callscreensharestate_nextStartSeq(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Fold in a verified state from another participant. Returns whether
     * the current share changed.
     *
     * # Errors
     *
     * `keyLength` for a signer key not 32 bytes.
     * @param {VerifiedCallScreenShare} state
     * @returns {boolean}
     */
    observe(state) {
        _assertClass(state, VerifiedCallScreenShare);
        const ret = wasm.callscreensharestate_observe(this.__wbg_ptr, state.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * The seq of this device's share when another share superseded it:
     * stop the capture, then `stopOwn()`.
     * @returns {bigint | undefined}
     */
    ownSuperseded() {
        const ret = wasm.callscreensharestate_ownSuperseded(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
    }
    /**
     * The participant whose leaf key is `signerKey` left the call.
     * Returns whether the current share changed.
     * @param {Uint8Array} signer_key
     * @returns {boolean}
     */
    participantLeft(signer_key) {
        const ptr0 = passArray8ToWasm0(signer_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callscreensharestate_participantLeft(this.__wbg_ptr, ptr0, len0);
        return ret !== 0;
    }
    /**
     * Record that this device started share `seq`. Returns whether the
     * current share changed.
     * @param {bigint} seq
     * @param {string} peer_id
     * @param {boolean} with_sound
     * @returns {boolean}
     */
    startOwn(seq, peer_id, with_sound) {
        const ptr0 = passStringToWasm0(peer_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callscreensharestate_startOwn(this.__wbg_ptr, seq, ptr0, len0, with_sound);
        return ret !== 0;
    }
    /**
     * Record that this device stopped its share: the seq its stopped state
     * must carry, or `undefined` when it had none.
     * @returns {bigint | undefined}
     */
    stopOwn() {
        const ret = wasm.callscreensharestate_stopOwn(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
    }
}
if (Symbol.dispose) CallScreenShareState.prototype[Symbol.dispose] = CallScreenShareState.prototype.free;

/**
 * How a channel subscriber invitation lets its holder read the channel
 * (ADR-0087 sections 6, 7 and 14).
 */
export class ChannelAccess {
    static __wrap(ptr) {
        const obj = Object.create(ChannelAccess.prototype);
        obj.__wbg_ptr = ptr;
        ChannelAccessFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelAccessFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelaccess_free(ptr, 0);
    }
    /**
     * The admission public key of a private channel, else `undefined`.
     * @returns {Uint8Array | undefined}
     */
    get admissionPublicKey() {
        const ret = wasm.channelaccess_admissionPublicKey(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The admission-key version of a private channel, else `undefined`.
     * @returns {number | undefined}
     */
    get admissionVersion() {
        const ret = wasm.channelaccess_admissionVersion(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret;
    }
    /**
     * The invite seed of a private channel (SECRET), else `undefined`.
     * @returns {Uint8Array | undefined}
     */
    get inviteSeed() {
        const ret = wasm.channelaccess_inviteSeed(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * `true` for a private channel.
     * @returns {boolean}
     */
    get isPrivate() {
        const ret = wasm.channelaccess_isPrivate(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * A private channel: the link's 32-byte invite seed (SECRET), the
     * channel's 32-byte admission public key and its version.
     *
     * # Errors
     *
     * Throws on a wrong-length key.
     * @param {Uint8Array} invite_seed
     * @param {Uint8Array} admission_public_key
     * @param {number} admission_version
     * @returns {ChannelAccess}
     */
    static privateChannel(invite_seed, admission_public_key, admission_version) {
        const ptr0 = passArray8ToWasm0(invite_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(admission_public_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.channelaccess_privateChannel(ptr0, len0, ptr1, len1, admission_version);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ChannelAccess.__wrap(ret[0]);
    }
    /**
     * A public channel: its 32-byte read key.
     *
     * # Errors
     *
     * Throws on a wrong-length key.
     * @param {Uint8Array} read_key
     * @returns {ChannelAccess}
     */
    static publicChannel(read_key) {
        const ptr0 = passArray8ToWasm0(read_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.channelaccess_publicChannel(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ChannelAccess.__wrap(ret[0]);
    }
    /**
     * The read key of a public channel, else `undefined`.
     * @returns {Uint8Array | undefined}
     */
    get readKey() {
        const ret = wasm.channelaccess_readKey(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
}
if (Symbol.dispose) ChannelAccess.prototype[Symbol.dispose] = ChannelAccess.prototype.free;

/**
 * A verified co-publisher binding.
 */
export class ChannelCoPublisherBinding {
    static __wrap(ptr) {
        const obj = Object.create(ChannelCoPublisherBinding.prototype);
        obj.__wbg_ptr = ptr;
        ChannelCoPublisherBindingFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelCoPublisherBindingFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelcopublisherbinding_free(ptr, 0);
    }
    /**
     * The co-publisher's account identity key, which signed the binding.
     * Appoint only when it equals the joiner key that was approved.
     * @returns {Uint8Array}
     */
    get accountPubkey() {
        const ret = wasm.channelcopublisherbinding_accountPubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The channel the key is for (16 bytes).
     * @returns {Uint8Array}
     */
    get channelId() {
        const ret = wasm.channelcopublisherbinding_channelId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The co-publisher key to appoint.
     * @returns {Uint8Array}
     */
    get copublisherPubkey() {
        const ret = wasm.channelcopublisherbinding_copublisherPubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ChannelCoPublisherBinding.prototype[Symbol.dispose] = ChannelCoPublisherBinding.prototype.free;

/**
 * A co-publisher's own key for one channel, which the channel's owner
 * appoints on `services/channels`.
 */
export class ChannelCoPublisherSigner {
    static __wrap(ptr) {
        const obj = Object.create(ChannelCoPublisherSigner.prototype);
        obj.__wbg_ptr = ptr;
        ChannelCoPublisherSignerFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelCoPublisherSignerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelcopublishersigner_free(ptr, 0);
    }
    /**
     * The `x-lakko-channel-copublisher-proof` header value for one request
     * (same arguments as `ChannelOwnerSigner.sign`). Fresh nonce per call.
     * @param {string} method
     * @param {string} path
     * @param {string} query
     * @param {Uint8Array} body
     * @returns {string}
     */
    sign(method, path, query, body) {
        let deferred5_0;
        let deferred5_1;
        try {
            const ptr0 = passStringToWasm0(method, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len0 = WASM_VECTOR_LEN;
            const ptr1 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            const ptr2 = passStringToWasm0(query, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len2 = WASM_VECTOR_LEN;
            const ptr3 = passArray8ToWasm0(body, wasm.__wbindgen_malloc);
            const len3 = WASM_VECTOR_LEN;
            const ret = wasm.channelcopublishersigner_sign(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
            deferred5_0 = ret[0];
            deferred5_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred5_0, deferred5_1, 1);
        }
    }
    /**
     * The 32-byte Ed25519 public key the owner appoints.
     * @returns {Uint8Array}
     */
    verifyingKey() {
        const ret = wasm.channelcopublishersigner_verifyingKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ChannelCoPublisherSigner.prototype[Symbol.dispose] = ChannelCoPublisherSigner.prototype.free;

/**
 * The account's key for one comment under one post.
 */
export class ChannelCommentWriteKey {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelCommentWriteKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelcommentwritekey_free(ptr, 0);
    }
    /**
     * The key of the comment `commentNonce` (16 bytes) on the post at
     * `postCursor`.
     *
     * # Errors
     *
     * Throws `write.badLength: ...`.
     * @param {Identity} account_identity
     * @param {Uint8Array} channel_id
     * @param {bigint} post_cursor
     * @param {Uint8Array} comment_nonce
     */
    constructor(account_identity, channel_id, post_cursor, comment_nonce) {
        _assertClass(account_identity, Identity);
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(comment_nonce, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.channelcommentwritekey_new(account_identity.__wbg_ptr, ptr0, len0, post_cursor, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        ChannelCommentWriteKeyFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The 32-byte public half the comment row names (the comment's
     * `writerKey`).
     * @returns {Uint8Array}
     */
    get publicKey() {
        const ret = wasm.channelcommentwritekey_publicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The header value for adding the comment whose sealed bytes are
     * `sealed`.
     * @param {Uint8Array} sealed
     * @returns {string}
     */
    signAdd(sealed) {
        let deferred2_0;
        let deferred2_1;
        try {
            const ptr0 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
            const len0 = WASM_VECTOR_LEN;
            const ret = wasm.channelcommentwritekey_signAdd(this.__wbg_ptr, ptr0, len0);
            deferred2_0 = ret[0];
            deferred2_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * The header value for deleting the comment filed at `commentCursor`.
     * @param {bigint} comment_cursor
     * @returns {string}
     */
    signDelete(comment_cursor) {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.channelcommentwritekey_signDelete(this.__wbg_ptr, comment_cursor);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) ChannelCommentWriteKey.prototype[Symbol.dispose] = ChannelCommentWriteKey.prototype.free;

/**
 * What the viewer is to a channel.
 */
export class ChannelInfoCapabilities {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelInfoCapabilitiesFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelinfocapabilities_free(ptr, 0);
    }
    /**
     * This device holds the publisher keys.
     * @returns {boolean}
     */
    get isPublisher() {
        const ret = wasm.channelinfocapabilities_isPublisher(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * This device reads the channel as a subscriber.
     * @returns {boolean}
     */
    get isSubscriber() {
        const ret = wasm.channelinfocapabilities_isSubscriber(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * In order: this device holds the publisher keys, this device reads the
     * channel as a subscriber, and this device has a subscriber count —
     * which only a publisher ever does.
     * @param {boolean} is_publisher
     * @param {boolean} is_subscriber
     * @param {boolean} subscriber_count_known
     */
    constructor(is_publisher, is_subscriber, subscriber_count_known) {
        const ret = wasm.channelinfocapabilities_new(is_publisher, is_subscriber, subscriber_count_known);
        this.__wbg_ptr = ret;
        ChannelInfoCapabilitiesFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * This device has a subscriber count.
     * @returns {boolean}
     */
    get subscriberCountKnown() {
        const ret = wasm.channelinfocapabilities_subscriberCountKnown(this.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) ChannelInfoCapabilities.prototype[Symbol.dispose] = ChannelInfoCapabilities.prototype.free;

/**
 * The subscriber's side of a private-channel invitation.
 */
export class ChannelInviteSigner {
    static __wrap(ptr) {
        const obj = Object.create(ChannelInviteSigner.prototype);
        obj.__wbg_ptr = ptr;
        ChannelInviteSignerFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelInviteSignerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelinvitesigner_free(ptr, 0);
    }
    /**
     * The signer of the link whose 32-byte invite seed is `inviteSeed`.
     *
     * # Errors
     *
     * Throws `invite.badLength: ...`.
     * @param {Uint8Array} invite_seed
     * @returns {ChannelInviteSigner}
     */
    static fromInviteSeed(invite_seed) {
        const ptr0 = passArray8ToWasm0(invite_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.channelinvitesigner_fromInviteSeed(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ChannelInviteSigner.__wrap(ret[0]);
    }
    /**
     * Seal the admission ticket for `subscriberPk` in `channelId` to the
     * channel's admission public key of `admissionVersion`.
     *
     * # Errors
     *
     * Throws `admissionTicket.<code>: ...`.
     * @param {Uint8Array} admission_public_key
     * @param {number} admission_version
     * @param {Uint8Array} channel_id
     * @param {Uint8Array} subscriber_pk
     * @returns {Uint8Array}
     */
    sealTicket(admission_public_key, admission_version, channel_id, subscriber_pk) {
        const ptr0 = passArray8ToWasm0(admission_public_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(subscriber_pk, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.channelinvitesigner_sealTicket(this.__wbg_ptr, ptr0, len0, admission_version, ptr1, len1, ptr2, len2);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v4;
    }
    /**
     * The `x-lakko-channel-invite-proof` header value for registering
     * `subscriberPk` in `channelId`. Good for one request.
     *
     * # Errors
     *
     * Throws `invite.badLength: ...`.
     * @param {Uint8Array} channel_id
     * @param {Uint8Array} subscriber_pk
     * @returns {string}
     */
    signProof(channel_id, subscriber_pk) {
        let deferred4_0;
        let deferred4_1;
        try {
            const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
            const len0 = WASM_VECTOR_LEN;
            const ptr1 = passArray8ToWasm0(subscriber_pk, wasm.__wbindgen_malloc);
            const len1 = WASM_VECTOR_LEN;
            const ret = wasm.channelinvitesigner_signProof(this.__wbg_ptr, ptr0, len0, ptr1, len1);
            var ptr3 = ret[0];
            var len3 = ret[1];
            if (ret[3]) {
                ptr3 = 0; len3 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred4_0 = ptr3;
            deferred4_1 = len3;
            return getStringFromWasm0(ptr3, len3);
        } finally {
            wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
        }
    }
    /**
     * The 32-byte invite public key the publisher registers.
     * @returns {Uint8Array}
     */
    get verifyingKey() {
        const ret = wasm.channelinvitesigner_verifyingKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ChannelInviteSigner.prototype[Symbol.dispose] = ChannelInviteSigner.prototype.free;

/**
 * A decoded directory listing. Every field is the owner's public statement;
 * verify the handle through the OPRF lookup before showing it as a link.
 */
export class ChannelListingRecord {
    static __wrap(ptr) {
        const obj = Object.create(ChannelListingRecord.prototype);
        obj.__wbg_ptr = ptr;
        ChannelListingRecordFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelListingRecordFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channellistingrecord_free(ptr, 0);
    }
    /**
     * The channel's 16-byte id.
     * @returns {Uint8Array}
     */
    get channelId() {
        const ret = wasm.channellistingrecord_channelId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The channel description (possibly empty).
     * @returns {string}
     */
    get description() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.channellistingrecord_description(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The canonical handle.
     * @returns {string}
     */
    get handle() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.channellistingrecord_handle(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The ISO 639 language.
     * @returns {string}
     */
    get language() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.channellistingrecord_language(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The channel name.
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.channellistingrecord_name(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The topic id; unknown reads as "Other".
     * @returns {number}
     */
    get topic() {
        const ret = wasm.channellistingrecord_topic(this.__wbg_ptr);
        return ret;
    }
    /**
     * Owner-clock UNIX milliseconds, a `bigint`.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.channellistingrecord_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) ChannelListingRecord.prototype[Symbol.dispose] = ChannelListingRecord.prototype.free;

/**
 * The owner signer of one channel. Only the account that created the
 * channel can derive it.
 */
export class ChannelOwnerSigner {
    static __wrap(ptr) {
        const obj = Object.create(ChannelOwnerSigner.prototype);
        obj.__wbg_ptr = ptr;
        ChannelOwnerSignerFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelOwnerSignerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelownersigner_free(ptr, 0);
    }
    /**
     * The `x-lakko-channel-owner-proof` header value for one request. `path`
     * is the service path without the query, `query` the raw query string
     * without `?`, `body` the exact bytes sent. Fresh nonce per call.
     * @param {string} method
     * @param {string} path
     * @param {string} query
     * @param {Uint8Array} body
     * @returns {string}
     */
    sign(method, path, query, body) {
        let deferred5_0;
        let deferred5_1;
        try {
            const ptr0 = passStringToWasm0(method, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len0 = WASM_VECTOR_LEN;
            const ptr1 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            const ptr2 = passStringToWasm0(query, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len2 = WASM_VECTOR_LEN;
            const ptr3 = passArray8ToWasm0(body, wasm.__wbindgen_malloc);
            const len3 = WASM_VECTOR_LEN;
            const ret = wasm.channelownersigner_sign(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
            deferred5_0 = ret[0];
            deferred5_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred5_0, deferred5_1, 1);
        }
    }
    /**
     * The 32-byte Ed25519 public key `POST /v1/channels` registers as
     * `owner_pubkey`.
     * @returns {Uint8Array}
     */
    verifyingKey() {
        const ret = wasm.channelownersigner_verifyingKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ChannelOwnerSigner.prototype[Symbol.dispose] = ChannelOwnerSigner.prototype.free;

/**
 * Decoded representation of a ChannelPost. JS treats this
 * as an opaque handle; the underlying field bytes are
 * accessible through dedicated getters so a future schema
 * bump can change the inner layout without breaking the
 * wire surface.
 */
export class ChannelPost {
    static __wrap(ptr) {
        const obj = Object.create(ChannelPost.prototype);
        obj.__wbg_ptr = ptr;
        ChannelPostFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelPostFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelpost_free(ptr, 0);
    }
    /**
     * Opaque ciphertext bytes (includes the 16-byte AEAD
     * tag).
     * @returns {Uint8Array}
     */
    get ciphertext() {
        const ret = wasm.channelpost_ciphertext(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `u32` epoch id the post was encrypted under.
     * @returns {number}
     */
    get epochId() {
        const ret = wasm.channelpost_epochId(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * `u64` monotone per-(channel, epoch, sender) counter.
     * Returned as a JS BigInt so values above 2^53 do not
     * lose precision.
     * @returns {bigint}
     */
    get messageCounter() {
        const ret = wasm.channelpost_messageCounter(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * 32-byte Ed25519 sender public key.
     * @returns {Uint8Array}
     */
    get senderPubkey() {
        const ret = wasm.channelpost_senderPubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * 64-byte Ed25519 signature.
     * @returns {Uint8Array}
     */
    get signature() {
        const ret = wasm.channelpost_signature(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Re-encode to the canonical wire bytes. Round-trip
     * of `decode → toBytes` is byte-identical (asserted
     * by `lakko-channels`'s own `wire_roundtrip` test).
     * @returns {Uint8Array}
     */
    toBytes() {
        const ret = wasm.channelpost_toBytes(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ChannelPost.prototype[Symbol.dispose] = ChannelPost.prototype.free;

/**
 * What a decrypted post plaintext turned out to be. JS reads `kind`
 * first, then the getters that kind populates: `text` for `"text"`;
 * `name` / `description` / `updatedAtMs` for `"meta"`; `error` for
 * `"malformed"`. Every other getter is `undefined`.
 */
export class ChannelPostContent {
    static __wrap(ptr) {
        const obj = Object.create(ChannelPostContent.prototype);
        obj.__wbg_ptr = ptr;
        ChannelPostContentFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelPostContentFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelpostcontent_free(ptr, 0);
    }
    /**
     * ADR-0087 section 18.2: the channel's photo when `kind === "meta"`.
     * Its keys are SECRET to the subscribers.
     * @returns {ProfileAvatar | undefined}
     */
    get avatar() {
        const ret = wasm.channelpostcontent_avatar(this.__wbg_ptr);
        return ret === 0 ? undefined : ProfileAvatar.__wrap(ret);
    }
    /**
     * ADR-0087 section 13a: the decoded ban list when
     * `kind === "ban-list"`. Never shown as a post.
     * @returns {BanList | undefined}
     */
    get banList() {
        const ret = wasm.channelpostcontent_banList(this.__wbg_ptr);
        return ret === 0 ? undefined : BanList.__wrap(ret);
    }
    /**
     * The channel description (possibly empty) when `kind === "meta"`.
     * @returns {string | undefined}
     */
    get description() {
        const ret = wasm.channelpostcontent_description(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The frozen `ChannelMetaError::code()` when `kind === "malformed"`.
     * @returns {string | undefined}
     */
    get error() {
        const ret = wasm.channelpostcontent_error(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * ADR-0087 section 3: the channel's `@name` when `kind === "meta"` and
     * the record carries one.
     * @returns {ConversationHandle | undefined}
     */
    get handle() {
        const ret = wasm.channelpostcontent_handle(this.__wbg_ptr);
        return ret === 0 ? undefined : ConversationHandle.__wrap(ret);
    }
    /**
     * `"text"` | `"meta"` | `"malformed"` | `"ban-list"`.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.channelpostcontent_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The ISO 639 language when `kind === "meta"`; empty = not set.
     * @returns {string | undefined}
     */
    get language() {
        const ret = wasm.channelpostcontent_language(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Whether the owner lists the channel in the directory, when
     * `kind === "meta"`. A member shares only listed channels in a group.
     * @returns {boolean | undefined}
     */
    get listed() {
        const ret = wasm.channelpostcontent_listed(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret !== 0;
    }
    /**
     * The channel name when `kind === "meta"`.
     * @returns {string | undefined}
     */
    get name() {
        const ret = wasm.channelpostcontent_name(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * ADR-0087 section 13a: which reactions the channel's posts take, when
     * `kind === "meta"`.
     * @returns {AllowedReactions | undefined}
     */
    get reactions() {
        const ret = wasm.channelpostcontent_reactions(this.__wbg_ptr);
        return ret === 0 ? undefined : AllowedReactions.__wrap(ret);
    }
    /**
     * The plaintext, verbatim, when `kind === "text"`.
     * @returns {Uint8Array | undefined}
     */
    get text() {
        const ret = wasm.channelpostcontent_text(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The topic id (ADR-0059 §2) when `kind === "meta"`; an unknown id
     * reads as "Other" (`channelTopicName`).
     * @returns {number | undefined}
     */
    get topic() {
        const ret = wasm.channelpostcontent_topic(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret;
    }
    /**
     * The record's stamp as a BigInt when `kind === "meta"`. A re-issue
     * MUST carry this value verbatim, never a fresh clock read.
     * @returns {bigint | undefined}
     */
    get updatedAtMs() {
        const ret = wasm.channelpostcontent_updatedAtMs(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
    }
}
if (Symbol.dispose) ChannelPostContent.prototype[Symbol.dispose] = ChannelPostContent.prototype.free;

/**
 * The reaction key of one channel epoch.
 */
export class ChannelReactionKey {
    static __wrap(ptr) {
        const obj = Object.create(ChannelReactionKey.prototype);
        obj.__wbg_ptr = ptr;
        ChannelReactionKeyFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelReactionKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelreactionkey_free(ptr, 0);
    }
    /**
     * The reaction key of `channelId` at `epochId`, from that epoch's seed.
     *
     * # Errors
     *
     * Throws `reaction.<code>: ...`.
     * @param {Uint8Array} epoch_seed
     * @param {Uint8Array} channel_id
     * @param {number} epoch_id
     * @returns {ChannelReactionKey}
     */
    static derive(epoch_seed, channel_id, epoch_id) {
        const ptr0 = passArray8ToWasm0(epoch_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.channelreactionkey_derive(ptr0, len0, ptr1, len1, epoch_id);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ChannelReactionKey.__wrap(ret[0]);
    }
    /**
     * The 16-byte label of `emoji` under the post at `postCursor`.
     *
     * # Errors
     *
     * Throws `reaction.invalidEmoji: ...`.
     * @param {bigint} post_cursor
     * @param {string} emoji
     * @returns {Uint8Array}
     */
    label(post_cursor, emoji) {
        const ptr0 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.channelreactionkey_label(this.__wbg_ptr, post_cursor, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Resolve the service's counts for the post at `postCursor`:
     * `labels` is the labels concatenated (16 bytes each) and `counts` the
     * count of each, in the same order. Returns the emoji that resolved,
     * in candidate order; read each one's count with the same index of
     * [`Self::resolve_count_values`].
     * @param {bigint} post_cursor
     * @param {string[]} candidates
     * @param {Uint8Array} labels
     * @param {BigUint64Array} counts
     * @returns {string[]}
     */
    resolveCountEmoji(post_cursor, candidates, labels, counts) {
        const ptr0 = passArrayJsValueToWasm0(candidates, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(labels, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray64ToWasm0(counts, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.channelreactionkey_resolveCountEmoji(this.__wbg_ptr, post_cursor, ptr0, len0, ptr1, len1, ptr2, len2);
        var v4 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v4;
    }
    /**
     * The counts that go with [`Self::resolve_count_emoji`], index for
     * index.
     * @param {bigint} post_cursor
     * @param {string[]} candidates
     * @param {Uint8Array} labels
     * @param {BigUint64Array} counts
     * @returns {BigUint64Array}
     */
    resolveCountValues(post_cursor, candidates, labels, counts) {
        const ptr0 = passArrayJsValueToWasm0(candidates, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(labels, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray64ToWasm0(counts, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.channelreactionkey_resolveCountValues(this.__wbg_ptr, post_cursor, ptr0, len0, ptr1, len1, ptr2, len2);
        var v4 = getArrayU64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v4;
    }
}
if (Symbol.dispose) ChannelReactionKey.prototype[Symbol.dispose] = ChannelReactionKey.prototype.free;

/**
 * The account's key for one reaction under one post.
 */
export class ChannelReactionWriteKey {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelReactionWriteKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelreactionwritekey_free(ptr, 0);
    }
    /**
     * The key of reaction `label` on the post at `postCursor`.
     *
     * # Errors
     *
     * Throws `write.badLength: ...`.
     * @param {Identity} account_identity
     * @param {Uint8Array} channel_id
     * @param {bigint} post_cursor
     * @param {Uint8Array} label
     */
    constructor(account_identity, channel_id, post_cursor, label) {
        _assertClass(account_identity, Identity);
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(label, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.channelreactionwritekey_new(account_identity.__wbg_ptr, ptr0, len0, post_cursor, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        ChannelReactionWriteKeyFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The 32-byte public half the reaction row names.
     * @returns {Uint8Array}
     */
    get publicKey() {
        const ret = wasm.channelreactionwritekey_publicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The write-proof header value for adding the reaction.
     * @returns {string}
     */
    signAdd() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.channelreactionwritekey_signAdd(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The write-proof header value for taking the reaction off.
     * @returns {string}
     */
    signDelete() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.channelreactionwritekey_signDelete(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) ChannelReactionWriteKey.prototype[Symbol.dispose] = ChannelReactionWriteKey.prototype.free;

/**
 * Ed25519 signing key wrapper for the publisher side.
 * Holds the same 32-byte seed `lakko_crypto::signing::
 * SigningKey` does; exposes a deterministic
 * `verifyingKey()` getter so JS can record the publisher
 * pubkey alongside the seed in local storage.
 */
export class ChannelSigningKey {
    static __wrap(ptr) {
        const obj = Object.create(ChannelSigningKey.prototype);
        obj.__wbg_ptr = ptr;
        ChannelSigningKeyFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelSigningKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelsigningkey_free(ptr, 0);
    }
    /**
     * Fresh random key. Backed by `crypto.getRandomValues`
     * on the wasm side via `getrandom + js` feature.
     * @returns {ChannelSigningKey}
     */
    static generate() {
        const ret = wasm.channelsigningkey_generate();
        return ChannelSigningKey.__wrap(ret);
    }
    /**
     * Reconstruct from a 32-byte seed (the form the
     * caller persists in their local key store).
     * @param {Uint8Array} seed
     */
    constructor(seed) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.channelsigningkey_new(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        ChannelSigningKeyFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Export the 32-byte seed for persistence.
     * Caller-side responsibility to keep this in a
     * secure store.
     * @returns {Uint8Array}
     */
    toSeed() {
        const ret = wasm.channelsigningkey_toSeed(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * 32-byte Ed25519 verifying key — the
     * `sender_pubkey` field of every post this key
     * composes.
     * @returns {Uint8Array}
     */
    verifyingKey() {
        const ret = wasm.channelsigningkey_verifyingKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ChannelSigningKey.prototype[Symbol.dispose] = ChannelSigningKey.prototype.free;

/**
 * The subscriber's key pair (ADR-0060: Ed25519). The public
 * half is the subscriber identifier everywhere - what gets
 * registered on the channel, the envelope and member-card
 * path key, and the key member-card requests are signed with.
 * The seed envelope is sealed to its X25519 image and opened
 * with the X25519 secret derived from the same seed. JS
 * treats this as an opaque handle; the seed never leaves the
 * boundary unless the caller explicitly invokes `secret()`
 * to persist it in their secure-store equivalent
 * (IndexedDB-encrypted on the web).
 */
export class ChannelSubscriberKeypair {
    static __wrap(ptr) {
        const obj = Object.create(ChannelSubscriberKeypair.prototype);
        obj.__wbg_ptr = ptr;
        ChannelSubscriberKeypairFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChannelSubscriberKeypairFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_channelsubscriberkeypair_free(ptr, 0);
    }
    /**
     * Fresh random Ed25519 key pair. Backed by
     * `crypto.getRandomValues` via the `getrandom + js`
     * feature.
     * @returns {ChannelSubscriberKeypair}
     */
    static generate() {
        const ret = wasm.channelsubscriberkeypair_generate();
        return ChannelSubscriberKeypair.__wrap(ret);
    }
    /**
     * The `memberCardSigHeaderName()` header value for
     * uploading `card` (`PUT .../members/<public hex>`) or,
     * with an empty `card`, deleting it: the Ed25519
     * signature over `lakko_channels::member_card_signed_bytes`,
     * base64url without padding. Throws on a wrong-length
     * channel id.
     * @param {Uint8Array} channel_id
     * @param {Uint8Array} card
     * @returns {string}
     */
    memberCardRequestSignature(channel_id, card) {
        let deferred4_0;
        let deferred4_1;
        try {
            const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
            const len0 = WASM_VECTOR_LEN;
            const ptr1 = passArray8ToWasm0(card, wasm.__wbindgen_malloc);
            const len1 = WASM_VECTOR_LEN;
            const ret = wasm.channelsubscriberkeypair_memberCardRequestSignature(this.__wbg_ptr, ptr0, len0, ptr1, len1);
            var ptr3 = ret[0];
            var len3 = ret[1];
            if (ret[3]) {
                ptr3 = 0; len3 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred4_0 = ptr3;
            deferred4_1 = len3;
            return getStringFromWasm0(ptr3, len3);
        } finally {
            wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
        }
    }
    /**
     * Reconstruct from the 32-byte Ed25519 seed `secret()`
     * exported.
     * @param {Uint8Array} secret
     */
    constructor(secret) {
        const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.channelsubscriberkeypair_new(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        ChannelSubscriberKeypairFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * 32-byte Ed25519 public key — the value the
     * subscriber registers on the channel.
     * @returns {Uint8Array}
     */
    public() {
        const ret = wasm.channelsubscriberkeypair_public(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Export the 32-byte Ed25519 seed for persistence.
     * Caller-side responsibility to keep it encrypted.
     * @returns {Uint8Array}
     */
    secret() {
        const ret = wasm.channelsubscriberkeypair_secret(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The `unsubscribeSigHeaderName()` header value for leaving the
     * channel (`DELETE /v1/channels/<id>/subscribers/<public hex>`,
     * ADR-0060): `<issuedAtMs>.<signature base64url>`, signed over
     * `lakko_channels::unsubscribe_signed_bytes`. Pass `Date.now()`; the
     * server accepts it within two minutes of its own clock. Throws on a
     * wrong-length channel id.
     * @param {Uint8Array} channel_id
     * @param {bigint} issued_at_ms
     * @returns {string}
     */
    unsubscribeRequestHeader(channel_id, issued_at_ms) {
        let deferred3_0;
        let deferred3_1;
        try {
            const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
            const len0 = WASM_VECTOR_LEN;
            const ret = wasm.channelsubscriberkeypair_unsubscribeRequestHeader(this.__wbg_ptr, ptr0, len0, issued_at_ms);
            var ptr2 = ret[0];
            var len2 = ret[1];
            if (ret[3]) {
                ptr2 = 0; len2 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred3_0 = ptr2;
            deferred3_1 = len2;
            return getStringFromWasm0(ptr2, len2);
        } finally {
            wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
        }
    }
}
if (Symbol.dispose) ChannelSubscriberKeypair.prototype[Symbol.dispose] = ChannelSubscriberKeypair.prototype.free;

/**
 * The chips under one message.
 */
export class ChipRow {
    static __wrap(ptr) {
        const obj = Object.create(ChipRow.prototype);
        obj.__wbg_ptr = ptr;
        ChipRowFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChipRowFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_chiprow_free(ptr, 0);
    }
    /**
     * The chips, in order.
     * @returns {ReactionChip[]}
     */
    get chips() {
        const ret = wasm.chiprow_chips(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * How many more kinds fold into a last `+N` chip.
     * @returns {number}
     */
    get overflowKinds() {
        const ret = wasm.chiprow_overflowKinds(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) ChipRow.prototype[Symbol.dispose] = ChipRow.prototype.free;

/**
 * One emoji's tally under a message.
 */
export class ChipSource {
    static __wrap(ptr) {
        const obj = Object.create(ChipSource.prototype);
        obj.__wbg_ptr = ptr;
        ChipSourceFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof ChipSource)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ChipSourceFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_chipsource_free(ptr, 0);
    }
    /**
     * How many people hold it.
     * @returns {number}
     */
    get count() {
        const ret = wasm.chipsource_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The emoji.
     * @returns {string}
     */
    get emoji() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.chipsource_emoji(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Whether this person holds it.
     * @returns {boolean}
     */
    get mine() {
        const ret = wasm.chipsource_mine(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Build a tally (`reactors` = 32-byte keys concatenated, may be empty).
     *
     * # Errors
     *
     * Throws `reaction.badLength`.
     * @param {string} emoji
     * @param {number} count
     * @param {bigint} first_at
     * @param {boolean} mine
     * @param {Uint8Array} reactors_flat
     */
    constructor(emoji, count, first_at, mine, reactors_flat) {
        const ptr0 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(reactors_flat, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.chipsource_new(ptr0, len0, count, first_at, mine, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        ChipSourceFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) ChipSource.prototype[Symbol.dispose] = ChipSource.prototype.free;

/**
 * The key of one epoch. It crosses as an object, never as bytes.
 */
export class CircleEpochKey {
    static __wrap(ptr) {
        const obj = Object.create(CircleEpochKey.prototype);
        obj.__wbg_ptr = ptr;
        CircleEpochKeyFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CircleEpochKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_circleepochkey_free(ptr, 0);
    }
    /**
     * The epoch.
     * @returns {bigint}
     */
    get epoch() {
        const ret = wasm.circleepochkey_epoch(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Open a filter sealed at this epoch.
     * @param {Uint8Array} wire
     * @returns {CircleFilter}
     */
    openFilter(wire) {
        const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.circleepochkey_openFilter(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return CircleFilter.__wrap(ret[0]);
    }
    /**
     * Open row `index`, sealed at this epoch, to the sealed card inside it.
     * @param {number} index
     * @param {Uint8Array} row
     * @returns {Uint8Array}
     */
    openRow(index, row) {
        const ptr0 = passArray8ToWasm0(row, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.circleepochkey_openRow(this.__wbg_ptr, index, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * The epoch key before this one, from this epoch's chain entry.
     * @param {Uint8Array} chain_wrap
     * @returns {CircleEpochKey}
     */
    previous(chain_wrap) {
        const ptr0 = passArray8ToWasm0(chain_wrap, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.circleepochkey_previous(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return CircleEpochKey.__wrap(ret[0]);
    }
    /**
     * The signer of reads at this epoch. Only the current epoch's is
     * accepted; an older key is for opening what it sealed.
     * @returns {DsProofSigner}
     */
    readSigner() {
        const ret = wasm.circleepochkey_readSigner(this.__wbg_ptr);
        return DsProofSigner.__wrap(ret);
    }
}
if (Symbol.dispose) CircleEpochKey.prototype[Symbol.dispose] = CircleEpochKey.prototype.free;

/**
 * An opened filter.
 */
export class CircleFilter {
    static __wrap(ptr) {
        const obj = Object.create(CircleFilter.prototype);
        obj.__wbg_ptr = ptr;
        CircleFilterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CircleFilterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_circlefilter_free(ptr, 0);
    }
    /**
     * The rows this term tag is on. Local. Never misses a stored term; a
     * false positive is a row that does not carry the term, which the caller
     * sees when it opens the card.
     * @param {Uint8Array} tag
     * @returns {Uint32Array}
     */
    lookup(tag) {
        const ptr0 = passArray8ToWasm0(tag, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.circlefilter_lookup(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * Row indices in use.
     * @returns {number}
     */
    get rowCount() {
        const ret = wasm.circlefilter_rowCount(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) CircleFilter.prototype[Symbol.dispose] = CircleFilter.prototype.free;

/**
 * A leaf given to a contact.
 */
export class CircleGrant {
    static __wrap(ptr) {
        const obj = Object.create(CircleGrant.prototype);
        obj.__wbg_ptr = ptr;
        CircleGrantFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CircleGrantFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_circlegrant_free(ptr, 0);
    }
    /**
     * The grant to send, once, in the one-to-one conversation. Secret.
     * @returns {Uint8Array}
     */
    get grant() {
        const ret = wasm.circlegrant_grant(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The contact's leaf. Keep it beside the contact: a removal names it.
     * @returns {number}
     */
    get leaf() {
        const ret = wasm.circlegrant_leaf(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) CircleGrant.prototype[Symbol.dispose] = CircleGrant.prototype.free;

/**
 * What a grant says, without its key.
 */
export class CircleGrantInfo {
    static __wrap(ptr) {
        const obj = Object.create(CircleGrantInfo.prototype);
        obj.__wbg_ptr = ptr;
        CircleGrantInfoFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CircleGrantInfoFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_circlegrantinfo_free(ptr, 0);
    }
    /**
     * The directory it opens.
     * @returns {Uint8Array}
     */
    get directoryId() {
        const ret = wasm.circlegrantinfo_directoryId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The leaf.
     * @returns {number}
     */
    get leaf() {
        const ret = wasm.circlegrantinfo_leaf(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) CircleGrantInfo.prototype[Symbol.dispose] = CircleGrantInfo.prototype.free;

/**
 * A decoded head.
 */
export class CircleHead {
    static __wrap(ptr) {
        const obj = Object.create(CircleHead.prototype);
        obj.__wbg_ptr = ptr;
        CircleHeadFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CircleHeadFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_circlehead_free(ptr, 0);
    }
    /**
     * The tree holds `1 << capacityLog` leaves.
     * @returns {number}
     */
    get capacityLog() {
        const ret = wasm.circlehead_capacityLog(this.__wbg_ptr);
        return ret;
    }
    /**
     * The current epoch.
     * @returns {bigint}
     */
    get epoch() {
        const ret = wasm.circlehead_epoch(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The current filter's version; 0 until one is written.
     * @returns {bigint}
     */
    get filterVersion() {
        const ret = wasm.circlehead_filterVersion(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The current read key.
     * @returns {Uint8Array}
     */
    get readPublicKey() {
        const ret = wasm.circlehead_readPublicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The directory version.
     * @returns {bigint}
     */
    get version() {
        const ret = wasm.circlehead_version(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) CircleHead.prototype[Symbol.dispose] = CircleHead.prototype.free;

/**
 * One write, ready to send.
 */
export class CircleWrite {
    static __wrap(ptr) {
        const obj = Object.create(CircleWrite.prototype);
        obj.__wbg_ptr = ptr;
        CircleWriteFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CircleWriteFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_circlewrite_free(ptr, 0);
    }
    /**
     * The JSON body. Send it again unchanged when the answer is lost.
     * @returns {string}
     */
    get bodyJson() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.circlewrite_bodyJson(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The epoch this ends on.
     * @returns {bigint}
     */
    get epoch() {
        const ret = wasm.circlewrite_epoch(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Whether this is the claim.
     * @returns {boolean}
     */
    get isClaim() {
        const ret = wasm.circlewrite_isClaim(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `PUT` for the claim, which also needs a `circle-claim` token, else
     * `POST`.
     * @returns {string}
     */
    get method() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.circlewrite_method(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The service path.
     * @returns {string}
     */
    get path() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.circlewrite_path(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The directory version this creates.
     * @returns {bigint}
     */
    get version() {
        const ret = wasm.circlewrite_version(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) CircleWrite.prototype[Symbol.dispose] = CircleWrite.prototype.free;

/**
 * The owner's side of a directory.
 */
export class CircleWriter {
    static __wrap(ptr) {
        const obj = Object.create(CircleWriter.prototype);
        obj.__wbg_ptr = ptr;
        CircleWriterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CircleWriterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_circlewriter_free(ptr, 0);
    }
    /**
     * Give a contact a free leaf.
     * @returns {CircleGrant}
     */
    addMember() {
        const ret = wasm.circlewriter_addMember(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return CircleGrant.__wrap(ret[0]);
    }
    /**
     * The tree holds `1 << capacityLog` leaves.
     * @returns {number}
     */
    get capacityLog() {
        const ret = wasm.circlewriter_capacityLog(this.__wbg_ptr);
        return ret;
    }
    /**
     * Queue the removal of row `index`.
     * @param {number} index
     */
    deleteRow(index) {
        const ret = wasm.circlewriter_deleteRow(this.__wbg_ptr, index);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * The directory id.
     * @returns {Uint8Array}
     */
    directoryId() {
        const ret = wasm.circlewriter_directoryId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The state to keep. Holds no key. Keep it beside other device-local
     * data: it says how many contacts the owner has.
     * @returns {Uint8Array}
     */
    encodeState() {
        const ret = wasm.circlewriter_encodeState(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The current epoch.
     * @returns {bigint}
     */
    get epoch() {
        const ret = wasm.circlewriter_epoch(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The current epoch key.
     * @returns {CircleEpochKey}
     */
    epochKey() {
        const ret = wasm.circlewriter_epochKey(this.__wbg_ptr);
        return CircleEpochKey.__wrap(ret);
    }
    /**
     * Whether anything is queued.
     * @returns {boolean}
     */
    hasPending() {
        const ret = wasm.circlewriter_hasPending(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The leaves assigned to contacts, ascending.
     * @returns {Uint32Array}
     */
    members() {
        const ret = wasm.circlewriter_members(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The owner's public key.
     * @returns {Uint8Array}
     */
    ownerPublicKey() {
        const ret = wasm.circlewriter_ownerPublicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The signer of this owner's requests, at `circleOwnerProofEpoch()`.
     * @returns {DsProofSigner}
     */
    ownerSigner() {
        const ret = wasm.circlewriter_ownerSigner(this.__wbg_ptr);
        return DsProofSigner.__wrap(ret);
    }
    /**
     * Queue a card as row `index`. Pass the bytes `circleSealCard` gave the
     * person the card describes: a plain card here would be readable by every
     * contact of this owner.
     * @param {number} index
     * @param {Uint8Array} card
     */
    putRow(index, card) {
        const ptr0 = passArray8ToWasm0(card, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.circlewriter_putRow(this.__wbg_ptr, index, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Remove the contacts at `leaves`, moving the epoch.
     * @param {Uint32Array} leaves
     */
    removeMembers(leaves) {
        const ptr0 = passArray32ToWasm0(leaves, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.circlewriter_removeMembers(this.__wbg_ptr, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Queue a new filter: `tags` concatenated, 32 bytes each, `rows[i]` going
     * with the `i`-th. `rowCount` is the number of row indices in use.
     * @param {Uint8Array} tags
     * @param {Uint32Array} rows
     * @param {number} row_count
     */
    setFilter(tags, rows, row_count) {
        const ptr0 = passArray8ToWasm0(tags, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray32ToWasm0(rows, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.circlewriter_setFilter(this.__wbg_ptr, ptr0, len0, ptr1, len1, row_count);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Seal what is queued into one write, under the current epoch. Call until
     * it returns `undefined`: a large change goes over several writes.
     * @returns {CircleWrite | undefined}
     */
    takeWrite() {
        const ret = wasm.circlewriter_takeWrite(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : CircleWrite.__wrap(ret[0]);
    }
    /**
     * The directory version the last write created.
     * @returns {bigint}
     */
    get version() {
        const ret = wasm.circlewriter_version(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) CircleWriter.prototype[Symbol.dispose] = CircleWriter.prototype.free;

/**
 * A proposer's committer delegations (ADR-0060 §6). Mirror of
 * `lakko_contacts::committer_delegation::CommitterDelegationEnvelope`.
 *
 * Under ADR-0045 the member who proposes an `Add` may not be the one who
 * commits it, and the added person's mailbox is closed. So the proposer
 * posts, in the group beside its proposal, one delegation per committer
 * candidate (the owner and each admin), each signed with the proposer's
 * reach key for the added person's mailbox (`signDelegationBlob`) and naming
 * that candidate's account signature key as delegate. A delegation lives at
 * most 24 hours: **re-send a fresh envelope while the proposal is pending**,
 * and whenever the candidate set changes.
 */
export class CommitterDelegationEnvelope {
    static __wrap(ptr) {
        const obj = Object.create(CommitterDelegationEnvelope.prototype);
        obj.__wbg_ptr = ptr;
        CommitterDelegationEnvelopeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        CommitterDelegationEnvelopeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_committerdelegationenvelope_free(ptr, 0);
    }
    /**
     * The added person's 32-byte account signature key.
     * @returns {Uint8Array}
     */
    get addedAccountKey() {
        const ret = wasm.committerdelegationenvelope_addedAccountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The added person's 97-byte mailbox binding (ADR-0078), not verified
     * here (`checkCommitterDelegations` verifies it).
     * @returns {Uint8Array}
     */
    get addedMailboxBinding() {
        const ret = wasm.committerdelegationenvelope_addedMailboxBinding(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The raw delegation blob at `index`, or `undefined` past the end.
     * @param {number} index
     * @returns {Uint8Array | undefined}
     */
    delegation(index) {
        const ret = wasm.committerdelegationenvelope_delegation(this.__wbg_ptr, index);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * How many delegations.
     * @returns {number}
     */
    delegationCount() {
        const ret = wasm.committerdelegationenvelope_delegationCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Build an envelope from the added person's 32-byte account signature
     * key, their 97-byte mailbox binding (ADR-0078; the `binding` of the
     * grant the proposer holds for them) and the raw 185-byte delegation
     * blobs (`Uint8Array[]`).
     *
     * # Errors
     *
     * Throws with `reachError` `keyLength`, or `committerDelegationError`
     * `badBinding` / `delegationMalformed` for a blob that is not 185 bytes.
     * @param {Uint8Array} added_account_key
     * @param {Uint8Array} added_mailbox_binding
     * @param {Uint8Array[]} delegations
     */
    constructor(added_account_key, added_mailbox_binding, delegations) {
        const ptr0 = passArray8ToWasm0(added_account_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(added_mailbox_binding, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArrayJsValueToWasm0(delegations, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.committerdelegationenvelope_new(ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        CommitterDelegationEnvelopeFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) CommitterDelegationEnvelope.prototype[Symbol.dispose] = CommitterDelegationEnvelope.prototype.free;

/**
 * Validated `ContactAck`. Mirror of [`lakko_contacts::ContactAck`].
 *
 * Senders call [`ContactAck::compose`] to mint the wire bytes
 * they publish to the sender's mailbox (the per-relationship
 * pseudonym from the original `ContactRequest.fromPseudonym`).
 * Recipients (= the original request's sender) call
 * [`ContactAck::decodeAndVerify`] to validate the signature +
 * structure, then cross-check the `accepterPubkey` against
 * the recipient pubkey they recorded when composing the
 * original request.
 */
export class ContactAck {
    static __wrap(ptr) {
        const obj = Object.create(ContactAck.prototype);
        obj.__wbg_ptr = ptr;
        ContactAckFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ContactAckFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_contactack_free(ptr, 0);
    }
    /**
     * UNIX milliseconds at which the ack was minted.
     * @returns {bigint}
     */
    get acceptedAtMs() {
        const ret = wasm.contactack_acceptedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Accepter's UUIDv7 account id (16 bytes).
     * @returns {Uint8Array}
     */
    get accepterAccountId() {
        const ret = wasm.contactack_accepterAccountId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Accepter's Ed25519 identity public key (32 bytes).
     * @returns {Uint8Array}
     */
    get accepterPubkey() {
        const ret = wasm.contactack_accepterPubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Accepter's X25519 HPKE init key (32 bytes).
     * @returns {Uint8Array}
     */
    get accepterX25519Pubkey() {
        const ret = wasm.contactack_accepterX25519Pubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 32-byte per-relationship pseudonym from the original
     * `ContactRequest.fromPseudonym`. Sender's poll layer matches
     * this against the pending-sent table to flip the local UI
     * state from "pending" to "accepted".
     * @returns {Uint8Array}
     */
    get ackToPseudonym() {
        const ret = wasm.contactack_ackToPseudonym(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Compose a signed `ContactAck` and return the wire bytes
     * ready to deliver to the original requester's mailbox.
     * `ackToPseudonym` is the same 32-byte pseudonym the
     * original `ContactRequest.fromPseudonym` carried.
     *
     * # Errors
     *
     * `JsError` on wrong-length seed / account-id / x25519-pub /
     * pseudonym, or on a malformed X25519 public key encoding.
     * @param {Uint8Array} accepter_signing_seed
     * @param {Uint8Array} accepter_account_id
     * @param {Uint8Array} accepter_x25519_pub
     * @param {Uint8Array} ack_to_pseudonym
     * @param {bigint} accepted_at_ms
     * @returns {Uint8Array}
     */
    static compose(accepter_signing_seed, accepter_account_id, accepter_x25519_pub, ack_to_pseudonym, accepted_at_ms) {
        const ptr0 = passArray8ToWasm0(accepter_signing_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(accepter_account_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(accepter_x25519_pub, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArray8ToWasm0(ack_to_pseudonym, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        const ret = wasm.contactack_compose(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, accepted_at_ms);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v5;
    }
    /**
     * Recipient-side: decode the wire bytes, verify the Ed25519
     * signature, return the validated `ContactAck`.
     * @param {Uint8Array} wire
     * @returns {ContactAck}
     */
    static decodeAndVerify(wire) {
        const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.contactack_decodeAndVerify(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ContactAck.__wrap(ret[0]);
    }
}
if (Symbol.dispose) ContactAck.prototype[Symbol.dispose] = ContactAck.prototype.free;

/**
 * Composed and verified ContactRequest. Mirror of
 * [`lakko_contacts::ContactRequest`].
 *
 * Senders use [`ContactRequest::compose`] to produce wire bytes;
 * recipients use [`ContactRequest::decode_and_verify`] to validate
 * and then [`ContactRequest::open_envelope`] (post-accept) to
 * recover the intro plaintext.
 */
export class ContactRequest {
    static __wrap(ptr) {
        const obj = Object.create(ContactRequest.prototype);
        obj.__wbg_ptr = ptr;
        ContactRequestFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ContactRequestFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_contactrequest_free(ptr, 0);
    }
    /**
     * Compose a signed ContactRequest. Returns the wire bytes ready
     * to deliver to the recipient's stranger-inbox via
     * `services/ds`. `senderSigningSeed` is a 32-byte Ed25519 seed
     * (see `Identity` in the MLS surface for one already wired up
     * to the device key); `recipientX25519Pub` is the recipient's
     * X25519 public key bytes.
     *
     * `profileEnvelope` is the sender's profile (ADR-0048) as produced by
     * `composeProfileEnvelope`, or an EMPTY array for no profile. It is
     * decoded and bounds-checked before sealing, so a recipient never
     * receives a profile it would drop.
     *
     * `replyGrant` (ADR-0060 §6) is the sender's `reply` reach grant, sealed
     * beside the profile so the recipient can write the accept and the ack
     * back to the sender's closed mailbox: `deriveReachGrant(accountSeed,
     * ReachClass.Reply, replyId, 0)` over a fresh random 16-byte `replyId`,
     * its public key registered with a 7-day expiry BEFORE sending and
     * removed once the request is answered. `undefined` only when the
     * sender's mailbox is open to everyone. A cold-start call join (a
     * `wrapCallKeyPackage` / `wrapCallWelcome` intro) carries it too. The
     * `ReachGrant` handle is consumed: read `verifyingKey()` first.
     *
     * `linkAnswerId` and `linkAnswerSecret` (ADR-0079 §2) name the `/i/`
     * link the request is sent from: the capability's `linkId` (16 bytes)
     * and the link secret from the URL fragment (12 bytes). Pass two EMPTY
     * arrays for a request that does not come from an `/i/` link.
     *
     * # Errors
     *
     * Throws on any input-shape or AEAD failure, a reply grant of any
     * class but `Reply`, or a link answer of the wrong width; throws an
     * `Error` with a `profileError` code when `profileEnvelope` is
     * non-empty and invalid.
     * @param {Uint8Array} sender_signing_seed
     * @param {Uint8Array} sender_account_id
     * @param {Uint8Array} recipient_x25519_pub
     * @param {Uint8Array} from_pseudonym
     * @param {bigint} valid_after_ms
     * @param {Uint8Array} privacy_pass_token
     * @param {Uint8Array} profile_envelope
     * @param {ReachGrant | null | undefined} reply_grant
     * @param {Uint8Array} link_answer_id
     * @param {Uint8Array} link_answer_secret
     * @param {Uint8Array} intro_message
     * @returns {Uint8Array}
     */
    static compose(sender_signing_seed, sender_account_id, recipient_x25519_pub, from_pseudonym, valid_after_ms, privacy_pass_token, profile_envelope, reply_grant, link_answer_id, link_answer_secret, intro_message) {
        const ptr0 = passArray8ToWasm0(sender_signing_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(sender_account_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(recipient_x25519_pub, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArray8ToWasm0(from_pseudonym, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        const ptr4 = passArray8ToWasm0(privacy_pass_token, wasm.__wbindgen_malloc);
        const len4 = WASM_VECTOR_LEN;
        const ptr5 = passArray8ToWasm0(profile_envelope, wasm.__wbindgen_malloc);
        const len5 = WASM_VECTOR_LEN;
        let ptr6 = 0;
        if (!isLikeNone(reply_grant)) {
            _assertClass(reply_grant, ReachGrant);
            ptr6 = reply_grant.__destroy_into_raw();
        }
        const ptr7 = passArray8ToWasm0(link_answer_id, wasm.__wbindgen_malloc);
        const len7 = WASM_VECTOR_LEN;
        const ptr8 = passArray8ToWasm0(link_answer_secret, wasm.__wbindgen_malloc);
        const len8 = WASM_VECTOR_LEN;
        const ptr9 = passArray8ToWasm0(intro_message, wasm.__wbindgen_malloc);
        const len9 = WASM_VECTOR_LEN;
        const ret = wasm.contactrequest_compose(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, valid_after_ms, ptr4, len4, ptr5, len5, ptr6, ptr7, len7, ptr8, len8, ptr9, len9);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v11 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v11;
    }
    /**
     * Recipient-side: decode wire bytes, verify the Ed25519
     * signature, return the validated `ContactRequest`.
     * @param {Uint8Array} wire
     * @returns {ContactRequest}
     */
    static decodeAndVerify(wire) {
        const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.contactrequest_decodeAndVerify(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ContactRequest.__wrap(ret[0]);
    }
    /**
     * Sender's `account_id` (16-byte UUIDv7) — present for v2
     * envelopes, `null`/`undefined` for legacy v1 envelopes.
     * `Phase 1.16e3b` always sends v2; v1 surfaces only when
     * the sender pre-dates the deployment upgrade.
     * @returns {Uint8Array | undefined}
     */
    get fromAccountId() {
        const ret = wasm.contactrequest_fromAccountId(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Per-recipient pseudonym chosen by the sender (32 bytes).
     * @returns {Uint8Array}
     */
    get fromPseudonym() {
        const ret = wasm.contactrequest_fromPseudonym(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Sender's identity public key (32-byte Ed25519 verifying key).
     * @returns {Uint8Array}
     */
    get fromPubkey() {
        const ret = wasm.contactrequest_fromPubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Decrypt the sealed envelope using the recipient's 32-byte X25519
     * private key. Returns an `OpenedContactRequest` carrying the
     * sender's profile (or `undefined`) and the intro message.
     *
     * The profile is the sender's **claim**, bound to `fromPubkey` (which
     * the request signature authenticates): store it under that key and
     * show its username only after verifying it through discovery
     * (ADR-0048 §3). A profile that fails its bounds arrives as
     * `undefined` with the intro intact.
     * @param {Uint8Array} recipient_x25519_priv
     * @returns {OpenedContactRequest}
     */
    openEnvelope(recipient_x25519_priv) {
        const ptr0 = passArray8ToWasm0(recipient_x25519_priv, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.contactrequest_openEnvelope(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OpenedContactRequest.__wrap(ret[0]);
    }
    /**
     * Privacy Pass / App-Attest / Play-Integrity attestation
     * blob the sender attached (Phase 1.9b2 — RFC 9576). Empty
     * `Uint8Array` means "no token" — either composed without
     * one (web / dev path) or a legacy v1/v2 envelope that
     * pre-dates this field. The DS ingest path is the authority
     * on what the token means (rate-limit tier lift); the local
     * client surfaces the raw bytes here for transparency.
     * @returns {Uint8Array}
     */
    get privacyPassToken() {
        const ret = wasm.contactrequest_privacyPassToken(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * UNIX milliseconds at which the request becomes valid (sender's
     * freshness anchor — the application layer enforces an absolute
     * staleness ceiling on top of this).
     * @returns {bigint}
     */
    get validAfterMs() {
        const ret = wasm.contactrequest_validAfterMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) ContactRequest.prototype[Symbol.dispose] = ContactRequest.prototype.free;

/**
 * The contacts of all contacts, or only of the chosen ones. Numeric values
 * are the frozen wire bytes.
 * @enum {1 | 2}
 */
export const ContactsForm = Object.freeze({
    /**
     * The contacts of every contact the person has.
     */
    All: 1, "1": "All",
    /**
     * Only the contacts of the ones in the chosen list.
     */
    Chosen: 2, "2": "Chosen",
});

/**
 * How this account's membership of a conversation stands, stamped (ADR-0087
 * section 9). Build it with `SyncedState.composeConversationEnded` /
 * `composeConversationJoined`.
 */
export class ConversationEndRegister {
    static __wrap(ptr) {
        const obj = Object.create(ConversationEndRegister.prototype);
        obj.__wbg_ptr = ptr;
        ConversationEndRegisterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ConversationEndRegisterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_conversationendregister_free(ptr, 0);
    }
    /**
     * The conversation.
     * @returns {FolderConversationRef}
     */
    get conversation() {
        const ret = wasm.conversationendregister_conversation(this.__wbg_ptr);
        return FolderConversationRef.__wrap(ret);
    }
    /**
     * `true` for an end, `false` for a join after one.
     * @returns {boolean}
     */
    get ended() {
        const ret = wasm.conversationendregister_ended(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The MLS epoch of the end or the join; `0n` for a channel. The fold
     * orders by this before the stamp.
     * @returns {bigint}
     */
    get epoch() {
        const ret = wasm.conversationendregister_epoch(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * HLC stamp of this register.
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.conversationendregister_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) ConversationEndRegister.prototype[Symbol.dispose] = ConversationEndRegister.prototype.free;

/**
 * A group's (or, inside its metadata record, a channel's) `@name`, its
 * public / private flag and the handle key's signature (ADR-0087 section 3).
 */
export class ConversationHandle {
    static __wrap(ptr) {
        const obj = Object.create(ConversationHandle.prototype);
        obj.__wbg_ptr = ptr;
        ConversationHandleFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ConversationHandleFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_conversationhandle_free(ptr, 0);
    }
    /**
     * The 169-byte discovery handle certificate.
     * @returns {Uint8Array}
     */
    get certificate() {
        const ret = wasm.conversationhandle_certificate(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The canonical `@name`, without `@`.
     * @returns {string}
     */
    get handle() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.conversationhandle_handle(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * `true` for a public group or channel.
     * @returns {boolean}
     */
    get public() {
        const ret = wasm.conversationhandle_public(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Build and sign with the 32-byte handle seed for `groupId` (a channel:
     * its PublisherGroup id).
     *
     * # Errors
     *
     * Throws `conversationHandle.<code>: ...`.
     * @param {Uint8Array} handle_seed
     * @param {Uint8Array} group_id
     * @param {bigint} updated_at_ms
     * @param {boolean} _public
     * @param {string} handle
     * @param {Uint8Array} certificate
     * @returns {ConversationHandle}
     */
    static sign(handle_seed, group_id, updated_at_ms, _public, handle, certificate) {
        const ptr0 = passArray8ToWasm0(handle_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(handle, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArray8ToWasm0(certificate, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        const ret = wasm.conversationhandle_sign(ptr0, len0, ptr1, len1, updated_at_ms, _public, ptr2, len2, ptr3, len3);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ConversationHandle.__wrap(ret[0]);
    }
    /**
     * Owner-clock UNIX milliseconds of the change.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.conversationhandle_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Verify the signature for `groupId` under the 32-byte `handleKey` (the
     * write key the verified certificate names).
     *
     * # Errors
     *
     * Throws `conversationHandle.badSignature: ...` (or a length code).
     * @param {Uint8Array} group_id
     * @param {Uint8Array} handle_key
     */
    verify(group_id, handle_key) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(handle_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.conversationhandle_verify(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
}
if (Symbol.dispose) ConversationHandle.prototype[Symbol.dispose] = ConversationHandle.prototype.free;

/**
 * Which screen a conversation row opens.
 *
 * Carried so a client can route a tap, and for nothing else. The list itself
 * says nothing about which kind a row is — no badge, no label, no differing
 * avatar — because a person who subscribed to a channel knows they subscribed
 * to a channel.
 * @enum {0 | 1 | 2}
 */
export const ConversationKind = Object.freeze({
    /**
     * A one-to-one conversation.
     */
    Chat: 0, "0": "Chat",
    /**
     * A group conversation.
     */
    Group: 1, "1": "Group",
    /**
     * A channel this person subscribes to.
     */
    Channel: 2, "2": "Channel",
});

/**
 * The explicit conversation order, stamped.
 */
export class ConversationOrderRegister {
    static __wrap(ptr) {
        const obj = Object.create(ConversationOrderRegister.prototype);
        obj.__wbg_ptr = ptr;
        ConversationOrderRegisterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ConversationOrderRegisterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_conversationorderregister_free(ptr, 0);
    }
    /**
     * Group ids, top first.
     * @returns {Uint8Array[]}
     */
    groupIds() {
        const ret = wasm.conversationorderregister_groupIds(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * HLC stamp.
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.conversationorderregister_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) ConversationOrderRegister.prototype[Symbol.dispose] = ConversationOrderRegister.prototype.free;

/**
 * One row of the merged list, reduced to what the order reads.
 *
 * Deliberately far smaller than the row the web client actually draws: the
 * order reads six fields and nothing else, so an avatar, a preview, an unread
 * count and a handle to open all stay on the JS side where they already are.
 *
 * A constructible class rather than six parallel arrays, following
 * [`crate::contacts::BeaconObservation`] — the house shape for a small record
 * crossing this boundary. Parallel arrays would let the lengths diverge
 * silently and would need a sentinel for the absent timestamp that
 * `undefined` already expresses.
 */
export class ConversationRow {
    static __unwrap(jsValue) {
        if (!(jsValue instanceof ConversationRow)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ConversationRowFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_conversationrow_free(ptr, 0);
    }
    /**
     * Archived by this person.
     * @returns {boolean}
     */
    get archived() {
        const ret = wasm.conversationrow_archived(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Something unread in it, or marked unread by hand.
     * @returns {boolean}
     */
    get hasUnread() {
        const ret = wasm.conversationrow_hasUnread(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Stable within a kind; `(kind, id)` is unique in the list.
     * @returns {string}
     */
    get id() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.conversationrow_id(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Which screen the row opens.
     * @returns {ConversationKind}
     */
    get kind() {
        const ret = wasm.conversationrow_kind(this.__wbg_ptr);
        return ret;
    }
    /**
     * When the last thing a person can see happened, or `undefined`.
     * @returns {bigint | undefined}
     */
    get lastActivityMs() {
        const ret = wasm.conversationrow_lastActivityMs(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : ret[1];
    }
    /**
     * Muted by this person.
     * @returns {boolean}
     */
    get muted() {
        const ret = wasm.conversationrow_muted(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Build one row from what the client already holds.
     *
     * `lastActivityMs` is when the last thing a person can SEE happened, in
     * unix milliseconds (BigInt on the JS side), or `undefined` for a
     * conversation nothing has happened in yet. A row that draws nothing does
     * not move a conversation to the top: a signal nobody sees is not
     * activity.
     *
     * `muted` and `hasUnread` (ADR-0058) are read only by the folder rule;
     * both are optional and default to `false`, so a six-argument call
     * stays valid. `hasUnread` means what the unread badge means: something
     * unread, or a manual "mark as unread".
     * @param {string} id
     * @param {ConversationKind} kind
     * @param {string} title
     * @param {bigint | null | undefined} last_activity_ms
     * @param {boolean} pinned
     * @param {boolean} archived
     * @param {boolean | null} [muted]
     * @param {boolean | null} [has_unread]
     */
    constructor(id, kind, title, last_activity_ms, pinned, archived, muted, has_unread) {
        const ptr0 = passStringToWasm0(id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(title, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.conversationrow_new(ptr0, len0, kind, ptr1, len1, !isLikeNone(last_activity_ms), isLikeNone(last_activity_ms) ? BigInt(0) : last_activity_ms, pinned, archived, isLikeNone(muted) ? 0xFFFFFF : muted ? 1 : 0, isLikeNone(has_unread) ? 0xFFFFFF : has_unread ? 1 : 0);
        this.__wbg_ptr = ret;
        ConversationRowFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Pinned by this person.
     * @returns {boolean}
     */
    get pinned() {
        const ret = wasm.conversationrow_pinned(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * What the row is called. Read only to break a tie.
     * @returns {string}
     */
    get title() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.conversationrow_title(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) ConversationRow.prototype[Symbol.dispose] = ConversationRow.prototype.free;

/**
 * Decoded pairing QR — returned by [`decode_pairing_qr`].
 * Exposes the two byte arrays the scanner side needs to drive
 * the add-device flow: the joining device's KeyPackage (feed
 * to `DeviceGroup.addDeviceFromWire`) and the rendezvous
 * pseudonym (the DS mailbox slot to PUT the Welcome at).
 */
export class DecodedPairingQr {
    static __wrap(ptr) {
        const obj = Object.create(DecodedPairingQr.prototype);
        obj.__wbg_ptr = ptr;
        DecodedPairingQrFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DecodedPairingQrFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_decodedpairingqr_free(ptr, 0);
    }
    /**
     * The new device's ADR-0080 device card, decoded, when the payload
     * carries one that decodes; else `undefined` (always on a rejoin knock,
     * and for a card that does not decode — never an error). Display only:
     * it is what the new device says about itself and nothing trusts it.
     * @returns {DeviceCard | undefined}
     */
    get deviceCard() {
        const ret = wasm.decodedpairingqr_deviceCard(this.__wbg_ptr);
        return ret === 0 ? undefined : DeviceCard.__wrap(ret);
    }
    /**
     * TLS-encoded `KeyPackage` for the joining device.
     * @returns {Uint8Array}
     */
    get keyPackage() {
        const ret = wasm.decodedpairingqr_keyPackage(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * 32-byte rendezvous pseudonym for the DS-mailbox
     * Welcome delivery slot.
     * @returns {Uint8Array}
     */
    get rendezvousPseudonym() {
        const ret = wasm.decodedpairingqr_rendezvousPseudonym(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * ADR-0081 amendment — on a DeviceGroup rejoin knock, the rejoining
     * device's self-removal proof (137 bytes, not verified here), else
     * `undefined`.
     * @returns {Uint8Array | undefined}
     */
    get selfRemovalProof() {
        const ret = wasm.decodedpairingqr_selfRemovalProof(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
}
if (Symbol.dispose) DecodedPairingQr.prototype[Symbol.dispose] = DecodedPairingQr.prototype.free;

/**
 * One delegation, decoded (structure only; the Delivery Service checks the
 * signature and the expiry when it is used).
 */
export class Delegation {
    static __wrap(ptr) {
        const obj = Object.create(Delegation.prototype);
        obj.__wbg_ptr = ptr;
        DelegationFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DelegationFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_delegation_free(ptr, 0);
    }
    /**
     * The 32-byte key that signs the delegated request.
     * @returns {Uint8Array}
     */
    get delegatePk() {
        const ret = wasm.delegation_delegatePk(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * When the delegation stops being honoured, unix ms (a `bigint`).
     * @returns {bigint}
     */
    get expiresAtMs() {
        const ret = wasm.delegation_expiresAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The `x-lakko-ds-delegation` header value.
     * @returns {string}
     */
    headerValue() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.delegation_headerValue(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The 32-byte reach public key that signed the delegation.
     * @returns {Uint8Array}
     */
    get issuerPk() {
        const ret = wasm.delegation_issuerPk(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 32-byte mailbox address the write may go to (ADR-0078).
     * @returns {Uint8Array}
     */
    get mailbox() {
        const ret = wasm.delegation_mailbox(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 16-byte nonce the DS consumes on use.
     * @returns {Uint8Array}
     */
    get nonce() {
        const ret = wasm.delegation_nonce(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) Delegation.prototype[Symbol.dispose] = Delegation.prototype.free;

/**
 * A device card. Every field is the sending device's own statement.
 */
export class DeviceCard {
    static __wrap(ptr) {
        const obj = Object.create(DeviceCard.prototype);
        obj.__wbg_ptr = ptr;
        DeviceCardFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DeviceCardFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_devicecard_free(ptr, 0);
    }
    /**
     * App version.
     * @returns {string}
     */
    get appVersion() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.devicecard_appVersion(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * City, or empty.
     * @returns {string}
     */
    get city() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.devicecard_city(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Two-letter country code, or empty.
     * @returns {string}
     */
    get country() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.devicecard_country(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Sender-clock UNIX milliseconds of the person's last action there.
     * @returns {bigint}
     */
    get lastActiveMs() {
        const ret = wasm.devicecard_lastActiveMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Model or browser name.
     * @returns {string}
     */
    get model() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.devicecard_model(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Build a card. `platform` is a stable name; `updatedAtMs` and
     * `lastActiveMs` are `bigint` UNIX milliseconds. Strings are taken as
     * given; `composeDeviceCardEnvelope` normalizes them.
     *
     * # Errors
     *
     * `"unknownPlatform: ..."` for a platform name outside the set.
     * @param {bigint} updated_at_ms
     * @param {bigint} last_active_ms
     * @param {string} platform
     * @param {string} model
     * @param {string} app_version
     * @param {string} os
     * @param {string} city
     * @param {string} country
     */
    constructor(updated_at_ms, last_active_ms, platform, model, app_version, os, city, country) {
        const ptr0 = passStringToWasm0(platform, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(model, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(app_version, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passStringToWasm0(os, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len3 = WASM_VECTOR_LEN;
        const ptr4 = passStringToWasm0(city, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len4 = WASM_VECTOR_LEN;
        const ptr5 = passStringToWasm0(country, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len5 = WASM_VECTOR_LEN;
        const ret = wasm.devicecard_new(updated_at_ms, last_active_ms, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, ptr5, len5);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        DeviceCardFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The same card with every string made admissible.
     * @returns {DeviceCard}
     */
    normalized() {
        const ret = wasm.devicecard_normalized(this.__wbg_ptr);
        return DeviceCard.__wrap(ret);
    }
    /**
     * System name and version.
     * @returns {string}
     */
    get os() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.devicecard_os(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The platform's stable name.
     * @returns {string}
     */
    get platform() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.devicecard_platform(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Sender-clock UNIX milliseconds of the card; the order key.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.devicecard_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) DeviceCard.prototype[Symbol.dispose] = DeviceCard.prototype.free;

/**
 * Per-user multi-device MLS group (ADR-0007). Mirror of
 * UniFFI's `DeviceGroup`.
 *
 * FFI subset for 2a3b's add-device flow:
 *
 *   * `createFounder` — bootstrap on this device.
 *   * `joinFromWelcome` — join from a Welcome the scanner
 *     produced + delivered via DS mailbox.
 *   * `addDeviceFromWire` — add a fresh device by its
 *     KeyPackage; returns the (commit, welcome) pair.
 *   * `groupId` — the deterministic
 *     `BLAKE3-derive-key("lakko/device-group/id/v2", account_seed)`
 *     (ADR-0078 section 6).
 *
 * 2a3c widens this with `devices` + `removeDevice` once the
 * per-platform UI lands.
 */
export class DeviceGroup {
    static __wrap(ptr) {
        const obj = Object.create(DeviceGroup.prototype);
        obj.__wbg_ptr = ptr;
        DeviceGroupFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DeviceGroupFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_devicegroup_free(ptr, 0);
    }
    /**
     * Add a device by its TLS-encoded KeyPackage. Returns
     * `(commit, welcome)`: broadcast `commit` to existing
     * members via the DS group journal, deliver `welcome` to
     * the joining device via DS mailbox at the rendezvous
     * pseudonym.
     * @param {Identity} identity
     * @param {Uint8Array} key_package
     * @returns {AddMember}
     */
    addDeviceFromWire(identity, key_package) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_addDeviceFromWire(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AddMember.__wrap(ret[0]);
    }
    /**
     * ADR-0081 amendment — `true` when a device leaf carries the leaf node
     * of `keyPackage`: a rejoin knock with it was already admitted (the
     * roster's keys cannot tell, the replaced leaf has the same key).
     * @param {Identity} identity
     * @param {Uint8Array} key_package
     * @returns {boolean}
     */
    containsLeafOfKeyPackage(identity, key_package) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_containsLeafOfKeyPackage(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Compose a `Draft` cross-device payload. Empty `text`
     * clears the receiver's local draft for the same group.
     * @param {Identity} identity
     * @param {Uint8Array} group_id
     * @param {string} text
     * @param {Uint8Array} reply_to_id
     * @param {bigint} hlc
     * @returns {Uint8Array}
     */
    createDeviceGroupDraftMessage(identity, group_id, text, reply_to_id, hlc) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(reply_to_id, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_createDeviceGroupDraftMessage(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, hlc);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v4;
    }
    /**
     * Compose a `Flags` cross-device payload (pin / mute /
     * archive / unread-override snapshot — last-write-wins by
     * HLC).
     * @param {Identity} identity
     * @param {Uint8Array} group_id
     * @param {boolean} pinned
     * @param {boolean} muted
     * @param {boolean} archived
     * @param {boolean} unread_override
     * @param {bigint} hlc
     * @returns {Uint8Array}
     */
    createDeviceGroupFlagsMessage(identity, group_id, pinned, muted, archived, unread_override, hlc) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_createDeviceGroupFlagsMessage(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, pinned, muted, archived, unread_override, hlc);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Compose a `FolderChanges` cross-device payload (ADR-0058): a live
     * chat-folder edit carrying only the changed registers, each with its
     * own stamp. Also how a Save / Remove of a saved message is sent
     * (ADR-0065 section 4: `FolderChangeSet.addSaved` /
     * `addSavedRemoved`). `hlc` is the message stamp and is ignored by the
     * fold for this kind. Throws when the change set breaks an ADR-0058 bound, so an
     * out-of-bounds edit is never sent. Fold the same change set locally
     * with `SyncedState.applyFolderChanges`.
     * @param {Identity} identity
     * @param {FolderChangeSet} changes
     * @param {bigint} hlc
     * @returns {Uint8Array}
     */
    createDeviceGroupFolderChangesMessage(identity, changes, hlc) {
        _assertClass(identity, Identity);
        _assertClass(changes, FolderChangeSet);
        const ret = wasm.devicegroup_createDeviceGroupFolderChangesMessage(this.__wbg_ptr, identity.__wbg_ptr, changes.__wbg_ptr, hlc);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Compose an `Order` cross-device payload — explicit
     * display ordering for the conversation list. `groupIds`
     * is the flat concatenation of all group_ids; the lengths
     * are encoded inline via the leading-length prefix
     * convention `len(u32 LE) || bytes`. Callers should
     * prefer the `Vec<Vec<u8>>` shape; wasm-bindgen flattens
     * it via a JS `Uint8Array[]`.
     * @param {Identity} identity
     * @param {Uint8Array[]} group_ids
     * @param {bigint} hlc
     * @returns {Uint8Array}
     */
    createDeviceGroupOrderMessage(identity, group_ids, hlc) {
        _assertClass(identity, Identity);
        const ptr0 = passArrayJsValueToWasm0(group_ids, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_createDeviceGroupOrderMessage(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, hlc);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Compose a `ReadCursor` cross-device payload + return the
     * encrypted wire bytes ready to PUT to the DeviceGroup DS
     * journal (Phase 2a3e2). `groupId` / `upToMessageId` are
     * raw bytes; `readAtMs` / `hlc` are unix-millis / HLC u64s.
     * @param {Identity} identity
     * @param {Uint8Array} group_id
     * @param {Uint8Array} up_to_message_id
     * @param {bigint} read_at_ms
     * @param {bigint} hlc
     * @returns {Uint8Array}
     */
    createDeviceGroupReadCursorMessage(identity, group_id, up_to_message_id, read_at_ms, hlc) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(up_to_message_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_createDeviceGroupReadCursorMessage(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, ptr1, len1, read_at_ms, hlc);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v3;
    }
    /**
     * Compose one `StateSnapshot` part (ADR-0058 section 5): the catch-up
     * re-send, from `SyncedState.snapshotParts()`. Send every part.
     * @param {Identity} identity
     * @param {SyncedSnapshotPart} part
     * @param {bigint} hlc
     * @returns {Uint8Array}
     */
    createDeviceGroupSnapshotMessage(identity, part, hlc) {
        _assertClass(identity, Identity);
        _assertClass(part, SyncedSnapshotPart);
        const ret = wasm.devicegroup_createDeviceGroupSnapshotMessage(this.__wbg_ptr, identity.__wbg_ptr, part.__wbg_ptr, hlc);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Bootstrap a fresh DeviceGroup with `identity` as the
     * founding leaf. `account_id` MUST be the raw 16-byte
     * UUIDv7 (removal attestations bind to it); the group id is derived
     * from `accountIdentity`'s seed (`deviceGroupIdFor`).
     * @param {Identity} identity
     * @param {Uint8Array} account_id
     * @param {Identity} account_identity
     * @returns {DeviceGroup}
     */
    static createFounder(identity, account_id, account_identity) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        _assertClass(account_identity, Identity);
        const ret = wasm.devicegroup_createFounder(identity.__wbg_ptr, ptr0, len0, account_identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DeviceGroup.__wrap(ret[0]);
    }
    /**
     * Compose a `serviceEnvelope` cross-device payload carrying a
     * `lakko-contacts` envelope verbatim (ADR-0060 section 7: the output
     * of `composeVisibilityEnvelope` / `composeReachStateEnvelope`).
     *
     * # Errors
     *
     * Throws when `envelope` is empty or longer than
     * `deviceGroupServiceEnvelopeMaxLen()`; nothing is encrypted.
     * @param {Identity} identity
     * @param {Uint8Array} envelope
     * @param {bigint} hlc
     * @returns {Uint8Array}
     */
    createServiceEnvelopeMessage(identity, envelope, hlc) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_createServiceEnvelopeMessage(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, hlc);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Snapshot of every device leaf currently in the
     * DeviceGroup (Phase 2a3c). One [`GroupMember`] per row —
     * the JS layer renders the list, marks the row whose
     * `leafIndex` matches [`Self::ownLeafIndex`] as
     * "(this device)", and offers a Remove affordance on every
     * other row.
     *
     * Same wasm-bindgen shape as the conversation
     * [`Group::members`] — flat `JsValue` array, ordering
     * matches `lakko_mls::DeviceGroup::devices` (left-to-right
     * over the leaf tree, stable inside a single epoch).
     * @returns {any[]}
     */
    devices() {
        const ret = wasm.devicegroup_devices(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The ADR-0053 access signer for the DeviceGroup journal at the current
     * epoch - the same derivation as `Group.dsAccessSigner` over the
     * underlying group. Re-derive after every commit.
     * @param {Identity} identity
     * @returns {DsProofSigner}
     */
    dsAccessSigner(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.devicegroup_dsAccessSigner(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsProofSigner.__wrap(ret[0]);
    }
    /**
     * The DeviceGroup's current MLS epoch (`bigint`). ADR-0079: the
     * journal readability rule asks whether the group moved past a commit
     * row's epoch after the row.
     * @returns {bigint}
     */
    epoch() {
        const ret = wasm.devicegroup_epoch(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return BigInt.asUintN(64, ret[0]);
    }
    /**
     * Derive a `length`-byte secret bound to this DeviceGroup's current
     * epoch (ADR-0018 Part 2a, factor (a) — "another-device" identity
     * transfer, Path B). Mirror of `Group.exportSecret` / the UniFFI
     * surface. A **read-only** MLS exporter call (no state mutation). After a
     * fresh device joins via [`DeviceGroup::joinFromWelcome`], the existing
     * and fresh device sit at the same epoch and derive the SAME bytes for
     * the same `(label, context, length)`; the existing device seals
     * `(grant_token ‖ phrase_seed)` under this key (via [`sealWithKey`]) and
     * the fresh device opens it (via [`openWithKey`]).
     * @param {Identity} identity
     * @param {string} label
     * @param {Uint8Array} context
     * @param {number} length
     * @returns {Uint8Array}
     */
    exportSecret(identity, label, context, length) {
        _assertClass(identity, Identity);
        const ptr0 = passStringToWasm0(label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(context, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_exportSecret(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, ptr1, len1, length);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v3;
    }
    /**
     * The `group_id` of this DeviceGroup (its DS journal id): for a founded
     * group, what `deviceGroupIdFor(accountIdentity)` returns.
     * @returns {Uint8Array}
     */
    groupId() {
        const ret = wasm.devicegroup_groupId(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Derive the per-pairing history-bundle exporter secret
     * (Phase 2a3d8). Returns 32 bytes — the scanner side feeds
     * it into [`sealHistoryBundle`], the joining side feeds it
     * into [`openHistoryBundle`]. Both devices reach the same
     * value after they advance to the post-add-device epoch.
     * @param {Identity} identity
     * @returns {Uint8Array}
     */
    historyBundleSecret(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.devicegroup_historyBundleSecret(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Whether THIS device is still in its account's DeviceGroup: `false`
     * once it has applied a commit that removes its own leaf. The client
     * then shows "This device was removed" and stops polling (UX
     * `second-device.md` section 3.1).
     * @returns {boolean}
     */
    get isActive() {
        const ret = wasm.devicegroup_isActive(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Join a DeviceGroup from a Welcome the scanner device
     * produced via [`DeviceGroup::addDeviceFromWire`]. Binds nothing about
     * the group id (ADR-0078 section 6): the joiner holds no account seed
     * yet; the recovery row opened under this group's exporter binds it.
     * @param {Uint8Array} welcome
     * @param {Identity} identity
     * @param {Uint8Array} account_id
     * @returns {DeviceGroup}
     */
    static joinFromWelcome(welcome, identity, account_id) {
        const ptr0 = passArray8ToWasm0(welcome, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        _assertClass(identity, Identity);
        const ptr1 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_joinFromWelcome(ptr0, len0, identity.__wbg_ptr, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DeviceGroup.__wrap(ret[0]);
    }
    /**
     * ADR-0056 join by name / ADR-0081 amendment (2026-10-19) - join a
     * DeviceGroup Welcome before the account is known. `Group.joinFromWelcome`
     * refuses a DeviceGroup Welcome (it carries no role table), so the by-name
     * flow joins here: the group is persisted and handed back as a plain
     * `Group` (its exporter opens the recovery row) for `loadDeviceGroupById` to
     * adopt. A conversation's Welcome is refused.
     * @param {Uint8Array} welcome
     * @param {Identity} identity
     * @returns {Group}
     */
    static joinWelcomeByName(welcome, identity) {
        const ptr0 = passArray8ToWasm0(welcome, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        _assertClass(identity, Identity);
        const ret = wasm.devicegroup_joinWelcomeByName(ptr0, len0, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Group.__wrap(ret[0]);
    }
    /**
     * Leaf index of THIS device inside the DeviceGroup
     * (Phase 2a3c). UI uses this to flag the "(this device)"
     * row and disable its Remove button.
     * @returns {number}
     */
    ownLeafIndex() {
        const ret = wasm.devicegroup_ownLeafIndex(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * Decrypt / advance the state machine for an inbound
     * DeviceGroup wire message (Phase 2a3e2). JS layer reads
     * `kind`:
     *
     *   * `"application"` — pass `data` into
     *     [`parseDeviceGroupPayload`] to project into a typed
     *     payload.
     *   * `"commit"` — DeviceGroup advanced. Caller refreshes
     *     `devices()`.
     *   * `"proposal"` / `"externalJoinProposal"` — defensive;
     *     no state advance.
     * @param {Identity} identity
     * @param {Uint8Array} message_bytes
     * @returns {ProcessedMessage}
     */
    processIncoming(identity, message_bytes) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(message_bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_processIncoming(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ProcessedMessage.__wrap(ret[0]);
    }
    /**
     * G-13 (ADR-0015): apply an incoming DeviceGroup commit **enforcing the
     * attested-removal policy**. When the policy is on and the commit
     * removes a leaf, `attestation` must be the attestation the auth service
     * issued for that removal — verified under `attestVerifyingKey` (the
     * 32-byte Ed25519 key from the device-attest JWKS), bound to this
     * account + the removed leaf + fresh at `nowUnixSecs`. A removal without
     * a valid attestation is refused (the commit is NOT merged). An empty
     * `attestation` means "none supplied". Policy-off / non-remove commits
     * behave like [`Self::dg_process_incoming`].
     * @param {Identity} identity
     * @param {Uint8Array} message_bytes
     * @param {Uint8Array} attestation
     * @param {Uint8Array} attest_verifying_key
     * @param {bigint} now_unix_secs
     * @returns {ProcessedMessage}
     */
    processIncomingVerifyingRemovals(identity, message_bytes, attestation, attest_verifying_key, now_unix_secs) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(message_bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(attestation, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(attest_verifying_key, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_processIncomingVerifyingRemovals(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, now_unix_secs);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ProcessedMessage.__wrap(ret[0]);
    }
    /**
     * Revoke a device leaf (Phase 2a3c). Returns the encoded
     * Commit bytes the caller MUST broadcast to every remaining
     * device via the DS group journal so they advance past the
     * revoked leaf and ratchet the group secrets out of the
     * removed device's reach.
     *
     * `leafIndex` must come from a [`Self::devices`] snapshot
     * at the SAME epoch; concurrent commits can renumber
     * leaves, in which case the caller refetches and retries.
     * Attempting to remove the local device's own leaf is
     * rejected with a wasm-side error.
     * @param {Identity} identity
     * @param {number} leaf_index
     * @returns {Uint8Array}
     */
    removeDeviceAt(identity, leaf_index) {
        _assertClass(identity, Identity);
        const ret = wasm.devicegroup_removeDeviceAt(this.__wbg_ptr, identity.__wbg_ptr, leaf_index);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * ADR-0081 amendment — replace the device at `leafIndex` by the device
     * whose KeyPackage is `keyPackage`, in ONE commit (a remove and an add):
     * the commit a sibling builds to readmit a device that lost its
     * DeviceGroup state. Under the attested-removal policy peers accept the
     * removal when the rejoining device's self-removal proof for this
     * KeyPackage (`signDeviceGroupSelfRemoval`) travels in the envelope's
     * removal-authorisation slot. Merges eagerly, like the other DeviceGroup
     * commits.
     * @param {Identity} identity
     * @param {number} leaf_index
     * @param {Uint8Array} key_package
     * @returns {AddMember}
     */
    replaceDeviceAt(identity, leaf_index, key_package) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.devicegroup_replaceDeviceAt(this.__wbg_ptr, identity.__wbg_ptr, leaf_index, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AddMember.__wrap(ret[0]);
    }
    /**
     * G-13 (ADR-0015): whether this DeviceGroup currently requires attested
     * removals (read from authenticated group state).
     * @returns {boolean}
     */
    requireAttestedRemovals() {
        const ret = wasm.devicegroup_requireAttestedRemovals(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * G-13 (ADR-0015): flip this DeviceGroup's policy to **require a
     * peer-verified attestation for device removals**. Returns the encoded
     * Commit to broadcast over the DS group journal. Call when the account
     * first enables a passkey (OPAQUE-only accounts never call it).
     * @param {Identity} identity
     * @returns {Uint8Array}
     */
    setRequireAttestedRemovals(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.devicegroup_setRequireAttestedRemovals(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) DeviceGroup.prototype[Symbol.dispose] = DeviceGroup.prototype.free;

/**
 * What the old device gets from a verified exchange.
 */
export class DeviceLinkAccepted {
    static __wrap(ptr) {
        const obj = Object.create(DeviceLinkAccepted.prototype);
        obj.__wbg_ptr = ptr;
        DeviceLinkAcceptedFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DeviceLinkAcceptedFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_devicelinkaccepted_free(ptr, 0);
    }
    /**
     * The old device's key confirmation (32 bytes); post it as
     * `confirm_b_b64` with outcome `accepted`.
     * @returns {Uint8Array}
     */
    get confirmB() {
        const ret = wasm.devicelinkaccepted_confirmB(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The ADR-0056 pairing payload; `parsePairingQr` it with the account's
     * own login handle, exactly as a scanned QR.
     * @returns {Uint8Array}
     */
    get pairingPayload() {
        const ret = wasm.devicelinkaccepted_pairingPayload(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) DeviceLinkAccepted.prototype[Symbol.dispose] = DeviceLinkAccepted.prototype.free;

/**
 * The new device's side of one device-link exchange.
 */
export class DeviceLinkInitiator {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DeviceLinkInitiatorFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_devicelinkinitiator_free(ptr, 0);
    }
    /**
     * Run the exchange with `pakeB` and the typed code, sealing
     * `pairingPayload`. Runs once. Throws `invalid-code: ...` (nothing
     * spent; the person may retype), `malformed: ...`, `bad-input: ...` or
     * `already-used: ...`.
     * @param {Uint8Array} pake_b
     * @param {string} typed_code
     * @param {Uint8Array} pairing_payload
     * @returns {DeviceLinkSealed}
     */
    finish(pake_b, typed_code, pairing_payload) {
        const ptr0 = passArray8ToWasm0(pake_b, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(typed_code, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(pairing_payload, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.devicelinkinitiator_finish(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DeviceLinkSealed.__wrap(ret[0]);
    }
    /**
     * Bind to `requestId` (16 bytes, from `POST /device/request`) and
     * `loginHandle` (32 bytes, the handle of the typed name). Throws
     * `bad-input: ...` on a wrong length.
     * @param {Uint8Array} request_id
     * @param {Uint8Array} login_handle
     */
    constructor(request_id, login_handle) {
        const ptr0 = passArray8ToWasm0(request_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(login_handle, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.devicelinkinitiator_new(ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        DeviceLinkInitiatorFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Check the old device's `confirmB` from the `accepted` status before
     * trusting the Welcome. Throws `wrong-code: ...`, `malformed: ...` or
     * `not-ready: ...`.
     * @param {Uint8Array} confirm_b
     */
    verifyConfirmation(confirm_b) {
        const ptr0 = passArray8ToWasm0(confirm_b, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.devicelinkinitiator_verifyConfirmation(this.__wbg_ptr, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
}
if (Symbol.dispose) DeviceLinkInitiator.prototype[Symbol.dispose] = DeviceLinkInitiator.prototype.free;

/**
 * The signed-in device's side of one device-link exchange.
 */
export class DeviceLinkResponder {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DeviceLinkResponderFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_devicelinkresponder_free(ptr, 0);
    }
    /**
     * Verify the new device's values and open its pairing payload. Runs
     * once. Throws `wrong-code: ...` (post `wrong_code`), `malformed: ...`
     * (post `wrong_code`), or `already-used: ...`. Pair nothing on a throw.
     * @param {Uint8Array} pake_a
     * @param {Uint8Array} sealed_payload
     * @param {Uint8Array} confirm_a
     * @returns {DeviceLinkAccepted}
     */
    accept(pake_a, sealed_payload, confirm_a) {
        const ptr0 = passArray8ToWasm0(pake_a, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(sealed_payload, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(confirm_a, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.devicelinkresponder_accept(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DeviceLinkAccepted.__wrap(ret[0]);
    }
    /**
     * The six digits to show, e.g. `"471903"`.
     * @returns {string}
     */
    get code() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.devicelinkresponder_code(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Choose a fresh code and start the exchange for `requestId` (16 bytes)
     * under `loginHandle` (32 bytes, this account's own handle). Throws
     * `bad-input: ...` on a wrong length.
     * @param {Uint8Array} request_id
     * @param {Uint8Array} login_handle
     */
    constructor(request_id, login_handle) {
        const ptr0 = passArray8ToWasm0(request_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(login_handle, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.devicelinkresponder_new(ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        DeviceLinkResponderFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The SPAKE2 message to post (33 bytes), as `pake_b_b64`.
     * @returns {Uint8Array}
     */
    get pakeMessage() {
        const ret = wasm.devicelinkresponder_pakeMessage(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) DeviceLinkResponder.prototype[Symbol.dispose] = DeviceLinkResponder.prototype.free;

/**
 * What the new device posts to `POST /device/request/payload`.
 */
export class DeviceLinkSealed {
    static __wrap(ptr) {
        const obj = Object.create(DeviceLinkSealed.prototype);
        obj.__wbg_ptr = ptr;
        DeviceLinkSealedFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DeviceLinkSealedFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_devicelinksealed_free(ptr, 0);
    }
    /**
     * The new device's key confirmation (32 bytes).
     * @returns {Uint8Array}
     */
    get confirmA() {
        const ret = wasm.devicelinksealed_confirmA(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * SPAKE2 side-A message (33 bytes).
     * @returns {Uint8Array}
     */
    get pakeA() {
        const ret = wasm.devicelinksealed_pakeA(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The pairing payload under the agreed key.
     * @returns {Uint8Array}
     */
    get sealedPayload() {
        const ret = wasm.devicelinksealed_sealedPayload(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) DeviceLinkSealed.prototype[Symbol.dispose] = DeviceLinkSealed.prototype.free;

/**
 * Direction of a per-relationship pseudonym. Distinct values
 * guarantee distinct pseudonyms for the same `seed`, breaking
 * trivial server-side pair correlation.
 * @enum {0 | 1}
 */
export const Direction = Object.freeze({
    /**
     * Self-to-Peer: addresses a message FROM us TO the contact.
     */
    Outbound: 0, "0": "Outbound",
    /**
     * Peer-to-Self: addresses a message FROM the contact TO us.
     */
    Inbound: 1, "1": "Inbound",
});

/**
 * Per-group draft. Mirror of [`lakko_sync::Draft`]. Replaceable by a
 * strictly-greater [`Hlc`] for the same `groupId`.
 */
export class Draft {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DraftFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_draft_free(ptr, 0);
    }
    /**
     * Current draft text.
     * @returns {string}
     */
    get body() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.draft_body(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * 32-byte group pseudonym this draft belongs to.
     * @returns {Uint8Array}
     */
    get groupId() {
        const ret = wasm.draft_groupId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * HLC at which the user last edited this draft.
     * @returns {Hlc}
     */
    get hlc() {
        const ret = wasm.draft_hlc(this.__wbg_ptr);
        return Hlc.__wrap(ret);
    }
    /**
     * Construct a fresh draft. `groupId` must be exactly 32 bytes.
     * @param {Uint8Array} group_id
     * @param {string} body
     * @param {Hlc} hlc
     */
    constructor(group_id, body, hlc) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        _assertClass(hlc, Hlc);
        const ret = wasm.draft_new(ptr0, len0, ptr1, len1, hlc.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        DraftFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Should `incoming` replace `self`? `true` iff `groupId` matches
     * AND `incoming.hlc` is strictly greater.
     * @param {Draft} incoming
     * @returns {boolean}
     */
    shouldReplaceWith(incoming) {
        _assertClass(incoming, Draft);
        const ret = wasm.draft_shouldReplaceWith(this.__wbg_ptr, incoming.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) Draft.prototype[Symbol.dispose] = Draft.prototype.free;

/**
 * A conversation's draft, stamped. Empty `text` = cleared at `hlc`.
 */
export class DraftRegister {
    static __wrap(ptr) {
        const obj = Object.create(DraftRegister.prototype);
        obj.__wbg_ptr = ptr;
        DraftRegisterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DraftRegisterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_draftregister_free(ptr, 0);
    }
    /**
     * The conversation's MLS group id.
     * @returns {Uint8Array}
     */
    get groupId() {
        const ret = wasm.draftregister_groupId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * HLC stamp.
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.draftregister_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Replying to this message id, or empty.
     * @returns {Uint8Array}
     */
    get replyToId() {
        const ret = wasm.draftregister_replyToId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Composer text.
     * @returns {string}
     */
    get text() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.draftregister_text(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) DraftRegister.prototype[Symbol.dispose] = DraftRegister.prototype.free;

/**
 * One journal's proof signer at one epoch. See the module doc.
 */
export class DsProofSigner {
    static __wrap(ptr) {
        const obj = Object.create(DsProofSigner.prototype);
        obj.__wbg_ptr = ptr;
        DsProofSignerFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DsProofSignerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_dsproofsigner_free(ptr, 0);
    }
    /**
     * The epoch this signer proves membership at (`dsMailboxEpoch()` for a
     * mailbox signer, `dsReachEpoch()` for a reach signer). A `bigint`.
     * @returns {bigint}
     */
    get epoch() {
        const ret = wasm.dsproofsigner_epoch(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * A group access signer rebuilt from a recorded access seed (ADR-0081):
     * the 32-byte seed `dsAccessSeed` returned at `epoch`. It exists for a
     * PublisherGroup commit, which merges eagerly: the seed of the epoch the
     * commit left is recorded with the commit, so a re-send after a restart
     * can still prove that epoch (the Delivery Service then appends it, or
     * answers the original row, or `409`).
     *
     * # Errors
     *
     * Throws when `accessSeed` is not 32 bytes.
     * @param {bigint} epoch
     * @param {Uint8Array} access_seed
     * @returns {DsProofSigner}
     */
    static forGroup(epoch, access_seed) {
        const ptr0 = passArray8ToWasm0(access_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.dsproofsigner_forGroup(epoch, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsProofSigner.__wrap(ret[0]);
    }
    /**
     * A reach-key signer (ADR-0060 §2) at `dsReachEpoch()`: signs a write to
     * a closed identity mailbox with a 32-byte reach seed the owner
     * registered (or a delegate seed, next to a delegation). Its proof header
     * names the signing key (a sixth field, inside the signed bytes) so the
     * DS checks exactly that key; nothing for the caller to add.
     *
     * # Errors
     *
     * Throws an `Error` with `reachError` `seedLength`.
     * @param {Uint8Array} reach_seed
     * @returns {DsProofSigner}
     */
    static forReach(reach_seed) {
        const ptr0 = passArray8ToWasm0(reach_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.dsproofsigner_forReach(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsProofSigner.__wrap(ret[0]);
    }
    /**
     * The JSON body of `PUT /ds/group/{hex}/access` registering this
     * signer's key at its epoch.
     *
     * **Superseded by ADR-0057; the Delivery Service no longer accepts this
     * body** (`PUT .../access` now takes a signed statement, and an epoch
     * key registers nothing). Kept only until the clients move to the
     * statement signers; do not add a caller.
     * @returns {string}
     */
    groupClaimBodyJson() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.dsproofsigner_groupClaimBodyJson(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The 32-byte address of the mailbox a mailbox signer's key names
     * (ADR-0078): the path segment of `/ds/mailbox/{hex}/...`.
     * @returns {Uint8Array}
     */
    mailboxAddress() {
        const ret = wasm.dsproofsigner_mailboxAddress(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The JSON body of `PUT /ds/mailbox/{hex}/access` claiming the mailbox
     * a mailbox signer's key names: `{"mailbox_pk_b64": ...}` (ADR-0078).
     * @returns {string}
     */
    mailboxClaimBodyJson() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.dsproofsigner_mailboxClaimBodyJson(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The `x-lakko-ds-proof` header value for one request. `path` is the
     * request path without the query, `query` the raw query string without
     * `?` (empty when none), `body` the exact bytes sent (empty for a `GET`).
     * Every call mints a fresh nonce; a value is good for one request within
     * the server's two-minute window.
     * @param {string} method
     * @param {string} path
     * @param {string} query
     * @param {Uint8Array} body
     * @returns {string}
     */
    sign(method, path, query, body) {
        let deferred5_0;
        let deferred5_1;
        try {
            const ptr0 = passStringToWasm0(method, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len0 = WASM_VECTOR_LEN;
            const ptr1 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            const ptr2 = passStringToWasm0(query, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len2 = WASM_VECTOR_LEN;
            const ptr3 = passArray8ToWasm0(body, wasm.__wbindgen_malloc);
            const len3 = WASM_VECTOR_LEN;
            const ret = wasm.dsproofsigner_sign(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
            deferred5_0 = ret[0];
            deferred5_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred5_0, deferred5_1, 1);
        }
    }
    /**
     * The 32-byte Ed25519 verifying key a claim registers for this epoch.
     * @returns {Uint8Array}
     */
    verifyingKey() {
        const ret = wasm.dsproofsigner_verifyingKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) DsProofSigner.prototype[Symbol.dispose] = DsProofSigner.prototype.free;

/**
 * One `PUT` to the Delivery Service: `path` is the service path
 * (`/ds/group/<hex>/<segment>`), `bodyJson` the exact JSON body. No proof
 * header.
 */
export class DsStatementRequest {
    static __wrap(ptr) {
        const obj = Object.create(DsStatementRequest.prototype);
        obj.__wbg_ptr = ptr;
        DsStatementRequestFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DsStatementRequestFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_dsstatementrequest_free(ptr, 0);
    }
    /**
     * The JSON body.
     * @returns {string}
     */
    get bodyJson() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.dsstatementrequest_bodyJson(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The service path.
     * @returns {string}
     */
    get path() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.dsstatementrequest_path(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) DsStatementRequest.prototype[Symbol.dispose] = DsStatementRequest.prototype.free;

/**
 * A root or registrar signer.
 */
export class DsStatementSigner {
    static __wrap(ptr) {
        const obj = Object.create(DsStatementSigner.prototype);
        obj.__wbg_ptr = ptr;
        DsStatementSignerFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DsStatementSignerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_dsstatementsigner_free(ptr, 0);
    }
    /**
     * `PUT .../claim`: the first claim with the first epoch's access key.
     *
     * # Errors
     *
     * On a wrong-length input or an invalid key.
     * @param {Uint8Array} journal_id
     * @param {bigint} epoch
     * @param {Uint8Array} epoch_key
     * @returns {DsStatementRequest}
     */
    claim(journal_id, epoch, epoch_key) {
        const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(epoch_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.dsstatementsigner_claim(this.__wbg_ptr, ptr0, len0, epoch, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsStatementRequest.__wrap(ret[0]);
    }
    /**
     * ADR-0081 - `POST .../commit`: register `epochKey` at `epoch` in
     * **append** mode (it must be the journal's next epoch; a member
     * registrar may append only one epoch past the last admin or root
     * registration; the flat 32-byte `revokeKeys` are revoked in the same
     * step) and append `row` - the commit, in the journal's
     * envelope - in ONE step. The returned request's `path` ends in
     * `/commit` and its `bodyJson` is the exact body; the caller sends it as
     * a `POST` with the request proof signed over those bytes by the key of
     * epoch `epoch - 1` (`DsProofSigner.sign("POST", path, "", body)`).
     * Re-sending the same row under the same key answers the original
     * message id (`200`); any other commit at a registered epoch is `409`.
     * Sign a new request for every attempt: the statement's nonce is spent.
     *
     * # Errors
     *
     * On a wrong-length input, an invalid key, a repeated key, more than
     * 256 revocations, or an empty row.
     * @param {Uint8Array} journal_id
     * @param {bigint} epoch
     * @param {Uint8Array} epoch_key
     * @param {Uint8Array} revoke_keys
     * @param {Uint8Array} row
     * @returns {DsStatementRequest}
     */
    commit(journal_id, epoch, epoch_key, revoke_keys, row) {
        const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(epoch_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(revoke_keys, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArray8ToWasm0(row, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        const ret = wasm.dsstatementsigner_commit(this.__wbg_ptr, ptr0, len0, epoch, ptr1, len1, ptr2, len2, ptr3, len3);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsStatementRequest.__wrap(ret[0]);
    }
    /**
     * The 32-byte public key the DS knows this signer by.
     * @returns {Uint8Array}
     */
    publicKey() {
        const ret = wasm.dsstatementsigner_publicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `PUT .../registrars`: `keys` flat 32-byte keys, `ranks` one byte each
     * (`1` member, `2` admin, `0` removes); 1 to 256 changes.
     *
     * # Errors
     *
     * On a wrong-length input, an invalid key, a repeated key, a count
     * mismatch, an unknown rank, or an empty or over-long list.
     * @param {Uint8Array} journal_id
     * @param {Uint8Array} keys
     * @param {Uint8Array} ranks
     * @returns {DsStatementRequest}
     */
    registrars(journal_id, keys, ranks) {
        const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(keys, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(ranks, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.dsstatementsigner_registrars(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsStatementRequest.__wrap(ret[0]);
    }
    /**
     * `PUT .../registrars-set`: the root replaces the journal's WHOLE
     * registrar set with `keys` (flat 32-byte keys) at `ranks` (one byte
     * each, `1` member / `2` admin); every registrar not listed is removed,
     * and empty lists remove all. A registrar sending it gets `403`.
     *
     * # Errors
     *
     * On a wrong-length input, an invalid key, a repeated key, a count
     * mismatch, an unknown rank, more than 4096 entries, or an entry naming
     * this signer's own key.
     * @param {Uint8Array} journal_id
     * @param {Uint8Array} keys
     * @param {Uint8Array} ranks
     * @returns {DsStatementRequest}
     */
    registrarsSet(journal_id, keys, ranks) {
        const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(keys, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(ranks, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.dsstatementsigner_registrarsSet(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsStatementRequest.__wrap(ret[0]);
    }
    /**
     * `PUT .../access` in **replace** mode: write `epochKey` at `epoch` and
     * drop every epoch above it, when everything above was registered by a
     * strictly lower rank than this signer's and the registration at
     * `epoch`, if any, was too or carries this same key (`403` otherwise; a
     * member never replaces; a skip past the next epoch is the root's).
     * Revokes the flat `revokeKeys` in the same transaction.
     *
     * # Errors
     *
     * On a wrong-length input, an invalid key, a repeated key or more than
     * 256 revocations.
     * @param {Uint8Array} journal_id
     * @param {bigint} epoch
     * @param {Uint8Array} epoch_key
     * @param {Uint8Array} revoke_keys
     * @returns {DsStatementRequest}
     */
    replaceEpoch(journal_id, epoch, epoch_key, revoke_keys) {
        const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(epoch_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(revoke_keys, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.dsstatementsigner_replaceEpoch(this.__wbg_ptr, ptr0, len0, epoch, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsStatementRequest.__wrap(ret[0]);
    }
    /**
     * `PUT .../reset` keeping the registrar set.
     *
     * # Errors
     *
     * On a wrong-length input or an invalid key.
     * @param {Uint8Array} journal_id
     * @param {bigint} epoch
     * @param {Uint8Array} epoch_key
     * @returns {DsStatementRequest}
     */
    reset(journal_id, epoch, epoch_key) {
        const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(epoch_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.dsstatementsigner_reset(this.__wbg_ptr, ptr0, len0, epoch, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsStatementRequest.__wrap(ret[0]);
    }
    /**
     * `PUT .../reset` replacing the registrar set with `keys` (flat 32-byte
     * keys) at `ranks` (one byte each, `1` member / `2` admin).
     *
     * # Errors
     *
     * On a wrong-length input, an invalid key, a repeated key, a count
     * mismatch or more than 256 registrars.
     * @param {Uint8Array} journal_id
     * @param {bigint} epoch
     * @param {Uint8Array} epoch_key
     * @param {Uint8Array} keys
     * @param {Uint8Array} ranks
     * @returns {DsStatementRequest}
     */
    resetWithRegistrars(journal_id, epoch, epoch_key, keys, ranks) {
        const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(epoch_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(keys, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArray8ToWasm0(ranks, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        const ret = wasm.dsstatementsigner_resetWithRegistrars(this.__wbg_ptr, ptr0, len0, epoch, ptr1, len1, ptr2, len2, ptr3, len3);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsStatementRequest.__wrap(ret[0]);
    }
    /**
     * `PUT .../root`: the current root names `newRoot`. Naming the current
     * root itself is a no-op `204` for the root and the uniform `401` for
     * any other key: the probe "am I still the root?".
     *
     * # Errors
     *
     * On a wrong-length input or an invalid key.
     * @param {Uint8Array} journal_id
     * @param {Uint8Array} new_root
     * @returns {DsStatementRequest}
     */
    rootTransfer(journal_id, new_root) {
        const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(new_root, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.dsstatementsigner_rootTransfer(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsStatementRequest.__wrap(ret[0]);
    }
}
if (Symbol.dispose) DsStatementSigner.prototype[Symbol.dispose] = DsStatementSigner.prototype.free;

/**
 * Append-only edit log per message, last-write-wins by [`Hlc`].
 * Mirror of [`lakko_sync::EditChain`].
 */
export class EditChain {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        EditChainFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_editchain_free(ptr, 0);
    }
    /**
     * Append an op. Cheap (Vec push). Idempotent in resolution —
     * duplicates are stored verbatim, but `resolve` still returns
     * the highest-HLC op.
     * @param {EditOp} op
     */
    append(op) {
        _assertClass(op, EditOp);
        const ret = wasm.editchain_append(this.__wbg_ptr, op.__wbg_ptr);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * `true` if no ops have been appended.
     * @returns {boolean}
     */
    get isEmpty() {
        const ret = wasm.editchain_isEmpty(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Number of stored ops.
     * @returns {number}
     */
    get len() {
        const ret = wasm.editchain_len(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * Empty chain.
     */
    constructor() {
        const ret = wasm.editchain_new();
        this.__wbg_ptr = ret;
        EditChainFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Resolve the chain to user-visible state.
     * @returns {EditResolved}
     */
    resolve() {
        const ret = wasm.editchain_resolve(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return EditResolved.__wrap(ret[0]);
    }
}
if (Symbol.dispose) EditChain.prototype[Symbol.dispose] = EditChain.prototype.free;

/**
 * Single operation in a message's edit chain. Use [`EditOp::update`]
 * or [`EditOp::delete`] to construct.
 */
export class EditOp {
    static __wrap(ptr) {
        const obj = Object.create(EditOp.prototype);
        obj.__wbg_ptr = ptr;
        EditOpFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        EditOpFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_editop_free(ptr, 0);
    }
    /**
     * Body for `Update`, `None` for `Delete`.
     * @returns {string | undefined}
     */
    get body() {
        const ret = wasm.editop_body(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Construct a `Delete` (tombstone) op.
     * @param {Hlc} hlc
     * @returns {EditOp}
     */
    static delete(hlc) {
        _assertClass(hlc, Hlc);
        const ret = wasm.editop_delete(hlc.__wbg_ptr);
        return EditOp.__wrap(ret);
    }
    /**
     * Hlc of this op.
     * @returns {Hlc}
     */
    get hlc() {
        const ret = wasm.editop_hlc(this.__wbg_ptr);
        return Hlc.__wrap(ret);
    }
    /**
     * Discriminator: `"Update"` or `"Delete"`.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.editop_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Construct an `Update` op.
     * @param {Hlc} hlc
     * @param {string} body
     * @returns {EditOp}
     */
    static update(hlc, body) {
        _assertClass(hlc, Hlc);
        const ptr0 = passStringToWasm0(body, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.editop_update(hlc.__wbg_ptr, ptr0, len0);
        return EditOp.__wrap(ret);
    }
}
if (Symbol.dispose) EditOp.prototype[Symbol.dispose] = EditOp.prototype.free;

/**
 * Resolved view of an edit chain.
 */
export class EditResolved {
    static __wrap(ptr) {
        const obj = Object.create(EditResolved.prototype);
        obj.__wbg_ptr = ptr;
        EditResolvedFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        EditResolvedFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_editresolved_free(ptr, 0);
    }
    /**
     * Resolved text — present iff `kind == "Visible"`.
     * @returns {string | undefined}
     */
    get body() {
        const ret = wasm.editresolved_body(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * `"Visible" | "Deleted" | "Empty"`.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.editresolved_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) EditResolved.prototype[Symbol.dispose] = EditResolved.prototype.free;

/**
 * Discriminator strings for [`EditChain::resolve`]. Returned alongside
 * the resolved body via [`EditResolved`].
 * @enum {0 | 1 | 2}
 */
export const EditResolvedKind = Object.freeze({
    /**
     * Latest op is an `Update` — `body` is `Some`.
     */
    Visible: 0, "0": "Visible",
    /**
     * Latest op is `Delete`.
     */
    Deleted: 1, "1": "Deleted",
    /**
     * Chain is empty.
     */
    Empty: 2, "2": "Empty",
});

/**
 * Output of [`encrypt_attachment`]. Carries ciphertext + CEK +
 * nonce + content-addressed `blob_id`.
 *
 * JS reads the four byte fields via the `ciphertext` / `key` /
 * `nonce` / `blobId` getters; the wasm-bindgen generated `.d.ts`
 * types them as `Uint8Array`.
 */
export class EncryptedAttachment {
    static __wrap(ptr) {
        const obj = Object.create(EncryptedAttachment.prototype);
        obj.__wbg_ptr = ptr;
        EncryptedAttachmentFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        EncryptedAttachmentFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_encryptedattachment_free(ptr, 0);
    }
    /**
     * `SHA-256(ciphertext)` — the storage service's content
     * address. 32 bytes raw; caller hex-encodes for the URL.
     * @returns {Uint8Array}
     */
    get blobId() {
        const ret = wasm.encryptedattachment_blobId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Ciphertext bytes — `ChaCha20-Poly1305(plaintext)` with AAD
     * empty. PUT verbatim to `/blob/<hex(blobId)>`.
     * @returns {Uint8Array}
     */
    get ciphertext() {
        const ret = wasm.encryptedattachment_ciphertext(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Fresh random 32-byte ChaCha20-Poly1305 key (CEK).
     * @returns {Uint8Array}
     */
    get key() {
        const ret = wasm.encryptedattachment_key(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Fresh random 12-byte AEAD nonce.
     * @returns {Uint8Array}
     */
    get nonce() {
        const ret = wasm.encryptedattachment_nonce(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) EncryptedAttachment.prototype[Symbol.dispose] = EncryptedAttachment.prototype.free;

/**
 * AEAD wrapper around an `InMemoryStore`. Mirror of
 * [`lakko_storage::EncryptedStore`].
 *
 * Phase 1.13b only takes [`InMemoryStore`] as the inner backend; the
 * IndexedDB-backed alternative arrives in 1.13c.
 */
export class EncryptedStore {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        EncryptedStoreFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_encryptedstore_free(ptr, 0);
    }
    /**
     * Delete the row.
     * @param {Table} table
     * @param {Uint8Array} key
     */
    delete(table, key) {
        const ptr0 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.encryptedstore_delete(this.__wbg_ptr, table, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Encrypted get.
     * @param {Table} table
     * @param {Uint8Array} key
     * @returns {Uint8Array | undefined}
     */
    get(table, key) {
        const ptr0 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.encryptedstore_get(this.__wbg_ptr, table, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v2;
        if (ret[0] !== 0) {
            v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v2;
    }
    /**
     * Wrap an [`InMemoryStore`] with AEAD encryption keyed by
     * `rootKey` (32 bytes).
     * @param {InMemoryStore} inner
     * @param {Uint8Array} root_key
     */
    constructor(inner, root_key) {
        _assertClass(inner, InMemoryStore);
        const ptr0 = passArray8ToWasm0(root_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.encryptedstore_new(inner.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        EncryptedStoreFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Encrypted put.
     * @param {Table} table
     * @param {Uint8Array} key
     * @param {Uint8Array} value
     */
    put(table, key, value) {
        const ptr0 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(value, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.encryptedstore_put(this.__wbg_ptr, table, ptr0, len0, ptr1, len1);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
}
if (Symbol.dispose) EncryptedStore.prototype[Symbol.dispose] = EncryptedStore.prototype.free;

/**
 * Build the canonical MLS Application message JSON envelope.
 * Mirror of `lakko_attachments::compose`.
 *
 * `attachments` is an array of [`AttachmentRef`] instances. The
 * wasm-bindgen `Vec<JsValue>`-marshalling pattern would force us
 * to manually downcast each entry; the simpler shape is to take
 * the refs one at a time via the [`EnvelopeBuilder`] helper
 * below. For the common case of a single-attachment message,
 * callers can use [`compose_envelope_one`] directly.
 *
 * JS idiom for the general case:
 *
 * ```ts
 * const b = new EnvelopeBuilder("text body");
 * for (const r of attachmentRefs) b.add(r);
 * const wire = b.build();
 * ```
 *
 * The optional parts of a chat message are set on the builder before
 * building and may be combined freely: `setReply`, `setForward` (both
 * ADR-0052) and `setResendOrigin` (ADR-0073). `build()` emits whatever was
 * set; `buildReply` / `buildForward` set their own part and keep the rest.
 *
 * ```ts
 * const b = new EnvelopeBuilder(original.text);
 * b.setReply(original.replyTo, original.replyAuthor, original.replyExcerpt);
 * b.setResendOrigin(oldGroupIdB64, original.messageId);
 * const wire = b.build();
 * ```
 */
export class EnvelopeBuilder {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        EnvelopeBuilderFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_envelopebuilder_free(ptr, 0);
    }
    /**
     * Append one attachment ref. Validates the byte-field lengths
     * up front — wrong-length `blobId` / `key` / `nonce` throws
     * immediately, not at build-time.
     * @param {AttachmentRef} r
     */
    add(r) {
        _assertClass(r, AttachmentRef);
        const ret = wasm.envelopebuilder_add(this.__wbg_ptr, r.__wbg_ptr);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Consume the builder + emit the JSON envelope bytes, with every
     * optional part set on it (`setReply`, `setForward`,
     * `setResendOrigin`). Hand the result to
     * `Group.createApplicationMessage(identity, bytes)`.
     * @returns {Uint8Array}
     */
    build() {
        const ptr = this.__destroy_into_raw();
        const ret = wasm.envelopebuilder_build(ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * ADR-0052 — consume the builder + emit a FORWARDED message: the copied
     * text and the re-encrypted attachments added with `add`, with the
     * original author's name as a snapshot and, when both halves are given,
     * the origin (base64 group id + `bigint` message id) for a recipient who
     * is also in that conversation. A forward of a forward passes the
     * ORIGINAL name and origin, never the forwarder's.
     * @param {string | null} [forwarded_from]
     * @param {string | null} [origin_group_id_b64]
     * @param {bigint | null} [origin_message_id]
     * @returns {Uint8Array}
     */
    buildForward(forwarded_from, origin_group_id_b64, origin_message_id) {
        const ptr = this.__destroy_into_raw();
        var ptr0 = isLikeNone(forwarded_from) ? 0 : passStringToWasm0(forwarded_from, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(origin_group_id_b64) ? 0 : passStringToWasm0(origin_group_id_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.envelopebuilder_buildForward(ptr, ptr0, len0, ptr1, len1, !isLikeNone(origin_message_id), isLikeNone(origin_message_id) ? BigInt(0) : origin_message_id);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v3;
    }
    /**
     * ADR-0052 — consume the builder + emit a reply envelope: the same
     * chat message, also naming the DS `messageId` it answers (`replyTo`,
     * a `bigint`) and carrying a snapshot of the quoted message as the
     * replier saw it. The snapshot is what every receiver renders - later
     * edits or deletion of the original never change the quote - and it
     * is one level deep: replying to a reply quotes that reply's own text.
     * Over-long snapshots are truncated on a character boundary; empty
     * ones are not carried.
     * @param {bigint} reply_to
     * @param {string | null} [reply_author]
     * @param {string | null} [reply_excerpt]
     * @returns {Uint8Array}
     */
    buildReply(reply_to, reply_author, reply_excerpt) {
        const ptr = this.__destroy_into_raw();
        var ptr0 = isLikeNone(reply_author) ? 0 : passStringToWasm0(reply_author, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(reply_excerpt) ? 0 : passStringToWasm0(reply_excerpt, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.envelopebuilder_buildReply(ptr, reply_to, ptr0, len0, ptr1, len1);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v3;
    }
    /**
     * Start a new envelope with the given user-visible text.
     * Empty string is valid (attachment-only message).
     * @param {string} text
     */
    constructor(text) {
        const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.envelopebuilder_new(ptr0, len0);
        this.__wbg_ptr = ret;
        EnvelopeBuilderFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * ADR-0052 — give the message a forward attribution; see
     * `buildForward`. The origin is carried only when both halves are
     * given; with neither a name nor a whole origin, any forward set
     * earlier is cleared.
     * @param {string | null} [forwarded_from]
     * @param {string | null} [origin_group_id_b64]
     * @param {bigint | null} [origin_message_id]
     */
    setForward(forwarded_from, origin_group_id_b64, origin_message_id) {
        var ptr0 = isLikeNone(forwarded_from) ? 0 : passStringToWasm0(forwarded_from, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(origin_group_id_b64) ? 0 : passStringToWasm0(origin_group_id_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        wasm.envelopebuilder_setForward(this.__wbg_ptr, ptr0, len0, ptr1, len1, !isLikeNone(origin_message_id), isLikeNone(origin_message_id) ? BigInt(0) : origin_message_id);
    }
    /**
     * ADR-0052 — make the message a reply to the DS `messageId` `replyTo`
     * (a `bigint`), carrying the two snapshots; see `buildReply`. Replaces
     * a reply set earlier.
     * @param {bigint} reply_to
     * @param {string | null} [reply_author]
     * @param {string | null} [reply_excerpt]
     */
    setReply(reply_to, reply_author, reply_excerpt) {
        var ptr0 = isLikeNone(reply_author) ? 0 : passStringToWasm0(reply_author, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(reply_excerpt) ? 0 : passStringToWasm0(reply_excerpt, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len1 = WASM_VECTOR_LEN;
        wasm.envelopebuilder_setReply(this.__wbg_ptr, reply_to, ptr0, len0, ptr1, len1);
    }
    /**
     * ADR-0073 — mark the message as a RE-SEND into a successor
     * one-to-one: `originGroupIdB64` is the base64 group id of the
     * conversation it was originally sent in and `originMessageId` (a
     * `bigint`) its DS `messageId` there. The marker is never rendered; it
     * lets a device that already holds the original show the message once.
     * Carried only when both halves are given (and the group id is
     * non-empty and within its bound); otherwise any marker set earlier is
     * cleared.
     * @param {string | null} [origin_group_id_b64]
     * @param {bigint | null} [origin_message_id]
     */
    setResendOrigin(origin_group_id_b64, origin_message_id) {
        var ptr0 = isLikeNone(origin_group_id_b64) ? 0 : passStringToWasm0(origin_group_id_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        wasm.envelopebuilder_setResendOrigin(this.__wbg_ptr, ptr0, len0, !isLikeNone(origin_message_id), isLikeNone(origin_message_id) ? BigInt(0) : origin_message_id);
    }
}
if (Symbol.dispose) EnvelopeBuilder.prototype[Symbol.dispose] = EnvelopeBuilder.prototype.free;

/**
 * One message, reduced to what ranking needs.
 */
export class FeedCandidate {
    static __unwrap(jsValue) {
        if (!(jsValue instanceof FeedCandidate)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FeedCandidateFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_feedcandidate_free(ptr, 0);
    }
    /**
     * Build a candidate. `messageId`, `replyTo` and `timeMs` are BigInt;
     * `replyTo` is `undefined` when it replies to nothing. `text` is used
     * only by the word filters and never logged.
     * @param {number} conversation
     * @param {bigint} message_id
     * @param {bigint | null | undefined} reply_to
     * @param {bigint} time_ms
     * @param {boolean} from_self
     * @param {boolean} from_contact
     * @param {boolean} mentions_me
     * @param {boolean} replies_to_me
     * @param {number} reactions_distinct
     * @param {boolean} has_media
     * @param {string} text
     */
    constructor(conversation, message_id, reply_to, time_ms, from_self, from_contact, mentions_me, replies_to_me, reactions_distinct, has_media, text) {
        const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.feedcandidate_new(conversation, message_id, !isLikeNone(reply_to), isLikeNone(reply_to) ? BigInt(0) : reply_to, time_ms, from_self, from_contact, mentions_me, replies_to_me, reactions_distinct, has_media, ptr0, len0);
        this.__wbg_ptr = ret;
        FeedCandidateFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) FeedCandidate.prototype[Symbol.dispose] = FeedCandidate.prototype.free;

/**
 * One conversation the client offers messages from.
 */
export class FeedConversation {
    static __unwrap(jsValue) {
        if (!(jsValue instanceof FeedConversation)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FeedConversationFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_feedconversation_free(ptr, 0);
    }
    /**
     * `id` is compared with the rule's include / exclude lists; `audience`
     * is the member count (ignored for a channel).
     * @param {Uint8Array} id
     * @param {FeedConversationKind} kind
     * @param {boolean} muted
     * @param {number} audience
     */
    constructor(id, kind, muted, audience) {
        const ptr0 = passArray8ToWasm0(id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.feedconversation_new(ptr0, len0, kind, muted, audience);
        this.__wbg_ptr = ret;
        FeedConversationFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) FeedConversation.prototype[Symbol.dispose] = FeedConversation.prototype.free;

/**
 * Group or channel. One-to-one chats have no value here.
 * @enum {0 | 1}
 */
export const FeedConversationKind = Object.freeze({
    /**
     * A group.
     */
    Group: 0, "0": "Group",
    /**
     * A channel.
     */
    Channel: 1, "1": "Channel",
});

/**
 * One item to draw: a candidate and the replies folded under it.
 */
export class FeedEntry {
    static __wrap(ptr) {
        const obj = Object.create(FeedEntry.prototype);
        obj.__wbg_ptr = ptr;
        FeedEntryFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FeedEntryFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_feedentry_free(ptr, 0);
    }
    /**
     * Index into the candidates array.
     * @returns {number}
     */
    get index() {
        const ret = wasm.feedentry_index(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Indices of its replies, oldest first.
     * @returns {Uint32Array}
     */
    get replies() {
        const ret = wasm.feedentry_replies(this.__wbg_ptr);
        var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
}
if (Symbol.dispose) FeedEntry.prototype[Symbol.dispose] = FeedEntry.prototype.free;

/**
 * How fast an item's recency fades.
 * @enum {0 | 1 | 2}
 */
export const FeedHalfLife = Object.freeze({
    /**
     * Six hours.
     */
    FewHours: 0, "0": "FewHours",
    /**
     * One day.
     */
    Day: 1, "1": "Day",
    /**
     * One week.
     */
    Week: 2, "2": "Week",
});

/**
 * A built-in feed.
 * @enum {0 | 1 | 2}
 */
export const FeedPreset = Object.freeze({
    /**
     * Every group and channel, newest first.
     */
    Latest: 0, "0": "Latest",
    /**
     * The last three days, group messages ranked by reactions and replies.
     */
    Popular: 1, "1": "Popular",
    /**
     * Mentions, replies to the person, their own reacted-to messages.
     */
    ForMe: 2, "2": "ForMe",
});

/**
 * A feed rule. Field meanings and bounds are `lakko_feed::FeedRule`'s.
 */
export class FeedRule {
    static __wrap(ptr) {
        const obj = Object.create(FeedRule.prototype);
        obj.__wbg_ptr = ptr;
        FeedRuleFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FeedRuleFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_feedrule_free(ptr, 0);
    }
    /**
     * Take channel posts.
     * @returns {boolean}
     */
    get channels() {
        const ret = wasm.feedrule_channels(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Decode a rule someone else made.
     *
     * # Errors
     *
     * Throws an `Error` with `feedRuleError` set to the reason code.
     * @param {Uint8Array} bytes
     * @returns {FeedRule}
     */
    static decode(bytes) {
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.feedrule_decode(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return FeedRule.__wrap(ret[0]);
    }
    /**
     * Encode for storage, a `.lakkofeed` file or a link.
     *
     * # Errors
     *
     * Throws an `Error` with `feedRuleError` set to the reason code.
     * @returns {Uint8Array}
     */
    encode() {
        const ret = wasm.feedrule_encode(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Never these conversations. Never exported.
     * @returns {Array<any>}
     */
    get exclude() {
        const ret = wasm.feedrule_exclude(this.__wbg_ptr);
        return ret;
    }
    /**
     * The rule as it may leave this device: conversation ids removed.
     * @returns {FeedRule}
     */
    export() {
        const ret = wasm.feedrule_export(this.__wbg_ptr);
        return FeedRule.__wrap(ret);
    }
    /**
     * Only mentions, replies to the person, and their own reacted-to messages.
     * @returns {boolean}
     */
    get forMe() {
        const ret = wasm.feedrule_forMe(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Take group messages.
     * @returns {boolean}
     */
    get groups() {
        const ret = wasm.feedrule_groups(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * How fast recency fades.
     * @returns {FeedHalfLife}
     */
    get halfLife() {
        const ret = wasm.feedrule_halfLife(this.__wbg_ptr);
        return ret;
    }
    /**
     * Never the person's own messages.
     * @returns {boolean}
     */
    get hideOwn() {
        const ret = wasm.feedrule_hideOwn(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Drop a message containing any of these (lowercase, whole words).
     * @returns {string[]}
     */
    get hideWords() {
        const ret = wasm.feedrule_hideWords(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Index into the client's fixed icon list.
     * @returns {number}
     */
    get icon() {
        const ret = wasm.feedrule_icon(this.__wbg_ptr);
        return ret;
    }
    /**
     * Only these conversations, when non-empty. Never exported.
     * @returns {Array<any>}
     */
    get include() {
        const ret = wasm.feedrule_include(this.__wbg_ptr);
        return ret;
    }
    /**
     * Take muted conversations too.
     * @returns {boolean}
     */
    get includeMuted() {
        const ret = wasm.feedrule_includeMuted(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Only messages with an attachment.
     * @returns {boolean}
     */
    get mediaOnly() {
        const ret = wasm.feedrule_mediaOnly(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Only messages with at least this many people reacting.
     * @returns {number}
     */
    get minReactions() {
        const ret = wasm.feedrule_minReactions(this.__wbg_ptr);
        return ret;
    }
    /**
     * The feed's name.
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.feedrule_name(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * A rule with this name that takes everything, newest first.
     * @param {string} name
     */
    constructor(name) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.feedrule_new(ptr0, len0);
        this.__wbg_ptr = ret;
        FeedRuleFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * When non-empty, keep only messages containing one of these.
     * @returns {string[]}
     */
    get onlyWords() {
        const ret = wasm.feedrule_onlyWords(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The rule behind a preset.
     * @param {FeedPreset} preset
     * @returns {FeedRule}
     */
    static preset(preset) {
        const ret = wasm.feedrule_preset(preset);
        return FeedRule.__wrap(ret);
    }
    /**
     * Never these conversations.
     *
     * # Errors
     *
     * Throws when an element is not a `Uint8Array`.
     * @param {Array<any>} ids
     */
    setExclude(ids) {
        const ret = wasm.feedrule_setExclude(this.__wbg_ptr, ids);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Only these conversations, when non-empty.
     *
     * # Errors
     *
     * Throws when an element is not a `Uint8Array`.
     * @param {Array<any>} ids
     */
    setInclude(ids) {
        const ret = wasm.feedrule_setInclude(this.__wbg_ptr, ids);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Take channel posts.
     * @param {boolean} value
     */
    set channels(value) {
        wasm.feedrule_set_channels(this.__wbg_ptr, value);
    }
    /**
     * Only mentions, replies to the person, and their own reacted-to messages.
     * @param {boolean} value
     */
    set forMe(value) {
        wasm.feedrule_set_forMe(this.__wbg_ptr, value);
    }
    /**
     * Take group messages.
     * @param {boolean} value
     */
    set groups(value) {
        wasm.feedrule_set_groups(this.__wbg_ptr, value);
    }
    /**
     * How fast recency fades.
     * @param {FeedHalfLife} value
     */
    set halfLife(value) {
        wasm.feedrule_set_halfLife(this.__wbg_ptr, value);
    }
    /**
     * Never the person's own messages.
     * @param {boolean} value
     */
    set hideOwn(value) {
        wasm.feedrule_set_hideOwn(this.__wbg_ptr, value);
    }
    /**
     * Drop a message containing any of these (lowercase, whole words).
     * @param {string[]} value
     */
    set hideWords(value) {
        const ptr0 = passArrayJsValueToWasm0(value, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.feedrule_set_hideWords(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Index into the client's fixed icon list.
     * @param {number} value
     */
    set icon(value) {
        wasm.feedrule_set_icon(this.__wbg_ptr, value);
    }
    /**
     * Take muted conversations too.
     * @param {boolean} value
     */
    set includeMuted(value) {
        wasm.feedrule_set_includeMuted(this.__wbg_ptr, value);
    }
    /**
     * Only messages with an attachment.
     * @param {boolean} value
     */
    set mediaOnly(value) {
        wasm.feedrule_set_mediaOnly(this.__wbg_ptr, value);
    }
    /**
     * Only messages with at least this many people reacting.
     * @param {number} value
     */
    set minReactions(value) {
        wasm.feedrule_set_minReactions(this.__wbg_ptr, value);
    }
    /**
     * The feed's name.
     * @param {string} value
     */
    set name(value) {
        const ptr0 = passStringToWasm0(value, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.feedrule_set_name(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * When non-empty, keep only messages containing one of these.
     * @param {string[]} value
     */
    set onlyWords(value) {
        const ptr0 = passArrayJsValueToWasm0(value, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.feedrule_set_onlyWords(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Set by export when it removed include / exclude.
     * @param {boolean} value
     */
    set sourcesStripped(value) {
        wasm.feedrule_set_sourcesStripped(this.__wbg_ptr, value);
    }
    /**
     * How often suggestions appear.
     * @param {FeedSuggestions} value
     */
    set suggestions(value) {
        wasm.feedrule_set_suggestions(this.__wbg_ptr, value);
    }
    /**
     * Weight of messages from contacts, 0..=100.
     * @param {number} value
     */
    set weightFromContacts(value) {
        wasm.feedrule_set_weightFromContacts(this.__wbg_ptr, value);
    }
    /**
     * Weight of attachments, 0..=100.
     * @param {number} value
     */
    set weightHasMedia(value) {
        wasm.feedrule_set_weightHasMedia(this.__wbg_ptr, value);
    }
    /**
     * Weight of mentions and replies to the person, 0..=100.
     * @param {number} value
     */
    set weightMentionsMe(value) {
        wasm.feedrule_set_weightMentionsMe(this.__wbg_ptr, value);
    }
    /**
     * Weight of reactions, 0..=100.
     * @param {number} value
     */
    set weightReactions(value) {
        wasm.feedrule_set_weightReactions(this.__wbg_ptr, value);
    }
    /**
     * Weight of recency, 0..=100.
     * @param {number} value
     */
    set weightRecency(value) {
        wasm.feedrule_set_weightRecency(this.__wbg_ptr, value);
    }
    /**
     * Weight of replies, 0..=100.
     * @param {number} value
     */
    set weightReplies(value) {
        wasm.feedrule_set_weightReplies(this.__wbg_ptr, value);
    }
    /**
     * How far back the feed looks, 1..=30 days.
     * @param {number} value
     */
    set windowDays(value) {
        wasm.feedrule_set_windowDays(this.__wbg_ptr, value);
    }
    /**
     * Set by export when it removed include / exclude.
     * @returns {boolean}
     */
    get sourcesStripped() {
        const ret = wasm.feedrule_sourcesStripped(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * How often suggestions appear.
     * @returns {FeedSuggestions}
     */
    get suggestions() {
        const ret = wasm.feedrule_suggestions(this.__wbg_ptr);
        return ret;
    }
    /**
     * Check every bound, to explain a disabled Save.
     *
     * # Errors
     *
     * Throws an `Error` with `feedRuleError` set to the reason code.
     */
    validate() {
        const ret = wasm.feedrule_validate(this.__wbg_ptr);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Weight of messages from contacts, 0..=100.
     * @returns {number}
     */
    get weightFromContacts() {
        const ret = wasm.feedrule_weightFromContacts(this.__wbg_ptr);
        return ret;
    }
    /**
     * Weight of attachments, 0..=100.
     * @returns {number}
     */
    get weightHasMedia() {
        const ret = wasm.feedrule_weightHasMedia(this.__wbg_ptr);
        return ret;
    }
    /**
     * Weight of mentions and replies to the person, 0..=100.
     * @returns {number}
     */
    get weightMentionsMe() {
        const ret = wasm.feedrule_weightMentionsMe(this.__wbg_ptr);
        return ret;
    }
    /**
     * Weight of reactions, 0..=100.
     * @returns {number}
     */
    get weightReactions() {
        const ret = wasm.feedrule_weightReactions(this.__wbg_ptr);
        return ret;
    }
    /**
     * Weight of recency, 0..=100.
     * @returns {number}
     */
    get weightRecency() {
        const ret = wasm.feedrule_weightRecency(this.__wbg_ptr);
        return ret;
    }
    /**
     * Weight of replies, 0..=100.
     * @returns {number}
     */
    get weightReplies() {
        const ret = wasm.feedrule_weightReplies(this.__wbg_ptr);
        return ret;
    }
    /**
     * How far back the feed looks, 1..=30 days.
     * @returns {number}
     */
    get windowDays() {
        const ret = wasm.feedrule_windowDays(this.__wbg_ptr);
        return ret;
    }
}
if (Symbol.dispose) FeedRule.prototype[Symbol.dispose] = FeedRule.prototype.free;

/**
 * How often a suggested channel appears in a feed (ADR-0059).
 * @enum {0 | 1 | 2}
 */
export const FeedSuggestions = Object.freeze({
    /**
     * Never.
     */
    Never: 0, "0": "Never",
    /**
     * Rarely.
     */
    Rarely: 1, "1": "Rarely",
    /**
     * Sometimes.
     */
    Sometimes: 2, "2": "Sometimes",
});

/**
 * A conversation's flags, stamped.
 */
export class FlagsRegister {
    static __wrap(ptr) {
        const obj = Object.create(FlagsRegister.prototype);
        obj.__wbg_ptr = ptr;
        FlagsRegisterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FlagsRegisterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_flagsregister_free(ptr, 0);
    }
    /**
     * Archived.
     * @returns {boolean}
     */
    get archived() {
        const ret = wasm.flagsregister_archived(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The conversation's MLS group id.
     * @returns {Uint8Array}
     */
    get groupId() {
        const ret = wasm.flagsregister_groupId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * HLC stamp.
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.flagsregister_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Muted.
     * @returns {boolean}
     */
    get muted() {
        const ret = wasm.flagsregister_muted(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Pinned.
     * @returns {boolean}
     */
    get pinned() {
        const ret = wasm.flagsregister_pinned(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Marked unread by hand.
     * @returns {boolean}
     */
    get unreadOverride() {
        const ret = wasm.flagsregister_unreadOverride(this.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) FlagsRegister.prototype[Symbol.dispose] = FlagsRegister.prototype.free;

/**
 * A live folder edit under construction: only the registers that changed,
 * each with its own stamp. At most 1024 registers.
 */
export class FolderChangeSet {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FolderChangeSetFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_folderchangeset_free(ptr, 0);
    }
    /**
     * Add a conversation-end register a `compose` call returned.
     * @param {ConversationEndRegister} register
     */
    addConversationEnd(register) {
        _assertClass(register, ConversationEndRegister);
        wasm.folderchangeset_addConversationEnd(this.__wbg_ptr, register.__wbg_ptr);
    }
    /**
     * Write one conversation's membership: `state` `0` none (the way to
     * take a chat out of the hand-added or hand-excluded set), `1`
     * included, `2` excluded.
     * @param {bigint} hlc
     * @param {Uint8Array} folder_id
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @param {number} state
     */
    addMember(hlc, folder_id, ref_kind, ref_id, state) {
        const ptr0 = passArray8ToWasm0(folder_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.folderchangeset_addMember(this.__wbg_ptr, hlc, ptr0, len0, ref_kind, ptr1, len1, state);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Write a folder's name and toggles. `includeKinds`: bit0 one-to-one,
     * bit1 groups, bit2 channels. `excludeFlags`: bit0 muted, bit1 read,
     * bit2 archived. Throws on a bad folder id; the name and bits are
     * checked by [`Self::validate`] and by every consumer.
     * @param {bigint} hlc
     * @param {Uint8Array} folder_id
     * @param {string} name
     * @param {number} include_kinds
     * @param {number} exclude_flags
     */
    addMeta(hlc, folder_id, name, include_kinds, exclude_flags) {
        const ptr0 = passArray8ToWasm0(folder_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.folderchangeset_addMeta(this.__wbg_ptr, hlc, ptr0, len0, ptr1, len1, include_kinds, exclude_flags);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Write a folder's whole pin list (at most 10), top first.
     * `refKinds[i]` / `refIds[i]` are one reference; the lengths must
     * agree.
     * @param {bigint} hlc
     * @param {Uint8Array} folder_id
     * @param {Uint8Array} ref_kinds
     * @param {Uint8Array[]} ref_ids
     */
    addPins(hlc, folder_id, ref_kinds, ref_ids) {
        const ptr0 = passArray8ToWasm0(folder_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(ref_kinds, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArrayJsValueToWasm0(ref_ids, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.folderchangeset_addPins(this.__wbg_ptr, hlc, ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Delete a folder (a tombstone at `hlc`).
     * @param {bigint} hlc
     * @param {Uint8Array} folder_id
     */
    addRemoved(hlc, folder_id) {
        const ptr0 = passArray8ToWasm0(folder_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.folderchangeset_addRemoved(this.__wbg_ptr, hlc, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Save a message (ADR-0065 section 4), stamped `hlc`.
     *
     * Pass what the client holds: `author` and `excerpt` are cut to their
     * bounds (255 and 512 bytes) on a character boundary, so the whole
     * message text may be passed as `excerpt`; a blob id named twice is
     * kept once. `attachmentKind` is a short token, empty when the message
     * has no attachment. `blobIds` is the 32-byte ids of the message's
     * files and thumbnails, flat-concatenated (may be empty). `sentAtMs`
     * is the message's own time in UNIX millis as this device shows it
     * (`0` when unknown), so any device can show the message date on the
     * Saved screen before it holds the message.
     *
     * Throws when `blobIds` is not a multiple of 32 bytes or names more
     * than 128 distinct ids, `attachmentKind` is longer than 32 bytes, or
     * the conversation reference is not well formed. Nothing is added
     * then.
     * @param {bigint} hlc
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @param {bigint} message_id
     * @param {bigint} saved_at_ms
     * @param {bigint} sent_at_ms
     * @param {string} author
     * @param {string} excerpt
     * @param {string} attachment_kind
     * @param {Uint8Array} blob_ids
     */
    addSaved(hlc, ref_kind, ref_id, message_id, saved_at_ms, sent_at_ms, author, excerpt, attachment_kind, blob_ids) {
        const ptr0 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(author, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(excerpt, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passStringToWasm0(attachment_kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len3 = WASM_VECTOR_LEN;
        const ptr4 = passArray8ToWasm0(blob_ids, wasm.__wbindgen_malloc);
        const len4 = WASM_VECTOR_LEN;
        const ret = wasm.folderchangeset_addSaved(this.__wbg_ptr, hlc, ref_kind, ptr0, len0, message_id, saved_at_ms, sent_at_ms, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Remove a message from saved (a tombstone at `hlc`, kept on every
     * device; saving the message again later brings it back). Throws on a
     * conversation reference that is not well formed.
     * @param {bigint} hlc
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @param {bigint} message_id
     */
    addSavedRemoved(hlc, ref_kind, ref_id, message_id) {
        const ptr0 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.folderchangeset_addSavedRemoved(this.__wbg_ptr, hlc, ref_kind, ptr0, len0, message_id);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * An empty change set.
     */
    constructor() {
        const ret = wasm.folderchangeset_new();
        this.__wbg_ptr = ret;
        FolderChangeSetFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Number of registers carried.
     * @returns {number}
     */
    get registerCount() {
        const ret = wasm.folderchangeset_registerCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Write the whole folder order (at most 256 ids), first tab first.
     * Replaces an order set earlier on this change set.
     * @param {bigint} hlc
     * @param {Uint8Array[]} folder_ids
     */
    setOrder(hlc, folder_ids) {
        const ptr0 = passArrayJsValueToWasm0(folder_ids, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.folderchangeset_setOrder(this.__wbg_ptr, hlc, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Throw when the change set breaks an ADR-0058 section 4 bound (name,
     * bitfields, pin / order / register caps) or a saved-message bound.
     */
    validate() {
        const ret = wasm.folderchangeset_validate(this.__wbg_ptr);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
}
if (Symbol.dispose) FolderChangeSet.prototype[Symbol.dispose] = FolderChangeSet.prototype.free;

/**
 * A conversation a folder names. `kind` is the wire number (`1`
 * one-to-one, `2` group, `3` channel); `id` the MLS group id, or the
 * channel id.
 */
export class FolderConversationRef {
    static __wrap(ptr) {
        const obj = Object.create(FolderConversationRef.prototype);
        obj.__wbg_ptr = ptr;
        FolderConversationRefFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FolderConversationRefFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_folderconversationref_free(ptr, 0);
    }
    /**
     * The MLS group id, or the channel id.
     * @returns {Uint8Array}
     */
    get id() {
        const ret = wasm.folderconversationref_id(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `1` one-to-one, `2` group, `3` channel.
     * @returns {number}
     */
    get kind() {
        const ret = wasm.folderconversationref_kind(this.__wbg_ptr);
        return ret;
    }
}
if (Symbol.dispose) FolderConversationRef.prototype[Symbol.dispose] = FolderConversationRef.prototype.free;

/**
 * A chat folder's rule in row terms (ADR-0058 section 6).
 *
 * `includeKinds` / `excludeFlags` are the folder's bits as the synced
 * state returns them (include: bit0 one-to-one, bit1 groups, bit2
 * channels; exclude: bit0 muted, bit1 read, bit2 archived). `included` /
 * `excluded` are the row ids the client maps the folder's hand-added and
 * hand-excluded references to.
 */
export class FolderRule {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FolderRuleFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_folderrule_free(ptr, 0);
    }
    /**
     * Build the rule.
     * @param {number} include_kinds
     * @param {number} exclude_flags
     * @param {string[]} included
     * @param {string[]} excluded
     */
    constructor(include_kinds, exclude_flags, included, excluded) {
        const ptr0 = passArrayJsValueToWasm0(included, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(excluded, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.folderrule_new(include_kinds, exclude_flags, ptr0, len0, ptr1, len1);
        this.__wbg_ptr = ret;
        FolderRuleFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) FolderRule.prototype[Symbol.dispose] = FolderRule.prototype.free;

/**
 * One folder as the client shows it.
 */
export class FolderView {
    static __wrap(ptr) {
        const obj = Object.create(FolderView.prototype);
        obj.__wbg_ptr = ptr;
        FolderViewFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        FolderViewFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_folderview_free(ptr, 0);
    }
    /**
     * Exclude toggles (bit0 muted, bit1 read, bit2 archived).
     * @returns {number}
     */
    get excludeFlags() {
        const ret = wasm.folderview_excludeFlags(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Conversations excluded by hand, sorted by `(kind, id)`.
     * @returns {FolderConversationRef[]}
     */
    excluded() {
        const ret = wasm.folderview_excluded(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * 16 bytes.
     * @returns {Uint8Array}
     */
    get folderId() {
        const ret = wasm.folderview_folderId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Kind toggles (bit0 one-to-one, bit1 groups, bit2 channels).
     * @returns {number}
     */
    get includeKinds() {
        const ret = wasm.folderview_includeKinds(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Conversations added by hand, sorted by `(kind, id)`.
     * @returns {FolderConversationRef[]}
     */
    included() {
        const ret = wasm.folderview_included(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Stamp of the meta register. A folder is listed only once its name
     * has arrived, so the meta getters always read a real value.
     * @returns {bigint}
     */
    get metaHlc() {
        const ret = wasm.folderview_metaHlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The folder name.
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.folderview_name(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The folder's pins, in stored order.
     * @returns {FolderConversationRef[]}
     */
    pinned() {
        const ret = wasm.folderview_pinned(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
}
if (Symbol.dispose) FolderView.prototype[Symbol.dispose] = FolderView.prototype.free;

/**
 * Lakko MLS conversation group. Mirror of UniFFI's `Group`.
 */
export class Group {
    static __wrap(ptr) {
        const obj = Object.create(Group.prototype);
        obj.__wbg_ptr = ptr;
        GroupFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_group_free(ptr, 0);
    }
    /**
     * **Lost.** Discard the sweep staged by [`Self::stage_pending_proposals`],
     * returning this group to the epoch the sweep was built against so it can
     * apply the winner's commit and carry on (ADR-0045).
     *
     * This touches one group and no other: the rollback sets **this** group's
     * state back to operational and writes nothing else, unlike a
     * whole-provider `restoreStorage`, which would roll sibling groups back
     * past commits they have already published.
     *
     * Returns [`StagedSweepOutcome::Abandoned`], or
     * [`StagedSweepOutcome::NothingStaged`] when there was nothing staged —
     * which is what every eagerly-merged path reports, and is why calling this
     * after one cannot undo anything.
     *
     * Throws when the discard did not take effect: the core re-reads the group
     * state afterwards and refuses to report a rollback it did not actually
     * perform (fail-closed, invariant #4).
     * @param {Identity} identity
     * @returns {StagedSweepOutcome}
     */
    abandonStagedSweep(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.group_abandonStagedSweep(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0];
    }
    /**
     * Add a member from their published KeyPackage (TLS wire form).
     * @param {Identity} identity
     * @param {Uint8Array} key_package
     * @returns {AddMember}
     */
    addMember(identity, key_package) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_addMember(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AddMember.__wrap(ret[0]);
    }
    /**
     * Add several members in ONE commit with ONE Welcome (ADR-0050): a
     * person's devices, or a founder's own other devices, must share a
     * commit, else every leaf but the last is stranded at a Welcome epoch the
     * founder advanced past with nobody to broadcast the commit to. The
     * returned Welcome is delivered byte-identical to each added leaf's
     * mailbox. `keyPackages` is a JS `Uint8Array[]`.
     * @param {Identity} identity
     * @param {Uint8Array[]} key_packages
     * @returns {AddMember}
     */
    addMembers(identity, key_packages) {
        _assertClass(identity, Identity);
        const ptr0 = passArrayJsValueToWasm0(key_packages, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_addMembers(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AddMember.__wrap(ret[0]);
    }
    /**
     * Approve a retained external-join request — **proposes** adding the
     * joiner (ADR-0027 G-3, on the ADR-0045 proposal path since S7).
     * `proposal_ref` is the joiner's signature key from a
     * `ProcessedMessage.externalJoinSignatureKey` or `pendingExternalJoins`.
     *
     * **Returns the wire-encoded Proposal to publish — not a Commit, and no
     * Welcome.** The epoch does not advance; the Welcome is produced by
     * whichever device sweeps this into a commit (`commitPendingProposals` /
     * `stagePendingProposals`, whose `welcome` the sweeping device posts as a
     * `WelcomeRendezvous` for each added key). That is the whole of S7: two
     * admins approving two joiners against one epoch used to eagerly merge two
     * commits and fork the group; now they compose into one sweep.
     *
     * The approval goes on the wire as an ordinary admin-authored `Add` — the
     * admin's own MLS-authenticated signature *is* the approval — so it is
     * indistinguishable from adding a contact and needs no new authorisation
     * channel. The joiner's own external proposal is still never stored and
     * never committed: a stranger with a `/g/` link may only ask.
     *
     * Role-gated to owner/admin, locally and again by every peer. A refused
     * approve emits nothing and leaves the request pending, so it can be
     * retried. A group with no role table has no committer and is refused —
     * see `PublisherGroup.approveExternalJoin`, whose return type differs
     * because its outcome does.
     * @param {Identity} identity
     * @param {Uint8Array} proposal_ref
     * @returns {Uint8Array}
     */
    approveExternalJoin(identity, proposal_ref) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(proposal_ref, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_approveExternalJoin(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Sweep the authorised pending proposals into **one** Commit and merge it
     * immediately (ADR-0045) — the **eager** committer path.
     *
     * **A committer that can lose a race must not use this.** It is exactly
     * [`Self::stage_pending_proposals`] followed at once by
     * [`Self::merge_staged_sweep`], and merging before the DS journal has
     * ordered the commit is precisely what strands the loser at an epoch
     * nobody else reached — silently and permanently (G-38 residual 1). Two
     * devices can both act as committer during any handover, so unless the
     * caller can prove it is the only one committing, the staged pair is the
     * correct entry point. This one is kept, unchanged, for the callers that
     * have not migrated.
     *
     * Returns `undefined` when nothing authorised is pending: deliberately not
     * an empty commit, which would advance the epoch (and race other
     * committers) for no effect. Otherwise the local state is already at the
     * new epoch when this returns, so the caller MUST durably record the
     * returned artifacts before publishing them (ADR-0040).
     *
     * Role-gated to owner/admin, and the sweep re-runs the ADR-0045
     * authorisation rule over every candidate, so this can never commit an
     * unauthorised proposal however the pending store was populated.
     * @param {Identity} identity
     * @returns {ProposalCommit | undefined}
     */
    commitPendingProposals(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.group_commitPendingProposals(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : ProposalCommit.__wrap(ret[0]);
    }
    /**
     * **Another** member's committer rank, by its 32-byte Ed25519 leaf
     * signature public key: `0` for the owner, `k + 1` for the `k`-th admin in
     * the role table's canonical order, `n + 1 + i` for any other member
     * account (ADR-0050 section 2.5a: `n` admins, `i` the account's position
     * among the non-admin member accounts sorted bytewise by account key),
     * held by every leaf of the account (ADR-0050 section 2.5a item 6);
     * `undefined` for a non-member, a wrong-length key, or a group with no
     * role table.
     *
     * The peer-facing twin of [`Self::own_committer_rank`], and the input
     * `BeaconObservation.rank` needs: a committer beacon carries no identity,
     * so the receiver resolves its MLS sender leaf to a signature key through
     * [`Self::members`] and ranks that key here.
     *
     * # Rank the key when you take the verdict, not when the beacon arrived
     *
     * A rank is a **position** in the role table's canonical order, not a
     * stable identity: promoting, demoting or removing any admin renumbers
     * every admin sorting after it. `actingCommitterVerdict` compares each
     * observation's rank against this device's own, which is necessarily
     * derived from the current table — so a rank captured at receipt and
     * compared later is not merely stale, it is a position in a *different*
     * ordering. Persist the sender's signature key with each observed beacon
     * and call this at fold time. Never persist a leaf index for this purpose:
     * OpenMLS reuses a vacated leaf for the next member added, so a stored
     * leaf can silently come to name someone else.
     *
     * As with [`Self::own_committer_rank`], `undefined` and `0` are **not**
     * the same thing — `0` is the owner — so `if (rank)` is wrong in both
     * directions. The owner and admins may sweep the whole set; a plain
     * member, ranked after them, sweeps only device admissions and its own
     * account's self-service proposals, and beacons only while it holds one.
     *
     * # Errors
     *
     * `JsError` if the internal group mutex was poisoned.
     * @param {Uint8Array} signature_key
     * @returns {number | undefined}
     */
    committerRank(signature_key) {
        const ptr0 = passArray8ToWasm0(signature_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_committerRank(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === Number.MAX_SAFE_INTEGER ? undefined : ret[0];
    }
    /**
     * Encrypt an application payload for the current epoch.
     * @param {Identity} identity
     * @param {Uint8Array} plaintext
     * @returns {Uint8Array}
     */
    createApplicationMessage(identity, plaintext) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_createApplicationMessage(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Delete this group's state on the word of this account's other devices,
     * without having applied the commit that ended it (ADR-0087 section 9,
     * C-88). The one way a device offline past the journal's retention stops
     * holding a group.
     *
     * **Call this only holding a `conversationEnded` register from the
     * account's own synced state, and pass that register's `epoch`.** The core
     * cannot see the register: called on anything else, this deletes a live
     * membership. Never on a refused Delivery Service read.
     *
     * Throws a `groupStillActive` refusal (`err.refusalGroupId`), changing
     * nothing, when the stored group is active at an epoch above
     * `endedEpoch`: this device came back after that end, so it answers with
     * a `joined` register. A group that is not active goes at any epoch.
     * Repeating it changes nothing. Persist the snapshot afterwards.
     * @param {Identity} identity
     * @param {bigint} ended_epoch
     */
    deleteAfterEndedElsewhere(identity, ended_epoch) {
        _assertClass(identity, Identity);
        const ret = wasm.group_deleteAfterEndedElsewhere(this.__wbg_ptr, identity.__wbg_ptr, ended_epoch);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Delete this group's state from `identity`'s storage, for a group this
     * device is out of (C-88). This is local: it tells nobody anything and
     * does not leave the group. Repeating it changes nothing. Persist the
     * snapshot afterwards.
     *
     * Throws a `groupStillActive` refusal (`err.refusalGroupId`), changing
     * nothing, when the group stored under this id is active, which also
     * covers a stale handle of a group a Welcome has since replaced.
     * @param {Identity} identity
     */
    deleteLocally(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.group_deleteLocally(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * The ADR-0053 access signer for this journal at the current epoch:
     * `export_secret("lakko/ds/access/v1", groupId, 32)` wrapped as a signer
     * whose `sign` produces the `x-lakko-ds-proof` header a claimed journal
     * requires and whose `groupClaimBodyJson` registers the epoch's key.
     * Re-derive after every commit.
     * @param {Identity} identity
     * @returns {DsProofSigner}
     */
    dsAccessSigner(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.group_dsAccessSigner(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsProofSigner.__wrap(ret[0]);
    }
    /**
     * The ADR-0053 access signer for the epoch a staged sweep moves to
     * (N+1), while this group still sits at N; `undefined` when nothing is
     * staged. Register its key, proven under the current signer, BEFORE
     * publishing the sweep.
     * @param {Identity} identity
     * @returns {DsProofSigner | undefined}
     */
    dsStagedAccessSigner(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.group_dsStagedAccessSigner(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : DsProofSigner.__wrap(ret[0]);
    }
    /**
     * Current group epoch.
     * @returns {bigint}
     */
    get epoch() {
        const ret = wasm.group_epoch(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return BigInt.asUintN(64, ret[0]);
    }
    /**
     * Derive a `length`-byte secret bound to the current epoch.
     * @param {Identity} identity
     * @param {string} label
     * @param {Uint8Array} context
     * @param {number} length
     * @returns {Uint8Array}
     */
    exportSecret(identity, label, context, length) {
        _assertClass(identity, Identity);
        const ptr0 = passStringToWasm0(label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(context, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.group_exportSecret(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, ptr1, len1, length);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v3;
    }
    /**
     * MLS `group_id` (RFC 9420 §6.2). Used by `apps/web` as
     * the routing key for `POST /ds/group/{group_id_hex}`. The
     * bytes are opaque — 32 random bytes minted by
     * `Group::create` (`lakko_mls::GROUP_ID_LEN`), which is the
     * width the DS requires of that path segment. Note this is
     * *not* OpenMLS's own default: `GroupId::random` is 16
     * bytes, so `Group::create` mints the id itself. Identical
     * for every member of the group at every epoch.
     * @returns {Uint8Array}
     */
    get groupId() {
        const ret = wasm.group_groupId(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The conversation's kind (ADR-0062), read from the GroupContext;
     * `undefined` only for a group that carries none (not a conversation).
     * @returns {GroupKind | undefined}
     */
    groupKind() {
        const ret = wasm.group_groupKind(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : ret[0];
    }
    /**
     * `true` when a sweep built by [`Self::stage_pending_proposals`] is staged
     * and still awaiting its journal verdict (ADR-0045).
     *
     * **This is the restart-time obligation check, and it is why the verdict
     * lives on the group rather than on the staged value.** A staged commit is
     * persisted by OpenMLS and read back by `Group.load`, so it outlives the
     * page — a JS object returned by the stage call could not. After loading a
     * group, a client that finds this `true` still owes a verdict on a commit
     * it may have already published, and must resolve it (ADR-0040's restart
     * sweep re-publishes, re-reads the journal order, then merges or
     * abandons).
     *
     * Leaving one unresolved is not inert: while a sweep is staged, OpenMLS
     * refuses this group's own `propose*` / add / remove / role-change
     * operations, so the group's membership is frozen until the verdict lands.
     * Incoming messages are unaffected.
     *
     * Every eager path leaves this `false` — [`Self::add_member`],
     * [`Self::remove_member`], [`Self::set_group_roles`] and
     * [`Self::commit_pending_proposals`] all merge before they return — which
     * is why a rollback here can never reach a path that never had one.
     * [`Self::approve_external_join`] left that list with ADR-0045 S7: it
     * merges nothing, because it commits nothing.
     * @returns {boolean}
     */
    hasStagedSweep() {
        const ret = wasm.group_hasStagedSweep(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Whether this device is still a member of the group.
     *
     * `false` means a Commit removing this device's own leaf has been applied.
     * The state is **terminal** — RFC 9420 offers no way back, and it survives
     * a snapshot / restore, so this answers the same at launch as it did in
     * the poll batch that carried the removal. Rejoining is a new `Group`.
     *
     * Nothing in the receive path signals the transition:
     * `processIncomingVerifyingRoles` returns the same content-less `Commit`
     * for a removing commit as for any other. **This is the only signal.**
     *
     * Once it is `false`, reads still answer (`members`, `epoch`,
     * `memberCount`, `ownLeafIndex`, `groupId`, `groupRoles`) but are frozen
     * at the removing epoch and will never move again, while
     * `createApplicationMessage`, `exportSecret` and every further decrypt
     * throw — including for rows still sitting unfetched on the DS, since the
     * refusal is not epoch-scoped.
     *
     * The UI gates on it: withdraw the composer and the membership
     * affordances, stop polling and stop emitting beacons, and keep rendering
     * the history, which is still readable. Prefer this over inferring
     * membership from `members()` — the inference rests on a leaf signature
     * key being the account key, which ADR-0022's per-device-leaf follow-up
     * would break silently.
     * @returns {boolean}
     */
    get isActive() {
        const ret = wasm.group_isActive(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Join an existing group from a wire-format Welcome message.
     * @param {Uint8Array} welcome
     * @param {Identity} identity
     * @returns {Group}
     */
    static joinFromWelcome(welcome, identity) {
        const ptr0 = passArray8ToWasm0(welcome, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        _assertClass(identity, Identity);
        const ret = wasm.group_joinFromWelcome(ptr0, len0, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Group.__wrap(ret[0]);
    }
    /**
     * Load a previously persisted MLS group from `identity`'s
     * provider storage (Phase 1.16e6b). Pair with
     * [`Identity::restoreStorage`](Identity::restore_storage)
     * — call `restoreStorage` first to seed the provider, then
     * `Group.load` for each known group_id.
     *
     * Returns `undefined` (`Option<Group> -> None`) when
     * the storage doesn't carry a row for `group_id` —
     * distinct from a thrown error so the JS side can
     * distinguish "no group with that id" from "corrupt
     * storage".
     * @param {Identity} identity
     * @param {Uint8Array} group_id
     * @returns {Group | undefined}
     */
    static load(identity, group_id) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_load(identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : Group.__wrap(ret[0]);
    }
    /**
     * Number of members at the current epoch.
     * @returns {number}
     */
    get memberCount() {
        const ret = wasm.group_memberCount(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * Snapshot of every member's `(leafIndex, signaturePublicKey)`
     * at the current epoch (Phase 1.18h).
     *
     * JS-side shape: returns a flat array of [`GroupMember`]
     * instances. wasm-bindgen doesn't let us return `Vec<JsValue>`
     * of a custom struct directly through a getter, so this is
     * a regular method (`.members()` not `.members`) returning
     * `Vec<JsValue>` after a per-element `JsValue::from`.
     *
     * Ordering matches `lakko_mls::Group::members` — left-to-right
     * over the leaf tree, stable inside a single epoch.
     * @returns {any[]}
     */
    members() {
        const ret = wasm.group_members(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * **Won.** Merge the sweep staged by [`Self::stage_pending_proposals`],
     * advancing this device to the new epoch — the same one the peers who
     * applied the published commit reached (ADR-0045).
     *
     * Call this only after the journal has confirmed this device's commit was
     * ordered first. Merging on any weaker signal is the eager shape again,
     * with all of its consequences.
     *
     * Returns [`StagedSweepOutcome::Merged`], or
     * [`StagedSweepOutcome::NothingStaged`] when there was nothing staged —
     * not a failure, and what makes a re-run after the merge a no-op.
     * @param {Identity} identity
     * @returns {StagedSweepOutcome}
     */
    mergeStagedSweep(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.group_mergeStagedSweep(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0];
    }
    /**
     * Create a fresh group with `identity` as the founding member and the
     * conversation `kind` written into its GroupContext for life (ADR-0062).
     * Mirror of UniFFI's `Group::create`.
     * @param {Identity} identity
     * @param {GroupKind} kind
     */
    constructor(identity, kind) {
        _assertClass(identity, Identity);
        const ret = wasm.group_new(identity.__wbg_ptr, kind);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        GroupFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * This device's ADR-0045 committer rank: `0` for the owner, `k + 1` for
     * the `k`-th admin in the role table's canonical order, and for any other
     * member (ADR-0050 section 2.5a) its account rank `n + 1 + i` **only while
     * its pending store holds a device admission or its own account's
     * self-service proposal** it could sweep — otherwise **`undefined`**.
     * `undefined` too for a device that holds no leaf in the roster, and for a
     * group with no role table. Every device of a ranked account holds the
     * account's rank (ADR-0050 section 2.5a item 6); `actingCommitterVerdict`
     * breaks the tie among the account's devices by signature key, among
     * those whose beacon is fresh.
     *
     * `undefined` and `0` are **not** the same thing and must never be
     * conflated: `0` is the owner, who acts as committer immediately, while
     * `undefined` means this device is not in the committer set at all and
     * must never sweep. Because `0` is falsy in JavaScript, `if (rank)` is
     * **wrong** in both directions — test `rank !== undefined` explicitly, and
     * never `??`-default the value.
     * @param {Identity} identity
     * @returns {number | undefined}
     */
    ownCommitterRank(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.group_ownCommitterRank(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === Number.MAX_SAFE_INTEGER ? undefined : ret[0];
    }
    /**
     * Local leaf index of the calling identity.
     * @returns {number}
     */
    get ownLeafIndex() {
        const ret = wasm.group_ownLeafIndex(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * This member's ADR-0022 role, or `undefined` for an open / legacy group
     * (no role table). The web gates the add / remove / promote UI on this.
     * @param {Identity} identity
     * @returns {Role | undefined}
     */
    ownRole(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.group_ownRole(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 3 ? undefined : ret[0];
    }
    /**
     * The account key behind a pending external-join request (32 bytes),
     * resolved from the joiner's verified ADR-0050 device binding, or
     * `undefined`. Use it to name a request from a contact; approving still
     * goes by the leaf key `pendingExternalJoins` returns.
     * @param {Uint8Array} joiner_signature_key
     * @returns {Uint8Array | undefined}
     */
    pendingExternalJoinAccountKey(joiner_signature_key) {
        const ptr0 = passArray8ToWasm0(joiner_signature_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_pendingExternalJoinAccountKey(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v2;
        if (ret[0] !== 0) {
            v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v2;
    }
    /**
     * Signature keys of the external-join proposals awaiting a decision
     * (ADR-0027), flat-concatenated — each is a fixed 32-byte Ed25519 key, so
     * the caller slices into 32-byte chunks (the role-table FFI convention).
     * Each chunk is a handle for `approveExternalJoin` / `rejectExternalJoin`.
     * @returns {Uint8Array}
     */
    pendingExternalJoins() {
        const ret = wasm.group_pendingExternalJoins(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Every ADR-0045 proposal currently in this device's pending set, in the
     * order OpenMLS holds them, as a flat array of [`ProposalRecord`]
     * instances (same convention as [`Self::members`] — wasm-bindgen cannot
     * return a `Vec` of a custom struct through a getter).
     *
     * This is the committer's "N changes waiting to apply" surface and the
     * proposer's "did my request land" surface. Every entry reports
     * `stored === true` by construction — the receipt gate admits nothing
     * else, and OpenMLS empties the whole store on every merged commit, so the
     * set is exactly what a sweep would consider.
     * @returns {any[]}
     */
    pendingProposals() {
        const ret = wasm.group_pendingProposals(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Process an incoming MLS message.
     * @param {Identity} identity
     * @param {Uint8Array} bytes
     * @returns {ProcessedMessage}
     */
    processIncoming(identity, bytes) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_processIncoming(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ProcessedMessage.__wrap(ret[0]);
    }
    /**
     * Apply an incoming commit ENFORCING the ADR-0022 role policy — a commit
     * whose committer's role does not authorise its effect is REFUSED (not
     * merged), fail-closed. A group with no role table behaves like
     * [`Self::process_incoming`]. Use on the chat-message receive loop only;
     * do NOT use for call-group receives (ADR-0022 call-path caveat).
     * @param {Identity} identity
     * @param {Uint8Array} bytes
     * @returns {ProcessedMessage}
     */
    processIncomingVerifyingRoles(identity, bytes) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_processIncomingVerifyingRoles(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ProcessedMessage.__wrap(ret[0]);
    }
    /**
     * Process one row read from this journal's **knock** queue (ADR-0053).
     * Anything that is not a stranger's external-join proposal for this group
     * is refused BEFORE it is processed - a knock row is an unauthenticated
     * write, and applying a commit or message someone posted there instead of
     * the journal would fork the admins who read knocks from everyone else.
     * A row that passes is processed exactly like `processIncomingVerifyingRoles`.
     * @param {Identity} identity
     * @param {Uint8Array} bytes
     * @returns {ProcessedMessage}
     */
    processKnock(identity, bytes) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_processKnock(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ProcessedMessage.__wrap(ret[0]);
    }
    /**
     * **Propose** adding a member from their published KeyPackage — the
     * ADR-0045 proposal-path twin of [`Self::add_member`].
     *
     * Returns the wire-encoded Proposal to publish to the group journal. The
     * epoch does **not** advance and there is no Welcome yet: the Welcome is
     * produced by whichever device sweeps this into a commit
     * ([`Self::commit_pending_proposals`]). Because a proposal is not
     * epoch-advancing, two members proposing concurrently compose instead of
     * forking the loser off the group.
     *
     * Role-gated locally to owner/admin, and re-gated by every peer's receipt
     * gate — a refused proposal is simply never stored.
     * @param {Identity} identity
     * @param {Uint8Array} key_package
     * @returns {Uint8Array}
     */
    proposeAddMember(identity, key_package) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_proposeAddMember(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * **Propose** rewriting the ADR-0022 role table — the ADR-0045
     * proposal-path twin of [`Self::set_group_roles`]. Owner-only.
     *
     * `owner` is 32 bytes; `admins_flat` is the concatenation of 32-byte admin
     * keys. Returns the wire-encoded Proposal to publish; the epoch does not
     * advance.
     *
     * **One role change at a time.** This THROWS while another role change —
     * including an ownership transfer — is already pending, because RFC 9420
     * admits at most one `GroupContextExtensions` proposal per commit, so a
     * second could not be swept. A pending add or remove does **not** block
     * it; only another role change does. To pre-empt the throw rather than
     * surface it, call `pendingProposals()` and look for a `RoleChange` — that
     * is the same question the core asks.
     * @param {Identity} identity
     * @param {Uint8Array} owner
     * @param {Uint8Array} admins_flat
     * @returns {Uint8Array}
     */
    proposeGroupRoles(identity, owner, admins_flat) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(owner, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(admins_flat, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.group_proposeGroupRoles(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v3;
    }
    /**
     * **Propose** handing the group's owner slot to `new_owner` (ADR-0045) —
     * the proposal-path twin of [`Self::transfer_ownership`], with identical
     * authorisation and identical refusals.
     *
     * The proposal is judged, by this device and by every peer, against the
     * **pre-merge** table in which this device is still the owner, so it needs
     * no new rule: it is a role change, which is owner-only. Any owner-or-admin
     * may sweep it, including the incoming owner. Returns the wire-encoded
     * Proposal to publish; the epoch does **not** advance.
     * @param {Identity} identity
     * @param {Uint8Array} new_owner
     * @param {OutgoingOwner} outgoing
     * @returns {Uint8Array}
     */
    proposeOwnershipTransfer(identity, new_owner, outgoing) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(new_owner, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_proposeOwnershipTransfer(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, outgoing);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * **Propose** removing the member at `leaf_index` — the ADR-0045
     * proposal-path twin of [`Self::remove_member`].
     *
     * Naming **this device's own** leaf is how a member leaves and needs no
     * role; any other leaf needs owner or admin, and the owner's leaf is
     * refused for everyone. Returns the wire-encoded Proposal to publish; the
     * epoch does not advance.
     * @param {Identity} identity
     * @param {number} leaf_index
     * @returns {Uint8Array}
     */
    proposeRemoveMember(identity, leaf_index) {
        _assertClass(identity, Identity);
        const ret = wasm.group_proposeRemoveMember(this.__wbg_ptr, identity.__wbg_ptr, leaf_index);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Reject a retained external-join proposal — discards it locally without
     * adding the joiner or advancing the epoch (ADR-0027).
     * `identity` is what the durable copy of the pending set is written
     * through (the request set rides the MLS storage snapshot, so a decision
     * survives a restart).
     * @param {Identity} identity
     * @param {Uint8Array} proposal_ref
     */
    rejectExternalJoin(identity, proposal_ref) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(proposal_ref, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_rejectExternalJoin(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Remove a member by their leaf index. Returns the wire-encoded
     * commit to broadcast.
     * @param {Identity} identity
     * @param {number} leaf_index
     * @returns {Uint8Array}
     */
    removeMember(identity, leaf_index) {
        _assertClass(identity, Identity);
        const ret = wasm.group_removeMember(this.__wbg_ptr, identity.__wbg_ptr, leaf_index);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * ADR-0087 section 5 — retain a request to join carried as a bare
     * KeyPackage (opened from a knock row with `openKnockRow`); approve or
     * reject it like an external-join proposal. Never advances the epoch.
     * @param {Identity} identity
     * @param {Uint8Array} key_package
     * @returns {JoinRequest}
     */
    retainJoinRequest(identity, key_package) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_retainJoinRequest(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return JoinRequest.__wrap(ret[0]);
    }
    /**
     * The role table's admin signature keys, concatenated (each 32 bytes);
     * empty for an open group or one with no admins. The web slices this into
     * 32-byte chunks.
     * @returns {Uint8Array}
     */
    roleTableAdmins() {
        const ret = wasm.group_roleTableAdmins(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The role table's owner signature key (32 bytes), or `undefined` for an
     * open / legacy group. A non-null return signals the group is role-enabled.
     * @returns {Uint8Array | undefined}
     */
    roleTableOwner() {
        const ret = wasm.group_roleTableOwner(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Rewrite the ADR-0022 role table (owner only — enforced on receive).
     * `owner` is 32 bytes; `admins_flat` is the concatenation of 32-byte admin
     * keys. Returns the `GroupContextExtensions` commit wire bytes to broadcast.
     * @param {Identity} identity
     * @param {Uint8Array} owner
     * @param {Uint8Array} admins_flat
     * @returns {Uint8Array}
     */
    setGroupRoles(identity, owner, admins_flat) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(owner, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(admins_flat, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.group_setGroupRoles(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v3;
    }
    /**
     * ADR-0081 staged twin of [`Self::add_member`]: the same commit and
     * Welcome, but the group stays at epoch N holding the commit as pending,
     * exactly as [`Self::stage_pending_proposals`] leaves a sweep. Record it
     * durably, send it with `dsStagedAccessSigner`, then `mergeStagedSweep`
     * on the Delivery Service's acceptance or `abandonStagedSweep` on its
     * `409`. While it is staged every membership operation throws the
     * `sweepAlreadyStaged` refusal. The staged commit is persisted with the
     * group, so it survives `snapshotStorage` and a reload.
     *
     * No client calls the staged twins today: the ADR-0081 amendment of
     * 2026-10-19 deleted the direct-commit paths for ordinary groups.
     * @param {Identity} identity
     * @param {Uint8Array} key_package
     * @returns {AddMember}
     */
    stageAddMember(identity, key_package) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_stageAddMember(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AddMember.__wrap(ret[0]);
    }
    /**
     * The several-leaf form of [`Self::stage_add_member`] (one commit, one
     * Welcome; see [`Self::add_members`]). `keyPackages` is a `Uint8Array[]`.
     * @param {Identity} identity
     * @param {Uint8Array[]} key_packages
     * @returns {AddMember}
     */
    stageAddMembers(identity, key_packages) {
        _assertClass(identity, Identity);
        const ptr0 = passArrayJsValueToWasm0(key_packages, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_stageAddMembers(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AddMember.__wrap(ret[0]);
    }
    /**
     * Sweep the authorised pending proposals into **one** Commit and **stop
     * before merging it** — the ADR-0045 rollback point (G-38 residual 1).
     *
     * This is the committer entry point for any device that can lose a race,
     * which in practice is every device: ADR-0045's designated committer makes
     * two simultaneous sweeps rare, not impossible, because no liveness signal
     * can distinguish a dead committer from a slow one. The DS journal already
     * arbitrates — every member observes its order identically — so the
     * collision needs no negotiation. What it needs is for the loser to be
     * able to *survive losing*, which the eager shape denies it.
     *
     * **The sequence, in order. Every step is required.**
     *
     * 1. `stagePendingProposals` — the group stays at epoch N holding an
     *    un-merged commit.
     * 2. Durably record `staged.commit` (and `staged.welcome`) — ADR-0040's
     *    outbox — then publish them.
     * 3. Read the journal order back: did this commit land first?
     * 4. **Won** → `mergeStagedSweep`. **Lost** → `abandonStagedSweep`, then
     *    apply the winner's commit normally.
     *
     * The two mechanisms answer different questions and neither subsumes the
     * other: the outbox answers *did my commit reach the wire*, this pair
     * answers *did it win the wire order*. The first must be `yes` before the
     * second is meaningful. A commit that **won** must be merged and never
     * rolled back — rolling one back after peers applied it manufactures the
     * very fork this closes.
     *
     * **Abandoning costs nothing.** OpenMLS empties the proposal store on
     * *merge*, and an abandoned sweep was never merged, so the proposals it
     * selected are still pending for the winner's next sweep.
     *
     * Returns `undefined` when nothing authorised is pending — the same
     * deliberate non-empty-commit rule as [`Self::commit_pending_proposals`],
     * and in that case nothing is staged and no verdict is owed.
     *
     * Throws a [`SWEEP_ALREADY_STAGED_CODE`]-coded `JsError` when a sweep is
     * already awaiting its verdict — handle it by reaching that verdict, never
     * by retrying the stage. Every other failure (no role table, this device
     * not owner or admin, an OpenMLS-side error) throws an uncoded `JsError`.
     * @param {Identity} identity
     * @returns {StagedSweep | undefined}
     */
    stagePendingProposals(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.group_stagePendingProposals(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : StagedSweep.__wrap(ret[0]);
    }
    /**
     * [`Self::remove_member`], staged (ADR-0081; see
     * [`Self::stage_add_member`]). Returns the commit to send.
     * @param {Identity} identity
     * @param {number} leaf_index
     * @returns {Uint8Array}
     */
    stageRemoveMember(identity, leaf_index) {
        _assertClass(identity, Identity);
        const ret = wasm.group_stageRemoveMember(this.__wbg_ptr, identity.__wbg_ptr, leaf_index);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * [`Self::set_group_roles`], staged (ADR-0081; see
     * [`Self::stage_add_member`]). Returns the commit to send.
     * @param {Identity} identity
     * @param {Uint8Array} owner
     * @param {Uint8Array} admins_flat
     * @returns {Uint8Array}
     */
    stageSetGroupRoles(identity, owner, admins_flat) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(owner, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(admins_flat, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.group_stageSetGroupRoles(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v3;
    }
    /**
     * Hand the group's owner slot to `new_owner`, as a **direct commit**
     * (ADR-0022's deferred ownership-transfer follow-up, built in ADR-0045).
     *
     * `new_owner` is the member's 32-byte Ed25519 signature public key — the
     * same stable handle `Group.members()` reports and the role table keys on,
     * never a leaf index. `outgoing` decides what becomes of **this** device's
     * own role and is **required**: omitting it throws rather than defaulting
     * (see [`OutgoingOwner`]).
     *
     * **Owner-only and irreversible.** Only the owner may transfer, because a
     * role change is owner-only and a transfer is a role change; anything
     * weaker would be a coup, since an admin able to transfer could name
     * itself. Once the commit lands, only the new owner can rewrite the table.
     * This does **not** recover a *lost* owner either — minting the commit
     * needs the owner's key.
     *
     * Transfer is what makes leaving possible: an owner may not leave while
     * owner, but the former owner is an ordinary member and may. The two are
     * necessarily separate commits — both proposals are judged against the
     * pre-merge table in which the transferor is still owner, so a fused
     * transfer-and-leave has its `Leave` half refused.
     *
     * Transfer is deliberately **not** a new commit type: it is the ordinary
     * owner-only role-table rewrite with the owner slot moved, so it rides the
     * audited [`Self::set_group_roles`] path and is judged by the existing
     * peer gate. Returns the wire-encoded commit to broadcast; the local epoch
     * has already advanced, so record it durably before publishing
     * (ADR-0040). In a group that has moved to the ADR-0045 proposal path,
     * prefer [`Self::propose_ownership_transfer`].
     *
     * Throws when `new_owner` is not 32 bytes, when the group carries no role
     * table, when this device is not the owner, when `new_owner` is already
     * the owner, or when `new_owner` is **not a member of this group** — the
     * last refused because an owner outside the group can never be replaced (a
     * role change is owner-only) and never removed (the owner's leaf is
     * unremovable), leaving the group permanently un-administrable. Peers
     * refuse such a commit independently (C-63), so this local refusal is the
     * clear error, not the enforcement.
     * @param {Identity} identity
     * @param {Uint8Array} new_owner
     * @param {OutgoingOwner} outgoing
     * @returns {Uint8Array}
     */
    transferOwnership(identity, new_owner, outgoing) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(new_owner, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.group_transferOwnership(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0, outgoing);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
}
if (Symbol.dispose) Group.prototype[Symbol.dispose] = Group.prototype.free;

/**
 * A group's closed notice (ADR-0073 phase 2, `docs/ux/identity-recovery.md`
 * §16.5). Mirror of [`lakko_contacts::group_closed::GroupClosed`].
 *
 * **A notice received from someone else is that person's assertion about
 * the whole group, and the sender's authority is the RECEIVER's check**:
 * resolve the MLS sender leaf to the account key at receipt (C-68), read the
 * live role table, accept only from the owner or an admin (a plain member's
 * notice is dropped), then keep it only when `groupClosedIsNewer` than the
 * stored stamp **for that group**, the whole read-decide-write under the
 * per-group lock. Persist the stamp and re-emit it VERBATIM on a growth
 * re-send. The successor id is the admin's claim: offer `Open the new
 * group` only once this device has joined it through the ordinary
 * invitation path.
 */
export class GroupClosed {
    static __wrap(ptr) {
        const obj = Object.create(GroupClosed.prototype);
        obj.__wbg_ptr = ptr;
        GroupClosedFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupClosedFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_groupclosed_free(ptr, 0);
    }
    /**
     * Closer-clock UNIX milliseconds, as a `bigint`.
     * @returns {bigint}
     */
    get closedAtMs() {
        const ret = wasm.groupclosed_closedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The recreated group's 32-byte id, or `undefined` when the group was
     * closed without a successor.
     * @returns {Uint8Array | undefined}
     */
    get successorGroupId() {
        const ret = wasm.groupclosed_successorGroupId(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
}
if (Symbol.dispose) GroupClosed.prototype[Symbol.dispose] = GroupClosed.prototype.free;

/**
 * What the viewer may do in a group. Every field is a decision the client
 * already made from the live role table; none is re-derived here.
 */
export class GroupInfoCapabilities {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupInfoCapabilitiesFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_groupinfocapabilities_free(ptr, 0);
    }
    /**
     * Whether this device can actually leave.
     * @returns {boolean}
     */
    get canLeave() {
        const ret = wasm.groupinfocapabilities_canLeave(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Owner or admin, and still a member.
     * @returns {boolean}
     */
    get canManageMembers() {
        const ret = wasm.groupinfocapabilities_canManageMembers(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * May mint an invite link.
     * @returns {boolean}
     */
    get canMintInvite() {
        const ret = wasm.groupinfocapabilities_canMintInvite(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * May change the shared name.
     * @returns {boolean}
     */
    get canRename() {
        const ret = wasm.groupinfocapabilities_canRename(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The owner.
     * @returns {boolean}
     */
    get isOwner() {
        const ret = wasm.groupinfocapabilities_isOwner(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * This device's membership is over: read-only.
     * @returns {boolean}
     */
    get membershipEnded() {
        const ret = wasm.groupinfocapabilities_membershipEnded(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * In order: owner-or-admin and still a member (ADR-0022), the same for
     * renaming (ADR-0051) and for minting an invite (ADR-0028), the owner
     * (ADR-0045), whether this device can actually leave (ADR-0045's member
     * exit is a `Leave` proposal, so a group with no role table has no
     * proposal path and nothing for the control to do), and whether this
     * device's membership is over.
     * @param {boolean} can_manage_members
     * @param {boolean} can_rename
     * @param {boolean} can_mint_invite
     * @param {boolean} is_owner
     * @param {boolean} can_leave
     * @param {boolean} membership_ended
     */
    constructor(can_manage_members, can_rename, can_mint_invite, is_owner, can_leave, membership_ended) {
        const ret = wasm.groupinfocapabilities_new(can_manage_members, can_rename, can_mint_invite, is_owner, can_leave, membership_ended);
        this.__wbg_ptr = ret;
        GroupInfoCapabilitiesFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) GroupInfoCapabilities.prototype[Symbol.dispose] = GroupInfoCapabilities.prototype.free;

/**
 * Decoded, signature-verified group join-ticket (ADR-0028) — the wasm face
 * of [`lakko_contacts::GroupInviteCapability`].
 */
export class GroupInviteCapability {
    static __wrap(ptr) {
        const obj = Object.create(GroupInviteCapability.prototype);
        obj.__wbg_ptr = ptr;
        GroupInviteCapabilityFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupInviteCapabilityFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_groupinvitecapability_free(ptr, 0);
    }
    /**
     * Decode PLAINTEXT capability wire bytes and verify the Ed25519 signature
     * (the join attribution carries plaintext; a link never does since the
     * 2026-09-28 security pass — use `openLink`). Does **not** check expiry —
     * call `isValidNow`.
     * @param {Uint8Array} wire
     * @returns {GroupInviteCapability}
     */
    static decodeAndVerify(wire) {
        const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.groupinvitecapability_decodeAndVerify(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return GroupInviteCapability.__wrap(ret[0]);
    }
    /**
     * UNIX milliseconds when the link stops being valid. `0` = never expires.
     * @returns {bigint}
     */
    get expiryMs() {
        const ret = wasm.groupinvitecapability_expiryMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The target group's opaque MLS GroupId bytes — pass to
     * `Identity.createExternalJoinProposal`.
     * @returns {Uint8Array}
     */
    get groupId() {
        const ret = wasm.groupinvitecapability_groupId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Whether the link carries a sealed preview at all.
     * @returns {boolean}
     */
    get hasPreview() {
        const ret = wasm.groupinvitecapability_hasPreview(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `true` iff the link has not expired at `nowMs`.
     * @param {bigint} now_ms
     * @returns {boolean}
     */
    isValidNow(now_ms) {
        const ret = wasm.groupinvitecapability_isValidNow(this.__wbg_ptr, now_ms);
        return ret !== 0;
    }
    /**
     * Signal-style anti-phishing fingerprint of the issuer's key.
     * @returns {string}
     */
    issuerFingerprint() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.groupinvitecapability_issuerFingerprint(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The issuer's Ed25519 verifying key (32 bytes).
     * @returns {Uint8Array}
     */
    get issuerSignaturePubkey() {
        const ret = wasm.groupinvitecapability_issuerSignaturePubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 32-byte knock public key the joiner seals its request to
     * (ADR-0087 section 5); `undefined` on a channel subscriber invitation.
     * @returns {Uint8Array | undefined}
     */
    get knockPublicKey() {
        const ret = wasm.groupinvitecapability_knockPublicKey(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The knock-key version; `undefined` on a subscriber invitation.
     * @returns {number | undefined}
     */
    get knockVersion() {
        const ret = wasm.groupinvitecapability_knockVersion(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret;
    }
    /**
     * Joiner-side: open a `/g/` link's sealed blob with the 12-byte secret
     * from the URL fragment and verify the capability inside; the same
     * secret then opens the preview (`openPreview`). A `/g/` link without its
     * fragment has nothing to open this with. Does NOT check expiry.
     *
     * # Errors
     *
     * Throws an `Error` with `inviteLinkError: "damaged"` on any failure.
     * @param {Uint8Array} sealed
     * @param {Uint8Array} link_secret
     * @returns {GroupInviteCapability}
     */
    static openLink(sealed, link_secret) {
        const ptr0 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(link_secret, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.groupinvitecapability_openLink(ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return GroupInviteCapability.__wrap(ret[0]);
    }
    /**
     * Open the sealed preview with the link secret from the URL fragment
     * (the 12 bytes the 16-character fragment decodes to). `undefined` when
     * the link carries no preview. A wrong secret, a tampered preview, or a
     * capability other than the one the secret was minted with (the derived
     * key binds the group id and issuer) throws; the caller shows the page
     * without a preview, never a guess.
     *
     * # Errors
     *
     * Throws when `linkKey` is not 12 bytes (the pre-amendment 32-byte key
     * included) or the preview does not open.
     * @param {Uint8Array} link_key
     * @returns {GroupInvitePreview | undefined}
     */
    openPreview(link_key) {
        const ptr0 = passArray8ToWasm0(link_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.groupinvitecapability_openPreview(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : GroupInvitePreview.__wrap(ret[0]);
    }
    /**
     * The plaintext capability wire — what `computeJoinerInviteTag` and
     * `composeGroupJoinAttribution` take.
     * @returns {Uint8Array}
     */
    get wire() {
        const ret = wasm.groupinvitecapability_wire(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) GroupInviteCapability.prototype[Symbol.dispose] = GroupInviteCapability.prototype.free;

/**
 * Composed + verified group join-ticket capability (ADR-0028). Mirror of
 * [`lakko_contacts::GroupInviteCapability`].
 * The preview a `/g/` link shows before the person asks to join (ADR-0028
 * v2): the ISSUER's claim at mint time, sealed under a key derived from the
 * link secret that rides in the URL fragment.
 */
export class GroupInvitePreview {
    static __wrap(ptr) {
        const obj = Object.create(GroupInvitePreview.prototype);
        obj.__wbg_ptr = ptr;
        GroupInvitePreviewFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupInvitePreviewFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_groupinvitepreview_free(ptr, 0);
    }
    /**
     * ADR-0087 section 18.4: the photo when the link was made, or
     * `undefined`. Its keys are readable by whoever holds the link.
     * @returns {ProfileAvatar | undefined}
     */
    get avatar() {
        const ret = wasm.groupinvitepreview_avatar(this.__wbg_ptr);
        return ret === 0 ? undefined : ProfileAvatar.__wrap(ret);
    }
    /**
     * ADR-0078 section 6: the 16-byte channel id of a co-publisher link
     * (then `groupName` is the channel's name), or `undefined` for a group
     * link.
     * @returns {Uint8Array | undefined}
     */
    get channelId() {
        const ret = wasm.groupinvitepreview_channelId(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The group's name, or `undefined`.
     * @returns {string | undefined}
     */
    get groupName() {
        const ret = wasm.groupinvitepreview_groupName(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The inviter's display name, or `undefined`.
     * @returns {string | undefined}
     */
    get inviterName() {
        const ret = wasm.groupinvitepreview_inviterName(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * How many people were in the group when the link was minted.
     * @returns {number}
     */
    get memberCount() {
        const ret = wasm.groupinvitepreview_memberCount(this.__wbg_ptr);
        return ret;
    }
    /**
     * ADR-0087 sections 7 and 14: set on a channel subscriber invitation.
     * @returns {SubscriberInvite | undefined}
     */
    get subscriber() {
        const ret = wasm.groupinvitepreview_subscriber(this.__wbg_ptr);
        return ret === 0 ? undefined : SubscriberInvite.__wrap(ret);
    }
}
if (Symbol.dispose) GroupInvitePreview.prototype[Symbol.dispose] = GroupInvitePreview.prototype.free;

/**
 * Decoded join-attribution (ADR-0028 OQ2) — which member's `/g/` link a join
 * request arrived on. Produced by `GroupJoinAttribution.parse`; the embedded
 * capability is re-verified (Ed25519) on decode.
 */
export class GroupJoinAttribution {
    static __wrap(ptr) {
        const obj = Object.create(GroupJoinAttribution.prototype);
        obj.__wbg_ptr = ptr;
        GroupJoinAttributionFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupJoinAttributionFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_groupjoinattribution_free(ptr, 0);
    }
    /**
     * UNIX milliseconds when the link stops being valid. `0` = never expires.
     * @returns {bigint}
     */
    get expiryMs() {
        const ret = wasm.groupjoinattribution_expiryMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The target group's opaque MLS GroupId bytes — the admin checks this
     * equals its own group before trusting the attribution.
     * @returns {Uint8Array}
     */
    get groupId() {
        const ret = wasm.groupjoinattribution_groupId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Signal-style anti-phishing fingerprint of the issuer's key.
     * @returns {string}
     */
    get issuerFingerprint() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.groupjoinattribution_issuerFingerprint(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The issuer's Ed25519 verifying key (32 bytes) — checked against the
     * roster (OQ2).
     * @returns {Uint8Array}
     */
    get issuerSignaturePubkey() {
        const ret = wasm.groupjoinattribution_issuerSignaturePubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The joiner's MLS leaf signature key — the tag matching the pending
     * external-join request.
     * @returns {Uint8Array}
     */
    get joinerSignatureKey() {
        const ret = wasm.groupjoinattribution_joinerSignatureKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Admin-side: decode + verify an attribution envelope from the group
     * journal. A malformed / forged envelope (or a capability whose signature
     * fails) is rejected.
     * @param {Uint8Array} bytes
     * @returns {GroupJoinAttribution}
     */
    static parse(bytes) {
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.groupjoinattribution_parse(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return GroupJoinAttribution.__wrap(ret[0]);
    }
    /**
     * The joiner's `reply` reach grant (ADR-0060 §6), inside the signed
     * part. SECRET and readable by every member: the acting committer that
     * sweeps the joiner's approved `Add` delivers the Welcome to the
     * joiner's mailbox with `DsProofSigner.forReach(replyGrant.seed)`.
     * @returns {ReachGrant}
     */
    get replyGrant() {
        const ret = wasm.groupjoinattribution_replyGrant(this.__wbg_ptr);
        return ReachGrant.__wrap(ret);
    }
}
if (Symbol.dispose) GroupJoinAttribution.prototype[Symbol.dispose] = GroupJoinAttribution.prototype.free;

/**
 * What kind of conversation a [`Group`] is (ADR-0062), fixed at creation
 * and the same on every member's device. Mirror of UniFFI's `GroupKind`.
 * The numeric values are the `0xFF03` wire bytes, so a caller that logs one
 * sees the same number the GroupContext carries.
 *
 * Named `GroupKind`, not `ConversationKind`: that name belongs to the
 * `lakko-ui-rules` list classification, a different thing.
 * @enum {1 | 2}
 */
export const GroupKind = Object.freeze({
    /**
     * Two people, always the same two; each person's devices may come and go.
     */
    OneToOne: 1, "1": "OneToOne",
    /**
     * Any number of people, under the role table and proposal rules.
     */
    Group: 2, "2": "Group",
});

/**
 * One member's identity snapshot at the current epoch. Mirror
 * of [`lakko_mls::MemberRecord`].
 *
 * The JS layer reads `leafIndex` to feed into
 * `Group.removeMember(...)` for kick UX and
 * `signaturePublicKey` to look the member up in the contacts
 * cache / render a fingerprint. `leafIndex` is stable inside a
 * single epoch but can renumber across an add/remove cycle —
 * callers MUST NOT cache it past a `processIncoming` commit.
 */
export class GroupMember {
    static __wrap(ptr) {
        const obj = Object.create(GroupMember.prototype);
        obj.__wbg_ptr = ptr;
        GroupMemberFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupMemberFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_groupmember_free(ptr, 0);
    }
    /**
     * The **account** key the leaf belongs to (ADR-0050) — what the UI
     * matches against the contacts list to render a name, so one person with
     * three devices is one person. Empty when the leaf's credential did not
     * resolve (never admitted by a Lakko gate).
     * @returns {Uint8Array}
     */
    get accountKey() {
        const ret = wasm.groupmember_accountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * OpenMLS leaf position at the current epoch.
     * @returns {number}
     */
    get leafIndex() {
        const ret = wasm.groupmember_leafIndex(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * 32-byte Ed25519 verifying key of the member's **leaf** (its device).
     * @returns {Uint8Array}
     */
    get signaturePublicKey() {
        const ret = wasm.groupmember_signaturePublicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) GroupMember.prototype[Symbol.dispose] = GroupMember.prototype.free;

/**
 * A group's shared name. Mirror of
 * [`lakko_contacts::group_name::GroupName`].
 *
 * **A name received from someone else is that person's assertion about the
 * whole group, and the sender's authority is the RECEIVER's check** (ADR-0051
 * §2): resolve the MLS sender leaf to the account key at receipt (C-68), read
 * the live role table, accept only from the owner or an admin (or from any
 * member when the group carries no role table), then keep it only when
 * `groupNameIsNewer` than the stored stamp for that group. Persist the stamp
 * and re-emit it VERBATIM on a growth re-send (the ADR-0044 lesson).
 */
export class GroupName {
    static __wrap(ptr) {
        const obj = Object.create(GroupName.prototype);
        obj.__wbg_ptr = ptr;
        GroupNameFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupNameFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_groupname_free(ptr, 0);
    }
    /**
     * The name; **empty means "clear the name"** (fall back to the
     * member-derived title).
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.groupname_name(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Setter-clock UNIX milliseconds, as a `bigint`.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.groupname_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) GroupName.prototype[Symbol.dispose] = GroupName.prototype.free;

/**
 * A group's photo as an owner or admin set it (ADR-0087 section 18.1).
 */
export class GroupPhoto {
    static __wrap(ptr) {
        const obj = Object.create(GroupPhoto.prototype);
        obj.__wbg_ptr = ptr;
        GroupPhotoFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupPhotoFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_groupphoto_free(ptr, 0);
    }
    /**
     * The photo, or `undefined` for none. Its keys are SECRET.
     * @returns {ProfileAvatar | undefined}
     */
    get avatar() {
        const ret = wasm.groupphoto_avatar(this.__wbg_ptr);
        return ret === 0 ? undefined : ProfileAvatar.__wrap(ret);
    }
    /**
     * A photo (`avatar` undefined = remove it) stamped `updatedAtMs`.
     * @param {ProfileAvatar | null | undefined} avatar
     * @param {bigint} updated_at_ms
     */
    constructor(avatar, updated_at_ms) {
        let ptr0 = 0;
        if (!isLikeNone(avatar)) {
            _assertClass(avatar, ProfileAvatar);
            ptr0 = avatar.__destroy_into_raw();
        }
        const ret = wasm.groupphoto_new(ptr0, updated_at_ms);
        this.__wbg_ptr = ret;
        GroupPhotoFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Setter-clock UNIX milliseconds.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.groupphoto_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) GroupPhoto.prototype[Symbol.dispose] = GroupPhoto.prototype.free;

/**
 * A group's allowed reactions as an owner or admin set them.
 */
export class GroupReactions {
    static __wrap(ptr) {
        const obj = Object.create(GroupReactions.prototype);
        obj.__wbg_ptr = ptr;
        GroupReactionsFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupReactionsFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_groupreactions_free(ptr, 0);
    }
    /**
     * The value stamped `updatedAtMs`.
     * @param {AllowedReactions} reactions
     * @param {bigint} updated_at_ms
     */
    constructor(reactions, updated_at_ms) {
        _assertClass(reactions, AllowedReactions);
        const ret = wasm.groupreactions_new(reactions.__wbg_ptr, updated_at_ms);
        this.__wbg_ptr = ret;
        GroupReactionsFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * What the group's messages take.
     * @returns {AllowedReactions}
     */
    get reactions() {
        const ret = wasm.groupreactions_reactions(this.__wbg_ptr);
        return AllowedReactions.__wrap(ret);
    }
    /**
     * Setter-clock UNIX milliseconds.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.groupreactions_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) GroupReactions.prototype[Symbol.dispose] = GroupReactions.prototype.free;

/**
 * Per-group MLS state facade. Mirror of
 * [`lakko_storage::GroupStateStore`].
 */
export class GroupStateStore {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        GroupStateStoreFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_groupstatestore_free(ptr, 0);
    }
    /**
     * Drop the snapshot.
     * @param {Uint8Array} group_id
     */
    forget(group_id) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.groupstatestore_forget(this.__wbg_ptr, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Load the most recent snapshot. Returns `None` for unknown groups.
     * @param {Uint8Array} group_id
     * @returns {Uint8Array | undefined}
     */
    load(group_id) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.groupstatestore_load(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v2;
        if (ret[0] !== 0) {
            v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v2;
    }
    /**
     * Wrap an [`EncryptedStore`].
     * @param {EncryptedStore} encrypted
     */
    constructor(encrypted) {
        _assertClass(encrypted, EncryptedStore);
        const ret = wasm.groupstatestore_new(encrypted.__wbg_ptr);
        this.__wbg_ptr = ret;
        GroupStateStoreFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Save a serialised MLS state snapshot.
     * @param {Uint8Array} group_id
     * @param {Uint8Array} state
     */
    save(group_id, state) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(state, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.groupstatestore_save(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
}
if (Symbol.dispose) GroupStateStore.prototype[Symbol.dispose] = GroupStateStore.prototype.free;

/**
 * All groups and channels, or only the chosen ones. Numeric values are the
 * frozen wire bytes (0 on the wire is "off", which is `undefined` here).
 * @enum {1 | 2}
 */
export const GroupsForm = Object.freeze({
    /**
     * Every group and channel the person is in.
     */
    All: 1, "1": "All",
    /**
     * Only the ones in the chosen list.
     */
    Chosen: 2, "2": "Chosen",
});

/**
 * The authenticated claims of a verified handle certificate.
 */
export class HandleCertificate {
    static __wrap(ptr) {
        const obj = Object.create(HandleCertificate.prototype);
        obj.__wbg_ptr = ptr;
        HandleCertificateFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        HandleCertificateFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_handlecertificate_free(ptr, 0);
    }
    /**
     * When discovery issued it, UNIX ms. Informational.
     * @returns {bigint}
     */
    get issuedAtUnixMillis() {
        const ret = wasm.handlecertificate_issuedAtUnixMillis(this.__wbg_ptr);
        return ret;
    }
    /**
     * The certified 64-byte lookup key of the name.
     * @returns {Uint8Array}
     */
    get lookupKey() {
        const ret = wasm.handlecertificate_lookupKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 32-byte handle key that holds the name.
     * @returns {Uint8Array}
     */
    get writePk() {
        const ret = wasm.handlecertificate_writePk(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) HandleCertificate.prototype[Symbol.dispose] = HandleCertificate.prototype.free;

/**
 * What a public group's or channel's record reveals.
 */
export class HandleRecordPayload {
    static __wrap(ptr) {
        const obj = Object.create(HandleRecordPayload.prototype);
        obj.__wbg_ptr = ptr;
        HandleRecordPayloadFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        HandleRecordPayloadFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_handlerecordpayload_free(ptr, 0);
    }
    /**
     * The photo (its keys are public), or `undefined`.
     * @returns {ProfileAvatar | undefined}
     */
    get avatar() {
        const ret = wasm.handlerecordpayload_avatar(this.__wbg_ptr);
        return ret === 0 ? undefined : ProfileAvatar.__wrap(ret);
    }
    /**
     * The description.
     * @returns {string}
     */
    get description() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.handlerecordpayload_description(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The canonical `@name`.
     * @returns {string}
     */
    get handle() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.handlerecordpayload_handle(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The group id or channel id.
     * @returns {Uint8Array}
     */
    get id() {
        const ret = wasm.handlerecordpayload_id(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The knock public key (group) or read key (channel).
     * @returns {Uint8Array}
     */
    get key() {
        const ret = wasm.handlerecordpayload_key(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `"group"` or `"channel"`.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.handlerecordpayload_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The version of the group's knock key, named in the knock row a
     * searcher seals to it; `0` for a channel.
     * @returns {number}
     */
    get knockVersion() {
        const ret = wasm.handlerecordpayload_knockVersion(this.__wbg_ptr);
        return ret;
    }
    /**
     * The name.
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.handlerecordpayload_name(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Build a payload: `kind` `"group"` (32-byte id, knock key) or
     * `"channel"` (16-byte id, read key). `knock_version` is the version of
     * the group's knock key; omitted means `0`, which a channel must carry.
     *
     * # Errors
     *
     * Throws `handleRecord.unknownKind` / `handleRecord.badLength: ...`.
     * @param {string} kind
     * @param {bigint} updated_at_ms
     * @param {Uint8Array} id
     * @param {Uint8Array} key
     * @param {string} handle
     * @param {string} name
     * @param {string} description
     * @param {number} size_band
     * @param {ProfileAvatar | null} [avatar]
     * @param {number | null} [knock_version]
     */
    constructor(kind, updated_at_ms, id, key, handle, name, description, size_band, avatar, knock_version) {
        const ptr0 = passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passStringToWasm0(handle, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len3 = WASM_VECTOR_LEN;
        const ptr4 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len4 = WASM_VECTOR_LEN;
        const ptr5 = passStringToWasm0(description, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len5 = WASM_VECTOR_LEN;
        let ptr6 = 0;
        if (!isLikeNone(avatar)) {
            _assertClass(avatar, ProfileAvatar);
            ptr6 = avatar.__destroy_into_raw();
        }
        const ret = wasm.handlerecordpayload_new(ptr0, len0, updated_at_ms, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, ptr5, len5, size_band, ptr6, isLikeNone(knock_version) ? 0xFFFFFF : knock_version);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        HandleRecordPayloadFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The size band, `0..=5`.
     * @returns {number}
     */
    get sizeBand() {
        const ret = wasm.handlerecordpayload_sizeBand(this.__wbg_ptr);
        return ret;
    }
    /**
     * Owner-clock UNIX milliseconds.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.handlerecordpayload_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) HandleRecordPayload.prototype[Symbol.dispose] = HandleRecordPayload.prototype.free;

/**
 * A reaction a person holds after the fold.
 */
export class HeldReaction {
    static __wrap(ptr) {
        const obj = Object.create(HeldReaction.prototype);
        obj.__wbg_ptr = ptr;
        HeldReactionFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof HeldReaction)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        HeldReactionFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_heldreaction_free(ptr, 0);
    }
    /**
     * The DS message id of the add that gave it.
     * @returns {bigint}
     */
    get at() {
        const ret = wasm.heldreaction_at(this.__wbg_ptr);
        return ret;
    }
    /**
     * The emoji.
     * @returns {string}
     */
    get emoji() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.heldreaction_emoji(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Rebuild a held reaction (for a cached fold).
     *
     * # Errors
     *
     * Throws `reaction.badLength`.
     * @param {Uint8Array} person
     * @param {string} emoji
     * @param {bigint} at
     */
    constructor(person, emoji, at) {
        const ptr0 = passArray8ToWasm0(person, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.heldreaction_new(ptr0, len0, ptr1, len1, at);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        HeldReactionFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The 32-byte account key.
     * @returns {Uint8Array}
     */
    get person() {
        const ret = wasm.heldreaction_person(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) HeldReaction.prototype[Symbol.dispose] = HeldReaction.prototype.free;

/**
 * One attachment the caller adds to a placeholder before
 * encoding. `blobId` / `key` MUST be 32 bytes, `nonce` 12 bytes.
 * JS-side `new HistoryAttachmentInput(...)`.
 */
export class HistoryAttachmentInput {
    static __unwrap(jsValue) {
        if (!(jsValue instanceof HistoryAttachmentInput)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        HistoryAttachmentInputFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_historyattachmentinput_free(ptr, 0);
    }
    /**
     * JS constructor — one call per attachment.
     * @param {Uint8Array} blob_id
     * @param {Uint8Array} key
     * @param {Uint8Array} nonce
     * @param {bigint} size
     * @param {string | null} [mime]
     * @param {string | null} [name]
     */
    constructor(blob_id, key, nonce, size, mime, name) {
        const ptr0 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(nonce, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(mime) ? 0 : passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        var ptr4 = isLikeNone(name) ? 0 : passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len4 = WASM_VECTOR_LEN;
        const ret = wasm.historyattachmentinput_new(ptr0, len0, ptr1, len1, ptr2, len2, size, ptr3, len3, ptr4, len4);
        this.__wbg_ptr = ret;
        HistoryAttachmentInputFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * ADR-0047 media metadata. `waveform` is the raw amplitude bytes.
     * Out-of-range values are dropped on encode, as for a live envelope.
     * @param {string | null} [kind]
     * @param {number | null} [duration_ms]
     * @param {Uint8Array | null} [waveform]
     * @param {number | null} [width]
     * @param {number | null} [height]
     */
    setMedia(kind, duration_ms, waveform, width, height) {
        var ptr0 = isLikeNone(kind) ? 0 : passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(waveform) ? 0 : passArray8ToWasm0(waveform, wasm.__wbindgen_malloc);
        var len1 = WASM_VECTOR_LEN;
        wasm.historyattachmentinput_setMedia(this.__wbg_ptr, ptr0, len0, isLikeNone(duration_ms) ? Number.MAX_SAFE_INTEGER : (duration_ms) >>> 0, ptr1, len1, isLikeNone(width) ? Number.MAX_SAFE_INTEGER : (width) >>> 0, isLikeNone(height) ? Number.MAX_SAFE_INTEGER : (height) >>> 0);
    }
    /**
     * ADR-0047 poster frame. `blobId` / `key` MUST be 32 bytes, `nonce`
     * 12 bytes.
     * @param {Uint8Array} blob_id
     * @param {Uint8Array} key
     * @param {Uint8Array} nonce
     * @param {bigint} size
     * @param {string | null} [mime]
     * @param {number | null} [width]
     * @param {number | null} [height]
     */
    setThumb(blob_id, key, nonce, size, mime, width, height) {
        const ptr0 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(nonce, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(mime) ? 0 : passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        wasm.historyattachmentinput_setThumb(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, size, ptr3, len3, isLikeNone(width) ? Number.MAX_SAFE_INTEGER : (width) >>> 0, isLikeNone(height) ? Number.MAX_SAFE_INTEGER : (height) >>> 0);
    }
}
if (Symbol.dispose) HistoryAttachmentInput.prototype[Symbol.dispose] = HistoryAttachmentInput.prototype.free;

/**
 * One group's worth of history rows for the seal path.
 * wasm-bindgen class — JS-side `new HistoryGroupInput(...)`.
 */
export class HistoryGroupInput {
    static __unwrap(jsValue) {
        if (!(jsValue instanceof HistoryGroupInput)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        HistoryGroupInputFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_historygroupinput_free(ptr, 0);
    }
    /**
     * JS constructor. `messages` is a flat `Array<HistoryMessageInput>`;
     * the binding consumes ownership.
     * @param {Uint8Array} group_id
     * @param {HistoryMessageInput[]} messages
     * @param {boolean} count_capped
     */
    constructor(group_id, messages, count_capped) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(messages, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.historygroupinput_new(ptr0, len0, ptr1, len1, count_capped);
        this.__wbg_ptr = ret;
        HistoryGroupInputFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) HistoryGroupInput.prototype[Symbol.dispose] = HistoryGroupInput.prototype.free;

/**
 * One message the scanner-side caller adds to a
 * [`HistoryGroupInput`] before sealing the bundle.
 * wasm-bindgen class — JS-side `new HistoryMessageInput(...)`.
 */
export class HistoryMessageInput {
    static __unwrap(jsValue) {
        if (!(jsValue instanceof HistoryMessageInput)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        HistoryMessageInputFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_historymessageinput_free(ptr, 0);
    }
    /**
     * JS constructor — one call per included message.
     * @param {Uint8Array} payload_bytes
     * @param {number} sender_leaf
     * @param {Uint8Array} message_id
     * @param {bigint} received_at_ms
     */
    constructor(payload_bytes, sender_leaf, message_id, received_at_ms) {
        const ptr0 = passArray8ToWasm0(payload_bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(message_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.historymessageinput_new(ptr0, len0, sender_leaf, ptr1, len1, received_at_ms);
        this.__wbg_ptr = ret;
        HistoryMessageInputFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) HistoryMessageInput.prototype[Symbol.dispose] = HistoryMessageInput.prototype.free;

/**
 * JS-visible Hybrid Logical Clock value: physical milliseconds plus
 * a logical counter. Total-ordered lexicographically. Mirror of
 * [`lakko_sync::Hlc`].
 */
export class Hlc {
    static __wrap(ptr) {
        const obj = Object.create(Hlc.prototype);
        obj.__wbg_ptr = ptr;
        HlcFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        HlcFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_hlc_free(ptr, 0);
    }
    /**
     * Lexicographic ordering: returns `-1` if `self < other`, `0` if
     * equal, `1` if `self > other`. JS lacks a built-in `Ord`; this
     * is the cheapest-friction equivalent.
     * @param {Hlc} other
     * @returns {number}
     */
    compareTo(other) {
        _assertClass(other, Hlc);
        const ret = wasm.hlc_compareTo(this.__wbg_ptr, other.__wbg_ptr);
        return ret;
    }
    /**
     * Logical counter — breaks ties when `physicalMs` collides.
     * @returns {bigint}
     */
    get logicalCounter() {
        const ret = wasm.hlc_logicalCounter(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Construct an `Hlc` from raw fields. Most callers should drive
     * a [`HybridLogicalClock`] instead.
     * @param {bigint} physical_ms
     * @param {bigint} logical_counter
     */
    constructor(physical_ms, logical_counter) {
        const ret = wasm.hlc_new(physical_ms, logical_counter);
        this.__wbg_ptr = ret;
        HlcFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Physical-time component, milliseconds since UNIX epoch.
     * @returns {bigint}
     */
    get physicalMs() {
        const ret = wasm.hlc_physicalMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The minimum-value `Hlc` (`pt = 0`, `lc = 0`).
     * @returns {Hlc}
     */
    static zero() {
        const ret = wasm.hlc_zero();
        return Hlc.__wrap(ret);
    }
}
if (Symbol.dispose) Hlc.prototype[Symbol.dispose] = Hlc.prototype.free;

/**
 * Per-device HLC state. Wrap once at startup, persist `last` across
 * restarts via [`HybridLogicalClock::last`] / the `with_state`
 * constructor. Mirror of [`lakko_sync::HybridLogicalClock`].
 *
 * `Mutex` so one shared instance can be cloned into multiple async
 * callers; on wasm32 there's only one thread anyway, so the mutex
 * never actually contests.
 */
export class HybridLogicalClock {
    static __wrap(ptr) {
        const obj = Object.create(HybridLogicalClock.prototype);
        obj.__wbg_ptr = ptr;
        HybridLogicalClockFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        HybridLogicalClockFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_hybridlogicalclock_free(ptr, 0);
    }
    /**
     * Most recent `Hlc` issued by this clock.
     * @returns {Hlc}
     */
    get last() {
        const ret = wasm.hybridlogicalclock_last(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Hlc.__wrap(ret[0]);
    }
    /**
     * Merge an `Hlc` from another device. Returns a fresh local
     * stamp ordered strictly after both sides.
     * @param {Hlc} remote
     * @param {bigint} wall_now_ms
     * @returns {Hlc}
     */
    mergeRemote(remote, wall_now_ms) {
        _assertClass(remote, Hlc);
        const ret = wasm.hybridlogicalclock_mergeRemote(this.__wbg_ptr, remote.__wbg_ptr, wall_now_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Hlc.__wrap(ret[0]);
    }
    /**
     * Fresh clock seeded at `Hlc::ZERO`.
     */
    constructor() {
        const ret = wasm.hybridlogicalclock_new();
        this.__wbg_ptr = ret;
        HybridLogicalClockFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Issue a fresh stamp for a local event happening at wall-clock
     * time `wall_now_ms`. Strictly greater than every previous
     * return.
     * @param {bigint} wall_now_ms
     * @returns {Hlc}
     */
    tick(wall_now_ms) {
        const ret = wasm.hybridlogicalclock_tick(this.__wbg_ptr, wall_now_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Hlc.__wrap(ret[0]);
    }
    /**
     * Construct from a previous `last` stamp — used to keep events
     * monotonic across app restarts (caller persists `last` to
     * local storage).
     * @param {Hlc} last
     * @returns {HybridLogicalClock}
     */
    static withState(last) {
        _assertClass(last, Hlc);
        const ret = wasm.hybridlogicalclock_withState(last.__wbg_ptr);
        return HybridLogicalClock.__wrap(ret);
    }
}
if (Symbol.dispose) HybridLogicalClock.prototype[Symbol.dispose] = HybridLogicalClock.prototype.free;

/**
 * Lakko identity — owns the device's signing keypair, MLS credential
 * and per-device storage provider. Mirror of UniFFI's `Identity`.
 *
 * JS usage:
 *
 * ```js
 * import { Identity } from "@lakko/ffi-wasm";
 * const alice = new Identity("alice");
 * const pk = alice.signaturePublicKey;        // Uint8Array
 * const kp = alice.createKeyPackage();        // Uint8Array
 * ```
 */
export class Identity {
    static __wrap(ptr) {
        const obj = Object.create(Identity.prototype);
        obj.__wbg_ptr = ptr;
        IdentityFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IdentityFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_identity_free(ptr, 0);
    }
    /**
     * The account key this identity acts for (ADR-0050): the binding's
     * account key when bound, else its own signature key.
     * @returns {Uint8Array | undefined}
     */
    get accountPublicKey() {
        const ret = wasm.identity_accountPublicKey(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Bind this identity to an account in place (ADR-0050); see
     * `fromSeedBound`.
     * @param {Uint8Array} account_seed
     * @param {bigint} issued_at_unix_secs
     */
    bindToAccount(account_seed, issued_at_unix_secs) {
        const ptr0 = passArray8ToWasm0(account_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.identity_bindToAccount(this.__wbg_ptr, ptr0, len0, issued_at_unix_secs);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Create an external-join proposal — the joiner side of ADR-0027 (G-3).
     *
     * A non-member crafts an external Add proposal for a group's current
     * `group_id` + `epoch` (learned out-of-band via a join ticket). The
     * returned wire bytes are posted to the group journal; an owner/admin
     * approves via `Group.approveExternalJoin`, and whichever device then
     * sweeps produces the Welcome the joiner opens via
     * `Group.joinFromWelcome`. The proposal embeds a fresh KeyPackage
     * whose private keys persist in this identity's provider, so the SAME
     * identity must later open the Welcome.
     * @param {Uint8Array} group_id
     * @param {bigint} epoch
     * @returns {Uint8Array}
     */
    createExternalJoinProposal(group_id, epoch) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.identity_createExternalJoinProposal(this.__wbg_ptr, ptr0, len0, epoch);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Create a fresh KeyPackage in TLS wire form.
     * @returns {Uint8Array}
     */
    createKeyPackage() {
        const ret = wasm.identity_createKeyPackage(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The wire bytes of this identity's ADR-0050 device binding, or
     * `undefined` for an unbound identity. Persist them beside the device
     * seed.
     * @returns {Uint8Array | undefined}
     */
    get deviceBinding() {
        const ret = wasm.identity_deviceBinding(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Raw Ed25519 signing-seed bytes (32 bytes). Hand this to
     * `ContactRequest.compose(...)` as the
     * `senderSigningSeed` argument when sending a contact
     * request. Same secret-handling rules as
     * [`x25519SecretKey`](Self::x25519_secret_key).
     * @returns {Uint8Array}
     */
    get ed25519Seed() {
        const ret = wasm.identity_ed25519Seed(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Derive an identity **deterministically** from a seed.
     *
     * Same `seed` ⇒ byte-identical Ed25519 + X25519 keys. The
     * expected caller is the `apps/web` auth layer, which
     * seeds this with the OPAQUE password export key so a
     * fresh login on any device reproduces the user's
     * long-lived public keys without persisting Identity bytes
     * anywhere.
     * @param {Uint8Array} seed
     * @returns {Identity}
     */
    static fromSeed(seed) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.identity_fromSeed(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Identity.__wrap(ret[0]);
    }
    /**
     * A **device** identity bound to an account (ADR-0050): derive it from
     * `seed` and mint its device binding under `accountSeed` (the account
     * identity's `ed25519Seed`). Every KeyPackage and every group this
     * identity creates carries the binding in its leaf credential.
     * @param {Uint8Array} seed
     * @param {Uint8Array} account_seed
     * @param {bigint} issued_at_unix_secs
     * @returns {Identity}
     */
    static fromSeedBound(seed, account_seed, issued_at_unix_secs) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(account_seed, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.identity_fromSeedBound(ptr0, len0, ptr1, len1, issued_at_unix_secs);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Identity.__wrap(ret[0]);
    }
    /**
     * Rebuild a bound device identity from its persisted seed and its
     * persisted binding bytes (ADR-0050), so `issuedAt` stays what it was.
     * @param {Uint8Array} seed
     * @param {Uint8Array} binding
     * @returns {Identity}
     */
    static fromSeedWithBinding(seed, binding) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(binding, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.identity_fromSeedWithBinding(ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return Identity.__wrap(ret[0]);
    }
    /**
     * Generate a fresh identity with the given user-display label.
     * @param {string} name
     */
    constructor(name) {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.identity_new(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        IdentityFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Replace this identity's MLS provider storage with the
     * contents of a snapshot produced by `snapshotStorage`
     * on a previous run. **Replaces** rather than merges —
     * see the Rust-side docstring for the seed-pairing
     * requirement.
     * @param {Uint8Array} snapshot
     */
    restoreStorage(snapshot) {
        const ptr0 = passArray8ToWasm0(snapshot, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.identity_restoreStorage(this.__wbg_ptr, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Public signature key bytes.
     * @returns {Uint8Array}
     */
    get signaturePublicKey() {
        const ret = wasm.identity_signaturePublicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Serialise the OpenMLS provider's storage to opaque
     * bytes (Phase 1.16e6b). Pair with
     * [`Identity::restoreStorage`](Self::restore_storage)
     * to round-trip MLS state across a page reload.
     *
     * The bytes are opaque to the JS caller — the only
     * supported operation is feeding them back through
     * `restoreStorage` on a fresh `Identity` derived from the
     * same seed. apps/web wraps the snapshot under the device
     * root key (AES-256-GCM, 1.16c3c2) before stashing in
     * IndexedDB so the on-disk bytes can't be lifted by
     * page-level JavaScript.
     * @returns {Uint8Array}
     */
    snapshotStorage() {
        const ret = wasm.identity_snapshotStorage(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Long-lived X25519 public key (32 bytes) — the HPKE
     * init_key callers of `lakko-contacts::compose` target when
     * sealing an envelope to this identity. Pair with the bundle
     * uploaded to the discovery service via
     * `POST /discovery/identity` (Phase 1.16d3a).
     * @returns {Uint8Array}
     */
    get x25519PublicKey() {
        const ret = wasm.identity_x25519PublicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Raw X25519 secret bytes (32 bytes). Used as input to
     * `ContactRequest.openEnvelope(...)` when decrypting an
     * incoming envelope.
     *
     * **Sensitive.** The caller MUST persist these bytes only
     * after wrapping them with the device root key (1.16c3c2's
     * `deriveDeviceRootKey` output). They MUST NOT touch
     * `localStorage` or any other unencrypted store.
     * @returns {Uint8Array}
     */
    get x25519SecretKey() {
        const ret = wasm.identity_x25519SecretKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) Identity.prototype[Symbol.dispose] = Identity.prototype.free;

/**
 * Permanent identity pseudonym derived from the long-term Ed25519
 * public key. Mirror of [`lakko_contacts::IdentityPseudonym`].
 */
export class IdentityPseudonym {
    static __wrap(ptr) {
        const obj = Object.create(IdentityPseudonym.prototype);
        obj.__wbg_ptr = ptr;
        IdentityPseudonymFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IdentityPseudonymFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_identitypseudonym_free(ptr, 0);
    }
    /**
     * 32 raw bytes.
     * @returns {Uint8Array}
     */
    asBytes() {
        const ret = wasm.identitypseudonym_asBytes(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Wrap raw 32-byte pseudonym (e.g. parsed off a wire format).
     * @param {Uint8Array} bytes
     * @returns {IdentityPseudonym}
     */
    static fromBytes(bytes) {
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.identitypseudonym_fromBytes(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return IdentityPseudonym.__wrap(ret[0]);
    }
    /**
     * Derive from a 32-byte Ed25519 verifying key.
     * @param {Uint8Array} pubkey
     * @returns {IdentityPseudonym}
     */
    static fromIdentity(pubkey) {
        const ptr0 = passArray8ToWasm0(pubkey, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.identitypseudonym_fromIdentity(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return IdentityPseudonym.__wrap(ret[0]);
    }
}
if (Symbol.dispose) IdentityPseudonym.prototype[Symbol.dispose] = IdentityPseudonym.prototype.free;

/**
 * `Mutex<HashMap>` per-table store. Lives entirely in RAM; everything
 * is dropped when the page unloads.
 *
 * Pass to [`EncryptedStore::new`] to wrap with AEAD encryption.
 */
export class InMemoryStore {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InMemoryStoreFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_inmemorystore_free(ptr, 0);
    }
    /**
     * Delete the row. Idempotent.
     * @param {Table} table
     * @param {Uint8Array} key
     */
    delete(table, key) {
        const ptr0 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.inmemorystore_delete(this.__wbg_ptr, table, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Fetch the value for `(table, key)`. Returns `None` if absent.
     * @param {Table} table
     * @param {Uint8Array} key
     * @returns {Uint8Array | undefined}
     */
    get(table, key) {
        const ptr0 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.inmemorystore_get(this.__wbg_ptr, table, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v2;
        if (ret[0] !== 0) {
            v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v2;
    }
    /**
     * Construct an empty store.
     */
    constructor() {
        const ret = wasm.inmemorystore_new();
        this.__wbg_ptr = ret;
        InMemoryStoreFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Insert or replace `(table, key) → value`.
     * @param {Table} table
     * @param {Uint8Array} key
     * @param {Uint8Array} value
     */
    put(table, key, value) {
        const ptr0 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(value, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.inmemorystore_put(this.__wbg_ptr, table, ptr0, len0, ptr1, len1);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
}
if (Symbol.dispose) InMemoryStore.prototype[Symbol.dispose] = InMemoryStore.prototype.free;

/**
 * FFI-portable result of [`dispatchIncomingEnvelope`]. The
 * payload bytes are materialised into an owned `Vec<u8>` at
 * the FFI boundary so the JavaScript caller doesn't have to
 * think about Rust borrow lifetimes.
 */
export class IncomingEnvelope {
    static __wrap(ptr) {
        const obj = Object.create(IncomingEnvelope.prototype);
        obj.__wbg_ptr = ptr;
        IncomingEnvelopeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IncomingEnvelopeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_incomingenvelope_free(ptr, 0);
    }
    /**
     * Set only for `MediaState`: whether the sender's camera
     * is on. `undefined` (`None`) for every other kind.
     * @returns {boolean | undefined}
     */
    get cameraOn() {
        const ret = wasm.incomingenvelope_cameraOn(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret !== 0;
    }
    /**
     * Set only for `CommitterDelegation` (ADR-0060 §6): the envelope,
     * structurally checked. `undefined` for every other kind.
     * @returns {CommitterDelegationEnvelope | undefined}
     */
    get committerDelegation() {
        const ret = wasm.incomingenvelope_committerDelegation(this.__wbg_ptr);
        return ret === 0 ? undefined : CommitterDelegationEnvelope.__wrap(ret);
    }
    /**
     * Set only for `DeviceCard` (ADR-0080): the decoded card. `undefined`
     * for every other kind.
     * @returns {DeviceCard | undefined}
     */
    get deviceCard() {
        const ret = wasm.incomingenvelope_deviceCard(this.__wbg_ptr);
        return ret === 0 ? undefined : DeviceCard.__wrap(ret);
    }
    /**
     * Set only for `GroupClosed` (ADR-0073): the decoded notice. `undefined`
     * for every other kind.
     * @returns {GroupClosed | undefined}
     */
    get groupClosed() {
        const ret = wasm.incomingenvelope_groupClosed(this.__wbg_ptr);
        return ret === 0 ? undefined : GroupClosed.__wrap(ret);
    }
    /**
     * Set only for `GroupName` (ADR-0051): the decoded group name.
     * `undefined` for every other kind.
     * @returns {GroupName | undefined}
     */
    get groupName() {
        const ret = wasm.incomingenvelope_groupName(this.__wbg_ptr);
        return ret === 0 ? undefined : GroupName.__wrap(ret);
    }
    /**
     * Set only for `GroupPhoto` (ADR-0087 section 18.1): the decoded photo.
     * @returns {GroupPhoto | undefined}
     */
    get groupPhoto() {
        const ret = wasm.incomingenvelope_groupPhoto(this.__wbg_ptr);
        return ret === 0 ? undefined : GroupPhoto.__wrap(ret);
    }
    /**
     * Set only for `GroupReactions` (ADR-0087 section 13a): the decoded
     * value.
     * @returns {GroupReactions | undefined}
     */
    get groupReactions() {
        const ret = wasm.incomingenvelope_groupReactions(this.__wbg_ptr);
        return ret === 0 ? undefined : GroupReactions.__wrap(ret);
    }
    /**
     * Set only for `IntroductionOffer` (ADR-0060): the record wire,
     * structurally checked and still sealed. `undefined` otherwise.
     * @returns {Uint8Array | undefined}
     */
    get introductionRecord() {
        const ret = wasm.incomingenvelope_introductionRecord(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Set only for `Introductions` (ADR-0060): the decoded bundle.
     * `undefined` otherwise.
     * @returns {IntroductionsBundle | undefined}
     */
    get introductions() {
        const ret = wasm.incomingenvelope_introductions(this.__wbg_ptr);
        return ret === 0 ? undefined : IntroductionsBundle.__wrap(ret);
    }
    /**
     * Set only for `KeyRotation` (ADR-0074): the decoded envelope, shape
     * only. `undefined` for every other kind.
     * @returns {KeyRotation | undefined}
     */
    get keyRotation() {
        const ret = wasm.incomingenvelope_keyRotation(this.__wbg_ptr);
        return ret === 0 ? undefined : KeyRotation.__wrap(ret);
    }
    /**
     * Which dispatch branch fired.
     * @returns {IncomingEnvelopeKind}
     */
    get kind() {
        const ret = wasm.incomingenvelope_kind(this.__wbg_ptr);
        return ret;
    }
    /**
     * Set only for `LinkClaim` (ADR-0079): the decoded claim. `undefined`
     * for every other kind.
     * @returns {LinkClaim | undefined}
     */
    get linkClaim() {
        const ret = wasm.incomingenvelope_linkClaim(this.__wbg_ptr);
        return ret === 0 ? undefined : LinkClaim.__wrap(ret);
    }
    /**
     * Set only for `LinkFounded` (ADR-0079): the decoded note. `undefined`
     * for every other kind.
     * @returns {LinkFounded | undefined}
     */
    get linkFounded() {
        const ret = wasm.incomingenvelope_linkFounded(this.__wbg_ptr);
        return ret === 0 ? undefined : LinkFounded.__wrap(ret);
    }
    /**
     * Set only for `PushRoutingSecret` (ADR-0044): the minter's
     * recency stamp, the primary key of the canonical total
     * order. `undefined` (`None`) for every other kind. JS reads
     * it as a `bigint | undefined` (the field is a `u64`),
     * matching the `Group.epoch()` precedent.
     *
     * The caller MUST NOT store the secret unconditionally —
     * run the ADR-0042 / ADR-0043 role gate, then
     * `pushRoutingSecretIsNewer` against whatever is already
     * stored. A stale re-broadcast carries a valid secret from
     * an authorised sender and is refused only by the order.
     * @returns {bigint | undefined}
     */
    get mintEpoch() {
        const ret = wasm.incomingenvelope_mintEpoch(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
    }
    /**
     * The dispatched payload — for `CallWelcome` and
     * `CallKeyPackage` this is the bytes **after** the
     * 22- / 25-byte domain separator; for `IntroMessage`
     * it is the original plaintext verbatim; for
     * `PushRoutingSecret` it is the decoded 32-byte secret;
     * for `MediaState` it is empty.
     * @returns {Uint8Array}
     */
    get payload() {
        const ret = wasm.incomingenvelope_payload(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Set only for `Pin` (ADR-0052): the decoded pin operation. `undefined`
     * for every other kind.
     * @returns {PinOp | undefined}
     */
    get pin() {
        const ret = wasm.incomingenvelope_pin(this.__wbg_ptr);
        return ret === 0 ? undefined : PinOp.__wrap(ret);
    }
    /**
     * Set only for `Profile` (ADR-0048): the decoded profile. `undefined`
     * for every other kind.
     * @returns {Profile | undefined}
     */
    get profile() {
        const ret = wasm.incomingenvelope_profile(this.__wbg_ptr);
        return ret === 0 ? undefined : Profile.__wrap(ret);
    }
    /**
     * Set only for `ReachState` (ADR-0060): the decoded reach state.
     * `undefined` otherwise.
     * @returns {ReachState | undefined}
     */
    get reachState() {
        const ret = wasm.incomingenvelope_reachState(this.__wbg_ptr);
        return ret === 0 ? undefined : ReachState.__wrap(ret);
    }
    /**
     * Set only for `SharedChannels` (ADR-0059): the decoded list.
     * `undefined` for every other kind.
     * @returns {SharedChannels | undefined}
     */
    get sharedChannels() {
        const ret = wasm.incomingenvelope_sharedChannels(this.__wbg_ptr);
        return ret === 0 ? undefined : SharedChannels.__wrap(ret);
    }
    /**
     * Set only for `Visibility` (ADR-0060): the decoded setting. `undefined`
     * otherwise.
     * @returns {VisibilitySetting | undefined}
     */
    get visibility() {
        const ret = wasm.incomingenvelope_visibility(this.__wbg_ptr);
        return ret === 0 ? undefined : VisibilitySetting.__wrap(ret);
    }
}
if (Symbol.dispose) IncomingEnvelope.prototype[Symbol.dispose] = IncomingEnvelope.prototype.free;

/**
 * Kind tag for [`WasmIncomingEnvelope`]. Mirror of the
 * underlying Rust enum variants. The numeric values are
 * stable wire tags — `MediaState` was appended as `3` so the
 * pre-existing `IntroMessage = 2` tag stays fixed. (The Rust
 * dispatch order is welcome → KP-forward → media-state →
 * intro; the numeric tag is independent of that order.)
 * @enum {0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 24 | 25 | 26}
 */
export const IncomingEnvelopeKind = Object.freeze({
    /**
     * First-joiner Welcome bytes for a link-based call.
     */
    CallWelcome: 0, "0": "CallWelcome",
    /**
     * Subsequent-joiner KeyPackage for a multi-party call.
     */
    CallKeyPackage: 1, "1": "CallKeyPackage",
    /**
     * Plain free-text intro message (ADR-0005 scenario A).
     */
    IntroMessage: 2, "2": "IntroMessage",
    /**
     * Camera on/off media-state signal (camera-off-cleanup).
     * The decoded flag rides the `cameraOn` getter of
     * [`WasmIncomingEnvelope`]; `payload` is empty.
     */
    MediaState: 3, "3": "MediaState",
    /**
     * In-group push-routing-secret distribution envelope
     * (ADR-0042, v2 per ADR-0044). The decoded 32-byte secret
     * rides the `payload` getter of [`WasmIncomingEnvelope`]
     * and the minter's recency stamp rides the `mintEpoch`
     * getter; `cameraOn` is `undefined`. Appended as `4` so the
     * pre-existing tags stay fixed.
     */
    PushRoutingSecret: 4, "4": "PushRoutingSecret",
    /**
     * Committer liveness beacon (ADR-0045 decision 2, variant 1).
     * Carries **no** payload — `payload` is empty, `cameraOn` and
     * `mintEpoch` are `undefined` — because the beacon has no
     * fields: the sender comes from the MLS sender leaf and the age
     * from the DS row. Appended as `5` so the pre-existing tags stay
     * fixed.
     *
     * **This variant exists so a beacon is classified, not so it is
     * consumed.** The fold that turns beacons into an
     * acting-committer decision (`actingCommitterVerdict` /
     * `shouldEmitBeacon` and their constants) is deliberately **not
     * yet exposed** on this surface; until it is, a client that sees
     * this kind should drop it. What the variant buys today is that
     * a beacon can never fall through to `IntroMessage` and be
     * rendered as a chat bubble.
     */
    CommitterBeacon: 5, "5": "CommitterBeacon",
    /**
     * A member's profile (ADR-0048). The decoded, bounds-checked profile
     * rides the `profile` getter of [`WasmIncomingEnvelope`]; `payload` is
     * empty, `cameraOn` and `mintEpoch` are `undefined`. Appended as `6` so
     * the pre-existing tags stay fixed. Never render it as a chat bubble;
     * store it under the sender's signature key resolved at receipt
     * (C-68), keep it only when `profileIsNewer`, and verify the username
     * through discovery before showing it.
     */
    Profile: 6, "6": "Profile",
    /**
     * The group's shared name (ADR-0051 §1). The decoded, bounds-checked
     * name rides the `groupName` getter of [`WasmIncomingEnvelope`];
     * `payload` is empty, `cameraOn`, `mintEpoch` and `profile` are
     * `undefined`. Appended as `7` so the pre-existing tags stay fixed.
     * Never render it as a chat bubble; the sender's authority is the
     * receiver's role-table check (ADR-0051 §2), then `groupNameIsNewer`.
     * An empty name clears the name.
     */
    GroupName: 7, "7": "GroupName",
    /**
     * A member asks the group to introduce itself (ADR-0048 §2 trigger 4).
     * `payload` is empty; answer with the own profile through the throttled
     * growth path; never render it.
     */
    ProfileRequest: 8, "8": "ProfileRequest",
    /**
     * A shared pin or unpin of one message (ADR-0052 decision 3). The
     * decoded operation rides the `pin` getter of [`WasmIncomingEnvelope`];
     * `payload` is empty. Never render it as a chat bubble; the sender's
     * authority is the receiver's role-table check, then `pinIsNewer` for
     * that message.
     */
    Pin: 9, "9": "Pin",
    /**
     * A journal root hands one member its Delivery Service registrar seed
     * (ADR-0057). `payload` is the whole envelope, for
     * `decodeRegistrarSeedEnvelope` / `openRegistrarSeedEnvelope`. Never
     * render it; keep it only when addressed to the own account key and
     * the sender resolves (C-68) to the group's owner.
     */
    RegistrarSeed: 10, "10": "RegistrarSeed",
    /**
     * A contact's introduction record to relay (ADR-0060 §4). The record
     * wire rides the `introductionRecord` getter; `payload` is empty. Never
     * render it; accept it only from the other participant of a 1:1
     * conversation and keep the newest per contact.
     */
    IntroductionOffer: 11, "11": "IntroductionOffer",
    /**
     * A contact's bundle of introduction records (ADR-0060 §4), on the
     * `introductions` getter. Keep the newest per contact via
     * `bundleIsNewer`, search it locally, never re-relay it.
     */
    Introductions: 12, "12": "Introductions",
    /**
     * The owner's "who can find me" setting from another of their devices
     * (ADR-0060 §7), on the `visibility` getter. Accept only on the owner's
     * DeviceGroup, keep via `visibilityIsNewer`.
     */
    Visibility: 13, "13": "Visibility",
    /**
     * The owner's reach-key version counters (ADR-0060 §7), on the
     * `reachState` getter. Same rules, ordered by `reachStateIsNewer`.
     */
    ReachState: 14, "14": "ReachState",
    /**
     * A proposer's committer delegations (ADR-0060 §6), on the
     * `committerDelegation` getter; `payload` is empty. Never render it.
     * Keep the newest per `(MLS sender resolved at receipt,
     * addedAccountKey)`; when this device commits a sweep that added that
     * person, get its delegation with `committerDelegationFor` and deliver
     * the Welcome under it.
     */
    CommitterDelegation: 15, "15": "CommitterDelegation",
    /**
     * The listed channels one member shares with the group (ADR-0059 §6).
     * The decoded list rides the `sharedChannels` getter; `payload` is
     * empty. Never render it as a chat bubble; store it per member only
     * when `sharedChannelsIsNewer`.
     */
    SharedChannels: 16, "16": "SharedChannels",
    /**
     * An admin closed this group and names the recreated one (ADR-0073
     * phase 2). The decoded notice rides the `groupClosed` getter; `payload`
     * is empty. Never render it as a chat bubble; the sender's authority is
     * the receiver's role-table check (owner / admin only), then
     * `groupClosedIsNewer` for that group.
     */
    GroupClosed: 17, "17": "GroupClosed",
    /**
     * Another device of this account is changing the account keys
     * (ADR-0074 section 3.3). The decoded envelope rides the `keyRotation`
     * getter; `payload` is empty. Never render it. Meaningful ONLY in the
     * account's own DeviceGroup: met anywhere else, drop it. There, pass
     * the original plaintext to `verifyKeyRotationEnvelope` with the
     * account key this device holds, store the result as pending, and
     * adopt it only once the server-side change is visible. It carries the
     * new account's entropy: never log it.
     */
    KeyRotation: 18, "18": "KeyRotation",
    /**
     * What another device of this account says about itself (ADR-0080).
     * The decoded card rides the `deviceCard` getter; `payload` is empty.
     * Never render it. Meaningful ONLY in the account's own DeviceGroup:
     * store it under the sender's leaf signature key resolved at receipt,
     * keep it only when `deviceCardIsNewer`, drop it when the leaf leaves.
     */
    DeviceCard: 19, "19": "DeviceCard",
    /**
     * Another device of this account claims a contact request sent through
     * one of the account's invite links (ADR-0079, reworked 2026-10-17).
     * The decoded claim rides the `linkClaim` getter; `payload` is empty.
     * Never render it. Meaningful ONLY in the account's own DeviceGroup.
     */
    LinkClaim: 20, "20": "LinkClaim",
    /**
     * The device that won an invite-link request names the one-to-one it
     * founded, with the founding Welcome (ADR-0079, ordering of 2026-10-17).
     * The decoded note rides the `linkFounded` getter; `payload` is empty.
     * Never render it. Meaningful ONLY in the account's own DeviceGroup.
     */
    LinkFounded: 21, "21": "LinkFounded",
    /**
     * A group's `@name` and its flag (ADR-0087 section 11). `payload` is the
     * whole envelope; the typed decode and the signature check are the FFI
     * step of ADR-0087. Never render it.
     */
    ConversationHandle: 22, "22": "ConversationHandle",
    /**
     * A knock-key seed sealed to one admin (ADR-0087 section 5). `payload`
     * is the whole envelope; the typed decode and open are the FFI step of
     * ADR-0087. Never render it.
     */
    KnockSeed: 23, "23": "KnockSeed",
    /**
     * A private channel's admission seed sealed to one co-publisher
     * (ADR-0087 section 7). `payload` is the whole envelope; the typed
     * decode and open are the FFI step. Never render it.
     */
    AdmissionSeed: 24, "24": "AdmissionSeed",
    /**
     * A group's photo (ADR-0087 section 18.1) in `groupPhoto`; `payload`
     * is empty. Admit it only from an owner or admin resolved at receipt and
     * keep it only when `groupPhotoIsNewer`. Never render it.
     */
    GroupPhoto: 25, "25": "GroupPhoto",
    /**
     * A group's allowed reactions (ADR-0087 section 13a) in
     * `groupReactions`; `payload` is empty. Admit it only from an owner or
     * admin and keep it only when `groupReactionsIsNewer`. Never render it.
     */
    GroupReactions: 26, "26": "GroupReactions",
});

/**
 * Which info a conversation's name opens — `docs/ux/conversation-info.md`
 * section 1.
 * @enum {0 | 1 | 2}
 */
export const InfoTarget = Object.freeze({
    /**
     * A conversation between two people opens the other person.
     */
    Person: 0, "0": "Person",
    /**
     * A group opens the group.
     */
    Group: 1, "1": "Group",
    /**
     * A channel opens the channel.
     */
    Channel: 2, "2": "Channel",
});

/**
 * ADR-0047 — how an attachment may be rendered inline. Returned by
 * [`inline_media_type`]; `undefined` there means a file chip.
 * @enum {0 | 1 | 2}
 */
export const InlineMediaType = Object.freeze({
    /**
     * Render as an image.
     */
    Image: 0, "0": "Image",
    /**
     * Play as audio.
     */
    Audio: 1, "1": "Audio",
    /**
     * Play as video.
     */
    Video: 2, "2": "Video",
});

/**
 * What an introduction record reveals to a searcher who opens it. The
 * introduced person's claim: verify the username with
 * `verifyUsernameBinding` and run the DS reach check before showing it.
 */
export class IntroductionPayload {
    static __wrap(ptr) {
        const obj = Object.create(IntroductionPayload.prototype);
        obj.__wbg_ptr = ptr;
        IntroductionPayloadFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IntroductionPayloadFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_introductionpayload_free(ptr, 0);
    }
    /**
     * The 16-byte account id.
     * @returns {Uint8Array}
     */
    get accountId() {
        const ret = wasm.introductionpayload_accountId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The display name, or `undefined`.
     * @returns {string | undefined}
     */
    get displayName() {
        const ret = wasm.introductionpayload_displayName(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The `Fof` reach grant (secret); `undefined` in a public record.
     * @returns {ReachGrant | undefined}
     */
    get fof() {
        const ret = wasm.introductionpayload_fof(this.__wbg_ptr);
        return ret === 0 ? undefined : ReachGrant.__wrap(ret);
    }
    /**
     * Build the payload of an Everyone public record (ADR-0060 section 5):
     * the same fields with no grant, and the owner's 97-byte mailbox binding
     * (`signMailboxBinding`, ADR-0078) so a finder knows where to write.
     * Pass it to `composePublicRecord`.
     *
     * # Errors
     *
     * Throws with `introductionError` `identityPkLength`,
     * `accountIdLength` or `badBinding`.
     * @param {Uint8Array} identity_pk
     * @param {Uint8Array} account_id
     * @param {string | null | undefined} display_name
     * @param {string | null | undefined} username
     * @param {Uint8Array | null | undefined} username_certificate
     * @param {Uint8Array} mailbox_binding
     * @returns {IntroductionPayload}
     */
    static forPublicRecord(identity_pk, account_id, display_name, username, username_certificate, mailbox_binding) {
        const ptr0 = passArray8ToWasm0(identity_pk, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(display_name) ? 0 : passStringToWasm0(display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(username) ? 0 : passStringToWasm0(username, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        var ptr4 = isLikeNone(username_certificate) ? 0 : passArray8ToWasm0(username_certificate, wasm.__wbindgen_malloc);
        var len4 = WASM_VECTOR_LEN;
        const ptr5 = passArray8ToWasm0(mailbox_binding, wasm.__wbindgen_malloc);
        const len5 = WASM_VECTOR_LEN;
        const ret = wasm.introductionpayload_forPublicRecord(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, ptr5, len5);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return IntroductionPayload.__wrap(ret[0]);
    }
    /**
     * The 32-byte identity (account) public key.
     * @returns {Uint8Array}
     */
    get identityPk() {
        const ret = wasm.introductionpayload_identityPk(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The owner's 97-byte mailbox binding (ADR-0078), verified against
     * `identityPk` when the payload was composed or opened.
     * @returns {Uint8Array}
     */
    get mailboxBinding() {
        const ret = wasm.introductionpayload_mailboxBinding(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Build a contacts-of-contacts payload carrying its `Fof` grant; the
     * payload's mailbox binding (ADR-0078) is the grant's. The field rules
     * (name, username, certificate, a `Fof` grant, a binding that verifies
     * under `identityPk`) are checked by `composeIntroduction`.
     *
     * # Errors
     *
     * Throws with `introductionError` `identityPkLength` or
     * `accountIdLength`.
     * @param {Uint8Array} identity_pk
     * @param {Uint8Array} account_id
     * @param {string | null | undefined} display_name
     * @param {string | null | undefined} username
     * @param {Uint8Array | null | undefined} username_certificate
     * @param {ReachGrant} fof
     */
    constructor(identity_pk, account_id, display_name, username, username_certificate, fof) {
        const ptr0 = passArray8ToWasm0(identity_pk, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        var ptr2 = isLikeNone(display_name) ? 0 : passStringToWasm0(display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(username) ? 0 : passStringToWasm0(username, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        var ptr4 = isLikeNone(username_certificate) ? 0 : passArray8ToWasm0(username_certificate, wasm.__wbindgen_malloc);
        var len4 = WASM_VECTOR_LEN;
        _assertClass(fof, ReachGrant);
        const ret = wasm.introductionpayload_new(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, fof.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        IntroductionPayloadFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The canonical username, or `undefined`.
     * @returns {string | undefined}
     */
    get username() {
        const ret = wasm.introductionpayload_username(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The 153-byte username certificate, or `undefined`.
     * @returns {Uint8Array | undefined}
     */
    get usernameCertificate() {
        const ret = wasm.introductionpayload_usernameCertificate(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
}
if (Symbol.dispose) IntroductionPayload.prototype[Symbol.dispose] = IntroductionPayload.prototype.free;

/**
 * A contact's decoded bundle of introduction records, as record wires.
 */
export class IntroductionsBundle {
    static __wrap(ptr) {
        const obj = Object.create(IntroductionsBundle.prototype);
        obj.__wbg_ptr = ptr;
        IntroductionsBundleFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        IntroductionsBundleFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_introductionsbundle_free(ptr, 0);
    }
    /**
     * The record wire at `index`, or `undefined` past the end.
     * @param {number} index
     * @returns {Uint8Array | undefined}
     */
    record(index) {
        const ret = wasm.introductionsbundle_record(this.__wbg_ptr, index);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * How many records, padding included.
     * @returns {number}
     */
    recordCount() {
        const ret = wasm.introductionsbundle_recordCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Sender-clock UNIX ms, a `bigint`; orders bundles from one contact via
     * `bundleIsNewer`.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.introductionsbundle_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) IntroductionsBundle.prototype[Symbol.dispose] = IntroductionsBundle.prototype.free;

/**
 * Composed and verified invite capability — capability-based
 * contact-invite flow per ADR-0005 §13.3 / §13.4. Mirror of
 * [`lakko_contacts::InviteCapability`].
 *
 * Owners drive [`InviteCapability::compose`] to produce signed
 * wire bytes; the JS layer base64url-encodes the bytes into a
 * `https://lakko.me/i/<...>` URL and shares it OOB. Joiners
 * drive [`InviteCapability::decodeAndVerify`] on the bytes
 * recovered from the URL slug, then read the getters to seed
 * the first ContactRequest back to the inviter.
 *
 * Phase 1.18b ships the JS surface; the V1 wire format is the
 * 1.18a Rust struct verbatim — no server dependency, capability
 * bytes live in the URL itself.
 */
export class InviteCapability {
    static __wrap(ptr) {
        const obj = Object.create(InviteCapability.prototype);
        obj.__wbg_ptr = ptr;
        InviteCapabilityFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InviteCapabilityFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_invitecapability_free(ptr, 0);
    }
    /**
     * `true` when this is a referral link for a fresh signup
     * (ADR-0005 §13.4).
     * @returns {boolean}
     */
    get bootstrapReferral() {
        const ret = wasm.invitecapability_bootstrapReferral(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Compose a signed invite capability and seal it for an `/i/` link under
     * a fresh link secret. Returns an `InviteLinkMint`.
     *
     * `ownerSigningSeed` is the 32-byte Ed25519 seed that powers
     * the `Identity` (see `mls.rs::Identity::ed25519Seed` for the
     * already-wired-up source). `ownerAccountId` is 16 bytes
     * (UUIDv7). `ownerX25519Pubkey` is the long-term HPKE init
     * key (32 bytes — `Identity::x25519PublicKey`). `expiryMs`
     * is UNIX millis; pass `0` for "never expires" (the JS
     * layer SHOULD surface a stalking-risk warning before
     * allowing this — see ADR-0005 §13.3). `maxUses` is a UI
     * hint only in V1 (1.18d wires server-side enforcement);
     * pass `0` for "single-use". `bootstrapReferral` flips on
     * the signup-deep-link path (ADR-0005 §13.4). `label` is
     * optional (empty allowed), capped at
     * [`lakko_contacts::MAX_LABEL_LEN`] bytes UTF-8.
     *
     * `linkId` (16 random bytes, `generateLinkId()`) and `linkGrant`
     * (ADR-0060 §6) scope the link's reach key: derive the grant as
     * `deriveReachGrant(accountSeed, ReachClass.Link, linkId, version)`,
     * register its public key with the Delivery Service BEFORE sharing the
     * link, and remove it when the link is revoked or expires.
     *
     * `inviterDisplayName` and `inviterUsername` (ADR-0079 §1) name the owner
     * on the invite page: the owner's display name (trimmed; blank or
     * `undefined` = none) and username (normalised; blank or `undefined` =
     * none), sealed with the rest of the capability.
     *
     * # Errors
     *
     * Throws on any input-shape failure, or a grant of any class but
     * `Link`; an `Error` with a `profileError` code for a preview that
     * breaks the ADR-0048 rules.
     * @param {Uint8Array} owner_signing_seed
     * @param {Uint8Array} owner_account_id
     * @param {Uint8Array} owner_x25519_pubkey
     * @param {bigint} expiry_ms
     * @param {number} max_uses
     * @param {boolean} bootstrap_referral
     * @param {string} label
     * @param {Uint8Array} link_id
     * @param {ReachGrant} link_grant
     * @param {string | null} [inviter_display_name]
     * @param {string | null} [inviter_username]
     * @returns {InviteLinkMint}
     */
    static compose(owner_signing_seed, owner_account_id, owner_x25519_pubkey, expiry_ms, max_uses, bootstrap_referral, label, link_id, link_grant, inviter_display_name, inviter_username) {
        const ptr0 = passArray8ToWasm0(owner_signing_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(owner_account_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(owner_x25519_pubkey, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passStringToWasm0(label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len3 = WASM_VECTOR_LEN;
        const ptr4 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
        const len4 = WASM_VECTOR_LEN;
        _assertClass(link_grant, ReachGrant);
        var ptr5 = isLikeNone(inviter_display_name) ? 0 : passStringToWasm0(inviter_display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len5 = WASM_VECTOR_LEN;
        var ptr6 = isLikeNone(inviter_username) ? 0 : passStringToWasm0(inviter_username, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len6 = WASM_VECTOR_LEN;
        const ret = wasm.invitecapability_compose(ptr0, len0, ptr1, len1, ptr2, len2, expiry_ms, max_uses, bootstrap_referral, ptr3, len3, ptr4, len4, link_grant.__wbg_ptr, ptr5, len5, ptr6, len6);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return InviteLinkMint.__wrap(ret[0]);
    }
    /**
     * Decode PLAINTEXT capability wire bytes and verify the Ed25519
     * signature. A link never carries plaintext since the 2026-09-28
     * security pass — a joiner uses [`InviteCapability::openLink`]. Does
     * NOT check expiry — call [`InviteCapability::isValidNow`].
     * @param {Uint8Array} wire
     * @returns {InviteCapability}
     */
    static decodeAndVerify(wire) {
        const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.invitecapability_decodeAndVerify(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return InviteCapability.__wrap(ret[0]);
    }
    /**
     * UNIX milliseconds when the invite stops being valid. `0`
     * is the `NEVER_EXPIRES` sentinel — UI must warn before
     * honouring (stalking risk, ADR-0005 §13.3).
     * @returns {bigint}
     */
    get expiryMs() {
        const ret = wasm.invitecapability_expiryMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The owner's display name as the link states it (ADR-0079 §1), or
     * `undefined`. The owner's own claim: shown on the invite page, never
     * used for identity.
     * @returns {string | undefined}
     */
    get inviterDisplayName() {
        const ret = wasm.invitecapability_inviterDisplayName(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The owner's username as the link states it (ADR-0079 §1), canonical,
     * or `undefined`.
     * @returns {string | undefined}
     */
    get inviterUsername() {
        const ret = wasm.invitecapability_inviterUsername(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * `true` iff the capability has not expired at `nowMs`.
     * `NEVER_EXPIRES` capabilities always return `true`.
     * @param {bigint} now_ms
     * @returns {boolean}
     */
    isValidNow(now_ms) {
        const ret = wasm.invitecapability_isValidNow(this.__wbg_ptr, now_ms);
        return ret !== 0;
    }
    /**
     * Optional human-visible label, UTF-8, ≤
     * `MAX_LABEL_LEN`. Empty string when the inviter didn't
     * set one — UI fills in a default from the inviter's
     * display name on lookup.
     * @returns {string}
     */
    get label() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.invitecapability_label(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The link's `link` reach grant (ADR-0060 §6). SECRET — the joiner
     * writes its `ContactRequest` to the owner's mailbox with
     * `DsProofSigner.forReach(linkGrant.seed)`, then drops it.
     * @returns {ReachGrant}
     */
    get linkGrant() {
        const ret = wasm.invitecapability_linkGrant(this.__wbg_ptr);
        return ReachGrant.__wrap(ret);
    }
    /**
     * The link's 16-byte reach scope id (ADR-0060 §6).
     * @returns {Uint8Array}
     */
    get linkId() {
        const ret = wasm.invitecapability_linkId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Max-uses hint. `0` is the `SINGLE_USE` sentinel. V1 is a
     * best-effort hint with no server enforcement — that lands
     * in 1.18d.
     * @returns {number}
     */
    get maxUses() {
        const ret = wasm.invitecapability_maxUses(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Joiner-side: open an `/i/` link's sealed blob (fetched from the
     * ledger by id) with the 12-byte secret from the URL fragment and
     * verify the capability inside. Does NOT check expiry.
     *
     * # Errors
     *
     * Throws an `Error` with `inviteLinkError: "damaged"` on any failure.
     * @param {Uint8Array} sealed
     * @param {Uint8Array} link_secret
     * @returns {InviteCapability}
     */
    static openLink(sealed, link_secret) {
        const ptr0 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(link_secret, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.invitecapability_openLink(ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return InviteCapability.__wrap(ret[0]);
    }
    /**
     * Inviter's `account_id` (16-byte UUIDv7).
     * @returns {Uint8Array}
     */
    get ownerAccountId() {
        const ret = wasm.invitecapability_ownerAccountId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Signal-style safety number derived from the owner's
     * Ed25519 identity key. Joiner UI shows it before
     * Accept so the human cross-checks the digits against
     * what the owner shared OOB. Defends against link-
     * substitution attacks; identical format + value to
     * `CallInviteCapability.ownerFingerprint` for the
     * same owner.
     * @returns {string}
     */
    get ownerFingerprint() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.invitecapability_ownerFingerprint(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Inviter's long-term Ed25519 verifying key (32 bytes).
     * @returns {Uint8Array}
     */
    get ownerSignaturePubkey() {
        const ret = wasm.invitecapability_ownerSignaturePubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Inviter's long-term X25519 public key (32 bytes).
     * @returns {Uint8Array}
     */
    get ownerX25519Pubkey() {
        const ret = wasm.invitecapability_ownerX25519Pubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The plaintext capability wire — what `computeJoinerInviteTag` takes.
     * @returns {Uint8Array}
     */
    get wire() {
        const ret = wasm.invitecapability_wire(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) InviteCapability.prototype[Symbol.dispose] = InviteCapability.prototype.free;

/**
 * What every invite compose returns (`/i/`, `/c/`, `/g/`): the sealed blob
 * to upload to the ledger (no inline form since 2026-10-19), the fresh
 * 12-byte link secret for the URL fragment only, and the
 * issuer's 32-byte Ed25519 key the upload carries in clear for the revoke.
 */
export class InviteLinkMint {
    static __wrap(ptr) {
        const obj = Object.create(InviteLinkMint.prototype);
        obj.__wbg_ptr = ptr;
        InviteLinkMintFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        InviteLinkMintFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_invitelinkmint_free(ptr, 0);
    }
    /**
     * The issuer's Ed25519 verifying key (32 bytes), for the upload body.
     * @returns {Uint8Array}
     */
    get issuerPubkey() {
        const ret = wasm.invitelinkmint_issuerPubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The link secret (12 bytes) for the URL fragment.
     * @returns {Uint8Array}
     */
    get linkSecret() {
        const ret = wasm.invitelinkmint_linkSecret(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The sealed link blob.
     * @returns {Uint8Array}
     */
    get sealed() {
        const ret = wasm.invitelinkmint_sealed(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) InviteLinkMint.prototype[Symbol.dispose] = InviteLinkMint.prototype.free;

/**
 * What `retainJoinRequest` did with a request read off a knock row
 * (ADR-0087 section 5).
 */
export class JoinRequest {
    static __wrap(ptr) {
        const obj = Object.create(JoinRequest.prototype);
        obj.__wbg_ptr = ptr;
        JoinRequestFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        JoinRequestFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_joinrequest_free(ptr, 0);
    }
    /**
     * The joiner's leaf signature key: the handle `approveExternalJoin` /
     * `rejectExternalJoin` take.
     * @returns {Uint8Array}
     */
    get joinerSignatureKey() {
        const ret = wasm.joinrequest_joinerSignatureKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `"retained"`, `"already-pending"` (an approve can act on both),
     * `"already-member"`, `"queue-full"` (stop the knock cursor before this
     * row) or `"not-admissible"` (drop the row).
     * @returns {string}
     */
    get outcome() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.joinrequest_outcome(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) JoinRequest.prototype[Symbol.dispose] = JoinRequest.prototype.free;

/**
 * The journal's epochs as this device has read them. Persist it with
 * `base`, `commitRowIds` and `commitEpochsAfter`; rebuild with `fromParts`.
 */
export class JournalEpochs {
    static __wrap(ptr) {
        const obj = Object.create(JournalEpochs.prototype);
        obj.__wbg_ptr = ptr;
        JournalEpochsFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        JournalEpochsFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_journalepochs_free(ptr, 0);
    }
    /**
     * The journal's epoch before the oldest remembered commit.
     * @returns {bigint}
     */
    get base() {
        const ret = wasm.journalepochs_base(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The epoch after each of those commits.
     * @returns {BigUint64Array}
     */
    commitEpochsAfter() {
        const ret = wasm.journalepochs_commitEpochsAfter(this.__wbg_ptr);
        var v1 = getArrayU64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * DS message ids of the effective commits, ascending.
     * @returns {BigUint64Array}
     */
    commitRowIds() {
        const ret = wasm.journalepochs_commitRowIds(this.__wbg_ptr);
        var v1 = getArrayU64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Rebuild a persisted record.
     *
     * # Errors
     *
     * `"bounds: ..."` when the two lists differ in length.
     * @param {bigint} base
     * @param {BigUint64Array} commit_row_ids
     * @param {BigUint64Array} commit_epochs_after
     * @returns {JournalEpochs}
     */
    static fromParts(base, commit_row_ids, commit_epochs_after) {
        const ptr0 = passArray64ToWasm0(commit_row_ids, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray64ToWasm0(commit_epochs_after, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.journalepochs_fromParts(base, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return JournalEpochs.__wrap(ret[0]);
    }
    /**
     * A record starting at `base` (the group's epoch when nothing is held).
     * @param {bigint} base
     */
    constructor(base) {
        const ret = wasm.journalepochs_new(base);
        this.__wbg_ptr = ret;
        JournalEpochsFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Take in a row after the device processed it (or skipped it as its
     * own). `true` when it was a commit that took effect (the record grew).
     * @param {bigint} row_id
     * @param {bigint} sealed_epoch
     * @param {bigint} epoch_after
     * @param {bigint} group_epoch_after
     * @returns {boolean}
     */
    observe(row_id, sealed_epoch, epoch_after, group_epoch_after) {
        const ret = wasm.journalepochs_observe(this.__wbg_ptr, row_id, sealed_epoch, epoch_after, group_epoch_after);
        return ret !== 0;
    }
    /**
     * Whether a claim or note row at `rowId`, sealed at `sealedEpoch`, is
     * readable by every device.
     * @param {bigint} row_id
     * @param {bigint} sealed_epoch
     * @returns {boolean}
     */
    rowCounts(row_id, sealed_epoch) {
        const ret = wasm.journalepochs_rowCounts(this.__wbg_ptr, row_id, sealed_epoch);
        return ret !== 0;
    }
}
if (Symbol.dispose) JournalEpochs.prototype[Symbol.dispose] = JournalEpochs.prototype.free;

/**
 * A decoded key-rotation envelope. **`newEntropy` is the new account**:
 * never log it, store it only under the device key, and adopt it only after
 * `verifyKeyRotationEnvelope` accepted the envelope AND the server-side
 * change is visible (ADR-0074 section 3.3).
 */
export class KeyRotation {
    static __wrap(ptr) {
        const obj = Object.create(KeyRotation.prototype);
        obj.__wbg_ptr = ptr;
        KeyRotationFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        KeyRotationFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_keyrotation_free(ptr, 0);
    }
    /**
     * The 16 bytes of BIP39 entropy of the NEW recovery phrase
     * (`RecoveryMnemonic.fromEntropy`).
     * @returns {Uint8Array}
     */
    get newEntropy() {
        const ret = wasm.keyrotation_newEntropy(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Rotating-device clock, UNIX milliseconds, as a `bigint`. Ages a
     * pending envelope out; orders and authorises nothing.
     * @returns {bigint}
     */
    get preparedAtMs() {
        const ret = wasm.keyrotation_preparedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The 145-byte supersession proof, old account key over new.
     * @returns {Uint8Array}
     */
    get supersessionProof() {
        const ret = wasm.keyrotation_supersessionProof(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) KeyRotation.prototype[Symbol.dispose] = KeyRotation.prototype.free;

/**
 * What a knock row carries, opened.
 */
export class KnockRequest {
    static __wrap(ptr) {
        const obj = Object.create(KnockRequest.prototype);
        obj.__wbg_ptr = ptr;
        KnockRequestFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        KnockRequestFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_knockrequest_free(ptr, 0);
    }
    /**
     * The plaintext `/g/` capability, when the joiner came by a link.
     * @returns {Uint8Array | undefined}
     */
    get capability() {
        const ret = wasm.knockrequest_capability(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The joiner's KeyPackage wire (`Group.retainJoinRequest` validates it).
     * @returns {Uint8Array}
     */
    get keyPackage() {
        const ret = wasm.knockrequest_keyPackage(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The joiner's profile (their claim), or `undefined`.
     * @returns {Profile | undefined}
     */
    get profile() {
        const ret = wasm.knockrequest_profile(this.__wbg_ptr);
        return ret === 0 ? undefined : Profile.__wrap(ret);
    }
    /**
     * The joiner's reply grant, class `reply`. SECRET.
     * @returns {ReachGrant}
     */
    get replyGrant() {
        const ret = wasm.knockrequest_replyGrant(this.__wbg_ptr);
        return ReachGrant.__wrap(ret);
    }
}
if (Symbol.dispose) KnockRequest.prototype[Symbol.dispose] = KnockRequest.prototype.free;

/**
 * A decoded knock-seed envelope; the seed stays sealed.
 */
export class KnockSeedEnvelope {
    static __wrap(ptr) {
        const obj = Object.create(KnockSeedEnvelope.prototype);
        obj.__wbg_ptr = ptr;
        KnockSeedEnvelopeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        KnockSeedEnvelopeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_knockseedenvelope_free(ptr, 0);
    }
    /**
     * The 32-byte journal id.
     * @returns {Uint8Array}
     */
    get journalId() {
        const ret = wasm.knockseedenvelope_journalId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 32-byte knock public key.
     * @returns {Uint8Array}
     */
    get knockPublicKey() {
        const ret = wasm.knockseedenvelope_knockPublicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The knock-key version.
     * @returns {number}
     */
    get knockVersion() {
        const ret = wasm.knockseedenvelope_knockVersion(this.__wbg_ptr);
        return ret;
    }
    /**
     * The addressee's 32-byte account key.
     * @returns {Uint8Array}
     */
    get memberAccountKey() {
        const ret = wasm.knockseedenvelope_memberAccountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) KnockSeedEnvelope.prototype[Symbol.dispose] = KnockSeedEnvelope.prototype.free;

/**
 * Everything the UI renders in one language.
 */
export class L10nCatalog {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        L10nCatalogFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_l10ncatalog_free(ptr, 0);
    }
    /**
     * Render a message with arguments. `texts` maps names to strings
     * (inserted with bidirectional isolation); `counts` maps names to whole
     * numbers that select plural forms. A count's display form, formatted by
     * `Intl.NumberFormat` for the locale, goes in `texts` under the same
     * name. `undefined` for an unknown key.
     * @param {string} key
     * @param {object} texts
     * @param {object} counts
     * @returns {string | undefined}
     */
    format(key, texts, counts) {
        const ptr0 = passStringToWasm0(key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.l10ncatalog_format(this.__wbg_ptr, ptr0, len0, texts, counts);
        let v2;
        if (ret[0] !== 0) {
            v2 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v2;
    }
    /**
     * The language code this catalog renders.
     * @returns {string}
     */
    get language() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.l10ncatalog_language(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Build the catalog for `language`; `built_in` is the compiled
     * translation (ignored for English).
     * @param {L10nSource} source
     * @param {string} language
     * @param {Uint8Array | null} [built_in]
     */
    constructor(source, language, built_in) {
        _assertClass(source, L10nSource);
        const ptr0 = passStringToWasm0(language, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(built_in) ? 0 : passArray8ToWasm0(built_in, wasm.__wbindgen_malloc);
        var len1 = WASM_VECTOR_LEN;
        const ret = wasm.l10ncatalog_new(source.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        L10nCatalogFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Every message without arguments, rendered, as a plain object keyed by
     * message key. Load once per language switch.
     * @returns {object}
     */
    plainStrings() {
        const ret = wasm.l10ncatalog_plainStrings(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Whether it is written right to left (sets `dir="rtl"`).
     * @returns {boolean}
     */
    get rtl() {
        const ret = wasm.l10ncatalog_rtl(this.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) L10nCatalog.prototype[Symbol.dispose] = L10nCatalog.prototype.free;

/**
 * The English source catalog, parsed once.
 */
export class L10nSource {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        L10nSourceFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_l10nsource_free(ptr, 0);
    }
    /**
     * Parse the source catalog (`source.json` from `lakko-l10n compile`).
     * @param {Uint8Array} source_json
     */
    constructor(source_json) {
        const ptr0 = passArray8ToWasm0(source_json, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.l10nsource_new(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        L10nSourceFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The catalog version compiled into this build.
     * @returns {bigint}
     */
    get version() {
        const ret = wasm.l10nsource_version(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) L10nSource.prototype[Symbol.dispose] = L10nSource.prototype.free;

/**
 * One supported language, for the language list.
 */
export class LanguageInfo {
    static __wrap(ptr) {
        const obj = Object.create(LanguageInfo.prototype);
        obj.__wbg_ptr = ptr;
        LanguageInfoFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        LanguageInfoFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_languageinfo_free(ptr, 0);
    }
    /**
     * Lakko's code; what the client stores as the choice.
     * @returns {string}
     */
    get code() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.languageinfo_code(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The English name (the row subtitle comes from the catalog key
     * `language.name.<code lowercased>`).
     * @returns {string}
     */
    get englishName() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.languageinfo_englishName(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The name in the language itself: the row title.
     * @returns {string}
     */
    get ownName() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.languageinfo_ownName(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Whether the language is written right to left.
     * @returns {boolean}
     */
    get rtl() {
        const ret = wasm.languageinfo_rtl(this.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) LanguageInfo.prototype[Symbol.dispose] = LanguageInfo.prototype.free;

/**
 * One remote tile's choice between the person's main video track and its
 * light companion: the tile never stands still while a lighter copy of the
 * picture is arriving.
 *
 * One per remote tile. The tile reports every presented frame of each track
 * and calls `tick` every {@link layerTickMs}, all on one millisecond clock
 * (a timestamp earlier than the latest one seen is read as the latest), and
 * shows what comes back. Timestamps are plain numbers, since this is called
 * once per video frame.
 */
export class LayerDisplay {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        LayerDisplayFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_layerdisplay_free(ptr, 0);
    }
    /**
     * Everything counted since the tile started, as
     * `[lightSwitches, lightMs, stallMaxMs, stallTotalMs]`.
     * @returns {Float64Array}
     */
    counters() {
        const ret = wasm.layerdisplay_counters(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Everything counted since the last drain, in the order of `counters`,
     * and start a new interval.
     * @returns {Float64Array}
     */
    drain() {
        const ret = wasm.layerdisplay_drain(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * The full track presented a frame at `nowMs`.
     * @param {number} now_ms
     * @returns {ShownLayer}
     */
    fullFrame(now_ms) {
        const ret = wasm.layerdisplay_fullFrame(this.__wbg_ptr, now_ms);
        return ret;
    }
    /**
     * The light track presented a frame at `nowMs`.
     * @param {number} now_ms
     * @returns {ShownLayer}
     */
    lightFrame(now_ms) {
        const ret = wasm.layerdisplay_lightFrame(this.__wbg_ptr, now_ms);
        return ret;
    }
    /**
     * A tile that started showing this person at `nowMs`; `lightAvailable`
     * is whether the person has a light track yet.
     * @param {number} now_ms
     * @param {boolean} light_available
     */
    constructor(now_ms, light_available) {
        const ret = wasm.layerdisplay_new(now_ms, light_available);
        this.__wbg_ptr = ret;
        LayerDisplayFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The tile could not observe frames until `nowMs` (a hidden page
     * presents none). Nothing is counted for that time and the tile starts
     * again on the full track, as a new tile would.
     * @param {number} now_ms
     * @returns {ShownLayer}
     */
    resync(now_ms) {
        const ret = wasm.layerdisplay_resync(this.__wbg_ptr, now_ms);
        return ret;
    }
    /**
     * The person's light track appeared or went away. Without one the tile
     * shows the full track, whatever it is doing.
     * @param {boolean} available
     * @param {number} now_ms
     * @returns {ShownLayer}
     */
    setLightAvailable(available, now_ms) {
        const ret = wasm.layerdisplay_setLightAvailable(this.__wbg_ptr, available, now_ms);
        return ret;
    }
    /**
     * What the tile shows.
     * @returns {ShownLayer}
     */
    shown() {
        const ret = wasm.layerdisplay_shown(this.__wbg_ptr);
        return ret;
    }
    /**
     * Time has passed with no frame: notices a stall without a frame
     * arriving.
     * @param {number} now_ms
     * @returns {ShownLayer}
     */
    tick(now_ms) {
        const ret = wasm.layerdisplay_tick(this.__wbg_ptr, now_ms);
        return ret;
    }
}
if (Symbol.dispose) LayerDisplay.prototype[Symbol.dispose] = LayerDisplay.prototype.free;

/**
 * One conversation-lineage register (ADR-0073 R.5): the group
 * `successorGroupId` continues the conversation `rootConversationId` (the
 * group id of the first group of the lineage).
 */
export class LineageRegister {
    static __wrap(ptr) {
        const obj = Object.create(LineageRegister.prototype);
        obj.__wbg_ptr = ptr;
        LineageRegisterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        LineageRegisterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_lineageregister_free(ptr, 0);
    }
    /**
     * HLC stamp the register was written at.
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.lineageregister_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The conversation id every person-facing store is keyed on.
     * @returns {Uint8Array}
     */
    get rootConversationId() {
        const ret = wasm.lineageregister_rootConversationId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * MLS group id of the group that continues the conversation.
     * @returns {Uint8Array}
     */
    get successorGroupId() {
        const ret = wasm.lineageregister_successorGroupId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) LineageRegister.prototype[Symbol.dispose] = LineageRegister.prototype.free;

/**
 * A decoded claim: the request it names.
 */
export class LinkClaim {
    static __wrap(ptr) {
        const obj = Object.create(LinkClaim.prototype);
        obj.__wbg_ptr = ptr;
        LinkClaimFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        LinkClaimFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_linkclaim_free(ptr, 0);
    }
    /**
     * The invite link's 16-byte id.
     * @returns {Uint8Array}
     */
    get linkId() {
        const ret = wasm.linkclaim_linkId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `linkId ‖ requesterAccountKey` (48 bytes): the key every client files
     * its claim bookkeeping under.
     * @returns {Uint8Array}
     */
    get requestKey() {
        const ret = wasm.linkclaim_requestKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The requester's 32-byte account key.
     * @returns {Uint8Array}
     */
    get requesterAccountKey() {
        const ret = wasm.linkclaim_requesterAccountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) LinkClaim.prototype[Symbol.dispose] = LinkClaim.prototype.free;

/**
 * How a request's claims end, for one device.
 */
export class LinkClaimDecision {
    static __wrap(ptr) {
        const obj = Object.create(LinkClaimDecision.prototype);
        obj.__wbg_ptr = ptr;
        LinkClaimDecisionFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        LinkClaimDecisionFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_linkclaimdecision_free(ptr, 0);
    }
    /**
     * Another device's claim wins: build nothing.
     * @returns {boolean}
     */
    get lost() {
        const ret = wasm.linkclaimdecision_lost(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The DS message id of the winning claim; `0n` when there is none.
     * @returns {bigint}
     */
    get messageId() {
        const ret = wasm.linkclaimdecision_messageId(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * No claim from a device in the current roster: post (again) later.
     * @returns {boolean}
     */
    get noWinner() {
        const ret = wasm.linkclaimdecision_noWinner(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * This device's claim wins: it answers the request.
     * @returns {boolean}
     */
    get won() {
        const ret = wasm.linkclaimdecision_won(this.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) LinkClaimDecision.prototype[Symbol.dispose] = LinkClaimDecision.prototype.free;

/**
 * A decoded note: the request, the founded one-to-one and its Welcome.
 */
export class LinkFounded {
    static __wrap(ptr) {
        const obj = Object.create(LinkFounded.prototype);
        obj.__wbg_ptr = ptr;
        LinkFoundedFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        LinkFoundedFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_linkfounded_free(ptr, 0);
    }
    /**
     * The founded one-to-one's 32-byte group id.
     * @returns {Uint8Array}
     */
    get groupId() {
        const ret = wasm.linkfounded_groupId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The invite link's 16-byte id.
     * @returns {Uint8Array}
     */
    get linkId() {
        const ret = wasm.linkfounded_linkId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `linkId ‖ requesterAccountKey` (48 bytes).
     * @returns {Uint8Array}
     */
    get requestKey() {
        const ret = wasm.linkfounded_requestKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The requester's 32-byte account key.
     * @returns {Uint8Array}
     */
    get requesterAccountKey() {
        const ret = wasm.linkfounded_requesterAccountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The founding Welcome (wire bytes).
     * @returns {Uint8Array}
     */
    get welcome() {
        const ret = wasm.linkfounded_welcome(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) LinkFounded.prototype[Symbol.dispose] = LinkFounded.prototype.free;

/**
 * One of the account's invite links (ADR-0079 section 4). The link secret
 * is SECRET: never log it.
 */
export class LinkRecord {
    static __wrap(ptr) {
        const obj = Object.create(LinkRecord.prototype);
        obj.__wbg_ptr = ptr;
        LinkRecordFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        LinkRecordFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_linkrecord_free(ptr, 0);
    }
    /**
     * The link's terms may still be changed: not revoked, never copied,
     * never answered (the core's one rule).
     * @returns {boolean}
     */
    get editable() {
        const ret = wasm.linkrecord_editable(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * UNIX millis after which the link no longer works; 0 = never.
     * @returns {bigint}
     */
    get expiryMs() {
        const ret = wasm.linkrecord_expiryMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * HLC stamp the register was written at (0 for a record not yet
     * written).
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.linkrecord_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * `true` when the link still works at `nowMs`.
     * @param {bigint} now_ms
     * @returns {boolean}
     */
    isLive(now_ms) {
        const ret = wasm.linkrecord_isLive(this.__wbg_ptr, now_ms);
        return ret !== 0;
    }
    /**
     * Which ledger.
     * @returns {LinkRecordKind}
     */
    get kind() {
        const ret = wasm.linkrecord_kind(this.__wbg_ptr);
        return ret;
    }
    /**
     * The owner's label, or empty.
     * @returns {string}
     */
    get label() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.linkrecord_label(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The 6-byte ledger id.
     * @returns {Uint8Array}
     */
    get ledgerId() {
        const ret = wasm.linkrecord_ledgerId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The link's 16-byte id.
     * @returns {Uint8Array}
     */
    get linkId() {
        const ret = wasm.linkrecord_linkId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The link's 12-byte secret. SECRET.
     * @returns {Uint8Array}
     */
    get linkSecret() {
        const ret = wasm.linkrecord_linkSecret(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The use limit as the capability carries it.
     * @returns {number}
     */
    get maxUses() {
        const ret = wasm.linkrecord_maxUses(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * UNIX millis at which the link was minted.
     * @returns {bigint}
     */
    get mintedAtMs() {
        const ret = wasm.linkrecord_mintedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * A record for a link just minted: no use yet, not revoked. `linkId`
     * 16 bytes, `linkSecret` 12, `ledgerId` 6 (every link has a ledger row;
     * the inline form was removed on 2026-10-19), `revocationSeed` 32
     * (ADR-0078 section 10, it
     * lets any device of the account that revokes the link delete the row),
     * `maxUses` as the capability carries it (0 = single use,
     * `0xFFFFFFFF` = no limit), `expiryMs` 0 = never.
     *
     * # Errors
     *
     * Throws `"bounds: ..."` for a field out of bounds.
     * @param {Uint8Array} link_id
     * @param {Uint8Array} link_secret
     * @param {LinkRecordKind} kind
     * @param {Uint8Array} ledger_id
     * @param {Uint8Array} revocation_seed
     * @param {bigint} expiry_ms
     * @param {number} max_uses
     * @param {bigint} minted_at_ms
     * @param {string} label
     */
    constructor(link_id, link_secret, kind, ledger_id, revocation_seed, expiry_ms, max_uses, minted_at_ms, label) {
        const ptr0 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(link_secret, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(ledger_id, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArray8ToWasm0(revocation_seed, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        const ptr4 = passStringToWasm0(label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len4 = WASM_VECTOR_LEN;
        const ret = wasm.linkrecord_new(ptr0, len0, ptr1, len1, kind, ptr2, len2, ptr3, len3, expiry_ms, max_uses, minted_at_ms, ptr4, len4);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        LinkRecordFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The 32-byte seed that deletes the ledger row. SECRET.
     * @returns {Uint8Array}
     */
    get revocationSeed() {
        const ret = wasm.linkrecord_revocationSeed(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Revoked (final).
     * @returns {boolean}
     */
    get revoked() {
        const ret = wasm.linkrecord_revoked(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Copied on one of the account's devices (final, 2026-10-26).
     * @returns {boolean}
     */
    get shared() {
        const ret = wasm.linkrecord_shared(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Requests accepted through the link so far.
     * @returns {number}
     */
    get uses() {
        const ret = wasm.linkrecord_uses(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) LinkRecord.prototype[Symbol.dispose] = LinkRecord.prototype.free;

/**
 * Which ledger an invite link lives in (ADR-0079).
 * @enum {1 | 2}
 */
export const LinkRecordKind = Object.freeze({
    /**
     * A contact invite link (`/i/`).
     */
    Contact: 1, "1": "Contact",
    /**
     * A call invite link (`/c/`).
     */
    Call: 2, "2": "Call",
});

/**
 * One stored message ready for the UI list. Mirror of
 * [`lakko_storage::LocalMessage`].
 */
export class LocalMessage {
    static __wrap(ptr) {
        const obj = Object.create(LocalMessage.prototype);
        obj.__wbg_ptr = ptr;
        LocalMessageFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        LocalMessageFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_localmessage_free(ptr, 0);
    }
    /**
     * Raw body bytes.
     * @returns {Uint8Array}
     */
    get body() {
        const ret = wasm.localmessage_body(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Server-assigned creation timestamp (milliseconds since UNIX
     * epoch).
     * @returns {bigint}
     */
    get createdAtMs() {
        const ret = wasm.localmessage_createdAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Body kind.
     * @returns {MessageKind}
     */
    get kind() {
        const ret = wasm.localmessage_kind(this.__wbg_ptr);
        return ret;
    }
    /**
     * 16-byte message id.
     * @returns {Uint8Array}
     */
    get msgId() {
        const ret = wasm.localmessage_msgId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Construct a fresh `LocalMessage`. `msgId` must be 16 bytes,
     * `senderPseudonym` must be 32 bytes.
     * @param {Uint8Array} msg_id
     * @param {bigint} created_at_ms
     * @param {Uint8Array} sender_pseudonym
     * @param {MessageKind} kind
     * @param {Uint8Array} body
     */
    constructor(msg_id, created_at_ms, sender_pseudonym, kind, body) {
        const ptr0 = passArray8ToWasm0(msg_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(sender_pseudonym, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(body, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.localmessage_new(ptr0, len0, created_at_ms, ptr1, len1, kind, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        LocalMessageFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * 32-byte sender pseudonym within the group.
     * @returns {Uint8Array}
     */
    get senderPseudonym() {
        const ret = wasm.localmessage_senderPseudonym(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) LocalMessage.prototype[Symbol.dispose] = LocalMessage.prototype.free;

/**
 * The account's own mailbox key. Holds the secret; never leaves the device.
 */
export class MailboxKey {
    static __wrap(ptr) {
        const obj = Object.create(MailboxKey.prototype);
        obj.__wbg_ptr = ptr;
        MailboxKeyFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        MailboxKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_mailboxkey_free(ptr, 0);
    }
    /**
     * The 32-byte address the mailbox is stored under on the Delivery
     * Service (`/ds/mailbox/{hex}/...`).
     * @returns {Uint8Array}
     */
    address() {
        const ret = wasm.mailboxkey_address(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The mailbox key of the account whose identity is `accountIdentity`
     * (derived from its 32-byte account seed; every device derives the
     * same).
     * @param {Identity} account_identity
     * @returns {MailboxKey}
     */
    static fromAccountIdentity(account_identity) {
        _assertClass(account_identity, Identity);
        const ret = wasm.mailboxkey_fromAccountIdentity(account_identity.__wbg_ptr);
        return MailboxKey.__wrap(ret);
    }
    /**
     * The 32-byte Ed25519 public key the claim registers.
     * @returns {Uint8Array}
     */
    publicKey() {
        const ret = wasm.mailboxkey_publicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) MailboxKey.prototype[Symbol.dispose] = MailboxKey.prototype.free;

/**
 * What a mailbox row carries (ADR-0078). The numeric values are the frozen
 * wire bytes.
 * @enum {1 | 2 | 3 | 4 | 5}
 */
export const MailboxKind = Object.freeze({
    /**
     * A signed `ContactRequest`.
     */
    ContactRequest: 1, "1": "ContactRequest",
    /**
     * An MLS Welcome.
     */
    Welcome: 2, "2": "Welcome",
    /**
     * A signed `ContactAck`.
     */
    ContactAck: 3, "3": "ContactAck",
    /**
     * A Welcome rendezvous (the channel co-publisher join).
     */
    WelcomeRendezvous: 4, "4": "WelcomeRendezvous",
    /**
     * A signed link refusal (ADR-0079, 2026-10-19).
     */
    LinkRefusal: 5, "5": "LinkRefusal",
});

/**
 * The codec of a frame, as far as the clear prefix of a sealed media frame
 * depends on it (ADR-0071). Mirrors [`lakko_calls::MediaCodec`]; the values
 * are frozen.
 * @enum {0 | 1 | 2 | 3}
 */
export const MediaCodec = Object.freeze({
    /**
     * Any codec without a rule, and a codec the sender could not determine:
     * nothing is left in the clear.
     */
    Other: 0, "0": "Other",
    /**
     * VP8 video: 10 clear bytes of a key frame, 3 of any other frame.
     */
    Vp8: 1, "1": "Vp8",
    /**
     * Opus audio: nothing is left in the clear.
     */
    Opus: 2, "2": "Opus",
    /**
     * An RFC 2198 RED container of Opus frames (`audio/red`): nothing is
     * left in the clear, and the trailer says the frame holds RED.
     */
    RedOpus: 3, "3": "RedOpus",
});

/**
 * What [`parse_media_frame`] reads off a sealed media frame.
 */
export class MediaFrameInfo {
    static __wrap(ptr) {
        const obj = Object.create(MediaFrameInfo.prototype);
        obj.__wbg_ptr = ptr;
        MediaFrameInfoFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        MediaFrameInfoFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_mediaframeinfo_free(ptr, 0);
    }
    /**
     * How many bytes at the start of the frame are in the clear.
     * @returns {number}
     */
    get clearPrefixLen() {
        const ret = wasm.mediaframeinfo_clearPrefixLen(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The 64-bit frame counter (a `bigint` in JS).
     * @returns {bigint}
     */
    get counter() {
        const ret = wasm.mediaframeinfo_counter(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The trailer says the frame holds an RFC 2198 RED container of Opus.
     * Authenticated once the frame opens (the trailer is additional data).
     * @returns {boolean}
     */
    get isRedOpus() {
        const ret = wasm.mediaframeinfo_isRedOpus(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The whole SFrame KID `(sender_leaf << 8) | stream_index` (ADR-0035), a
     * `bigint` in JS.
     * @returns {bigint}
     */
    get kid() {
        const ret = wasm.mediaframeinfo_kid(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) MediaFrameInfo.prototype[Symbol.dispose] = MediaFrameInfo.prototype.free;

/**
 * Body kind for a stored message. Mirror of
 * [`lakko_storage::MessageKind`].
 * @enum {0 | 1}
 */
export const MessageKind = Object.freeze({
    /**
     * UTF-8 text body.
     */
    Text: 0, "0": "Text",
    /**
     * JSON-encoded attachment manifest.
     */
    Attachment: 1, "1": "Attachment",
});

/**
 * Per-group message log facade. Mirror of [`lakko_storage::MessageStore`].
 */
export class MessageStore {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        MessageStoreFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_messagestore_free(ptr, 0);
    }
    /**
     * Append a message to a group's local log.
     * @param {Uint8Array} group_id
     * @param {LocalMessage} msg
     */
    append(group_id, msg) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        _assertClass(msg, LocalMessage);
        const ret = wasm.messagestore_append(this.__wbg_ptr, ptr0, len0, msg.__wbg_ptr);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * How many messages stored locally for this group.
     * @param {Uint8Array} group_id
     * @returns {number}
     */
    count(group_id) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.messagestore_count(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * Return messages strictly newer than `afterCreatedAtMs`,
     * ordered ascending. Pass `0` to fetch the whole log.
     * @param {Uint8Array} group_id
     * @param {bigint} after_created_at_ms
     * @param {number} limit
     * @returns {any}
     */
    listSince(group_id, after_created_at_ms, limit) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.messagestore_listSince(this.__wbg_ptr, ptr0, len0, after_created_at_ms, limit);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Wrap an [`EncryptedStore`].
     * @param {EncryptedStore} encrypted
     */
    constructor(encrypted) {
        _assertClass(encrypted, EncryptedStore);
        const ret = wasm.messagestore_new(encrypted.__wbg_ptr);
        this.__wbg_ptr = ret;
        MessageStoreFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) MessageStore.prototype[Symbol.dispose] = MessageStore.prototype.free;

/**
 * One run of a message's text: `docs/ux/message-links.md`.
 */
export class MessageTextPiece {
    static __wrap(ptr) {
        const obj = Object.create(MessageTextPiece.prototype);
        obj.__wbg_ptr = ptr;
        MessageTextPieceFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        MessageTextPieceFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_messagetextpiece_free(ptr, 0);
    }
    /**
     * Whether this run is a link to open. The address is the text.
     * @returns {boolean}
     */
    get isLink() {
        const ret = wasm.messagetextpiece_isLink(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The text as sent.
     * @returns {string}
     */
    get text() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.messagetextpiece_text(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) MessageTextPiece.prototype[Symbol.dispose] = MessageTextPiece.prototype.free;

/**
 * Client-side OPAQUE login session — JS handle. Mirror of
 * [`OpaqueRegister`].
 *
 * On a successful `finish` call, the returned
 * [`OpaqueLoginOutput`] carries:
 *
 *   * `finalizeB64` — go-ahead bytes for `/login/finish`;
 *   * `sessionKey` — symmetric channel key shared with the
 *      server (use to bind a follow-up session token);
 *   * `exportKey` — deterministic seed for the device root key
 *      (matches the export key produced at registration time
 *      for the same secret).
 *
 * JS usage:
 *
 * ```js
 * import { OpaqueLogin } from "@lakko/ffi-wasm";
 *
 * const login = OpaqueLogin.startPassword(passwordBytes);
 * const requestB64 = login.requestB64;
 * // POST /login/start { account_id, request: requestB64, … pow }
 * // → server returns { session_id, response }.
 * const out = login.finish(passwordBytes, response);
 * // POST /login/finish { session_id, finalize: out.finalizeB64 }
 * ```
 */
export class OpaqueLogin {
    static __wrap(ptr) {
        const obj = Object.create(OpaqueLogin.prototype);
        obj.__wbg_ptr = ptr;
        OpaqueLoginFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpaqueLoginFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_opaquelogin_free(ptr, 0);
    }
    /**
     * Finalize the login.
     *
     * Single-use, same rationale as
     * [`OpaqueRegister::finish`]. A wrong-password attempt
     * returns the same error as an unknown-account attempt — by
     * design (OPAQUE indistinguishable failure).
     * @param {Uint8Array} secret
     * @param {string} server_response_b64
     * @returns {OpaqueLoginOutput}
     */
    finish(secret, server_response_b64) {
        const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(server_response_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.opaquelogin_finish(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OpaqueLoginOutput.__wrap(ret[0]);
    }
    /**
     * `true` iff this session was started via
     * [`Self::start_password`].
     * @returns {boolean}
     */
    get isPassword() {
        const ret = wasm.opaquelogin_isPassword(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * `true` iff this session was started via
     * [`Self::start_recovery`].
     * @returns {boolean}
     */
    get isRecovery() {
        const ret = wasm.opaquelogin_isRecovery(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Base64-encoded `CredentialRequest` for the `/login/start`
     * body.
     * @returns {string}
     */
    get requestB64() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.opaquelogin_requestB64(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Begin a password login.
     * @param {Uint8Array} password
     * @returns {OpaqueLogin}
     */
    static startPassword(password) {
        const ptr0 = passArray8ToWasm0(password, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.opaquelogin_startPassword(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OpaqueLogin.__wrap(ret[0]);
    }
    /**
     * Begin a recovery login.
     * @param {Uint8Array} seed
     * @returns {OpaqueLogin}
     */
    static startRecovery(seed) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.opaquelogin_startRecovery(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OpaqueLogin.__wrap(ret[0]);
    }
}
if (Symbol.dispose) OpaqueLogin.prototype[Symbol.dispose] = OpaqueLogin.prototype.free;

/**
 * Output of [`OpaqueLogin::finish`].
 */
export class OpaqueLoginOutput {
    static __wrap(ptr) {
        const obj = Object.create(OpaqueLoginOutput.prototype);
        obj.__wbg_ptr = ptr;
        OpaqueLoginOutputFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpaqueLoginOutputFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_opaqueloginoutput_free(ptr, 0);
    }
    /**
     * OPAQUE export key bytes. Same provenance as
     * [`OpaqueRegisterOutput::export_key`] — deterministic for
     * a given password.
     * @returns {Uint8Array}
     */
    get exportKey() {
        const ret = wasm.opaqueloginoutput_exportKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Base64-encoded `CredentialFinalization` for the
     * `/login/finish` body.
     * @returns {string}
     */
    get finalizeB64() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.opaqueloginoutput_finalizeB64(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * OPAQUE session key bytes. 64 bytes for `LakkoCipherSuite`.
     * Symmetric key shared with the server — use to bind a
     * follow-up session token, **not** to encrypt user data.
     * @returns {Uint8Array}
     */
    get sessionKey() {
        const ret = wasm.opaqueloginoutput_sessionKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) OpaqueLoginOutput.prototype[Symbol.dispose] = OpaqueLoginOutput.prototype.free;

/**
 * Client-side OPAQUE registration session — JS handle.
 *
 * JS usage:
 *
 * ```js
 * import { OpaqueRegister } from "@lakko/ffi-wasm";
 *
 * // Phase 1: produce the registration request.
 * const reg = OpaqueRegister.startPassword(passwordBytes);
 * const passwordRequestB64 = reg.requestB64;
 *
 * // POST { account_id, password_request: passwordRequestB64, … pow }
 * // → server returns { password_response: "..." }.
 *
 * // Phase 2: finalize once the response arrives.
 * const out = reg.finish(passwordBytes, serverResponseB64);
 * // out.uploadB64    — goes into POST /signup/finish
 * // out.exportKey    — Uint8Array(64), seed for the device root key
 * ```
 */
export class OpaqueRegister {
    static __wrap(ptr) {
        const obj = Object.create(OpaqueRegister.prototype);
        obj.__wbg_ptr = ptr;
        OpaqueRegisterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpaqueRegisterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_opaqueregister_free(ptr, 0);
    }
    /**
     * Finalize the registration with the server's
     * `RegistrationResponse`.
     *
     * Single-use: a second call to `finish` on the same instance
     * returns an error rather than producing a second envelope.
     * `secret` MUST be byte-equal to the bytes passed into
     * `startPassword` / `startRecovery`.
     * @param {Uint8Array} secret
     * @param {string} server_response_b64
     * @returns {OpaqueRegisterOutput}
     */
    finish(secret, server_response_b64) {
        const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(server_response_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.opaqueregister_finish(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OpaqueRegisterOutput.__wrap(ret[0]);
    }
    /**
     * `true` iff this session was started via
     * [`Self::start_password`].
     * @returns {boolean}
     */
    get isPassword() {
        const ret = wasm.opaqueregister_isPassword(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * `true` iff this session was started via
     * [`Self::start_recovery`].
     * @returns {boolean}
     */
    get isRecovery() {
        const ret = wasm.opaqueregister_isRecovery(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Base64-encoded `RegistrationRequest`. Goes into
     * `password_request` (or `recovery_request`) on
     * `/signup/start`.
     *
     * Cheap to read — returns a copy of the cached string.
     * @returns {string}
     */
    get requestB64() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.opaqueregister_requestB64(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Begin a password registration. Equivalent to the Rust call
     * [`RegistrationSession::start_password`].
     *
     * `password` is the raw byte sequence the user typed
     * (UTF-8-encoded by the JS caller). It is **not** sent to
     * the server — OPAQUE blinds it through the OPRF.
     * @param {Uint8Array} password
     * @returns {OpaqueRegister}
     */
    static startPassword(password) {
        const ptr0 = passArray8ToWasm0(password, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.opaqueregister_startPassword(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OpaqueRegister.__wrap(ret[0]);
    }
    /**
     * Begin a recovery registration. Driven by the 64-byte BIP39
     * seed in the real flow; the FFI is byte-agnostic.
     * @param {Uint8Array} seed
     * @returns {OpaqueRegister}
     */
    static startRecovery(seed) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.opaqueregister_startRecovery(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OpaqueRegister.__wrap(ret[0]);
    }
}
if (Symbol.dispose) OpaqueRegister.prototype[Symbol.dispose] = OpaqueRegister.prototype.free;

/**
 * Output of [`OpaqueRegister::finish`].
 *
 * `exportKey` is the OPAQUE export key. Apps store it (or, more
 * commonly, a key derived from it) in non-extractable WebCrypto
 * — never in plain `localStorage`.
 */
export class OpaqueRegisterOutput {
    static __wrap(ptr) {
        const obj = Object.create(OpaqueRegisterOutput.prototype);
        obj.__wbg_ptr = ptr;
        OpaqueRegisterOutputFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpaqueRegisterOutputFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_opaqueregisteroutput_free(ptr, 0);
    }
    /**
     * OPAQUE export key bytes. 64 bytes for `LakkoCipherSuite`
     * (Ristretto255-SHA512). Each access returns a fresh copy —
     * the caller is expected to consume / wipe the result
     * promptly.
     * @returns {Uint8Array}
     */
    get exportKey() {
        const ret = wasm.opaqueregisteroutput_exportKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Base64-encoded `RegistrationUpload` for the `/signup/finish`
     * body.
     * @returns {string}
     */
    get uploadB64() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.opaqueregisteroutput_uploadB64(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) OpaqueRegisterOutput.prototype[Symbol.dispose] = OpaqueRegisterOutput.prototype.free;

/**
 * A channel invitation card opened (ADR-0087 section 14).
 */
export class OpenedChannelInviteCard {
    static __wrap(ptr) {
        const obj = Object.create(OpenedChannelInviteCard.prototype);
        obj.__wbg_ptr = ptr;
        OpenedChannelInviteCardFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedChannelInviteCardFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedchannelinvitecard_free(ptr, 0);
    }
    /**
     * The verified plaintext capability wire.
     * @returns {Uint8Array}
     */
    get capabilityWire() {
        const ret = wasm.openedchannelinvitecard_capabilityWire(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 6-byte ledger id (consume it on Subscribe).
     * @returns {Uint8Array}
     */
    get ledgerId() {
        const ret = wasm.openedchannelinvitecard_ledgerId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 12-byte link secret. SECRET.
     * @returns {Uint8Array}
     */
    get linkSecret() {
        const ret = wasm.openedchannelinvitecard_linkSecret(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The preview: always a subscriber invitation.
     * @returns {GroupInvitePreview}
     */
    get preview() {
        const ret = wasm.openedchannelinvitecard_preview(this.__wbg_ptr);
        return GroupInvitePreview.__wrap(ret);
    }
    /**
     * The sealed `/g/` blob.
     * @returns {Uint8Array}
     */
    get sealed() {
        const ret = wasm.openedchannelinvitecard_sealed(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) OpenedChannelInviteCard.prototype[Symbol.dispose] = OpenedChannelInviteCard.prototype.free;

/**
 * A comment opened and its author signature verified.
 */
export class OpenedComment {
    static __wrap(ptr) {
        const obj = Object.create(OpenedComment.prototype);
        obj.__wbg_ptr = ptr;
        OpenedCommentFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedCommentFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedcomment_free(ptr, 0);
    }
    /**
     * The author's 32-byte per-channel key: what a ban list names.
     * @returns {Uint8Array}
     */
    get authorKey() {
        const ret = wasm.openedcomment_authorKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The author's picture, or `undefined`.
     * @returns {AvatarRef | undefined}
     */
    get avatar() {
        const ret = wasm.openedcomment_avatar(this.__wbg_ptr);
        return ret === 0 ? undefined : AvatarRef.__wrap(ret);
    }
    /**
     * The name the author typed.
     * @returns {string}
     */
    get name() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.openedcomment_name(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The server cursor of the comment this one answers, or `undefined`.
     * @returns {bigint | undefined}
     */
    get replyTo() {
        const ret = wasm.openedcomment_replyTo(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
    }
    /**
     * The comment's text.
     * @returns {string}
     */
    get text() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.openedcomment_text(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) OpenedComment.prototype[Symbol.dispose] = OpenedComment.prototype.free;

/**
 * Result of `ContactRequest.openEnvelope` (ADR-0048 §2).
 */
export class OpenedContactRequest {
    static __wrap(ptr) {
        const obj = Object.create(OpenedContactRequest.prototype);
        obj.__wbg_ptr = ptr;
        OpenedContactRequestFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedContactRequestFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedcontactrequest_free(ptr, 0);
    }
    /**
     * The intro message bytes, possibly empty. May itself be a call
     * envelope — classify with `dispatchIncomingEnvelope` as before.
     * @returns {Uint8Array}
     */
    get introMessage() {
        const ret = wasm.openedcontactrequest_introMessage(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 16-byte id of the `/i/` link the request answers (ADR-0079 §2),
     * or `undefined`. Ask `SyncedState.linkAnswerVerdict` with it, the
     * secret and the request's `fromPubkey`.
     * @returns {Uint8Array | undefined}
     */
    get linkAnswerId() {
        const ret = wasm.openedcontactrequest_linkAnswerId(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The 12-byte secret of that link, or `undefined`. SECRET: never log.
     * @returns {Uint8Array | undefined}
     */
    get linkAnswerSecret() {
        const ret = wasm.openedcontactrequest_linkAnswerSecret(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The sender's profile, or `undefined` when the request carried none
     * or carried one that failed its bounds.
     * @returns {Profile | undefined}
     */
    get profile() {
        const ret = wasm.openedcontactrequest_profile(this.__wbg_ptr);
        return ret === 0 ? undefined : Profile.__wrap(ret);
    }
    /**
     * The requester's `reply` reach grant (ADR-0060 §6), or `undefined`
     * when it sent none. SECRET — store encrypted, never log. Sign the
     * accept (the one-to-one Welcome, or a call Welcome) and the ack back to
     * the requester's mailbox with `DsProofSigner.forReach(replyGrant.seed)`.
     * Always class `Reply`; a grant of any other class fails the open.
     * @returns {ReachGrant | undefined}
     */
    get replyGrant() {
        const ret = wasm.openedcontactrequest_replyGrant(this.__wbg_ptr);
        return ret === 0 ? undefined : ReachGrant.__wrap(ret);
    }
}
if (Symbol.dispose) OpenedContactRequest.prototype[Symbol.dispose] = OpenedContactRequest.prototype.free;

/**
 * One attachment after the decode path — flat getters.
 */
export class OpenedHistoryAttachment {
    static __wrap(ptr) {
        const obj = Object.create(OpenedHistoryAttachment.prototype);
        obj.__wbg_ptr = ptr;
        OpenedHistoryAttachmentFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedHistoryAttachmentFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedhistoryattachment_free(ptr, 0);
    }
    /**
     * @returns {Uint8Array}
     */
    get blobId() {
        const ret = wasm.openedhistoryattachment_blobId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {number | undefined}
     */
    get durationMs() {
        const ret = wasm.openedhistoryattachment_durationMs(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * @returns {number | undefined}
     */
    get height() {
        const ret = wasm.openedhistoryattachment_height(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * @returns {Uint8Array}
     */
    get key() {
        const ret = wasm.openedhistoryattachment_key(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {string | undefined}
     */
    get kind() {
        const ret = wasm.openedhistoryattachment_kind(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * @returns {string | undefined}
     */
    get mime() {
        const ret = wasm.openedhistoryattachment_mime(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * @returns {string | undefined}
     */
    get name() {
        const ret = wasm.openedhistoryattachment_name(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * @returns {Uint8Array}
     */
    get nonce() {
        const ret = wasm.openedhistoryattachment_nonce(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {bigint}
     */
    get size() {
        const ret = wasm.openedhistoryattachment_size(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * @returns {OpenedHistoryThumb | undefined}
     */
    get thumb() {
        const ret = wasm.openedhistoryattachment_thumb(this.__wbg_ptr);
        return ret === 0 ? undefined : OpenedHistoryThumb.__wrap(ret);
    }
    /**
     * Raw waveform amplitude bytes.
     * @returns {Uint8Array | undefined}
     */
    get waveform() {
        const ret = wasm.openedhistoryattachment_waveform(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * @returns {number | undefined}
     */
    get width() {
        const ret = wasm.openedhistoryattachment_width(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
}
if (Symbol.dispose) OpenedHistoryAttachment.prototype[Symbol.dispose] = OpenedHistoryAttachment.prototype.free;

/**
 * Top-level result of [`openHistoryBundleStructured`].
 * wasm-bindgen class — JS reads `.groups()` and `.maxHlc`.
 */
export class OpenedHistoryBundle {
    static __wrap(ptr) {
        const obj = Object.create(OpenedHistoryBundle.prototype);
        obj.__wbg_ptr = ptr;
        OpenedHistoryBundleFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedHistoryBundleFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedhistorybundle_free(ptr, 0);
    }
    /**
     * Flat array of [`OpenedHistoryGroup`]. Same JS-iter
     * pattern as `OpenedHistoryGroup.messages()`.
     * @returns {any[]}
     */
    groups() {
        const ret = wasm.openedhistorybundle_groups(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * HLC of the most recent message included in the bundle.
     * @returns {bigint}
     */
    get maxHlc() {
        const ret = wasm.openedhistorybundle_maxHlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) OpenedHistoryBundle.prototype[Symbol.dispose] = OpenedHistoryBundle.prototype.free;

/**
 * One group's worth of opened history rows for the receive
 * path. wasm-bindgen class — JS reads `.groupId`, `.messages()`,
 * `.countCapped`.
 */
export class OpenedHistoryGroup {
    static __wrap(ptr) {
        const obj = Object.create(OpenedHistoryGroup.prototype);
        obj.__wbg_ptr = ptr;
        OpenedHistoryGroupFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedHistoryGroupFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedhistorygroup_free(ptr, 0);
    }
    /**
     * `true` when the per-group message list was truncated
     * to the producer-side cap. UI surfaces a "load older"
     * affordance later.
     * @returns {boolean}
     */
    get countCapped() {
        const ret = wasm.openedhistorygroup_countCapped(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * MLS group_id this group's history rows belong to.
     * @returns {Uint8Array}
     */
    get groupId() {
        const ret = wasm.openedhistorygroup_groupId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Flat array of [`OpenedHistoryMessage`] — JS iterates via
     * `Array.from(g.messages())`. wasm-bindgen owns the
     * inner allocations; the caller must not retain references
     * past the parent `OpenedHistoryGroup`'s lifetime.
     * @returns {any[]}
     */
    messages() {
        const ret = wasm.openedhistorygroup_messages(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
}
if (Symbol.dispose) OpenedHistoryGroup.prototype[Symbol.dispose] = OpenedHistoryGroup.prototype.free;

/**
 * One message after the open path — flat getters for the JS
 * caller. wasm-bindgen doesn't expose tuple-style returns, so
 * each opened message is a class instance.
 */
export class OpenedHistoryMessage {
    static __wrap(ptr) {
        const obj = Object.create(OpenedHistoryMessage.prototype);
        obj.__wbg_ptr = ptr;
        OpenedHistoryMessageFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedHistoryMessageFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedhistorymessage_free(ptr, 0);
    }
    /**
     * @returns {Uint8Array}
     */
    get messageId() {
        const ret = wasm.openedhistorymessage_messageId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {Uint8Array}
     */
    get payloadBytes() {
        const ret = wasm.openedhistorymessage_payloadBytes(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {bigint}
     */
    get receivedAtMs() {
        const ret = wasm.openedhistorymessage_receivedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * @returns {number}
     */
    get senderLeaf() {
        const ret = wasm.openedhistorymessage_senderLeaf(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) OpenedHistoryMessage.prototype[Symbol.dispose] = OpenedHistoryMessage.prototype.free;

/**
 * A decoded placeholder — flat getters for the JS caller.
 * `direction` is 0 = outgoing, 1 = incoming, 2 = system.
 */
export class OpenedHistoryPlaceholder {
    static __wrap(ptr) {
        const obj = Object.create(OpenedHistoryPlaceholder.prototype);
        obj.__wbg_ptr = ptr;
        OpenedHistoryPlaceholderFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedHistoryPlaceholderFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedhistoryplaceholder_free(ptr, 0);
    }
    /**
     * `Array<OpenedHistoryAttachment>`.
     * @returns {OpenedHistoryAttachment[]}
     */
    get attachments() {
        const ret = wasm.openedhistoryplaceholder_attachments(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * @returns {string}
     */
    get createdAt() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.openedhistoryplaceholder_createdAt(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * @returns {number}
     */
    get direction() {
        const ret = wasm.openedhistoryplaceholder_direction(this.__wbg_ptr);
        return ret;
    }
    /**
     * @returns {bigint}
     */
    get messageId() {
        const ret = wasm.openedhistoryplaceholder_messageId(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * @returns {string}
     */
    get text() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.openedhistoryplaceholder_text(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) OpenedHistoryPlaceholder.prototype[Symbol.dispose] = OpenedHistoryPlaceholder.prototype.free;

/**
 * A decoded poster frame (ADR-0047) — flat getters.
 */
export class OpenedHistoryThumb {
    static __wrap(ptr) {
        const obj = Object.create(OpenedHistoryThumb.prototype);
        obj.__wbg_ptr = ptr;
        OpenedHistoryThumbFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedHistoryThumbFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedhistorythumb_free(ptr, 0);
    }
    /**
     * @returns {Uint8Array}
     */
    get blobId() {
        const ret = wasm.openedhistorythumb_blobId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {number | undefined}
     */
    get height() {
        const ret = wasm.openedhistorythumb_height(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * @returns {Uint8Array}
     */
    get key() {
        const ret = wasm.openedhistorythumb_key(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {string | undefined}
     */
    get mime() {
        const ret = wasm.openedhistorythumb_mime(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * @returns {Uint8Array}
     */
    get nonce() {
        const ret = wasm.openedhistorythumb_nonce(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * @returns {bigint}
     */
    get size() {
        const ret = wasm.openedhistorythumb_size(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * @returns {number | undefined}
     */
    get width() {
        const ret = wasm.openedhistorythumb_width(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
}
if (Symbol.dispose) OpenedHistoryThumb.prototype[Symbol.dispose] = OpenedHistoryThumb.prototype.free;

/**
 * An opened mailbox row.
 */
export class OpenedMailboxRow {
    static __wrap(ptr) {
        const obj = Object.create(OpenedMailboxRow.prototype);
        obj.__wbg_ptr = ptr;
        OpenedMailboxRowFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedMailboxRowFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedmailboxrow_free(ptr, 0);
    }
    /**
     * The inner wire (a request, an ack, a Welcome, a rendezvous),
     * unchanged.
     * @returns {Uint8Array}
     */
    get body() {
        const ret = wasm.openedmailboxrow_body(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * What the row carries.
     * @returns {MailboxKind}
     */
    get kind() {
        const ret = wasm.openedmailboxrow_kind(this.__wbg_ptr);
        return ret;
    }
}
if (Symbol.dispose) OpenedMailboxRow.prototype[Symbol.dispose] = OpenedMailboxRow.prototype.free;

/**
 * A member card opened and its account binding verified.
 */
export class OpenedMemberCard {
    static __wrap(ptr) {
        const obj = Object.create(OpenedMemberCard.prototype);
        obj.__wbg_ptr = ptr;
        OpenedMemberCardFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OpenedMemberCardFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_openedmembercard_free(ptr, 0);
    }
    /**
     * The 32-byte account identity public key that signed the card: file
     * the profile and any grant under this account.
     * @returns {Uint8Array}
     */
    get identityPk() {
        const ret = wasm.openedmembercard_identityPk(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The profile envelope; decode it with `decodeProfileEnvelope`.
     * @returns {Uint8Array}
     */
    get profile() {
        const ret = wasm.openedmembercard_profile(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) OpenedMemberCard.prototype[Symbol.dispose] = OpenedMemberCard.prototype.free;

/**
 * Client-side OPRF blinder — JS handle.
 *
 * JS usage:
 *
 * ```js
 * import { OprfBlinder } from "@lakko/ffi-wasm";
 *
 * // 1. Blind the user-typed handle.
 * const blinder = OprfBlinder.blind("@Alice");
 *
 * // 2. Round-trip through the server.
 * const { evaluated } = await fetch("/api/discovery/oprf", {
 *   method: "POST",
 *   headers: { "content-type": "application/json" },
 *   body: JSON.stringify({ pow, blinded: blinder.blindedB64 }),
 * }).then((r) => r.json());
 *
 * // 3. Finalize: lookupKey goes to /discovery/{register, lookup};
 * //    output never leaves the device.
 * const done = blinder.finalize(evaluated);
 * const oprfHash = done.lookupKey; // Uint8Array(64)
 * ```
 */
export class OprfBlinder {
    static __wrap(ptr) {
        const obj = Object.create(OprfBlinder.prototype);
        obj.__wbg_ptr = ptr;
        OprfBlinderFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OprfBlinderFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_oprfblinder_free(ptr, 0);
    }
    /**
     * Normalise the typed username and produce a fresh blinded
     * element. The `blindedB64` getter then exposes the base64
     * string for the `/discovery/oprf` request body.
     *
     * # Errors
     *
     * Throws an `Error` with a `usernameError` code property when the
     * name fails the ADR-0048 §4 rule (empty after normalisation
     * included), or a plain `Error` if the underlying OPRF blinding fails
     * (extremely unlikely — would indicate a broken RNG).
     * @param {string} username
     * @returns {OprfBlinder}
     */
    static blind(username) {
        const ptr0 = passStringToWasm0(username, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.oprfblinder_blind(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OprfBlinder.__wrap(ret[0]);
    }
    /**
     * Blind the one term a typed name query looks up (`nameQueryTerm` of
     * `query`). Filter what the lookup returns with `nameMatchesQuery`.
     *
     * # Errors
     *
     * Throws when the query is not searchable, or on an OPRF blinding
     * failure.
     * @param {string} query
     * @returns {OprfBlinder}
     */
    static blindName(query) {
        const ptr0 = passStringToWasm0(query, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.oprfblinder_blindName(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OprfBlinder.__wrap(ret[0]);
    }
    /**
     * Blind one name term (ADR-0060 §5) in the name OPRF namespace, apart
     * from `@name`s. A publisher blinds each of
     * `nameSearchTerms`; `normalizedUsername` returns the term.
     *
     * # Errors
     *
     * Throws when `term` is not a name term (`isNameTerm`), or on an OPRF
     * blinding failure.
     * @param {string} term
     * @returns {OprfBlinder}
     */
    static blindNameTerm(term) {
        const ptr0 = passStringToWasm0(term, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.oprfblinder_blindNameTerm(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OprfBlinder.__wrap(ret[0]);
    }
    /**
     * Base64-encoded blinded element — the `blinded` field of
     * `/discovery/oprf`'s request body.
     * @returns {string}
     */
    get blindedB64() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.oprfblinder_blindedB64(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Finalise the OPRF dance with the server's evaluation. Returns the
     * OPRF output and its lookup key; only `lookupKey` goes to the server.
     *
     * Single-use — a second call returns an error.
     * @param {string} evaluated_b64
     * @returns {OprfResult}
     */
    finalize(evaluated_b64) {
        const ptr0 = passStringToWasm0(evaluated_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.oprfblinder_finalize(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return OprfResult.__wrap(ret[0]);
    }
    /**
     * The normalised form of the typed username. Surface in the
     * UI ("Looking up @alice…") so the user sees what's actually
     * being queried.
     * @returns {string}
     */
    get normalizedUsername() {
        let deferred2_0;
        let deferred2_1;
        try {
            const ret = wasm.oprfblinder_normalizedUsername(this.__wbg_ptr);
            var ptr1 = ret[0];
            var len1 = ret[1];
            if (ret[3]) {
                ptr1 = 0; len1 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred2_0 = ptr1;
            deferred2_1 = len1;
            return getStringFromWasm0(ptr1, len1);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
}
if (Symbol.dispose) OprfBlinder.prototype[Symbol.dispose] = OprfBlinder.prototype.free;

/**
 * What one OPRF round yields (ADR-0060 section 5). `lookupKey` is the
 * `oprf_hash` of every discovery request and what a username certificate
 * binds; `output` never leaves the device, because public and introduction
 * records are sealed under keys derived from it.
 */
export class OprfResult {
    static __wrap(ptr) {
        const obj = Object.create(OprfResult.prototype);
        obj.__wbg_ptr = ptr;
        OprfResultFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OprfResultFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_oprfresult_free(ptr, 0);
    }
    /**
     * The 64-byte lookup key derived from `output`.
     * @returns {Uint8Array}
     */
    get lookupKey() {
        const ret = wasm.oprfresult_lookupKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 64-byte OPRF output. Never send it to the server.
     * @returns {Uint8Array}
     */
    get output() {
        const ret = wasm.oprfresult_output(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) OprfResult.prototype[Symbol.dispose] = OprfResult.prototype.free;

/**
 * What becomes of the outgoing owner when ownership is transferred (ADR-0022's
 * deferred follow-up, built in ADR-0045). Mirror of UniFFI's `OutgoingOwner` /
 * [`lakko_mls::OutgoingOwner`].
 *
 * **This is a required argument, never a default.** The core deliberately
 * forces the caller to state it, because either silent choice is wrong in one
 * direction: silently keeping admin leaves an unwanted administrator behind in
 * a group the outgoing owner meant to hand over and walk away from, and
 * silently dropping to plain member strips them, in one irreversible commit,
 * of every power they had in a group they created.
 *
 * **The discriminants deliberately start at 1, and that is what carries the
 * "never a default" property across the JS boundary.** A missing or
 * `undefined` argument reaches the wasm ABI as the integer `0`, which is not a
 * declared variant, so wasm-bindgen's generated `FromWasmAbi` throws
 * `"invalid enum value passed"` rather than silently selecting the first
 * variant. Had `Admin` been `0` — the Rust default — omitting the argument
 * would have silently meant `Admin`. `outgoing_owner_variants_are_never_zero`
 * pins this.
 *
 * A UI should still **pre-select** [`OutgoingOwner::Admin`] — not because it
 * preserves a way back (it does not: a role change is owner-only, so a
 * transfer cannot be undone by the person who made it) but because it is the
 * recoverable side of an unrecoverable action.
 * @enum {1 | 2}
 */
export const OutgoingOwner = Object.freeze({
    /**
     * Keep the outgoing owner in the admin set. The variant to pre-select.
     */
    Admin: 1, "1": "Admin",
    /**
     * Demote the outgoing owner to a plain member.
     */
    Member: 2, "2": "Member",
});

/**
 * The account's own-username register as held (ADR-0076 section 3), a
 * stamped "none" included.
 */
export class OwnUsernameRegister {
    static __wrap(ptr) {
        const obj = Object.create(OwnUsernameRegister.prototype);
        obj.__wbg_ptr = ptr;
        OwnUsernameRegisterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        OwnUsernameRegisterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_ownusernameregister_free(ptr, 0);
    }
    /**
     * HLC stamp the register was written at.
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.ownusernameregister_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The canonical username, or `undefined` when the register records
     * that the account has no username.
     * @returns {string | undefined}
     */
    get username() {
        const ret = wasm.ownusernameregister_username(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
}
if (Symbol.dispose) OwnUsernameRegister.prototype[Symbol.dispose] = OwnUsernameRegister.prototype.free;

/**
 * Structured view of a parsed backup blob — the wasm projection of
 * `lakko_backup::BackupBlob`. Returned by [`parse_backup_blob`].
 */
export class ParsedBackupBlob {
    static __wrap(ptr) {
        const obj = Object.create(ParsedBackupBlob.prototype);
        obj.__wbg_ptr = ptr;
        ParsedBackupBlobFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ParsedBackupBlobFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_parsedbackupblob_free(ptr, 0);
    }
    /**
     * The envelope portion (the whole blob when `framed` is `false`; the tail
     * after the tag + head when `framed` is `true`). Feed to [`open_backup`].
     * @returns {Uint8Array}
     */
    get envelope() {
        const ret = wasm.parsedbackupblob_envelope(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `true` when the blob carried the `0xB1` container tag and a full head;
     * `false` for a legacy raw envelope with no head.
     * @returns {boolean}
     */
    get framed() {
        const ret = wasm.parsedbackupblob_framed(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The raw backup-head bytes when `framed` is `true`, else `undefined`.
     * Still **unverified** — feed to [`verify_backup_head`] against the pinned
     * auth attest key.
     * @returns {Uint8Array | undefined}
     */
    get head() {
        const ret = wasm.parsedbackupblob_head(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
}
if (Symbol.dispose) ParsedBackupBlob.prototype[Symbol.dispose] = ParsedBackupBlob.prototype.free;

/**
 * Structured view of a decoded backup payload. Built on the
 * receive side by [`parse_backup_payload`] so the web client
 * can pull out the fields it needs to restore.
 */
export class ParsedBackupPayload {
    static __wrap(ptr) {
        const obj = Object.create(ParsedBackupPayload.prototype);
        obj.__wbg_ptr = ptr;
        ParsedBackupPayloadFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ParsedBackupPayloadFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_parsedbackuppayload_free(ptr, 0);
    }
    /**
     * Raw 16-byte UUIDv7 account_id from the payload.
     * @returns {Uint8Array}
     */
    get accountId() {
        const ret = wasm.parsedbackuppayload_accountId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Accepted-contacts list as a JS array of `BackupContact`.
     * @returns {BackupContact[]}
     */
    get contacts() {
        const ret = wasm.parsedbackuppayload_contacts(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Wall-clock UNIX ms recorded at backup-creation time.
     * @returns {bigint}
     */
    get createdAtMs() {
        const ret = wasm.parsedbackuppayload_createdAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Raw 32-byte identity signing seed.
     * @returns {Uint8Array}
     */
    get ed25519Seed() {
        const ret = wasm.parsedbackuppayload_ed25519Seed(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The MLS `group_id`s of the conversations the account's devices knew
     * when the backup was taken, flat-concatenated 32-byte ids. Empty for
     * an account that knew none.
     *
     * Names and nothing more: the backup carries no MLS state, so a
     * restored device learns from these which conversations exist before
     * any member has admitted it. Membership comes from the other members
     * (ADR-0050 section 2.5a), never from this list.
     * @returns {Uint8Array}
     */
    get knownGroupIds() {
        const ret = wasm.parsedbackuppayload_knownGroupIds(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * When the account adopted the key that replaced each predecessor, unix
     * millis, in the order of `ownPredecessorSeeds`.
     * @returns {BigUint64Array}
     */
    get ownPredecessorAdoptedAtMs() {
        const ret = wasm.parsedbackuppayload_ownPredecessorAdoptedAtMs(this.__wbg_ptr);
        var v1 = getArrayU64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * The account's own predecessor seeds, oldest first, flat-concatenated
     * 64-byte seeds (ADR-0074 section 3.7). Empty for an account that never
     * rotated. Never log them.
     * @returns {Uint8Array}
     */
    get ownPredecessorSeeds() {
        const ret = wasm.parsedbackuppayload_ownPredecessorSeeds(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The account's own profile envelope; empty when none was set.
     * @returns {Uint8Array}
     */
    get ownProfile() {
        const ret = wasm.parsedbackuppayload_ownProfile(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The other people's profile envelopes this device held, sorted by key.
     * Restore each one through the ordinary profile receive gate under its
     * `personKey` (the strictly-newer-stamp rule), never by writing it over
     * what the device holds, so a stale backup cannot replace a newer name.
     * @returns {BackupReceivedProfile[]}
     */
    get receivedProfiles() {
        const ret = wasm.parsedbackuppayload_receivedProfiles(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The ADR-0058 folded synced state, as `SyncedState.toBytes()` wrote
     * it; restore with `SyncedState.fromBytes(...)`. Empty = none held.
     * @returns {Uint8Array}
     */
    get syncedState() {
        const ret = wasm.parsedbackuppayload_syncedState(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The account's own "who can find me" envelope; empty when none was
     * chosen.
     * @returns {Uint8Array}
     */
    get visibility() {
        const ret = wasm.parsedbackuppayload_visibility(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ParsedBackupPayload.prototype[Symbol.dispose] = ParsedBackupPayload.prototype.free;

/**
 * Output of [`parse_body_envelope`] (ADR-0023). `kind` selects
 * which fields are meaningful: `text` + attachments for `"chat"`
 * (and `text` for `"edit"`); `target` for every op; `emoji` for
 * `"reaction"`. JS reads `target` as a `BigInt` (the DS `messageId`),
 * matching the `AttachmentRef.size` precedent.
 */
export class ParsedBody {
    static __wrap(ptr) {
        const obj = Object.create(ParsedBody.prototype);
        obj.__wbg_ptr = ptr;
        ParsedBodyFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ParsedBodyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_parsedbody_free(ptr, 0);
    }
    /**
     * Pull one attachment by index (chat only).
     *
     * # Errors
     *
     * `RangeError` on out-of-bounds `i`.
     * @param {number} i
     * @returns {AttachmentRef}
     */
    attachment(i) {
        const ret = wasm.parsedbody_attachment(this.__wbg_ptr, i);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AttachmentRef.__wrap(ret[0]);
    }
    /**
     * Number of attachments (chat only; `0` otherwise).
     * @returns {number}
     */
    get attachmentCount() {
        const ret = wasm.parsedbody_attachmentCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * ADR-0087 section 14: a channel invitation card beside the text, set
     * only on `kind == "chat"`. Open it with `openChannelInviteCard`.
     * @returns {Uint8Array | undefined}
     */
    get channelInvite() {
        const ret = wasm.parsedbody_channelInvite(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The reaction emoji, or `undefined` unless `kind == "reaction"`.
     * @returns {string | undefined}
     */
    get emoji() {
        const ret = wasm.parsedbody_emoji(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * ADR-0052 — base64 of the origin conversation's group id, set only
     * together with `forwardOriginMessageId`; for a local jump by a recipient
     * who is also in that conversation.
     * @returns {string | undefined}
     */
    get forwardOriginGroupB64() {
        const ret = wasm.parsedbody_forwardOriginGroupB64(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * ADR-0052 — the DS `messageId` of the original (a `bigint`).
     * @returns {bigint | undefined}
     */
    get forwardOriginMessageId() {
        const ret = wasm.parsedbody_forwardOriginMessageId(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : ret[1];
    }
    /**
     * ADR-0052 — on a forwarded message, the ORIGINAL author's display name
     * as a snapshot: a label, never a verified identity or a route to them.
     * @returns {string | undefined}
     */
    get forwardedFrom() {
        const ret = wasm.parsedbody_forwardedFrom(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The typing state, or `undefined` unless `kind == "typing"` (ADR-0024).
     * @returns {boolean | undefined}
     */
    get isTyping() {
        const ret = wasm.parsedbody_isTyping(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret !== 0;
    }
    /**
     * `"chat" | "edit" | "delete" | "reaction" | "typing" | "read-receipt" |
     * "service-envelope" | "unsupported"`. `"service-envelope"` is never
     * rendered; `"unsupported"` (ADR-0061 section 6) is a chat envelope from
     * a newer wire version, rendered with the client's own catalog sentence
     * (its `text` is empty — no core prose).
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedbody_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * UX reactions-and-emoji section 17: `"add"` or `"remove"`, set only
     * on `kind == "reaction"`.
     * @returns {string | undefined}
     */
    get reactionAction() {
        const ret = wasm.parsedbody_reactionAction(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * UX reactions-and-emoji section 17: the 32-byte account key of the
     * reacted-to message's author as the reactor saw it, or `undefined`.
     * The reactor's claim, never an identity.
     * @returns {Uint8Array | undefined}
     */
    get reactionTargetAuthor() {
        const ret = wasm.parsedbody_reactionTargetAuthor(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * ADR-0052 — the quoted author's name as the replier wrote it: a
     * snapshot and a courtesy label, never a verified identity.
     * @returns {string | undefined}
     */
    get replyAuthor() {
        const ret = wasm.parsedbody_replyAuthor(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * ADR-0052 — an excerpt of the quoted message's own text, one level
     * deep, unchanged by later edits or deletion of the original.
     * @returns {string | undefined}
     */
    get replyExcerpt() {
        const ret = wasm.parsedbody_replyExcerpt(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * ADR-0052 — the DS `messageId` this message replies to, or `undefined`
     * (a `bigint`, like `target`). Only ever set on `kind == "chat"`.
     * @returns {bigint | undefined}
     */
    get replyTo() {
        const ret = wasm.parsedbody_replyTo(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : ret[1];
    }
    /**
     * ADR-0073 — on a message re-sent into a successor one-to-one, base64
     * of the ORIGINAL conversation's group id; set only together with
     * `resendOriginMessageId`. Never shown: it lets a device that already
     * holds the original show the message once.
     * @returns {string | undefined}
     */
    get resendOriginGroupB64() {
        const ret = wasm.parsedbody_resendOriginGroupB64(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * ADR-0073 — the DS `messageId` of the original in that conversation
     * (a `bigint`).
     * @returns {bigint | undefined}
     */
    get resendOriginMessageId() {
        const ret = wasm.parsedbody_resendOriginMessageId(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : ret[1];
    }
    /**
     * The DS `messageId` the op acts on, or `undefined` for `"chat"`. For
     * `"unsupported"` it carries the envelope's wire version, as on UniFFI.
     * @returns {bigint | undefined}
     */
    get target() {
        const ret = wasm.parsedbody_target(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : ret[1];
    }
    /**
     * Chat / edit text (empty for delete / reaction).
     * @returns {string}
     */
    get text() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedbody_text(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * ADR-0061 section 6 — the wire version of an `"unsupported"` envelope,
     * or `undefined` for every other kind.
     * @returns {number | undefined}
     */
    get unsupportedVersion() {
        const ret = wasm.parsedbody_unsupportedVersion(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret;
    }
}
if (Symbol.dispose) ParsedBody.prototype[Symbol.dispose] = ParsedBody.prototype.free;

/**
 * Parsed call-JWT claims — flat class with getters.
 */
export class ParsedCallJwt {
    static __wrap(ptr) {
        const obj = Object.create(ParsedCallJwt.prototype);
        obj.__wbg_ptr = ptr;
        ParsedCallJwtFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ParsedCallJwtFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_parsedcalljwt_free(ptr, 0);
    }
    /**
     * `aud` claim — always `"lakko-sfu"`.
     * @returns {string}
     */
    get aud() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcalljwt_aud(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * `exp` — expires-at, unix seconds.
     * @returns {bigint}
     */
    get exp() {
        const ret = wasm.parsedcalljwt_exp(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * `iat` — issued-at, unix seconds.
     * @returns {bigint}
     */
    get iat() {
        const ret = wasm.parsedcalljwt_iat(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * `iss` claim — always `"lakko-auth"` on a well-formed JWT.
     * @returns {string}
     */
    get iss() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcalljwt_iss(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * JWS header `kid` — 16 hex chars.
     * @returns {string}
     */
    get kid() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcalljwt_kid(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The SFU peer id the token admits as: 32-char lower-case hex.
     * @returns {string}
     */
    get peerId() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcalljwt_peerId(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * One of `"participant"` / `"publisher"` / `"subscriber"`.
     * @returns {string}
     */
    get role() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcalljwt_role(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The room the token admits to: 64-char lower-case hex (ADR-0078
     * section 2; never the call id, never an account).
     * @returns {string}
     */
    get room() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcalljwt_room(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) ParsedCallJwt.prototype[Symbol.dispose] = ParsedCallJwt.prototype.free;

/**
 * One composed call-signaling event — flat class with a
 * `kind` discriminator + every possible field, mirroring
 * the `ParsedDeviceGroupPayload` shape from 2a3e2a.
 */
export class ParsedCallSignaling {
    static __wrap(ptr) {
        const obj = Object.create(ParsedCallSignaling.prototype);
        obj.__wbg_ptr = ptr;
        ParsedCallSignalingFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ParsedCallSignalingFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_parsedcallsignaling_free(ptr, 0);
    }
    /**
     * 16-byte call_id (every kind carries one).
     * @returns {Uint8Array}
     */
    get callId() {
        const ret = wasm.parsedcallsignaling_callId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `iceCandidate` only: ICE candidate SDP fragment.
     * @returns {string}
     */
    get candidate() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcallsignaling_candidate(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * `offer` only: wall-clock UNIX millis.
     * @returns {bigint}
     */
    get createdAtMs() {
        const ret = wasm.parsedcallsignaling_createdAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * `end` / `groupLeave` only: one of `"hungUp"` /
     * `"busy"` / `"declined"` / `"timedOut"` / `"error"` /
     * `"endedForAll"` (`end` of a group or link call only: the call's
     * starter ended it for everyone; act on it only when the
     * MLS-authenticated sender is that starter).
     * For `groupLeave` only `"hungUp"` / `"timedOut"` /
     * `"error"` are meaningful.
     * @returns {string}
     */
    get endReason() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcallsignaling_endReason(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * `groupStart` only: MLS leaf index of the initiator.
     * @returns {number}
     */
    get initiatorLeaf() {
        const ret = wasm.parsedcallsignaling_initiatorLeaf(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * `groupJoin` only: wall-clock UNIX millis when the
     * join landed on the chat journal.
     * @returns {bigint}
     */
    get joinedAtMs() {
        const ret = wasm.parsedcallsignaling_joinedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * One of `"offer"` / `"answer"` / `"iceCandidate"` /
     * `"end"`. JS reads this to decide which other fields
     * are meaningful.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcallsignaling_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * `groupJoin` / `groupLeave` only: MLS leaf index of
     * the joining / leaving participant.
     * @returns {number}
     */
    get leafIndex() {
        const ret = wasm.parsedcallsignaling_leafIndex(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * `offer` only: audio media flag.
     * @returns {boolean}
     */
    get mediaAudio() {
        const ret = wasm.parsedcallsignaling_mediaAudio(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `offer` only: video media flag.
     * @returns {boolean}
     */
    get mediaVideo() {
        const ret = wasm.parsedcallsignaling_mediaVideo(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `screenShare` only: the signed share state (ADR-0083); verify it
     * with `verifyCallScreenShare` against the MLS sender's leaf.
     * @returns {Uint8Array}
     */
    get screenShareState() {
        const ret = wasm.parsedcallsignaling_screenShareState(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `offer` / `answer` only: SDP body.
     * @returns {string}
     */
    get sdp() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcallsignaling_sdp(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * `iceCandidate` only: zero-based SDP m-section index.
     * @returns {number}
     */
    get sdpMLineIndex() {
        const ret = wasm.parsedcallsignaling_sdpMLineIndex(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * `iceCandidate` only: SDP m-section identifier.
     * @returns {string}
     */
    get sdpMid() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedcallsignaling_sdpMid(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) ParsedCallSignaling.prototype[Symbol.dispose] = ParsedCallSignaling.prototype.free;

/**
 * JS-side projection of
 * [`lakko_mls::device_group::ParsedDeviceGroupPayload`]
 * (Phase 2a3e2). One flat class with a `kind` discriminator +
 * every possible field as a typed getter — keeps the
 * wasm-bindgen surface simple at the cost of some
 * always-present fields on every variant.
 *
 * Each field carries a trailing `_val` to disambiguate from
 * the same-named wasm-bindgen getter; this is mechanically
 * required by wasm-bindgen and outside our control.
 */
export class ParsedDeviceGroupPayload {
    static __wrap(ptr) {
        const obj = Object.create(ParsedDeviceGroupPayload.prototype);
        obj.__wbg_ptr = ptr;
        ParsedDeviceGroupPayloadFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ParsedDeviceGroupPayloadFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_parseddevicegrouppayload_free(ptr, 0);
    }
    /**
     * `flags` only: archived indicator.
     * @returns {boolean}
     */
    get archived() {
        const ret = wasm.parseddevicegrouppayload_archived(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Conversation group_id. Set for `readCursor`, `draft`,
     * and `flags`; empty for `order`.
     * @returns {Uint8Array}
     */
    get groupId() {
        const ret = wasm.parseddevicegrouppayload_groupId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * HLC timestamp from the envelope.
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.parseddevicegrouppayload_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * One of `"readCursor"` / `"draft"` / `"flags"` /
     * `"order"` / `"folderChanges"` / `"stateSnapshot"` /
     * `"serviceEnvelope"`. JS reads this to decide which other fields are
     * meaningful. `folderChanges` / `stateSnapshot` carry no flat fields:
     * fold them with `SyncedState.applyPayload`.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parseddevicegrouppayload_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * `flags` only: muted indicator.
     * @returns {boolean}
     */
    get muted() {
        const ret = wasm.parseddevicegrouppayload_muted(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `order` only: conversation group_ids in display order,
     * top-to-bottom. Empty for non-`order` kinds.
     * @returns {Uint8Array[]}
     */
    orderGroupIds() {
        const ret = wasm.parseddevicegrouppayload_orderGroupIds(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * `flags` only: pinned indicator.
     * @returns {boolean}
     */
    get pinned() {
        const ret = wasm.parseddevicegrouppayload_pinned(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `readCursor` only: wall-clock UNIX millis when the read
     * happened (display only).
     * @returns {bigint}
     */
    get readAtMs() {
        const ret = wasm.parseddevicegrouppayload_readAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * `draft` only: the message-id the draft is replying to,
     * or empty.
     * @returns {Uint8Array}
     */
    get replyToId() {
        const ret = wasm.parseddevicegrouppayload_replyToId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Leaf index of the device that authored this payload.
     * @returns {number}
     */
    get senderLeaf() {
        const ret = wasm.parseddevicegrouppayload_senderLeaf(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * `serviceEnvelope` only: the `lakko-contacts` envelope bytes,
     * uninterpreted (ADR-0060 section 7). Hand them to
     * `dispatchIncomingEnvelope` and keep only Visibility / ReachState.
     * Empty for other kinds.
     * @returns {Uint8Array}
     */
    get serviceEnvelope() {
        const ret = wasm.parseddevicegrouppayload_serviceEnvelope(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `stateSnapshot` only: the 0-based part index (else 0).
     * @returns {number}
     */
    get snapshotPart() {
        const ret = wasm.parseddevicegrouppayload_snapshotPart(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * `stateSnapshot` only: the number of parts (else 0).
     * @returns {number}
     */
    get snapshotParts() {
        const ret = wasm.parseddevicegrouppayload_snapshotParts(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * `draft` only: composer text. May be empty (clear).
     * @returns {string}
     */
    get text() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parseddevicegrouppayload_text(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * `flags` only: manual unread-override indicator.
     * @returns {boolean}
     */
    get unreadOverride() {
        const ret = wasm.parseddevicegrouppayload_unreadOverride(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `readCursor` only: the message-id the sender claims to
     * have read up to.
     * @returns {Uint8Array}
     */
    get upToMessageId() {
        const ret = wasm.parseddevicegrouppayload_upToMessageId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ParsedDeviceGroupPayload.prototype[Symbol.dispose] = ParsedDeviceGroupPayload.prototype.free;

/**
 * Output of [`parse_envelope`]. Carries the user-visible text +
 * zero or more [`AttachmentRef`]s.
 */
export class ParsedEnvelope {
    static __wrap(ptr) {
        const obj = Object.create(ParsedEnvelope.prototype);
        obj.__wbg_ptr = ptr;
        ParsedEnvelopeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ParsedEnvelopeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_parsedenvelope_free(ptr, 0);
    }
    /**
     * Pull one attachment by index. wasm-bindgen does not export
     * `Vec<T>` of non-`Copy` `T` over the boundary directly, so
     * we expose an index-based accessor — JS iterates `for (let
     * i = 0; i < env.attachmentCount; i++) env.attachment(i)`.
     *
     * # Errors
     *
     * `RangeError` on out-of-bounds `i`.
     * @param {number} i
     * @returns {AttachmentRef}
     */
    attachment(i) {
        const ret = wasm.parsedenvelope_attachment(this.__wbg_ptr, i);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AttachmentRef.__wrap(ret[0]);
    }
    /**
     * Number of attachments — exposed as a getter so JS can size
     * arrays / progress indicators before iterating.
     * @returns {number}
     */
    get attachmentCount() {
        const ret = wasm.parsedenvelope_attachmentCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The user-visible text. Empty string is valid (attachment-
     * only message).
     * @returns {string}
     */
    get text() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.parsedenvelope_text(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) ParsedEnvelope.prototype[Symbol.dispose] = ParsedEnvelope.prototype.free;

/**
 * Per-participant AEAD key + per-participant nonce salt
 * + per-participant frame counter. Sealing increments
 * the counter under a `Mutex` so the transformer's
 * codec-thread call is safe without external
 * serialisation.
 */
export class ParticipantKey {
    static __wrap(ptr) {
        const obj = Object.create(ParticipantKey.prototype);
        obj.__wbg_ptr = ptr;
        ParticipantKeyFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ParticipantKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_participantkey_free(ptr, 0);
    }
    /**
     * Current 64-bit frame counter value (a `bigint` in JS): the counter of
     * the last frame sealed, or the key's random start (ADR-0069) when none
     * was. Diagnostics only.
     * @returns {bigint}
     */
    counter() {
        const ret = wasm.participantkey_counter(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Open `ciphertext` using the 64-bit `counter` (a
     * `bigint` in JS) from the SFrame header.
     *
     * # Errors
     *
     * `JsError` on AEAD authentication failure (wrong
     * key / wrong AAD / tampered ciphertext / wrong
     * counter).
     * @param {Uint8Array} aad
     * @param {Uint8Array} ciphertext
     * @param {bigint} counter
     * @returns {Uint8Array}
     */
    open(aad, ciphertext, counter) {
        const ptr0 = passArray8ToWasm0(aad, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(ciphertext, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.participantkey_open(this.__wbg_ptr, ptr0, len0, ptr1, len1, counter);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v3;
    }
    /**
     * Open a complete RFC 9605 wire frame produced by
     * [`Self::seal_framed`] (or any ADR-0013-conformant peer):
     * parse the header, bind the on-wire header bytes as AAD,
     * AEAD-open. This key must be derived for the frame's sender
     * leaf (the worker selects it by the header's KID). The
     * incoming-side path for the web worker.
     *
     * # Errors
     *
     * `JsError` on a malformed header, a KID that doesn't match
     * this key's leaf, or AEAD auth failure. Any 64-bit counter
     * the header carries is accepted (ADR-0069).
     * @param {Uint8Array} wire
     * @returns {Uint8Array}
     */
    openFramed(wire) {
        const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.participantkey_openFramed(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Open a frame in the ADR-0071 layout and return the encoded frame
     * `clear_prefix || plaintext`. This key must be the one derived for the
     * frame's KID. A frame in the header-first layout is refused.
     *
     * # Errors
     *
     * `JsError` on a malformed frame, a KID that is not this key's, or an
     * AEAD authentication failure.
     * @param {Uint8Array} wire
     * @returns {Uint8Array}
     */
    openMediaFrame(wire) {
        const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.participantkey_openMediaFrame(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Seal `plaintext` under the next counter value.
     * Returns [`WasmSealedFrame { counter, ciphertext }`].
     *
     * # Errors
     *
     * `JsError` containing `CounterExhausted` when the
     * 64-bit counter stands at its last value (ADR-0069);
     * nothing is sealed and the caller derives a new key
     * before continuing.
     * @param {Uint8Array} aad
     * @param {Uint8Array} plaintext
     * @returns {SealedFrame}
     */
    seal(aad, plaintext) {
        const ptr0 = passArray8ToWasm0(aad, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.participantkey_seal(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return SealedFrame.__wrap(ret[0]);
    }
    /**
     * Seal `plaintext` into a complete RFC 9605 wire frame —
     * `SframeHeader(kid, ctr) || ciphertext` — with the SFrame
     * header bound as the AEAD AAD, per ADR-0013. This is the
     * path the web `RTCRtpScriptTransform` worker uses on the
     * outgoing side; the resulting bytes decrypt byte-for-byte on
     * iOS/Android (and vice versa) because the AAD is the
     * platform-independent header, not any host RTP metadata.
     *
     * # Errors
     *
     * `JsError` containing `CounterExhausted` when the 64-bit
     * counter stands at its last value, else an AEAD error.
     * @param {Uint8Array} plaintext
     * @returns {Uint8Array}
     */
    sealFramed(plaintext) {
        const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.participantkey_sealFramed(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Seal an encoded media frame into the ADR-0071 layout,
     * `clear_prefix || ciphertext_and_tag || sframe_header || trailer`, with
     * the clear prefix the sender's rule gives `codec` for this frame: 10
     * bytes of a VP8 key frame, 3 of any other VP8 frame, none for Opus or an
     * unknown codec. This is the web worker's outgoing path. Key, nonce and
     * counter are those of ADR-0035 / ADR-0069; the header, the clear prefix
     * and the trailer are the AEAD additional data.
     *
     * # Errors
     *
     * `JsError` containing `CounterExhausted` when the 64-bit counter stands
     * at its last value, else an AEAD error. Nothing is sealed on either.
     * @param {Uint8Array} frame
     * @param {MediaCodec} codec
     * @returns {Uint8Array}
     */
    sealMediaFrame(frame, codec) {
        const ptr0 = passArray8ToWasm0(frame, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.participantkey_sealMediaFrame(this.__wbg_ptr, ptr0, len0, codec);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * [`Self::seal_media_frame`] with the clear prefix length stated by the
     * caller. A length beyond the frame is cut to the frame.
     *
     * # Errors
     *
     * `JsError` when `clear_prefix_len` exceeds 16, on counter exhaustion,
     * or on an AEAD error. Nothing is sealed.
     * @param {Uint8Array} frame
     * @param {number} clear_prefix_len
     * @returns {Uint8Array}
     */
    sealMediaFrameWithPrefix(frame, clear_prefix_len) {
        const ptr0 = passArray8ToWasm0(frame, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.participantkey_sealMediaFrameWithPrefix(this.__wbg_ptr, ptr0, len0, clear_prefix_len);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Sender-leaf index baked into this key.
     * @returns {number}
     */
    senderLeaf() {
        const ret = wasm.participantkey_senderLeaf(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) ParticipantKey.prototype[Symbol.dispose] = ParticipantKey.prototype.free;

/**
 * Per-relationship pseudonym. Mirror of [`lakko_contacts::PerRelationship`].
 */
export class PerRelationship {
    static __wrap(ptr) {
        const obj = Object.create(PerRelationship.prototype);
        obj.__wbg_ptr = ptr;
        PerRelationshipFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PerRelationshipFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_perrelationship_free(ptr, 0);
    }
    /**
     * 32 raw bytes.
     * @returns {Uint8Array}
     */
    asBytes() {
        const ret = wasm.perrelationship_asBytes(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Derive from the shared 32-byte relationship seed and direction.
     * @param {Uint8Array} seed
     * @param {Direction} direction
     * @returns {PerRelationship}
     */
    static derive(seed, direction) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.perrelationship_derive(ptr0, len0, direction);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return PerRelationship.__wrap(ret[0]);
    }
}
if (Symbol.dispose) PerRelationship.prototype[Symbol.dispose] = PerRelationship.prototype.free;

/**
 * What the viewer is to a one-to-one conversation.
 *
 * A constructible class rather than two positional booleans on the call, so
 * the reading of each flag is written down at the call site rather than
 * inferred from its position.
 */
export class PersonInfoCapabilities {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PersonInfoCapabilitiesFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_personinfocapabilities_free(ptr, 0);
    }
    /**
     * Contacts of my contacts is on in "Who can find me".
     * @returns {boolean}
     */
    get contactsOfContactsOn() {
        const ret = wasm.personinfocapabilities_contactsOfContactsOn(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The person is on this device's contact list.
     * @returns {boolean}
     */
    get isContact() {
        const ret = wasm.personinfocapabilities_isContact(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * This device's membership in the conversation is over.
     * @returns {boolean}
     */
    get membershipEnded() {
        const ret = wasm.personinfocapabilities_membershipEnded(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `membershipEnded`: this device's membership in the conversation is
     * over (ADR-0045 UX 6.2). `isContact`: the person is on this device's
     * contact list - `false` only for a person the viewer removed, whose
     * one-to-one remains, and whose card then offers `add-contact`.
     * `contactsOfContactsOn`: Contacts of my contacts is on in "Who can find
     * me", under either form; left out it reads `false`, and the card has
     * no `their-contacts-can-find-me` row.
     * @param {boolean} membership_ended
     * @param {boolean} is_contact
     * @param {boolean | null} [contacts_of_contacts_on]
     */
    constructor(membership_ended, is_contact, contacts_of_contacts_on) {
        const ret = wasm.personinfocapabilities_new(membership_ended, is_contact, isLikeNone(contacts_of_contacts_on) ? 0xFFFFFF : contacts_of_contacts_on ? 1 : 0);
        this.__wbg_ptr = ret;
        PersonInfoCapabilitiesFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) PersonInfoCapabilities.prototype[Symbol.dispose] = PersonInfoCapabilities.prototype.free;

/**
 * One shared-pin operation on one message. Mirror of
 * [`lakko_contacts::pin::PinOp`].
 *
 * **A pin received from someone else is that person's assertion about the
 * whole conversation, and the sender's authority is the RECEIVER's check**:
 * resolve the MLS sender leaf to the account key at receipt (C-68), read the
 * live role table, accept only from the owner or an admin (any member when
 * the group carries no role table; either participant of a one-to-one), then
 * keep it only when `pinIsNewer` than the stored stamp **for that message**,
 * the whole read-decide-write under the per-group lock. Persist the stamp
 * and re-emit it VERBATIM on a re-send.
 */
export class PinOp {
    static __wrap(ptr) {
        const obj = Object.create(PinOp.prototype);
        obj.__wbg_ptr = ptr;
        PinOpFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PinOpFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_pinop_free(ptr, 0);
    }
    /**
     * DS `messageId` of the message, as a `bigint`.
     * @returns {bigint}
     */
    get messageId() {
        const ret = wasm.pinop_messageId(this.__wbg_ptr);
        return ret;
    }
    /**
     * `true` = pinned for everyone; `false` = no longer pinned.
     * @returns {boolean}
     */
    get pinned() {
        const ret = wasm.pinop_pinned(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Sender-clock UNIX milliseconds, as a `bigint`.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.pinop_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) PinOp.prototype[Symbol.dispose] = PinOp.prototype.free;

/**
 * JS-facing PoW proof. Three base64 fields ready to inline into
 * the `pow` object on `/signup/start`, `/signup/finish`,
 * `/login/start`, `/recovery/login/start` request bodies.
 *
 * Each getter returns a fresh `String` copy — cheap (the strings
 * are tiny, ~24 chars each) and means the caller cannot
 * accidentally mutate cached state.
 *
 * `Debug` is derived because the bytes here are not secret —
 * the seed comes from the public challenge endpoint, and the
 * nonce / solution are about to be sent in cleartext to the
 * server in the next request.
 */
export class PowProof {
    static __wrap(ptr) {
        const obj = Object.create(PowProof.prototype);
        obj.__wbg_ptr = ptr;
        PowProofFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PowProofFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_powproof_free(ptr, 0);
    }
    /**
     * Base64-encoded nonce — 16 bytes.
     * @returns {string}
     */
    get nonceB64() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.powproof_nonceB64(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Base64-encoded seed (echoes the `seed` from the
     * `/pow/challenge` response — surfaced here so callers can
     * build the `pow` object from the proof alone).
     * @returns {string}
     */
    get seedB64() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.powproof_seedB64(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Base64-encoded Equix solution — 16 bytes.
     * @returns {string}
     */
    get solutionB64() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.powproof_solutionB64(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) PowProof.prototype[Symbol.dispose] = PowProof.prototype.free;

/**
 * Static-only namespace class for the PoW solver.
 *
 * Exists as a class so the JS surface stays uniform with the rest
 * of `lakko-ffi-wasm` (where every operation hangs off a class).
 * The struct itself carries no state — every method is a static
 * factory.
 *
 * JS usage:
 *
 * ```js
 * import { PowSolver } from "@lakko/ffi-wasm";
 *
 * // 1. Fetch the challenge.
 * const { seed, effort_bits } = await fetch("/pow/challenge", {
 *     method: "POST",
 * }).then((r) => r.json());
 *
 * // 2. Solve (blocking — call from a Web Worker for non-trivial
 * //    effort_bits values).
 * const proof = PowSolver.solve(seed, effort_bits);
 *
 * // 3. Attach to the next expensive request.
 * await fetch("/signup/start", {
 *     method: "POST",
 *     headers: { "content-type": "application/json" },
 *     body: JSON.stringify({
 *         pow: {
 *             seed: proof.seedB64,
 *             nonce: proof.nonceB64,
 *             solution: proof.solutionB64,
 *         },
 *         account_id: "...",
 *         password_request: "...",
 *     }),
 * });
 * ```
 */
export class PowSolver {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PowSolverFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_powsolver_free(ptr, 0);
    }
    /**
     * Solve the Equix PoW for the given challenge.
     *
     * Blocking. At the server-default `effort_bits = 8` this
     * typically takes 100–500 ms on a 2024+ phone; at higher
     * difficulties it can take seconds. **Run inside a Web
     * Worker** to keep the UI responsive.
     *
     * # Errors
     *
     * * `seedB64` does not decode as base64 → thrown error.
     * * Decoded seed has the wrong byte length (must be 16) →
     *   thrown error.
     * * Solver budget exhausted (extremely unlikely at default
     *   server effort, possible at `effort_bits ≥ 14` — caller
     *   should retry with a fresh challenge).
     * @param {string} seed_b64
     * @param {number} effort_bits
     * @returns {PowProof}
     */
    static solve(seed_b64, effort_bits) {
        const ptr0 = passStringToWasm0(seed_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.powsolver_solve(ptr0, len0, effort_bits);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return PowProof.__wrap(ret[0]);
    }
    /**
     * One cancellable slice of a solve: at most `maxAttempts` nonces, then
     * `undefined` if none of them produced a proof.
     *
     * A Worker loops over chunks and checks for a cancel message between
     * them. Each call samples fresh nonces, so the loop is the same search
     * as one [`PowSolver::solve`].
     *
     * # Errors
     *
     * Throws for a seed that is not base64 or not 16 bytes. Running out of
     * attempts is not an error: it is `undefined`.
     * @param {string} seed_b64
     * @param {number} effort_bits
     * @param {number} max_attempts
     * @returns {PowProof | undefined}
     */
    static solveChunk(seed_b64, effort_bits, max_attempts) {
        const ptr0 = passStringToWasm0(seed_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.powsolver_solveChunk(ptr0, len0, effort_bits, max_attempts);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : PowProof.__wrap(ret[0]);
    }
}
if (Symbol.dispose) PowSolver.prototype[Symbol.dispose] = PowSolver.prototype.free;

/**
 * 16-byte presence token. Mirror of [`lakko_presence::PresenceToken`].
 */
export class PresenceToken {
    static __wrap(ptr) {
        const obj = Object.create(PresenceToken.prototype);
        obj.__wbg_ptr = ptr;
        PresenceTokenFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PresenceTokenFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_presencetoken_free(ptr, 0);
    }
    /**
     * Return the underlying 16 raw bytes.
     * @returns {Uint8Array}
     */
    asBytes() {
        const ret = wasm.presencetoken_asBytes(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Wrap raw bytes (e.g. parsed off a wire format). Must be exactly
     * 16 bytes.
     * @param {Uint8Array} bytes
     * @returns {PresenceToken}
     */
    static fromBytes(bytes) {
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.presencetoken_fromBytes(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return PresenceToken.__wrap(ret[0]);
    }
}
if (Symbol.dispose) PresenceToken.prototype[Symbol.dispose] = PresenceToken.prototype.free;

/**
 * Per-group presence tracker. Mirror of
 * [`lakko_presence::PresenceTracker`].
 */
export class PresenceTracker {
    static __wrap(ptr) {
        const obj = Object.create(PresenceTracker.prototype);
        obj.__wbg_ptr = ptr;
        PresenceTrackerFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PresenceTrackerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_presencetracker_free(ptr, 0);
    }
    /**
     * `true` if no observations recorded.
     * @returns {boolean}
     */
    get isEmpty() {
        const ret = wasm.presencetracker_isEmpty(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Is `token` within the presence window as of `now`?
     * @param {PresenceToken} token
     * @param {bigint} now_unix_ms
     * @returns {boolean}
     */
    isPresent(token, now_unix_ms) {
        _assertClass(token, PresenceToken);
        const ret = wasm.presencetracker_isPresent(this.__wbg_ptr, token.__wbg_ptr, now_unix_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * How many tokens are currently tracked.
     * @returns {number}
     */
    get len() {
        const ret = wasm.presencetracker_len(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * New tracker with custom presence window.
     * @param {bigint} presence_window_ms
     */
    constructor(presence_window_ms) {
        const ret = wasm.presencetracker_new(presence_window_ms);
        this.__wbg_ptr = ret;
        PresenceTrackerFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Record an observation of `token` at wall-clock time `now`.
     * @param {PresenceToken} token
     * @param {bigint} now_unix_ms
     */
    observe(token, now_unix_ms) {
        _assertClass(token, PresenceToken);
        const ret = wasm.presencetracker_observe(this.__wbg_ptr, token.__wbg_ptr, now_unix_ms);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Drop tokens whose `last_seen_ms` is older than the presence
     * window.
     * @param {bigint} now_unix_ms
     */
    purgeExpired(now_unix_ms) {
        const ret = wasm.presencetracker_purgeExpired(this.__wbg_ptr, now_unix_ms);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * New tracker with [`DEFAULT_PRESENCE_WINDOW_MS`] (60s).
     * @returns {PresenceTracker}
     */
    static withDefaultWindow() {
        const ret = wasm.presencetracker_withDefaultWindow();
        return PresenceTracker.__wrap(ret);
    }
}
if (Symbol.dispose) PresenceTracker.prototype[Symbol.dispose] = PresenceTracker.prototype.free;

/**
 * Tagged result of [`Group::process_incoming`].
 *
 * JS code reads `kind` (a string) to discriminate, then
 * `applicationData` for [`ProcessedMessageKind::Application`].
 * We deliberately don't expose a sum-type on the JS side because
 * wasm-bindgen has no idiomatic story for Rust enums with
 * associated data; a tagged class is what TypeScript would idiom
 * to anyway.
 */
export class ProcessedMessage {
    static __wrap(ptr) {
        const obj = Object.create(ProcessedMessage.prototype);
        obj.__wbg_ptr = ptr;
        ProcessedMessageFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ProcessedMessageFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_processedmessage_free(ptr, 0);
    }
    /**
     * Application plaintext, present iff `kind == "Application"`.
     * @returns {Uint8Array | undefined}
     */
    get applicationData() {
        const ret = wasm.processedmessage_applicationData(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Whether an external-join request was **retained**, so an approve can act
     * on it. Meaningful only when `kind === "ExternalJoinProposal"`; `false`
     * for every other kind.
     *
     * **`false` means an approve would fail, so do not offer one.** The event
     * fires either way — a request arriving while the stranger quarantine is
     * full is surfaced but not kept, because a stranger must not be able to
     * make a member's receive loop error. Rendering an unretained request as
     * actionable is exactly the shape that offers a user an action that cannot
     * work. The remedy belongs to the joiner (ask again later), not the admin.
     * @returns {boolean}
     */
    get externalJoinRetained() {
        const ret = wasm.processedmessage_externalJoinRetained(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The prospective joiner's signature key, present iff
     * `kind == "ExternalJoinProposal"` (ADR-0027, G-3). The handle the caller
     * passes to `approveExternalJoin` / `rejectExternalJoin`.
     * @returns {Uint8Array | undefined}
     */
    get externalJoinSignatureKey() {
        const ret = wasm.processedmessage_externalJoinSignatureKey(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Discriminator. `"Application" | "Commit" | "Proposal" |
     * "ExternalJoinProposal"`. Useful for `switch` / `case` in
     * TypeScript.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.processedmessage_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The ADR-0045 proposal record, present **iff** `kind === "Proposal"` and
     * `undefined` otherwise.
     *
     * Read `proposal.stored` before treating it as a pending request:
     * `false` means the receipt gate **refused** it and no sweep will ever
     * commit it. `senderLeaf` is also mirrored onto this `ProcessedMessage`
     * for a uniform "who sent this" read across kinds.
     * @returns {ProposalRecord | undefined}
     */
    get proposal() {
        const ret = wasm.processedmessage_proposal(this.__wbg_ptr);
        return ret === 0 ? undefined : ProposalRecord.__wrap(ret);
    }
    /**
     * The **account** key the sender leaf resolves to (ADR-0050), or
     * `undefined` for a control message, an external sender or an
     * unresolvable leaf. This — never the leaf — is what authorship,
     * reactions and read state key on, so one person is one author across
     * all their devices.
     * @returns {Uint8Array | undefined}
     */
    get senderAccount() {
        const ret = wasm.processedmessage_senderAccount(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The sender's leaf index for an Application message (ADR-0023),
     * or `undefined` for a control message / external sender. The
     * chat receive path uses it for the author-only edit/delete gate
     * + reaction attribution.
     * @returns {number | undefined}
     */
    get senderLeaf() {
        const ret = wasm.processedmessage_senderLeaf(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
}
if (Symbol.dispose) ProcessedMessage.prototype[Symbol.dispose] = ProcessedMessage.prototype.free;

/**
 * Discriminator for [`ProcessedMessage`]. wasm-bindgen renders this
 * as a JS number-enum; we expose a string discriminator via
 * [`ProcessedMessage::kind`] for ergonomic `switch` / `case`.
 * @enum {0 | 1 | 2 | 3}
 */
export const ProcessedMessageKind = Object.freeze({
    /**
     * Application payload (decrypted plaintext available via
     * `applicationData`).
     */
    Application: 0, "0": "Application",
    /**
     * A commit was processed and merged. Group state advanced.
     */
    Commit: 1, "1": "Commit",
    /**
     * A standalone proposal was processed (no state advance).
     */
    Proposal: 2, "2": "Proposal",
    /**
     * External-join proposal was processed.
     */
    ExternalJoinProposal: 3, "3": "ExternalJoinProposal",
});

/**
 * A person's self-stated profile. Mirror of
 * [`lakko_contacts::profile::Profile`].
 *
 * **Every field received from someone else is that person's claim.** Store
 * it under the sender's signature key resolved at receipt (C-68), keep it
 * only when `profileIsNewer`, and show `username` only once discovery
 * resolves it to that same key.
 */
export class Profile {
    static __wrap(ptr) {
        const obj = Object.create(Profile.prototype);
        obj.__wbg_ptr = ptr;
        ProfileFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ProfileFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_profile_free(ptr, 0);
    }
    /**
     * The sender's 16-byte auth account id, or `undefined` (ADR-0048 v2):
     * what the call roster derives the ADR-0021 peer tag from.
     * @returns {Uint8Array | undefined}
     */
    get accountId() {
        const ret = wasm.profile_accountId(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The profile photo (ADR-0084), or `undefined`. Its keys are SECRET.
     * @returns {ProfileAvatar | undefined}
     */
    get avatar() {
        const ret = wasm.profile_avatar(this.__wbg_ptr);
        return ret === 0 ? undefined : ProfileAvatar.__wrap(ret);
    }
    /**
     * Display name, or `undefined` for no name.
     * @returns {string | undefined}
     */
    get displayName() {
        const ret = wasm.profile_displayName(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The reach grants handed to the receivers (ADR-0060 §3, §6), at most
     * two (`Contact` / `Group` / `Member` / `Channel`, one per class).
     * SECRET: store encrypted, never log. Keep a `Contact` grant ONLY from
     * the other participant of a one-to-one conversation.
     * @returns {ReachGrant[]}
     */
    get grants() {
        const ret = wasm.profile_grants(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Sender-clock UNIX milliseconds, as a `bigint`.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.profile_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Claimed username in canonical form, or `undefined` for none.
     * @returns {string | undefined}
     */
    get username() {
        const ret = wasm.profile_username(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The 153-byte discovery-signed username certificate (ADR-0060 §5), or
     * `undefined`. Show the username only after `verifyUsernameBinding`
     * accepts it for the OPRF of the claimed username and this account id.
     * @returns {Uint8Array | undefined}
     */
    get usernameCertificate() {
        const ret = wasm.profile_usernameCertificate(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
}
if (Symbol.dispose) Profile.prototype[Symbol.dispose] = Profile.prototype.free;

/**
 * The profile photo (ADR-0084): a square JPEG still and optionally a square
 * MP4 video without sound whose first frame the still is.
 */
export class ProfileAvatar {
    static __wrap(ptr) {
        const obj = Object.create(ProfileAvatar.prototype);
        obj.__wbg_ptr = ptr;
        ProfileAvatarFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ProfileAvatarFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_profileavatar_free(ptr, 0);
    }
    /**
     * A photo (`video` and `videoDurationMs` undefined) or a profile video
     * (both given).
     *
     * # Errors
     *
     * Throws when exactly one of `video` / `videoDurationMs` is given.
     * @param {AvatarRef} still
     * @param {AvatarRef | null} [video]
     * @param {number | null} [video_duration_ms]
     */
    constructor(still, video, video_duration_ms) {
        _assertClass(still, AvatarRef);
        var ptr0 = still.__destroy_into_raw();
        let ptr1 = 0;
        if (!isLikeNone(video)) {
            _assertClass(video, AvatarRef);
            ptr1 = video.__destroy_into_raw();
        }
        const ret = wasm.profileavatar_new(ptr0, ptr1, isLikeNone(video_duration_ms) ? 0xFFFFFF : video_duration_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        ProfileAvatarFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The still.
     * @returns {AvatarRef}
     */
    get still() {
        const ret = wasm.profileavatar_still(this.__wbg_ptr);
        return AvatarRef.__wrap(ret);
    }
    /**
     * The video, or `undefined` for a photo.
     * @returns {AvatarRef | undefined}
     */
    get video() {
        const ret = wasm.profileavatar_video(this.__wbg_ptr);
        return ret === 0 ? undefined : AvatarRef.__wrap(ret);
    }
    /**
     * The video's length in milliseconds, or `undefined` for a photo.
     * @returns {number | undefined}
     */
    get videoDurationMs() {
        const ret = wasm.profileavatar_videoDurationMs(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret;
    }
}
if (Symbol.dispose) ProfileAvatar.prototype[Symbol.dispose] = ProfileAvatar.prototype.free;

/**
 * Result of [`Group::commit_pending_proposals`] — the ADR-0045 sweep.
 * wasm-bindgen mirror of UniFFI's `ProposalCommit`.
 *
 * Mirrors [`AddMember`]'s shape so ADR-0040's durable pending-commit outbox
 * wraps it unchanged, with two differences the proposal path forces: the
 * Welcome is **optional** (a sweep that carried no `add` produces none), and
 * `swept` names what the commit actually carried so the caller can mark those
 * requests applied.
 */
export class ProposalCommit {
    static __wrap(ptr) {
        const obj = Object.create(ProposalCommit.prototype);
        obj.__wbg_ptr = ptr;
        ProposalCommitFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ProposalCommitFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_proposalcommit_free(ptr, 0);
    }
    /**
     * Encoded MLS commit message — broadcast to all existing members.
     * @returns {Uint8Array}
     */
    get commit() {
        const ret = wasm.proposalcommit_commit(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The proposals this commit carried, in sweep order, as a flat array of
     * [`ProposalRecord`] instances (same convention as `Group.members()` —
     * wasm-bindgen cannot return a `Vec` of a custom struct through a getter,
     * so this is a method, not a property).
     *
     * Read back off the staged commit by the core, so it reports what the
     * commit really carries rather than what the caller predicted it would.
     * @returns {any[]}
     */
    swept() {
        const ret = wasm.proposalcommit_swept(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Encoded MLS welcome message — delivered to the newly added members.
     * `undefined` unless the sweep carried at least one `add`.
     * @returns {Uint8Array | undefined}
     */
    get welcome() {
        const ret = wasm.proposalcommit_welcome(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
}
if (Symbol.dispose) ProposalCommit.prototype[Symbol.dispose] = ProposalCommit.prototype.free;

/**
 * One ADR-0045 proposal as the receive path saw it. wasm-bindgen mirror of
 * UniFFI's `ProposalRecord` / [`lakko_mls::ProposalRecord`].
 *
 * # Why a flat tagged class rather than a sum type
 *
 * wasm-bindgen has no idiomatic story for Rust enums carrying data, so this
 * follows the two shapes the repo already uses for exactly this problem:
 * [`ProcessedMessage`] (a string `kind` discriminator plus per-variant
 * optional getters) and `ParsedBody` in `attachments.rs`. The alternative
 * precedent — `SfuServerFrame`, where the web reads fields off a TS type over
 * a native `JSON.parse` — does not apply: those frames arrive as JSON on the
 * wire already, whereas this record is produced by the core, so routing it
 * through a JSON round-trip would invent a serialisation that nothing else
 * needs.
 *
 * # The payload fields are per-variant, never shared
 *
 * `ParsedBody` reuses one `target` slot across `edit` / `delete` / `reaction`
 * because every one of those means the same thing (a message id). The two
 * payloads here do **not**: an `Add` carries a 32-byte signature key and a
 * `Remove` carries a leaf index. They therefore get **separate, differently
 * named** getters — [`Self::add_signature_key`] and
 * [`Self::remove_target_leaf`] — each `undefined` outside its own variant, so
 * a caller cannot read a `Remove`'s target as if it were an `Add`'s key.
 */
export class ProposalRecord {
    static __wrap(ptr) {
        const obj = Object.create(ProposalRecord.prototype);
        obj.__wbg_ptr = ptr;
        ProposalRecordFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ProposalRecordFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_proposalrecord_free(ptr, 0);
    }
    /**
     * The prospective member's 32-byte Ed25519 signature public key, present
     * **iff** `kind === "add"` and `undefined` for every other kind.
     * @returns {Uint8Array | undefined}
     */
    get addSignatureKey() {
        const ret = wasm.proposalrecord_addSignatureKey(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Discriminator: `"add" | "remove" | "leave" | "update" | "role-change" |
     * "unsupported"`. Useful for `switch` / `case` in TypeScript.
     *
     * Two kinds always arrive with `stored === false`, because the core's
     * authorisation rule refuses them for every member (fail-closed, invariant
     * #4). `"unsupported"` is a proposal type Lakko never emits. `"update"` is
     * an RFC 9420 leaf rotation, which openmls 0.8.1 does not bind to the
     * sender's existing signature key — so it can rotate the very key the
     * ADR-0022 role table is keyed on, and it is the one kind whose proposal
     * reference is fresh on every emission, which made it floodable past the
     * pending-set cap. A Lakko member rotates its leaf with `selfUpdate`, which
     * composes no proposal at all.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.proposalrecord_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The leaf index the proposal removes, present **iff** `kind ===
     * "remove"` and `undefined` for every other kind.
     * @returns {number | undefined}
     */
    get removeTargetLeaf() {
        const ret = wasm.proposalrecord_removeTargetLeaf(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * The proposing member's leaf index, or `undefined` for a sender that is
     * not a resolvable group member (always unauthorised — fail-closed).
     * @returns {number | undefined}
     */
    get senderLeaf() {
        const ret = wasm.proposalrecord_senderLeaf(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * `true` when the proposal was authorised and retained in the pending set
     * so a later sweep can commit it; `false` when it was **dropped**.
     *
     * A dropped proposal is a **refusal**, not a failure to parse: a proposal
     * does not advance the epoch, so dropping it unstored *is* the refusal, and
     * the core reports it as data rather than throwing. Surface or log it
     * distinctly from a stored one — a stored proposal is a pending request
     * awaiting a sweep, a dropped one will never be committed.
     * @returns {boolean}
     */
    get stored() {
        const ret = wasm.proposalrecord_stored(this.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) ProposalRecord.prototype[Symbol.dispose] = ProposalRecord.prototype.free;

/**
 * Persistent MLS-group wrapper for one Lakko channel's
 * publisher tier (ADR-0010 Tier 1). Mirror of the
 * UniFFI `PublisherGroup` (`crates/lakko-ffi-uniffi`).
 */
export class PublisherGroup {
    static __wrap(ptr) {
        const obj = Object.create(PublisherGroup.prototype);
        obj.__wbg_ptr = ptr;
        PublisherGroupFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PublisherGroupFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_publishergroup_free(ptr, 0);
    }
    /**
     * ADR-0028 OQ3 — approve a retained co-publisher external-join; returns
     * the Commit + the joiner's Welcome, as it always has.
     *
     * **This is the direct approve, and this is the only type it survives on**
     * (ADR-0045 S7). A PublisherGroup carries no ADR-0022 role table, so it has
     * no committer to sweep a proposal, and ADR-0045 leaves such a group on
     * direct commits. The return type is the difference and it is deliberate:
     * `Group.approveExternalJoin` hands back proposal bytes and no Welcome,
     * this hands back a merged Commit and a Welcome to deliver now, and neither
     * can be used where the other is expected.
     * @param {Identity} identity
     * @param {Uint8Array} proposal_ref
     * @returns {AddMember}
     */
    approveExternalJoin(identity, proposal_ref) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(proposal_ref, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_approveExternalJoin(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AddMember.__wrap(ret[0]);
    }
    /**
     * Channel id this group was minted for. Returned as raw
     * bytes (16 elements) so the JS side can compare with
     * whatever shape the channel-discovery layer hands it.
     * @returns {Uint8Array}
     */
    channelId() {
        const ret = wasm.publishergroup_channelId(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * 32-byte channel content-key seed from the MLS
     * exporter at the current group epoch. Stable across
     * process restarts once the group has been persisted —
     * hand directly to the channels composer's
     * `ChannelPublisherKeys.seed` field.
     * @param {Identity} identity
     * @returns {Uint8Array}
     */
    channelSeed(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.publishergroup_channelSeed(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * ADR-0087 section 7 — encrypt an application message for the
     * publishers at the current epoch (the admission-seed envelope a channel
     * owner hands its co-publishers); read it with
     * `processIncomingVerifyingRoles`.
     * @param {Identity} identity
     * @param {Uint8Array} plaintext
     * @returns {Uint8Array}
     */
    createApplicationMessage(identity, plaintext) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_createApplicationMessage(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * ADR-0081 - the 32-byte access seed of the current epoch, to record
     * with a commit before it merges (a rotation, an approve) so a re-send
     * after a reload can prove the epoch the commit left
     * (`DsProofSigner.forGroup`). Keep it only in the device-key-encrypted
     * record of that commit.
     * @param {Identity} identity
     * @returns {Uint8Array}
     */
    dsAccessSeed(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.publishergroup_dsAccessSeed(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The ADR-0053 access signer for the publisher journal at the current
     * epoch - the same derivation as `Group.dsAccessSigner` over the
     * underlying group. Re-derive after every commit (every rotation).
     * @param {Identity} identity
     * @returns {DsProofSigner}
     */
    dsAccessSigner(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.publishergroup_dsAccessSigner(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DsProofSigner.__wrap(ret[0]);
    }
    /**
     * The current MLS epoch (`bigint`). G-50: a PublisherGroup commit's
     * durable record names the epoch it moved the group to, and a restart
     * compares it with this.
     * @returns {bigint}
     */
    epoch() {
        const ret = wasm.publishergroup_epoch(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return BigInt.asUintN(64, ret[0]);
    }
    /**
     * ADR-0028 OQ3 — the underlying MLS `group_id`. A co-publisher invite-link
     * is minted over this; the joiner targets it when crafting its
     * external-join proposal.
     * @returns {Uint8Array}
     */
    groupId() {
        const ret = wasm.publishergroup_groupId(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * ADR-0028 OQ3 / ADR-0081 amendment (2026-10-19) — join a PublisherGroup
     * as a co-publisher from the owner's Welcome. A PublisherGroup carries no
     * role table, so `Group.joinFromWelcome` refuses its Welcome
     * (`noRoleTable`); this is its entry point. A Welcome for another group
     * than `groupId`, or a conversation's Welcome, is refused before anything
     * is written. The joined state persists in `identity`'s provider
     * (`loadJoined` reopens it).
     * @param {Identity} identity
     * @param {Uint8Array} welcome
     * @param {Uint8Array} group_id
     * @param {Uint8Array} channel_id
     * @returns {PublisherGroup}
     */
    static joinFromWelcome(identity, welcome, group_id, channel_id) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(welcome, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_joinFromWelcome(identity.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return PublisherGroup.__wrap(ret[0]);
    }
    /**
     * Load the persisted PublisherGroup for `channel_id`.
     * Returns `undefined` (`Option<...> -> None`) when no
     * group has been minted yet — pair with
     * [`PublisherGroup.openOrCreate`] for the load-or-create
     * path every composer wants.
     * @param {Identity} identity
     * @param {Uint8Array} channel_id
     * @returns {PublisherGroup | undefined}
     */
    static load(identity, channel_id) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_load(identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : PublisherGroup.__wrap(ret[0]);
    }
    /**
     * ADR-0028 OQ3 / ADR-0078 section 6 — open a PublisherGroup this identity
     * JOINED as a co-publisher, under the `group_id` its `/g/` capability
     * conveyed and the `channel_id` that link's sealed preview carried (a
     * co-publisher cannot derive the owner's id). `undefined` when nothing is
     * stored there; never creates.
     * @param {Identity} identity
     * @param {Uint8Array} group_id
     * @param {Uint8Array} channel_id
     * @returns {PublisherGroup | undefined}
     */
    static loadJoined(identity, group_id, channel_id) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_loadJoined(identity.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : PublisherGroup.__wrap(ret[0]);
    }
    /**
     * Mint a fresh PublisherGroup for `channel_id`. Persists
     * the MLS state via the OpenMLS provider; subsequent
     * [`PublisherGroup.load`] calls return the same group.
     *
     * `channel_id` MUST be exactly 16 bytes (
     * [`lakko_mls::CHANNEL_ID_LEN`]).
     * @param {Identity} identity
     * @param {Uint8Array} channel_id
     */
    constructor(identity, channel_id) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_new(identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        PublisherGroupFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Load-or-create convenience. Returns the persisted
     * PublisherGroup for `(identity, channel_id)` if one
     * exists; otherwise mints a fresh one and persists it
     * as a side effect. Idempotent across consecutive
     * calls — the load-bearing invariant that lets
     * subscribers' previously-received seed envelopes keep
     * decrypting across publisher restarts.
     * @param {Identity} identity
     * @param {Uint8Array} channel_id
     * @returns {PublisherGroup}
     */
    static openOrCreate(identity, channel_id) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_openOrCreate(identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return PublisherGroup.__wrap(ret[0]);
    }
    /**
     * ADR-0028 OQ3 — signature keys of the pending co-publisher external-join
     * proposals, flat-concatenated 32-byte chunks (the role-table FFI
     * convention). Each chunk is a handle for `approveExternalJoin` /
     * `rejectExternalJoin`.
     * @returns {Uint8Array}
     */
    pendingExternalJoins() {
        const ret = wasm.publishergroup_pendingExternalJoins(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * ADR-0028 OQ3 — process an incoming MLS message on the publisher tier,
     * role-gated (ADR-0022). Surfaces a non-member's external-join proposal as
     * `ProcessedMessage.externalJoinSignatureKey` so the owner can approve a
     * co-publisher.
     * @param {Identity} identity
     * @param {Uint8Array} bytes
     * @returns {ProcessedMessage}
     */
    processIncomingVerifyingRoles(identity, bytes) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_processIncomingVerifyingRoles(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ProcessedMessage.__wrap(ret[0]);
    }
    /**
     * Process one row read from this journal's **knock** queue (ADR-0053).
     * Anything that is not a stranger's external-join proposal for this group
     * is refused BEFORE it is processed - a knock row is an unauthenticated
     * write, and applying a commit or message someone posted there instead of
     * the journal would fork the admins who read knocks from everyone else.
     * A row that passes is processed exactly like `processIncomingVerifyingRoles`.
     * @param {Identity} identity
     * @param {Uint8Array} bytes
     * @returns {ProcessedMessage}
     */
    processKnock(identity, bytes) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_processKnock(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ProcessedMessage.__wrap(ret[0]);
    }
    /**
     * ADR-0028 OQ3 — reject a retained co-publisher external-join (local drop;
     * no epoch advance).
     * `identity` is what the durable copy of the pending set is written
     * through (the request set rides the MLS storage snapshot, so a decision
     * survives a restart).
     * @param {Identity} identity
     * @param {Uint8Array} proposal_ref
     */
    rejectExternalJoin(identity, proposal_ref) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(proposal_ref, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_rejectExternalJoin(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * ADR-0087 section 5 — retain a request to join carried as a bare
     * KeyPackage (a co-publisher's, opened from the channel's knock with `openKnockRow`);
     * approve it with `approveExternalJoin`.
     * @param {Identity} identity
     * @param {Uint8Array} key_package
     * @returns {JoinRequest}
     */
    retainJoinRequest(identity, key_package) {
        _assertClass(identity, Identity);
        const ptr0 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.publishergroup_retainJoinRequest(this.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return JoinRequest.__wrap(ret[0]);
    }
    /**
     * G4-2-mls-persistence step 4 — rotate the channel
     * content-key seed via an MLS self-update commit.
     * Mirror of the UniFFI `PublisherGroup.rotate`. Returns
     * the wire-encoded commit bytes; the caller broadcasts
     * them to every other PublisherGroup member so they
     * advance to the same epoch in lock-step.
     *
     * After `rotate` resolves, the next `channelSeed` call
     * returns the **new-epoch** seed. The web composer
     * pairs this with `sealSubscriberSeedEnvelope` + the
     * services/channels batch fanout to reseal the seed to
     * every subscriber under the new epoch id.
     * @param {Identity} identity
     * @returns {Uint8Array}
     */
    rotate(identity) {
        _assertClass(identity, Identity);
        const ret = wasm.publishergroup_rotate(this.__wbg_ptr, identity.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) PublisherGroup.prototype[Symbol.dispose] = PublisherGroup.prototype.free;

/**
 * Mirror of [`lakko_push::PushPayload`]. Four discriminated variants
 * addressed via `kind`:
 *
 * - `"GroupMessage"` — `groupPseudonym` + `envelopePointer` set
 * - `"CallSignal"` — same two fields as `"GroupMessage"`; the kind is
 *   what tells the service worker to ring instead of showing a
 *   message preview
 * - `"MailboxMessage"` — only `envelopePointer` set
 * - `"Wakeup"` — neither set
 */
export class PushPayload {
    static __wrap(ptr) {
        const obj = Object.create(PushPayload.prototype);
        obj.__wbg_ptr = ptr;
        PushPayloadFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PushPayloadFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_pushpayload_free(ptr, 0);
    }
    /**
     * Construct a `CallSignal` payload — a call offer / accept /
     * decline / hang-up landed on this group's journal.
     * @param {Uint8Array} group_pseudonym
     * @param {bigint} envelope_pointer
     * @returns {PushPayload}
     */
    static callSignal(group_pseudonym, envelope_pointer) {
        const ptr0 = passArray8ToWasm0(group_pseudonym, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.pushpayload_callSignal(ptr0, len0, envelope_pointer);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return PushPayload.__wrap(ret[0]);
    }
    /**
     * Decode JSON received from the OS push pipeline.
     * @param {Uint8Array} bytes
     * @returns {PushPayload}
     */
    static decodeJson(bytes) {
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.pushpayload_decodeJson(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return PushPayload.__wrap(ret[0]);
    }
    /**
     * Encode as compact JSON ready to ship through APNs / FCM.
     * @returns {Uint8Array}
     */
    encodeJson() {
        const ret = wasm.pushpayload_encodeJson(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Envelope pointer — present iff `kind != "Wakeup"`.
     * @returns {bigint | undefined}
     */
    get envelopePointer() {
        const ret = wasm.pushpayload_envelopePointer(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
    }
    /**
     * Construct a `GroupMessage` payload.
     * @param {Uint8Array} group_pseudonym
     * @param {bigint} envelope_pointer
     * @returns {PushPayload}
     */
    static groupMessage(group_pseudonym, envelope_pointer) {
        const ptr0 = passArray8ToWasm0(group_pseudonym, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.pushpayload_groupMessage(ptr0, len0, envelope_pointer);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return PushPayload.__wrap(ret[0]);
    }
    /**
     * Group pseudonym bytes — present iff `kind` is `"GroupMessage"`
     * or `"CallSignal"`.
     * @returns {Uint8Array | undefined}
     */
    get groupPseudonym() {
        const ret = wasm.pushpayload_groupPseudonym(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * `true` iff this wake is a call signal. Sugar over reading
     * [`Self::kind`] — the service worker branches on this before it
     * decides between ringing and a message preview, so spelling it
     * as a string comparison at every call site invites a typo that
     * silently degrades a call to a notification.
     *
     * Not `const` despite being trivially so: `#[wasm_bindgen]`
     * refuses to export a `const fn` ("can only #[wasm_bindgen]
     * non-const functions"), so `clippy::missing_const_for_fn` is
     * suppressed here rather than obeyed.
     * @returns {boolean}
     */
    get isCall() {
        const ret = wasm.pushpayload_isCall(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Discriminator.
     * `"GroupMessage" | "CallSignal" | "MailboxMessage" | "Wakeup"`.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.pushpayload_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Construct a `MailboxMessage` payload (recipient knows its own
     * device pseudonym).
     * @param {bigint} envelope_pointer
     * @returns {PushPayload}
     */
    static mailboxMessage(envelope_pointer) {
        const ret = wasm.pushpayload_mailboxMessage(envelope_pointer);
        return PushPayload.__wrap(ret);
    }
    /**
     * Construct a generic `Wakeup` payload.
     * @returns {PushPayload}
     */
    static wakeup() {
        const ret = wasm.pushpayload_wakeup();
        return PushPayload.__wrap(ret);
    }
}
if (Symbol.dispose) PushPayload.prototype[Symbol.dispose] = PushPayload.prototype.free;

/**
 * A decoded push-routing-secret distribution envelope
 * (ADR-0042 v2, per ADR-0044). Output of
 * `decodePushRoutingSecret`.
 *
 * TS-facing shape: `{ mintEpoch: bigint, secret: Uint8Array }`.
 * `mintEpoch` crosses as a `BigInt` (the field is a `u64`),
 * matching the `Group.epoch()` and `ParsedBody.target`
 * precedents.
 */
export class PushRoutingSecret {
    static __wrap(ptr) {
        const obj = Object.create(PushRoutingSecret.prototype);
        obj.__wbg_ptr = ptr;
        PushRoutingSecretFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PushRoutingSecretFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_pushroutingsecret_free(ptr, 0);
    }
    /**
     * The minter's recency stamp, the primary key of the
     * canonical total order — a `bigint` on the JS side. Opaque
     * to the core: it imposes an order and nothing else, and
     * never reaches `derivePushGid`.
     * @returns {bigint}
     */
    get mintEpoch() {
        const ret = wasm.pushroutingsecret_mintEpoch(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The decoded 32-byte per-group routing secret.
     * @returns {Uint8Array}
     */
    get secret() {
        const ret = wasm.pushroutingsecret_secret(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) PushRoutingSecret.prototype[Symbol.dispose] = PushRoutingSecret.prototype.free;

/**
 * This device's provider tokens, built one entry at a time.
 */
export class PushTokenList {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        PushTokenListFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_pushtokenlist_free(ptr, 0);
    }
    /**
     * Add the token of `provider` (`"apns"`, `"fcm"`, `"web_push"`,
     * `"unified_push"`, `"apns_voip"`).
     *
     * # Errors
     *
     * `badProvider` for any other name.
     * @param {string} provider
     * @param {Uint8Array} token
     */
    add(provider, token) {
        const ptr0 = passStringToWasm0(provider, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(token, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.pushtokenlist_add(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * How many entries the list holds.
     * @returns {number}
     */
    get length() {
        const ret = wasm.pushtokenlist_length(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * An empty list.
     */
    constructor() {
        const ret = wasm.pushtokenlist_new();
        this.__wbg_ptr = ret;
        PushTokenListFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) PushTokenList.prototype[Symbol.dispose] = PushTokenList.prototype.free;

/**
 * What a wake tells the woken devices about. The numeric values are the
 * frozen wire bytes.
 * @enum {1 | 2}
 */
export const PushWakeKind = Object.freeze({
    /**
     * An ordinary message.
     */
    Message: 1, "1": "Message",
    /**
     * A call signal: the woken device rings.
     */
    Call: 2, "2": "Call",
});

/**
 * Who a reach key is handed to. The numeric values ARE the frozen wire
 * bytes of `lakko_contacts::reach::ReachClass`.
 * @enum {1 | 2 | 3 | 4 | 5 | 6 | 7}
 */
export const ReachClass = Object.freeze({
    /**
     * Every contact. Empty scope.
     */
    Contact: 1, "1": "Contact",
    /**
     * Whoever holds one invite link. Scope: the capability id.
     */
    Link: 2, "2": "Link",
    /**
     * The one party a request of the owner's went to. Scope: a random id.
     */
    Reply: 3, "3": "Reply",
    /**
     * Members of every group the owner lets find them. Empty scope: one
     * key per account (ADR-0078 section 9).
     */
    Group: 4, "4": "Group",
    /**
     * Every co-member of every group, whatever the setting. Empty scope.
     */
    Member: 5, "5": "Member",
    /**
     * Members of every channel the owner lets find them. Empty scope.
     */
    Channel: 6, "6": "Channel",
    /**
     * Contacts of contacts. Empty scope.
     */
    Fof: 7, "7": "Fof",
});

/**
 * A reach key handed to someone: class, version, seed, and the owner's
 * 97-byte mailbox binding (ADR-0078). **The seed is secret.** Mirror of
 * `lakko_contacts::reach::ReachGrant` (whose `Debug` redacts the seed).
 *
 * **A writer verifies the binding before every write**
 * (`mailboxPkVerified(ownerAccountPk)`): a grant whose binding does not
 * verify must not be written under.
 */
export class ReachGrant {
    static __wrap(ptr) {
        const obj = Object.create(ReachGrant.prototype);
        obj.__wbg_ptr = ptr;
        ReachGrantFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof ReachGrant)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ReachGrantFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_reachgrant_free(ptr, 0);
    }
    /**
     * The owner's 97-byte mailbox binding, not verified.
     * @returns {Uint8Array}
     */
    get binding() {
        const ret = wasm.reachgrant_binding(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Who the key is for.
     * @returns {ReachClass}
     */
    get class() {
        const ret = wasm.reachgrant_class(this.__wbg_ptr);
        return ret;
    }
    /**
     * The 32-byte mailbox key this grant writes to, after verifying its
     * binding against `ownerAccountPk`, the owner's 32-byte account key as
     * the writer holds it pinned (ADR-0078). Call before every write.
     *
     * # Errors
     *
     * Throws with `mailboxError` `badBinding` / `badKey`, or `keyLength`.
     * @param {Uint8Array} owner_account_pk
     * @returns {Uint8Array}
     */
    mailboxPkVerified(owner_account_pk) {
        const ptr0 = passArray8ToWasm0(owner_account_pk, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.reachgrant_mailboxPkVerified(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Build a grant from its four fields.
     *
     * # Errors
     *
     * Throws with `reachError` `seedLength` when `seed` is not 32 bytes,
     * `badBinding` when `binding` is not a well-formed 97-byte binding.
     * @param {ReachClass} _class
     * @param {number} version
     * @param {Uint8Array} seed
     * @param {Uint8Array} binding
     */
    constructor(_class, version, seed, binding) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(binding, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.reachgrant_new(_class, version, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        ReachGrantFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The 32-byte seed. Secret.
     * @returns {Uint8Array}
     */
    get seed() {
        const ret = wasm.reachgrant_seed(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 32-byte public key the holder signs mailbox writes under.
     * @returns {Uint8Array}
     */
    verifyingKey() {
        const ret = wasm.reachgrant_verifyingKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The key version.
     * @returns {number}
     */
    get version() {
        const ret = wasm.reachgrant_version(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) ReachGrant.prototype[Symbol.dispose] = ReachGrant.prototype.free;

/**
 * Every reach key version counter the owner's devices share.
 */
export class ReachState {
    static __wrap(ptr) {
        const obj = Object.create(ReachState.prototype);
        obj.__wbg_ptr = ptr;
        ReachStateFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ReachStateFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_reachstate_free(ptr, 0);
    }
    /**
     * The entries.
     * @returns {ReachStateEntry[]}
     */
    get entries() {
        const ret = wasm.reachstate_entries(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Build a state. Validity is checked by `composeReachStateEnvelope`.
     * @param {ReachStateEntry[]} entries
     * @param {bigint} updated_at_ms
     */
    constructor(entries, updated_at_ms) {
        const ptr0 = passArrayJsValueToWasm0(entries, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.reachstate_new(ptr0, len0, updated_at_ms);
        this.__wbg_ptr = ret;
        ReachStateFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Setter-clock UNIX ms, a `bigint`.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.reachstate_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) ReachState.prototype[Symbol.dispose] = ReachState.prototype.free;

/**
 * One reach key's version counter.
 */
export class ReachStateEntry {
    static __wrap(ptr) {
        const obj = Object.create(ReachStateEntry.prototype);
        obj.__wbg_ptr = ptr;
        ReachStateEntryFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof ReachStateEntry)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ReachStateEntryFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_reachstateentry_free(ptr, 0);
    }
    /**
     * The class.
     * @returns {ReachClass}
     */
    get class() {
        const ret = wasm.reachstateentry_class(this.__wbg_ptr);
        return ret;
    }
    /**
     * Build one. The scope's shape is checked by
     * `composeReachStateEnvelope`.
     * @param {ReachClass} _class
     * @param {Uint8Array} scope_id
     * @param {number} version
     */
    constructor(_class, scope_id, version) {
        const ptr0 = passArray8ToWasm0(scope_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.reachstateentry_new(_class, ptr0, len0, version);
        this.__wbg_ptr = ret;
        ReachStateEntryFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The scope (empty for every class but `Link` / `Reply`).
     * @returns {Uint8Array}
     */
    get scopeId() {
        const ret = wasm.reachstateentry_scopeId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The current key version.
     * @returns {number}
     */
    get version() {
        const ret = wasm.reachstateentry_version(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) ReachStateEntry.prototype[Symbol.dispose] = ReachStateEntry.prototype.free;

/**
 * What `mergeReachState` tells the receiving device to do.
 */
export class ReachStateMerge {
    static __wrap(ptr) {
        const obj = Object.create(ReachStateMerge.prototype);
        obj.__wbg_ptr = ptr;
        ReachStateMergeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ReachStateMergeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_reachstatemerge_free(ptr, 0);
    }
    /**
     * Send `state` to the account's other devices, as it is (never
     * re-stamped).
     * @returns {boolean}
     */
    get send() {
        const ret = wasm.reachstatemerge_send(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The state this device holds after the merge.
     * @returns {ReachState}
     */
    get state() {
        const ret = wasm.reachstatemerge_state(this.__wbg_ptr);
        return ReachState.__wrap(ret);
    }
    /**
     * Persist `state` and re-derive the keys: it differs from what was
     * stored.
     * @returns {boolean}
     */
    get store() {
        const ret = wasm.reachstatemerge_store(this.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) ReachStateMerge.prototype[Symbol.dispose] = ReachStateMerge.prototype.free;

/**
 * One chip under a message.
 */
export class ReactionChip {
    static __wrap(ptr) {
        const obj = Object.create(ReactionChip.prototype);
        obj.__wbg_ptr = ptr;
        ReactionChipFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ReactionChipFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_reactionchip_free(ptr, 0);
    }
    /**
     * How many people hold it.
     * @returns {number}
     */
    get count() {
        const ret = wasm.reactionchip_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The emoji.
     * @returns {string}
     */
    get emoji() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.reactionchip_emoji(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * `"emoji"`, `"count"` or `"avatars"`.
     * @returns {string}
     */
    get face() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.reactionchip_face(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Whether this person holds it.
     * @returns {boolean}
     */
    get mine() {
        const ret = wasm.reactionchip_mine(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The people of an `"avatars"` face, 32-byte keys concatenated; empty
     * otherwise.
     * @returns {Uint8Array}
     */
    get reactors() {
        const ret = wasm.reactionchip_reactors(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ReactionChip.prototype[Symbol.dispose] = ReactionChip.prototype.free;

/**
 * One reaction op on one message.
 */
export class ReactionOp {
    static __unwrap(jsValue) {
        if (!(jsValue instanceof ReactionOp)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ReactionOpFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_reactionop_free(ptr, 0);
    }
    /**
     * `person` the 32-byte account key resolved at receipt, `change`
     * `"add"` / `"remove"`, `at` the op's DS message id.
     *
     * # Errors
     *
     * Throws `reaction.badLength` / `reaction.badChange`.
     * @param {Uint8Array} person
     * @param {string} emoji
     * @param {string} change
     * @param {bigint} at
     */
    constructor(person, emoji, change, at) {
        const ptr0 = passArray8ToWasm0(person, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(change, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.reactionop_new(ptr0, len0, ptr1, len1, ptr2, len2, at);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        ReactionOpFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) ReactionOp.prototype[Symbol.dispose] = ReactionOp.prototype.free;

/**
 * A conversation's read cursor, stamped.
 */
export class ReadCursorRegister {
    static __wrap(ptr) {
        const obj = Object.create(ReadCursorRegister.prototype);
        obj.__wbg_ptr = ptr;
        ReadCursorRegisterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ReadCursorRegisterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_readcursorregister_free(ptr, 0);
    }
    /**
     * The conversation's MLS group id.
     * @returns {Uint8Array}
     */
    get groupId() {
        const ret = wasm.readcursorregister_groupId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * HLC stamp.
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.readcursorregister_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Wall-clock millis of the read. Display only.
     * @returns {bigint}
     */
    get readAtMs() {
        const ret = wasm.readcursorregister_readAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Read up to this message id.
     * @returns {Uint8Array}
     */
    get upToMessageId() {
        const ret = wasm.readcursorregister_upToMessageId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) ReadCursorRegister.prototype[Symbol.dispose] = ReadCursorRegister.prototype.free;

/**
 * The account's recovery email register as held (ADR-0075, amended
 * 2026-10-13), a removal included.
 */
export class RecoveryEmailRegister {
    static __wrap(ptr) {
        const obj = Object.create(RecoveryEmailRegister.prototype);
        obj.__wbg_ptr = ptr;
        RecoveryEmailRegisterFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        RecoveryEmailRegisterFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_recoveryemailregister_free(ptr, 0);
    }
    /**
     * The normalised address, or `undefined` when the register records
     * that the account has no recovery email (a removal).
     * @returns {string | undefined}
     */
    get address() {
        const ret = wasm.recoveryemailregister_address(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * HLC stamp the register was written at.
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.recoveryemailregister_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) RecoveryEmailRegister.prototype[Symbol.dispose] = RecoveryEmailRegister.prototype.free;

/**
 * JS-facing 12-word English BIP39 mnemonic.
 *
 * JS usage:
 *
 * ```js
 * import { RecoveryMnemonic } from "@lakko/ffi-wasm";
 *
 * // Signup: generate fresh words.
 * const m = RecoveryMnemonic.generate();
 * const words = m.words; // string[12]
 * // Display `words` once. Never persist or transmit.
 * const seed = m.toSeed(); // Uint8Array(64) — feed to OpaqueRegister.startRecovery
 *
 * // Recovery: parse the user's typed phrase.
 * const m = RecoveryMnemonic.fromPhrase(typedPhrase); // throws on bad input
 * ```
 */
export class RecoveryMnemonic {
    static __wrap(ptr) {
        const obj = Object.create(RecoveryMnemonic.prototype);
        obj.__wbg_ptr = ptr;
        RecoveryMnemonicFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        RecoveryMnemonicFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_recoverymnemonic_free(ptr, 0);
    }
    /**
     * Reconstruct a mnemonic from raw 16-byte entropy. Inverse
     * of [`RecoveryMnemonic::to_entropy`]; mirror of
     * `lakko_mnemonic::Mnemonic::from_entropy`.
     *
     * # Errors
     *
     * `JsError` when `entropy` is not exactly 16 bytes — every
     * 16-byte buffer is a valid BIP39 entropy (the 4-bit
     * checksum is computed at the constructor, not verified),
     * so this is the only failure path.
     * @param {Uint8Array} entropy
     * @returns {RecoveryMnemonic}
     */
    static fromEntropy(entropy) {
        const ptr0 = passArray8ToWasm0(entropy, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.recoverymnemonic_fromEntropy(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return RecoveryMnemonic.__wrap(ret[0]);
    }
    /**
     * Parse a typed phrase back into a [`RecoveryMnemonic`].
     *
     * Trims and collapses whitespace before splitting; the
     * browser is forgiving about how the user copy-pastes the
     * words.
     *
     * # Errors
     *
     * Throws if the phrase has the wrong word count, contains a
     * non-wordlist token, or fails the BIP39 checksum. The two
     * security-sensitive cases (unknown word vs bad checksum)
     * produce the same JS error message — preserving BIP39's
     * "no probing" property.
     * @param {string} phrase
     * @returns {RecoveryMnemonic}
     */
    static fromPhrase(phrase) {
        const ptr0 = passStringToWasm0(phrase, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.recoverymnemonic_fromPhrase(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return RecoveryMnemonic.__wrap(ret[0]);
    }
    /**
     * Generate a fresh 12-word mnemonic from the OS CSPRNG.
     *
     * On wasm32 the CSPRNG routes through WebCrypto via
     * `getrandom/js`. The function is infallible per the BIP39
     * spec (12 is a valid word count and the OS RNG never
     * refuses on a sane platform).
     * @returns {RecoveryMnemonic}
     */
    static generate() {
        const ret = wasm.recoverymnemonic_generate();
        return RecoveryMnemonic.__wrap(ret);
    }
    /**
     * The mnemonic as a single-space-separated string (12 words,
     * English wordlist). Returns a fresh `String` per call.
     * @returns {string}
     */
    get phrase() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.recoverymnemonic_phrase(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * Extract the 16-byte BIP39 entropy. The passkey vault
     * (ADR-0072) seals this exact buffer, and `fromEntropy`
     * round-trips it back to the same mnemonic for the
     * standard recovery path.
     * @returns {Uint8Array}
     */
    toEntropy() {
        const ret = wasm.recoverymnemonic_toEntropy(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Derive the 64-byte BIP39 PBKDF2 seed.
     *
     * Lakko intentionally calls this with an empty BIP39
     * passphrase — recovery must work with the words alone, so
     * no `passphrase` argument is exposed at the FFI. Hand the
     * returned bytes directly to
     * [`OpaqueRegister.startRecovery`](crate::auth::OpaqueRegister::start_recovery).
     * @returns {Uint8Array}
     */
    toSeed() {
        const ret = wasm.recoverymnemonic_toSeed(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 12 words as a JS array of strings — convenient for
     * rendering a numbered grid in the UI.
     * @returns {string[]}
     */
    get words() {
        const ret = wasm.recoverymnemonic_words(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
}
if (Symbol.dispose) RecoveryMnemonic.prototype[Symbol.dispose] = RecoveryMnemonic.prototype.free;

/**
 * A decoded registrar-seed envelope; the seed is still sealed.
 */
export class RegistrarSeedEnvelope {
    static __wrap(ptr) {
        const obj = Object.create(RegistrarSeedEnvelope.prototype);
        obj.__wbg_ptr = ptr;
        RegistrarSeedEnvelopeFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        RegistrarSeedEnvelopeFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_registrarseedenvelope_free(ptr, 0);
    }
    /**
     * The journal (group id) the registrar key is for.
     * @returns {Uint8Array}
     */
    get journalId() {
        const ret = wasm.registrarseedenvelope_journalId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The addressee's account Ed25519 public key.
     * @returns {Uint8Array}
     */
    get memberAccountKey() {
        const ret = wasm.registrarseedenvelope_memberAccountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `1` member, `2` admin, `3` root handover (ownership transfer).
     * @returns {number}
     */
    get rank() {
        const ret = wasm.registrarseedenvelope_rank(this.__wbg_ptr);
        return ret;
    }
    /**
     * The registrar's Ed25519 public key (32 bytes), readable without
     * opening: the key the Delivery Service knows the registrar by (for a
     * root handover, the root's). Every member records
     * `memberAccountKey -> registrarPublicKey` from an envelope whose MLS
     * sender resolved to the owner, and revokes it when it commits that
     * account's removal. Bound to the seal; `openRegistrarSeedEnvelope`
     * refuses a seed that does not derive it.
     * @returns {Uint8Array}
     */
    get registrarPublicKey() {
        const ret = wasm.registrarseedenvelope_registrarPublicKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) RegistrarSeedEnvelope.prototype[Symbol.dispose] = RegistrarSeedEnvelope.prototype.free;

/**
 * What an arriving call offer does to this device —
 * `docs/ux/calls.md` section 2.1.
 * @enum {0 | 1 | 2}
 */
export const RingDecision = Object.freeze({
    /**
     * Ring: ringtone, vibration, and the call screen that takes over.
     */
    Ring: 0, "0": "Ring",
    /**
     * Record the call, interrupt nobody. The conversation is muted.
     */
    Silent: 1, "1": "Silent",
    /**
     * The caller has already given up. Record a missed call, show nothing.
     */
    TooLate: 2, "2": "TooLate",
});

/**
 * A member's role in an ADR-0022 role-enabled group. Mirror of UniFFI's
 * `Role`. The web gates the add / remove / promote affordances on this.
 * @enum {0 | 1 | 2}
 */
export const Role = Object.freeze({
    /**
     * The group creator — exactly one, unremovable, the only role that may
     * change the role table.
     */
    Owner: 0, "0": "Owner",
    /**
     * Appointed by the owner — may add / remove non-owner members.
     */
    Admin: 1, "1": "Admin",
    /**
     * May not add or remove anyone.
     */
    Member: 2, "2": "Member",
});

/**
 * One saved message as the Saved screen lists it (ADR-0065 section 4).
 */
export class SavedMessageView {
    static __wrap(ptr) {
        const obj = Object.create(SavedMessageView.prototype);
        obj.__wbg_ptr = ptr;
        SavedMessageViewFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SavedMessageViewFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_savedmessageview_free(ptr, 0);
    }
    /**
     * The attachment kind token; empty when the message has none.
     * @returns {string}
     */
    get attachmentKind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.savedmessageview_attachmentKind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The author's display name as shown when saved.
     * @returns {string}
     */
    get author() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.savedmessageview_author(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The blob ids of the message's files and thumbnails,
     * flat-concatenated (32 bytes each).
     * @returns {Uint8Array}
     */
    get blobIds() {
        const ret = wasm.savedmessageview_blobIds(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The conversation the message is in.
     * @returns {FolderConversationRef}
     */
    get conversation() {
        const ret = wasm.savedmessageview_conversation(this.__wbg_ptr);
        return FolderConversationRef.__wrap(ret);
    }
    /**
     * The start of the message text.
     * @returns {string}
     */
    get excerpt() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.savedmessageview_excerpt(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * HLC stamp the save was written at.
     * @returns {bigint}
     */
    get hlc() {
        const ret = wasm.savedmessageview_hlc(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The message's Delivery Service journal id.
     * @returns {bigint}
     */
    get messageId() {
        const ret = wasm.savedmessageview_messageId(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The conversation's MLS group id, or channel id.
     * @returns {Uint8Array}
     */
    get refId() {
        const ret = wasm.savedmessageview_refId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The conversation's kind: `1` one-to-one, `2` group, `3` channel.
     * @returns {number}
     */
    get refKind() {
        const ret = wasm.savedmessageview_refKind(this.__wbg_ptr);
        return ret;
    }
    /**
     * Wall-clock millis of the save.
     * @returns {bigint}
     */
    get savedAtMs() {
        const ret = wasm.savedmessageview_savedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The message's own time in UNIX millis as the saving device showed
     * it; `0` when unknown.
     * @returns {bigint}
     */
    get sentAtMs() {
        const ret = wasm.savedmessageview_sentAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) SavedMessageView.prototype[Symbol.dispose] = SavedMessageView.prototype.free;

/**
 * One group or channel.
 */
export class ScopeId {
    static __wrap(ptr) {
        const obj = Object.create(ScopeId.prototype);
        obj.__wbg_ptr = ptr;
        ScopeIdFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof ScopeId)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ScopeIdFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_scopeid_free(ptr, 0);
    }
    /**
     * The id.
     * @returns {Uint8Array}
     */
    get id() {
        const ret = wasm.scopeid_id(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Group or channel.
     * @returns {ScopeKind}
     */
    get kind() {
        const ret = wasm.scopeid_kind(this.__wbg_ptr);
        return ret;
    }
    /**
     * Build one. The id length (1 to 64 bytes) is checked where it is used.
     * @param {ScopeKind} kind
     * @param {Uint8Array} id
     */
    constructor(kind, id) {
        const ptr0 = passArray8ToWasm0(id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.scopeid_new(kind, ptr0, len0);
        this.__wbg_ptr = ret;
        ScopeIdFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) ScopeId.prototype[Symbol.dispose] = ScopeId.prototype.free;

/**
 * Group or channel. Numeric values are the frozen wire bytes.
 * @enum {1 | 2}
 */
export const ScopeKind = Object.freeze({
    /**
     * A group (MLS group id).
     */
    Group: 1, "1": "Group",
    /**
     * A channel (channel id).
     */
    Channel: 2, "2": "Channel",
});

/**
 * Output of [`WasmParticipantKey::seal`] — paired
 * counter + ciphertext so the JS transformer can embed
 * the counter in the SFrame wire header alongside the
 * ciphertext.
 */
export class SealedFrame {
    static __wrap(ptr) {
        const obj = Object.create(SealedFrame.prototype);
        obj.__wbg_ptr = ptr;
        SealedFrameFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SealedFrameFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_sealedframe_free(ptr, 0);
    }
    /**
     * Ciphertext including the trailing AEAD tag.
     * @returns {Uint8Array}
     */
    get ciphertext() {
        const ret = wasm.sealedframe_ciphertext(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * 64-bit frame counter the seal consumed (a `bigint`
     * in JS). Goes into the SFrame header.
     * @returns {bigint}
     */
    get counter() {
        const ret = wasm.sealedframe_counter(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) SealedFrame.prototype[Symbol.dispose] = SealedFrame.prototype.free;

/**
 * One opened search record: a person or a group / channel.
 */
export class SearchRecord {
    static __wrap(ptr) {
        const obj = Object.create(SearchRecord.prototype);
        obj.__wbg_ptr = ptr;
        SearchRecordFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SearchRecordFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_searchrecord_free(ptr, 0);
    }
    /**
     * The group's or channel's record payload, or `undefined`.
     * @returns {HandleRecordPayload | undefined}
     */
    get conversation() {
        const ret = wasm.searchrecord_conversation(this.__wbg_ptr);
        return ret === 0 ? undefined : HandleRecordPayload.__wrap(ret);
    }
    /**
     * `"person"` or `"conversation"`.
     * @returns {string}
     */
    get kind() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.searchrecord_kind(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The person's public record payload, or `undefined`.
     * @returns {IntroductionPayload | undefined}
     */
    get person() {
        const ret = wasm.searchrecord_person(this.__wbg_ptr);
        return ret === 0 ? undefined : IntroductionPayload.__wrap(ret);
    }
}
if (Symbol.dispose) SearchRecord.prototype[Symbol.dispose] = SearchRecord.prototype.free;

/**
 * Per-participant SFrame key + nonce salt (Phase 3a4).
 * wasm-bindgen class with two getters.
 */
export class SframeParticipantKey {
    static __wrap(ptr) {
        const obj = Object.create(SframeParticipantKey.prototype);
        obj.__wbg_ptr = ptr;
        SframeParticipantKeyFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SframeParticipantKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_sframeparticipantkey_free(ptr, 0);
    }
    /**
     * 32-byte AEAD key.
     * @returns {Uint8Array}
     */
    get keyBytes() {
        const ret = wasm.sframeparticipantkey_keyBytes(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * 12-byte nonce salt — XOR with `(counter as 8 big-endian
     * bytes in the low 8 bytes)` to compose each per-frame
     * nonce (ADR-0069: the counter is 64-bit).
     * @returns {Uint8Array}
     */
    get nonceSalt() {
        const ret = wasm.sframeparticipantkey_nonceSalt(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) SframeParticipantKey.prototype[Symbol.dispose] = SframeParticipantKey.prototype.free;

/**
 * The listed channels one member shares with one group (ADR-0059 §6).
 * Entry `i` is `channelIds[16*i .. 16*i+16]`, `handles[i]`, `names[i]`.
 *
 * **Speaks only for its sender.** Store it per member, keyed on the sender's
 * account key resolved from the MLS sender leaf at receipt (C-68), only when
 * `sharedChannelsIsNewer` than the list stored for that member, under the
 * per-group lock. Every handle is the sender's claim: resolve it through the
 * OPRF lookup to the same channel id before subscribing. An empty list means
 * the sender stopped sharing.
 */
export class SharedChannels {
    static __wrap(ptr) {
        const obj = Object.create(SharedChannels.prototype);
        obj.__wbg_ptr = ptr;
        SharedChannelsFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SharedChannelsFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_sharedchannels_free(ptr, 0);
    }
    /**
     * The channel ids, 16 bytes each, concatenated in list order.
     * @returns {Uint8Array}
     */
    get channelIds() {
        const ret = wasm.sharedchannels_channelIds(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * How many channels the list carries.
     * @returns {number}
     */
    get count() {
        const ret = wasm.sharedchannels_count(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The handles, in list order.
     * @returns {string[]}
     */
    get handles() {
        const ret = wasm.sharedchannels_handles(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The names, in list order.
     * @returns {string[]}
     */
    get names() {
        const ret = wasm.sharedchannels_names(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Sender-clock UNIX milliseconds, as a `bigint`.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.sharedchannels_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) SharedChannels.prototype[Symbol.dispose] = SharedChannels.prototype.free;

/**
 * Which of a remote person's two video tracks a call tile shows
 * (`docs/ux/group-call-grid.md` section 7.5).
 * @enum {0 | 1}
 */
export const ShownLayer = Object.freeze({
    /**
     * The person's main picture.
     */
    Full: 0, "0": "Full",
    /**
     * The light companion.
     */
    Light: 1, "1": "Light",
});

/**
 * Who is speaking in a group call: the ring on each tile and the one person
 * holding the floor, decided by one rule for the three clients
 * (`docs/ux/group-call-grid.md` sections 5.2 and 7.3).
 *
 * One per call. For every person of the roster, at every reading, call
 * `sample` with the level that was read (`undefined` or `null` when it could
 * not be read), or `silence` while they are muted; then `decideFloor` with
 * the roster order, and read `speaking`, `floor` and the floor history back.
 * Levels are the root mean square of decoded samples, `0..1`. Times are
 * plain numbers on one millisecond clock; fractions are dropped and a
 * negative or non-finite time reads as 0.
 */
export class SpeakingTracker {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SpeakingTrackerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_speakingtracker_free(ptr, 0);
    }
    /**
     * Settle who holds the floor after the samples so far, and return them
     * (`undefined` for nobody). `order` is the roster in order, which
     * breaks a tie; this device's own person may be in it and is skipped.
     * @param {string[]} order
     * @param {number} now_ms
     * @returns {string | undefined}
     */
    decideFloor(order, now_ms) {
        const ptr0 = passArrayJsValueToWasm0(order, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.speakingtracker_decideFloor(this.__wbg_ptr, ptr0, len0, now_ms);
        let v2;
        if (ret[0] !== 0) {
            v2 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v2;
    }
    /**
     * Who holds the floor, as last decided. Never this device's own person.
     * @returns {string | undefined}
     */
    floor() {
        const ret = wasm.speakingtracker_floor(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * When each person of `floorHolders` last held the floor, on the
     * caller's clock, in that order.
     * @returns {Float64Array}
     */
    floorHeldAtMs() {
        const ret = wasm.speakingtracker_floorHeldAtMs(this.__wbg_ptr);
        var v1 = getArrayF64FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 8, 8);
        return v1;
    }
    /**
     * Everyone who has held the floor, sorted by id. `floorHeldAtMs` is the
     * same list's times, in the same order.
     * @returns {string[]}
     */
    floorHolders() {
        const ret = wasm.speakingtracker_floorHolders(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The person left the call: everything about them is dropped.
     * @param {string} person
     */
    forget(person) {
        const ptr0 = passStringToWasm0(person, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.speakingtracker_forget(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Whether the person's ring is on.
     * @param {string} person
     * @returns {boolean}
     */
    isSpeaking(person) {
        const ptr0 = passStringToWasm0(person, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.speakingtracker_isSpeaking(this.__wbg_ptr, ptr0, len0);
        return ret !== 0;
    }
    /**
     * Forget everyone who is not one of `people`, the roster as it is now.
     * @param {string[]} people
     */
    keepOnly(people) {
        const ptr0 = passArrayJsValueToWasm0(people, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.speakingtracker_keepOnly(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * When the person last held the floor, or `undefined` for someone who
     * never has.
     * @param {string} person
     * @returns {number | undefined}
     */
    lastHeldFloorMs(person) {
        const ptr0 = passStringToWasm0(person, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.speakingtracker_lastHeldFloorMs(this.__wbg_ptr, ptr0, len0);
        return ret[0] === 0 ? undefined : ret[1];
    }
    /**
     * A tracker of a call nobody has been heard in.
     */
    constructor() {
        const ret = wasm.speakingtracker_new();
        this.__wbg_ptr = ret;
        SpeakingTrackerFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * One level reading for one person at `nowMs`. No level is "could not
     * be read": silence for the ring, and nothing for the person's noise
     * floor. A level that is not a number is silence too.
     * @param {string} person
     * @param {number | null | undefined} level
     * @param {number} now_ms
     */
    sample(person, level, now_ms) {
        const ptr0 = passStringToWasm0(person, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.speakingtracker_sample(this.__wbg_ptr, ptr0, len0, !isLikeNone(level), isLikeNone(level) ? 0 : level, now_ms);
    }
    /**
     * Name this device's own person (`undefined` for none). They are
     * sampled and have a ring like anyone, and never hold the floor,
     * wherever they stand in the order given to `decideFloor`.
     * @param {string | null} [person]
     */
    setLocal(person) {
        var ptr0 = isLikeNone(person) ? 0 : passStringToWasm0(person, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        wasm.speakingtracker_setLocal(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * The person is muted: their ring goes off at once and they give up the
     * floor. What was learned about their room is kept.
     * @param {string} person
     */
    silence(person) {
        const ptr0 = passStringToWasm0(person, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.speakingtracker_silence(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Everyone whose ring is on, this device's own person included, sorted
     * by id.
     * @returns {string[]}
     */
    speaking() {
        const ret = wasm.speakingtracker_speaking(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * What counts as sound for the person now, on the scale of the levels
     * (for the diagnostics line).
     * @param {string} person
     * @returns {number}
     */
    threshold(person) {
        const ptr0 = passStringToWasm0(person, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.speakingtracker_threshold(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
}
if (Symbol.dispose) SpeakingTracker.prototype[Symbol.dispose] = SpeakingTracker.prototype.free;

/**
 * Result of [`Group::stage_pending_proposals`] — an ADR-0045 sweep that has
 * been **built but not merged**. wasm-bindgen mirror of UniFFI's
 * `StagedSweep`.
 *
 * Carries byte-for-byte what [`ProposalCommit`] carries, and is a **separate
 * class on purpose**: the payloads are identical but the post-conditions are
 * opposite. Holding a `ProposalCommit` means the local epoch has **already**
 * advanced; holding a `StagedSweep` means it has **not**, and that this device
 * owes the core a verdict. Making that visible in the type means a staged
 * sweep cannot be handed to eager-path code — or the reverse — without a
 * deliberate, reviewer-visible conversion.
 *
 * The obligation attached to this value:
 *
 * 1. durably record `commit` (and `welcome`) — ADR-0040's outbox;
 * 2. publish them to the group journal;
 * 3. read the journal order back;
 * 4. **won** → [`Group::merge_staged_sweep`]; **lost** →
 *    [`Group::abandon_staged_sweep`].
 *
 * Steps 3 and 4 are not optional. A staged sweep is persisted by OpenMLS, so
 * leaving one unadjudicated freezes this group's own membership operations
 * **across restarts** — see [`Group::has_staged_sweep`], which is how a
 * freshly loaded group discovers it owes a verdict.
 */
export class StagedSweep {
    static __wrap(ptr) {
        const obj = Object.create(StagedSweep.prototype);
        obj.__wbg_ptr = ptr;
        StagedSweepFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        StagedSweepFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_stagedsweep_free(ptr, 0);
    }
    /**
     * Encoded MLS commit message — publish to all existing members. The local
     * epoch has **not** advanced; it advances only on
     * `group.mergeStagedSweep(...)`.
     * @returns {Uint8Array}
     */
    get commit() {
        const ret = wasm.stagedsweep_commit(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The proposals this commit carried, in sweep order, as a flat array of
     * [`ProposalRecord`] instances (same convention as `Group.members()` —
     * wasm-bindgen cannot return a `Vec` of a custom struct through a getter,
     * so this is a method, not a property).
     *
     * Read back off the staged commit by the core, so it reports what the
     * commit really carries rather than what the caller predicted it would.
     * @returns {any[]}
     */
    swept() {
        const ret = wasm.stagedsweep_swept(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Encoded MLS welcome message — delivered to the newly added members.
     * `undefined` unless the sweep carried at least one `add`.
     * @returns {Uint8Array | undefined}
     */
    get welcome() {
        const ret = wasm.stagedsweep_welcome(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
}
if (Symbol.dispose) StagedSweep.prototype[Symbol.dispose] = StagedSweep.prototype.free;

/**
 * What a verdict call did to the sweep staged by
 * [`Group::stage_pending_proposals`] (ADR-0045). Mirror of UniFFI's
 * `StagedSweepOutcome`.
 *
 * Named rather than a bare `boolean`, because the third state is the one that
 * matters: [`StagedSweepOutcome::NothingStaged`] is **not** a failure — it is
 * what a verdict reports on a group that never staged anything, and it is what
 * makes both verdict entry points idempotent, so ADR-0040's restart sweep can
 * run them twice without consequence.
 *
 * Each entry point produces only two of the three variants:
 *
 * | Call | Possible outcomes |
 * |---|---|
 * | `group.mergeStagedSweep(...)` | `Merged`, `NothingStaged` |
 * | `group.abandonStagedSweep(...)` | `Abandoned`, `NothingStaged` |
 *
 * **The discriminants deliberately start at 1**, so no variant is JavaScript's
 * falsy `0`. `if (outcome)` is therefore never silently false for a real
 * outcome — the same class of trap `ownCommitterRank` documents for rank `0`.
 * @enum {1 | 2 | 3}
 */
export const StagedSweepOutcome = Object.freeze({
    /**
     * A staged sweep was merged: this device is now at the new epoch — the
     * same one the peers who applied the published commit reached.
     */
    Merged: 1, "1": "Merged",
    /**
     * A staged sweep was discarded: this device stayed at the epoch the sweep
     * was built against, and is free to apply the winner's commit. The
     * proposals the sweep selected are still pending for the winner to sweep.
     */
    Abandoned: 2, "2": "Abandoned",
    /**
     * Nothing was staged, so nothing was decided and no state changed.
     */
    NothingStaged: 3, "3": "NothingStaged",
});

/**
 * Long-lived stranger-inbox pseudonym. Mirror of
 * [`lakko_contacts::StrangerInbox`].
 */
export class StrangerInbox {
    static __wrap(ptr) {
        const obj = Object.create(StrangerInbox.prototype);
        obj.__wbg_ptr = ptr;
        StrangerInboxFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        StrangerInboxFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_strangerinbox_free(ptr, 0);
    }
    /**
     * 32 raw bytes.
     * @returns {Uint8Array}
     */
    asBytes() {
        const ret = wasm.strangerinbox_asBytes(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Derive from the owner's 32-byte stranger-inbox secret.
     * @param {Uint8Array} secret
     * @returns {StrangerInbox}
     */
    static derive(secret) {
        const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.strangerinbox_derive(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return StrangerInbox.__wrap(ret[0]);
    }
}
if (Symbol.dispose) StrangerInbox.prototype[Symbol.dispose] = StrangerInbox.prototype.free;

/**
 * What a subscriber invitation carries beyond the channel id and name.
 */
export class SubscriberInvite {
    static __wrap(ptr) {
        const obj = Object.create(SubscriberInvite.prototype);
        obj.__wbg_ptr = ptr;
        SubscriberInviteFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SubscriberInviteFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_subscriberinvite_free(ptr, 0);
    }
    /**
     * Private or public, with the keys each needs.
     * @returns {ChannelAccess}
     */
    get access() {
        const ret = wasm.subscriberinvite_access(this.__wbg_ptr);
        return ChannelAccess.__wrap(ret);
    }
    /**
     * The channel description; may be empty.
     * @returns {string}
     */
    get description() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.subscriberinvite_description(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) SubscriberInvite.prototype[Symbol.dispose] = SubscriberInvite.prototype.free;

/**
 * One part of the catch-up re-send (ADR-0058 section 5). Get parts from
 * [`SyncedState::snapshot_parts`]; send each with
 * `DeviceGroup.createDeviceGroupSnapshotMessage`.
 */
export class SyncedSnapshotPart {
    static __wrap(ptr) {
        const obj = Object.create(SyncedSnapshotPart.prototype);
        obj.__wbg_ptr = ptr;
        SyncedSnapshotPartFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SyncedSnapshotPartFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_syncedsnapshotpart_free(ptr, 0);
    }
    /**
     * Number of registers this part carries.
     * @returns {number}
     */
    get entryCount() {
        const ret = wasm.syncedsnapshotpart_entryCount(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * 0-based part index.
     * @returns {number}
     */
    get part() {
        const ret = wasm.syncedsnapshotpart_part(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * Number of parts in this re-send.
     * @returns {number}
     */
    get parts() {
        const ret = wasm.syncedsnapshotpart_parts(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) SyncedSnapshotPart.prototype[Symbol.dispose] = SyncedSnapshotPart.prototype.free;

/**
 * The folded synced state of this device (ADR-0058 section 3).
 *
 * Commutative, associative and idempotent: feed it every DeviceGroup
 * payload in whatever order they arrive, and every local write (a device
 * never receives its own messages back), and two devices that have seen
 * the same writes hold the same state.
 */
export class SyncedState {
    static __wrap(ptr) {
        const obj = Object.create(SyncedState.prototype);
        obj.__wbg_ptr = ptr;
        SyncedStateFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SyncedStateFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_syncedstate_free(ptr, 0);
    }
    /**
     * Record that the person has seen the "a device was added to your
     * account" warning (identity-recovery 17.5) for each device whose
     * 32-byte signature key is in `device_keys` (flat-concatenated),
     * stamped `hlc`, at wall-clock `now_ms`. Write-once per key. Returns
     * the envelopes to send in DeviceGroup service envelopes, carrying
     * exactly what this call wrote (empty when nothing was new). Throws
     * `"bounds: ..."` when the length is not a multiple of 32.
     * @param {bigint} hlc
     * @param {Uint8Array} device_keys
     * @param {bigint} now_ms
     * @returns {Uint8Array[]}
     */
    acknowledgeDevices(hlc, device_keys, now_ms) {
        const ptr0 = passArray8ToWasm0(device_keys, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_acknowledgeDevices(this.__wbg_ptr, hlc, ptr0, len0, now_ms);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * Every acknowledged device key, ascending, flat-concatenated (32 bytes
     * each).
     * @returns {Uint8Array}
     */
    acknowledgedDevices() {
        const ret = wasm.syncedstate_acknowledgedDevices(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Every draft register, cleared ones included.
     * @returns {DraftRegister[]}
     */
    allDrafts() {
        const ret = wasm.syncedstate_allDrafts(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Every flags register.
     * @returns {FlagsRegister[]}
     */
    allFlags() {
        const ret = wasm.syncedstate_allFlags(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Every lineage register, ascending by successor id.
     * @returns {LineageRegister[]}
     */
    allLineage() {
        const ret = wasm.syncedstate_allLineage(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Every link record (revoked ones included), ascending by link id.
     * @returns {LinkRecord[]}
     */
    allLinkRecords() {
        const ret = wasm.syncedstate_allLinkRecords(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Every read-cursor register.
     * @returns {ReadCursorRegister[]}
     */
    allReadCursors() {
        const ret = wasm.syncedstate_allReadCursors(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Fold in an own-channel-comment envelope. Throws `"<code>: <reason>"`.
     * @param {Uint8Array} envelope
     * @returns {boolean}
     */
    applyChannelCommentEnvelope(envelope) {
        const ptr0 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyChannelCommentEnvelope(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Fold in an own-channel-reaction envelope. Throws `"<code>: <reason>"`.
     * @param {Uint8Array} envelope
     * @returns {boolean}
     */
    applyChannelReactionEnvelope(envelope) {
        const ptr0 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyChannelReactionEnvelope(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Fold a local conversation-order write in.
     * @param {bigint} hlc
     * @param {Uint8Array[]} group_ids
     * @returns {boolean}
     */
    applyConversationOrder(hlc, group_ids) {
        const ptr0 = passArrayJsValueToWasm0(group_ids, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyConversationOrder(this.__wbg_ptr, hlc, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Decode a device-acknowledgement envelope received in a DeviceGroup
     * service envelope (route on `isDeviceAckEnvelope`) and fold every
     * register in. Fail closed. Returns whether anything changed. Throws an
     * `Error` whose message is `"<code>: <reason>"` with code `wrongDomain`
     * / `unknownVersion` / `count` / `truncated` / `notCanonical` /
     * `trailing`.
     * @param {Uint8Array} envelope
     * @returns {boolean}
     */
    applyDeviceAckEnvelope(envelope) {
        const ptr0 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyDeviceAckEnvelope(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Fold a local draft write in (empty `text` clears).
     * @param {bigint} hlc
     * @param {Uint8Array} group_id
     * @param {string} text
     * @param {Uint8Array} reply_to_id
     * @returns {boolean}
     */
    applyDraft(hlc, group_id, text, reply_to_id) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(reply_to_id, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyDraft(this.__wbg_ptr, hlc, ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Fold a local flags write in (the same values sent with
     * `createDeviceGroupFlagsMessage` at the same `hlc`).
     * @param {bigint} hlc
     * @param {Uint8Array} group_id
     * @param {boolean} pinned
     * @param {boolean} muted
     * @param {boolean} archived
     * @param {boolean} unread_override
     * @returns {boolean}
     */
    applyFlags(hlc, group_id, pinned, muted, archived, unread_override) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyFlags(this.__wbg_ptr, hlc, ptr0, len0, pinned, muted, archived, unread_override);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Fold a local folder edit, or a local Save / Remove of a saved
     * message, in (the change set the client then sends). Throws when it
     * breaks a bound.
     * @param {FolderChangeSet} changes
     * @returns {boolean}
     */
    applyFolderChanges(changes) {
        _assertClass(changes, FolderChangeSet);
        const ret = wasm.syncedstate_applyFolderChanges(this.__wbg_ptr, changes.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Decode a lineage envelope received in a DeviceGroup service envelope
     * (route on `isLineageEnvelope`) and fold every register in with its
     * original stamp. Fail closed: a malformed envelope changes nothing.
     * Returns whether anything changed. Throws an `Error` whose message is
     * `"<code>: <reason>"` with code `wrongDomain` / `unknownVersion` /
     * `truncated` / `trailing` / `count` / `notCanonical` / `bounds`.
     * @param {Uint8Array} envelope
     * @returns {boolean}
     */
    applyLineageEnvelope(envelope) {
        const ptr0 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyLineageEnvelope(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Decode a link-record envelope (route on `isLinkRecordEnvelope`) and
     * fold every register in. Fail closed. Returns whether anything
     * changed. Throws `"<code>: <reason>"` with code `wrongDomain` /
     * `unknownVersion` / `truncated` / `trailing` / `count` /
     * `notCanonical` / `entry`.
     * @param {Uint8Array} envelope
     * @returns {boolean}
     */
    applyLinkRecordEnvelope(envelope) {
        const ptr0 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyLinkRecordEnvelope(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Decode an own-username envelope received in a DeviceGroup service
     * envelope (route on `isOwnUsernameEnvelope`) and fold the register in
     * with its original stamp. Fail closed: a malformed envelope changes
     * nothing. Returns whether anything changed. Throws an `Error` whose
     * message is `"<code>: <reason>"` with code `wrongDomain` /
     * `unknownVersion` / `truncated` / `trailing` / `notUtf8` / `bounds`.
     * @param {Uint8Array} envelope
     * @returns {boolean}
     */
    applyOwnUsernameEnvelope(envelope) {
        const ptr0 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyOwnUsernameEnvelope(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Fold a parsed DeviceGroup payload in, whatever its kind. Returns
     * whether anything changed.
     * @param {ParsedDeviceGroupPayload} payload
     * @returns {boolean}
     */
    applyPayload(payload) {
        _assertClass(payload, ParsedDeviceGroupPayload);
        const ret = wasm.syncedstate_applyPayload(this.__wbg_ptr, payload.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Fold in a quick-reaction envelope (route on `isQuickReactionEnvelope`).
     * Throws `"<code>: <reason>"`.
     * @param {Uint8Array} envelope
     * @returns {boolean}
     */
    applyQuickReactionEnvelope(envelope) {
        const ptr0 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyQuickReactionEnvelope(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Fold a local read-cursor write in.
     * @param {bigint} hlc
     * @param {Uint8Array} group_id
     * @param {Uint8Array} up_to_message_id
     * @param {bigint} read_at_ms
     * @returns {boolean}
     */
    applyReadCursor(hlc, group_id, up_to_message_id, read_at_ms) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(up_to_message_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyReadCursor(this.__wbg_ptr, hlc, ptr0, len0, ptr1, len1, read_at_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Decode a recovery-email envelope received in a DeviceGroup service
     * envelope (route on `isRecoveryEmailEnvelope`) and fold the register
     * in with its original stamp. Fail closed: a malformed envelope changes
     * nothing. Returns whether anything changed. Throws an `Error` whose
     * message is `"<code>: <reason>"` with code `wrongDomain` /
     * `unknownVersion` / `truncated` / `trailing` / `notUtf8` / `bounds`.
     * @param {Uint8Array} envelope
     * @returns {boolean}
     */
    applyRecoveryEmailEnvelope(envelope) {
        const ptr0 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_applyRecoveryEmailEnvelope(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Every held own comment record as envelopes (the catch-up re-send).
     * @returns {Uint8Array[]}
     */
    channelCommentEnvelopes() {
        const ret = wasm.syncedstate_channelCommentEnvelopes(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The nonces of this account's live comments under the post,
     * concatenated (16 bytes each).
     * @param {Uint8Array} channel_id
     * @param {bigint} post_cursor
     * @returns {Uint8Array}
     */
    channelCommentsOn(channel_id, post_cursor) {
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_channelCommentsOn(this.__wbg_ptr, ptr0, len0, post_cursor);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Every held own channel reaction as envelopes (the catch-up re-send).
     * @returns {Uint8Array[]}
     */
    channelReactionEnvelopes() {
        const ret = wasm.syncedstate_channelReactionEnvelopes(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The emoji this account holds on the post.
     * @param {Uint8Array} channel_id
     * @param {bigint} post_cursor
     * @returns {string[]}
     */
    channelReactionsOn(channel_id, post_cursor) {
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_channelReactionsOn(this.__wbg_ptr, ptr0, len0, post_cursor);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * The register to write now that this account's membership has ended,
     * or `undefined` when there is nothing to tell the other devices (the
     * register already says so, the account has rejoined at a later epoch, or
     * `conversationEndsMaxClient()` are held). `epoch` is the epoch the group
     * froze at, `0n` for a channel. `undefined` never stops the end itself:
     * the device still wipes the conversation. Folds nothing. Throws on a
     * conversation reference that is not well formed.
     * @param {bigint} hlc
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @param {bigint} epoch
     * @returns {ConversationEndRegister | undefined}
     */
    composeConversationEnded(hlc, ref_kind, ref_id, epoch) {
        const ptr0 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_composeConversationEnded(this.__wbg_ptr, hlc, ref_kind, ptr0, len0, epoch);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : ConversationEndRegister.__wrap(ret[0]);
    }
    /**
     * The register to write now that this account has joined a conversation,
     * or `undefined` when no end is held to clear: a first join writes
     * nothing. `epoch` is the epoch the Welcome joined at, `0n` for a
     * channel. Folds nothing. Throws on a conversation reference that is not
     * well formed.
     * @param {bigint} hlc
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @param {bigint} epoch
     * @returns {ConversationEndRegister | undefined}
     */
    composeConversationJoined(hlc, ref_kind, ref_id, epoch) {
        const ptr0 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_composeConversationJoined(this.__wbg_ptr, hlc, ref_kind, ptr0, len0, epoch);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : ConversationEndRegister.__wrap(ret[0]);
    }
    /**
     * The register held for a conversation, a "joined" included; `undefined`
     * when no end ever reached it or the reference is not well formed.
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @returns {ConversationEndRegister | undefined}
     */
    conversationEnd(ref_kind, ref_id) {
        const ptr0 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_conversationEnd(this.__wbg_ptr, ref_kind, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : ConversationEndRegister.__wrap(ret[0]);
    }
    /**
     * The conversation id a group belongs to: the root its lineage register
     * names, or `groupId` itself when it holds none.
     * @param {Uint8Array} group_id
     * @returns {Uint8Array}
     */
    conversationIdOf(group_id) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_conversationIdOf(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * The explicit conversation order, or `undefined`.
     * @returns {ConversationOrderRegister | undefined}
     */
    conversationOrder() {
        const ret = wasm.syncedstate_conversationOrder(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : ConversationOrderRegister.__wrap(ret[0]);
    }
    /**
     * Record that this account deleted one of its comments. Returns the
     * envelope to send, or `undefined`.
     * @param {bigint} hlc
     * @param {Uint8Array} channel_id
     * @param {bigint} post_cursor
     * @param {Uint8Array} comment_nonce
     * @param {bigint} post_at_ms
     * @returns {Uint8Array | undefined}
     */
    deleteChannelComment(hlc, channel_id, post_cursor, comment_nonce, post_at_ms) {
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(comment_nonce, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_deleteChannelComment(this.__wbg_ptr, hlc, ptr0, len0, post_cursor, ptr1, len1, post_at_ms);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v3;
        if (ret[0] !== 0) {
            v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v3;
    }
    /**
     * Every held acknowledgement as envelopes: the re-send a device owes
     * when a live send did not go out.
     * @returns {Uint8Array[]}
     */
    deviceAckEnvelopes() {
        const ret = wasm.syncedstate_deviceAckEnvelopes(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Draft held for a conversation (a cleared one included), or
     * `undefined`.
     * @param {Uint8Array} group_id
     * @returns {DraftRegister | undefined}
     */
    draft(group_id) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_draft(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : DraftRegister.__wrap(ret[0]);
    }
    /**
     * Every conversation whose register says ended, by `(kind, id)`.
     * @returns {ConversationEndRegister[]}
     */
    endedConversations() {
        const ret = wasm.syncedstate_endedConversations(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Flags held for a conversation, or `undefined`.
     * @param {Uint8Array} group_id
     * @returns {FlagsRegister | undefined}
     */
    flags(group_id) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_flags(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : FlagsRegister.__wrap(ret[0]);
    }
    /**
     * Every live folder, in the resolved order.
     * @returns {FolderView[]}
     */
    folders() {
        const ret = wasm.syncedstate_folders(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Restore a state persisted with [`Self::to_bytes`] or carried in a
     * backup payload. Throws on a malformed or out-of-bounds entry: the
     * whole state is refused.
     * @param {Uint8Array} bytes
     * @returns {SyncedState}
     */
    static fromBytes(bytes) {
        const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_fromBytes(ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return SyncedState.__wrap(ret[0]);
    }
    /**
     * Whether this account holds `emoji` on the post.
     * @param {Uint8Array} channel_id
     * @param {bigint} post_cursor
     * @param {string} emoji
     * @returns {boolean}
     */
    hasChannelReaction(channel_id, post_cursor, emoji) {
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_hasChannelReaction(this.__wbg_ptr, ptr0, len0, post_cursor, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Whether this account's membership of a conversation has ended. `false`
     * for one never ended, for one joined again since, and for a reference
     * that is not well formed.
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @returns {boolean}
     */
    isConversationEnded(ref_kind, ref_id) {
        const ptr0 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_isConversationEnded(this.__wbg_ptr, ref_kind, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Whether the added-device warning for `deviceKey` was seen on some
     * device of the account. A key of another length is never acknowledged.
     * @param {Uint8Array} device_key
     * @returns {boolean}
     */
    isDeviceAcknowledged(device_key) {
        const ptr0 = passArray8ToWasm0(device_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_isDeviceAcknowledged(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Whether this message is saved now (the row label and the saved
     * mark). `false` for a message never saved, for a removed one, and for
     * a conversation reference that is not well formed.
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @param {bigint} message_id
     * @returns {boolean}
     */
    isSaved(ref_kind, ref_id, message_id) {
        const ptr0 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_isSaved(this.__wbg_ptr, ref_kind, ptr0, len0, message_id);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * The envelope carrying the one register held for `successorGroupId`
     * with its original stamp, or `undefined` when none is held: the live
     * send after `setLineage` returned `true`.
     * @param {Uint8Array} successor_group_id
     * @returns {Uint8Array | undefined}
     */
    lineageEnvelopeFor(successor_group_id) {
        const ptr0 = passArray8ToWasm0(successor_group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_lineageEnvelopeFor(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v2;
        if (ret[0] !== 0) {
            v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v2;
    }
    /**
     * Every held lineage register with its original stamp, as service
     * envelopes: the catch-up re-send beside `snapshotParts()` (lineage
     * registers are NOT in the snapshot parts). Empty when none is held.
     * @returns {Uint8Array[]}
     */
    lineageEnvelopes() {
        const ret = wasm.syncedstate_lineageEnvelopes(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The lineage register held for a successor group, or `undefined` (the
     * group is a root, or this device knows no lineage for it).
     * @param {Uint8Array} successor_group_id
     * @returns {LineageRegister | undefined}
     */
    lineageRoot(successor_group_id) {
        const ptr0 = passArray8ToWasm0(successor_group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_lineageRoot(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : LineageRegister.__wrap(ret[0]);
    }
    /**
     * Every successor recorded for one conversation, ordered by `(stamp,
     * successor id)`. The root itself is not listed.
     * @param {Uint8Array} root_conversation_id
     * @returns {LineageRegister[]}
     */
    lineageSuccessors(root_conversation_id) {
        const ptr0 = passArray8ToWasm0(root_conversation_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_lineageSuccessors(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v2;
    }
    /**
     * The one rule for a contact request carrying a link answer (ADR-0079
     * section 3 as amended 2026-10-19): `"accept"` (run the accept path
     * with no card, then `recordLinkUse`), `"answered"` (show nothing),
     * `"dead"` (drop the request silently and answer with a link refusal)
     * or `"unknown"` (no record of the link here: keep the request
     * undecided). `pressedAtMs` is the time the Delivery Service stamped on
     * the request row: expiry is judged then, revocation and uses now. The
     * secret is compared in constant time; an answer of the wrong width is
     * `"dead"`.
     * @param {Uint8Array} link_id
     * @param {Uint8Array} link_secret
     * @param {Uint8Array} requester_key
     * @param {bigint} pressed_at_ms
     * @returns {string}
     */
    linkAnswerVerdict(link_id, link_secret, requester_key, pressed_at_ms) {
        let deferred5_0;
        let deferred5_1;
        try {
            const ptr0 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
            const len0 = WASM_VECTOR_LEN;
            const ptr1 = passArray8ToWasm0(link_secret, wasm.__wbindgen_malloc);
            const len1 = WASM_VECTOR_LEN;
            const ptr2 = passArray8ToWasm0(requester_key, wasm.__wbindgen_malloc);
            const len2 = WASM_VECTOR_LEN;
            const ret = wasm.syncedstate_linkAnswerVerdict(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, pressed_at_ms);
            var ptr4 = ret[0];
            var len4 = ret[1];
            if (ret[3]) {
                ptr4 = 0; len4 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred5_0 = ptr4;
            deferred5_1 = len4;
            return getStringFromWasm0(ptr4, len4);
        } finally {
            wasm.__wbindgen_free(deferred5_0, deferred5_1, 1);
        }
    }
    /**
     * The record held for a link id, or `undefined`.
     * @param {Uint8Array} link_id
     * @returns {LinkRecord | undefined}
     */
    linkRecord(link_id) {
        const ptr0 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_linkRecord(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : LinkRecord.__wrap(ret[0]);
    }
    /**
     * The service envelope carrying one held link record with its original
     * stamp, or `undefined`.
     * @param {Uint8Array} link_id
     * @returns {Uint8Array | undefined}
     */
    linkRecordEnvelopeFor(link_id) {
        const ptr0 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_linkRecordEnvelopeFor(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v2;
        if (ret[0] !== 0) {
            v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v2;
    }
    /**
     * Every held link record as service envelopes: the catch-up re-send
     * beside `snapshotParts()` (link records are NOT in the parts).
     * @returns {Uint8Array[]}
     */
    linkRecordEnvelopes() {
        const ret = wasm.syncedstate_linkRecordEnvelopes(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Mark a link copied (final, 2026-10-26): it is no longer editable.
     * `true` when the register changed (send its envelope).
     * @param {bigint} hlc
     * @param {Uint8Array} link_id
     * @returns {boolean}
     */
    markLinkShared(hlc, link_id) {
        const ptr0 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_markLinkShared(this.__wbg_ptr, hlc, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * The greatest stamp held, or 0. Seed the HLC above it after a restore.
     * @returns {bigint}
     */
    maxHlc() {
        const ret = wasm.syncedstate_maxHlc(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return BigInt.asUintN(64, ret[0]);
    }
    /**
     * Fold every register `other` holds into this state, each with its
     * original stamp (a restore merges the backup's state this way).
     * Returns whether anything changed.
     * @param {SyncedState} other
     * @returns {boolean}
     */
    mergeFrom(other) {
        _assertClass(other, SyncedState);
        const ret = wasm.syncedstate_mergeFrom(this.__wbg_ptr, other.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * An empty state.
     */
    constructor() {
        const ret = wasm.syncedstate_new();
        this.__wbg_ptr = ret;
        SyncedStateFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The account's own username, or `undefined` when the register was
     * never written or holds "none".
     * @returns {string | undefined}
     */
    ownUsername() {
        const ret = wasm.syncedstate_ownUsername(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The service envelope carrying the held register with its original
     * stamp, a "none" included: the live send after `setOwnUsername`
     * returned `true`, and the catch-up re-send beside `snapshotParts()`,
     * `lineageEnvelopes()` and `recoveryEmailEnvelope()` (the register is
     * NOT in the snapshot parts). `undefined` when the register was never
     * written.
     * @returns {Uint8Array | undefined}
     */
    ownUsernameEnvelope() {
        const ret = wasm.syncedstate_ownUsernameEnvelope(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The own-username register as held, a "none" included (`username`
     * undefined, with its stamp); `undefined` when it was never written.
     * @returns {OwnUsernameRegister | undefined}
     */
    ownUsernameRegister() {
        const ret = wasm.syncedstate_ownUsernameRegister(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : OwnUsernameRegister.__wrap(ret[0]);
    }
    /**
     * Drop own channel reactions and comments past the 30-day retention at
     * `nowMs`; returns how many registers were dropped.
     * @param {bigint} now_ms
     * @returns {number}
     */
    pruneChannelActivity(now_ms) {
        const ret = wasm.syncedstate_pruneChannelActivity(this.__wbg_ptr, now_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * The account's quick reaction, or `undefined` (then use
     * `defaultQuickReaction()`).
     * @returns {string | undefined}
     */
    quickReaction() {
        const ret = wasm.syncedstate_quickReaction(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The envelope carrying the quick reaction with its original stamp, or
     * `undefined` when never written.
     * @returns {Uint8Array | undefined}
     */
    quickReactionEnvelope() {
        const ret = wasm.syncedstate_quickReactionEnvelope(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Read cursor held for a conversation, or `undefined`.
     * @param {Uint8Array} group_id
     * @returns {ReadCursorRegister | undefined}
     */
    readCursor(group_id) {
        const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_readCursor(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : ReadCursorRegister.__wrap(ret[0]);
    }
    /**
     * Record a comment this account wrote: its 16-byte `commentNonce` under
     * the post. Returns the envelope to send, or `undefined`.
     * @param {bigint} hlc
     * @param {Uint8Array} channel_id
     * @param {bigint} post_cursor
     * @param {Uint8Array} comment_nonce
     * @param {bigint} post_at_ms
     * @returns {Uint8Array | undefined}
     */
    recordChannelComment(hlc, channel_id, post_cursor, comment_nonce, post_at_ms) {
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(comment_nonce, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_recordChannelComment(this.__wbg_ptr, hlc, ptr0, len0, post_cursor, ptr1, len1, post_at_ms);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v3;
        if (ret[0] !== 0) {
            v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v3;
    }
    /**
     * Count one request accepted through a link from `requesterKey` (the
     * request's 32-byte `fromPubkey`). `true` when the register changed.
     * @param {bigint} hlc
     * @param {Uint8Array} link_id
     * @param {Uint8Array} requester_key
     * @returns {boolean}
     */
    recordLinkUse(hlc, link_id, requester_key) {
        const ptr0 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(requester_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_recordLinkUse(this.__wbg_ptr, hlc, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * The account's recovery email address, or `undefined` when the
     * register was never written or holds a removal.
     * @returns {string | undefined}
     */
    recoveryEmail() {
        const ret = wasm.syncedstate_recoveryEmail(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The service envelope carrying the held register with its original
     * stamp, a removal included: the live send after `setRecoveryEmail`
     * returned `true`, and the catch-up re-send beside `snapshotParts()`
     * and `lineageEnvelopes()` (the register is NOT in the snapshot parts).
     * `undefined` when the register was never written.
     * @returns {Uint8Array | undefined}
     */
    recoveryEmailEnvelope() {
        const ret = wasm.syncedstate_recoveryEmailEnvelope(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The recovery email register as held, a removal included (`address`
     * undefined, with its stamp); `undefined` when it was never written.
     * @returns {RecoveryEmailRegister | undefined}
     */
    recoveryEmailRegister() {
        const ret = wasm.syncedstate_recoveryEmailRegister(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] === 0 ? undefined : RecoveryEmailRegister.__wrap(ret[0]);
    }
    /**
     * Mark a link revoked (final). `true` when the register changed.
     * @param {bigint} hlc
     * @param {Uint8Array} link_id
     * @returns {boolean}
     */
    revokeLinkRecord(hlc, link_id) {
        const ptr0 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_revokeLinkRecord(this.__wbg_ptr, hlc, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * The account's saved messages, most recently saved first (ADR-0065
     * section 4). Removed ones are not listed.
     * @returns {SavedMessageView[]}
     */
    saved() {
        const ret = wasm.syncedstate_saved(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Every blob id a saved message names, each once, in bytewise order,
     * flat-concatenated (32 bytes each): what this device sends keeps for
     * (ADR-0065 section 3).
     * @returns {Uint8Array}
     */
    savedBlobIds() {
        const ret = wasm.syncedstate_savedBlobIds(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Record this account's reaction `emoji` (`reacted`) or its removal on
     * the post at `postCursor` of the 16-byte `channelId`, posted at
     * `postAtMs` (ADR-0087 section 13a). Returns the envelope to send, or
     * `undefined` when nothing changed. Throws `bounds: ...` for a fourth
     * own reaction on one post or a bad argument.
     * @param {bigint} hlc
     * @param {Uint8Array} channel_id
     * @param {bigint} post_cursor
     * @param {string} emoji
     * @param {boolean} reacted
     * @param {bigint} post_at_ms
     * @returns {Uint8Array | undefined}
     */
    setChannelReaction(hlc, channel_id, post_cursor, emoji, reacted, post_at_ms) {
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_setChannelReaction(this.__wbg_ptr, hlc, ptr0, len0, post_cursor, ptr1, len1, reacted, post_at_ms);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        let v3;
        if (ret[0] !== 0) {
            v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v3;
    }
    /**
     * Record that `successorGroupId` continues the conversation
     * `rootConversationId` (ADR-0073 R.5), stamped `hlc`. **Write-once**:
     * when a register is already held for this successor nothing is written
     * and `false` is returned, whatever root it names (read it with
     * `lineageRoot`). On `true`, send `lineageEnvelopeFor(successorGroupId)`
     * to the account's other devices with
     * `DeviceGroup.createServiceEnvelopeMessage`. Throws when either id is
     * empty or longer than `lineageGroupIdMaxLen()`, or the two are equal.
     * @param {bigint} hlc
     * @param {Uint8Array} successor_group_id
     * @param {Uint8Array} root_conversation_id
     * @returns {boolean}
     */
    setLineage(hlc, successor_group_id, root_conversation_id) {
        const ptr0 = passArray8ToWasm0(successor_group_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(root_conversation_id, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_setLineage(this.__wbg_ptr, hlc, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Write one of the account's invite links (ADR-0079 section 4) at
     * `hlc`. `true` when written; then send `linkRecordEnvelopeFor(linkId)`
     * in a DeviceGroup service envelope.
     * @param {bigint} hlc
     * @param {LinkRecord} record
     * @returns {boolean}
     */
    setLinkRecord(hlc, record) {
        _assertClass(record, LinkRecord);
        const ret = wasm.syncedstate_setLinkRecord(this.__wbg_ptr, hlc, record.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Write the account's own-username register (ADR-0076 section 3),
     * stamped `hlc`: a username sets it, `undefined` / `null` (or a blank
     * string) records that the account has none. The username is
     * normalised and checked here with the one rule (`validateUsername`),
     * so `"@Bob"` may be passed and `"bob"` is stored.
     *
     * An ordinary last-writer-wins write, NOT write-once: `true` when the
     * register now holds this value, in which case send
     * `ownUsernameEnvelope()` to the account's other devices with
     * `DeviceGroup.createServiceEnvelopeMessage`; `false` when what is held
     * is newer or identical and nothing changed. A "none" is kept with its
     * stamp, so a stale device cannot bring an old name back.
     *
     * Throws the same `Error` as `validateUsername` (own `usernameError`
     * property `"empty"` / `"tooShort"` / `"tooLong"` /
     * `"invalidCharacters"`) when the username is refused.
     * @param {bigint} hlc
     * @param {string | null} [username]
     * @returns {boolean}
     */
    setOwnUsername(hlc, username) {
        var ptr0 = isLikeNone(username) ? 0 : passStringToWasm0(username, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_setOwnUsername(this.__wbg_ptr, hlc, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Set the account's quick reaction (UX reactions-and-emoji section 6),
     * stamped `hlc`. `true` when written: send `quickReactionEnvelope()`.
     * Throws `bounds: ...` for an empty or over-long value.
     * @param {bigint} hlc
     * @param {string} emoji
     * @returns {boolean}
     */
    setQuickReaction(hlc, emoji) {
        const ptr0 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_setQuickReaction(this.__wbg_ptr, hlc, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * Write the account's recovery email register (ADR-0075, amended
     * 2026-10-13), stamped `hlc`: an address sets it, `undefined` / `null`
     * (or a blank string) records that the account has none. The address
     * is normalised here with the one rule (`normalizeRecoveryEmail`).
     *
     * An ordinary last-writer-wins write, NOT write-once: `true` when the
     * register now holds this value, in which case send
     * `recoveryEmailEnvelope()` to the account's other devices with
     * `DeviceGroup.createServiceEnvelopeMessage`; `false` when what is held
     * is newer or identical and nothing changed. A removal is kept with its
     * stamp, so a stale device cannot bring the address back.
     *
     * The address is personal data: it stays on the account's devices and
     * in the sealed backup, and must not be logged. Throws an `Error`
     * whose message is `"<code>: <reason>"` (the codes of
     * `normalizeRecoveryEmail`) when the address is refused.
     * @param {bigint} hlc
     * @param {string | null} [address]
     * @returns {boolean}
     */
    setRecoveryEmail(hlc, address) {
        var ptr0 = isLikeNone(address) ? 0 : passStringToWasm0(address, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        const ret = wasm.syncedstate_setRecoveryEmail(this.__wbg_ptr, hlc, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * The catch-up re-send (ADR-0058 section 5.1): the whole state, every
     * entry with its original stamp, split so each encoded part is at most
     * 256 KiB. Always at least one part. Throws when more than 64 parts
     * would be needed.
     * @returns {SyncedSnapshotPart[]}
     */
    snapshotParts() {
        const ret = wasm.syncedstate_snapshotParts(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * Persist the whole state (also what a backup payload carries).
     * @returns {Uint8Array}
     */
    toBytes() {
        const ret = wasm.syncedstate_toBytes(this.__wbg_ptr);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) SyncedState.prototype[Symbol.dispose] = SyncedState.prototype.free;

/**
 * Logical bucket name. Mirror of [`lakko_storage::Table`].
 * @enum {0 | 1 | 2 | 3}
 */
export const Table = Object.freeze({
    /**
     * `MessageStore` rows.
     */
    Messages: 0, "0": "Messages",
    /**
     * `GroupStateStore` rows.
     */
    GroupState: 1, "1": "GroupState",
    /**
     * Contacts cache.
     */
    Contacts: 2, "2": "Contacts",
    /**
     * Arbitrary single-row settings.
     */
    Settings: 3, "3": "Settings",
});

/**
 * ADR-0047 — a poster-frame blob attached to an [`AttachmentRef`]: its
 * own encrypted blob under its own CEK. Attach with
 * `AttachmentRef.setThumb`; read with the `thumb` getter.
 */
export class ThumbRef {
    static __wrap(ptr) {
        const obj = Object.create(ThumbRef.prototype);
        obj.__wbg_ptr = ptr;
        ThumbRefFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ThumbRefFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_thumbref_free(ptr, 0);
    }
    /**
     * Raw 32-byte SHA-256 blob id of the poster blob.
     * @returns {Uint8Array}
     */
    get blobId() {
        const ret = wasm.thumbref_blobId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Poster pixel height, `undefined` when absent.
     * @returns {number | undefined}
     */
    get height() {
        const ret = wasm.thumbref_height(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * Raw 32-byte CEK of the poster blob.
     * @returns {Uint8Array}
     */
    get key() {
        const ret = wasm.thumbref_key(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Poster MIME type, `undefined` when not set. Check it with
     * `inlineMediaType` before rendering.
     * @returns {string | undefined}
     */
    get mime() {
        const ret = wasm.thumbref_mime(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Construct from raw byte fields + metadata. Field lengths are
     * validated when the owning ref is added to an `EnvelopeBuilder`;
     * `size` (1..=256 KiB) and the dimensions (1..=4096) are bounded at
     * build time, where an out-of-bound thumb is dropped.
     * @param {Uint8Array} blob_id
     * @param {Uint8Array} key
     * @param {Uint8Array} nonce
     * @param {string | null | undefined} mime
     * @param {bigint} size
     * @param {number | null} [width]
     * @param {number | null} [height]
     */
    constructor(blob_id, key, nonce, mime, size, width, height) {
        const ptr0 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(nonce, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(mime) ? 0 : passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.thumbref_new(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, size, isLikeNone(width) ? Number.MAX_SAFE_INTEGER : (width) >>> 0, isLikeNone(height) ? Number.MAX_SAFE_INTEGER : (height) >>> 0);
        this.__wbg_ptr = ret;
        ThumbRefFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Raw 12-byte AEAD nonce of the poster blob.
     * @returns {Uint8Array}
     */
    get nonce() {
        const ret = wasm.thumbref_nonce(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Poster plaintext size in bytes (`BigInt`).
     * @returns {bigint}
     */
    get size() {
        const ret = wasm.thumbref_size(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Poster pixel width, `undefined` when absent.
     * @returns {number | undefined}
     */
    get width() {
        const ret = wasm.thumbref_width(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
}
if (Symbol.dispose) ThumbRef.prototype[Symbol.dispose] = ThumbRef.prototype.free;

/**
 * Which of a person's video tracks a call tile asks the call server for
 * (`docs/ux/group-call-grid.md` section 7.6, ADR-0070 step 5). The light
 * track always arrives beside it.
 * @enum {0 | 1 | 2}
 */
export const TileVideoQuality = Object.freeze({
    /**
     * The main track: the full picture.
     */
    Main: 0, "0": "Main",
    /**
     * The middle track: the camera at half size.
     */
    Middle: 1, "1": "Middle",
    /**
     * The light track alone.
     */
    Light: 2, "2": "Light",
});

/**
 * What the message menu offers for one message.
 * @enum {0 | 1 | 2}
 */
export const TranslateOffer = Object.freeze({
    /**
     * No `Translate` row.
     */
    None: 0, "0": "None",
    /**
     * A `Translate` row from the detected language.
     */
    Translate: 1, "1": "Translate",
    /**
     * A `Translate` row that opens with the language picker.
     */
    TranslateAskLanguage: 2, "2": "TranslateAskLanguage",
});

/**
 * What the device can do for one language, as its engine reports it.
 * @enum {0 | 1 | 2}
 */
export const TranslationEngineState = Object.freeze({
    /**
     * Works now, on this device.
     */
    Ready: 0, "0": "Ready",
    /**
     * Works after a language pack is on the device.
     */
    NeedsPack: 1, "1": "NeedsPack",
    /**
     * This device cannot do it for this language, locally.
     */
    Unavailable: 2, "2": "Unavailable",
});

/**
 * JS-side mirror of `UnwrappedFrame`.
 */
export class UnwrappedFrame {
    static __wrap(ptr) {
        const obj = Object.create(UnwrappedFrame.prototype);
        obj.__wbg_ptr = ptr;
        UnwrappedFrameFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        UnwrappedFrameFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_unwrappedframe_free(ptr, 0);
    }
    /**
     * The frame ciphertext body (the bytes after the SFrame header).
     * @returns {Uint8Array}
     */
    get ciphertext() {
        const ret = wasm.unwrappedframe_ciphertext(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The per-sender 64-bit frame counter (a `bigint` in JS), parsed from
     * the SFrame header CTR.
     * @returns {bigint}
     */
    get counter() {
        const ret = wasm.unwrappedframe_counter(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The sender's MLS leaf index, parsed from the SFrame header KID.
     * @returns {number}
     */
    get senderLeaf() {
        const ret = wasm.unwrappedframe_senderLeaf(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) UnwrappedFrame.prototype[Symbol.dispose] = UnwrappedFrame.prototype.free;

/**
 * What a client on its current release should do about an update. The wasm
 * projection of [`lakko_attest::UpdateStatus`]. wasm-bindgen renders this as a
 * JS number-enum — `UpToDate = 0`, `UpdateAvailable = 1`, `UpdateRequired = 2`
 * (same convention as the [`crate::mls::Role`] enum) — so the web client
 * switches on the numeric value.
 * @enum {0 | 1 | 2}
 */
export const UpdateStatus = Object.freeze({
    /**
     * The current release is at or above `latest_release` — nothing to do (`0`).
     */
    UpToDate: 0, "0": "UpToDate",
    /**
     * A newer release exists (`current < latest`) but the current release is
     * still supported — offer an optional update (`1`).
     */
    UpdateAvailable: 1, "1": "UpdateAvailable",
    /**
     * The current release is below `min_supported_release` — the client must
     * update to keep working (`2`). Takes precedence over
     * [`UpdateStatus::UpdateAvailable`].
     */
    UpdateRequired: 2, "2": "UpdateRequired",
});

/**
 * The authenticated claims of a verified username certificate.
 */
export class UsernameCertificate {
    static __wrap(ptr) {
        const obj = Object.create(UsernameCertificate.prototype);
        obj.__wbg_ptr = ptr;
        UsernameCertificateFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        UsernameCertificateFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_usernamecertificate_free(ptr, 0);
    }
    /**
     * The 16-byte account the username is registered to.
     * @returns {Uint8Array}
     */
    get accountId() {
        const ret = wasm.usernamecertificate_accountId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * When discovery issued it, Unix ms (a `bigint`). Informational.
     * @returns {bigint}
     */
    get issuedAtUnixMillis() {
        const ret = wasm.usernamecertificate_issuedAtUnixMillis(this.__wbg_ptr);
        return ret;
    }
    /**
     * The certified 64-byte lookup key of the username (the
     * `discoveryLookupKey` of its OPRF output).
     * @returns {Uint8Array}
     */
    get oprfHash() {
        const ret = wasm.usernamecertificate_oprfHash(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) UsernameCertificate.prototype[Symbol.dispose] = UsernameCertificate.prototype.free;

/**
 * An attachment reference of a saved message: where the encrypted file is
 * and how to open it. A reference, not the file, which may have expired by
 * the time the history is read (ADR-0065 section 8). The media fields are
 * ADR-0047's, bounded whenever they are set (see the module doc).
 */
export class VaultAttachment {
    static __wrap(ptr) {
        const obj = Object.create(VaultAttachment.prototype);
        obj.__wbg_ptr = ptr;
        VaultAttachmentFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof VaultAttachment)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultAttachmentFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultattachment_free(ptr, 0);
    }
    /**
     * 32-byte blob id.
     * @returns {Uint8Array}
     */
    get blobId() {
        const ret = wasm.vaultattachment_blobId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Playback length in milliseconds.
     * @returns {number | undefined}
     */
    get durationMs() {
        const ret = wasm.vaultattachment_durationMs(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * Pixel height.
     * @returns {number | undefined}
     */
    get height() {
        const ret = wasm.vaultattachment_height(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * 32-byte content-encryption key.
     * @returns {Uint8Array}
     */
    get key() {
        const ret = wasm.vaultattachment_key(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Media kind (`voice`, `video_note`, `gif`, or one this build does not
     * know); `undefined` for a plain file.
     * @returns {string | undefined}
     */
    get kind() {
        const ret = wasm.vaultattachment_kind(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * MIME type.
     * @returns {string | undefined}
     */
    get mime() {
        const ret = wasm.vaultattachment_mime(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * File name.
     * @returns {string | undefined}
     */
    get name() {
        const ret = wasm.vaultattachment_name(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * `blobId` / `key` MUST be 32 bytes, `nonce` 12 bytes; `size` is the
     * plaintext size in bytes (a `bigint`).
     *
     * # Errors
     *
     * Throws `blobIdLength: ...`, `keyLength: ...` or `nonceLength: ...`.
     * @param {Uint8Array} blob_id
     * @param {Uint8Array} key
     * @param {Uint8Array} nonce
     * @param {bigint} size
     * @param {string | null} [mime]
     * @param {string | null} [name]
     */
    constructor(blob_id, key, nonce, size, mime, name) {
        const ptr0 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(nonce, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(mime) ? 0 : passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        var ptr4 = isLikeNone(name) ? 0 : passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len4 = WASM_VECTOR_LEN;
        const ret = wasm.vaultattachment_new(ptr0, len0, ptr1, len1, ptr2, len2, size, ptr3, len3, ptr4, len4);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        VaultAttachmentFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * 12-byte AEAD nonce.
     * @returns {Uint8Array}
     */
    get nonce() {
        const ret = wasm.vaultattachment_nonce(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * ADR-0047 media metadata; replaces what was set before. `waveform` is
     * the raw amplitude bytes. A value outside its bound is dropped, as for
     * a live envelope: a duration is kept only for `voice` / `video_note`
     * and within that kind's limit, a dimension only within 1..=4096.
     * @param {string | null} [kind]
     * @param {number | null} [duration_ms]
     * @param {Uint8Array | null} [waveform]
     * @param {number | null} [width]
     * @param {number | null} [height]
     */
    setMedia(kind, duration_ms, waveform, width, height) {
        var ptr0 = isLikeNone(kind) ? 0 : passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(waveform) ? 0 : passArray8ToWasm0(waveform, wasm.__wbindgen_malloc);
        var len1 = WASM_VECTOR_LEN;
        wasm.vaultattachment_setMedia(this.__wbg_ptr, ptr0, len0, isLikeNone(duration_ms) ? Number.MAX_SAFE_INTEGER : (duration_ms) >>> 0, ptr1, len1, isLikeNone(width) ? Number.MAX_SAFE_INTEGER : (width) >>> 0, isLikeNone(height) ? Number.MAX_SAFE_INTEGER : (height) >>> 0);
    }
    /**
     * ADR-0047 poster frame; replaces one set before. `blobId` / `key` MUST
     * be 32 bytes, `nonce` 12 bytes. A thumb outside the size limit is
     * dropped whole (`thumb` then reads `undefined`).
     *
     * # Errors
     *
     * Throws `blobIdLength: ...`, `keyLength: ...` or `nonceLength: ...`.
     * @param {Uint8Array} blob_id
     * @param {Uint8Array} key
     * @param {Uint8Array} nonce
     * @param {bigint} size
     * @param {string | null} [mime]
     * @param {number | null} [width]
     * @param {number | null} [height]
     */
    setThumb(blob_id, key, nonce, size, mime, width, height) {
        const ptr0 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(nonce, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        var ptr3 = isLikeNone(mime) ? 0 : passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len3 = WASM_VECTOR_LEN;
        const ret = wasm.vaultattachment_setThumb(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, size, ptr3, len3, isLikeNone(width) ? Number.MAX_SAFE_INTEGER : (width) >>> 0, isLikeNone(height) ? Number.MAX_SAFE_INTEGER : (height) >>> 0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Plaintext size in bytes. A `bigint`.
     * @returns {bigint}
     */
    get size() {
        const ret = wasm.vaultattachment_size(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Poster frame.
     * @returns {VaultThumb | undefined}
     */
    get thumb() {
        const ret = wasm.vaultattachment_thumb(this.__wbg_ptr);
        return ret === 0 ? undefined : VaultThumb.__wrap(ret);
    }
    /**
     * Raw waveform amplitude bytes.
     * @returns {Uint8Array | undefined}
     */
    get waveform() {
        const ret = wasm.vaultattachment_waveform(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * Pixel width.
     * @returns {number | undefined}
     */
    get width() {
        const ret = wasm.vaultattachment_width(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
}
if (Symbol.dispose) VaultAttachment.prototype[Symbol.dispose] = VaultAttachment.prototype.free;

/**
 * One row of a head's chunk table.
 */
export class VaultChunkEntry {
    static __wrap(ptr) {
        const obj = Object.create(VaultChunkEntry.prototype);
        obj.__wbg_ptr = ptr;
        VaultChunkEntryFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof VaultChunkEntry)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultChunkEntryFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultchunkentry_free(ptr, 0);
    }
    /**
     * How many messages the chunk holds.
     * @returns {number}
     */
    get count() {
        const ret = wasm.vaultchunkentry_count(this.__wbg_ptr);
        return ret;
    }
    /**
     * Its lowest message id. A `bigint`.
     * @returns {bigint}
     */
    get firstId() {
        const ret = wasm.vaultchunkentry_firstId(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Its highest message id. A `bigint`.
     * @returns {bigint}
     */
    get lastId() {
        const ret = wasm.vaultchunkentry_lastId(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * How many messages the chunk holds, its lowest and highest message
     * ids, and the chunk object's current version on the storage service.
     * The ids and the version are `bigint`.
     * @param {number} count
     * @param {bigint} first_id
     * @param {bigint} last_id
     * @param {bigint} version
     */
    constructor(count, first_id, last_id, version) {
        const ret = wasm.vaultchunkentry_new(count, first_id, last_id, version);
        this.__wbg_ptr = ret;
        VaultChunkEntryFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The chunk object's current version. A `bigint`.
     * @returns {bigint}
     */
    get version() {
        const ret = wasm.vaultchunkentry_version(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) VaultChunkEntry.prototype[Symbol.dispose] = VaultChunkEntry.prototype.free;

/**
 * A conversation's head. See the module doc for how it is built and read.
 */
export class VaultConversationHead {
    static __wrap(ptr) {
        const obj = Object.create(VaultConversationHead.prototype);
        obj.__wbg_ptr = ptr;
        VaultConversationHeadFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultConversationHeadFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultconversationhead_free(ptr, 0);
    }
    /**
     * `Array<VaultChunkEntry>`.
     * @returns {VaultChunkEntry[]}
     */
    get chunks() {
        const ret = wasm.vaultconversationhead_chunks(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * `0` unknown, `1` one-to-one, `2` group.
     * @returns {number}
     */
    get kind() {
        const ret = wasm.vaultconversationhead_kind(this.__wbg_ptr);
        return ret;
    }
    /**
     * Last activity, unix milliseconds. A `bigint`.
     * @returns {bigint}
     */
    get lastActivityMs() {
        const ret = wasm.vaultconversationhead_lastActivityMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * `0` in, `1` left, `2` removed.
     * @returns {number}
     */
    get membership() {
        const ret = wasm.vaultconversationhead_membership(this.__wbg_ptr);
        return ret;
    }
    /**
     * `refKind` / `refId` name the conversation the ADR-0058 way (`1`
     * one-to-one, `2` group, `3` channel; 1..=64 bytes); `kind` is `0`
     * unknown, `1` one-to-one, `2` group (ADR-0062); `title` the group name,
     * empty for a one-to-one or an unnamed group; `membership` `0` in, `1`
     * left, `2` removed; `lastActivityMs` unix milliseconds (a `bigint`).
     * Everything else starts empty; add it with the setters.
     *
     * # Errors
     *
     * Throws `unknownKind: ...`, `idLength: ...` or `unknownOption: ...`.
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @param {number} kind
     * @param {string} title
     * @param {number} membership
     * @param {bigint} last_activity_ms
     */
    constructor(ref_kind, ref_id, kind, title, membership, last_activity_ms) {
        const ptr0 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(title, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.vaultconversationhead_new(ref_kind, ptr0, len0, kind, ptr1, len1, membership, last_activity_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        VaultConversationHeadFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * `Array<VaultPerson>`.
     * @returns {VaultPerson[]}
     */
    get people() {
        const ret = wasm.vaultconversationhead_people(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * `Array<VaultPersonalPin>`.
     * @returns {VaultPersonalPin[]}
     */
    get personalPins() {
        const ret = wasm.vaultconversationhead_personalPins(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * `Array<VaultReentryEpoch>`.
     * @returns {VaultReentryEpoch[]}
     */
    get reentry() {
        const ret = wasm.vaultconversationhead_reentry(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The MLS group id, or the channel id.
     * @returns {Uint8Array}
     */
    get refId() {
        const ret = wasm.vaultconversationhead_refId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The conversation's reference kind: `1` one-to-one, `2` group, `3`
     * channel.
     * @returns {number}
     */
    get refKind() {
        const ret = wasm.vaultconversationhead_refKind(this.__wbg_ptr);
        return ret;
    }
    /**
     * The rank the account's registrar seed was issued at, when the head
     * carries one.
     * @returns {number | undefined}
     */
    get registrarRank() {
        const ret = wasm.vaultconversationhead_registrarRank(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret;
    }
    /**
     * The account's 32-byte registrar seed for this journal, when the head
     * carries one.
     *
     * **Secret.** It crosses this boundary as bytes because the client
     * already holds it that way: it stores the one it receives in-group
     * (`openRegistrarSeedEnvelope`) wrapped under the device key, and it
     * must store one read from a head the same way and nowhere else. Never
     * log it.
     * @returns {Uint8Array | undefined}
     */
    get registrarSeed() {
        const ret = wasm.vaultconversationhead_registrarSeed(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The chunk table, an `Array<VaultChunkEntry>` of at most 65 536,
     * consumed.
     * @param {VaultChunkEntry[]} chunks
     */
    setChunks(chunks) {
        const ptr0 = passArrayJsValueToWasm0(chunks, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.vaultconversationhead_setChunks(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * The people, an `Array<VaultPerson>` of at most `vaultMaxPeople()`,
     * consumed.
     * @param {VaultPerson[]} people
     */
    setPeople(people) {
        const ptr0 = passArrayJsValueToWasm0(people, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.vaultconversationhead_setPeople(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Personal pins, an `Array<VaultPersonalPin>` of at most 1024, consumed.
     * @param {VaultPersonalPin[]} pins
     */
    setPersonalPins(pins) {
        const ptr0 = passArrayJsValueToWasm0(pins, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.vaultconversationhead_setPersonalPins(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * Re-entry material for the newest epochs: an `Array<VaultReentryEpoch>`
     * of at most `vaultMaxReentryEpochs()`, consumed. Empty when the account
     * is not in the conversation.
     * @param {VaultReentryEpoch[]} reentry
     */
    setReentry(reentry) {
        const ptr0 = passArrayJsValueToWasm0(reentry, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.vaultconversationhead_setReentry(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * The account's registrar seed for this journal (ADR-0057), when it
     * holds one: the rank it was issued at (opaque to the codec) and the
     * 32-byte seed.
     *
     * # Errors
     *
     * Throws `seedLength: ...`.
     * @param {number} rank
     * @param {Uint8Array} seed
     */
    setRegistrarSeed(rank, seed) {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vaultconversationhead_setRegistrarSeed(this.__wbg_ptr, rank, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * Shared pins, an `Array<VaultSharedPin>` of at most 1024, consumed.
     * @param {VaultSharedPin[]} pins
     */
    setSharedPins(pins) {
        const ptr0 = passArrayJsValueToWasm0(pins, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.vaultconversationhead_setSharedPins(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * The newest messages, not yet moved into a chunk: an
     * `Array<VaultMessage>` of at most `vaultMaxTailMessages()`, consumed.
     * @param {VaultMessage[]} tail
     */
    setTail(tail) {
        const ptr0 = passArrayJsValueToWasm0(tail, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        wasm.vaultconversationhead_setTail(this.__wbg_ptr, ptr0, len0);
    }
    /**
     * `Array<VaultSharedPin>`.
     * @returns {VaultSharedPin[]}
     */
    get sharedPins() {
        const ret = wasm.vaultconversationhead_sharedPins(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * `Array<VaultMessage>`: the newest messages, not yet in a chunk.
     * @returns {VaultMessage[]}
     */
    get tail() {
        const ret = wasm.vaultconversationhead_tail(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The group name; empty for a one-to-one or an unnamed group.
     * @returns {string}
     */
    get title() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.vaultconversationhead_title(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) VaultConversationHead.prototype[Symbol.dispose] = VaultConversationHead.prototype.free;

/**
 * One conversation in the index.
 */
export class VaultIndexEntry {
    static __wrap(ptr) {
        const obj = Object.create(VaultIndexEntry.prototype);
        obj.__wbg_ptr = ptr;
        VaultIndexEntryFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof VaultIndexEntry)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultIndexEntryFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultindexentry_free(ptr, 0);
    }
    /**
     * Last activity, unix milliseconds. A `bigint`.
     * @returns {bigint}
     */
    get lastActivityMs() {
        const ret = wasm.vaultindexentry_lastActivityMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * `0` in, `1` left, `2` removed.
     * @returns {number}
     */
    get membership() {
        const ret = wasm.vaultindexentry_membership(this.__wbg_ptr);
        return ret;
    }
    /**
     * `refKind` / `refId` name the conversation; `lastActivityMs` (unix
     * milliseconds, a `bigint`) orders the loading; `membership` is `0` in,
     * `1` left, `2` removed.
     *
     * # Errors
     *
     * Throws `unknownKind: ...`, `idLength: ...` or `unknownOption: ...`.
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @param {bigint} last_activity_ms
     * @param {number} membership
     */
    constructor(ref_kind, ref_id, last_activity_ms, membership) {
        const ptr0 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vaultindexentry_new(ref_kind, ptr0, len0, last_activity_ms, membership);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        VaultIndexEntryFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The MLS group id, or the channel id.
     * @returns {Uint8Array}
     */
    get refId() {
        const ret = wasm.vaultindexentry_refId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * `1` one-to-one, `2` group, `3` channel.
     * @returns {number}
     */
    get refKind() {
        const ret = wasm.vaultindexentry_refKind(this.__wbg_ptr);
        return ret;
    }
}
if (Symbol.dispose) VaultIndexEntry.prototype[Symbol.dispose] = VaultIndexEntry.prototype.free;

/**
 * The vault names and keys of one account. See the module doc.
 *
 * Holds the vault root, from which every vault of the account can be named,
 * read and rewritten and every file the account uploaded deleted. The root
 * and what it derives stay inside this object: no method returns the root, a
 * vault key or a write seed.
 */
export class VaultKeys {
    static __wrap(ptr) {
        const obj = Object.create(VaultKeys.prototype);
        obj.__wbg_ptr = ptr;
        VaultKeysFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultKeysFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultkeys_free(ptr, 0);
    }
    /**
     * The 32-byte Ed25519 public key an upload of the blob `blobId`
     * carries, so that only a device of this account can delete it
     * (ADR-0065 section 6).
     *
     * # Errors
     *
     * Throws `blobIdLength: ...` when `blobId` is not 32 bytes.
     * @param {Uint8Array} blob_id
     * @returns {Uint8Array}
     */
    blobDeletePublicKey(blob_id) {
        const ptr0 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vaultkeys_blobDeletePublicKey(this.__wbg_ptr, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Open bytes the storage service returned as chunk `chunkNo` of the
     * vault at `version`. Bytes sealed as anything else - another vault, a
     * head, another chunk number, another version - fail with
     * `authentication`.
     *
     * # Errors
     *
     * As `openHead`.
     * @param {VaultRef} vault
     * @param {number} chunk_no
     * @param {bigint} version
     * @param {Uint8Array} sealed
     * @returns {Uint8Array}
     */
    openChunk(vault, chunk_no, version, sealed) {
        _assertClass(vault, VaultRef);
        const ptr0 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vaultkeys_openChunk(this.__wbg_ptr, vault.__wbg_ptr, chunk_no, version, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Open bytes the storage service returned as the vault's head at
     * `version`. Bytes sealed as anything else - another vault, a chunk,
     * another version - fail with `authentication`.
     *
     * # Errors
     *
     * Throws `objectTooLarge`, `truncated`, `unknownFormat`, `notABucket`,
     * `authentication` or `padding`, each followed by `": "` and the reason.
     * @param {VaultRef} vault
     * @param {bigint} version
     * @param {Uint8Array} sealed
     * @returns {Uint8Array}
     */
    openHead(vault, version, sealed) {
        _assertClass(vault, VaultRef);
        const ptr0 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vaultkeys_openHead(this.__wbg_ptr, vault.__wbg_ptr, version, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Seal `plaintext` (an encoded chunk) as chunk `chunkNo` of the vault at
     * `version`. The nonce is drawn here, as for `sealHead`.
     *
     * # Errors
     *
     * Throws `plaintextTooLarge: ...` or `seal: ...`.
     * @param {VaultRef} vault
     * @param {number} chunk_no
     * @param {bigint} version
     * @param {Uint8Array} plaintext
     * @returns {Uint8Array}
     */
    sealChunk(vault, chunk_no, version, plaintext) {
        _assertClass(vault, VaultRef);
        const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vaultkeys_sealChunk(this.__wbg_ptr, vault.__wbg_ptr, chunk_no, version, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * Seal `plaintext` (an encoded head) as the vault's head at `version`.
     * The 12-byte nonce is drawn here from the platform's random source,
     * fresh for every call; the result is padded to a size bucket.
     *
     * # Errors
     *
     * Throws `plaintextTooLarge: ...` or `seal: ...`.
     * @param {VaultRef} vault
     * @param {bigint} version
     * @param {Uint8Array} plaintext
     * @returns {Uint8Array}
     */
    sealHead(vault, version, plaintext) {
        _assertClass(vault, VaultRef);
        const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vaultkeys_sealHead(this.__wbg_ptr, vault.__wbg_ptr, version, ptr0, len0);
        if (ret[3]) {
            throw takeFromExternrefTable0(ret[2]);
        }
        var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v2;
    }
    /**
     * The `storageProofHeaderName()` header value for `DELETE /blob/{hex}`
     * of the blob `blobId`, signed with that blob's delete key. Arguments as
     * for `signRequest`.
     *
     * # Errors
     *
     * Throws `blobIdLength: ...` or `requestNotCanonical: ...`.
     * @param {Uint8Array} blob_id
     * @param {string} method
     * @param {string} path
     * @param {Uint8Array} body
     * @returns {string}
     */
    signBlobDelete(blob_id, method, path, body) {
        let deferred6_0;
        let deferred6_1;
        try {
            const ptr0 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
            const len0 = WASM_VECTOR_LEN;
            const ptr1 = passStringToWasm0(method, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            const ptr2 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len2 = WASM_VECTOR_LEN;
            const ptr3 = passArray8ToWasm0(body, wasm.__wbindgen_malloc);
            const len3 = WASM_VECTOR_LEN;
            const ret = wasm.vaultkeys_signBlobDelete(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
            var ptr5 = ret[0];
            var len5 = ret[1];
            if (ret[3]) {
                ptr5 = 0; len5 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred6_0 = ptr5;
            deferred6_1 = len5;
            return getStringFromWasm0(ptr5, len5);
        } finally {
            wasm.__wbindgen_free(deferred6_0, deferred6_1, 1);
        }
    }
    /**
     * The `storageProofHeaderName()` header value for one request to the
     * vault's routes, signed with the vault's write key. `method` is upper
     * case (`GET`, `PUT`, `DELETE`); `path` is the service path
     * (`/vault/<hex>/head`), never a reverse-proxy prefix; `body` is the
     * exact bytes sent (empty for a `GET`). The clock is read here; a value
     * is good within `storageProofWindowMs()` of the server's clock.
     *
     * # Errors
     *
     * Throws `requestNotCanonical: ...` when `method` or `path` contains a
     * NUL byte.
     * @param {VaultRef} vault
     * @param {string} method
     * @param {string} path
     * @param {Uint8Array} body
     * @returns {string}
     */
    signRequest(vault, method, path, body) {
        let deferred5_0;
        let deferred5_1;
        try {
            _assertClass(vault, VaultRef);
            const ptr0 = passStringToWasm0(method, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len0 = WASM_VECTOR_LEN;
            const ptr1 = passStringToWasm0(path, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            const ptr2 = passArray8ToWasm0(body, wasm.__wbindgen_malloc);
            const len2 = WASM_VECTOR_LEN;
            const ret = wasm.vaultkeys_signRequest(this.__wbg_ptr, vault.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
            var ptr4 = ret[0];
            var len4 = ret[1];
            if (ret[3]) {
                ptr4 = 0; len4 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred5_0 = ptr4;
            deferred5_1 = len4;
            return getStringFromWasm0(ptr4, len4);
        } finally {
            wasm.__wbindgen_free(deferred5_0, deferred5_1, 1);
        }
    }
    /**
     * The 32-byte name of the vault on the storage service: what the request
     * path carries, in lowercase hex. Not secret, and never sent to another
     * person.
     * @param {VaultRef} vault
     * @returns {Uint8Array}
     */
    vaultId(vault) {
        _assertClass(vault, VaultRef);
        const ret = wasm.vaultkeys_vaultId(this.__wbg_ptr, vault.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The 32-byte Ed25519 public key a claim (`PUT /vault/{id}`) stores: the
     * public half of the vault's write key.
     * @param {VaultRef} vault
     * @returns {Uint8Array}
     */
    writePublicKey(vault) {
        _assertClass(vault, VaultRef);
        const ret = wasm.vaultkeys_writePublicKey(this.__wbg_ptr, vault.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) VaultKeys.prototype[Symbol.dispose] = VaultKeys.prototype.free;

/**
 * One saved message: the row shape of a head's tail and of every chunk.
 */
export class VaultMessage {
    static __wrap(ptr) {
        const obj = Object.create(VaultMessage.prototype);
        obj.__wbg_ptr = ptr;
        VaultMessageFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof VaultMessage)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultMessageFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultmessage_free(ptr, 0);
    }
    /**
     * Add one reaction: the reacting person's 32-byte account key and the
     * emoji (1..=64 bytes).
     *
     * # Errors
     *
     * Throws `accountKeyLength: ...`.
     * @param {Uint8Array} reactor
     * @param {string} emoji
     */
    addReaction(reactor, emoji) {
        const ptr0 = passArray8ToWasm0(reactor, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.vaultmessage_addReaction(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * `Array<VaultAttachment>`.
     * @returns {VaultAttachment[]}
     */
    get attachments() {
        const ret = wasm.vaultmessage_attachments(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The author's 32-byte account key, when known.
     * @returns {Uint8Array | undefined}
     */
    get author() {
        const ret = wasm.vaultmessage_author(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * How long a call row lasted, in seconds.
     * @returns {number | undefined}
     */
    get callDurationSecs() {
        const ret = wasm.vaultmessage_callDurationSecs(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * The outcome of a call row; `undefined` for any other row.
     * @returns {number | undefined}
     */
    get callOutcome() {
        const ret = wasm.vaultmessage_callOutcome(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret;
    }
    /**
     * Whether a call row was a video call.
     * @returns {boolean | undefined}
     */
    get callVideo() {
        const ret = wasm.vaultmessage_callVideo(this.__wbg_ptr);
        return ret === 0xFFFFFF ? undefined : ret !== 0;
    }
    /**
     * When it was sent, unix milliseconds. A `bigint`.
     * @returns {bigint}
     */
    get createdAtMs() {
        const ret = wasm.vaultmessage_createdAtMs(this.__wbg_ptr);
        return ret;
    }
    /**
     * The message was deleted.
     * @returns {boolean}
     */
    get deleted() {
        const ret = wasm.vaultmessage_deleted(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * `0` outgoing, `1` incoming, `2` system.
     * @returns {number}
     */
    get direction() {
        const ret = wasm.vaultmessage_direction(this.__wbg_ptr);
        return ret;
    }
    /**
     * The text was edited.
     * @returns {boolean}
     */
    get edited() {
        const ret = wasm.vaultmessage_edited(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The original author's display name (empty when none), for a forward.
     * @returns {string | undefined}
     */
    get forwardAuthor() {
        const ret = wasm.vaultmessage_forwardAuthor(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The origin conversation of a forward, when the forwarder held it.
     * @returns {Uint8Array | undefined}
     */
    get forwardOriginGroupId() {
        const ret = wasm.vaultmessage_forwardOriginGroupId(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The original's message id in its conversation, beside
     * `forwardOriginGroupId`. A `bigint`.
     * @returns {bigint | undefined}
     */
    get forwardOriginMessageId() {
        const ret = wasm.vaultmessage_forwardOriginMessageId(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
    }
    /**
     * Whether the message was forwarded.
     * @returns {boolean}
     */
    get forwarded() {
        const ret = wasm.vaultmessage_forwarded(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Journal id of the newest operation applied to the row. A `bigint`.
     * @returns {bigint}
     */
    get lastOpId() {
        const ret = wasm.vaultmessage_lastOpId(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The Delivery Service journal id. A `bigint`.
     * @returns {bigint}
     */
    get messageId() {
        const ret = wasm.vaultmessage_messageId(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * `messageId` is the Delivery Service journal id (the merge key);
     * `direction` `0` outgoing / `1` incoming / `2` system; `createdAtMs`
     * unix milliseconds; `lastOpId` the journal id of the newest edit,
     * delete or reaction applied to the row, or `messageId` when none was
     * (it decides a merge); `attachments` an `Array<VaultAttachment>`, at
     * most 64, consumed. The three ids and the time are `bigint`.
     *
     * # Errors
     *
     * Throws `unknownOption: ...` for a `direction` outside `0..=2`.
     * @param {bigint} message_id
     * @param {number} direction
     * @param {bigint} created_at_ms
     * @param {bigint} last_op_id
     * @param {string} text
     * @param {VaultAttachment[]} attachments
     */
    constructor(message_id, direction, created_at_ms, last_op_id, text, attachments) {
        const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(attachments, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.vaultmessage_new(message_id, direction, created_at_ms, last_op_id, ptr0, len0, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        VaultMessageFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * `Array<VaultReaction>`.
     * @returns {VaultReaction[]}
     */
    get reactions() {
        const ret = wasm.vaultmessage_reactions(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The quoted author's display name (empty when none), for a reply.
     * @returns {string | undefined}
     */
    get replyAuthor() {
        const ret = wasm.vaultmessage_replyAuthor(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The quoted excerpt (empty when none), for a reply.
     * @returns {string | undefined}
     */
    get replyExcerpt() {
        const ret = wasm.vaultmessage_replyExcerpt(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * The quoted message's id, for a reply; `undefined` otherwise. A
     * `bigint`.
     * @returns {bigint | undefined}
     */
    get replyTarget() {
        const ret = wasm.vaultmessage_replyTarget(this.__wbg_ptr);
        return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
    }
    /**
     * The author's 32-byte account key.
     *
     * # Errors
     *
     * Throws `accountKeyLength: ...`.
     * @param {Uint8Array} author
     */
    setAuthor(author) {
        const ptr0 = passArray8ToWasm0(author, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vaultmessage_setAuthor(this.__wbg_ptr, ptr0, len0);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * The outcome of a call row: `outcome` as the clients number it (opaque
     * to the codec), whether it was a video call, and its length in
     * seconds.
     * @param {number} outcome
     * @param {boolean} video
     * @param {number} duration_secs
     */
    setCall(outcome, video, duration_secs) {
        wasm.vaultmessage_setCall(this.__wbg_ptr, outcome, video, duration_secs);
    }
    /**
     * Whether the text was edited, and whether the message was deleted (the
     * row is kept, emptied).
     * @param {boolean} edited
     * @param {boolean} deleted
     */
    setFlags(edited, deleted) {
        wasm.vaultmessage_setFlags(this.__wbg_ptr, edited, deleted);
    }
    /**
     * The origin of a forwarded message (ADR-0052): the original author's
     * display name (empty when none) and, when the forwarder held it, the
     * original's location. The location is kept only as a whole: both
     * `originGroupId` (1..=128 bytes) and `originMessageId` (a `bigint`),
     * or neither.
     * @param {string} author
     * @param {Uint8Array | null} [origin_group_id]
     * @param {bigint | null} [origin_message_id]
     */
    setForward(author, origin_group_id, origin_message_id) {
        const ptr0 = passStringToWasm0(author, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        var ptr1 = isLikeNone(origin_group_id) ? 0 : passArray8ToWasm0(origin_group_id, wasm.__wbindgen_malloc);
        var len1 = WASM_VECTOR_LEN;
        wasm.vaultmessage_setForward(this.__wbg_ptr, ptr0, len0, ptr1, len1, !isLikeNone(origin_message_id), isLikeNone(origin_message_id) ? BigInt(0) : origin_message_id);
    }
    /**
     * The quote of a reply (ADR-0052): the quoted message's id (a `bigint`)
     * and the author / excerpt snapshots, each empty when none.
     * @param {bigint} target
     * @param {string} author
     * @param {string} excerpt
     */
    setReply(target, author, excerpt) {
        const ptr0 = passStringToWasm0(author, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(excerpt, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        wasm.vaultmessage_setReply(this.__wbg_ptr, target, ptr0, len0, ptr1, len1);
    }
    /**
     * Mark the message as one of a wire version the saving device could not
     * read: that version.
     * @param {number} version
     */
    setUnsupportedVersion(version) {
        wasm.vaultmessage_setUnsupportedVersion(this.__wbg_ptr, version);
    }
    /**
     * The text.
     * @returns {string}
     */
    get text() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.vaultmessage_text(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The wire version the saving device could not read, when that is why
     * the row is empty.
     * @returns {number | undefined}
     */
    get unsupportedVersion() {
        const ret = wasm.vaultmessage_unsupportedVersion(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
}
if (Symbol.dispose) VaultMessage.prototype[Symbol.dispose] = VaultMessage.prototype.free;

/**
 * One person in a conversation, as shown at save time. Never a device leaf.
 */
export class VaultPerson {
    static __wrap(ptr) {
        const obj = Object.create(VaultPerson.prototype);
        obj.__wbg_ptr = ptr;
        VaultPersonFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof VaultPerson)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultPersonFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultperson_free(ptr, 0);
    }
    /**
     * The person's 32-byte account key.
     * @returns {Uint8Array}
     */
    get accountKey() {
        const ret = wasm.vaultperson_accountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Display-name snapshot; empty when none.
     * @returns {string}
     */
    get displayName() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.vaultperson_displayName(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * `accountKey` is the person's 32-byte account key; `displayName` and
     * `username` are snapshots, each empty when none; `role` is `0` member,
     * `1` admin, `2` owner.
     *
     * # Errors
     *
     * Throws `accountKeyLength: ...` or `unknownOption: ...`.
     * @param {Uint8Array} account_key
     * @param {string} display_name
     * @param {string} username
     * @param {number} role
     */
    constructor(account_key, display_name, username, role) {
        const ptr0 = passArray8ToWasm0(account_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(username, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.vaultperson_new(ptr0, len0, ptr1, len1, ptr2, len2, role);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        VaultPersonFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * `0` member, `1` admin, `2` owner.
     * @returns {number}
     */
    get role() {
        const ret = wasm.vaultperson_role(this.__wbg_ptr);
        return ret;
    }
    /**
     * Username snapshot; empty when none.
     * @returns {string}
     */
    get username() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.vaultperson_username(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
}
if (Symbol.dispose) VaultPerson.prototype[Symbol.dispose] = VaultPerson.prototype.free;

/**
 * A pin of this account alone.
 */
export class VaultPersonalPin {
    static __wrap(ptr) {
        const obj = Object.create(VaultPersonalPin.prototype);
        obj.__wbg_ptr = ptr;
        VaultPersonalPinFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof VaultPersonalPin)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultPersonalPinFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultpersonalpin_free(ptr, 0);
    }
    /**
     * The pinned message. A `bigint`.
     * @returns {bigint}
     */
    get messageId() {
        const ret = wasm.vaultpersonalpin_messageId(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The pinned message and when it was pinned, unix milliseconds. Both
     * `bigint`.
     * @param {bigint} message_id
     * @param {bigint} pinned_at_ms
     */
    constructor(message_id, pinned_at_ms) {
        const ret = wasm.vaultpersonalpin_new(message_id, pinned_at_ms);
        this.__wbg_ptr = ret;
        VaultPersonalPinFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * When it was pinned, unix milliseconds. A `bigint`.
     * @returns {bigint}
     */
    get pinnedAtMs() {
        const ret = wasm.vaultpersonalpin_pinnedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) VaultPersonalPin.prototype[Symbol.dispose] = VaultPersonalPin.prototype.free;

/**
 * One reaction of a [`VaultMessage`]. Built with `VaultMessage.addReaction`;
 * read here.
 */
export class VaultReaction {
    static __wrap(ptr) {
        const obj = Object.create(VaultReaction.prototype);
        obj.__wbg_ptr = ptr;
        VaultReactionFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultReactionFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultreaction_free(ptr, 0);
    }
    /**
     * The emoji.
     * @returns {string}
     */
    get emoji() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.vaultreaction_emoji(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The reacting person's 32-byte account key.
     * @returns {Uint8Array}
     */
    get reactor() {
        const ret = wasm.vaultreaction_reactor(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) VaultReaction.prototype[Symbol.dispose] = VaultReaction.prototype.free;

/**
 * Re-entry material for one epoch (ADR-0064 section 4).
 */
export class VaultReentryEpoch {
    static __wrap(ptr) {
        const obj = Object.create(VaultReentryEpoch.prototype);
        obj.__wbg_ptr = ptr;
        VaultReentryEpochFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof VaultReentryEpoch)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultReentryEpochFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultreentryepoch_free(ptr, 0);
    }
    /**
     * The epoch the GroupInfo describes. A `bigint`.
     * @returns {bigint}
     */
    get epoch() {
        const ret = wasm.vaultreentryepoch_epoch(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The GroupInfo with its ratchet tree.
     * @returns {Uint8Array}
     */
    get groupInfo() {
        const ret = wasm.vaultreentryepoch_groupInfo(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The epoch (a `bigint`) and the GroupInfo with its ratchet tree, as
     * MLS encodes it: opaque to the codec, 1 byte to 1 MiB.
     * @param {bigint} epoch
     * @param {Uint8Array} group_info
     */
    constructor(epoch, group_info) {
        const ptr0 = passArray8ToWasm0(group_info, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vaultreentryepoch_new(epoch, ptr0, len0);
        this.__wbg_ptr = ret;
        VaultReentryEpochFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
}
if (Symbol.dispose) VaultReentryEpoch.prototype[Symbol.dispose] = VaultReentryEpoch.prototype.free;

/**
 * Which vault: one conversation's, or one of the two account-level vaults.
 */
export class VaultRef {
    static __wrap(ptr) {
        const obj = Object.create(VaultRef.prototype);
        obj.__wbg_ptr = ptr;
        VaultRefFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultRefFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultref_free(ptr, 0);
    }
    /**
     * The vault of one conversation or channel, named the ADR-0058 way:
     * `refKind` is `1` one-to-one, `2` group, `3` channel; `refId` the MLS
     * group id or the channel id (1..=64 bytes).
     *
     * # Errors
     *
     * Throws `unknownKind: ...` or `idLength: ...`.
     * @param {number} ref_kind
     * @param {Uint8Array} ref_id
     * @returns {VaultRef}
     */
    static conversation(ref_kind, ref_id) {
        const ptr0 = passArray8ToWasm0(ref_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.vaultref_conversation(ref_kind, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return VaultRef.__wrap(ret[0]);
    }
    /**
     * The vault of the account's own device group.
     * @returns {VaultRef}
     */
    static devices() {
        const ret = wasm.vaultref_devices();
        return VaultRef.__wrap(ret);
    }
    /**
     * The vault that lists the account's conversations.
     * @returns {VaultRef}
     */
    static index() {
        const ret = wasm.vaultref_index();
        return VaultRef.__wrap(ret);
    }
}
if (Symbol.dispose) VaultRef.prototype[Symbol.dispose] = VaultRef.prototype.free;

/**
 * A pin shared with the conversation (ADR-0052). An unpin is kept, so a
 * stale pin cannot bring it back.
 */
export class VaultSharedPin {
    static __wrap(ptr) {
        const obj = Object.create(VaultSharedPin.prototype);
        obj.__wbg_ptr = ptr;
        VaultSharedPinFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof VaultSharedPin)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultSharedPinFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultsharedpin_free(ptr, 0);
    }
    /**
     * The pinned message. A `bigint`.
     * @returns {bigint}
     */
    get messageId() {
        const ret = wasm.vaultsharedpin_messageId(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The pinned message, pinned or unpinned, and the stamp of that state
     * in unix milliseconds. The id and the stamp are `bigint`.
     * @param {bigint} message_id
     * @param {boolean} pinned
     * @param {bigint} updated_at_ms
     */
    constructor(message_id, pinned, updated_at_ms) {
        const ret = wasm.vaultsharedpin_new(message_id, pinned, updated_at_ms);
        this.__wbg_ptr = ret;
        VaultSharedPinFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Pinned, or unpinned.
     * @returns {boolean}
     */
    get pinned() {
        const ret = wasm.vaultsharedpin_pinned(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The stamp of this state, unix milliseconds. A `bigint`.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.vaultsharedpin_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
}
if (Symbol.dispose) VaultSharedPin.prototype[Symbol.dispose] = VaultSharedPin.prototype.free;

/**
 * A poster-frame blob of a [`VaultAttachment`] (ADR-0047). Built with
 * `VaultAttachment.setThumb`; read here.
 */
export class VaultThumb {
    static __wrap(ptr) {
        const obj = Object.create(VaultThumb.prototype);
        obj.__wbg_ptr = ptr;
        VaultThumbFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VaultThumbFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_vaultthumb_free(ptr, 0);
    }
    /**
     * 32-byte blob id.
     * @returns {Uint8Array}
     */
    get blobId() {
        const ret = wasm.vaultthumb_blobId(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Pixel height.
     * @returns {number | undefined}
     */
    get height() {
        const ret = wasm.vaultthumb_height(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
    /**
     * 32-byte content-encryption key.
     * @returns {Uint8Array}
     */
    get key() {
        const ret = wasm.vaultthumb_key(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * MIME type.
     * @returns {string | undefined}
     */
    get mime() {
        const ret = wasm.vaultthumb_mime(this.__wbg_ptr);
        let v1;
        if (ret[0] !== 0) {
            v1 = getStringFromWasm0(ret[0], ret[1]).slice();
            wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        }
        return v1;
    }
    /**
     * 12-byte AEAD nonce.
     * @returns {Uint8Array}
     */
    get nonce() {
        const ret = wasm.vaultthumb_nonce(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Plaintext size in bytes. A `bigint`.
     * @returns {bigint}
     */
    get size() {
        const ret = wasm.vaultthumb_size(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Pixel width.
     * @returns {number | undefined}
     */
    get width() {
        const ret = wasm.vaultthumb_width(this.__wbg_ptr);
        return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
    }
}
if (Symbol.dispose) VaultThumb.prototype[Symbol.dispose] = VaultThumb.prototype.free;

/**
 * Structured view of a verified backup head — the wasm projection of
 * `lakko_attest::BackupHead`. Returned by [`verify_backup_head`] and
 * [`verify_backup_head_binding`] on success.
 */
export class VerifiedBackupHead {
    static __wrap(ptr) {
        const obj = Object.create(VerifiedBackupHead.prototype);
        obj.__wbg_ptr = ptr;
        VerifiedBackupHeadFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VerifiedBackupHeadFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_verifiedbackuphead_free(ptr, 0);
    }
    /**
     * The auth-assigned monotone backup version. Strictly increasing per
     * account; the caller refuses any head below its persisted floor
     * (`lastKnownVersion`) as a user-acknowledged rollback override.
     * @returns {bigint}
     */
    get backupVersion() {
        const ret = wasm.verifiedbackuphead_backupVersion(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * Raw 32-byte `BLAKE3` of the envelope portion the head commits to. Binds
     * the head to exactly the served envelope.
     * @returns {Uint8Array}
     */
    get envelopeHash() {
        const ret = wasm.verifiedbackuphead_envelopeHash(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The mint timestamp, Unix **milliseconds** (BigInt on the JS side). The
     * client applies its own staleness window against this (it is not an
     * expiry).
     * @returns {bigint}
     */
    get signedAtMillis() {
        const ret = wasm.verifiedbackuphead_signedAtMillis(this.__wbg_ptr);
        return ret;
    }
}
if (Symbol.dispose) VerifiedBackupHead.prototype[Symbol.dispose] = VerifiedBackupHead.prototype.free;

/**
 * A peer announcement that verified against the call's roster.
 */
export class VerifiedCallPeer {
    static __wrap(ptr) {
        const obj = Object.create(VerifiedCallPeer.prototype);
        obj.__wbg_ptr = ptr;
        VerifiedCallPeerFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VerifiedCallPeerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_verifiedcallpeer_free(ptr, 0);
    }
    /**
     * The account key the leaf resolves to: the person.
     * @returns {Uint8Array}
     */
    get accountKey() {
        const ret = wasm.verifiedcallpeer_accountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The signer's leaf index at this epoch.
     * @returns {number}
     */
    get leafIndex() {
        const ret = wasm.verifiedcallpeer_leafIndex(this.__wbg_ptr);
        return ret >>> 0;
    }
    /**
     * The SFU peer id.
     * @returns {string}
     */
    get peerId() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.verifiedcallpeer_peerId(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The signer's 32-byte leaf signature key.
     * @returns {Uint8Array}
     */
    get signatureKey() {
        const ret = wasm.verifiedcallpeer_signatureKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) VerifiedCallPeer.prototype[Symbol.dispose] = VerifiedCallPeer.prototype.free;

/**
 * A screen-share state that verified against the call's roster.
 */
export class VerifiedCallScreenShare {
    static __wrap(ptr) {
        const obj = Object.create(VerifiedCallScreenShare.prototype);
        obj.__wbg_ptr = ptr;
        VerifiedCallScreenShareFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VerifiedCallScreenShareFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_verifiedcallscreenshare_free(ptr, 0);
    }
    /**
     * The account key the signer's leaf resolves to: the person.
     * @returns {Uint8Array}
     */
    get accountKey() {
        const ret = wasm.verifiedcallscreenshare_accountKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The sharer's peer id.
     * @returns {string}
     */
    get peerId() {
        let deferred1_0;
        let deferred1_1;
        try {
            const ret = wasm.verifiedcallscreenshare_peerId(this.__wbg_ptr);
            deferred1_0 = ret[0];
            deferred1_1 = ret[1];
            return getStringFromWasm0(ret[0], ret[1]);
        } finally {
            wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
        }
    }
    /**
     * The share's sequence number.
     * @returns {bigint}
     */
    get seq() {
        const ret = wasm.verifiedcallscreenshare_seq(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * `true` while sharing, `false` for a stop.
     * @returns {boolean}
     */
    get sharing() {
        const ret = wasm.verifiedcallscreenshare_sharing(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * The sharer's 32-byte leaf signature key.
     * @returns {Uint8Array}
     */
    get signerKey() {
        const ret = wasm.verifiedcallscreenshare_signerKey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Whether the share carries sound.
     * @returns {boolean}
     */
    get withSound() {
        const ret = wasm.verifiedcallscreenshare_withSound(this.__wbg_ptr);
        return ret !== 0;
    }
}
if (Symbol.dispose) VerifiedCallScreenShare.prototype[Symbol.dispose] = VerifiedCallScreenShare.prototype.free;

/**
 * The authenticated claims a verified key-reset attestation carries.
 * Returned by `verifyKeyResetAttestation` / `verifyKeyResetAttestationFresh`.
 */
export class VerifiedKeyResetAttestation {
    static __wrap(ptr) {
        const obj = Object.create(VerifiedKeyResetAttestation.prototype);
        obj.__wbg_ptr = ptr;
        VerifiedKeyResetAttestationFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VerifiedKeyResetAttestationFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_verifiedkeyresetattestation_free(ptr, 0);
    }
    /**
     * The key generation the reset moved the account to (BigInt on the JS
     * side).
     * @returns {bigint}
     */
    get keyGeneration() {
        const ret = wasm.verifiedkeyresetattestation_keyGeneration(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The mint timestamp, Unix milliseconds (BigInt on the JS side).
     * @returns {bigint}
     */
    get signedAtMillis() {
        const ret = wasm.verifiedkeyresetattestation_signedAtMillis(this.__wbg_ptr);
        return ret;
    }
}
if (Symbol.dispose) VerifiedKeyResetAttestation.prototype[Symbol.dispose] = VerifiedKeyResetAttestation.prototype.free;

/**
 * What `verifyKeyRotationEnvelope` established about an envelope.
 */
export class VerifiedKeyRotation {
    static __wrap(ptr) {
        const obj = Object.create(VerifiedKeyRotation.prototype);
        obj.__wbg_ptr = ptr;
        VerifiedKeyRotationFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VerifiedKeyRotationFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_verifiedkeyrotation_free(ptr, 0);
    }
    /**
     * As on `KeyRotation`. Checked: the account identity it derives has
     * exactly `newSignaturePubkey`.
     * @returns {Uint8Array}
     */
    get newEntropy() {
        const ret = wasm.verifiedkeyrotation_newEntropy(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * The account identity public key the proof authorises and the entropy
     * derives. A pending envelope is keyed on this: the device adopts the
     * one whose key the published bundle (or the server) shows as the
     * account's.
     * @returns {Uint8Array}
     */
    get newSignaturePubkey() {
        const ret = wasm.verifiedkeyrotation_newSignaturePubkey(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * As on `KeyRotation`.
     * @returns {bigint}
     */
    get preparedAtMs() {
        const ret = wasm.verifiedkeyrotation_preparedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * When the proof stops being accepted, UNIX seconds, as a `bigint`.
     * @returns {bigint}
     */
    get proofExpiresAtUnixSecs() {
        const ret = wasm.verifiedkeyrotation_proofExpiresAtUnixSecs(this.__wbg_ptr);
        return ret;
    }
    /**
     * The proof, verified under the account key the caller holds.
     * @returns {Uint8Array}
     */
    get supersessionProof() {
        const ret = wasm.verifiedkeyrotation_supersessionProof(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
}
if (Symbol.dispose) VerifiedKeyRotation.prototype[Symbol.dispose] = VerifiedKeyRotation.prototype.free;

/**
 * The authenticated release numbers a verified manifest carries — the wasm
 * projection of [`lakko_attest::ReleaseManifest`]. Returned by
 * [`verify_release_manifest`] on success; the caller feeds `latestRelease` +
 * `minSupportedRelease` (with its own current release) to
 * [`release_update_status`].
 */
export class VerifiedReleaseManifest {
    static __wrap(ptr) {
        const obj = Object.create(VerifiedReleaseManifest.prototype);
        obj.__wbg_ptr = ptr;
        VerifiedReleaseManifestFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VerifiedReleaseManifestFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_verifiedreleasemanifest_free(ptr, 0);
    }
    /**
     * The newest published release number (BigInt on the JS side).
     * @returns {bigint}
     */
    get latestRelease() {
        const ret = wasm.verifiedreleasemanifest_latestRelease(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The oldest release number the backend still accepts (BigInt on the JS
     * side). A client below this must update; feed to [`release_update_status`].
     * @returns {bigint}
     */
    get minSupportedRelease() {
        const ret = wasm.verifiedreleasemanifest_minSupportedRelease(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The mint timestamp, Unix **milliseconds** (BigInt on the JS side). The
     * client applies its own staleness window against this (it is not an
     * expiry).
     * @returns {bigint}
     */
    get signedAtMillis() {
        const ret = wasm.verifiedreleasemanifest_signedAtMillis(this.__wbg_ptr);
        return ret;
    }
}
if (Symbol.dispose) VerifiedReleaseManifest.prototype[Symbol.dispose] = VerifiedReleaseManifest.prototype.free;

/**
 * The three positions. Numeric values are the frozen wire bytes.
 * @enum {0 | 1 | 2}
 */
export const VisibilityMode = Object.freeze({
    /**
     * No one finds the person by username or name.
     */
    Nobody: 0, "0": "Nobody",
    /**
     * The parts that are on.
     */
    Some: 1, "1": "Some",
    /**
     * Anyone finds the person.
     */
    Everyone: 2, "2": "Everyone",
});

/**
 * The "who can find me" setting. Edit it only through the Settings
 * transitions (`chooseVisibilityMode`, …), stamp it with
 * `withUpdatedAtMs`, then compose the envelope.
 */
export class VisibilitySetting {
    static __wrap(ptr) {
        const obj = Object.create(VisibilitySetting.prototype);
        obj.__wbg_ptr = ptr;
        VisibilitySettingFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        VisibilitySettingFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_visibilitysetting_free(ptr, 0);
    }
    /**
     * The chosen groups and channels (kept whatever the form).
     * @returns {ScopeId[]}
     */
    get chosen() {
        const ret = wasm.visibilitysetting_chosen(this.__wbg_ptr);
        var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
        return v1;
    }
    /**
     * The chosen contacts as concatenated 16-byte account ids (kept
     * whatever the form).
     * @returns {Uint8Array}
     */
    get chosenContacts() {
        const ret = wasm.visibilitysetting_chosenContacts(this.__wbg_ptr);
        var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
        return v1;
    }
    /**
     * Part 2.1's form: meaningful only while part 2.1 is on, and kept
     * whatever the position.
     * @returns {ContactsForm}
     */
    get contactsForm() {
        const ret = wasm.visibilitysetting_contactsForm(this.__wbg_ptr);
        return ret;
    }
    /**
     * Part 2.1: contacts of my contacts.
     * @returns {boolean}
     */
    get contactsOfContacts() {
        const ret = wasm.visibilitysetting_contactsOfContacts(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Part 2.2 and its form; `undefined` when off.
     * @returns {GroupsForm | undefined}
     */
    get groups() {
        const ret = wasm.visibilitysetting_groups(this.__wbg_ptr);
        return ret === 0 ? undefined : ret;
    }
    /**
     * Memory: part 2.1 when the person last left Some people.
     * @returns {boolean}
     */
    get lastContactsOfContacts() {
        const ret = wasm.visibilitysetting_lastContactsOfContacts(this.__wbg_ptr);
        return ret !== 0;
    }
    /**
     * Memory: part 2.2's form when the person last left Some people.
     * @returns {GroupsForm | undefined}
     */
    get lastGroups() {
        const ret = wasm.visibilitysetting_lastGroups(this.__wbg_ptr);
        return ret === 0 ? undefined : ret;
    }
    /**
     * The position.
     * @returns {VisibilityMode}
     */
    get mode() {
        const ret = wasm.visibilitysetting_mode(this.__wbg_ptr);
        return ret;
    }
    /**
     * Build a setting from all of its fields (as stored). `chosenContacts`
     * is concatenated 16-byte account ids. Validity is checked by
     * `composeVisibilityEnvelope`, except the ids' length, which cannot be
     * held in a setting.
     *
     * # Errors
     *
     * Throws with `visibilityError` `contactIdLength`.
     * @param {VisibilityMode} mode
     * @param {boolean} contacts_of_contacts
     * @param {GroupsForm | null | undefined} groups
     * @param {ScopeId[]} chosen
     * @param {boolean} last_contacts_of_contacts
     * @param {GroupsForm | null | undefined} last_groups
     * @param {bigint} updated_at_ms
     * @param {ContactsForm} contacts_form
     * @param {Uint8Array} chosen_contacts
     */
    constructor(mode, contacts_of_contacts, groups, chosen, last_contacts_of_contacts, last_groups, updated_at_ms, contacts_form, chosen_contacts) {
        const ptr0 = passArrayJsValueToWasm0(chosen, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(chosen_contacts, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.visibilitysetting_new(mode, contacts_of_contacts, isLikeNone(groups) ? 0 : groups, ptr0, len0, last_contacts_of_contacts, isLikeNone(last_groups) ? 0 : last_groups, updated_at_ms, contacts_form, ptr1, len1);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        VisibilitySettingFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * Setter-clock UNIX ms, a `bigint`.
     * @returns {bigint}
     */
    get updatedAtMs() {
        const ret = wasm.visibilitysetting_updatedAtMs(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * The same setting stamped `updatedAtMs` (the transitions never stamp).
     * @param {bigint} updated_at_ms
     * @returns {VisibilitySetting}
     */
    withUpdatedAtMs(updated_at_ms) {
        const ret = wasm.visibilitysetting_withUpdatedAtMs(this.__wbg_ptr, updated_at_ms);
        return VisibilitySetting.__wrap(ret);
    }
}
if (Symbol.dispose) VisibilitySetting.prototype[Symbol.dispose] = VisibilitySetting.prototype.free;

/**
 * Accept a verified call-invite capability (Phase 3c3).
 * `capability` must already be `decodeAndVerify`'d.
 * @param {CallInviteCapability} capability
 * @param {Identity} joiner_identity
 * @returns {AcceptedCallInvite}
 */
export function acceptCallInvite(capability, joiner_identity) {
    _assertClass(capability, CallInviteCapability);
    _assertClass(joiner_identity, Identity);
    const ret = wasm.acceptCallInvite(capability.__wbg_ptr, joiner_identity.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return AcceptedCallInvite.__wrap(ret[0]);
}

/**
 * The request header that carries a statement on a route with no JSON body:
 * `x-lakko-account-statement`, value base64url without padding.
 * @returns {string}
 */
export function accountStatementHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.accountStatementHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The length of a statement's wire form (138 bytes).
 * @returns {number}
 */
export function accountStatementLen() {
    const ret = wasm.accountStatementLen();
    return ret >>> 0;
}

/**
 * Derive the transparency `usernameHash` for an `accountId`
 * (`BLAKE3("lakko/transparency/account/v1" ‖ accountId)`) — the same derivation
 * discovery uses on append. A client computes the `expectedUsernameHash` it
 * feeds to `verifyGenesisAttestation` from the target's `accountId` (obtained
 * via the `username → accountId` discovery lookup) without re-implementing
 * BLAKE3. Returns the 32-byte hash.
 * @param {Uint8Array} account_id
 * @returns {Uint8Array}
 */
export function accountUsernameHash(account_id) {
    const ptr0 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.accountUsernameHash(ptr0, len0);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The fold: decide whether this device is the acting committer (ADR-0045
 * decision 2, variant 1).
 *
 * Pure, total and clock-free — the caller supplies `now` implicitly, as
 * already-computed ages. Exposed on the FFI precisely so no client
 * reimplements the rules: three platforms each re-deriving them is the drift
 * class that produced ADR-0044's F-A replay.
 *
 * The rules, in the order they apply:
 *
 * 1. `ownRank === undefined` is `"not-a-candidate"`, decided without reading a
 *    single beacon. An **omitted** argument reaches the ABI as absent and
 *    lands here too, which is the fail-closed direction: a caller that forgets
 *    the argument never sweeps.
 * 2. An observation whose `rank` is `undefined` is ignored (its sender is not
 *    a candidate).
 * 3. An observation older than `freshnessWindowMs` is ignored.
 * 4. An observation under `ownSignatureKey` is ignored whatever its rank,
 *    which is what makes a device's own echoed-back beacon harmless.
 * 5. Only a better-placed observation defers this device: a **strictly**
 *    lower rank, or the **same** rank and a **strictly** lower signature key,
 *    bytewise (ADR-0050 section 2.5a item 6 — every device of a ranked
 *    account holds the account's rank, and the account's acting device is its
 *    lowest-keyed one whose beacon is fresh, so a lost device that stays in
 *    the roster stops holding the duty once its beacon ages out). Rank `0`
 *    never waits for a higher rank, but does defer to a fresh lower-keyed
 *    device of the owner's own account.
 * 6. Surviving all of that with nothing left is `"act"`: hearing nothing is
 *    the correct answer when nobody better placed is running, not a failure to
 *    elect.
 *
 * **This does not close the race.** Each candidate folds over its own
 * poll-timed view and a beacon is stale by up to one
 * [`committer_beacon_interval_ms`] even when its sender is perfectly healthy,
 * so two candidates — two devices of one account included — can both reach
 * `"act"`. ADR-0045's stand-down journal
 * re-read and its staged-commit rollback both remain required; deleting either
 * on the strength of this verdict is a mis-reading.
 *
 * Pass [`committer_beacon_freshness_window_ms`] for `freshnessWindowMs` unless
 * there is a measured reason not to — the interval and the window are two
 * halves of one budget.
 *
 * # The observations are CONSUMED
 *
 * wasm-bindgen moves each element of `observed` into wasm, so every
 * [`BeaconObservation`] in the array is freed by this call and must not be
 * used again. Build the array fresh, on each evaluation, from the stored
 * signature-key + DS-`created_at` rows — which is what
 * [`BeaconObservation::rank`] requires anyway, since the rank has to be
 * re-derived against the current role table each time.
 *
 * # Errors
 *
 * `JsError` when `ownSignatureKey` is not exactly 32 bytes (each
 * [`BeaconObservation`] checked its own key when it was built).
 * @param {number | null | undefined} own_rank
 * @param {Uint8Array} own_signature_key
 * @param {BeaconObservation[]} observed
 * @param {bigint} freshness_window_ms
 * @returns {ActingCommitter}
 */
export function actingCommitterVerdict(own_rank, own_signature_key, observed, freshness_window_ms) {
    const ptr0 = passArray8ToWasm0(own_signature_key, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayJsValueToWasm0(observed, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.actingCommitterVerdict(isLikeNone(own_rank) ? Number.MAX_SAFE_INTEGER : (own_rank) >>> 0, ptr0, len0, ptr1, len1, freshness_window_ms);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ActingCommitter.__wrap(ret[0]);
}

/**
 * The one well-formed admission-seed envelope length.
 * @returns {number}
 */
export function admissionSeedEnvelopeLen() {
    const ret = wasm.admissionSeedEnvelopeLen();
    return ret >>> 0;
}

/**
 * The X25519 public key of an admission seed.
 *
 * # Errors
 *
 * Throws `admissionSeed.badLength: ...`.
 * @param {Uint8Array} admission_seed
 * @returns {Uint8Array}
 */
export function admissionSeedPublicKey(admission_seed) {
    const ptr0 = passArray8ToWasm0(admission_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.admissionSeedPublicKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The longest "only these" list: 100.
 * @returns {number}
 */
export function allowedReactionsMax() {
    const ret = wasm.allowedReactionsMax();
    return ret >>> 0;
}

/**
 * Whether the contacts of `accountId` can find the person: Everyone, or
 * Some people with part 2.1 on and either all contacts or this one chosen.
 * A permission, not a delivery decision. An id that is not 16 bytes names
 * no contact, so the answer is `false`.
 * @param {VisibilitySetting} setting
 * @param {Uint8Array} account_id
 * @returns {boolean}
 */
export function allowsContact(setting, account_id) {
    _assertClass(setting, VisibilitySetting);
    const ptr0 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.allowsContact(setting.__wbg_ptr, ptr0, len0);
    return ret !== 0;
}

/**
 * Whether contacts of contacts can find the person.
 * @param {VisibilitySetting} setting
 * @returns {boolean}
 */
export function allowsContactsOfContacts(setting) {
    _assertClass(setting, VisibilitySetting);
    const ret = wasm.allowsContactsOfContacts(setting.__wbg_ptr);
    return ret !== 0;
}

/**
 * Whether the members of `scope` can find the person. A permission, not a
 * delivery decision.
 * @param {VisibilitySetting} setting
 * @param {ScopeId} scope
 * @returns {boolean}
 */
export function allowsScope(setting, scope) {
    _assertClass(setting, VisibilitySetting);
    _assertClass(scope, ScopeId);
    const ret = wasm.allowsScope(setting.__wbg_ptr, scope.__wbg_ptr);
    return ret !== 0;
}

/**
 * Every context name, in wire order: `discovery-lookup`,
 * `discovery-register`, `invite-consume`, `ds-knock`, `push-register`,
 * `channel-subscribe`, `directory-search`, `vault-claim`, `blob-upload`,
 * `blob-read`, `mailbox-write`, `call-join`, `circle-claim`, `channel-react`,
 * `channel-comment`.
 * @returns {string[]}
 */
export function anonTokenContexts() {
    const ret = wasm.anonTokenContexts();
    var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * The default epoch window in seconds (one day), a `bigint`.
 * @returns {bigint}
 */
export function anonTokenDefaultWindowSecs() {
    const ret = wasm.anonTokenDefaultWindowSecs();
    return BigInt.asUintN(64, ret);
}

/**
 * The epoch containing `unixSecs` under `windowSecs` (both `bigint`).
 * Throws `invalid-window: ...` for a zero window.
 * @param {bigint} unix_secs
 * @param {bigint} window_secs
 * @returns {bigint}
 */
export function anonTokenEpochAt(unix_secs, window_secs) {
    const ret = wasm.anonTokenEpochAt(unix_secs, window_secs);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return BigInt.asUintN(64, ret[0]);
}

/**
 * The request header a spent token travels in (`x-lakko-anon-token`).
 * @returns {string}
 */
export function anonTokenHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.anonTokenHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Whether a token of `tokenEpoch` is still spendable when the current epoch
 * is `currentEpoch` (its own epoch and the next one).
 * @param {bigint} token_epoch
 * @param {bigint} current_epoch
 * @returns {boolean}
 */
export function anonTokenIsLive(token_epoch, current_epoch) {
    const ret = wasm.anonTokenIsLive(token_epoch, current_epoch);
    return ret !== 0;
}

/**
 * The largest batch one request may ask for.
 * @returns {number}
 */
export function anonTokenMaxBatch() {
    const ret = wasm.anonTokenMaxBatch();
    return ret >>> 0;
}

/**
 * What the rule can and cannot achieve with the setters a browser has.
 * @param {boolean} audio_settable
 * @param {boolean} video_settable
 * @returns {AvCapability}
 */
export function avAlignCapability(audio_settable, video_settable) {
    const ret = wasm.avAlignCapability(audio_settable, video_settable);
    return ret;
}

/**
 * The skew inside which a track is left as it is, milliseconds. Chosen,
 * not measured.
 * @returns {number}
 */
export function avAlignDeadBandMs() {
    const ret = wasm.avAlignDeadBandMs();
    return ret >>> 0;
}

/**
 * How often a client reads its receivers and calls `AvAlign.read`,
 * milliseconds. Chosen, not measured.
 * @returns {number}
 */
export function avAlignReadingMs() {
    const ret = wasm.avAlignReadingMs();
    return ret >>> 0;
}

/**
 * The most the sound is asked to wait for a picture above what the audio
 * needs for itself, milliseconds. Chosen, not measured.
 * @returns {number}
 */
export function avAlignSoundWaitMaxMs() {
    const ret = wasm.avAlignSoundWaitMaxMs();
    return ret >>> 0;
}

/**
 * The word a diagnostic line uses for an audio reason, the same on every
 * client.
 * @param {AvAudioReason} reason
 * @returns {string}
 */
export function avAudioReasonName(reason) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.avAudioReasonName(reason);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The word a diagnostic line uses for a capability, the same on every
 * client.
 * @param {AvCapability} capability
 * @returns {string}
 */
export function avCapabilityName(capability) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.avCapabilityName(capability);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The word a diagnostic line uses for a video reason, the same on every
 * client.
 * @param {AvVideoReason} reason
 * @returns {string}
 */
export function avVideoReasonName(reason) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.avVideoReasonName(reason);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The letters drawn in an avatar without a photo: the first letter of each
 * of the first two words, or the first two letters of a one-word name, in
 * upper case; at most two grapheme clusters; empty when nothing in the name
 * is a letter, digit or emoji (the client then draws its placeholder).
 * @param {string} name
 * @returns {string}
 */
export function avatarInitials(name) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.avatarInitials(ptr0, len0);
        deferred2_0 = ret[0];
        deferred2_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * Compute the `BLAKE3` envelope hash (`lakko_backup::chain::envelope_hash`)
 * over the served backup envelope bytes — the 32-byte value the web client
 * feeds as `expectedEnvelopeHash` to [`verify_backup_head`] at restore, and
 * posts to `POST /auth/backup/head` at mint. Exposed so all three clients
 * compute the identical hash without re-implementing BLAKE3. Returns the
 * 32-byte hash.
 * @param {Uint8Array} envelope_bytes
 * @returns {Uint8Array}
 */
export function backupEnvelopeHash(envelope_bytes) {
    const ptr0 = passArray8ToWasm0(envelope_bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.backupEnvelopeHash(ptr0, len0);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * `true` when `incoming` replaces `stored` for the same channel.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function banListIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.banListIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * The most keys one list holds.
 * @returns {number}
 */
export function banListMaxKeys() {
    const ret = wasm.banListMaxKeys();
    return ret >>> 0;
}

/**
 * Strip the leading `@` people type and no namespace stores.
 * @param {string} typed
 * @returns {string}
 */
export function bareName(typed) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ptr0 = passStringToWasm0(typed, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.bareName(ptr0, len0);
        deferred2_0 = ret[0];
        deferred2_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * Build the structured backup payload wire bytes that go into
 * [`seal_backup`].
 *
 * Caller supplies:
 *
 * - `account_id` — owner's 16-byte UUIDv7.
 * - `created_at_ms` — wall-clock UNIX ms at backup-creation time
 *   (caller passes `Date.now()` so the server-side rejection of
 *   stale uploads — should it ever land — has a stable reference).
 * - `ed25519_seed` — 32-byte identity signing seed.
 * - `contacts` — `Array<BackupContact>` produced by the
 *   constructor above. Empty array is fine — degenerate case
 *   for a brand-new account with no accepted contacts yet.
 * - `known_group_ids` — the MLS `group_id`s of the conversations this
 *   account's devices know about, flat-concatenated 32-byte ids.
 * - `synced_state` — `SyncedState.toBytes()`, or omitted / empty.
 *
 * The payload carries no MLS state. An OpenMLS provider snapshot belongs
 * to the device that made it, so it is never backed up: a restored device
 * is admitted to its conversations by other members (ADR-0050 section
 * 2.5a).
 *
 * # Errors
 *
 * `JsError` on wrong-length `account_id` (≠ 16) /
 * `ed25519_seed` (≠ 32), a `known_group_ids` length that is not a
 * multiple of 32, a `synced_state` the fold refuses, or any other
 * [`BackupPayload::encode`] failure mode.
 * @param {Uint8Array} account_id
 * @param {bigint} created_at_ms
 * @param {Uint8Array} ed25519_seed
 * @param {BackupContact[]} contacts
 * @param {Uint8Array} known_group_ids
 * @param {Uint8Array | null} [synced_state]
 * @param {Uint8Array | null} [own_predecessor_seeds]
 * @param {BigUint64Array | null} [own_predecessor_adopted_at_ms]
 * @param {Uint8Array | null} [own_profile]
 * @param {Uint8Array | null} [visibility]
 * @param {BackupReceivedProfile[] | null} [received_profiles]
 * @returns {Uint8Array}
 */
export function buildBackupPayload(account_id, created_at_ms, ed25519_seed, contacts, known_group_ids, synced_state, own_predecessor_seeds, own_predecessor_adopted_at_ms, own_profile, visibility, received_profiles) {
    const ptr0 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(ed25519_seed, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayJsValueToWasm0(contacts, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(known_group_ids, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    var ptr4 = isLikeNone(synced_state) ? 0 : passArray8ToWasm0(synced_state, wasm.__wbindgen_malloc);
    var len4 = WASM_VECTOR_LEN;
    var ptr5 = isLikeNone(own_predecessor_seeds) ? 0 : passArray8ToWasm0(own_predecessor_seeds, wasm.__wbindgen_malloc);
    var len5 = WASM_VECTOR_LEN;
    var ptr6 = isLikeNone(own_predecessor_adopted_at_ms) ? 0 : passArray64ToWasm0(own_predecessor_adopted_at_ms, wasm.__wbindgen_malloc);
    var len6 = WASM_VECTOR_LEN;
    var ptr7 = isLikeNone(own_profile) ? 0 : passArray8ToWasm0(own_profile, wasm.__wbindgen_malloc);
    var len7 = WASM_VECTOR_LEN;
    var ptr8 = isLikeNone(visibility) ? 0 : passArray8ToWasm0(visibility, wasm.__wbindgen_malloc);
    var len8 = WASM_VECTOR_LEN;
    var ptr9 = isLikeNone(received_profiles) ? 0 : passArrayJsValueToWasm0(received_profiles, wasm.__wbindgen_malloc);
    var len9 = WASM_VECTOR_LEN;
    const ret = wasm.buildBackupPayload(ptr0, len0, created_at_ms, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, ptr5, len5, ptr6, len6, ptr7, len7, ptr8, len8, ptr9, len9);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v11 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v11;
}

/**
 * `true` when a bundle stamped `incomingUpdatedAtMs` must replace the stored
 * one from the same contact. Strictly greater wins.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function bundleIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.bundleIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * The record counts a bundle may carry.
 * @returns {Uint32Array}
 */
export function bundleSizeBuckets() {
    const ret = wasm.bundleSizeBuckets();
    var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * The running time the bar shows, or `undefined` when its line carries a
 * word instead. Reading this without {@link callBarStatusKind} is the
 * mistake the pair exists to prevent: a line never says two things.
 * @param {CallBarPhase} phase
 * @param {bigint} connected_for_ms
 * @returns {bigint | undefined}
 */
export function callBarElapsedMs(phase, connected_for_ms) {
    const ret = wasm.callBarElapsedMs(phase, connected_for_ms);
    return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
}

/**
 * What the collapsed bar's one status line says: `calling`, `connecting`,
 * `reconnecting`, or `elapsed` - and never two at once, which is why this
 * answers a single name rather than three booleans.
 *
 * On `elapsed` the client formats {@link callBarElapsedMs}; how long a call
 * has been running is not a presentation decision, so the rule carries the
 * number through untouched.
 * @param {CallBarPhase} phase
 * @returns {string}
 */
export function callBarStatusKind(phase) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.callBarStatusKind(phase);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Whether the collapsed call bar is on screen.
 *
 * `callScreenUp` is the client's own read of its call presentation. One
 * state, one place: the bar exists because the call screen is away.
 * @param {CallBarPhase} phase
 * @param {boolean} call_screen_up
 * @returns {boolean}
 */
export function callBarVisible(phase, call_screen_up) {
    const ret = wasm.callBarVisible(phase, call_screen_up);
    return ret !== 0;
}

/**
 * Lay `count` equal tiles into a call area `width` by `height` (the space
 * between the status line and the control bar, inside the side margins).
 * @param {number} count
 * @param {number} width
 * @param {number} height
 * @returns {CallGrid}
 */
export function callGrid(count, width, height) {
    const ret = wasm.callGrid(count, width, height);
    return CallGrid.__wrap(ret);
}

/**
 * Whether the composer's voice and video-note recorder must stand aside
 * because a call is holding the microphone and camera.
 *
 * `callIsLive` is the client's read of its one app-wide call slot: a call
 * collapsed into the bar of `docs/ux/calls.md` section 4.5 is live in every
 * chat, not only in the one it was placed from.
 * `unansweredIncomingRing` narrows it to the one state that owns no
 * capture: an incoming call still being offered. An outgoing call holds
 * capture from the moment it is placed, so it is deliberately not carved
 * out.
 * @param {boolean} call_is_live
 * @param {boolean} unanswered_incoming_ring
 * @returns {boolean}
 */
export function callHoldsCapture(call_is_live, unanswered_incoming_ring) {
    const ret = wasm.callHoldsCapture(call_is_live, unanswered_incoming_ring);
    return ret !== 0;
}

/**
 * The expiry stamp for a link to a live call minted at `now_ms`.
 *
 * The person is no longer asked how long their link should last, so the
 * answer is decided once, in `lakko_contacts::call_invite`, and reached from
 * here — three clients each carrying their own constant is how the window
 * came to differ between them in the first place.
 *
 * Saturating, and never the never-expires value, which that field's own
 * documentation names a stalking risk. A `bigint` on the JS side.
 * @param {bigint} now_ms
 * @returns {bigint}
 */
export function callInviteExpiryMs(now_ms) {
    const ret = wasm.callInviteExpiryMs(now_ms);
    return BigInt.asUintN(64, ret);
}

/**
 * How many people a link to a live call admits: no limit.
 *
 * The host admits every arrival, so a count here would cap a meeting at the
 * door of a room the host was holding open.
 * @returns {number}
 */
export function callInviteMaxUses() {
    const ret = wasm.callInviteMaxUses();
    return ret >>> 0;
}

/**
 * Re-export the floor constant so platform code can keep
 * its own size sanity-check (`kp.length >=
 * CALL_KP_FORWARD_MIN_PAYLOAD_LEN` before calling
 * `wrapCallKeyPackage`).
 * @returns {number}
 */
export function callKeyPackageMinPayloadLen() {
    const ret = wasm.callKeyPackageMinPayloadLen();
    return ret >>> 0;
}

/**
 * How long a call rings before both sides give up, in milliseconds.
 *
 * One copy, because it is a number two DIFFERENT devices must agree on.
 * @returns {bigint}
 */
export function callRingWindowMs() {
    const ret = wasm.callRingWindowMs();
    return BigInt.asUintN(64, ret);
}

/**
 * Derive the human-comparable call **safety number** from the shared
 * 32-byte per-call seed.
 *
 * Every participant derives the same seed via the MLS exporter, so the
 * safety number is identical for everyone on the call. Render it in the
 * in-call UI for an out-of-band man-in-the-middle check: matching codes
 * prove a shared E2EE root key (security invariant #4). The format is
 * four space-separated four-digit groups.
 *
 * # Errors
 *
 * `JsError` if `seed` is not exactly 32 bytes.
 * @param {Uint8Array} seed
 * @returns {string}
 */
export function callSafetyNumber(seed) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.callSafetyNumber(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * The peer id a one-to-one call's screen-share state carries (ADR-0083).
 * @returns {string}
 */
export function callScreenShareOneToOnePeerId() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.callScreenShareOneToOnePeerId();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Who is on the stage in the layout with one large tile: an index into the
 * roster, or `undefined` when the call is empty.
 *
 * `flags` carries one byte per tile (bit 1: this device's own tile, bit 2:
 * camera on, bit 4: speaking now, bit 8: a shared screen) and `lastSpokeAtMs` one number per person
 * (0 for never), both in roster order. A `lastSpokeAtMs` shorter than
 * `flags` reads as "never" for the people it does not cover.
 * @param {Uint8Array} flags
 * @param {Float64Array} last_spoke_at_ms
 * @param {number | null} [pinned]
 * @returns {number | undefined}
 */
export function callStage(flags, last_spoke_at_ms, pinned) {
    const ptr0 = passArray8ToWasm0(flags, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayF64ToWasm0(last_spoke_at_ms, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.callStage(ptr0, len0, ptr1, len1, isLikeNone(pinned) ? Number.MAX_SAFE_INTEGER : (pinned) >>> 0);
    return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
}

/**
 * Re-export the floor constant so platform code can keep its
 * own size sanity-check (`welcome.length >=
 * CALL_WELCOME_MIN_PAYLOAD_LEN` before calling
 * `wrapCallWelcome`).
 * @returns {number}
 */
export function callWelcomeMinPayloadLen() {
    const ret = wasm.callWelcomeMinPayloadLen();
    return ret >>> 0;
}

/**
 * The stored form of a language tag, or `undefined` for a tag that names no
 * language.
 * @param {string} tag
 * @returns {string | undefined}
 */
export function canonicalLanguageTag(tag) {
    const ptr0 = passStringToWasm0(tag, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.canonicalLanguageTag(ptr0, len0);
    let v2;
    if (ret[0] !== 0) {
        v2 = getStringFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * The X25519 public key of an admission seed.
 *
 * # Errors
 *
 * Throws `admissionTicket.badLength: ...`.
 * @param {Uint8Array} admission_seed
 * @returns {Uint8Array}
 */
export function channelAdmissionPublicKey(admission_seed) {
    const ptr0 = passArray8ToWasm0(admission_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.channelAdmissionPublicKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Length of an admission ticket.
 * @returns {number}
 */
export function channelAdmissionTicketLen() {
    const ret = wasm.channelAdmissionTicketLen();
    return ret >>> 0;
}

/**
 * The service path the owner signs to appoint (`PUT`) or revoke (`DELETE`)
 * `copublisherKey` for `channelId`.
 *
 * # Errors
 *
 * Throws when `channelId` is not 16 bytes or the key not 32.
 * @param {Uint8Array} channel_id
 * @param {Uint8Array} copublisher_key
 * @returns {string}
 */
export function channelCoPublisherPath(channel_id, copublisher_key) {
    let deferred4_0;
    let deferred4_1;
    try {
        const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(copublisher_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.channelCoPublisherPath(ptr0, len0, ptr1, len1);
        var ptr3 = ret[0];
        var len3 = ret[1];
        if (ret[3]) {
            ptr3 = 0; len3 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred4_0 = ptr3;
        deferred4_1 = len3;
        return getStringFromWasm0(ptr3, len3);
    } finally {
        wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
    }
}

/**
 * The request header a co-publisher proof travels in.
 * @returns {string}
 */
export function channelCoPublisherProofHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.channelCoPublisherProofHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The co-publisher signer of `channelId` for `accountIdentity`.
 *
 * # Errors
 *
 * Throws when `channelId` is not 16 bytes.
 * @param {Identity} account_identity
 * @param {Uint8Array} channel_id
 * @returns {ChannelCoPublisherSigner}
 */
export function channelCoPublisherSigner(account_identity, channel_id) {
    _assertClass(account_identity, Identity);
    const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.channelCoPublisherSigner(account_identity.__wbg_ptr, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ChannelCoPublisherSigner.__wrap(ret[0]);
}

/**
 * Canonical MLS exporter label for channel content-key
 * seeds — `"lakko/channel/v1"`. Pinned ASCII, never
 * translated, never re-encoded.
 *
 * Use as the `label` argument to `Group.exportSecret`
 * when deriving a channel seed. The `_v1` suffix is
 * part of the wire contract.
 * @returns {string}
 */
export function channelExporterLabel() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.channelExporterLabel();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * What a channel's info holds — section 2.3.
 * @param {ChannelInfoCapabilities} caps
 * @returns {string[]}
 */
export function channelInfoSections(caps) {
    _assertClass(caps, ChannelInfoCapabilities);
    var ptr0 = caps.__destroy_into_raw();
    const ret = wasm.channelInfoSections(ptr0);
    var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * The request header an invite proof travels in.
 * @returns {string}
 */
export function channelInviteProofHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.channelInviteProofHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Whether `language` is an admissible ISO 639 code (2 or 3 ASCII lowercase
 * letters).
 * @param {string} language
 * @returns {boolean}
 */
export function channelLanguageIsValid(language) {
    const ptr0 = passStringToWasm0(language, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.channelLanguageIsValid(ptr0, len0);
    return ret !== 0;
}

/**
 * `true` when a listing stamped `incomingUpdatedAtMs` replaces the stored
 * one for the same channel. Strictly greater.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function channelListingIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.channelListingIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * How many co-publisher keys one channel may have appointed.
 * @returns {number}
 */
export function channelMaxCoPublishers() {
    const ret = wasm.channelMaxCoPublishers();
    return ret >>> 0;
}

/**
 * `true` when a record stamped `incomingUpdatedAtMs` must replace the
 * stored one for the same channel. Strictly greater; equal is NOT
 * newer, so a re-issue of the record already held is not an update.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function channelMetaIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.channelMetaIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * The request header an owner proof travels in.
 * @returns {string}
 */
export function channelOwnerProofHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.channelOwnerProofHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The owner signer of `channelId` for `accountIdentity` (the account
 * identity, never a device identity).
 *
 * # Errors
 *
 * Throws when `channelId` is not 16 bytes.
 * @param {Identity} account_identity
 * @param {Uint8Array} channel_id
 * @returns {ChannelOwnerSigner}
 */
export function channelOwnerSigner(account_identity, channel_id) {
    _assertClass(account_identity, Identity);
    const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.channelOwnerSigner(account_identity.__wbg_ptr, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ChannelOwnerSigner.__wrap(ret[0]);
}

/**
 * Length of a reaction label: 16.
 * @returns {number}
 */
export function channelReactionLabelLen() {
    const ret = wasm.channelReactionLabelLen();
    return ret >>> 0;
}

/**
 * The most own reactions one post takes (3).
 * @returns {number}
 */
export function channelReactionsPerPostMax() {
    const ret = wasm.channelReactionsPerPostMax();
    return ret >>> 0;
}

/**
 * The 32-byte read key of `channelId`, from its OWNER's account identity.
 *
 * # Errors
 *
 * Throws `publicSeed.badLength: ...` for a bad channel id.
 * @param {Identity} owner_account_identity
 * @param {Uint8Array} channel_id
 * @returns {Uint8Array}
 */
export function channelReadKey(owner_account_identity, channel_id) {
    _assertClass(owner_account_identity, Identity);
    const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.channelReadKey(owner_account_identity.__wbg_ptr, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Build the recommended default policy — 24 h or 1 000
 * posts, whichever lands first. Pinned in ADR-0010
 * §"PublisherGroup".
 * @returns {ChannelRotationPolicy}
 */
export function channelRotationPolicyDefault() {
    const ret = wasm.channelRotationPolicyDefault();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Bump the post counter (saturating at `u32::MAX`).
 * @param {ChannelRotationState} state
 * @returns {ChannelRotationState}
 */
export function channelRotationStateAfterPost(state) {
    const ret = wasm.channelRotationStateAfterPost(state);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Stamp a fresh rotation state right after a successful
 * fanout. Caller passes `Date.now()` (or any monotonic-
 * adjacent ms timestamp).
 * @param {bigint} now_ms
 * @returns {ChannelRotationState}
 */
export function channelRotationStateAfterRotation(now_ms) {
    const ret = wasm.channelRotationStateAfterRotation(now_ms);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Genesis rotation state (`{ lastRotationMs: 0n,
 * postsSinceRotation: 0 }`).
 * @returns {ChannelRotationState}
 */
export function channelRotationStateNew() {
    const ret = wasm.channelRotationStateNew();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Total wire length of a seed envelope —
 * `eph_pub(32) + sealed_seed(32) + tag(16) = 80`. Exposed
 * so JS can pre-size buffers / sanity-check incoming
 * envelope blobs.
 * @returns {number}
 */
export function channelSeedEnvelopeLen() {
    const ret = wasm.channelSeedEnvelopeLen();
    return ret >>> 0;
}

/**
 * Wrap raw bytes from `Group.exportSecret` into the
 * canonical channel content-key seed format.
 *
 * **Production path.** The caller is expected to hold a
 * PublisherGroup (created via the wasm `Group` surface),
 * invoke `group.exportSecret(identity,
 * channelExporterLabel(), channelIdBytes, 32)`, and feed
 * the resulting 32 bytes through this function. The
 * pass-through gives the production seed a grep-clean
 * audit marker (`channelSeedFromMlsExporterOutput`) and
 * lets a future schema bump add a domain separator
 * without breaking call sites.
 *
 * Throws on `bytes.length !== 32`.
 * @param {Uint8Array} bytes
 * @returns {Uint8Array}
 */
export function channelSeedFromMlsExporterOutput(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.channelSeedFromMlsExporterOutput(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Pure decision: should the publisher rotate now?
 *
 * `now_ms` — caller-supplied epoch milliseconds (typically
 * `Date.now()`).
 * @param {ChannelRotationPolicy} policy
 * @param {ChannelRotationState} state
 * @param {bigint} now_ms
 * @returns {boolean}
 */
export function channelShouldRotate(policy, state, now_ms) {
    const ret = wasm.channelShouldRotate(policy, state, now_ms);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] !== 0;
}

/**
 * The admission-key version a ticket names, or `undefined`.
 * @param {Uint8Array} ticket
 * @returns {number | undefined}
 */
export function channelTicketAdmissionVersion(ticket) {
    const ptr0 = passArray8ToWasm0(ticket, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.channelTicketAdmissionVersion(ptr0, len0);
    return ret === 0xFFFFFF ? undefined : ret;
}

/**
 * The topic ids, in order (parallel to `channelTopicNames()`).
 * @returns {Uint8Array}
 */
export function channelTopicIds() {
    const ret = wasm.channelTopicIds();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * The English name of topic `id`; "Other" for an unknown id.
 * @param {number} id
 * @returns {string}
 */
export function channelTopicName(id) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.channelTopicName(id);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The English topic names, in id order. Id 0 is "Other".
 * @returns {string[]}
 */
export function channelTopicNames() {
    const ret = wasm.channelTopicNames();
    var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * The request header a subscriber write proof travels in.
 * @returns {string}
 */
export function channelWriteProofHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.channelWriteProofHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Check a comment's text the way sealing does.
 *
 * # Errors
 *
 * Throws `commentText.<code>: ...`.
 * @param {string} text
 */
export function checkCommentText(text) {
    const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.checkCommentText(ptr0, len0);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

/**
 * Parse and check every delegation of an envelope (ADR-0060 §6): each blob
 * parses, names the added person's mailbox, and no two name the same
 * delegate. Any failure refuses the whole envelope — keep the previous one.
 *
 * # Errors
 *
 * Throws with `committerDelegationError` `badAddedAccountKey` /
 * `malformed` / `wrongMailbox` / `duplicateDelegate`.
 * @param {CommitterDelegationEnvelope} envelope
 * @returns {Delegation[]}
 */
export function checkCommitterDelegations(envelope) {
    _assertClass(envelope, CommitterDelegationEnvelope);
    const ret = wasm.checkCommitterDelegations(envelope.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * Tally held reactions from this person's (`own`) point of view.
 *
 * # Errors
 *
 * Throws `reaction.badLength` when `own` is not 32 bytes.
 * @param {HeldReaction[]} held
 * @param {Uint8Array} own
 * @returns {ChipSource[]}
 */
export function chipSourcesFromHeld(held, own) {
    const ptr0 = passArrayJsValueToWasm0(held, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(own, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.chipSourcesFromHeld(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v3;
}

/**
 * Choose one of the three positions. Leaving Some people remembers its
 * parts; coming back restores them (or the default parts).
 * @param {VisibilitySetting} setting
 * @param {VisibilityMode} mode
 * @returns {VisibilitySetting}
 */
export function chooseVisibilityMode(setting, mode) {
    _assertClass(setting, VisibilitySetting);
    const ret = wasm.chooseVisibilityMode(setting.__wbg_ptr, mode);
    return VisibilitySetting.__wrap(ret);
}

/**
 * Decode a head.
 * @param {Uint8Array} head
 * @returns {CircleHead}
 */
export function circleDecodeHead(head) {
    const ptr0 = passArray8ToWasm0(head, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.circleDecodeHead(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return CircleHead.__wrap(ret[0]);
}

/**
 * The path of a directory on the Delivery Service, without a prefix. Its
 * routes hang off it: `/header`, `/filter`, `/rows/<index>`.
 * @param {Uint8Array} directory_id
 * @returns {string}
 */
export function circleDirectoryPath(directory_id) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passArray8ToWasm0(directory_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.circleDirectoryPath(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * The epoch whose key opens this sealed filter.
 * @param {Uint8Array} wire
 * @returns {bigint}
 */
export function circleFilterEpoch(wire) {
    const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.circleFilterEpoch(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return BigInt.asUintN(64, ret[0]);
}

/**
 * What a grant names, without its key.
 * @param {Uint8Array} grant
 * @returns {CircleGrantInfo}
 */
export function circleGrantInfo(grant) {
    const ptr0 = passArray8ToWasm0(grant, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.circleGrantInfo(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return CircleGrantInfo.__wrap(ret[0]);
}

/**
 * The length of a grant.
 * @returns {number}
 */
export function circleGrantLen() {
    const ret = wasm.circleGrantLen();
    return ret >>> 0;
}

/**
 * The most rows a lookup returns.
 * @returns {number}
 */
export function circleMaxRowsPerTerm() {
    const ret = wasm.circleMaxRowsPerTerm();
    return ret >>> 0;
}

/**
 * The most rows one write carries.
 * @returns {number}
 */
export function circleMaxRowsPerWrite() {
    const ret = wasm.circleMaxRowsPerWrite();
    return ret >>> 0;
}

/**
 * Derive the current epoch key from a grant, the encoded head and the edges
 * of the grant's path. A `headerMismatch` refusal means the header is stale or
 * partial, or this contact was removed.
 * @param {Uint8Array} grant
 * @param {Uint8Array} head
 * @param {Uint32Array} nodes
 * @param {Uint8Array} wraps
 * @returns {CircleEpochKey}
 */
export function circleMemberEpochKey(grant, head, nodes, wraps) {
    const ptr0 = passArray8ToWasm0(grant, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(head, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray32ToWasm0(nodes, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(wraps, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.circleMemberEpochKey(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return CircleEpochKey.__wrap(ret[0]);
}

/**
 * Open a sealed card with the 64-byte OPRF output of a search term.
 * @param {Uint8Array} sealed
 * @param {Uint8Array} oprf_output
 * @returns {Uint8Array}
 */
export function circleOpenCard(sealed, oprf_output) {
    const ptr0 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(oprf_output, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.circleOpenCard(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * The proof epoch of an owner's requests.
 * @returns {bigint}
 */
export function circleOwnerProofEpoch() {
    const ret = wasm.circleOwnerProofEpoch();
    return BigInt.asUintN(64, ret);
}

/**
 * The nodes whose edges a member at `leaf` reads.
 * @param {number} leaf
 * @param {number} capacity_log
 * @returns {Uint32Array}
 */
export function circlePathNodes(leaf, capacity_log) {
    const ret = wasm.circlePathNodes(leaf, capacity_log);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * The most bytes a row carries: one sealed card.
 * @returns {number}
 */
export function circleRowCardMaxLen() {
    const ret = wasm.circleRowCardMaxLen();
    return ret >>> 0;
}

/**
 * The epoch whose key opens this sealed row.
 * @param {Uint8Array} row
 * @returns {bigint}
 */
export function circleRowEpoch(row) {
    const ptr0 = passArray8ToWasm0(row, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.circleRowEpoch(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return BigInt.asUintN(64, ret[0]);
}

/**
 * Seal `card` under the search terms of the person it describes:
 * `termOutputs` concatenated, 64 bytes each. Each opens it, and nothing else
 * does: not the epoch key, not a term tag. Every sealed card has one length.
 * @param {Uint8Array} card
 * @param {Uint8Array} term_outputs
 * @returns {Uint8Array}
 */
export function circleSealCard(card, term_outputs) {
    const ptr0 = passArray8ToWasm0(card, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(term_outputs, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.circleSealCard(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * The length of every sealed card.
 * @returns {number}
 */
export function circleSealedCardLen() {
    const ret = wasm.circleSealedCardLen();
    return ret >>> 0;
}

/**
 * A new directory for the account. `accountIdentity` is the ACCOUNT identity,
 * the same on every device. Its first write is the claim.
 * @param {Identity} account_identity
 * @returns {CircleWriter}
 */
export function circleWriterCreate(account_identity) {
    _assertClass(account_identity, Identity);
    const ret = wasm.circleWriterCreate(account_identity.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return CircleWriter.__wrap(ret[0]);
}

/**
 * A writer from its kept state.
 * @param {Identity} account_identity
 * @param {Uint8Array} state
 * @returns {CircleWriter}
 */
export function circleWriterRestore(account_identity, state) {
    _assertClass(account_identity, Identity);
    const ptr0 = passArray8ToWasm0(state, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.circleWriterRestore(account_identity.__wbg_ptr, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return CircleWriter.__wrap(ret[0]);
}

/**
 * Classify an opened relay plaintext.
 * @param {Uint8Array} plaintext
 * @returns {CallRelayPayload}
 */
export function classifyCallRelayPlaintext(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.classifyCallRelayPlaintext(ptr0, len0);
    return CallRelayPayload.__wrap(ret);
}

/**
 * Classify a decrypted post plaintext: run this on EVERY post before
 * the text path. Never throws — an unrecognised plaintext is `"text"`,
 * a matching domain that fails a bound is `"malformed"`.
 * @param {Uint8Array} plaintext
 * @returns {ChannelPostContent}
 */
export function classifyChannelPostPlaintext(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.classifyChannelPostPlaintext(ptr0, len0);
    return ChannelPostContent.__wrap(ret);
}

/**
 * The 32-byte comment author key of `accountIdentity` in `channelId`.
 *
 * # Errors
 *
 * Throws `comment.badLength: ...`.
 * @param {Identity} account_identity
 * @param {Uint8Array} channel_id
 * @returns {Uint8Array}
 */
export function commentAuthorKey(account_identity, channel_id) {
    _assertClass(account_identity, Identity);
    const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.commentAuthorKey(account_identity.__wbg_ptr, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The longest comment text in bytes.
 * @returns {number}
 */
export function commentTextMaxBytes() {
    const ret = wasm.commentTextMaxBytes();
    return ret >>> 0;
}

/**
 * Exact byte length of a composed committer beacon (ADR-0045) — the domain
 * separator plus the one-byte payload.
 *
 * Exposed for the cheap length pre-filter a receive path runs before the
 * authoritative [`decode_committer_beacon`], so that length is not hardcoded
 * on three platforms. Because the beacon is field-less, every beacon is
 * byte-identical, so an exact-equality pre-filter against
 * [`compose_committer_beacon`] is also correct.
 * @returns {number}
 */
export function committerBeaconEnvelopeLen() {
    const ret = wasm.committerBeaconEnvelopeLen();
    return ret >>> 0;
}

/**
 * How long a beacon keeps counting its sender as an active candidate, in
 * milliseconds, measured against the **DS row's** age (ADR-0045). Pass this to
 * [`acting_committer_verdict`].
 *
 * It is `2 * interval + worst_case_poll`, and each term earns its place: one
 * interval because a perfectly healthy candidate's most recent beacon is
 * already that old the moment anyone looks; a second to tolerate exactly one
 * lost or delayed beacon, so a single dropped journal round does not evict a
 * live candidate; and one poll cadence because a beacon is only *observed* at
 * the observer's next poll.
 * @returns {bigint}
 */
export function committerBeaconFreshnessWindowMs() {
    const ret = wasm.committerBeaconFreshnessWindowMs();
    return BigInt.asUintN(64, ret);
}

/**
 * How often a candidate re-emits its beacon, in milliseconds (ADR-0045).
 *
 * Exposed rather than hand-copied: this and
 * [`committer_beacon_freshness_window_ms`] are two halves of one budget, and a
 * client that re-derives its emit cadence locally can silently fall out of
 * step with the window every other client is judging it against.
 *
 * **The number is CHOSEN, not measured.** It is sized for journal litter and
 * for privacy — every beacon is a journal row that persists to the 30-day
 * prune and publishes that this candidate had the app foregrounded at that
 * moment — not for responsiveness, because nothing interactive waits on it.
 * Takeover latency is bounded by the freshness window, not by this.
 * @returns {bigint}
 */
export function committerBeaconIntervalMs() {
    const ret = wasm.committerBeaconIntervalMs();
    return BigInt.asUintN(64, ret);
}

/**
 * The slowest group-poll cadence in the client tree, in milliseconds — a term
 * in the freshness window's construction (ADR-0045).
 *
 * Exposed because it is a **constraint on the caller**, not decoration: a
 * client that polls its group journal slower than this invalidates the
 * window's derivation, so the cadence has to be checkable against one shared
 * number instead of against a comment.
 * @returns {bigint}
 */
export function committerBeaconWorstCasePollMs() {
    const ret = wasm.committerBeaconWorstCasePollMs();
    return BigInt.asUintN(64, ret);
}

/**
 * Length of one delegation blob: 185.
 * @returns {number}
 */
export function committerDelegationBlobLen() {
    const ret = wasm.committerDelegationBlobLen();
    return ret >>> 0;
}

/**
 * The committer's side of ADR-0060 §6: after its sweep added the envelope's
 * person, the delegation naming `ownAccountKey` (this device's account
 * signature key), as the `x-lakko-ds-delegation` header value to send with
 * the Welcome's mailbox write — signed with
 * `DsProofSigner.forReach(accountIdentity ed25519 seed)`, the key the
 * delegation names. `undefined` when no delegation names this account. The
 * envelope is checked first (`checkCommitterDelegations`). Use a delegation
 * only from the newest envelope kept per `(MLS sender resolved at receipt,
 * addedAccountKey)`, and only for the Welcome of a sweep that added that
 * person.
 *
 * # Errors
 *
 * Every error of `checkCommitterDelegations`; `reachError` `keyLength`.
 * @param {CommitterDelegationEnvelope} envelope
 * @param {Uint8Array} own_account_key
 * @returns {string | undefined}
 */
export function committerDelegationFor(envelope, own_account_key) {
    _assertClass(envelope, CommitterDelegationEnvelope);
    const ptr0 = passArray8ToWasm0(own_account_key, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.committerDelegationFor(envelope.__wbg_ptr, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    let v2;
    if (ret[0] !== 0) {
        v2 = getStringFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * Is a proposer's committer-delegation envelope due for a pending `Add`
 * (ADR-0060 §6)? `sentAtMs` is `undefined` when none was sent; `delegated` and
 * `covered` describe the last one (whether it carried delegations, and the
 * committer candidates it covered), `candidates` the group's now, both as
 * concatenated 32-byte keys. The caller checks the `Add` is still pending
 * and that it has something to send.
 *
 * # Errors
 *
 * Throws with `committerDelegationError` `keyLength`.
 * @param {bigint | null | undefined} sent_at_ms
 * @param {boolean} delegated
 * @param {Uint8Array} covered
 * @param {Uint8Array} candidates
 * @param {bigint} now_ms
 * @returns {boolean}
 */
export function committerEnvelopeDue(sent_at_ms, delegated, covered, candidates, now_ms) {
    const ptr0 = passArray8ToWasm0(covered, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(candidates, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.committerEnvelopeDue(!isLikeNone(sent_at_ms), isLikeNone(sent_at_ms) ? BigInt(0) : sent_at_ms, delegated, ptr0, len0, ptr1, len1, now_ms);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] !== 0;
}

/**
 * How long after sending a signed committer envelope it is due again, in
 * milliseconds.
 * @returns {bigint}
 */
export function committerEnvelopeResendAfterMs() {
    const ret = wasm.committerEnvelopeResendAfterMs();
    return BigInt.asUintN(64, ret);
}

/**
 * Compose an admission-seed envelope for one co-publisher; send it with
 * `PublisherGroup.createApplicationMessage`.
 *
 * # Errors
 *
 * Throws `admissionSeed.<code>: ...`.
 * @param {Uint8Array} channel_id
 * @param {Uint8Array} member_account_key
 * @param {Uint8Array} member_x25519_public_key
 * @param {number} admission_version
 * @param {Uint8Array} admission_seed
 * @returns {Uint8Array}
 */
export function composeAdmissionSeedEnvelope(channel_id, member_account_key, member_x25519_public_key, admission_version, admission_seed) {
    const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(member_account_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(member_x25519_public_key, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(admission_seed, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.composeAdmissionSeedEnvelope(ptr0, len0, ptr1, len1, ptr2, len2, admission_version, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * Compose a ban list: the PLAINTEXT to hand to `composeChannelPost`.
 *
 * # Errors
 *
 * Throws `banList.<code>: ...`.
 * @param {BanList} list
 * @returns {Uint8Array}
 */
export function composeBanList(list) {
    _assertClass(list, BanList);
    const ret = wasm.composeBanList(list.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Compose an Answer call-signaling event.
 * @param {Uint8Array} call_id
 * @param {string} sdp
 * @returns {Uint8Array}
 */
export function composeCallAnswerSignaling(call_id, sdp) {
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(sdp, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.composeCallAnswerSignaling(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compose an End call-signaling event. `reason` is one of
 * `"hungUp"` / `"busy"` / `"declined"` / `"timedOut"` /
 * `"error"` / `"endedForAll"`; unknown values fold to `"hungUp"`.
 * @param {Uint8Array} call_id
 * @param {string} reason
 * @returns {Uint8Array}
 */
export function composeCallEndSignaling(call_id, reason) {
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.composeCallEndSignaling(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compose an IceCandidate call-signaling event.
 * @param {Uint8Array} call_id
 * @param {string} candidate
 * @param {string} sdp_mid
 * @param {number} sdp_m_line_index
 * @returns {Uint8Array}
 */
export function composeCallIceCandidateSignaling(call_id, candidate, sdp_mid, sdp_m_line_index) {
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(candidate, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(sdp_mid, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.composeCallIceCandidateSignaling(ptr0, len0, ptr1, len1, ptr2, len2, sdp_m_line_index);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Compose + sign a call-invite capability (Phase 3c3) and seal it for a
 * `/c/` link under a fresh link secret (the 2026-09-28 security pass).
 * Returns an `InviteLinkMint`; the link is `/c/<id>#<secret>` after the
 * upload.
 *
 * Same shape as `composeInviteCapability` but for a
 * link-based group-call invite. The owner embeds a
 * one-time `KeyPackage` minted via `Identity.createKeyPackage`;
 * the joiner uses it to bootstrap a fresh MLS group for
 * the call. `linkId` + `linkGrant` scope the link's reach key exactly as
 * `InviteCapability.compose` does (ADR-0060 §6).
 * @param {Uint8Array} owner_signing_seed
 * @param {Uint8Array} owner_account_id
 * @param {Uint8Array} call_id
 * @param {bigint} expiry_ms
 * @param {number} max_uses
 * @param {boolean} media_audio
 * @param {boolean} media_video
 * @param {Uint8Array} one_time_key_package
 * @param {string} label
 * @param {Uint8Array} link_id
 * @param {ReachGrant} link_grant
 * @returns {InviteLinkMint}
 */
export function composeCallInviteCapability(owner_signing_seed, owner_account_id, call_id, expiry_ms, max_uses, media_audio, media_video, one_time_key_package, label, link_id, link_grant) {
    const ptr0 = passArray8ToWasm0(owner_signing_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(owner_account_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(one_time_key_package, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passStringToWasm0(label, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len4 = WASM_VECTOR_LEN;
    const ptr5 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
    const len5 = WASM_VECTOR_LEN;
    _assertClass(link_grant, ReachGrant);
    const ret = wasm.composeCallInviteCapability(ptr0, len0, ptr1, len1, ptr2, len2, expiry_ms, max_uses, media_audio, media_video, ptr3, len3, ptr4, len4, ptr5, len5, link_grant.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return InviteLinkMint.__wrap(ret[0]);
}

/**
 * Compose an Offer call-signaling event into plaintext
 * bytes (Phase 3a3).
 * @param {Uint8Array} call_id
 * @param {string} sdp
 * @param {bigint} created_at_ms
 * @param {boolean} media_audio
 * @param {boolean} media_video
 * @returns {Uint8Array}
 */
export function composeCallOfferSignaling(call_id, sdp, created_at_ms, media_audio, media_video) {
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(sdp, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.composeCallOfferSignaling(ptr0, len0, ptr1, len1, created_at_ms, media_audio, media_video);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compose this device's peer announcement: `peerId` (the SFU Welcome's
 * `peer_id`) in call `callId`, signed with `identity`'s leaf key. Seal it
 * with `CallRelayKey.seal` before relaying it.
 *
 * # Errors
 *
 * `callIdLength`, `malformed` or `identityKeyMismatch`.
 * @param {Identity} identity
 * @param {Uint8Array} call_id
 * @param {string} peer_id
 * @returns {Uint8Array}
 */
export function composeCallPeerAnnouncement(identity, call_id, peer_id) {
    _assertClass(identity, Identity);
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(peer_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.composeCallPeerAnnouncement(identity.__wbg_ptr, ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compose this device's screen-share state (ADR-0083): share `seq` in
 * call `callId` under `peerId` (the SFU peer id, or
 * `callScreenShareOneToOnePeerId()`), sharing or stopped, signed with
 * `identity`'s leaf key.
 *
 * # Errors
 *
 * `callIdLength`, `malformed` (a bad peer id or a seq of 0) or
 * `identityKeyMismatch`.
 * @param {Identity} identity
 * @param {Uint8Array} call_id
 * @param {string} peer_id
 * @param {bigint} seq
 * @param {boolean} sharing
 * @param {boolean} with_sound
 * @returns {Uint8Array}
 */
export function composeCallScreenShare(identity, call_id, peer_id, seq, sharing, with_sound) {
    _assertClass(identity, Identity);
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(peer_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.composeCallScreenShare(identity.__wbg_ptr, ptr0, len0, ptr1, len1, seq, sharing, with_sound);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compose a one-to-one `ScreenShare` call signal (ADR-0083) carrying
 * `state`, the output of `composeCallScreenShare`.
 * @param {Uint8Array} call_id
 * @param {Uint8Array} state
 * @returns {Uint8Array}
 */
export function composeCallScreenShareSignaling(call_id, state) {
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(state, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.composeCallScreenShareSignaling(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * The binding a prospective co-publisher sends the owner with its knock.
 *
 * # Errors
 *
 * Throws when `channelId` is not 16 bytes.
 * @param {Identity} account_identity
 * @param {Uint8Array} channel_id
 * @returns {Uint8Array}
 */
export function composeChannelCoPublisherBinding(account_identity, channel_id) {
    _assertClass(account_identity, Identity);
    const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeChannelCoPublisherBinding(account_identity.__wbg_ptr, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Encode a channel invitation card for `composeChannelInviteEnvelope`.
 *
 * # Errors
 *
 * Throws for a wrong-length id or secret, or a sealed blob no `/g/` link
 * can have.
 * @param {Uint8Array} ledger_id
 * @param {Uint8Array} link_secret
 * @param {Uint8Array} sealed
 * @returns {Uint8Array}
 */
export function composeChannelInviteCard(ledger_id, link_secret, sealed) {
    const ptr0 = passArray8ToWasm0(ledger_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(link_secret, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.composeChannelInviteCard(ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Build a chat envelope carrying a channel invitation card (ADR-0087
 * section 14) beside `text` (may be empty); the card comes from
 * `composeChannelInviteCard`.
 *
 * # Errors
 *
 * Throws for an empty card or one longer than `maxChannelInviteBytes()`.
 * @param {string} text
 * @param {Uint8Array} card
 * @returns {Uint8Array}
 */
export function composeChannelInviteEnvelope(text, card) {
    const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(card, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.composeChannelInviteEnvelope(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compose a directory listing — the body `PUT /v1/channels/{id}/listing`
 * uploads.
 *
 * # Errors
 *
 * Throws an `Error` with a `channelListingError` code, or a plain `Error`
 * when `channelId` is not 16 bytes.
 * @param {Uint8Array} channel_id
 * @param {string} handle
 * @param {string} name
 * @param {string} description
 * @param {number} topic
 * @param {string} language
 * @param {bigint} updated_at_ms
 * @returns {Uint8Array}
 */
export function composeChannelListing(channel_id, handle, name, description, topic, language, updated_at_ms) {
    const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(handle, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passStringToWasm0(description, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passStringToWasm0(language, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len4 = WASM_VECTOR_LEN;
    const ret = wasm.composeChannelListing(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, topic, ptr4, len4, updated_at_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v6 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v6;
}

/**
 * Compose a channel metadata record — the PLAINTEXT to hand to
 * `composeChannelPost`. Trim the inputs first; the composer refuses
 * untrimmed text exactly as every reader's decoder does.
 *
 * # Errors
 *
 * Throws an `Error` whose `channelMetaError` property is the frozen
 * reason code (`name.empty`, `name.tooLong`, `name.lineBreak`,
 * `name.controlOrFormatCharacter`, `name.tooManyBytes`,
 * `name.notTrimmed`, and the same under `description.`, plus
 * `language.badLength` / `language.notLowercaseAscii`).
 *
 * `topic` (default 0, "Other"), `language` (default empty, "not set") and
 * `listed` (default false) are the ADR-0059 §2 fields of schema version 2;
 * since meta v3 `listed` is the channel's PUBLIC flag (ADR-0087). `handle`,
 * `avatar` and `reactions` (default: all) are the v3 fields.
 * @param {string} name
 * @param {string} description
 * @param {bigint} updated_at_ms
 * @param {number | null} [topic]
 * @param {string | null} [language]
 * @param {boolean | null} [listed]
 * @param {ConversationHandle | null} [handle]
 * @param {ProfileAvatar | null} [avatar]
 * @param {AllowedReactions | null} [reactions]
 * @returns {Uint8Array}
 */
export function composeChannelMeta(name, description, updated_at_ms, topic, language, listed, handle, avatar, reactions) {
    const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(description, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    var ptr2 = isLikeNone(language) ? 0 : passStringToWasm0(language, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len2 = WASM_VECTOR_LEN;
    let ptr3 = 0;
    if (!isLikeNone(handle)) {
        _assertClass(handle, ConversationHandle);
        ptr3 = handle.__destroy_into_raw();
    }
    let ptr4 = 0;
    if (!isLikeNone(avatar)) {
        _assertClass(avatar, ProfileAvatar);
        ptr4 = avatar.__destroy_into_raw();
    }
    let ptr5 = 0;
    if (!isLikeNone(reactions)) {
        _assertClass(reactions, AllowedReactions);
        ptr5 = reactions.__destroy_into_raw();
    }
    const ret = wasm.composeChannelMeta(ptr0, len0, ptr1, len1, updated_at_ms, isLikeNone(topic) ? 0xFFFFFF : topic, ptr2, len2, isLikeNone(listed) ? 0xFFFFFF : listed ? 1 : 0, ptr3, ptr4, ptr5);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v7 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v7;
}

/**
 * Compose a signed encrypted ChannelPost.
 *
 * `channelId` must be 16 bytes (ADR-0010); the JS caller
 * hands it as a `Uint8Array` — typically the decoded form
 * of the 32-char hex id from the URL. `seed` is the
 * caller-side content-key seed (in production: result of
 * `lakko_mls::group::Group::export_secret(
 * "lakko/channel/v1", channelId, 32)`).
 *
 * Throws on:
 * - wrong `channelId` length
 * - wrong `seed` length
 * - `plaintext.length` exceeds `lakko_channels::post::
 *   MAX_PLAINTEXT_LEN` (16 MiB)
 * @param {Uint8Array} seed
 * @param {Uint8Array} channel_id
 * @param {number} epoch_id
 * @param {ChannelSigningKey} signing_key
 * @param {bigint} message_counter
 * @param {Uint8Array} plaintext
 * @returns {Uint8Array}
 */
export function composeChannelPost(seed, channel_id, epoch_id, signing_key, message_counter, plaintext) {
    const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    _assertClass(signing_key, ChannelSigningKey);
    const ptr2 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.composeChannelPost(ptr0, len0, ptr1, len1, epoch_id, signing_key.__wbg_ptr, message_counter, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * ADR-0087 sections 7 and 14 — mint a channel SUBSCRIBER invitation over
 * the 16-byte `channelId` (no knock key): its sealed preview carries the
 * channel's name (required), description, photo and access keys. Returns
 * an `InviteLinkMint`.
 *
 * # Errors
 *
 * Throws on a bad seed, channel id or key, a name / description the
 * channel rules refuse, or a photo out of bounds.
 * @param {Uint8Array} issuer_signing_seed
 * @param {Uint8Array} channel_id
 * @param {bigint} expiry_ms
 * @param {string} channel_name
 * @param {string} description
 * @param {ChannelAccess} access
 * @param {ProfileAvatar | null} [avatar]
 * @returns {InviteLinkMint}
 */
export function composeChannelSubscriberInviteCapability(issuer_signing_seed, channel_id, expiry_ms, channel_name, description, access, avatar) {
    const ptr0 = passArray8ToWasm0(issuer_signing_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(channel_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passStringToWasm0(description, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len3 = WASM_VECTOR_LEN;
    _assertClass(access, ChannelAccess);
    let ptr4 = 0;
    if (!isLikeNone(avatar)) {
        _assertClass(avatar, ProfileAvatar);
        ptr4 = avatar.__destroy_into_raw();
    }
    const ret = wasm.composeChannelSubscriberInviteCapability(ptr0, len0, ptr1, len1, expiry_ms, ptr2, len2, ptr3, len3, access.__wbg_ptr, ptr4);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return InviteLinkMint.__wrap(ret[0]);
}

/**
 * ADR-0028 OQ3 / ADR-0078 section 6 — mint a co-publisher `/g/` link over a
 * channel's PublisherGroup `groupId`, its sealed preview carrying the
 * 16-byte `channelId` and the channel's name (optional). Since ADR-0078 the
 * PublisherGroup id is derived from the owner's seed, so nothing inverts it,
 * and the plaintext capability must not name the channel. Returns an
 * `InviteLinkMint`.
 *
 * # Errors
 *
 * Throws on a bad seed, group id or channel id, or a name the rule refuses.
 * @param {Uint8Array} issuer_signing_seed
 * @param {Uint8Array} group_id
 * @param {bigint} expiry_ms
 * @param {Uint8Array} channel_id
 * @param {string | null | undefined} channel_name
 * @param {Uint8Array} knock_public_key
 * @param {number} knock_version
 * @param {ProfileAvatar | null} [avatar]
 * @returns {InviteLinkMint}
 */
export function composeCoPublisherInviteCapability(issuer_signing_seed, group_id, expiry_ms, channel_id, channel_name, knock_public_key, knock_version, avatar) {
    const ptr0 = passArray8ToWasm0(issuer_signing_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    var ptr3 = isLikeNone(channel_name) ? 0 : passStringToWasm0(channel_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray8ToWasm0(knock_public_key, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    let ptr5 = 0;
    if (!isLikeNone(avatar)) {
        _assertClass(avatar, ProfileAvatar);
        ptr5 = avatar.__destroy_into_raw();
    }
    const ret = wasm.composeCoPublisherInviteCapability(ptr0, len0, ptr1, len1, expiry_ms, ptr2, len2, ptr3, len3, ptr4, len4, knock_version, ptr5);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return InviteLinkMint.__wrap(ret[0]);
}

/**
 * Compose a committer-liveness beacon (ADR-0045 decision 2, variant 1).
 *
 * The returned bytes are the **inner** plaintext handed to the MLS
 * application-message send path, which seals them before they reach the DS.
 * A fixed 27 bytes, identical on every call and for every sender, because the
 * beacon carries no fields at all: naming the sender would add an
 * unauthenticated identity claim next to the MLS-authenticated one, a
 * self-reported timestamp would let a candidate hold the committer duty by
 * asserting its own freshness, and a self-reported rank is one every correct
 * receiver must ignore.
 *
 * Gate the call on [`should_emit_beacon`], and only from a **foregrounded**
 * client that is actually polling — a beacon means *able to sweep*, and the
 * service-worker background decrypt structurally cannot commit (ADR-0041,
 * C-59), so a beacon emitted from the background is a lie that costs the group
 * a takeover.
 * @returns {Uint8Array}
 */
export function composeCommitterBeacon() {
    const ret = wasm.composeCommitterBeacon();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Compose a committer-delegation envelope: the plaintext of an MLS
 * application message the proposer sends in the group beside its `Add`
 * proposal (ADR-0060 §6). Structural checks only.
 *
 * # Errors
 *
 * Throws with `committerDelegationError` `badCount`.
 * @param {CommitterDelegationEnvelope} envelope
 * @returns {Uint8Array}
 */
export function composeCommitterDelegationEnvelope(envelope) {
    _assertClass(envelope, CommitterDelegationEnvelope);
    const ret = wasm.composeCommitterDelegationEnvelope(envelope.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Compose a conversation-handle envelope for the group's journal.
 *
 * # Errors
 *
 * Throws `conversationHandle.<code>: ...`.
 * @param {ConversationHandle} value
 * @returns {Uint8Array}
 */
export function composeConversationHandleEnvelope(value) {
    _assertClass(value, ConversationHandle);
    const ret = wasm.composeConversationHandleEnvelope(value.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Build a DELETE (tombstone) envelope (ADR-0023) for `target`.
 *
 * # Errors
 *
 * `Error` on serialisation failure.
 * @param {bigint} target
 * @returns {Uint8Array}
 */
export function composeDeleteEnvelope(target) {
    const ret = wasm.composeDeleteEnvelope(target);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Compose a device-card envelope: the bytes to hand to
 * `DeviceGroup.createServiceEnvelopeMessage`. Normalizes first, so text
 * taken from the browser never makes this fail.
 *
 * # Errors
 *
 * A wire error string; unreachable after normalization.
 * @param {DeviceCard} card
 * @returns {Uint8Array}
 */
export function composeDeviceCardEnvelope(card) {
    _assertClass(card, DeviceCard);
    const ret = wasm.composeDeviceCardEnvelope(card.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Build an EDIT envelope (ADR-0023) replacing the target message's
 * body with `new_text`. `target` is the DS `messageId` (`BigInt`).
 *
 * # Errors
 *
 * `Error` on serialisation failure.
 * @param {bigint} target
 * @param {string} new_text
 * @returns {Uint8Array}
 */
export function composeEditEnvelope(target, new_text) {
    const ptr0 = passStringToWasm0(new_text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeEditEnvelope(target, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Convenience for the no-attachment case: build a text-only
 * envelope without going through [`EnvelopeBuilder`].
 * @param {string} text
 * @returns {Uint8Array}
 */
export function composeEnvelopeText(text) {
    const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeEnvelopeText(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Compose a `GroupCallJoin` event (Phase 3c2). Sent by
 * each participant after their per-participant SFU
 * session reaches `connected`.
 * @param {Uint8Array} call_id
 * @param {number} leaf_index
 * @param {bigint} joined_at_ms
 * @returns {Uint8Array}
 */
export function composeGroupCallJoinSignaling(call_id, leaf_index, joined_at_ms) {
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeGroupCallJoinSignaling(ptr0, len0, leaf_index, joined_at_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Compose a `GroupCallLeave` event (Phase 3c2). `reason`
 * accepts the same values as `composeCallEndSignaling`;
 * only `"hungUp"` / `"timedOut"` / `"error"` are
 * meaningful for group-call leave.
 * @param {Uint8Array} call_id
 * @param {number} leaf_index
 * @param {string} reason
 * @returns {Uint8Array}
 */
export function composeGroupCallLeaveSignaling(call_id, leaf_index, reason) {
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.composeGroupCallLeaveSignaling(ptr0, len0, leaf_index, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compose a `GroupCallStart` call-signaling event
 * (Phase 3c2). Posted by the initiator to the chat MLS
 * group; observers render a joinable-call card.
 * @param {Uint8Array} call_id
 * @param {number} initiator_leaf
 * @param {bigint} created_at_ms
 * @param {boolean} media_audio
 * @param {boolean} media_video
 * @returns {Uint8Array}
 */
export function composeGroupCallStartSignaling(call_id, initiator_leaf, created_at_ms, media_audio, media_video) {
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeGroupCallStartSignaling(ptr0, len0, initiator_leaf, created_at_ms, media_audio, media_video);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Compose a group-closed envelope (ADR-0073 phase 2) — the inner plaintext
 * to publish as an MLS application message on the OLD group's journal.
 * `successorGroupId` is the recreated group's 32-byte id, or `undefined`
 * when the group is closed without one.
 *
 * # Errors
 *
 * Throws an `Error` with a `groupClosedError` code `"badSuccessorLen"`
 * when the successor is present but not 32 bytes — refused before any
 * bytes exist, since a receiver would refuse the envelope as a whole.
 * @param {bigint} closed_at_ms
 * @param {Uint8Array | null} [successor_group_id]
 * @returns {Uint8Array}
 */
export function composeGroupClosedEnvelope(closed_at_ms, successor_group_id) {
    var ptr0 = isLikeNone(successor_group_id) ? 0 : passArray8ToWasm0(successor_group_id, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeGroupClosedEnvelope(closed_at_ms, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Owner-side: compose + sign a group join-ticket capability without a
 * preview (ADR-0028) and seal it for a `/g/` link under a fresh link secret.
 * `issuerSigningSeed` is the 32-byte Ed25519 seed; `groupId` is the target
 * group's opaque MLS GroupId bytes; `expiryMs` is UNIX millis (`0` = never).
 * Returns an `InviteLinkMint`.
 *
 * `knockPublicKey` / `knockVersion` are the group's knock key the joiner
 * seals its request to (ADR-0087 section 5, capability v3).
 * @param {Uint8Array} issuer_signing_seed
 * @param {Uint8Array} group_id
 * @param {bigint} expiry_ms
 * @param {Uint8Array} knock_public_key
 * @param {number} knock_version
 * @returns {InviteLinkMint}
 */
export function composeGroupInviteCapability(issuer_signing_seed, group_id, expiry_ms, knock_public_key, knock_version) {
    const ptr0 = passArray8ToWasm0(issuer_signing_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(knock_public_key, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.composeGroupInviteCapability(ptr0, len0, ptr1, len1, expiry_ms, ptr2, len2, knock_version);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return InviteLinkMint.__wrap(ret[0]);
}

/**
 * Mint a `/g/` capability that carries a sealed preview (ADR-0028 v2) and
 * seal the whole capability for the link under the same fresh link secret
 * (the 2026-09-28 security pass). Names are prepared like a profile / group
 * name (trimmed; blank = none) and refused past the display-name rule.
 * Returns an `InviteLinkMint`.
 *
 * # Errors
 *
 * Throws on a bad seed / group id or a name the rule refuses.
 * @param {Uint8Array} issuer_signing_seed
 * @param {Uint8Array} group_id
 * @param {bigint} expiry_ms
 * @param {string | null | undefined} group_name
 * @param {string | null | undefined} inviter_name
 * @param {number} member_count
 * @param {Uint8Array} knock_public_key
 * @param {number} knock_version
 * @param {ProfileAvatar | null} [avatar]
 * @returns {InviteLinkMint}
 */
export function composeGroupInviteCapabilityWithPreview(issuer_signing_seed, group_id, expiry_ms, group_name, inviter_name, member_count, knock_public_key, knock_version, avatar) {
    const ptr0 = passArray8ToWasm0(issuer_signing_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    var ptr2 = isLikeNone(group_name) ? 0 : passStringToWasm0(group_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len2 = WASM_VECTOR_LEN;
    var ptr3 = isLikeNone(inviter_name) ? 0 : passStringToWasm0(inviter_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray8ToWasm0(knock_public_key, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    let ptr5 = 0;
    if (!isLikeNone(avatar)) {
        _assertClass(avatar, ProfileAvatar);
        ptr5 = avatar.__destroy_into_raw();
    }
    const ret = wasm.composeGroupInviteCapabilityWithPreview(ptr0, len0, ptr1, len1, expiry_ms, ptr2, len2, ptr3, len3, member_count, ptr4, len4, knock_version, ptr5);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return InviteLinkMint.__wrap(ret[0]);
}

/**
 * Joiner-side: compose + self-sign the attribution envelope binding the
 * joiner's `GroupInviteCapability` to its MLS leaf signature key (ADR-0028
 * OQ2). `joinerSigningSeed` is the joiner's 32-byte Ed25519 identity seed
 * (`Identity.ed25519Seed`); the envelope embeds the matching verifying key and
 * is signed with it, so the admin can verify the attribution is authentic. The
 * joiner posts this to the group journal alongside its external-join proposal.
 *
 * `replyGrant` (ADR-0060 §6, C-69) is the joiner's `reply` reach grant:
 * `deriveReachGrant(accountSeed, ReachClass.Reply, replyId, 0)` over a fresh
 * random 16-byte `replyId`, its public key registered with a 7-day expiry
 * BEFORE posting and removed once joined. Every member of the group can read
 * it (the ADR's stated residual). A grant of any other class throws.
 * @param {Uint8Array} joiner_signing_seed
 * @param {Uint8Array} capability_bytes
 * @param {ReachGrant} reply_grant
 * @returns {Uint8Array}
 */
export function composeGroupJoinAttribution(joiner_signing_seed, capability_bytes, reply_grant) {
    const ptr0 = passArray8ToWasm0(joiner_signing_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(capability_bytes, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    _assertClass(reply_grant, ReachGrant);
    const ret = wasm.composeGroupJoinAttribution(ptr0, len0, ptr1, len1, reply_grant.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compose a group-name envelope (ADR-0051 §1) — the inner plaintext to
 * publish as an MLS application message on the group's journal.
 *
 * The name is trimmed (blank = clear the name) and must pass the profile
 * display-name rule. The envelope produced is always one a receiver
 * accepts.
 *
 * # Errors
 *
 * Throws an `Error` with a `groupNameError` code such as
 * `"name.controlOrFormatCharacter"` or `"name.tooLong"`.
 * @param {string} name
 * @param {bigint} updated_at_ms
 * @returns {Uint8Array}
 */
export function composeGroupNameEnvelope(name, updated_at_ms) {
    const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeGroupNameEnvelope(ptr0, len0, updated_at_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Compose a group-photo envelope.
 *
 * # Errors
 *
 * Throws `groupPhoto.<code>: ...`.
 * @param {GroupPhoto} photo
 * @returns {Uint8Array}
 */
export function composeGroupPhotoEnvelope(photo) {
    _assertClass(photo, GroupPhoto);
    const ret = wasm.composeGroupPhotoEnvelope(photo.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Compose a group allowed-reactions envelope.
 *
 * # Errors
 *
 * Throws `groupReactions.<code>: ...`.
 * @param {GroupReactions} value
 * @returns {Uint8Array}
 */
export function composeGroupReactionsEnvelope(value) {
    _assertClass(value, GroupReactions);
    const ret = wasm.composeGroupReactionsEnvelope(value.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Seal a public record under the concatenated 64-byte OPRF outputs of its
 * search terms.
 *
 * # Errors
 *
 * Throws `handleRecord.<code>: ...`.
 * @param {HandleRecordPayload} payload
 * @param {Uint8Array} term_oprfs
 * @param {bigint} issued_at_ms
 * @returns {Uint8Array}
 */
export function composeHandleRecord(payload, term_oprfs, issued_at_ms) {
    _assertClass(payload, HandleRecordPayload);
    const ptr0 = passArray8ToWasm0(term_oprfs, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeHandleRecord(payload.__wbg_ptr, ptr0, len0, issued_at_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Build one introduction record findable by each search term, supplied as
 * its 64-byte server OPRF output (`Uint8Array[]`, order kept). Returns the
 * record wire.
 *
 * # Errors
 *
 * Throws with `introductionError` (`oprfLength`, `noTerms`,
 * `tooManyTerms`, `duplicateTag`, `certificate.length`, `grant.notFof`, …)
 * or `profileError` for the name / username.
 * @param {IntroductionPayload} payload
 * @param {Uint8Array[]} term_oprfs
 * @param {bigint} issued_at_ms
 * @returns {Uint8Array}
 */
export function composeIntroduction(payload, term_oprfs, issued_at_ms) {
    _assertClass(payload, IntroductionPayload);
    const ptr0 = passArrayJsValueToWasm0(term_oprfs, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeIntroduction(payload.__wbg_ptr, ptr0, len0, issued_at_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Wrap one record as an introduction offer (owner to a contact, in their
 * 1:1 conversation).
 *
 * # Errors
 *
 * Throws with `introductionError` for a malformed record.
 * @param {Uint8Array} record
 * @returns {Uint8Array}
 */
export function composeIntroductionOffer(record) {
    const ptr0 = passArray8ToWasm0(record, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeIntroductionOffer(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Build a bundle from the real record wires this device holds
 * (`Uint8Array[]`): padded to the next size bucket, shuffled, encoded. Sent
 * to each contact in their 1:1 conversation; never re-relayed.
 *
 * # Errors
 *
 * Throws with `introductionError` `tooManyRecords` or a record error.
 * @param {Uint8Array[]} records
 * @param {bigint} updated_at_ms
 * @returns {Uint8Array}
 */
export function composeIntroductionsBundle(records, updated_at_ms) {
    const ptr0 = passArrayJsValueToWasm0(records, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composeIntroductionsBundle(ptr0, len0, updated_at_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Compose a key-rotation envelope: the plaintext of ONE MLS application
 * message in the account's DeviceGroup (inside the `ServiceEnvelope`
 * carrier). `newEntropy` is the new phrase's 16-byte entropy;
 * `supersessionProof` is `signSupersessionProof(old, new, ...)`.
 * `preparedAtMs` is a `bigint`.
 *
 * **Send it only after the removed device is out of the DeviceGroup and the
 * epoch has advanced** (the module docs).
 *
 * # Errors
 *
 * Throws `bad-entropy-length` / `bad-proof-length`.
 * @param {bigint} prepared_at_ms
 * @param {Uint8Array} new_entropy
 * @param {Uint8Array} supersession_proof
 * @returns {Uint8Array}
 */
export function composeKeyRotationEnvelope(prepared_at_ms, new_entropy, supersession_proof) {
    const ptr0 = passArray8ToWasm0(new_entropy, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(supersession_proof, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.composeKeyRotationEnvelope(prepared_at_ms, ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compose a knock-seed envelope for one admin, sealed to that account's
 * X25519 key.
 *
 * # Errors
 *
 * Throws `knockSeed.<code>: ...`.
 * @param {Uint8Array} journal_id
 * @param {Uint8Array} member_account_key
 * @param {Uint8Array} member_x25519_public_key
 * @param {number} knock_version
 * @param {Uint8Array} knock_seed
 * @returns {Uint8Array}
 */
export function composeKnockSeedEnvelope(journal_id, member_account_key, member_x25519_public_key, knock_version, knock_seed) {
    const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(member_account_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(member_x25519_public_key, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(knock_seed, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.composeKnockSeedEnvelope(ptr0, len0, ptr1, len1, ptr2, len2, knock_version, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * Compose a claim envelope: the bytes to hand to
 * `DeviceGroup.createServiceEnvelopeMessage`.
 *
 * # Errors
 *
 * `"bounds: ..."` on a wrong-length input.
 * @param {Uint8Array} link_id
 * @param {Uint8Array} requester_account_key
 * @returns {Uint8Array}
 */
export function composeLinkClaimEnvelope(link_id, requester_account_key) {
    const ptr0 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(requester_account_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.composeLinkClaimEnvelope(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compose a note: the bytes to hand to
 * `DeviceGroup.createServiceEnvelopeMessage`.
 *
 * # Errors
 *
 * `"bounds: ..."` for a wrong-length id or key; `"welcomeLength: ..."` for
 * an empty or oversized Welcome.
 * @param {Uint8Array} link_id
 * @param {Uint8Array} requester_account_key
 * @param {Uint8Array} group_id
 * @param {Uint8Array} welcome
 * @returns {Uint8Array}
 */
export function composeLinkFoundedEnvelope(link_id, requester_account_key, group_id, welcome) {
    const ptr0 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(requester_account_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(welcome, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.composeLinkFoundedEnvelope(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * Compose a refusal of the request `requesterAccountKey` sent through the
 * invite link `linkId`, signed by the inviter's account key
 * (`inviterSigningSeed`, the account identity's 32-byte Ed25519 seed).
 *
 * # Errors
 *
 * A `bounds:` error when an input has the wrong length.
 * @param {Uint8Array} inviter_signing_seed
 * @param {Uint8Array} link_id
 * @param {Uint8Array} requester_account_key
 * @returns {Uint8Array}
 */
export function composeLinkRefusal(inviter_signing_seed, link_id, requester_account_key) {
    const ptr0 = passArray8ToWasm0(inviter_signing_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(requester_account_key, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.composeLinkRefusal(ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Compose a camera on/off media-state envelope
 * (camera-off-cleanup). The sender broadcasts the returned
 * bytes over the opaque SFU `signal-relay` payload; receivers
 * classify them (via `dispatchIncomingEnvelope` or
 * `decodeMediaState`) and reset the sender's tile `camOn`
 * state.
 *
 * See [`lakko_contacts::media_state`] for the wire format and
 * threat-model rationale (the SFU never parses the payload —
 * invariant #2).
 * @param {boolean} camera_on
 * @returns {Uint8Array}
 */
export function composeMediaState(camera_on) {
    const ret = wasm.composeMediaState(camera_on);
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Compose a shared-pin envelope (ADR-0052 decision 3) — the inner plaintext
 * to publish as an MLS application message on the conversation's journal.
 * Infallible: the envelope has no variable field.
 * @param {bigint} message_id
 * @param {boolean} pinned
 * @param {bigint} updated_at_ms
 * @returns {Uint8Array}
 */
export function composePinEnvelope(message_id, pinned, updated_at_ms) {
    const ret = wasm.composePinEnvelope(message_id, pinned, updated_at_ms);
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Compose a profile envelope (ADR-0048) — the inner plaintext to publish as
 * an MLS application message, or to pass to `ContactRequest.compose`.
 *
 * The display name is trimmed (blank = no name); a non-empty username is
 * normalised (`@Alice` -> `alice`) and must pass the ADR-0048 §4 rule. The
 * envelope produced is always one a receiver accepts.
 *
 * # Errors
 *
 * Throws an `Error` with a `profileError` code such as
 * `"displayName.controlOrFormatCharacter"`, `"username.tooShort"`, or the
 * ADR-0060 bounds `"certificate.length"`, `"certificate.withoutUsername"`,
 * `"grants.tooMany"`, `"grants.duplicateClass"`, `"grants.classNotAllowed"`.
 *
 * `usernameCertificate` (153 bytes) and `grants` (at most two, `Contact` /
 * `Group` / `Member` / `Channel`, one per class) are optional: `undefined`
 * means none. A group's profile carries `Group` (only where the setting
 * allows that group) + `Member`; a one-to-one conversation's profile
 * carries `Contact` + `Member`; a channel card carries `Channel`.
 *
 * `avatar` (ADR-0084) is optional: `undefined` means no photo. Its bounds
 * throw `"avatar.notSquare"`, `"avatar.dimensions"`, `"avatar.size"`,
 * `"avatar.duration"`.
 * @param {string | null | undefined} display_name
 * @param {string | null | undefined} username
 * @param {bigint} updated_at_ms
 * @param {Uint8Array | null} [account_id]
 * @param {Uint8Array | null} [username_certificate]
 * @param {ReachGrant[] | null} [grants]
 * @param {ProfileAvatar | null} [avatar]
 * @returns {Uint8Array}
 */
export function composeProfileEnvelope(display_name, username, updated_at_ms, account_id, username_certificate, grants, avatar) {
    var ptr0 = isLikeNone(display_name) ? 0 : passStringToWasm0(display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len0 = WASM_VECTOR_LEN;
    var ptr1 = isLikeNone(username) ? 0 : passStringToWasm0(username, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len1 = WASM_VECTOR_LEN;
    var ptr2 = isLikeNone(account_id) ? 0 : passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    var len2 = WASM_VECTOR_LEN;
    var ptr3 = isLikeNone(username_certificate) ? 0 : passArray8ToWasm0(username_certificate, wasm.__wbindgen_malloc);
    var len3 = WASM_VECTOR_LEN;
    var ptr4 = isLikeNone(grants) ? 0 : passArrayJsValueToWasm0(grants, wasm.__wbindgen_malloc);
    var len4 = WASM_VECTOR_LEN;
    let ptr5 = 0;
    if (!isLikeNone(avatar)) {
        _assertClass(avatar, ProfileAvatar);
        ptr5 = avatar.__destroy_into_raw();
    }
    const ret = wasm.composeProfileEnvelope(ptr0, len0, ptr1, len1, updated_at_ms, ptr2, len2, ptr3, len3, ptr4, len4, ptr5);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v7 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v7;
}

/**
 * ADR-0048 §2 trigger 4 — the newcomer's "introduce yourselves" envelope.
 * Publish it as an MLS application message right after joining a group,
 * and on opening a group with an unnamed member when
 * `shouldRequestProfiles` says so. Field-less; byte-identical every call.
 * @returns {Uint8Array}
 */
export function composeProfileRequest() {
    const ret = wasm.composeProfileRequest();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Build the public record a person in Everyone publishes with
 * `PUT /discovery/visibility` (ADR-0060 section 5): the introduction record
 * shape, findable by the username's OPRF output and every name term's,
 * with NO grant (build the payload with `IntroductionPayload.forPublicRecord`).
 *
 * # Errors
 *
 * Throws with `introductionError` `grant.unexpected` when the payload
 * carries a grant, and every error of `composeIntroduction`.
 * @param {IntroductionPayload} payload
 * @param {Uint8Array[]} term_oprfs
 * @param {bigint} issued_at_ms
 * @returns {Uint8Array}
 */
export function composePublicRecord(payload, term_oprfs, issued_at_ms) {
    _assertClass(payload, IntroductionPayload);
    const ptr0 = passArrayJsValueToWasm0(term_oprfs, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composePublicRecord(payload.__wbg_ptr, ptr0, len0, issued_at_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Compose the in-group push-routing-secret distribution envelope
 * (ADR-0042 v2, per ADR-0044) for a group's 32-byte
 * `push_routing_secret`, stamped with `mintEpoch` (a `bigint`).
 * The founder / adder / rotator broadcasts the returned bytes as
 * an MLS application message on the group journal so members +
 * joiners learn the secret; receivers classify them (via
 * `dispatchIncomingEnvelope` or `decodePushRoutingSecret`) and
 * persist the secret — never render it as a chat bubble.
 *
 * `mintEpoch` is stamped **at mint time** and then travels with
 * the secret forever: a re-broadcast MUST replay the epoch the
 * secret was minted under, never a fresh one. Re-stamping on
 * re-broadcast would make a stale secret out-rank the live one
 * and reintroduce the F-A replay (G-36 residual 9) this envelope
 * closes. The caller sources the stamp from the MLS group epoch
 * (`Group.epoch()`), which is shared state every receiver can
 * independently observe.
 *
 * See [`lakko_contacts::push_routing`] for the wire format
 * (`domain || version || mint_epoch(8, BE) || secret(32)`) and
 * threat model (the DS sees ciphertext — invariant #2).
 *
 * # Errors
 *
 * `JsError` when `secret` is not exactly 32 bytes.
 * @param {bigint} mint_epoch
 * @param {Uint8Array} secret
 * @returns {Uint8Array}
 */
export function composePushRoutingSecret(mint_epoch, secret) {
    const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.composePushRoutingSecret(mint_epoch, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Compose the reach-state envelope, sent over the owner's DeviceGroup.
 *
 * # Errors
 *
 * Throws with `visibilityError` (`tooManyEntries`, `duplicateEntry`,
 * `reach.*`).
 * @param {ReachState} state
 * @returns {Uint8Array}
 */
export function composeReachStateEnvelope(state) {
    _assertClass(state, ReachState);
    const ret = wasm.composeReachStateEnvelope(state.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Build a REACTION envelope (ADR-0023): `emoji` to `target`.
 *
 * # Errors
 *
 * `Error` if `emoji` is empty or exceeds `MAX_EMOJI_BYTES`, or on
 * serialisation failure.
 *
 * `action` is `"add"` or `"remove"` (UX reactions-and-emoji section 17);
 * `targetAuthor` the 32-byte account key of the reacted-to message's author
 * as this device resolved it, or `undefined`.
 * @param {bigint} target
 * @param {string} emoji
 * @param {string} action
 * @param {Uint8Array | null} [target_author]
 * @returns {Uint8Array}
 */
export function composeReactionEnvelope(target, emoji, action, target_author) {
    const ptr0 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(action, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    var ptr2 = isLikeNone(target_author) ? 0 : passArray8ToWasm0(target_author, wasm.__wbindgen_malloc);
    var len2 = WASM_VECTOR_LEN;
    const ret = wasm.composeReactionEnvelope(target, ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Build a READ RECEIPT envelope (ADR-0024): read up to `up_to`.
 *
 * # Errors
 *
 * `Error` on serialisation failure.
 * @param {bigint} up_to
 * @returns {Uint8Array}
 */
export function composeReadReceiptEnvelope(up_to) {
    const ret = wasm.composeReadReceiptEnvelope(up_to);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Compose the registrar-seed envelope a root sends one member inside the
 * group: `registrarSeed` sealed to `memberX25519PublicKey` (the member
 * account's identity-bundle `x25519_pubkey`), addressed by its account key.
 * `rank`: `1` member, `2` admin. The envelope's clear registrar public key
 * is derived from `registrarSeed` inside, so it always matches.
 *
 * # Errors
 *
 * On a wrong-length input, an invalid key or an unknown rank.
 * @param {Uint8Array} journal_id
 * @param {Uint8Array} member_account_key
 * @param {Uint8Array} member_x25519_public_key
 * @param {number} rank
 * @param {Uint8Array} registrar_seed
 * @returns {Uint8Array}
 */
export function composeRegistrarSeedEnvelope(journal_id, member_account_key, member_x25519_public_key, rank, registrar_seed) {
    const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(member_account_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(member_x25519_public_key, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(registrar_seed, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.composeRegistrarSeedEnvelope(ptr0, len0, ptr1, len1, ptr2, len2, rank, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * ADR-0052 — the text-only reply: [`EnvelopeBuilder::build_reply`]
 * without attachments.
 * @param {string} text
 * @param {bigint} reply_to
 * @param {string | null} [reply_author]
 * @param {string | null} [reply_excerpt]
 * @returns {Uint8Array}
 */
export function composeReplyEnvelopeText(text, reply_to, reply_author, reply_excerpt) {
    const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    var ptr1 = isLikeNone(reply_author) ? 0 : passStringToWasm0(reply_author, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len1 = WASM_VECTOR_LEN;
    var ptr2 = isLikeNone(reply_excerpt) ? 0 : passStringToWasm0(reply_excerpt, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len2 = WASM_VECTOR_LEN;
    const ret = wasm.composeReplyEnvelopeText(ptr0, len0, reply_to, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Compose the root-handover envelope (ADR-0057, ownership transfer): the
 * outgoing owner's journal root seed (`dsJournalRootSeed`) sealed to the new
 * owner's account X25519 key, addressed by its account key. Decodes with
 * rank `3`.
 *
 * # Errors
 *
 * On a wrong-length input or an invalid key.
 * @param {Uint8Array} journal_id
 * @param {Uint8Array} new_owner_account_key
 * @param {Uint8Array} new_owner_x25519_public_key
 * @param {Uint8Array} root_seed
 * @returns {Uint8Array}
 */
export function composeRootHandoverEnvelope(journal_id, new_owner_account_key, new_owner_x25519_public_key, root_seed) {
    const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(new_owner_account_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(new_owner_x25519_public_key, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(root_seed, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.composeRootHandoverEnvelope(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * Compose a client → SFU trickle-ICE candidate.
 * @param {string} candidate_sdp
 * @returns {string}
 */
export function composeSfuIceCandidate(candidate_sdp) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passStringToWasm0(candidate_sdp, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.composeSfuIceCandidate(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * Compose an application-layer ping (the SFU echoes a `Pong`).
 * @param {bigint} nonce
 * @returns {string}
 */
export function composeSfuPing(nonce) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ret = wasm.composeSfuPing(nonce);
        var ptr1 = ret[0];
        var len1 = ret[1];
        if (ret[3]) {
            ptr1 = 0; len1 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred2_0 = ptr1;
        deferred2_1 = len1;
        return getStringFromWasm0(ptr1, len1);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * Compose a client → SFU SDP answer (responds to a SFU-initiated
 * offer).
 * @param {string} sdp
 * @returns {string}
 */
export function composeSfuSdpAnswer(sdp) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passStringToWasm0(sdp, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.composeSfuSdpAnswer(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * Compose a client → SFU SDP offer.
 *
 * `light_mids` (`string[]`) names the `mid`s of the offer's video
 * m-lines that carry a light copy of the sender's camera (the same
 * picture scaled down, on its own m-line beside the main one); the SFU
 * forwards such a track as the sender's light companion. Pass `[]` for
 * an offer without one. More than `lakko_sfu_proto::MAX_LIGHT_MIDS`
 * entries, an empty entry or one longer than
 * `lakko_sfu_proto::MAX_LIGHT_MID_BYTES` bytes throws.
 * @param {string} sdp
 * @param {string[]} light_mids
 * @returns {string}
 */
export function composeSfuSdpOffer(sdp, light_mids) {
    let deferred4_0;
    let deferred4_1;
    try {
        const ptr0 = passStringToWasm0(sdp, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(light_mids, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.composeSfuSdpOffer(ptr0, len0, ptr1, len1);
        var ptr3 = ret[0];
        var len3 = ret[1];
        if (ret[3]) {
            ptr3 = 0; len3 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred4_0 = ptr3;
        deferred4_1 = len3;
        return getStringFromWasm0(ptr3, len3);
    } finally {
        wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
    }
}

/**
 * Compose a client → SFU SDP offer that also names its middle track
 * (ADR-0070 step 5).
 *
 * `middle_mids` (`string[]`) names the `mid`s of the offer's video
 * m-lines that carry the middle copy of the sender's camera (the same
 * picture at half size, on its own m-line); the SFU forwards such a
 * track only to a subscriber whose `video-wants` asks for it. The
 * bounds are those of `light_mids`; outside them this throws. With an
 * empty `middle_mids` the frame is exactly `composeSfuSdpOffer`'s.
 * @param {string} sdp
 * @param {string[]} light_mids
 * @param {string[]} middle_mids
 * @returns {string}
 */
export function composeSfuSdpOfferWithMiddle(sdp, light_mids, middle_mids) {
    let deferred5_0;
    let deferred5_1;
    try {
        const ptr0 = passStringToWasm0(sdp, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(light_mids, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArrayJsValueToWasm0(middle_mids, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.composeSfuSdpOfferWithMiddle(ptr0, len0, ptr1, len1, ptr2, len2);
        var ptr4 = ret[0];
        var len4 = ret[1];
        if (ret[3]) {
            ptr4 = 0; len4 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred5_0 = ptr4;
        deferred5_1 = len4;
        return getStringFromWasm0(ptr4, len4);
    } finally {
        wasm.__wbindgen_free(deferred5_0, deferred5_1, 1);
    }
}

/**
 * Compose a client -> SFU SDP offer that also names its screen-share
 * lines (ADR-0083). `screen_mids` (`string[]`) are the `mid`s of the
 * share's picture and, when it has one, its sound; the SFU forwards them
 * under the stream id `<peer>~screen`. The bounds are those of
 * `light_mids`; outside them this throws. With an empty `screen_mids` the
 * frame is exactly `composeSfuSdpOfferWithMiddle`'s.
 * @param {string} sdp
 * @param {string[]} light_mids
 * @param {string[]} middle_mids
 * @param {string[]} screen_mids
 * @returns {string}
 */
export function composeSfuSdpOfferWithScreen(sdp, light_mids, middle_mids, screen_mids) {
    let deferred6_0;
    let deferred6_1;
    try {
        const ptr0 = passStringToWasm0(sdp, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(light_mids, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArrayJsValueToWasm0(middle_mids, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArrayJsValueToWasm0(screen_mids, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        const ret = wasm.composeSfuSdpOfferWithScreen(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
        var ptr5 = ret[0];
        var len5 = ret[1];
        if (ret[3]) {
            ptr5 = 0; len5 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred6_0 = ptr5;
        deferred6_1 = len5;
        return getStringFromWasm0(ptr5, len5);
    } finally {
        wasm.__wbindgen_free(deferred6_0, deferred6_1, 1);
    }
}

/**
 * Compose a `SignalRelay` broadcast to every other peer in the call.
 * @param {Uint8Array} payload
 * @returns {string}
 */
export function composeSfuSignalRelayToCall(payload) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passArray8ToWasm0(payload, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.composeSfuSignalRelayToCall(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * Compose a `SignalRelay` to a single peer. `payload` is the raw
 * opaque bytes (SDP / ICE / SFrame-key envelope); base64url-encoded
 * into the frame.
 * @param {string} peer_id
 * @param {Uint8Array} payload
 * @returns {string}
 */
export function composeSfuSignalRelayToPeer(peer_id, payload) {
    let deferred4_0;
    let deferred4_1;
    try {
        const ptr0 = passStringToWasm0(peer_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(payload, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.composeSfuSignalRelayToPeer(ptr0, len0, ptr1, len1);
        var ptr3 = ret[0];
        var len3 = ret[1];
        if (ret[3]) {
            ptr3 = 0; len3 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred4_0 = ptr3;
        deferred4_1 = len3;
        return getStringFromWasm0(ptr3, len3);
    } finally {
        wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
    }
}

/**
 * Compose a client → SFU `video-wants` frame (ADR-0070 step 5): which
 * picture of each publisher this subscriber shows.
 *
 * `peers` and `qualities` are parallel `string[]`s: `qualities[i]` is
 * `"main"`, `"middle"` or `"light"` (`tileVideoQualityName`) for the
 * publisher `peers[i]`. Each frame replaces the whole set; a publisher
 * left out is `main`. Throws when the two lengths differ, a quality is
 * none of the three, or the list is outside the protocol's bounds (more
 * than `lakko_sfu_proto::MAX_VIDEO_WANTS` entries, an empty peer id or
 * one longer than `lakko_sfu_proto::MAX_VIDEO_WANT_PEER_BYTES` bytes).
 * @param {string[]} peers
 * @param {string[]} qualities
 * @returns {string}
 */
export function composeSfuVideoWants(peers, qualities) {
    let deferred4_0;
    let deferred4_1;
    try {
        const ptr0 = passArrayJsValueToWasm0(peers, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(qualities, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.composeSfuVideoWants(ptr0, len0, ptr1, len1);
        var ptr3 = ret[0];
        var len3 = ret[1];
        if (ret[3]) {
            ptr3 = 0; len3 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred4_0 = ptr3;
        deferred4_1 = len3;
        return getStringFromWasm0(ptr3, len3);
    } finally {
        wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
    }
}

/**
 * Compose a shared-channels envelope (ADR-0059 §6) — the inner plaintext to
 * publish as an MLS application message on the group's journal.
 * `channelIds` is the 16-byte ids concatenated; `handles` and `names` are
 * parallel to it.
 *
 * # Errors
 *
 * Throws an `Error` with a `sharedChannelsError` code, or a plain `Error`
 * when the three lists do not line up.
 * @param {bigint} updated_at_ms
 * @param {Uint8Array} channel_ids
 * @param {string[]} handles
 * @param {string[]} names
 * @returns {Uint8Array}
 */
export function composeSharedChannelsEnvelope(updated_at_ms, channel_ids, handles, names) {
    const ptr0 = passArray8ToWasm0(channel_ids, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayJsValueToWasm0(handles, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayJsValueToWasm0(names, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.composeSharedChannelsEnvelope(updated_at_ms, ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Build an ephemeral TYPING envelope (ADR-0024).
 *
 * # Errors
 *
 * `Error` on serialisation failure.
 * @param {boolean} is_typing
 * @returns {Uint8Array}
 */
export function composeTypingEnvelope(is_typing) {
    const ret = wasm.composeTypingEnvelope(is_typing);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Compose the setting envelope, sent over the owner's DeviceGroup.
 *
 * # Errors
 *
 * Throws with `visibilityError` (`someWithNoPart`, `partsOutsideSome`,
 * `tooManyChosen`, `duplicateScope`, `scopeIdLength`,
 * `tooManyChosenContacts`, `duplicateContact`).
 * @param {VisibilitySetting} setting
 * @returns {Uint8Array}
 */
export function composeVisibilityEnvelope(setting) {
    _assertClass(setting, VisibilitySetting);
    const ret = wasm.composeVisibilityEnvelope(setting.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Compute the per-joiner consume-dedup tag for an invite-link
 * `(capability_bytes, joiner_account_id)` pair (Phase 1.18d3).
 *
 * The web client calls this just before posting
 * `POST /discovery/invite/{blob_id}/consume` so the server
 * can dedup repeat consumes from the same joiner without
 * learning the joiner's `account_id` in plaintext. Layout +
 * privacy story: see
 * [`lakko_contacts::invite::compute_joiner_tag`].
 *
 * Returns the 32-byte HMAC-SHA-256 tag. Caller base64-encodes
 * for the JSON body.
 *
 * # Errors
 *
 * `JsError` if `joinerAccountId` is the wrong length —
 * 16 bytes is the only supported shape today (UUIDv7 raw).
 * The capability slice is unbounded by length here; the
 * 1.18d1 server-side ceiling enforces the actual cap.
 * @param {Uint8Array} capability_bytes
 * @param {Uint8Array} joiner_account_id
 * @returns {Uint8Array}
 */
export function computeJoinerInviteTag(capability_bytes, joiner_account_id) {
    const ptr0 = passArray8ToWasm0(capability_bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(joiner_account_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.computeJoinerInviteTag(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Compute the canonical Signal-style safety number for two
 * Ed25519 identity public keys (Phase 1.9b3 — ADR-0005 §13.5).
 *
 * The output is a 71-byte ASCII string: 12 groups of 5 decimal
 * digits separated by single spaces, e.g. `"12345 67890 …"`.
 * Both sides of a conversation compute the same string
 * regardless of input order, so the OOB compare ("read me your
 * number / mine reads X") works either way.
 *
 * Each pubkey input is the raw 32-byte Ed25519 verifying key.
 * The inputs are NOT validated as on-curve points — the
 * function is purely a hash over the bytes; callers that need
 * on-curve validation should do it before invoking this helper.
 *
 * # Errors
 *
 * `JsError` when either input is not exactly 32 bytes long.
 * @param {Uint8Array} my_pubkey
 * @param {Uint8Array} their_pubkey
 * @returns {string}
 */
export function computeSafetyNumber(my_pubkey, their_pubkey) {
    let deferred4_0;
    let deferred4_1;
    try {
        const ptr0 = passArray8ToWasm0(my_pubkey, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(their_pubkey, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.computeSafetyNumber(ptr0, len0, ptr1, len1);
        var ptr3 = ret[0];
        var len3 = ret[1];
        if (ret[3]) {
            ptr3 = 0; len3 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred4_0 = ptr3;
        deferred4_1 = len3;
        return getStringFromWasm0(ptr3, len3);
    } finally {
        wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
    }
}

/**
 * How far a conversation is read: the highest id such that every id at or
 * below it is seen, or `undefined`. Ids are BigInt arrays.
 * @param {BigInt64Array} ids
 * @param {BigInt64Array} seen
 * @returns {bigint | undefined}
 */
export function contiguousSeen(ids, seen) {
    const ptr0 = passArray64ToWasm0(ids, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray64ToWasm0(seen, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.contiguousSeen(ptr0, len0, ptr1, len1);
    return ret[0] === 0 ? undefined : ret[1];
}

/**
 * The 32-byte **continuity reach seed** of an account at a key generation
 * (ADR-0073 R.4): `BLAKE3-derive-key("lakko/reach/continuity/v1",
 * accountId(16) || keyGeneration(8, BE))`.
 *
 * After an account key reset the resetting device registers
 * `reachVerifyingKey(seed)` on its new mailbox (with an expiry) and a peer
 * that accepted the reset signs its Welcome with
 * `DsProofSigner.forReach(seed)`. `keyGeneration` (a `bigint`) is the
 * generation the reset moved to, as the verified key-reset attestation
 * states it. No input is secret: it is a reach key, never a credential.
 *
 * # Errors
 *
 * Throws with `reachError` `accountIdLength`.
 * @param {Uint8Array} account_id
 * @param {bigint} key_generation
 * @returns {Uint8Array}
 */
export function continuityReachSeed(account_id, key_generation) {
    const ptr0 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.continuityReachSeed(ptr0, len0, key_generation);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The language the conversation bar names, in canonical form, or `undefined`.
 *
 * `recentTags[i]` and `recentPermille[i]` describe the same message, newest
 * first; extra entries of the longer array are ignored. Show the bar only
 * when the engine's answer for the returned language is not `Unavailable`.
 * @param {boolean} enabled
 * @param {boolean} auto_translate_on
 * @param {string[]} recent_tags
 * @param {Uint16Array} recent_permille
 * @param {string[]} dismissed
 * @param {string[]} reading_languages
 * @returns {string | undefined}
 */
export function conversationBarLanguage(enabled, auto_translate_on, recent_tags, recent_permille, dismissed, reading_languages) {
    const ptr0 = passArrayJsValueToWasm0(recent_tags, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray16ToWasm0(recent_permille, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayJsValueToWasm0(dismissed, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArrayJsValueToWasm0(reading_languages, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.conversationBarLanguage(enabled, auto_translate_on, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    let v5;
    if (ret[0] !== 0) {
        v5 = getStringFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v5;
}

/**
 * How many conversation-end registers a client writes:
 * `composeConversationEnded` answers `undefined` for a new conversation past
 * it. A client-side limit, chosen; NOT a decode bound.
 * @returns {number}
 */
export function conversationEndsMaxClient() {
    const ret = wasm.conversationEndsMaxClient();
    return ret >>> 0;
}

/**
 * Longest conversation-handle envelope.
 * @returns {number}
 */
export function conversationHandleEnvelopeMaxLen() {
    const ret = wasm.conversationHandleEnvelopeMaxLen();
    return ret >>> 0;
}

/**
 * Shortest conversation-handle envelope.
 * @returns {number}
 */
export function conversationHandleEnvelopeMinLen() {
    const ret = wasm.conversationHandleEnvelopeMinLen();
    return ret >>> 0;
}

/**
 * `true` when `incomingMs` replaces `storedMs`.
 * @param {bigint} incoming_ms
 * @param {bigint} stored_ms
 * @returns {boolean}
 */
export function conversationHandleIsNewer(incoming_ms, stored_ms) {
    const ret = wasm.conversationHandleIsNewer(incoming_ms, stored_ms);
    return ret !== 0;
}

/**
 * What a group's or channel's info holds under ADR-0087 (UX
 * `group-and-channel-privacy.md` sections 4, 10, 11, 12.1 and 15), as
 * kebab-case section names. `kind` is `"group"` or `"channel"`, `role`
 * `"owner"`, `"admin"` or `"member"`. Replaces `groupInfoSections` and
 * `channelInfoSections` once the clients move to it.
 *
 * # Errors
 *
 * Throws `info.badKind` / `info.badRole` for another value.
 * @param {string} kind
 * @param {string} role
 * @param {boolean} alone
 * @param {boolean} can_transfer
 * @param {boolean} can_leave
 * @param {boolean} has_handle
 * @returns {string[]}
 */
export function conversationInfoSections(kind, role, alone, can_transfer, can_leave, has_handle) {
    const ptr0 = passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(role, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.conversationInfoSections(ptr0, len0, ptr1, len1, alone, can_transfer, can_leave, has_handle);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v3;
}

/**
 * Map a UNIX-millisecond timestamp to the current epoch counter.
 * Mirror of [`lakko_presence::current_epoch`].
 * @param {bigint} now_unix_ms
 * @param {bigint} epoch_secs
 * @returns {bigint}
 */
export function currentEpoch(now_unix_ms, epoch_secs) {
    const ret = wasm.currentEpoch(now_unix_ms, epoch_secs);
    return BigInt.asUintN(64, ret);
}

/**
 * Strictly decode an admission-seed envelope.
 *
 * # Errors
 *
 * Throws `admissionSeed.<code>: ...`.
 * @param {Uint8Array} plaintext
 * @returns {AdmissionSeedEnvelope}
 */
export function decodeAdmissionSeedEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeAdmissionSeedEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return AdmissionSeedEnvelope.__wrap(ret[0]);
}

/**
 * Strictly decode a ban list.
 *
 * # Errors
 *
 * Throws `banList.<code>: ...`.
 * @param {Uint8Array} plaintext
 * @returns {BanList}
 */
export function decodeBanList(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeBanList(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return BanList.__wrap(ret[0]);
}

/**
 * Decode and verify a binding (the signature under its embedded account
 * key). The caller still checks `accountPubkey` and `channelId`.
 *
 * # Errors
 *
 * Throws an `Error` carrying `channelCoPublisherBindingError`.
 * @param {Uint8Array} bytes
 * @returns {ChannelCoPublisherBinding}
 */
export function decodeChannelCoPublisherBinding(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeChannelCoPublisherBinding(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ChannelCoPublisherBinding.__wrap(ret[0]);
}

/**
 * Decode a directory listing. Fail-closed.
 *
 * # Errors
 *
 * Throws an `Error` with a `channelListingError` code.
 * @param {Uint8Array} bytes
 * @returns {ChannelListingRecord}
 */
export function decodeChannelListing(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeChannelListing(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ChannelListingRecord.__wrap(ret[0]);
}

/**
 * Decode a `ChannelPost` from its on-wire bytes without
 * verifying the signature or attempting to decrypt.
 * Throws on a wire-format failure.
 * @param {Uint8Array} wire
 * @returns {ChannelPost}
 */
export function decodeChannelPost(wire) {
    const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeChannelPost(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ChannelPost.__wrap(ret[0]);
}

/**
 * Strictly decode a committer-liveness beacon (ADR-0045). Fail-closed:
 * throws for a plaintext that does not carry the beacon domain separator, or
 * whose payload is the wrong length or an unknown schema version.
 *
 * **Returning without throwing is the entire result.** There is deliberately
 * no payload to hand back — everything a receiver needs (who sent it, how old
 * it is) comes from the MLS sender leaf and the DS row, not from the bytes.
 *
 * Use [`dispatch_incoming_envelope`] instead when the plaintext might be any
 * relay-payload / in-group envelope kind; it classifies a beacon as
 * [`WasmIncomingEnvelopeKind::CommitterBeacon`] and falls through to the other
 * kinds rather than throwing.
 *
 * # Errors
 *
 * `JsError` when `plaintext` is not a valid committer beacon (wrong domain /
 * bad length / unknown version).
 * @param {Uint8Array} plaintext
 */
export function decodeCommitterBeacon(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeCommitterBeacon(ptr0, len0);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

/**
 * Strictly decode a committer-delegation envelope (structure only).
 *
 * # Errors
 *
 * Throws with `committerDelegationError` `wrongDomain` / `unknownVersion` /
 * `badCount` / `badLength`.
 * @param {Uint8Array} plaintext
 * @returns {CommitterDelegationEnvelope}
 */
export function decodeCommitterDelegationEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeCommitterDelegationEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return CommitterDelegationEnvelope.__wrap(ret[0]);
}

/**
 * Strictly decode a conversation-handle envelope (the signature is not
 * checked here: `verifyHandleBinding`, then `ConversationHandle.verify`).
 *
 * # Errors
 *
 * Throws `conversationHandle.<code>: ...`.
 * @param {Uint8Array} plaintext
 * @returns {ConversationHandle}
 */
export function decodeConversationHandleEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeConversationHandleEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ConversationHandle.__wrap(ret[0]);
}

/**
 * Decode what `VaultKeys.openHead` returned for a conversation's vault. The
 * `registrarSeed` of the result is secret.
 *
 * # Errors
 *
 * Throws the `RecordError` code of the first thing that is wrong
 * (`unknownVersion`, `truncated`, `trailingBytes`, a bound, ...).
 * @param {Uint8Array} bytes
 * @returns {VaultConversationHead}
 */
export function decodeConversationHead(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeConversationHead(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VaultConversationHead.__wrap(ret[0]);
}

/**
 * Decode one raw 185-byte delegation blob (structure only).
 *
 * # Errors
 *
 * Throws with `committerDelegationError` `delegationMalformed` /
 * `delegationUnknownVersion`.
 * @param {Uint8Array} blob
 * @returns {Delegation}
 */
export function decodeDelegation(blob) {
    const ptr0 = passArray8ToWasm0(blob, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeDelegation(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return Delegation.__wrap(ret[0]);
}

/**
 * Strictly decode a device-card envelope. Fail-closed.
 *
 * # Errors
 *
 * `"<code>: <reason>"`.
 * @param {Uint8Array} plaintext
 * @returns {DeviceCard}
 */
export function decodeDeviceCardEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeDeviceCardEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return DeviceCard.__wrap(ret[0]);
}

/**
 * Decode the head of the `VaultRef.devices()` vault into its re-entry
 * material.
 *
 * # Errors
 *
 * Throws the `RecordError` code of the first thing that is wrong.
 * @param {Uint8Array} bytes
 * @returns {VaultReentryEpoch[]}
 */
export function decodeDevicesHead(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeDevicesHead(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Strictly decode a group-closed envelope (ADR-0073 phase 2). Fail-closed:
 * anything malformed is refused as a whole.
 *
 * Use `dispatchIncomingEnvelope` when the plaintext might be any kind.
 *
 * # Errors
 *
 * Throws an `Error` with a `groupClosedError` code (`"wrongDomain"`,
 * `"truncated"`, `"trailingBytes"`, `"unknownVersion"`,
 * `"badSuccessorLen"`).
 * @param {Uint8Array} plaintext
 * @returns {GroupClosed}
 */
export function decodeGroupClosedEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeGroupClosedEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return GroupClosed.__wrap(ret[0]);
}

/**
 * Strictly decode a group-name envelope (ADR-0051 §1). Fail-closed:
 * anything malformed or out of bounds is refused as a whole.
 *
 * Use `dispatchIncomingEnvelope` when the plaintext might be any kind.
 *
 * # Errors
 *
 * Throws an `Error` with a `groupNameError` code (`"wrongDomain"`,
 * `"truncated"`, `"name.tooLong"`, …).
 * @param {Uint8Array} plaintext
 * @returns {GroupName}
 */
export function decodeGroupNameEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeGroupNameEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return GroupName.__wrap(ret[0]);
}

/**
 * Strictly decode a group-photo envelope.
 *
 * # Errors
 *
 * Throws `groupPhoto.<code>: ...`.
 * @param {Uint8Array} plaintext
 * @returns {GroupPhoto}
 */
export function decodeGroupPhotoEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeGroupPhotoEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return GroupPhoto.__wrap(ret[0]);
}

/**
 * Strictly decode a group allowed-reactions envelope.
 *
 * # Errors
 *
 * Throws `groupReactions.<code>: ...`.
 * @param {Uint8Array} plaintext
 * @returns {GroupReactions}
 */
export function decodeGroupReactionsEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeGroupReactionsEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return GroupReactions.__wrap(ret[0]);
}

/**
 * Decode what `VaultKeys.openChunk` returned into its messages.
 *
 * # Errors
 *
 * Throws the `RecordError` code of the first thing that is wrong.
 * @param {Uint8Array} bytes
 * @returns {VaultMessage[]}
 */
export function decodeHistoryChunk(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeHistoryChunk(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Decode canonical `payload_bytes` (produced by
 * `encodeHistoryPlaceholder` on any platform) into an
 * [`OpenedHistoryPlaceholder`].
 * @param {Uint8Array} bytes
 * @returns {OpenedHistoryPlaceholder}
 */
export function decodeHistoryPlaceholder(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeHistoryPlaceholder(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return OpenedHistoryPlaceholder.__wrap(ret[0]);
}

/**
 * Strictly decode an introduction offer, returning the record wire. Accept
 * one only from the other participant of a 1:1 conversation.
 *
 * # Errors
 *
 * Throws with `introductionError` (`wrongDomain`, `unknownVersion`, a
 * record error).
 * @param {Uint8Array} plaintext
 * @returns {Uint8Array}
 */
export function decodeIntroductionOffer(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeIntroductionOffer(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Strictly decode an introductions bundle.
 *
 * # Errors
 *
 * Throws with `introductionError` (`wrongDomain`, `badRecordCount`,
 * `recordLength`, a record error).
 * @param {Uint8Array} plaintext
 * @returns {IntroductionsBundle}
 */
export function decodeIntroductionsBundle(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeIntroductionsBundle(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return IntroductionsBundle.__wrap(ret[0]);
}

/**
 * Strictly decode a key-rotation envelope. Shape only: this proves nothing
 * about who made it. Use `verifyKeyRotationEnvelope` before keeping one.
 *
 * # Errors
 *
 * Throws the envelope's code (`wrongDomain`, `truncated`, `unknownVersion`,
 * `badLength`).
 * @param {Uint8Array} plaintext
 * @returns {KeyRotation}
 */
export function decodeKeyRotationEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeKeyRotationEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return KeyRotation.__wrap(ret[0]);
}

/**
 * Strictly decode a knock-seed envelope.
 *
 * # Errors
 *
 * Throws `knockSeed.<code>: ...`.
 * @param {Uint8Array} plaintext
 * @returns {KnockSeedEnvelope}
 */
export function decodeKnockSeedEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeKnockSeedEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return KnockSeedEnvelope.__wrap(ret[0]);
}

/**
 * Strictly decode a claim envelope. Fail-closed.
 *
 * # Errors
 *
 * `"<code>: <reason>"` with the frozen reason code.
 * @param {Uint8Array} plaintext
 * @returns {LinkClaim}
 */
export function decodeLinkClaimEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeLinkClaimEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return LinkClaim.__wrap(ret[0]);
}

/**
 * Strictly decode a note. Fail-closed.
 *
 * # Errors
 *
 * `"<code>: <reason>"` with the frozen reason code.
 * @param {Uint8Array} plaintext
 * @returns {LinkFounded}
 */
export function decodeLinkFoundedEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeLinkFoundedEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return LinkFounded.__wrap(ret[0]);
}

/**
 * Strictly decode a media-state envelope, returning the
 * camera flag (camera-off-cleanup). Fail-closed: rejects a
 * plaintext that does not carry the media-state domain
 * separator or whose payload is malformed. Use
 * `dispatchIncomingEnvelope` instead when the plaintext might
 * be any relay-payload kind.
 *
 * # Errors
 *
 * `JsError` when `plaintext` is not a valid media-state
 * envelope (wrong domain / bad length / unknown version /
 * out-of-range flag byte).
 * @param {Uint8Array} plaintext
 * @returns {boolean}
 */
export function decodeMediaState(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeMediaState(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] !== 0;
}

/**
 * Confirming side: parse the wire bytes, verify the embedded login
 * handle matches `expected_login_handle` (the handle of the local
 * account's own username), return a structured [`DecodedPairingQr`]
 * handle.
 *
 * # Errors
 *
 * `JsError` on:
 *   - Wrong-length `expected_login_handle` (≠ 32).
 *   - Malformed wire bytes (proto-decode failure, wrong
 *     embedded field lengths).
 *   - A handle mismatch — the payload was made for another
 *     account's name.
 *   - Any payload version other than 2 (a v1 payload is refused).
 * @param {Uint8Array} bytes
 * @param {Uint8Array} expected_login_handle
 * @returns {DecodedPairingQr}
 */
export function decodePairingQr(bytes, expected_login_handle) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(expected_login_handle, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.decodePairingQr(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return DecodedPairingQr.__wrap(ret[0]);
}

/**
 * Strictly decode a shared-pin envelope (ADR-0052 decision 3). Fail-closed:
 * anything malformed is refused as a whole.
 *
 * Use `dispatchIncomingEnvelope` when the plaintext might be any kind.
 *
 * # Errors
 *
 * Throws an `Error` with a `pinError` code (`"wrongDomain"`, `"badLength"`,
 * `"unknownVersion"`, `"unknownAction"`).
 * @param {Uint8Array} plaintext
 * @returns {PinOp}
 */
export function decodePinEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodePinEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return PinOp.__wrap(ret[0]);
}

/**
 * Strictly decode a profile envelope (ADR-0048). Fail-closed: anything
 * malformed or out of bounds is refused as a whole.
 *
 * Use `dispatchIncomingEnvelope` when the plaintext might be any kind.
 *
 * # Errors
 *
 * Throws an `Error` with a `profileError` code (`"wrongDomain"`,
 * `"truncated"`, `"displayName.tooLong"`, …).
 * @param {Uint8Array} plaintext
 * @returns {Profile}
 */
export function decodeProfileEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeProfileEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return Profile.__wrap(ret[0]);
}

/**
 * Recognise a profile request: resolves for a well-formed one, throws for
 * a foreign domain or a malformed payload.
 * @param {Uint8Array} plaintext
 */
export function decodeProfileRequest(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeProfileRequest(ptr0, len0);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

/**
 * Strictly decode a push-routing-secret distribution envelope
 * (ADR-0042 v2, per ADR-0044), returning
 * `{ mintEpoch, secret }`. Fail-closed: rejects a plaintext that
 * does not carry the push-routing-secret domain separator or
 * whose payload is malformed — including a retired v1 envelope,
 * which carries no `mintEpoch` and so cannot be placed in the
 * total order. Use `dispatchIncomingEnvelope` instead when the
 * plaintext might be any relay-payload / in-group envelope kind.
 *
 * # Errors
 *
 * `JsError` when `plaintext` is not a valid push-routing-secret
 * envelope (wrong domain / bad length / unknown version — a v1
 * envelope lands here).
 * @param {Uint8Array} plaintext
 * @returns {PushRoutingSecret}
 */
export function decodePushRoutingSecret(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodePushRoutingSecret(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return PushRoutingSecret.__wrap(ret[0]);
}

/**
 * Decode exactly `reachGrantLen()` bytes, fail-closed.
 *
 * # Errors
 *
 * Throws with `reachError` `badLength`, `unknownClass` or `badBinding`. The
 * binding's signature is not checked (`mailboxPkVerified` does).
 * @param {Uint8Array} bytes
 * @returns {ReachGrant}
 */
export function decodeReachGrant(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeReachGrant(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ReachGrant.__wrap(ret[0]);
}

/**
 * Strictly decode a reach-state envelope, ordered by `reachStateIsNewer`.
 *
 * # Errors
 *
 * Throws with `visibilityError`.
 * @param {Uint8Array} plaintext
 * @returns {ReachState}
 */
export function decodeReachStateEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeReachStateEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ReachState.__wrap(ret[0]);
}

/**
 * Strictly decode a registrar-seed envelope (the seed stays sealed).
 *
 * # Errors
 *
 * When `plaintext` is not a well-formed envelope.
 * @param {Uint8Array} plaintext
 * @returns {RegistrarSeedEnvelope}
 */
export function decodeRegistrarSeedEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeRegistrarSeedEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return RegistrarSeedEnvelope.__wrap(ret[0]);
}

/**
 * Strictly decode a shared-channels envelope. Fail-closed.
 *
 * # Errors
 *
 * Throws an `Error` with a `sharedChannelsError` code.
 * @param {Uint8Array} plaintext
 * @returns {SharedChannels}
 */
export function decodeSharedChannelsEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeSharedChannelsEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SharedChannels.__wrap(ret[0]);
}

/**
 * Decode the head of the `VaultRef.index()` vault into its entries.
 *
 * # Errors
 *
 * Throws the `RecordError` code of the first thing that is wrong.
 * @param {Uint8Array} bytes
 * @returns {VaultIndexEntry[]}
 */
export function decodeVaultIndex(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeVaultIndex(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Strictly decode a setting envelope. Accept one only on the owner's own
 * DeviceGroup, and keep it only when `visibilityIsNewer`.
 *
 * # Errors
 *
 * Throws with `visibilityError`.
 * @param {Uint8Array} plaintext
 * @returns {VisibilitySetting}
 */
export function decodeVisibilityEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decodeVisibilityEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VisibilitySetting.__wrap(ret[0]);
}

/**
 * Decrypt `ciphertext` under `key` + `nonce`. Mirror of the Rust
 * crate's `decrypt_attachment`.
 * @param {Uint8Array} ciphertext
 * @param {Uint8Array} key
 * @param {Uint8Array} nonce
 * @returns {Uint8Array}
 */
export function decryptAttachment(ciphertext, key, nonce) {
    const ptr0 = passArray8ToWasm0(ciphertext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(nonce, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.decryptAttachment(ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Default ring-buffer size for [`CallQualitySampler`].
 * @returns {number}
 */
export function defaultQualitySampleWindow() {
    const ret = wasm.defaultQualitySampleWindow();
    return ret >>> 0;
}

/**
 * The quick reaction until a person picks another.
 * @returns {string}
 */
export function defaultQuickReaction() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.defaultQuickReaction();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The default for a new account: Some people with contacts of contacts and
 * all groups and channels on; `updatedAtMs` 0.
 * @returns {VisibilitySetting}
 */
export function defaultVisibilitySetting() {
    const ret = wasm.defaultVisibilitySetting();
    return VisibilitySetting.__wrap(ret);
}

/**
 * The request header a reach-key delegation travels in.
 * @returns {string}
 */
export function delegationHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.delegationHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The `x-lakko-ds-delegation` header value of a raw delegation blob.
 *
 * # Errors
 *
 * As `decodeDelegation`.
 * @param {Uint8Array} blob
 * @returns {string}
 */
export function delegationHeaderValue(blob) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passArray8ToWasm0(blob, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.delegationHeaderValue(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * Derive the stable opaque `push_gid` from a group's 32-byte
 * `push_routing_secret` (ADR-0042). Returns the 32-byte routing
 * id the client subscribes / notifies under (and reverse-indexes
 * to `(account, group_id)`), instead of the leaky DS journal
 * key.
 *
 * See [`lakko_contacts::push_routing::derive_push_gid`] for the
 * derivation (keyed-BLAKE3, domain
 * `lakko/push/group-routing-id/v1`) and the invariant-#5
 * rationale.
 *
 * # Errors
 *
 * `JsError` when `secret` is not exactly 32 bytes.
 * @param {Uint8Array} secret
 * @returns {Uint8Array}
 */
export function derivePushGid(secret) {
    const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.derivePushGid(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The 32-byte public half of the group's wake signing key.
 *
 * # Errors
 *
 * `keyLength`.
 * @param {Uint8Array} secret
 * @returns {Uint8Array}
 */
export function derivePushWakePublicKey(secret) {
    const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.derivePushWakePublicKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The 32-byte unlock key of the group. Secret: exposed for the golden tests
 * only.
 *
 * # Errors
 *
 * `keyLength`.
 * @param {Uint8Array} secret
 * @returns {Uint8Array}
 */
export function derivePushWakeUnlockKey(secret) {
    const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.derivePushWakeUnlockKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * `reachSeed` wrapped as a grant, ready to hand out.
 *
 * # Errors
 *
 * Exactly `reachSeed`'s.
 * @param {Uint8Array} account_seed
 * @param {ReachClass} _class
 * @param {Uint8Array} scope_id
 * @param {number} version
 * @returns {ReachGrant}
 */
export function deriveReachGrant(account_seed, _class, scope_id, version) {
    const ptr0 = passArray8ToWasm0(account_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(scope_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.deriveReachGrant(ptr0, len0, _class, ptr1, len1, version);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ReachGrant.__wrap(ret[0]);
}

/**
 * Derive a token for `(presenceSeed, memberPseudonym, epoch)`.
 * Mirror of [`lakko_presence::derive_token`].
 * @param {Uint8Array} presence_seed
 * @param {Uint8Array} member_pseudonym
 * @param {bigint} epoch
 * @returns {PresenceToken}
 */
export function deriveToken(presence_seed, member_pseudonym, epoch) {
    const ptr0 = passArray8ToWasm0(presence_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(member_pseudonym, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.deriveToken(ptr0, len0, ptr1, len1, epoch);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return PresenceToken.__wrap(ret[0]);
}

/**
 * Longest device-card envelope.
 * @returns {number}
 */
export function deviceCardEnvelopeMaxLen() {
    const ret = wasm.deviceCardEnvelopeMaxLen();
    return ret >>> 0;
}

/**
 * Shortest device-card envelope.
 * @returns {number}
 */
export function deviceCardEnvelopeMinLen() {
    const ret = wasm.deviceCardEnvelopeMinLen();
    return ret >>> 0;
}

/**
 * `true` when a card stamped `incoming` must replace the one stored for the
 * same leaf key. Strictly greater wins.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function deviceCardIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.deviceCardIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * The deterministic DeviceGroup `group_id` of the account whose identity is
 * `accountIdentity` (ADR-0078 section 6):
 * `BLAKE3-derive-key("lakko/device-group/id/v2", account_seed)`. Pass the
 * account identity the conversation leaves are bound to (the oldest own
 * predecessor after a rotation, else the current one). Not computable from
 * the account id.
 * @param {Identity} account_identity
 * @returns {Uint8Array}
 */
export function deviceGroupIdFor(account_identity) {
    _assertClass(account_identity, Identity);
    const ret = wasm.deviceGroupIdFor(account_identity.__wbg_ptr);
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * ADR-0081 amendment — would `proof` cover replacing the leaf
 * `removedSignaturePubkey` by `keyPackage` in the DeviceGroup `groupId`, in a
 * commit leaving `epoch` (the sibling's current epoch)? The admitting
 * sibling's check BEFORE it builds the replacement commit.
 * `identity` supplies the crypto provider. Malformed input answers `false`.
 * @param {Identity} identity
 * @param {Uint8Array} proof
 * @param {Uint8Array} group_id
 * @param {bigint} epoch
 * @param {Uint8Array} removed_signature_pubkey
 * @param {Uint8Array} key_package
 * @returns {boolean}
 */
export function deviceGroupSelfRemovalCovers(identity, proof, group_id, epoch, removed_signature_pubkey, key_package) {
    _assertClass(identity, Identity);
    const ptr0 = passArray8ToWasm0(proof, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(removed_signature_pubkey, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.deviceGroupSelfRemovalCovers(identity.__wbg_ptr, ptr0, len0, ptr1, len1, epoch, ptr2, len2, ptr3, len3);
    return ret !== 0;
}

/**
 * The wire length of a DeviceGroup self-removal proof (137).
 * @returns {number}
 */
export function deviceGroupSelfRemovalProofLen() {
    const ret = wasm.deviceGroupSelfRemovalProofLen();
    return ret >>> 0;
}

/**
 * Longest envelope a DeviceGroup `serviceEnvelope` payload carries
 * (ADR-0060 section 7).
 * @returns {number}
 */
export function deviceGroupServiceEnvelopeMaxLen() {
    const ret = wasm.deviceGroupServiceEnvelopeMaxLen();
    return ret >>> 0;
}

/**
 * Render a code as three pairs, `"47 19 03"`. Throws `invalid-code: ...`.
 * @param {string} code
 * @returns {string}
 */
export function deviceLinkFormatCode(code) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passStringToWasm0(code, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.deviceLinkFormatCode(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * Normalise a typed code to its six digits (`"47 19 03"` -> `"471903"`).
 * Throws `invalid-code: ...`.
 * @param {string} input
 * @returns {string}
 */
export function deviceLinkNormalizeCode(input) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passStringToWasm0(input, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.deviceLinkNormalizeCode(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * The lookup key of an OPRF output (ADR-0060 section 5), for a caller that
 * kept an output from an earlier round.
 *
 * # Errors
 *
 * Throws when `output` is not 64 bytes.
 * @param {Uint8Array} output
 * @returns {Uint8Array}
 */
export function discoveryLookupKey(output) {
    const ptr0 = passArray8ToWasm0(output, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.discoveryLookupKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Single-call dispatcher for opaque relay-payload plaintexts
 * (Phase 3c3-incoming-dispatcher). Runs the classification
 * helpers in dispatch order
 * (welcome → KP-forward → media-state → intro-message
 * fallthrough) and returns the typed variant.
 *
 * See [`lakko_contacts::incoming_envelope`] for the full
 * dispatch contract.
 *
 * # Errors
 *
 * `JsError` when a domain separator matched but the payload
 * is malformed (below the Welcome / KP-forward minimum floor,
 * or an invalid media-state payload).
 * @param {Uint8Array} plaintext
 * @returns {IncomingEnvelope}
 */
export function dispatchIncomingEnvelope(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.dispatchIncomingEnvelope(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return IncomingEnvelope.__wrap(ret[0]);
}

/**
 * The registrar seed the root appoints for a member account key.
 *
 * # Errors
 *
 * When an input is not 32 bytes.
 * @param {Uint8Array} root_seed
 * @param {Uint8Array} member_account_key
 * @returns {Uint8Array}
 */
export function dsJournalRegistrarSeed(root_seed, member_account_key) {
    const ptr0 = passArray8ToWasm0(root_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(member_account_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.dsJournalRegistrarSeed(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * The journal's root seed from the owner's ACCOUNT identity seed (32 bytes,
 * `Identity.ed25519Seed` of the account) and the 32-byte journal id.
 *
 * # Errors
 *
 * When an input is not 32 bytes.
 * @param {Uint8Array} account_identity_seed
 * @param {Uint8Array} journal_id
 * @returns {Uint8Array}
 */
export function dsJournalRootSeed(account_identity_seed, journal_id) {
    const ptr0 = passArray8ToWasm0(account_identity_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.dsJournalRootSeed(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * The journal's root signer for the owner's ACCOUNT identity.
 *
 * # Errors
 *
 * When `journal_id` is not 32 bytes.
 * @param {Identity} account_identity
 * @param {Uint8Array} journal_id
 * @returns {DsStatementSigner}
 */
export function dsJournalRootSigner(account_identity, journal_id) {
    _assertClass(account_identity, Identity);
    const ptr0 = passArray8ToWasm0(journal_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.dsJournalRootSigner(account_identity.__wbg_ptr, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return DsStatementSigner.__wrap(ret[0]);
}

/**
 * The epoch a mailbox proof is signed at (a mailbox has no MLS epoch). A
 * `bigint`.
 * @returns {bigint}
 */
export function dsMailboxEpoch() {
    const ret = wasm.dsMailboxEpoch();
    return BigInt.asUintN(64, ret);
}

/**
 * The signer for the own mailbox of the account whose identity is
 * `accountIdentity`: its **mailbox key** (ADR-0078, derived from the account
 * seed; never the identity key) at `dsMailboxEpoch()`.
 * @param {Identity} account_identity
 * @returns {DsProofSigner}
 */
export function dsMailboxSigner(account_identity) {
    _assertClass(account_identity, Identity);
    const ret = wasm.dsMailboxSigner(account_identity.__wbg_ptr);
    return DsProofSigner.__wrap(ret);
}

/**
 * The request header a proof travels in.
 * @returns {string}
 */
export function dsProofHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.dsProofHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The epoch label a reach-key proof carries (`2n ** 64n - 1n`): never a
 * group epoch, never `dsMailboxEpoch()`. A `bigint`.
 * @returns {bigint}
 */
export function dsReachEpoch() {
    const ret = wasm.dsReachEpoch();
    return BigInt.asUintN(64, ret);
}

/**
 * The signer of a root or registrar seed.
 *
 * # Errors
 *
 * When `seed` is not 32 bytes.
 * @param {Uint8Array} seed
 * @returns {DsStatementSigner}
 */
export function dsStatementSigner(seed) {
    const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.dsStatementSigner(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return DsStatementSigner.__wrap(ret[0]);
}

/**
 * Deterministically elect the committer-of-record for a multi-joiner link call
 * (ADR-0032): the owner if present, otherwise the present member with the
 * lowest MLS leaf index. Returns the elected leaf index, or `undefined` when no
 * member is present.
 *
 * Candidates are passed as three parallel arrays (leaf index, owner flag,
 * presence flag) — the wasm-friendly shape for a small struct array. A length
 * mismatch yields `undefined` (defensive). This is the one canonical
 * implementation every platform shares, so failover + reclaim cannot split-brain.
 * @param {Uint32Array} leaf_indices
 * @param {Uint8Array} is_owner
 * @param {Uint8Array} is_present
 * @returns {number | undefined}
 */
export function electCommitter(leaf_indices, is_owner, is_present) {
    const ptr0 = passArray32ToWasm0(leaf_indices, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(is_owner, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(is_present, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.electCommitter(ptr0, len0, ptr1, len1, ptr2, len2);
    return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
}

/**
 * Open an email-vault blob and rebuild the recovery phrase.
 *
 * # Errors
 *
 * Throws `"<code>: <reason>"`: `not-opened` when the blob does not open
 * under this handle, address and salt, whatever the reason; an address
 * code; `bad-handle-length`, `bad-salt-length` or `bad-sealed-length`.
 * @param {Uint8Array} login_handle
 * @param {string} address
 * @param {Uint8Array} vault_salt
 * @param {Uint8Array} sealed
 * @returns {RecoveryMnemonic}
 */
export function emailVaultOpen(login_handle, address, vault_salt, sealed) {
    const ptr0 = passArray8ToWasm0(login_handle, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(address, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(vault_salt, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.emailVaultOpen(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return RecoveryMnemonic.__wrap(ret[0]);
}

/**
 * Seal the mnemonic's entropy under the login handle (32 bytes), the
 * recovery email address and the server-issued vault salt (16 bytes). The
 * address is normalised here.
 *
 * # Errors
 *
 * Throws `"<code>: <reason>"`: an address code, `bad-handle-length`,
 * `bad-salt-length`, `kdf` or `seal`.
 * @param {RecoveryMnemonic} mnemonic
 * @param {Uint8Array} login_handle
 * @param {string} address
 * @param {Uint8Array} vault_salt
 * @returns {Uint8Array}
 */
export function emailVaultSeal(mnemonic, login_handle, address, vault_salt) {
    _assertClass(mnemonic, RecoveryMnemonic);
    const ptr0 = passArray8ToWasm0(login_handle, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(address, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(vault_salt, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.emailVaultSeal(mnemonic.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * 1 to 3 when `text` is that many emoji and white space only, else 0.
 * @param {string} text
 * @returns {number}
 */
export function emojiOnlyCount(text) {
    const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.emojiOnlyCount(ptr0, len0);
    return ret;
}

/**
 * The most emoji a message drawn large holds (3).
 * @returns {number}
 */
export function emojiOnlyMax() {
    const ret = wasm.emojiOnlyMax();
    return ret;
}

/**
 * Encode a conversation's head: the plaintext `VaultKeys.sealHead` takes.
 * The head is read, not consumed.
 *
 * # Errors
 *
 * Throws a `RecordError` bound (`title`, `people`, `pins`, `chunks`,
 * `messages`, `text`, `attachments`, `attachment`, `reply`, `forward`,
 * `reactions`, `reentry`), or `tooLarge` when the encoding does not fit one
 * object - move messages from the tail into chunks and encode again.
 * @param {VaultConversationHead} head
 * @returns {Uint8Array}
 */
export function encodeConversationHead(head) {
    _assertClass(head, VaultConversationHead);
    const ret = wasm.encodeConversationHead(head.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Encode the head of the account's own device group (ADR-0064 section 5):
 * re-entry material for at most `vaultMaxReentryEpochs()` epochs (an
 * `Array<VaultReentryEpoch>`, consumed), the plaintext of the head of the
 * `VaultRef.devices()` vault.
 *
 * # Errors
 *
 * Throws `reentry: ...` or `tooLarge: ...`.
 * @param {VaultReentryEpoch[]} reentry
 * @returns {Uint8Array}
 */
export function encodeDevicesHead(reentry) {
    const ptr0 = passArrayJsValueToWasm0(reentry, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.encodeDevicesHead(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Encode one chunk of earlier messages (an `Array<VaultMessage>` of at most
 * `vaultMaxChunkMessages()`, consumed): the plaintext `VaultKeys.sealChunk`
 * takes. The codec asks for no order; `mergeVaultMessages` is what yields
 * rows sorted by id, each id once.
 *
 * # Errors
 *
 * Throws a message bound, or `tooLarge` when the encoding does not fit one
 * object - split the chunk.
 * @param {VaultMessage[]} messages
 * @returns {Uint8Array}
 */
export function encodeHistoryChunk(messages) {
    const ptr0 = passArrayJsValueToWasm0(messages, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.encodeHistoryChunk(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Encode a placeholder to the canonical `payload_bytes` wire
 * bytes. `direction` is 0 = outgoing, 1 = incoming, 2 = system.
 * `attachments` is an `Array<HistoryAttachmentInput>`.
 * @param {bigint} message_id
 * @param {number} direction
 * @param {string} created_at
 * @param {string} text
 * @param {HistoryAttachmentInput[]} attachments
 * @returns {Uint8Array}
 */
export function encodeHistoryPlaceholder(message_id, direction, created_at, text, attachments) {
    const ptr0 = passStringToWasm0(created_at, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArrayJsValueToWasm0(attachments, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.encodeHistoryPlaceholder(message_id, direction, ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Build the wire-format pairing payload.
 *
 * `login_handle` is the 32-byte handle of the username the person
 * typed (`loginHandle(username)`). `key_package` is the TLS-encoded
 * `KeyPackage` from the joining device's DeviceIdentity.
 * `rendezvous_pseudonym` is a fresh 32-byte random slot the joining
 * device chose for this pairing session — the DS mailbox at that
 * pseudonym is the one-shot Welcome delivery channel.
 * `device_card_envelope` is this device's own ADR-0080 device-card
 * envelope (`composeDeviceCardEnvelope`), or `undefined` / `null` for none;
 * it is carried as given for the confirming device to show.
 *
 * # Errors
 *
 * `JsError` on wrong-length `login_handle` (≠ 32) /
 * `rendezvous_pseudonym` (≠ 32), a `device_card_envelope` longer than the
 * device-card envelope bound (270 bytes), or any of the inner
 * encode failure modes (all unreachable in practice given
 * valid inputs).
 * @param {Uint8Array} login_handle
 * @param {Uint8Array} key_package
 * @param {Uint8Array} rendezvous_pseudonym
 * @param {Uint8Array | null} [device_card_envelope]
 * @returns {Uint8Array}
 */
export function encodePairingQr(login_handle, key_package, rendezvous_pseudonym, device_card_envelope) {
    const ptr0 = passArray8ToWasm0(login_handle, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(rendezvous_pseudonym, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    var ptr3 = isLikeNone(device_card_envelope) ? 0 : passArray8ToWasm0(device_card_envelope, wasm.__wbindgen_malloc);
    var len3 = WASM_VECTOR_LEN;
    const ret = wasm.encodePairingQr(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * Encode a grant as its 134-byte wire (`class || version || seed ||
 * binding`). Holds the seed: seal it before it leaves the device.
 * @param {ReachGrant} grant
 * @returns {Uint8Array}
 */
export function encodeReachGrant(grant) {
    _assertClass(grant, ReachGrant);
    const ret = wasm.encodeReachGrant(grant.__wbg_ptr);
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * ADR-0081 amendment — a DeviceGroup rejoin knock: the pairing payload plus
 * the device's self-removal proof (`signDeviceGroupSelfRemoval`, exactly 137
 * bytes) for `keyPackage`.
 *
 * # Errors
 *
 * `JsError` on a wrong-length handle, rendezvous pseudonym or proof.
 * @param {Uint8Array} login_handle
 * @param {Uint8Array} key_package
 * @param {Uint8Array} rendezvous_pseudonym
 * @param {Uint8Array} self_removal_proof
 * @returns {Uint8Array}
 */
export function encodeRejoinKnock(login_handle, key_package, rendezvous_pseudonym, self_removal_proof) {
    const ptr0 = passArray8ToWasm0(login_handle, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(rendezvous_pseudonym, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(self_removal_proof, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.encodeRejoinKnock(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * Encode the account's index (an `Array<VaultIndexEntry>` of at most
 * 65 536, no conversation twice, consumed): the plaintext of the head of the
 * `VaultRef.index()` vault.
 *
 * # Errors
 *
 * Throws `entries: ...`, `repeatedRef: ...` or `tooLarge: ...`.
 * @param {VaultIndexEntry[]} entries
 * @returns {Uint8Array}
 */
export function encodeVaultIndex(entries) {
    const ptr0 = passArrayJsValueToWasm0(entries, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.encodeVaultIndex(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Encrypt `plaintext` under a fresh random CEK + nonce. Mirror of
 * the Rust crate's `encrypt_attachment`.
 * @param {Uint8Array} plaintext
 * @returns {EncryptedAttachment}
 */
export function encryptAttachment(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.encryptAttachment(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return EncryptedAttachment.__wrap(ret[0]);
}

/**
 * The epoch the group is in AFTER a journal row (ADR-0028 §3): the framed
 * epoch, plus one when the row is a Commit. Over the journal's last MLS row
 * this is the number a joiner puts on its external-join proposal; the framed
 * epoch is stale whenever the head is a Commit and the proposal is then
 * silently refused. Returns `undefined` exactly where `epochOfMessage` does.
 * @param {Uint8Array} bytes
 * @returns {bigint | undefined}
 */
export function epochAfterMessage(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.epochAfterMessage(ptr0, len0);
    return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
}

/**
 * Read a group's current MLS epoch from a wire-form journal message's public
 * framing, without any group secret (ADR-0028 §3 — the join-ticket joiner
 * side). Returns `undefined` for non-MLS bytes or an epoch-less variant
 * (Welcome / KeyPackage) — the caller treats that as epoch `0`.
 * @param {Uint8Array} bytes
 * @returns {bigint | undefined}
 */
export function epochOfMessage(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.epochOfMessage(ptr0, len0);
    return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
}

/**
 * Estimate call quality from a single network sample
 * (Phase 3a11). `codec` accepts `"opus"` (default for
 * Lakko voice / video) or any other value (mapped to the
 * no-FEC baseline).
 * @param {number} rtt_ms
 * @param {number} jitter_ms
 * @param {number} loss_pct
 * @param {string} codec
 * @returns {CallQualityScore}
 */
export function estimateCallQuality(rtt_ms, jitter_ms, loss_pct, codec) {
    const ptr0 = passStringToWasm0(codec, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.estimateCallQuality(rtt_ms, jitter_ms, loss_pct, ptr0, len0);
    return CallQualityScore.__wrap(ret);
}

/**
 * Exclude one contact while "all" is on: the form becomes "only the ones I
 * choose" with every other contact in `allContactIds` (concatenated 16-byte
 * account ids) chosen.
 *
 * # Errors
 *
 * Throws an `Error` with `visibilityError` `contactIdLength` /
 * `tooManyChosenContacts`.
 * @param {VisibilitySetting} setting
 * @param {Uint8Array} all_contact_ids
 * @param {Uint8Array} excluded
 * @returns {VisibilitySetting}
 */
export function excludeContactUnderAll(setting, all_contact_ids, excluded) {
    _assertClass(setting, VisibilitySetting);
    const ptr0 = passArray8ToWasm0(all_contact_ids, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(excluded, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.excludeContactUnderAll(setting.__wbg_ptr, ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VisibilitySetting.__wrap(ret[0]);
}

/**
 * Exclude one group or channel while "all" is on: the form becomes "only
 * the ones I choose" with every other scope in `allScopes` chosen.
 *
 * # Errors
 *
 * Throws an `Error` with `visibilityError` `scopeIdLength` /
 * `tooManyChosen`.
 * @param {VisibilitySetting} setting
 * @param {ScopeId[]} all_scopes
 * @param {ScopeId} excluded
 * @returns {VisibilitySetting}
 */
export function excludeScopeUnderAll(setting, all_scopes, excluded) {
    _assertClass(setting, VisibilitySetting);
    const ptr0 = passArrayJsValueToWasm0(all_scopes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    _assertClass(excluded, ScopeId);
    const ret = wasm.excludeScopeUnderAll(setting.__wbg_ptr, ptr0, len0, excluded.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VisibilitySetting.__wrap(ret[0]);
}

/**
 * Highest weight.
 * @returns {number}
 */
export function feedMaxWeight() {
    const ret = wasm.feedMaxWeight();
    return ret;
}

/**
 * Longest window, in days.
 * @returns {number}
 */
export function feedMaxWindowDays() {
    const ret = wasm.feedMaxWindowDays();
    return ret;
}

/**
 * Longest word, in UTF-8 bytes.
 * @returns {number}
 */
export function feedMaxWordBytes() {
    const ret = wasm.feedMaxWordBytes();
    return ret >>> 0;
}

/**
 * Most words in a word list.
 * @returns {number}
 */
export function feedMaxWords() {
    const ret = wasm.feedMaxWords();
    return ret >>> 0;
}

/**
 * Longest encoded rule, in bytes.
 * @returns {number}
 */
export function feedRuleMaxLen() {
    const ret = wasm.feedRuleMaxLen();
    return ret >>> 0;
}

/**
 * The tag a record entry for one 64-byte OPRF output carries.
 *
 * # Errors
 *
 * Throws with `introductionError` `oprfLength`.
 * @param {Uint8Array} oprf_output
 * @returns {Uint8Array}
 */
export function fofTag(oprf_output) {
    const ptr0 = passArray8ToWasm0(oprf_output, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.fofTag(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Length of a record tag: 32.
 * @returns {number}
 */
export function fofTagLen() {
    const ret = wasm.fofTagLen();
    return ret >>> 0;
}

/**
 * Fold one message's reaction ops (UX sections 10 and 17).
 * @param {ReactionOp[]} ops
 * @returns {HeldReaction[]}
 */
export function foldReactions(ops) {
    const ptr0 = passArrayJsValueToWasm0(ops, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.foldReactions(ptr0, len0);
    var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Whether `row` is in the folder: excluded by hand beats everything; then
 * included by kind toggle or by hand; then not muted / read / archived
 * while the matching exclude toggle is on (`docs/ux/chat-folders.md` 3.2).
 * Borrows both arguments; neither is consumed.
 * @param {FolderRule} folder
 * @param {ConversationRow} row
 * @returns {boolean}
 */
export function folderMatches(folder, row) {
    _assertClass(folder, FolderRule);
    _assertClass(row, ConversationRow);
    const ret = wasm.folderMatches(folder.__wbg_ptr, row.__wbg_ptr);
    return ret !== 0;
}

/**
 * Frame an auth-signed backup head and its envelope into the single storage
 * blob `CONTAINER_TAG ‖ head ‖ envelope` to PUT to `services/storage` on the
 * mint / upload path (ADR-0037 §4). `head` must be the raw
 * `lakko_attest::BACKUP_HEAD_LEN`-byte head bytes.
 *
 * # Errors
 *
 * Throws a coded `invalid: …` `JsError` if `head` is the wrong length (hard
 * fail-closed).
 * @param {Uint8Array} head
 * @param {Uint8Array} envelope
 * @returns {Uint8Array}
 */
export function frameBackupBlob(head, envelope) {
    const ptr0 = passArray8ToWasm0(head, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(envelope, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.frameBackupBlob(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * A fresh random 32-byte admission seed (an X25519 secret).
 * @returns {Uint8Array}
 */
export function generateChannelAdmissionSeed() {
    const ret = wasm.generateChannelAdmissionSeed();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * A fresh random 32-byte invite seed for one subscriber link (SECRET).
 * @returns {Uint8Array}
 */
export function generateChannelInviteSeed() {
    const ret = wasm.generateChannelInviteSeed();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * A fresh random 16-byte link id (ADR-0060 §6): the scope of an `/i/` or
 * `/c/` link's `link` reach grant. Mint one per link.
 * @returns {Uint8Array}
 */
export function generateLinkId() {
    const ret = wasm.generateLinkId();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Longest well-formed group-closed envelope in bytes (closed with a
 * successor). There is no length in between.
 * @returns {number}
 */
export function groupClosedEnvelopeMaxLen() {
    const ret = wasm.groupClosedEnvelopeMaxLen();
    return ret >>> 0;
}

/**
 * Shortest well-formed group-closed envelope in bytes (closed without a
 * successor).
 * @returns {number}
 */
export function groupClosedEnvelopeMinLen() {
    const ret = wasm.groupClosedEnvelopeMinLen();
    return ret >>> 0;
}

/**
 * `true` when a closed notice stamped `incomingClosedAtMs` must replace the
 * stored one **for the same group**. Strictly greater wins; equal is not
 * newer, so a growth re-send of the stored notice is not an update.
 * @param {bigint} incoming_closed_at_ms
 * @param {bigint} stored_closed_at_ms
 * @returns {boolean}
 */
export function groupClosedIsNewer(incoming_closed_at_ms, stored_closed_at_ms) {
    const ret = wasm.groupClosedIsNewer(incoming_closed_at_ms, stored_closed_at_ms);
    return ret !== 0;
}

/**
 * What a group's info holds — section 2.2.
 * @param {GroupInfoCapabilities} caps
 * @returns {string[]}
 */
export function groupInfoSections(caps) {
    _assertClass(caps, GroupInfoCapabilities);
    var ptr0 = caps.__destroy_into_raw();
    const ret = wasm.groupInfoSections(ptr0);
    var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Width in bytes of a `/g/` link secret (the URL fragment): 12.
 * @returns {number}
 */
export function groupInviteLinkSecretLen() {
    const ret = wasm.groupInviteLinkSecretLen();
    return ret >>> 0;
}

/**
 * Length of a `/g/` link secret in the URL fragment: base64url without
 * padding, 16 characters. Any other fragment length is refused.
 * @returns {number}
 */
export function groupInviteLinkSecretUrlLen() {
    const ret = wasm.groupInviteLinkSecretUrlLen();
    return ret >>> 0;
}

/**
 * The 32-byte X25519 knock public key of a knock seed.
 *
 * # Errors
 *
 * Throws `knock.badLength: ...` for a seed that is not 32 bytes.
 * @param {Uint8Array} knock_seed
 * @returns {Uint8Array}
 */
export function groupKnockPublicKey(knock_seed) {
    const ptr0 = passArray8ToWasm0(knock_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.groupKnockPublicKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The 32-byte knock seed of `version` from the group's journal root seed.
 *
 * # Errors
 *
 * Throws `knock.badLength: ...` for a root seed that is not 32 bytes.
 * @param {Uint8Array} journal_root_seed
 * @param {number} version
 * @returns {Uint8Array}
 */
export function groupKnockSeed(journal_root_seed, version) {
    const ptr0 = passArray8ToWasm0(journal_root_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.groupKnockSeed(ptr0, len0, version);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * `true` when a group name stamped `incomingUpdatedAtMs` must replace the
 * stored one **for the same group**. Strictly greater wins; equal is not
 * newer, so a growth re-send of the stored name is not an update.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function groupNameIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.groupNameIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * Longest group-photo envelope.
 * @returns {number}
 */
export function groupPhotoEnvelopeMaxLen() {
    const ret = wasm.groupPhotoEnvelopeMaxLen();
    return ret >>> 0;
}

/**
 * Shortest group-photo envelope.
 * @returns {number}
 */
export function groupPhotoEnvelopeMinLen() {
    const ret = wasm.groupPhotoEnvelopeMinLen();
    return ret >>> 0;
}

/**
 * `true` when `incoming` replaces `stored` for the same group.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function groupPhotoIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.groupPhotoIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * Longest group allowed-reactions envelope.
 * @returns {number}
 */
export function groupReactionsEnvelopeMaxLen() {
    const ret = wasm.groupReactionsEnvelopeMaxLen();
    return ret >>> 0;
}

/**
 * Shortest group allowed-reactions envelope.
 * @returns {number}
 */
export function groupReactionsEnvelopeMinLen() {
    const ret = wasm.groupReactionsEnvelopeMinLen();
    return ret >>> 0;
}

/**
 * `true` when `incoming` replaces `stored` for the same group.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function groupReactionsIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.groupReactionsIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * The name prefilled for a guest joining a call from a link:
 * "<adjective> <animal>". `random` is any random 32-bit value from the
 * caller (`crypto.getRandomValues`), enough to reach every pair.
 * @param {number} random
 * @returns {string}
 */
export function guestName(random) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.guestName(random);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Wire length of a handle certificate: 169.
 * @returns {number}
 */
export function handleCertificateLen() {
    const ret = wasm.handleCertificateLen();
    return ret >>> 0;
}

/**
 * The 32-byte content hash over the sealed `record` (`undefined` for a
 * private name) and the concatenated 64-byte lookup keys of its terms.
 *
 * # Errors
 *
 * Throws `handleStatement.badContent: ...` (or a length code).
 * @param {Uint8Array | null | undefined} record
 * @param {Uint8Array} terms
 * @returns {Uint8Array}
 */
export function handleContentHash(record, terms) {
    var ptr0 = isLikeNone(record) ? 0 : passArray8ToWasm0(record, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(terms, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.handleContentHash(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * The largest size band a record carries.
 * @returns {number}
 */
export function handleRecordMaxSizeBand() {
    const ret = wasm.handleRecordMaxSizeBand();
    return ret;
}

/**
 * The 32-byte public handle key of a handle seed.
 *
 * # Errors
 *
 * Throws `handleStatement.badLength: ...`.
 * @param {Uint8Array} handle_seed
 * @returns {Uint8Array}
 */
export function handleWritePublicKey(handle_seed) {
    const ptr0 = passArray8ToWasm0(handle_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.handleWritePublicKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The 32-byte handle seed from the 32-byte journal root seed.
 *
 * # Errors
 *
 * Throws `handleStatement.badLength: ...`.
 * @param {Uint8Array} journal_root_seed
 * @returns {Uint8Array}
 */
export function handleWriteSeed(journal_root_seed) {
    const ptr0 = passArray8ToWasm0(journal_root_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.handleWriteSeed(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Which info the header opens for a conversation of this kind.
 * @param {ConversationKind} kind
 * @returns {InfoTarget}
 */
export function infoTarget(kind) {
    const ret = wasm.infoTarget(kind);
    return ret;
}

/**
 * ADR-0047 — the inline-playback allowlist: how an attachment with this
 * MIME type may be rendered inline, or `undefined` for a file chip.
 * Decided by MIME alone (parameters after `;` ignored, case-insensitive);
 * never pass `kind` here.
 * @param {string} mime
 * @returns {InlineMediaType | undefined}
 */
export function inlineMediaType(mime) {
    const ptr0 = passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.inlineMediaType(ptr0, len0);
    return ret === 3 ? undefined : ret;
}

/**
 * Length of a server OPRF output: 64.
 * @returns {number}
 */
export function introductionOprfOutputLen() {
    const ret = wasm.introductionOprfOutputLen();
    return ret >>> 0;
}

/**
 * The tags of a record wire, flat-concatenated (`fofTagLen()` = 32 bytes
 * each, in entry order), to match against `fofTag` without opening.
 *
 * # Errors
 *
 * Throws with `introductionError` for a malformed record.
 * @param {Uint8Array} record
 * @returns {Uint8Array}
 */
export function introductionTags(record) {
    const ptr0 = passArray8ToWasm0(record, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.introductionTags(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Width in bytes of a discovery-ledger id (`/i/`, `/c/`, `/g/` pointer
 * links): 6. The server mints it; a client only carries it (ADR-0028
 * short-link amendment, 2026-09-27).
 * @returns {number}
 */
export function inviteBlobIdLen() {
    const ret = wasm.inviteBlobIdLen();
    return ret >>> 0;
}

/**
 * Length of a ledger id in a link: base64url without padding, 8 characters.
 * A pointer path segment of any other length is not a pointer id.
 * @returns {number}
 */
export function inviteBlobIdUrlLen() {
    const ret = wasm.inviteBlobIdUrlLen();
    return ret >>> 0;
}

/**
 * Width in bytes of an invite link secret (the URL fragment), every kind: 12.
 * @returns {number}
 */
export function inviteLinkSecretLen() {
    const ret = wasm.inviteLinkSecretLen();
    return ret >>> 0;
}

/**
 * Length of an invite link secret in the URL fragment: base64url without
 * padding, 16 characters. Any other fragment length (or none) is a damaged
 * link.
 * @returns {number}
 */
export function inviteLinkSecretUrlLen() {
    const ret = wasm.inviteLinkSecretUrlLen();
    return ret >>> 0;
}

/**
 * Whether `bytes` is an own-channel-comment envelope.
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isChannelCommentEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isChannelCommentEnvelope(ptr0, len0);
    return ret !== 0;
}

/**
 * Whether `bytes` is an own-channel-reaction envelope.
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isChannelReactionEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isChannelReactionEnvelope(ptr0, len0);
    return ret !== 0;
}

/**
 * `true` when `bytes` open with the device-acknowledgement envelope domain
 * (`lakko/sync/device-ack/v1`): the routing question for a DeviceGroup
 * service envelope. Says nothing about validity;
 * `SyncedState.applyDeviceAckEnvelope` decides that.
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isDeviceAckEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isDeviceAckEnvelope(ptr0, len0);
    return ret !== 0;
}

/**
 * Whether `len` is one of the two knock row sizes.
 * @param {number} len
 * @returns {boolean}
 */
export function isKnockRowLen(len) {
    const ret = wasm.isKnockRowLen(len);
    return ret !== 0;
}

/**
 * `true` when `bytes` open with the lineage envelope domain
 * (`lakko/sync/lineage/v1`): the routing question for a DeviceGroup service
 * envelope, asked before the `lakko-contacts` dispatcher. Says nothing
 * about validity; `SyncedState.applyLineageEnvelope` decides that.
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isLineageEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isLineageEnvelope(ptr0, len0);
    return ret !== 0;
}

/**
 * `true` when `bytes` open with the claim domain: the cheap routing question
 * a client asks of a DeviceGroup service envelope.
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isLinkClaimEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isLinkClaimEnvelope(ptr0, len0);
    return ret !== 0;
}

/**
 * `true` when `bytes` open with the note's domain.
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isLinkFoundedEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isLinkFoundedEnvelope(ptr0, len0);
    return ret !== 0;
}

/**
 * `true` when `bytes` open with the link-record envelope domain
 * (`lakko/sync/link-record/v1`, ADR-0079): the routing question for a
 * DeviceGroup service envelope.
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isLinkRecordEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isLinkRecordEnvelope(ptr0, len0);
    return ret !== 0;
}

/**
 * Whether `term` is a name term: 1 to 16 characters, no whitespace, already
 * normalised.
 * @param {string} term
 * @returns {boolean}
 */
export function isNameTerm(term) {
    const ptr0 = passStringToWasm0(term, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isNameTerm(ptr0, len0);
    return ret !== 0;
}

/**
 * `true` when `bytes` open with the own-username envelope domain
 * (`lakko/sync/username/v1`): the routing question for a DeviceGroup
 * service envelope, asked before the `lakko-contacts` dispatcher. Says
 * nothing about validity; `SyncedState.applyOwnUsernameEnvelope` decides
 * that.
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isOwnUsernameEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isOwnUsernameEnvelope(ptr0, len0);
    return ret !== 0;
}

/**
 * Whether `bytes` is a quick-reaction envelope.
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isQuickReactionEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isQuickReactionEnvelope(ptr0, len0);
    return ret !== 0;
}

/**
 * `true` when `bytes` open with the recovery-email envelope domain
 * (`lakko/sync/recovery-email/v1`): the routing question for a DeviceGroup
 * service envelope, asked before the `lakko-contacts` dispatcher. Says
 * nothing about validity; `SyncedState.applyRecoveryEmailEnvelope` decides
 * that.
 * @param {Uint8Array} bytes
 * @returns {boolean}
 */
export function isRecoveryEmailEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isRecoveryEmailEnvelope(ptr0, len0);
    return ret !== 0;
}

/**
 * Whether `text` is exactly one emoji.
 * @param {string} text
 * @returns {boolean}
 */
export function isSingleEmoji(text) {
    const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isSingleEmoji(ptr0, len0);
    return ret !== 0;
}

/**
 * Whether `name` is an acceptable folder name as it stands (1..=24
 * characters, at most 96 bytes, the display-name rule, trimmed).
 * @param {string} name
 * @returns {boolean}
 */
export function isValidFolderName(name) {
    const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isValidFolderName(ptr0, len0);
    return ret !== 0;
}

/**
 * Whether `row` has the shape of a sealed mailbox row (version byte and a
 * bucket length), checkable without any key.
 * @param {Uint8Array} row
 * @returns {boolean}
 */
export function isWellFormedMailboxRow(row) {
    const ptr0 = passArray8ToWasm0(row, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.isWellFormedMailboxRow(ptr0, len0);
    return ret !== 0;
}

/**
 * Resolve a wire-form KeyPackage's leaf to the **account** it belongs to
 * (ADR-0050): the device binding's account key, the leaf key itself for an
 * unbound Phase 1 leaf, or `undefined` for a leaf whose binding does not
 * verify. The call-admission dedup and a contact's device list key on this.
 * @param {Uint8Array} key_package
 * @returns {Uint8Array | undefined}
 */
export function keyPackageAccountKey(key_package) {
    const ptr0 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.keyPackageAccountKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    let v2;
    if (ret[0] !== 0) {
        v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * Extract the 32-byte leaf Ed25519 signature key from a wire-form KeyPackage —
 * the stable per-identity key distinct KeyPackages minted by the same identity
 * share (only the HPKE init key is fresh per KP). Used by the call-admission
 * dedup (ADR-0032 / G-22) so a peer re-minting fresh KeyPackages under one
 * identity is admitted at most once.
 *
 * # Errors
 *
 * `JsError` on a KeyPackage parse failure.
 * @param {Uint8Array} key_package
 * @returns {Uint8Array}
 */
export function keyPackageSignatureKey(key_package) {
    const ptr0 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.keyPackageSignatureKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * `BLAKE3-derive-key("lakko/key-reset/account-ref/v1", accountId)`: the
 * one-way per-account value a key-reset attestation names instead of the
 * account id. Returns the 32-byte value; throws `invalid: ...` when
 * `accountId` is not 16 bytes.
 * @param {Uint8Array} account_id
 * @returns {Uint8Array}
 */
export function keyResetAccountRef(account_id) {
    const ptr0 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.keyResetAccountRef(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The one length of a key-rotation envelope (192 bytes).
 * @returns {number}
 */
export function keyRotationEnvelopeLen() {
    const ret = wasm.keyRotationEnvelopeLen();
    return ret >>> 0;
}

/**
 * `true` when a pending envelope prepared at `preparedAtMs` is too old to
 * keep at `nowMs` (both `bigint`). A stamp in the future is not expired.
 * @param {bigint} prepared_at_ms
 * @param {bigint} now_ms
 * @returns {boolean}
 */
export function keyRotationPendingExpired(prepared_at_ms, now_ms) {
    const ret = wasm.keyRotationPendingExpired(prepared_at_ms, now_ms);
    return ret !== 0;
}

/**
 * How long a pending envelope is kept before it is dropped, in
 * milliseconds (14 days), as a `bigint`.
 * @returns {bigint}
 */
export function keyRotationPendingMaxAgeMs() {
    const ret = wasm.keyRotationPendingMaxAgeMs();
    return BigInt.asUintN(64, ret);
}

/**
 * The larger knock row size.
 * @returns {number}
 */
export function knockLargeRowLen() {
    const ret = wasm.knockLargeRowLen();
    return ret >>> 0;
}

/**
 * The knock-key version a row names, or `undefined`.
 * @param {Uint8Array} row
 * @returns {number | undefined}
 */
export function knockRowVersion(row) {
    const ptr0 = passArray8ToWasm0(row, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.knockRowVersion(ptr0, len0);
    return ret === 0xFFFFFF ? undefined : ret;
}

/**
 * The one well-formed knock-seed envelope length.
 * @returns {number}
 */
export function knockSeedEnvelopeLen() {
    const ret = wasm.knockSeedEnvelopeLen();
    return ret >>> 0;
}

/**
 * The smaller knock row size.
 * @returns {number}
 */
export function knockSmallRowLen() {
    const ret = wasm.knockSmallRowLen();
    return ret >>> 0;
}

/**
 * Which language to show: the stored choice (`undefined` = "Same as
 * system"), else the first supported entry of `navigator.languages`, else
 * English.
 * @param {string | null | undefined} selected
 * @param {string[]} system_preferred
 * @returns {string}
 */
export function l10nResolveLanguage(selected, system_preferred) {
    let deferred3_0;
    let deferred3_1;
    try {
        var ptr0 = isLikeNone(selected) ? 0 : passStringToWasm0(selected, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        var len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(system_preferred, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.l10nResolveLanguage(ptr0, len0, ptr1, len1);
        deferred3_0 = ret[0];
        deferred3_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * Every supported language, English first.
 * @returns {LanguageInfo[]}
 */
export function l10nSupportedLanguages() {
    const ret = wasm.l10nSupportedLanguages();
    var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * How long a full track that has never presented a frame is waited for
 * before a playing light track is shown instead, milliseconds. Chosen, not
 * measured.
 * @returns {number}
 */
export function layerFirstFrameGraceMs() {
    const ret = wasm.layerFirstFrameGraceMs();
    return ret >>> 0;
}

/**
 * The full track is standing still once it has presented no frame for this
 * long, milliseconds. Chosen, not measured.
 * @returns {number}
 */
export function layerFullStallMs() {
    const ret = wasm.layerFullStallMs();
    return ret >>> 0;
}

/**
 * How long the full track must present frames without a gap before the tile
 * returns to it, milliseconds. Chosen, not measured.
 * @returns {number}
 */
export function layerFullSteadyMs() {
    const ret = wasm.layerFullSteadyMs();
    return ret >>> 0;
}

/**
 * The light track is playing if it presented a frame within this,
 * milliseconds. Chosen, not measured.
 * @returns {number}
 */
export function layerLightFreshMs() {
    const ret = wasm.layerLightFreshMs();
    return ret >>> 0;
}

/**
 * How often a tile calls `LayerDisplay.tick`, milliseconds. Chosen, not
 * measured: a third of {@link layerFullStallMs}.
 * @returns {number}
 */
export function layerTickMs() {
    const ret = wasm.layerTickMs();
    return ret >>> 0;
}

/**
 * Longest lineage envelope `SyncedState.lineageEnvelopes()` produces; below
 * `deviceGroupServiceEnvelopeMaxLen()`.
 * @returns {number}
 */
export function lineageEnvelopeMaxLen() {
    const ret = wasm.lineageEnvelopeMaxLen();
    return ret >>> 0;
}

/**
 * Longest group id a lineage register accepts (each of the two): 64.
 * @returns {number}
 */
export function lineageGroupIdMaxLen() {
    const ret = wasm.lineageGroupIdMaxLen();
    return ret >>> 0;
}

/**
 * Whether a device that holds NO claim of its own treats the request as
 * answered: some device in the current roster claimed it.
 *
 * # Errors
 *
 * `"bounds: ..."` when a key is not 32 bytes or the lists do not match.
 * @param {BigUint64Array} claim_ids
 * @param {Uint8Array} claim_authors
 * @param {Uint8Array} roster_keys
 * @returns {boolean}
 */
export function linkClaimAnsweredElsewhere(claim_ids, claim_authors, roster_keys) {
    const ptr0 = passArray64ToWasm0(claim_ids, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(claim_authors, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(roster_keys, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.linkClaimAnsweredElsewhere(ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] !== 0;
}

/**
 * The one length of a claim envelope.
 * @returns {number}
 */
export function linkClaimEnvelopeLen() {
    const ret = wasm.linkClaimEnvelopeLen();
    return ret >>> 0;
}

/**
 * How the claims for one request end for the device whose DeviceGroup leaf
 * key is `ownKey`. `claimIds[i]` was posted by the device whose leaf key is
 * `claimAuthors[32*i..32*i+32]` (this device's own claims carry `ownKey`);
 * `rosterKeys` is the DeviceGroup's current leaf keys, concatenated. The
 * earliest claim from a roster device wins, and its author builds.
 *
 * # Errors
 *
 * `"bounds: ..."` when a key is not 32 bytes or the lists do not match.
 * @param {Uint8Array} own_key
 * @param {BigUint64Array} claim_ids
 * @param {Uint8Array} claim_authors
 * @param {Uint8Array} roster_keys
 * @returns {LinkClaimDecision}
 */
export function linkClaimOutcome(own_key, claim_ids, claim_authors, roster_keys) {
    const ptr0 = passArray8ToWasm0(own_key, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray64ToWasm0(claim_ids, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(claim_authors, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(roster_keys, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.linkClaimOutcome(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return LinkClaimDecision.__wrap(ret[0]);
}

/**
 * The request key `linkId ‖ requesterAccountKey` (48 bytes).
 *
 * # Errors
 *
 * `"bounds: ..."` on a wrong-length input.
 * @param {Uint8Array} link_id
 * @param {Uint8Array} requester_account_key
 * @returns {Uint8Array}
 */
export function linkClaimRequestKey(link_id, requester_account_key) {
    const ptr0 = passArray8ToWasm0(link_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(requester_account_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.linkClaimRequestKey(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * The longest well-formed note.
 * @returns {number}
 */
export function linkFoundedEnvelopeMaxLen() {
    const ret = wasm.linkFoundedEnvelopeMaxLen();
    return ret >>> 0;
}

/**
 * The shortest well-formed note.
 * @returns {number}
 */
export function linkFoundedEnvelopeMinLen() {
    const ret = wasm.linkFoundedEnvelopeMinLen();
    return ret >>> 0;
}

/**
 * Width in bytes of a link id: 16.
 * @returns {number}
 */
export function linkIdLen() {
    const ret = wasm.linkIdLen();
    return ret >>> 0;
}

/**
 * The one length of a refusal.
 * @returns {number}
 */
export function linkRefusalLen() {
    const ret = wasm.linkRefusalLen();
    return ret >>> 0;
}

/**
 * The Ed25519 public key of a per-link revocation seed (ADR-0078
 * section 10): what an invite upload (`/i/`, `/c/`, `/g/`) stores in the
 * ledger in place of an owner or issuer. The client draws 32 random bytes
 * for each link it mints, keeps them beside the link secret, uploads this
 * key, and later signs a revocation with the seed.
 *
 * # Errors
 *
 * `JsError` when `seed` is not 32 bytes.
 * @param {Uint8Array} seed
 * @returns {Uint8Array}
 */
export function linkRevocationPublicKey(seed) {
    const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.linkRevocationPublicKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Load the DeviceGroup of the account whose identity is `accountIdentity`
 * from `identity`'s MLS provider storage. Pair with
 * `Identity.restoreStorage` on app startup: restore the
 * snapshot first, then call this to get the live handle.
 *
 * Returns `undefined` when the provider has no row under the
 * deterministic group_id — typical case: a freshly-derived
 * Identity that hasn't joined the DeviceGroup yet.
 *
 * Free function instead of a `DeviceGroup::load` constructor
 * because wasm-bindgen's constructor surface doesn't tolerate
 * `Result<Option<Self>, _>` returns cleanly — the same shape
 * `Group::load` uses works fine as a free function.
 * @param {Identity} identity
 * @param {Uint8Array} account_id
 * @param {Identity} account_identity
 * @returns {DeviceGroup | undefined}
 */
export function loadDeviceGroup(identity, account_id, account_identity) {
    _assertClass(identity, Identity);
    const ptr0 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    _assertClass(account_identity, Identity);
    const ret = wasm.loadDeviceGroup(identity.__wbg_ptr, ptr0, len0, account_identity.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] === 0 ? undefined : DeviceGroup.__wrap(ret[0]);
}

/**
 * Load the DeviceGroup stored under `groupId`: one this device joined from a
 * Welcome before it held the account seed. `undefined` when nothing is
 * stored there.
 * @param {Identity} identity
 * @param {Uint8Array} account_id
 * @param {Uint8Array} group_id
 * @returns {DeviceGroup | undefined}
 */
export function loadDeviceGroupById(identity, account_id, group_id) {
    _assertClass(identity, Identity);
    const ptr0 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.loadDeviceGroupById(identity.__wbg_ptr, ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] === 0 ? undefined : DeviceGroup.__wrap(ret[0]);
}

/**
 * The login handle for a typed username (ADR-0054 S5): normalise, apply
 * the username rule, then `BLAKE3-derive-key("lakko/auth/login-handle/v1",
 * canonical)`. 32 bytes; sent base64url (no padding) as `handle` to the
 * pre-session sign-in and recovery routes of `services/auth`, and bound to
 * the signed-in account with `PUT /auth/login-handle`.
 *
 * # Errors
 *
 * Throws the same `Error` as [`validate_username`], with its
 * `usernameError` code property.
 * @param {string} username
 * @returns {Uint8Array}
 */
export function loginHandle(username) {
    const ptr0 = passStringToWasm0(username, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.loginHandle(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Whether a typed string is worth spending a network lookup on.
 *
 * The predicate is the canonical username rule the discovery blinder itself
 * enforces, not a copy of it: asking here is the difference between a quiet
 * "type the whole name" and making a person pay a proof of work to be told
 * no.
 *
 * It is NOT a check that the name exists — it cannot be. The server evaluates
 * a blinded name without learning it, so it answers only "does this exact
 * name exist" and nothing about prefixes. A client must offer this as "look
 * up the whole name", never as a list of partial matches.
 * @param {string} typed
 * @returns {boolean}
 */
export function looksLikeAName(typed) {
    const ptr0 = passStringToWasm0(typed, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.looksLikeAName(ptr0, len0);
    return ret !== 0;
}

/**
 * The 32-byte address of the mailbox named by the 32-byte `mailboxPk`.
 *
 * # Errors
 *
 * Throws with `mailboxError` `keyLength`.
 * @param {Uint8Array} mailbox_pk
 * @returns {Uint8Array}
 */
export function mailboxAddress(mailbox_pk) {
    const ptr0 = passArray8ToWasm0(mailbox_pk, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.mailboxAddress(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Length of a mailbox binding: 97.
 * @returns {number}
 */
export function mailboxBindingLen() {
    const ret = wasm.mailboxBindingLen();
    return ret >>> 0;
}

/**
 * The longest body a mailbox row carries (983 035 bytes).
 * @returns {number}
 */
export function mailboxMaxBodyLen() {
    const ret = wasm.mailboxMaxBodyLen();
    return ret >>> 0;
}

/**
 * Most records one bundle carries, padding included: 1024.
 * @returns {number}
 */
export function maxBundleRecords() {
    const ret = wasm.maxBundleRecords();
    return ret >>> 0;
}

/**
 * The longest channel invitation card the chat envelope carries.
 * @returns {number}
 */
export function maxChannelInviteBytes() {
    const ret = wasm.maxChannelInviteBytes();
    return ret >>> 0;
}

/**
 * Most contacts the chosen list holds: 4096.
 * @returns {number}
 */
export function maxChosenContacts() {
    const ret = wasm.maxChosenContacts();
    return ret >>> 0;
}

/**
 * Most groups and channels the chosen list holds: 1024.
 * @returns {number}
 */
export function maxChosenScopes() {
    const ret = wasm.maxChosenScopes();
    return ret >>> 0;
}

/**
 * Most delegations one envelope carries: 16.
 * @returns {number}
 */
export function maxCommitterDelegations() {
    const ret = wasm.maxCommitterDelegations();
    return ret >>> 0;
}

/**
 * The longest a delegation may still have to live when the DS checks it,
 * in ms (24 hours). A `bigint`.
 * @returns {bigint}
 */
export function maxDelegationTtlMs() {
    const ret = wasm.maxDelegationTtlMs();
    return BigInt.asUintN(64, ret);
}

/**
 * The most search terms one record carries.
 * @returns {number}
 */
export function maxHandleTerms() {
    const ret = wasm.maxHandleTerms();
    return ret >>> 0;
}

/**
 * Most search terms one record carries: 65.
 * @returns {number}
 */
export function maxIntroductionTerms() {
    const ret = wasm.maxIntroductionTerms();
    return ret >>> 0;
}

/**
 * Longest profile a member card seals, in bytes.
 * @returns {number}
 */
export function maxMemberCardProfileLen() {
    const ret = wasm.maxMemberCardProfileLen();
    return ret >>> 0;
}

/**
 * Most name terms one person publishes: 64.
 * @returns {number}
 */
export function maxNameTerms() {
    const ret = wasm.maxNameTerms();
    return ret >>> 0;
}

/**
 * Maximum ring-buffer size accepted by
 * [`CallQualitySampler::new`].
 * @returns {number}
 */
export function maxQualitySampleWindow() {
    const ret = wasm.maxQualitySampleWindow();
    return ret >>> 0;
}

/**
 * Longest scope id a reach key is derived over: 64.
 * @returns {number}
 */
export function maxReachScopeIdLen() {
    const ret = wasm.maxReachScopeIdLen();
    return ret >>> 0;
}

/**
 * Most entries a reach state holds: 4096.
 * @returns {number}
 */
export function maxReachStateEntries() {
    const ret = wasm.maxReachStateEntries();
    return ret >>> 0;
}

/**
 * The most kinds of reaction shown under one message.
 * @returns {number}
 */
export function maxReactionKindsShown() {
    const ret = wasm.maxReactionKindsShown();
    return ret >>> 0;
}

/**
 * The most reactions one person gives one message.
 * @returns {number}
 */
export function maxReactionsPerMessage() {
    const ret = wasm.maxReactionsPerMessage();
    return ret >>> 0;
}

/**
 * Longest group or channel id in the chosen list: 64.
 * @returns {number}
 */
export function maxVisibilityScopeIdLen() {
    const ret = wasm.maxVisibilityScopeIdLen();
    return ret >>> 0;
}

/**
 * How many leading bytes of `frame` a sender leaves in the clear for
 * `codec` (ADR-0071). Never more than the frame holds.
 * @param {MediaCodec} codec
 * @param {Uint8Array} frame
 * @returns {number}
 */
export function mediaClearPrefixLen(codec, frame) {
    const ptr0 = passArray8ToWasm0(frame, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.mediaClearPrefixLen(codec, ptr0, len0);
    return ret >>> 0;
}

/**
 * Map a negotiated RTP codec MIME type (`"video/VP8"`, `"audio/opus"`,
 * `"audio/red"`, any case) to a `MediaCodec`. Anything else is `Other`.
 * @param {string} mime
 * @returns {MediaCodec}
 */
export function mediaCodecFromMime(mime) {
    const ptr0 = passStringToWasm0(mime, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.mediaCodecFromMime(ptr0, len0);
    return ret;
}

/**
 * The clear epoch of a member card, to pick the matching seed before
 * `openMemberCard`. Not authenticated until the card opens. Throws on a
 * malformed card.
 * @param {Uint8Array} card
 * @returns {number}
 */
export function memberCardEpoch(card) {
    const ptr0 = passArray8ToWasm0(card, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.memberCardEpoch(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] >>> 0;
}

/**
 * The request header a member-card upload or delete carries
 * its subscriber signature in (`x-lakko-member-card-sig`).
 * @returns {string}
 */
export function memberCardSigHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.memberCardSigHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * What the caller should do about a membership-refusal code —
 * `"retryLater"`, `"doNotRetry"` or `"notPermitted"`, and `null` for an
 * unknown code.
 *
 * **Three-valued, and the middle value is the point.** "Not permitted" and
 * "retry later" are the obvious two, and they leave out the commonest case: a
 * request that is allowed but cannot be accepted **as formed**, ever. Retrying
 * that loops forever. The rule is: retryable exactly when a bounded queue was
 * full.
 *
 * The thrown error already carries `refusalDisposition`, so this is for a
 * caller that kept a code and wants to re-ask later. Ask it rather than
 * deciding from the code's spelling: the rule lives in the core so it cannot
 * drift between web, iOS and Android.
 * @param {string} reason
 * @returns {string | undefined}
 */
export function membershipRefusalDisposition(reason) {
    const ptr0 = passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.membershipRefusalDisposition(ptr0, len0);
    let v2;
    if (ret[0] !== 0) {
        v2 = getStringFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * `true` when the **identical** call will succeed once a committer has swept,
 * with the caller doing nothing else — i.e. when the UI may offer a plain
 * retry. An unknown code is `false`, which is the fail-closed direction: no
 * retry is offered for a refusal this build does not understand.
 * @param {string} reason
 * @returns {boolean}
 */
export function membershipRefusalRetryMaySucceed(reason) {
    const ptr0 = passStringToWasm0(reason, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.membershipRefusalRetryMaySucceed(ptr0, len0);
    return ret !== 0;
}

/**
 * Whether `text` mentions `username` (`@` + the canonical username, whole).
 * @param {string} text
 * @param {string} username
 * @returns {boolean}
 */
export function mentionsUsername(text, username) {
    const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(username, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.mentionsUsername(ptr0, len0, ptr1, len1);
    return ret !== 0;
}

/**
 * Fold a reach state received from another of the owner's devices into the
 * stored one (ADR-0060 section 7): counters by maximum per slot, never a
 * whole-state replace, and the core decides whether to persist and whether
 * to send the result on. Every client uses this rule; none re-implements it.
 *
 * # Errors
 *
 * Throws with `visibilityError` when the merged state breaks a bound; keep
 * what was stored.
 * @param {ReachState} stored
 * @param {ReachState} incoming
 * @param {bigint} now_ms
 * @returns {ReachStateMerge}
 */
export function mergeReachState(stored, incoming, now_ms) {
    _assertClass(stored, ReachState);
    _assertClass(incoming, ReachState);
    const ret = wasm.mergeReachState(stored.__wbg_ptr, incoming.__wbg_ptr, now_ms);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ReachStateMerge.__wrap(ret[0]);
}

/**
 * Merge two sets of saved messages (both `Array<VaultMessage>`, consumed):
 * what a device does when its write meets `409`. The union of the rows by
 * `messageId`; for one id the row with the higher `lastOpId` wins, and on a
 * tie the same row wins on every device. The result is sorted by
 * `messageId` and holds each id once. Commutative, associative and
 * idempotent; never fails.
 * @param {VaultMessage[]} a
 * @param {VaultMessage[]} b
 * @returns {VaultMessage[]}
 */
export function mergeVaultMessages(a, b) {
    const ptr0 = passArrayJsValueToWasm0(a, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayJsValueToWasm0(b, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.mergeVaultMessages(ptr0, len0, ptr1, len1);
    var v3 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v3;
}

/**
 * A person sends the middle copy of their camera from this many people in
 * the call on. Chosen.
 * @returns {number}
 */
export function middleSenderMinPeople() {
    const ret = wasm.middleSenderMinPeople();
    return ret >>> 0;
}

/**
 * Whether this device sends the middle copy of its camera: only from
 * {@link middleSenderMinPeople} people in the call on (its own person
 * included).
 * @param {number} people
 * @returns {boolean}
 */
export function middleSenderWanted(people) {
    const ret = wasm.middleSenderWanted(people);
    return ret !== 0;
}

/**
 * A fresh random 16-byte folder id, minted once when a folder is created.
 * @returns {Uint8Array}
 */
export function mintFolderId() {
    const ret = wasm.mintFolderId();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Longest input the normaliser accepts, in bytes: 1024.
 * @returns {number}
 */
export function nameInputMaxBytes() {
    const ret = wasm.nameInputMaxBytes();
    return ret >>> 0;
}

/**
 * The post-filter a lookup's candidates pass through: every word of the
 * query is a prefix of some word of the name.
 * @param {string} display_name
 * @param {string} query
 * @returns {boolean}
 */
export function nameMatchesQuery(display_name, query) {
    const ptr0 = passStringToWasm0(display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(query, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.nameMatchesQuery(ptr0, len0, ptr1, len1);
    return ret !== 0;
}

/**
 * The one term a typed query looks up: its longest normalised word, cut to
 * 16 characters. `undefined` when the query is not searchable.
 * @param {string} query
 * @returns {string | undefined}
 */
export function nameQueryTerm(query) {
    const ptr0 = passStringToWasm0(query, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.nameQueryTerm(ptr0, len0);
    let v2;
    if (ret[0] !== 0) {
        v2 = getStringFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * Fewest characters a normalised query needs to be searched: 2.
 * @returns {number}
 */
export function nameSearchMinChars() {
    const ret = wasm.nameSearchMinChars();
    return ret >>> 0;
}

/**
 * The search terms a display name publishes or seals under: every prefix of
 * 3 to 16 characters of every normalised word (a shorter word whole), at
 * most 64, in order. Blind each with `OprfBlinder.blindNameTerm`.
 * @param {string} display_name
 * @returns {string[]}
 */
export function nameSearchTerms(display_name) {
    const ptr0 = passStringToWasm0(display_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.nameSearchTerms(ptr0, len0);
    var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Longest term: 16.
 * @returns {number}
 */
export function nameTermMaxChars() {
    const ret = wasm.nameTermMaxChars();
    return ret >>> 0;
}

/**
 * Shortest prefix a word publishes as a term: 3.
 * @returns {number}
 */
export function nameTermMinChars() {
    const ret = wasm.nameTermMinChars();
    return ret >>> 0;
}

/**
 * A fresh random 16-byte comment nonce.
 * @returns {Uint8Array}
 */
export function newChannelCommentNonce() {
    const ret = wasm.newChannelCommentNonce();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Generate a fresh 32-byte content-key seed.
 *
 * Production seeds come out of the PublisherGroup MLS
 * exporter; this is the demo / single-publisher
 * affordance for the web channels UI before the MLS-
 * per-channel wiring lands. The `ForTest` suffix on
 * the JS name makes that scope explicit — grep the
 * codebase for "ForTest" to find non-production seed
 * sources.
 * @returns {Uint8Array}
 */
export function newChannelSeedForTest() {
    const ret = wasm.newChannelSeedForTest();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Prepare what a person typed as their display name: trim, `undefined` for
 * blank ("no name"), and throw the reason for anything out of bounds.
 *
 * # Errors
 *
 * Throws an `Error` with a `displayNameError` code: `"lineBreak"`,
 * `"controlOrFormatCharacter"`, `"tooLong"` or `"tooManyBytes"`.
 * @param {string} input
 * @returns {string | undefined}
 */
export function normalizeDisplayName(input) {
    const ptr0 = passStringToWasm0(input, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.normalizeDisplayName(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    let v2;
    if (ret[0] !== 0) {
        v2 = getStringFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * Trim and lowercase a word the person typed; `undefined` when nothing is
 * left.
 * @param {string} raw
 * @returns {string | undefined}
 */
export function normalizeFeedWord(raw) {
    const ptr0 = passStringToWasm0(raw, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.normalizeFeedWord(ptr0, len0);
    let v2;
    if (ret[0] !== 0) {
        v2 = getStringFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * Normalise a display name or a typed query for search (NFKD, lowercase,
 * marks dropped, whitespace collapsed, trimmed). `undefined` when the input
 * is not searchable: over 1024 bytes, or under 2 characters normalised.
 * @param {string} input
 * @returns {string | undefined}
 */
export function normalizeNameForSearch(input) {
    const ptr0 = passStringToWasm0(input, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.normalizeNameForSearch(ptr0, len0);
    let v2;
    if (ret[0] !== 0) {
        v2 = getStringFromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * Normalise a recovery email address: trim, lower-case the whole address,
 * then require exactly one `@` with text on both sides, 3 to 254 bytes, no
 * control characters and no white space.
 *
 * # Errors
 *
 * Throws `"<code>: <reason>"` with the codes `too-short`, `too-long`,
 * `control-character`, `whitespace`, `not-one-at-sign`, `empty-part`.
 * @param {string} address
 * @returns {string}
 */
export function normalizeRecoveryEmail(address) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passStringToWasm0(address, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.normalizeRecoveryEmail(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * Canonical username form — same transformation the OPRF
 * blinder applies internally. Useful for UI confirmation
 * ("you're looking up @alice") and for client-side dedup of the
 * contact list.
 *
 * Pure function; idempotent.
 * @param {string} input
 * @returns {string}
 */
export function normalizeUsername(input) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ptr0 = passStringToWasm0(input, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.normalizeUsername(ptr0, len0);
        deferred2_0 = ret[0];
        deferred2_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * Open an admission-seed envelope with the ACCOUNT identity it is
 * addressed to, returning the 32-byte admission seed. Also check the MLS
 * sender is the channel's owner.
 *
 * # Errors
 *
 * Throws `admissionSeed.<code>: ...`.
 * @param {Uint8Array} plaintext
 * @param {Identity} account_identity
 * @returns {Uint8Array}
 */
export function openAdmissionSeedEnvelope(plaintext, account_identity) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    _assertClass(account_identity, Identity);
    const ret = wasm.openAdmissionSeedEnvelope(ptr0, len0, account_identity.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Decrypt an on-the-wire backup envelope back to its payload
 * bytes. Wrong phrase / tampered blob folds into a single
 * failure variant — see [`lakko_backup::BackupError`].
 *
 * # Errors
 *
 * `JsError` for wrong-phrase / tampered-blob / malformed-wire
 * failures.
 * @param {Uint8Array} phrase
 * @param {Uint8Array} wire
 * @returns {Uint8Array}
 */
export function openBackup(phrase, wire) {
    const ptr0 = passArray8ToWasm0(phrase, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.openBackup(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Publisher side: open an admission ticket and verify the invite key's
 * signature; returns the 32-byte invite public key.
 *
 * # Errors
 *
 * Throws `admissionTicket.<code>: ...`.
 * @param {Uint8Array} admission_seed
 * @param {Uint8Array} channel_id
 * @param {Uint8Array} subscriber_pk
 * @param {Uint8Array} ticket
 * @returns {Uint8Array}
 */
export function openChannelAdmissionTicket(admission_seed, channel_id, subscriber_pk, ticket) {
    const ptr0 = passArray8ToWasm0(admission_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(subscriber_pk, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(ticket, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.openChannelAdmissionTicket(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * Open a comment row and verify its author signature. Fail-closed.
 *
 * # Errors
 *
 * Throws `comment.<code>: ...`.
 * @param {Uint8Array} epoch_seed
 * @param {Uint8Array} channel_id
 * @param {number} epoch_id
 * @param {bigint} post_cursor
 * @param {Uint8Array} writer_key
 * @param {Uint8Array} sealed
 * @returns {OpenedComment}
 */
export function openChannelComment(epoch_seed, channel_id, epoch_id, post_cursor, writer_key, sealed) {
    const ptr0 = passArray8ToWasm0(epoch_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(writer_key, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.openChannelComment(ptr0, len0, ptr1, len1, epoch_id, post_cursor, ptr2, len2, ptr3, len3);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return OpenedComment.__wrap(ret[0]);
}

/**
 * Decode a channel invitation card, open its link and check it carries a
 * subscriber invitation. Does NOT check expiry.
 *
 * # Errors
 *
 * Throws when the card is malformed, the link does not open, or it is not
 * a subscriber invitation.
 * @param {Uint8Array} card
 * @returns {OpenedChannelInviteCard}
 */
export function openChannelInviteCard(card) {
    const ptr0 = passArray8ToWasm0(card, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.openChannelInviteCard(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return OpenedChannelInviteCard.__wrap(ret[0]);
}

/**
 * Open a group's or channel's record with the 64-byte OPRF output of the
 * searched term; `undefined` when no entry matches.
 *
 * # Errors
 *
 * Throws `handleRecord.<code>: ...`.
 * @param {Uint8Array} record
 * @param {Uint8Array} oprf_output
 * @returns {HandleRecordPayload | undefined}
 */
export function openHandleRecord(record, oprf_output) {
    const ptr0 = passArray8ToWasm0(record, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(oprf_output, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.openHandleRecord(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] === 0 ? undefined : HandleRecordPayload.__wrap(ret[0]);
}

/**
 * Inverse of [`sealHistoryBundle`]. Returns the prost-encoded
 * `HistoryBundle` bytes; the caller decodes via its own proto
 * runtime (`protobuf-ts` on TS).
 * @param {Uint8Array} secret
 * @param {Uint8Array} sealed_bytes
 * @returns {Uint8Array}
 */
export function openHistoryBundle(secret, sealed_bytes) {
    const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(sealed_bytes, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.openHistoryBundle(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Inverse of [`sealHistoryBundleStructured`]. Returns a flat
 * JS object the caller iterates through getters; no proto
 * runtime needed.
 * @param {Uint8Array} secret
 * @param {Uint8Array} sealed_bytes
 * @returns {OpenedHistoryBundle}
 */
export function openHistoryBundleStructured(secret, sealed_bytes) {
    const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(sealed_bytes, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.openHistoryBundleStructured(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return OpenedHistoryBundle.__wrap(ret[0]);
}

/**
 * Open a record wire with one 64-byte OPRF output; `undefined` when no
 * entry matches (not this person, or a padding dummy). A payload returned
 * always carries its `fof` grant.
 *
 * # Errors
 *
 * Throws with `introductionError` `open` when a tag matched but the record
 * failed to authenticate (a forgery, never a miss), `grant.missing` when
 * it opened without a grant, or for a malformed record.
 * @param {Uint8Array} record
 * @param {Uint8Array} oprf_output
 * @returns {IntroductionPayload | undefined}
 */
export function openIntroduction(record, oprf_output) {
    const ptr0 = passArray8ToWasm0(record, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(oprf_output, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.openIntroduction(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] === 0 ? undefined : IntroductionPayload.__wrap(ret[0]);
}

/**
 * Open a knock row with the knock seed of the version it names.
 *
 * # Errors
 *
 * Throws `knock.<code>: ...` (`notOpened` for another group's row).
 * @param {Uint8Array} knock_seed
 * @param {Uint8Array} row
 * @returns {KnockRequest}
 */
export function openKnockRow(knock_seed, row) {
    const ptr0 = passArray8ToWasm0(knock_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(row, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.openKnockRow(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return KnockRequest.__wrap(ret[0]);
}

/**
 * Open a knock-seed envelope with the ACCOUNT identity it is addressed to,
 * returning the 32-byte knock seed. Also check the MLS sender is the owner.
 *
 * # Errors
 *
 * Throws `knockSeed.<code>: ...` (`otherAccount` when addressed elsewhere).
 * @param {Uint8Array} plaintext
 * @param {Identity} account_identity
 * @returns {Uint8Array}
 */
export function openKnockSeedEnvelope(plaintext, account_identity) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    _assertClass(account_identity, Identity);
    const ret = wasm.openKnockSeedEnvelope(ptr0, len0, account_identity.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Open one row of the own mailbox. Fail-closed: an unknown version, a
 * length that is not a bucket, a row for another mailbox, an unknown kind
 * and malformed padding are all refused; the caller drops the row.
 *
 * # Errors
 *
 * Throws with `mailboxError` `badLength`, `unknownVersion`, `notOpened`,
 * `unknownKind`, `badPadding`.
 * @param {MailboxKey} key
 * @param {Uint8Array} row
 * @returns {OpenedMailboxRow}
 */
export function openMailboxRow(key, row) {
    _assertClass(key, MailboxKey);
    const ptr0 = passArray8ToWasm0(row, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.openMailboxRow(key.__wbg_ptr, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return OpenedMailboxRow.__wrap(ret[0]);
}

/**
 * Open a member card served for `subscriberPk` in `channelId` with the
 * 32-byte seed of the epoch it names and verify its account binding;
 * returns `{identityPk, profile}` (decode the profile with
 * `decodeProfileEnvelope`). Fail-closed: a card sealed for another
 * subscriber, channel or epoch, or naming an account that did not sign it,
 * does not open. Throws on any failure.
 * @param {Uint8Array} seed
 * @param {Uint8Array} card
 * @param {Uint8Array} channel_id
 * @param {Uint8Array} subscriber_pk
 * @returns {OpenedMemberCard}
 */
export function openMemberCard(seed, card, channel_id, subscriber_pk) {
    const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(card, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(subscriber_pk, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.openMemberCard(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return OpenedMemberCard.__wrap(ret[0]);
}

/**
 * Open a public record served by `/discovery/lookup` or
 * `/discovery/name/lookup` with the OPRF output of the searched username or
 * name term; `undefined` when no entry matches. A payload returned never
 * carries a grant (the person's mailbox is open).
 *
 * # Errors
 *
 * Throws with `introductionError` `grant.unexpected` when it opened with a
 * grant, and every error of `openIntroduction`.
 * @param {Uint8Array} record
 * @param {Uint8Array} oprf_output
 * @returns {IntroductionPayload | undefined}
 */
export function openPublicRecord(record, oprf_output) {
    const ptr0 = passArray8ToWasm0(record, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(oprf_output, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.openPublicRecord(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] === 0 ? undefined : IntroductionPayload.__wrap(ret[0]);
}

/**
 * Open a public seed envelope fetched for `expectedEpoch`.
 *
 * # Errors
 *
 * Throws `publicSeed.open: ...` for another epoch, key or channel, or
 * tampering.
 * @param {Uint8Array} read_key
 * @param {Uint8Array} channel_id
 * @param {number} expected_epoch
 * @param {Uint8Array} wire
 * @returns {Uint8Array}
 */
export function openPublicSeedEnvelope(read_key, channel_id, expected_epoch, wire) {
    const ptr0 = passArray8ToWasm0(read_key, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.openPublicSeedEnvelope(ptr0, len0, ptr1, len1, expected_epoch, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Open a registrar-seed envelope with the ACCOUNT identity it is addressed
 * to; the 32-byte registrar seed (feed it to `dsStatementSigner`).
 *
 * # Errors
 *
 * When the envelope is malformed, addressed to another account key, does
 * not open under this identity's X25519 key, or opens to a seed that does
 * not derive the envelope's registrar public key.
 * @param {Uint8Array} plaintext
 * @param {Identity} account_identity
 * @returns {Uint8Array}
 */
export function openRegistrarSeedEnvelope(plaintext, account_identity) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    _assertClass(account_identity, Identity);
    const ret = wasm.openRegistrarSeedEnvelope(ptr0, len0, account_identity.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Open any record a name or `@name` lookup returns (one namespace for
 * people, groups and channels); `undefined` when no entry matches.
 *
 * # Errors
 *
 * Throws `handleRecord.<code>: ...`.
 * @param {Uint8Array} record
 * @param {Uint8Array} oprf_output
 * @returns {SearchRecord | undefined}
 */
export function openSearchRecord(record, oprf_output) {
    const ptr0 = passArray8ToWasm0(record, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(oprf_output, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.openSearchRecord(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] === 0 ? undefined : SearchRecord.__wrap(ret[0]);
}

/**
 * Open a seed envelope with the subscriber's keypair.
 * Returns the recovered 32-byte content-key seed — hand
 * it to `verifyAndDecryptChannelPost` to decode posts on
 * this channel for this epoch.
 *
 * Throws on wire-shape / AEAD-open / wrong-recipient /
 * wrong-`(channel_id, epoch_id)` binding failures.
 * @param {ChannelSubscriberKeypair} keypair
 * @param {Uint8Array} channel_id
 * @param {number} epoch_id
 * @param {Uint8Array} wire
 * @returns {Uint8Array}
 */
export function openSubscriberSeedEnvelope(keypair, channel_id, epoch_id, wire) {
    _assertClass(keypair, ChannelSubscriberKeypair);
    const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.openSubscriberSeedEnvelope(keypair.__wbg_ptr, ptr0, len0, epoch_id, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Inverse of [`seal_with_key`]: open a `nonce(12) ‖ ciphertext+tag` wire blob
 * under the same raw 32-byte `key`. Returns the recovered plaintext. Throws
 * when the key is not 32 bytes, the blob is too short, or authentication
 * fails (wrong key / tampered blob).
 * @param {Uint8Array} key
 * @param {Uint8Array} wire
 * @returns {Uint8Array}
 */
export function openWithKey(key, wire) {
    const ptr0 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.openWithKey(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Order the one conversation list: pinned first, archived last, newest
 * between.
 *
 * Returns the input INDICES in display order rather than the rows themselves,
 * so the caller reorders the array it already holds instead of marshalling
 * every avatar and preview across this boundary twice to learn an order.
 *
 * The order is total: two conversations nothing has happened in yet still
 * order the same way on every call, so an unrelated refresh never reshuffles
 * the list.
 *
 * Each [`ConversationRow`] in the array is consumed by this call and must not
 * be used afterwards — build them at the call site from the rows you are
 * sorting, which is what the fresh-`lastActivityMs` reading requires anyway.
 * @param {ConversationRow[]} rows
 * @returns {Uint32Array}
 */
export function orderConversations(rows) {
    const ptr0 = passArrayJsValueToWasm0(rows, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.orderConversations(ptr0, len0);
    var v2 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Order a folder's rows: the folder's own pins first in their stored
 * order (`folderPins` are row ids; unknown ids skipped), then the
 * `orderConversations` rule with the global pin ignored. Returns input
 * indices, every row exactly once. Consumes the rows, like
 * `orderConversations`.
 * @param {ConversationRow[]} rows
 * @param {string[]} folder_pins
 * @returns {Uint32Array}
 */
export function orderConversationsInFolder(rows, folder_pins) {
    const ptr0 = passArrayJsValueToWasm0(rows, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayJsValueToWasm0(folder_pins, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.orderConversationsInFolder(ptr0, len0, ptr1, len1);
    var v3 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v3;
}

/**
 * Longest own-username envelope `SyncedState.ownUsernameEnvelope()`
 * produces (65); below `deviceGroupServiceEnvelopeMaxLen()`.
 * @returns {number}
 */
export function ownUsernameEnvelopeMaxLen() {
    const ret = wasm.ownUsernameEnvelopeMaxLen();
    return ret >>> 0;
}

/**
 * Peek a stored backup blob's container framing on the restore path, before any
 * head verification or AEAD-open (ADR-0037 §4). Returns a [`ParsedBackupBlob`]
 * carrying the framed-vs-legacy kind, the (unverified) head bytes when framed,
 * and the envelope bytes.
 *
 * # Errors
 *
 * Throws a coded `invalid: …` `JsError` when the blob carries the `0xB1`
 * container tag but is too short to hold a full head — active tamper /
 * corruption of a framed blob (ADR-0037 §6 "Invalid"). A legacy (non-`0xB1`)
 * or empty blob is **not** an error: it returns a `Legacy` [`ParsedBackupBlob`]
 * (the §6 "Absent → fail-open" lane).
 * @param {Uint8Array} blob
 * @returns {ParsedBackupBlob}
 */
export function parseBackupBlob(blob) {
    const ptr0 = passArray8ToWasm0(blob, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseBackupBlob(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ParsedBackupBlob.__wrap(ret[0]);
}

/**
 * Decode the wire bytes [`seal_backup`] produced (post-AEAD-open
 * via [`open_backup`]) into a structured `ParsedBackupPayload`.
 *
 * # Errors
 *
 * `JsError` on malformed wire bytes (wrong version, truncated,
 * trailing bytes, display-name over cap, non-UTF-8 name, …).
 * @param {Uint8Array} wire
 * @returns {ParsedBackupPayload}
 */
export function parseBackupPayload(wire) {
    const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseBackupPayload(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ParsedBackupPayload.__wrap(ret[0]);
}

/**
 * Decode an MLS Application message body into a classified
 * [`ParsedBody`] (ADR-0023/0024). Never fails — a malformed /
 * ambiguous op degrades to `"chat"`.
 * @param {Uint8Array} bytes
 * @returns {ParsedBody}
 */
export function parseBodyEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseBodyEnvelope(ptr0, len0);
    return ParsedBody.__wrap(ret);
}

/**
 * Parse a JWT into typed claims WITHOUT verifying the
 * signature. Useful for inspecting role / expiry / kid
 * client-side before presenting the JWT to the SFU.
 * @param {string} jwt
 * @returns {ParsedCallJwt}
 */
export function parseCallJwtUnverified(jwt) {
    const ptr0 = passStringToWasm0(jwt, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseCallJwtUnverified(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ParsedCallJwt.__wrap(ret[0]);
}

/**
 * Parse plaintext bytes (the `Application` payload from
 * `processIncoming`) into a typed
 * [`ParsedCallSignaling`] (Phase 3a3).
 * @param {Uint8Array} plaintext
 * @returns {ParsedCallSignaling}
 */
export function parseCallSignaling(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseCallSignaling(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ParsedCallSignaling.__wrap(ret[0]);
}

/**
 * Parse a DeviceGroup application-payload plaintext (the
 * `data` field of `ProcessedMessage::Application`) into a
 * typed [`ParsedDeviceGroupPayload`] (Phase 2a3e2).
 * @param {Uint8Array} plaintext
 * @returns {ParsedDeviceGroupPayload}
 */
export function parseDeviceGroupPayload(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseDeviceGroupPayload(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ParsedDeviceGroupPayload.__wrap(ret[0]);
}

/**
 * Decode an MLS Application message JSON envelope. Mirror of
 * `lakko_attachments::parse` — never fails; legacy / malformed
 * inputs collapse into the documented fallback paths.
 * @param {Uint8Array} bytes
 * @returns {ParsedEnvelope}
 */
export function parseEnvelope(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseEnvelope(ptr0, len0);
    return ParsedEnvelope.__wrap(ret);
}

/**
 * Read the trailer and the SFrame header of a sealed media frame without a
 * key: the KID, the counter, the clear prefix length and whether the frame
 * holds a RED container. Nothing here is authenticated until the frame
 * opens; it is for selecting a key and for diagnostics.
 *
 * # Errors
 *
 * `JsError` when the bytes are not a well-formed sealed media frame.
 * @param {Uint8Array} wire
 * @returns {MediaFrameInfo}
 */
export function parseMediaFrame(wire) {
    const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseMediaFrame(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return MediaFrameInfo.__wrap(ret[0]);
}

/**
 * The two ceilings of a `publish-budget` server frame (ADR-0068,
 * amendment of 2026-10-11) as a two-element `Uint32Array`
 * `[main_kbps, middle_kbps]`: the most this publisher's own main and
 * middle camera sender may send, in kilobits per second, each at least 1
 * and at most `MAX_PUBLISH_BUDGET_KBPS` (20000). The client sets each as
 * that sender's maximum bitrate (`maxBitrate` is in bits per second:
 * multiply by 1000). The light sender and audio are not in the frame and
 * are not limited by it. While the publisher sends a screen (ADR-0083)
 * the array has a third element, the screen sender's ceiling. Throws when
 * the JSON is not a valid `publish-budget` frame.
 * @param {string} json
 * @returns {Uint32Array}
 */
export function parseSfuPublishBudget(json) {
    const ptr0 = passStringToWasm0(json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseSfuPublishBudget(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * The `given` list of a `video-given` server frame (ADR-0070 step 6) as
 * two parallel lists flattened into one: `[peer0, quality0, peer1,
 * quality1, ...]`, each quality `"main"`, `"middle"` or `"light"`
 * (`tileVideoQualityName`), in the frame's order (the subscriber's order
 * of importance). Throws when the JSON is not a valid `video-given`
 * frame.
 * @param {string} json
 * @returns {string[]}
 */
export function parseSfuVideoGiven(json) {
    const ptr0 = passStringToWasm0(json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseSfuVideoGiven(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * The two flags of a `video-needed` server frame as a two-byte array
 * `[main, middle]`, each `1` (somebody is being sent, or is about to be
 * sent, that camera sender of this publisher: keep it encoding) or `0`
 * (nobody is: the client may stop encoding and uploading it). The light
 * sender is always needed and is not in the frame. Throws when the JSON
 * is not a valid `video-needed` frame.
 * @param {string} json
 * @returns {Uint8Array}
 */
export function parseSfuVideoNeeded(json) {
    const ptr0 = passStringToWasm0(json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.parseSfuVideoNeeded(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Open a passkey-vault blob with the same credential's PRF output.
 *
 * # Errors
 *
 * Throws when `prf_output` is not 32 bytes or the blob does not open
 * under it.
 * @param {Uint8Array} prf_output
 * @param {Uint8Array} sealed
 * @returns {RecoveryMnemonic}
 */
export function passkeyVaultOpen(prf_output, sealed) {
    const ptr0 = passArray8ToWasm0(prf_output, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(sealed, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.passkeyVaultOpen(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return RecoveryMnemonic.__wrap(ret[0]);
}

/**
 * Seal a mnemonic's entropy under a passkey's 32-byte PRF output
 * (ADR-0072 decision 6). The result is the opaque blob `PUT /vault/passkey`
 * stores.
 *
 * # Errors
 *
 * Throws when `prf_output` is not 32 bytes or the primitives refuse.
 * @param {Uint8Array} prf_output
 * @param {RecoveryMnemonic} mnemonic
 * @returns {Uint8Array}
 */
export function passkeyVaultSeal(prf_output, mnemonic) {
    const ptr0 = passArray8ToWasm0(prf_output, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    _assertClass(mnemonic, RecoveryMnemonic);
    const ret = wasm.passkeyVaultSeal(ptr0, len0, mnemonic.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * What a person's info holds — section 2.1.
 *
 * The blocks come back as their kebab-case names, which is the vocabulary
 * the web client's own section type has always been written in — so the
 * names are handed over rather than translated, and a client re-spelling
 * one is no longer possible.
 *
 * Nothing in the result is ever group-shaped, whatever the capabilities.
 * @param {PersonInfoCapabilities} caps
 * @returns {string[]}
 */
export function personInfoSections(caps) {
    _assertClass(caps, PersonInfoCapabilities);
    var ptr0 = caps.__destroy_into_raw();
    const ret = wasm.personInfoSections(ptr0);
    var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Draw three distinct 1-based word positions in `1..=12`, uniformly at
 * random, in the order drawn (not sorted).
 * @returns {Uint8Array}
 */
export function phraseCheckRandomPositions() {
    const ret = wasm.phraseCheckRandomPositions();
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Do the three typed words match the phrase at the three positions?
 * `words[i]` answers `positions[i]`.
 *
 * # Errors
 *
 * Throws an `Error` whose message is `"<code>: <reason>"` when the input
 * is malformed: `wrong-position-count`, `position-out-of-range`,
 * `repeated-position` or `wrong-word-count`. A wrong word does not throw.
 * @param {RecoveryMnemonic} mnemonic
 * @param {Uint8Array} positions
 * @param {string[]} words
 * @returns {boolean}
 */
export function phraseCheckWords(mnemonic, positions, words) {
    _assertClass(mnemonic, RecoveryMnemonic);
    const ptr0 = passArray8ToWasm0(positions, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayJsValueToWasm0(words, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.phraseCheckWords(mnemonic.__wbg_ptr, ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] !== 0;
}

/**
 * `true` when a pin action stamped `incomingUpdatedAtMs` must replace the
 * stored action **for the same message**. Strictly greater wins; equal is
 * not newer, so a re-send of the stored action is not an update.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function pinIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.pinIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * `true` when a profile stamped `incomingUpdatedAtMs` must replace the stored
 * one **from the same signature key**. Strictly greater wins; equal is not
 * newer.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function profileIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.profileIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * Length of the profile-request wire, for a cheap pre-filter.
 * @returns {number}
 */
export function profileRequestEnvelopeLen() {
    const ret = wasm.profileRequestEnvelopeLen();
    return ret >>> 0;
}

/**
 * The daily floor between two requests to one group outside a join.
 * @returns {bigint}
 */
export function profileRequestMinIntervalMs() {
    const ret = wasm.profileRequestMinIntervalMs();
    return BigInt.asUintN(64, ret);
}

/**
 * The epoch a public seed envelope names, or `undefined`.
 * @param {Uint8Array} wire
 * @returns {number | undefined}
 */
export function publicSeedEnvelopeEpoch(wire) {
    const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.publicSeedEnvelopeEpoch(ptr0, len0);
    return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
}

/**
 * Length of a public seed envelope.
 * @returns {number}
 */
export function publicSeedEnvelopeLen() {
    const ret = wasm.publicSeedEnvelopeLen();
    return ret >>> 0;
}

/**
 * ADR-0078 section 6 — the PublisherGroup id the account whose identity is
 * `ownerIdentity` derives for `channelId`
 * (`BLAKE3-derive-key("lakko/pubgrp/id/v3", owner_seed || channel_id)`).
 * @param {Identity} owner_identity
 * @param {Uint8Array} channel_id
 * @returns {Uint8Array}
 */
export function publisherGroupIdFor(owner_identity, channel_id) {
    _assertClass(owner_identity, Identity);
    const ptr0 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.publisherGroupIdFor(owner_identity.__wbg_ptr, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The canonical total order on push-routing secrets (ADR-0044):
 * `true` when the incoming envelope must supersede the stored
 * one. Epochs are `bigint`s on the JS side.
 *
 * Ordered on the pair `(mintEpoch, secretBytes)` — epoch first,
 * then the 32 secret bytes lexicographically — and **strictly
 * greater wins; equal is NOT newer**, which is what keeps a
 * benign retry of the secret a device already holds from reading
 * as an update.
 *
 * Exposed on the FFI precisely so no client reimplements the
 * comparison: three platforms each re-deriving it is the drift
 * class that produced the F-A stale-re-broadcast replay (G-36
 * residual 9). Run the ADR-0042 / ADR-0043 role gate first, then
 * route the accept/refuse decision through this function — an
 * authorised sender can still be replaying a superseded secret,
 * and only the order catches that.
 *
 * Fail-closed on a wrong-length secret on **either** side:
 * returns `false` (not newer), so a malformed incoming secret
 * never displaces a stored one and a corrupt stored value is
 * left in place rather than overwritten. Never throws;
 * infallible by construction, hence no `Result`.
 *
 * See [`lakko_contacts::push_routing::push_routing_secret_is_newer`].
 * @param {bigint} incoming_epoch
 * @param {Uint8Array} incoming_secret
 * @param {bigint} stored_epoch
 * @param {Uint8Array} stored_secret
 * @returns {boolean}
 */
export function pushRoutingSecretIsNewer(incoming_epoch, incoming_secret, stored_epoch, stored_secret) {
    const ptr0 = passArray8ToWasm0(incoming_secret, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(stored_secret, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.pushRoutingSecretIsNewer(incoming_epoch, ptr0, len0, stored_epoch, ptr1, len1);
    return ret !== 0;
}

/**
 * Length of a subscription id.
 * @returns {number}
 */
export function pushSubscriptionIdLen() {
    const ret = wasm.pushSubscriptionIdLen();
    return ret >>> 0;
}

/**
 * The name of the header a sealed wake rides in.
 * @returns {string}
 */
export function pushWakeHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.pushWakeHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The `x-lakko-wake` header value: the wake for the group whose routing
 * secret is `secret`, sealed to push-gw's 32-byte X25519 key, as base64url
 * without padding. `timestamp_ms` is this device's clock.
 *
 * # Errors
 *
 * `keyLength`.
 * @param {Uint8Array} push_gw_key
 * @param {Uint8Array} secret
 * @param {PushWakeKind} kind
 * @param {number} timestamp_ms
 * @returns {string}
 */
export function pushWakeHeaderValue(push_gw_key, secret, kind, timestamp_ms) {
    let deferred4_0;
    let deferred4_1;
    try {
        const ptr0 = passArray8ToWasm0(push_gw_key, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.pushWakeHeaderValue(ptr0, len0, ptr1, len1, kind, timestamp_ms);
        var ptr3 = ret[0];
        var len3 = ret[1];
        if (ret[3]) {
            ptr3 = 0; len3 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred4_0 = ptr3;
        deferred4_1 = len3;
        return getStringFromWasm0(ptr3, len3);
    } finally {
        wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
    }
}

/**
 * The quick reaction row for a message.
 * @param {string | null | undefined} quick
 * @param {string[]} recent
 * @param {AllowedReactions} allowed
 * @param {string[]} message_kinds
 * @returns {string[]}
 */
export function quickReactionRow(quick, recent, allowed, message_kinds) {
    var ptr0 = isLikeNone(quick) ? 0 : passStringToWasm0(quick, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayJsValueToWasm0(recent, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    _assertClass(allowed, AllowedReactions);
    const ptr2 = passArrayJsValueToWasm0(message_kinds, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.quickReactionRow(ptr0, len0, ptr1, len1, allowed.__wbg_ptr, ptr2, len2);
    var v4 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v4;
}

/**
 * The quick reaction row's defaults.
 * @returns {string[]}
 */
export function quickReactions() {
    const ret = wasm.quickReactions();
    var v1 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v1;
}

/**
 * How many emoji the quick row holds.
 * @returns {number}
 */
export function quickRowLen() {
    const ret = wasm.quickRowLen();
    return ret >>> 0;
}

/**
 * Rank the candidates under the rule at `nowMs` (BigInt). Returns entries
 * in display order, replies folded under their parent.
 * @param {FeedRule} rule
 * @param {FeedConversation[]} conversations
 * @param {FeedCandidate[]} candidates
 * @param {bigint} now_ms
 * @returns {FeedEntry[]}
 */
export function rankFeed(rule, conversations, candidates, now_ms) {
    _assertClass(rule, FeedRule);
    const ptr0 = passArrayJsValueToWasm0(conversations, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayJsValueToWasm0(candidates, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.rankFeed(rule.__wbg_ptr, ptr0, len0, ptr1, len1, now_ms);
    var v3 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v3;
}

/**
 * Wire length of a reach grant: 134.
 * @returns {number}
 */
export function reachGrantLen() {
    const ret = wasm.reachGrantLen();
    return ret >>> 0;
}

/**
 * Derive the 32-byte reach seed for `(class, scopeId, version)` from the
 * 32-byte account seed. Every device of the account derives the same seed.
 *
 * # Errors
 *
 * Throws with `reachError` `seedLength`, `scopeIdTooLong`,
 * `scopeIdNotEmpty` or `scopeIdEmpty`.
 * @param {Uint8Array} account_seed
 * @param {ReachClass} _class
 * @param {Uint8Array} scope_id
 * @param {number} version
 * @returns {Uint8Array}
 */
export function reachSeed(account_seed, _class, scope_id, version) {
    const ptr0 = passArray8ToWasm0(account_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(scope_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.reachSeed(ptr0, len0, _class, ptr1, len1, version);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Length of an account seed, a reach seed and a reach public key: 32.
 * @returns {number}
 */
export function reachSeedLen() {
    const ret = wasm.reachSeedLen();
    return ret >>> 0;
}

/**
 * `true` when a reach state stamped `incomingUpdatedAtMs` must replace the
 * stored one. Strictly greater wins.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function reachStateIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.reachStateIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * The version of `(class, scopeId)` in `state`, or `undefined`.
 * @param {ReachState} state
 * @param {ReachClass} _class
 * @param {Uint8Array} scope_id
 * @returns {number | undefined}
 */
export function reachStateVersionOf(state, _class, scope_id) {
    _assertClass(state, ReachState);
    const ptr0 = passArray8ToWasm0(scope_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.reachStateVersionOf(state.__wbg_ptr, _class, ptr0, len0);
    return ret === Number.MAX_SAFE_INTEGER ? undefined : ret;
}

/**
 * The 32-byte Ed25519 public key of a reach seed.
 *
 * # Errors
 *
 * Throws with `reachError` `seedLength`.
 * @param {Uint8Array} seed
 * @returns {Uint8Array}
 */
export function reachVerifyingKey(seed) {
    const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.reachVerifyingKey(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The chips to draw under a message; `conversation` is `"one-to-one"`,
 * `"group"` or `"channel"`.
 *
 * # Errors
 *
 * Throws `reaction.badConversation`.
 * @param {ChipSource[]} sources
 * @param {string} conversation
 * @param {AllowedReactions} allowed
 * @returns {ChipRow}
 */
export function reactionChips(sources, conversation, allowed) {
    const ptr0 = passArrayJsValueToWasm0(sources, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(conversation, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    _assertClass(allowed, AllowedReactions);
    const ret = wasm.reactionChips(ptr0, len0, ptr1, len1, allowed.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ChipRow.__wrap(ret[0]);
}

/**
 * Whether a reaction `emoji` is shown under `allowed`.
 * @param {string} emoji
 * @param {AllowedReactions} allowed
 * @returns {boolean}
 */
export function reactionShown(emoji, allowed) {
    const ptr0 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    _assertClass(allowed, AllowedReactions);
    const ret = wasm.reactionShown(ptr0, len0, allowed.__wbg_ptr);
    return ret !== 0;
}

/**
 * What tapping `emoji` does: `"add"`, `"remove"`, `"not-allowed"`,
 * `"cap-reached"` or `"kind-limit"`.
 * @param {string} emoji
 * @param {string[]} own
 * @param {AllowedReactions} allowed
 * @param {string[]} message_kinds
 * @returns {string}
 */
export function reactionTapVerdict(emoji, own, allowed, message_kinds) {
    let deferred4_0;
    let deferred4_1;
    try {
        const ptr0 = passStringToWasm0(emoji, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArrayJsValueToWasm0(own, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        _assertClass(allowed, AllowedReactions);
        const ptr2 = passArrayJsValueToWasm0(message_kinds, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.reactionTapVerdict(ptr0, len0, ptr1, len1, allowed.__wbg_ptr, ptr2, len2);
        deferred4_0 = ret[0];
        deferred4_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
    }
}

/**
 * Whether a tag is one of the person's reading languages.
 * @param {string[]} reading_languages
 * @param {string} tag
 * @returns {boolean}
 */
export function readsLanguage(reading_languages, tag) {
    const ptr0 = passArrayJsValueToWasm0(reading_languages, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(tag, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.readsLanguage(ptr0, len0, ptr1, len1);
    return ret !== 0;
}

/**
 * The canonical body of a kind-5 `recall-start` statement, for
 * `signAccountStatementAt`: `anchor_seq(8 BE) ‖ BLAKE3(login_request)`.
 *
 * # Errors
 *
 * Throws `bad-base64`.
 * @param {bigint} anchor_seq
 * @param {string} login_request_b64
 * @returns {Uint8Array}
 */
export function recallStartStatementBody(anchor_seq, login_request_b64) {
    const ptr0 = passStringToWasm0(login_request_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.recallStartStatementBody(anchor_seq, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * The canonical body of a kind-2 `recall` statement, for
 * `signAccountStatementAt`: `server_nonce ‖ new_signature_pubkey ‖
 * BLAKE3(password_upload) ‖ BLAKE3(recovery_upload)`.
 *
 * # Errors
 *
 * Throws `bad-server-nonce-length`, `bad-pubkey-length`, `bad-base64`.
 * @param {Uint8Array} server_nonce
 * @param {Uint8Array} new_signature_pubkey
 * @param {string} password_upload_b64
 * @param {string} recovery_upload_b64
 * @returns {Uint8Array}
 */
export function recallStatementBody(server_nonce, new_signature_pubkey, password_upload_b64, recovery_upload_b64) {
    const ptr0 = passArray8ToWasm0(server_nonce, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(new_signature_pubkey, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(password_upload_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passStringToWasm0(recovery_upload_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.recallStatementBody(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * Recommend a new encoder bitrate given the current one +
 * a fresh quality sample. Returns the current value
 * unchanged when the sample falls in the "hold" band.
 * @param {number} current_bps
 * @param {number} rtt_ms
 * @param {number} jitter_ms
 * @param {number} loss_pct
 * @param {string} codec
 * @returns {number}
 */
export function recommendCallBitrate(current_bps, rtt_ms, jitter_ms, loss_pct, codec) {
    const ptr0 = passStringToWasm0(codec, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.recommendCallBitrate(current_bps, rtt_ms, jitter_ms, loss_pct, ptr0, len0);
    return ret >>> 0;
}

/**
 * Longest recovery-email envelope `SyncedState.recoveryEmailEnvelope()`
 * produces (293); below `deviceGroupServiceEnvelopeMaxLen()`.
 * @returns {number}
 */
export function recoveryEmailEnvelopeMaxLen() {
    const ret = wasm.recoveryEmailEnvelopeMaxLen();
    return ret >>> 0;
}

/**
 * The primary (current) frame of an RFC 2198 RED container, which a
 * receiver whose browser does not decode RED hands to its Opus decoder in
 * place of the container. Call it on an OPENED frame whose `MediaFrameInfo`
 * said `isRedOpus`. `undefined` when the bytes are not a container.
 * @param {Uint8Array} container
 * @returns {Uint8Array | undefined}
 */
export function redPrimary(container) {
    const ptr0 = passArray8ToWasm0(container, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.redPrimary(ptr0, len0);
    let v2;
    if (ret[0] !== 0) {
        v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * The one well-formed registrar-seed envelope length in bytes.
 * @returns {number}
 */
export function registrarSeedEnvelopeLen() {
    const ret = wasm.registrarSeedEnvelopeLen();
    return ret >>> 0;
}

/**
 * Decide what a client on `currentRelease` should do, given the `latestRelease`
 * / `minSupportedRelease` numbers of a *verified* manifest. A PURE function — no
 * I/O, no clock, no allocation, no failure — so the web client need not pass a
 * manifest object back across the boundary: it reads the two numbers off a prior
 * [`verify_release_manifest`] and calls this with its own current release. All
 * three arguments are BigInt on the JS side.
 *
 * `minSupportedRelease` takes precedence: a client below it gets
 * [`UpdateStatus::UpdateRequired`] even when it is also below `latestRelease`.
 * @param {bigint} current_release
 * @param {bigint} latest_release
 * @param {bigint} min_supported_release
 * @returns {UpdateStatus}
 */
export function releaseUpdateStatus(current_release, latest_release, min_supported_release) {
    const ret = wasm.releaseUpdateStatus(current_release, latest_release, min_supported_release);
    return ret;
}

/**
 * How much of the ring window is left, in milliseconds, for an affordance
 * that needs a deadline rather than a verdict. Saturates at zero.
 * @param {bigint} offer_age_ms
 * @returns {bigint}
 */
export function remainingRingMs(offer_age_ms) {
    const ret = wasm.remainingRingMs(offer_age_ms);
    return BigInt.asUintN(64, ret);
}

/**
 * G-13 (ADR-0015): the action hash a remove-device passkey ceremony and its
 * attestation bind to — `SHA-256("lakko/action/remove-device/v1" ‖
 * removed_leaf_signature_pubkey)`. The web client computes this from the
 * leaf it is about to remove to drive the `/auth/action/{start,finish}`
 * ceremony, so it matches what a peer recomputes when verifying.
 * @param {Uint8Array} removed_signature_pubkey
 * @returns {Uint8Array}
 */
export function removeDeviceActionHash(removed_signature_pubkey) {
    const ptr0 = passArray8ToWasm0(removed_signature_pubkey, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.removeDeviceActionHash(ptr0, len0);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Whether an arriving offer rings, given the conversation's mute flag and
 * how old the offer is.
 *
 * `offerAgeMs` is measured from the delivery service's timestamp on the row,
 * never from a local clock: the two devices in a call share no clock, and
 * the window only works if both read it the same way.
 * @param {boolean} muted
 * @param {bigint} offer_age_ms
 * @returns {RingDecision}
 */
export function ringDecision(muted, offer_age_ms) {
    const ret = wasm.ringDecision(muted, offer_age_ms);
    return ret;
}

/**
 * Whether the outgoing tone loops (`docs/ux/calls.md` section 3.4).
 *
 * Only while a one-to-one call this device placed waits for an answer. A
 * group call, or a call joined from a link, rings nobody.
 * @param {boolean} group_call
 * @param {CallBarPhase} phase
 * @returns {boolean}
 */
export function ringbackPlays(group_call, phase) {
    const ret = wasm.ringbackPlays(group_call, phase);
    return ret !== 0;
}

/**
 * Whether the quick row ends with the control that opens the picker.
 * @param {AllowedReactions} allowed
 * @returns {boolean}
 */
export function rowHasExpand(allowed) {
    _assertClass(allowed, AllowedReactions);
    const ret = wasm.rowHasExpand(allowed.__wbg_ptr);
    return ret !== 0;
}

/**
 * Whether a reader of one tag reads the other.
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
export function sameLanguage(a, b) {
    const ptr0 = passStringToWasm0(a, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(b, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.sameLanguage(ptr0, len0, ptr1, len1);
    return ret !== 0;
}

/**
 * How many saved messages a client lets one account hold: refuse a new
 * Save once `SyncedState.saved()` lists this many. A client-side limit,
 * chosen; NOT a decode bound (two devices that each saved while offline
 * can exceed it together, and that state still decodes and folds).
 * @returns {number}
 */
export function savedMaxClient() {
    const ret = wasm.savedMaxClient();
    return ret >>> 0;
}

/**
 * How many saved registers a client puts in one change set. A larger
 * selection (Select mode's Save) goes out as several change sets, so one
 * DeviceGroup message stays well under what the Delivery Service accepts.
 * A client-side sending rule, chosen; NOT a decode bound.
 * @returns {number}
 */
export function savedMaxPerChangeSet() {
    const ret = wasm.savedMaxPerChangeSet();
    return ret >>> 0;
}

/**
 * The stream-id suffix a share's tracks travel under (`~screen`).
 * @returns {string}
 */
export function screenStreamSuffix() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.screenStreamSuffix();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Encrypt `payload_bytes` under a KEK derived from `phrase`
 * (BIP39 recovery phrase, UTF-8 bytes). Returns the on-the-wire
 * envelope ready to PUT to `services/storage`'s `/backup`
 * endpoint.
 *
 * `phrase` should be the canonical-form recovery phrase
 * (normalised whitespace + lowercase); this function treats the
 * slice as opaque IKM into Argon2id.
 *
 * # Errors
 *
 * `JsError` on Argon2id parameter failure (cannot happen for
 * the fixed-params API) or oversize payload (unreachable in
 * practice — the wire field is u32be).
 * @param {Uint8Array} phrase
 * @param {Uint8Array} payload_bytes
 * @returns {Uint8Array}
 */
export function sealBackup(phrase, payload_bytes) {
    const ptr0 = passArray8ToWasm0(phrase, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(payload_bytes, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.sealBackup(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Seal a comment by `accountIdentity` under the post at `postCursor`
 * (sealed in `epochId`, whose seed is `epochSeed`) for the comment write
 * key `writerKey` (`ChannelCommentWriteKey.publicKey`).
 *
 * # Errors
 *
 * Throws `comment.<code>: ...`.
 * @param {Identity} account_identity
 * @param {Uint8Array} epoch_seed
 * @param {Uint8Array} channel_id
 * @param {number} epoch_id
 * @param {bigint} post_cursor
 * @param {Uint8Array} writer_key
 * @param {string} name
 * @param {AvatarRef | null | undefined} avatar
 * @param {string} text
 * @param {bigint | null} [reply_to]
 * @returns {Uint8Array}
 */
export function sealChannelComment(account_identity, epoch_seed, channel_id, epoch_id, post_cursor, writer_key, name, avatar, text, reply_to) {
    _assertClass(account_identity, Identity);
    const ptr0 = passArray8ToWasm0(epoch_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(writer_key, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len3 = WASM_VECTOR_LEN;
    let ptr4 = 0;
    if (!isLikeNone(avatar)) {
        _assertClass(avatar, AvatarRef);
        ptr4 = avatar.__destroy_into_raw();
    }
    const ptr5 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len5 = WASM_VECTOR_LEN;
    const ret = wasm.sealChannelComment(account_identity.__wbg_ptr, ptr0, len0, ptr1, len1, epoch_id, post_cursor, ptr2, len2, ptr3, len3, ptr4, ptr5, len5, !isLikeNone(reply_to), isLikeNone(reply_to) ? BigInt(0) : reply_to);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v7 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v7;
}

/**
 * AEAD-seal a [`lakko_proto::mls_app::history_bundle::v1::HistoryBundle`]
 * (Phase 2a3d8).
 *
 * `secret` MUST be 32 bytes. `bundleBytes` is the prost-encoded
 * `HistoryBundle` proto bytes the caller built from its local
 * message store. Returns the wire-format `SealedBundle` proto
 * bytes ready to PUT to the rendezvous mailbox.
 *
 * Refuses to seal bundles whose total message count exceeds
 * `lakko_mls::history_bundle::MAX_BUNDLE_MESSAGES` (1000).
 * @param {Uint8Array} secret
 * @param {Uint8Array} bundle_bytes
 * @returns {Uint8Array}
 */
export function sealHistoryBundle(secret, bundle_bytes) {
    const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(bundle_bytes, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.sealHistoryBundle(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * AEAD-seal a structured history bundle (Phase 2a3d8-ui).
 *
 * `secret` MUST be 32 bytes (typically the output of
 * [`DeviceGroup::historyBundleSecret`]). `groups` is an
 * `Array<HistoryGroupInput>`.
 * @param {Uint8Array} secret
 * @param {HistoryGroupInput[]} groups
 * @param {bigint} max_hlc
 * @returns {Uint8Array}
 */
export function sealHistoryBundleStructured(secret, groups, max_hlc) {
    const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayJsValueToWasm0(groups, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.sealHistoryBundleStructured(ptr0, len0, ptr1, len1, max_hlc);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Seal a request to join as a knock row for the group whose knock key of
 * `version` is `knockPublicKey`. `profile` is the joiner's composed profile
 * envelope (empty for none).
 *
 * # Errors
 *
 * Throws `knock.<code>: ...`.
 * @param {Uint8Array} knock_public_key
 * @param {number} version
 * @param {Uint8Array} key_package
 * @param {ReachGrant} reply_grant
 * @param {Uint8Array} profile
 * @param {Uint8Array | null} [capability]
 * @returns {Uint8Array}
 */
export function sealKnockRow(knock_public_key, version, key_package, reply_grant, profile, capability) {
    const ptr0 = passArray8ToWasm0(knock_public_key, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(key_package, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    _assertClass(reply_grant, ReachGrant);
    const ptr2 = passArray8ToWasm0(profile, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    var ptr3 = isLikeNone(capability) ? 0 : passArray8ToWasm0(capability, wasm.__wbindgen_malloc);
    var len3 = WASM_VECTOR_LEN;
    const ret = wasm.sealKnockRow(ptr0, len0, version, ptr1, len1, reply_grant.__wbg_ptr, ptr2, len2, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * Seal `body` as one mailbox row of `kind` to the 32-byte `mailboxPk`,
 * which must come from `verifyMailboxBinding` (or
 * `ReachGrant.mailboxPkVerified`). Post the row to
 * `/ds/mailbox/{mailboxAddress(mailboxPk)}/messages`.
 *
 * # Errors
 *
 * Throws with `mailboxError` `keyLength`, `badKey`, `bodyTooLong`.
 * @param {Uint8Array} mailbox_pk
 * @param {MailboxKind} kind
 * @param {Uint8Array} body
 * @returns {Uint8Array}
 */
export function sealMailboxRow(mailbox_pk, kind, body) {
    const ptr0 = passArray8ToWasm0(mailbox_pk, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(body, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.sealMailboxRow(ptr0, len0, kind, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * `verifyMailboxBinding` then `sealMailboxRow` in one call: the form a
 * writer should use, since it cannot seal without the check.
 *
 * # Errors
 *
 * Every throw of the two.
 * @param {Uint8Array} binding
 * @param {Uint8Array} account_pk
 * @param {MailboxKind} kind
 * @param {Uint8Array} body
 * @returns {Uint8Array}
 */
export function sealMailboxRowToBinding(binding, account_pk, kind, body) {
    const ptr0 = passArray8ToWasm0(binding, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(account_pk, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(body, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.sealMailboxRowToBinding(ptr0, len0, ptr1, len1, kind, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Seal `profile` (a profile envelope, ADR-0048 v3) into a member card for
 * `subscriberPk` in `channelId` under the 32-byte seed of `epochId`, bound
 * to the account whose 32-byte Ed25519 identity seed is
 * `accountIdentitySeed` (ADR-0060: the card names the account, signed).
 *
 * Throws on a wrong-length seed, channel id, subscriber key or identity
 * seed, or a profile over `maxMemberCardProfileLen()` bytes.
 * @param {Uint8Array} seed
 * @param {number} epoch_id
 * @param {Uint8Array} channel_id
 * @param {Uint8Array} subscriber_pk
 * @param {Uint8Array} account_identity_seed
 * @param {Uint8Array} profile
 * @returns {Uint8Array}
 */
export function sealMemberCard(seed, epoch_id, channel_id, subscriber_pk, account_identity_seed, profile) {
    const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(subscriber_pk, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(account_identity_seed, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray8ToWasm0(profile, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    const ret = wasm.sealMemberCard(ptr0, len0, epoch_id, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v6 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v6;
}

/**
 * Seal the 32-byte seed of `epochId` under the read key.
 *
 * # Errors
 *
 * Throws `publicSeed.<code>: ...`.
 * @param {Uint8Array} read_key
 * @param {Uint8Array} channel_id
 * @param {number} epoch_id
 * @param {Uint8Array} epoch_seed
 * @returns {Uint8Array}
 */
export function sealPublicSeedEnvelope(read_key, channel_id, epoch_id, epoch_seed) {
    const ptr0 = passArray8ToWasm0(read_key, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(epoch_seed, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.sealPublicSeedEnvelope(ptr0, len0, ptr1, len1, epoch_id, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * This device's provider tokens sealed as the subscription row `sub_id` of
 * the group whose routing secret is `secret`.
 *
 * # Errors
 *
 * `keyLength`, `subIdLength`, `badTokens`.
 * @param {Uint8Array} secret
 * @param {Uint8Array} sub_id
 * @param {PushTokenList} tokens
 * @returns {Uint8Array}
 */
export function sealPushSubscription(secret, sub_id, tokens) {
    const ptr0 = passArray8ToWasm0(secret, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(sub_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    _assertClass(tokens, PushTokenList);
    const ret = wasm.sealPushSubscription(ptr0, len0, ptr1, len1, tokens.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Seal a 32-byte content-key seed against a subscriber's
 * Ed25519 public key (sealed to its X25519 image). Returns
 * the wire bytes ready to
 * `POST /v1/channels/:id/subscribers/:pk/envelope`.
 *
 * Always returns exactly [`channel_seed_envelope_len`]
 * bytes (`eph_pub(32) || sealed_seed(32) || tag(16) = 80`).
 *
 * Throws on wrong-length args / a public key that is not a
 * valid Ed25519 point or is of small order / AEAD seal
 * failure.
 * @param {Uint8Array} subscriber_pub
 * @param {Uint8Array} channel_id
 * @param {number} epoch_id
 * @param {Uint8Array} seed
 * @returns {Uint8Array}
 */
export function sealSubscriberSeedEnvelope(subscriber_pub, channel_id, epoch_id, seed) {
    const ptr0 = passArray8ToWasm0(subscriber_pub, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.sealSubscriberSeedEnvelope(ptr0, len0, ptr1, len1, epoch_id, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Seal `plaintext` under a raw 32-byte AEAD `key` (ADR-0018 Part 2a, factor
 * (a) — "another-device" identity transfer, Path B). Returns the self-
 * describing wire blob `nonce(12) ‖ ciphertext+tag` (ChaCha20-Poly1305, empty
 * AAD; the nonce is generated internally and prepended). The existing device
 * derives `key` via `deviceGroup.exportSecret(...)` and seals
 * `(grant_token ‖ phrase_seed)` here, then publishes the blob to the
 * rendezvous mailbox; the fresh device derives the same `key` and opens it
 * via [`open_with_key`]. Mirror of the UniFFI `seal_with_key`.
 * @param {Uint8Array} key
 * @param {Uint8Array} plaintext
 * @returns {Uint8Array}
 */
export function sealWithKey(key, plaintext) {
    const ptr0 = passArray8ToWasm0(key, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.sealWithKey(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * The canonical body of a kind-4 `session-upgrade` statement, for
 * `signAccountStatementAt`: `BLAKE3(refreshToken)`.
 * @param {string} refresh_token
 * @returns {Uint8Array}
 */
export function sessionUpgradeStatementBody(refresh_token) {
    const ptr0 = passStringToWasm0(refresh_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.sessionUpgradeStatementBody(ptr0, len0);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Choose or unchoose one contact in the chosen list.
 *
 * # Errors
 *
 * Throws an `Error` with `visibilityError` `contactIdLength` /
 * `tooManyChosenContacts`.
 * @param {VisibilitySetting} setting
 * @param {Uint8Array} account_id
 * @param {boolean} on
 * @returns {VisibilitySetting}
 */
export function setContactChosen(setting, account_id, on) {
    _assertClass(setting, VisibilitySetting);
    const ptr0 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.setContactChosen(setting.__wbg_ptr, ptr0, len0, on);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VisibilitySetting.__wrap(ret[0]);
}

/**
 * All my contacts, or only the chosen ones. No effect unless part 2.1 is
 * on.
 * @param {VisibilitySetting} setting
 * @param {ContactsForm} form
 * @returns {VisibilitySetting}
 */
export function setContactsForm(setting, form) {
    _assertClass(setting, VisibilitySetting);
    const ret = wasm.setContactsForm(setting.__wbg_ptr, form);
    return VisibilitySetting.__wrap(ret);
}

/**
 * Switch part 2.1 (contacts of my contacts). Turning the last part off
 * moves to Nobody; turning a part on from Nobody or Everyone moves to Some
 * people.
 * @param {VisibilitySetting} setting
 * @param {boolean} on
 * @returns {VisibilitySetting}
 */
export function setContactsOfContacts(setting, on) {
    _assertClass(setting, VisibilitySetting);
    const ret = wasm.setContactsOfContacts(setting.__wbg_ptr, on);
    return VisibilitySetting.__wrap(ret);
}

/**
 * All groups and channels, or only the chosen ones. No effect unless part
 * 2.2 is on.
 * @param {VisibilitySetting} setting
 * @param {GroupsForm} form
 * @returns {VisibilitySetting}
 */
export function setGroupsForm(setting, form) {
    _assertClass(setting, VisibilitySetting);
    const ret = wasm.setGroupsForm(setting.__wbg_ptr, form);
    return VisibilitySetting.__wrap(ret);
}

/**
 * Switch part 2.2 (groups and channels), keeping its form.
 * @param {VisibilitySetting} setting
 * @param {boolean} on
 * @returns {VisibilitySetting}
 */
export function setGroupsVisible(setting, on) {
    _assertClass(setting, VisibilitySetting);
    const ret = wasm.setGroupsVisible(setting.__wbg_ptr, on);
    return VisibilitySetting.__wrap(ret);
}

/**
 * Choose or unchoose one group or channel in the chosen list.
 *
 * # Errors
 *
 * Throws an `Error` with `visibilityError` `scopeIdLength` /
 * `tooManyChosen`.
 * @param {VisibilitySetting} setting
 * @param {ScopeId} scope
 * @param {boolean} on
 * @returns {VisibilitySetting}
 */
export function setScopeChosen(setting, scope, on) {
    _assertClass(setting, VisibilitySetting);
    _assertClass(scope, ScopeId);
    const ret = wasm.setScopeChosen(setting.__wbg_ptr, scope.__wbg_ptr, on);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VisibilitySetting.__wrap(ret[0]);
}

/**
 * Derive the per-call SFrame seed from a conversation MLS
 * group's exporter (Phase 3a4).
 * @param {Group} group
 * @param {Identity} identity
 * @param {Uint8Array} call_id
 * @returns {Uint8Array}
 */
export function sframeDeriveCallKey(group, identity, call_id) {
    _assertClass(group, Group);
    _assertClass(identity, Identity);
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.sframeDeriveCallKey(group.__wbg_ptr, identity.__wbg_ptr, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Derive a per-participant SFrame key + nonce salt for a given
 * `(sender_leaf, stream_index)` stream (ADR-0035). The seal-side derivation:
 * each of a leaf's concurrent streams (audio, camera, screen-share, …) gets a
 * distinct key + nonce salt keyed on the composite SFrame KID
 * `(sender_leaf << 8) | stream_index`, so their independent counters cannot
 * reuse a `(key, nonce)` pair — the guarantee the ADR-0034 per-media-TYPE tag
 * could not give two same-kind streams. `stream_index` crosses as a `u32`; a
 * value `> 255` fails closed rather than silently truncating.
 * @param {Uint8Array} call_key_seed
 * @param {number} sender_leaf
 * @param {number} stream_index
 * @returns {SframeParticipantKey}
 */
export function sframeDeriveParticipantKey(call_key_seed, sender_leaf, stream_index) {
    const ptr0 = passArray8ToWasm0(call_key_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.sframeDeriveParticipantKey(ptr0, len0, sender_leaf, stream_index);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SframeParticipantKey.__wrap(ret[0]);
}

/**
 * Open an SFrame frame with the Phase 3c5 overlap window.
 * Tries `currentCallKeySeed` first; on AEAD rejection
 * falls back to `previousCallKeySeed` if not `null`
 * (caller passes `null` outside the 1-second overlap
 * window). Returns the plaintext on success.
 *
 * Seed-flat shape — platform code holds the 32-byte seeds
 * in its own non-extractable WebCrypto store and ferries
 * them in per-call. `kid` is the composite SFrame key identifier
 * `(sender_leaf << 8) | stream_index` (ADR-0035) the receiver read from the
 * frame's header (crossing to JS as a `BigInt`); the decryptor re-derives the
 * exact sender-stream key from the KID with no separate media discriminator.
 * `frame_counter` is the 64-bit counter from the header (ADR-0069), also a
 * `BigInt` in JS.
 * @param {Uint8Array} current_call_key_seed
 * @param {Uint8Array | null | undefined} previous_call_key_seed
 * @param {bigint} kid
 * @param {Uint8Array} aad
 * @param {Uint8Array} ciphertext
 * @param {bigint} frame_counter
 * @returns {Uint8Array}
 */
export function sframeOpenWithOverlap(current_call_key_seed, previous_call_key_seed, kid, aad, ciphertext, frame_counter) {
    const ptr0 = passArray8ToWasm0(current_call_key_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    var ptr1 = isLikeNone(previous_call_key_seed) ? 0 : passArray8ToWasm0(previous_call_key_seed, wasm.__wbindgen_malloc);
    var len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(aad, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(ciphertext, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.sframeOpenWithOverlap(ptr0, len0, ptr1, len1, kid, ptr2, len2, ptr3, len3, frame_counter);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * SFrame stream index of a screen share's picture (ADR-0083).
 * @returns {number}
 */
export function sframeStreamScreen() {
    const ret = wasm.sframeStreamScreen();
    return ret;
}

/**
 * SFrame stream index of a screen share's sound (ADR-0083).
 * @returns {number}
 */
export function sframeStreamScreenAudio() {
    const ret = wasm.sframeStreamScreenAudio();
    return ret;
}

/**
 * Base64url-decode a relay payload from a `SignalRelay`.
 * @param {string} payload_b64
 * @returns {Uint8Array}
 */
export function sfuDecodePayload(payload_b64) {
    const ptr0 = passStringToWasm0(payload_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.sfuDecodePayload(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Base64url-encode an opaque relay payload for a `SignalRelay`.
 * @param {Uint8Array} payload
 * @returns {string}
 */
export function sfuEncodePayload(payload) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ptr0 = passArray8ToWasm0(payload, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.sfuEncodePayload(ptr0, len0);
        deferred2_0 = ret[0];
        deferred2_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * The SFU room of a call: 64 lower-case hex characters,
 * `BLAKE3_derive_key("lakko/sfu/room/v1", call_id)`.
 *
 * # Errors
 *
 * `callIdLength` when `callId` is not 16 bytes.
 * @param {Uint8Array} call_id
 * @returns {string}
 */
export function sfuRoomForCall(call_id) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.sfuRoomForCall(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * `true` when a list stamped `incomingUpdatedAtMs` must replace the list
 * stored **for the same member**. Strictly greater; equal is not newer.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function sharedChannelsIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.sharedChannelsIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * The send-side throttle: should this device emit a beacon now (ADR-0045)?
 *
 * 1. **Only candidates emit** — `ownRank === undefined` is `false`. A plain
 *    member's beacon can never make it the acting committer (the fold drops
 *    it), so emitting one is pure journal litter and a pure privacy leak.
 * 2. **The first beacon of a session is immediate** — `lastEmittedAtMs ===
 *    undefined` is `true`, so a candidate that has just opened the app
 *    announces itself at once rather than staying invisible for a whole
 *    interval, which is exactly when a stale view is likeliest to hand the
 *    duty elsewhere.
 * 3. Otherwise, one per [`committer_beacon_interval_ms`].
 *
 * Both timestamps come from this device's **own local** clock, which is
 * legitimate here and nowhere else in the beacon design: it governs only this
 * device's send cadence and feeds no judgement about anybody else. A backward
 * clock jump suppresses one beacon and self-heals on the next tick.
 *
 * **Foregroundedness is a caller obligation this function cannot check.** See
 * [`compose_committer_beacon`].
 * @param {number | null | undefined} own_rank
 * @param {bigint | null | undefined} last_emitted_at_ms
 * @param {bigint} now_ms
 * @returns {boolean}
 */
export function shouldEmitBeacon(own_rank, last_emitted_at_ms, now_ms) {
    const ret = wasm.shouldEmitBeacon(isLikeNone(own_rank) ? Number.MAX_SAFE_INTEGER : (own_rank) >>> 0, !isLikeNone(last_emitted_at_ms), isLikeNone(last_emitted_at_ms) ? BigInt(0) : last_emitted_at_ms, now_ms);
    return ret !== 0;
}

/**
 * Should this device ask the group to introduce itself now? Always on a
 * join; otherwise only with an unnamed member and no sooner than
 * `profileRequestMinIntervalMs` after the last request (`undefined` =
 * never asked).
 * @param {boolean} joined_just_now
 * @param {number} unnamed_members
 * @param {bigint | null | undefined} last_requested_at_ms
 * @param {bigint} now_ms
 * @returns {boolean}
 */
export function shouldRequestProfiles(joined_just_now, unnamed_members, last_requested_at_ms, now_ms) {
    const ret = wasm.shouldRequestProfiles(joined_just_now, unnamed_members, !isLikeNone(last_requested_at_ms), isLikeNone(last_requested_at_ms) ? BigInt(0) : last_requested_at_ms, now_ms);
    return ret !== 0;
}

/**
 * Whether this device publishes the catch-up snapshot after the
 * DeviceGroup grew (ADR-0058 section 5.1): only the lowest leaf among the
 * members BEFORE the growth. Wait a random delay of up to 30 seconds first.
 * @param {number} own_leaf
 * @param {Uint32Array} prior_member_leaves
 * @returns {boolean}
 */
export function shouldSendSnapshot(own_leaf, prior_member_leaves) {
    const ptr0 = passArray32ToWasm0(prior_member_leaves, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.shouldSendSnapshot(own_leaf, ptr0, len0);
    return ret !== 0;
}

/**
 * Sign an account statement of `kind` (1 recall-status, 2 recall,
 * 3 device-revoke, 4 session-upgrade, 5 recall-start) for the 16-byte `accountId` over
 * `body` (the kind's canonical body) with the account identity's 32-byte
 * Ed25519 seed (`identity.ed25519Seed`). Returns the 138-byte wire form; the
 * caller base64url-encodes it (no padding) for a `statement_b64` body field
 * or the header `accountStatementHeaderName()` names.
 *
 * # Errors
 *
 * Throws `"<code>: <reason>"`: `bad-seed-length`, `unknown-kind`,
 * `bad-account-id-length`.
 * @param {Uint8Array} account_seed
 * @param {number} kind
 * @param {Uint8Array} account_id
 * @param {Uint8Array} body
 * @returns {Uint8Array}
 */
export function signAccountStatement(account_seed, kind, account_id, body) {
    const ptr0 = passArray8ToWasm0(account_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(body, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.signAccountStatement(ptr0, len0, kind, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * `signAccountStatement` with the signing time (`unixMs`, a `bigint`) given
 * by the caller.
 *
 * For the one case the device's own clock is known to be wrong: a route
 * answered `401 {"error":"statement-stale","server_time_ms":T}`. That
 * answer is given only to a statement whose signature verified, so the
 * device holds the right key and only its time is off; it signs the same
 * claims again with `unixMs = T` (plus the milliseconds that passed since it
 * read the answer) and repeats the request. `body` is the kind's canonical
 * body: for the kinds whose body hashes a request field, build it with
 * `recallStartStatementBody`, `recallStatementBody` or
 * `sessionUpgradeStatementBody`; kind 1 has an empty body and kind 3 the
 * 32-byte device id.
 *
 * # Errors
 *
 * As `signAccountStatement`.
 * @param {Uint8Array} account_seed
 * @param {number} kind
 * @param {Uint8Array} account_id
 * @param {Uint8Array} body
 * @param {bigint} unix_ms
 * @returns {Uint8Array}
 */
export function signAccountStatementAt(account_seed, kind, account_id, body, unix_ms) {
    const ptr0 = passArray8ToWasm0(account_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(body, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.signAccountStatementAt(ptr0, len0, kind, ptr1, len1, ptr2, len2, unix_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Sign a **call-invite** link revocation request (Phase 3c3-revoke).
 *
 * Sibling of [`signInviteRevocation`] for the
 * [`CallInviteCapability`] ecosystem. The web client calls this
 * just before posting `DELETE /discovery/call-invite/{blob_id}`
 * so the body's `signature` field carries proof that the
 * revocation came from the same Ed25519 key that signed the
 * capability.
 *
 * The canonical bytes the signature covers — domain separator
 * `lakko-call-invite-revoke/v1\0` + `blob_id` + big-endian
 * `signed_at_ms` — live in
 * [`lakko_contacts::call_invite::call_revoke_canonical_bytes`].
 * The distinct domain separator (vs. contact-invite revocation)
 * ensures a captured contact-invite revoke signature cannot be
 * replayed as a call-invite revoke (and vice versa).
 *
 * Returns the 64-byte raw Ed25519 signature. Caller
 * base64-encodes for the JSON body.
 *
 * # Errors
 *
 * `JsError` on any input-shape failure (32-byte signing seed,
 * 6-byte blob_id required).
 * @param {Uint8Array} signing_seed
 * @param {Uint8Array} blob_id
 * @param {bigint} signed_at_ms
 * @returns {Uint8Array}
 */
export function signCallInviteRevocation(signing_seed, blob_id, signed_at_ms) {
    const ptr0 = passArray8ToWasm0(signing_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.signCallInviteRevocation(ptr0, len0, ptr1, len1, signed_at_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Sign a two-way conversation link (ADR-0074 section 3.7): both keys state
 * that the account's conversations are bound to the first and the directory
 * publishes the second. Pass each identity's `ed25519Seed`. Returns the
 * 201-byte link to upload with the identity bundle.
 *
 * Throws if a seed is not 32 bytes, or the two seeds are one key.
 * @param {Uint8Array} conversation_ed25519_seed
 * @param {Uint8Array} current_ed25519_seed
 * @param {bigint} issued_at_unix_secs
 * @returns {Uint8Array}
 */
export function signConversationLink(conversation_ed25519_seed, current_ed25519_seed, issued_at_unix_secs) {
    const ptr0 = passArray8ToWasm0(conversation_ed25519_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(current_ed25519_seed, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.signConversationLink(ptr0, len0, ptr1, len1, issued_at_unix_secs);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Sign a delegation (ADR-0060 §2): the holder of the reach seed
 * `issuerSeed` authorises ONE write by `delegatePk` to the 32-byte `mailbox`
 * pseudonym until `expiresAtMs`, under a fresh 16-byte `nonce` the DS
 * consumes. Returns the `delegationHeaderName()` header value; the delegate
 * sends it with a proof signed by `DsProofSigner.forReach(delegateSeed)`.
 *
 * # Errors
 *
 * Throws an `Error` with `reachError` `seedLength` / `keyLength` /
 * `nonceLength`.
 * @param {Uint8Array} issuer_seed
 * @param {Uint8Array} mailbox
 * @param {Uint8Array} delegate_pk
 * @param {bigint} expires_at_ms
 * @param {Uint8Array} nonce
 * @returns {string}
 */
export function signDelegation(issuer_seed, mailbox, delegate_pk, expires_at_ms, nonce) {
    let deferred6_0;
    let deferred6_1;
    try {
        const ptr0 = passArray8ToWasm0(issuer_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(mailbox, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(delegate_pk, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArray8ToWasm0(nonce, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        const ret = wasm.signDelegation(ptr0, len0, ptr1, len1, ptr2, len2, expires_at_ms, ptr3, len3);
        var ptr5 = ret[0];
        var len5 = ret[1];
        if (ret[3]) {
            ptr5 = 0; len5 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred6_0 = ptr5;
        deferred6_1 = len5;
        return getStringFromWasm0(ptr5, len5);
    } finally {
        wasm.__wbindgen_free(deferred6_0, deferred6_1, 1);
    }
}

/**
 * `signDelegation`, returning the raw 185-byte delegation instead of the
 * header value: the blob a committer-delegation envelope carries (ADR-0060
 * §6, `composeCommitterDelegationEnvelope`). The proposer signs one per
 * committer candidate, the delegate being that candidate's account
 * signature key and the mailbox the added person's.
 *
 * # Errors
 *
 * As `signDelegation`.
 * @param {Uint8Array} issuer_seed
 * @param {Uint8Array} mailbox
 * @param {Uint8Array} delegate_pk
 * @param {bigint} expires_at_ms
 * @param {Uint8Array} nonce
 * @returns {Uint8Array}
 */
export function signDelegationBlob(issuer_seed, mailbox, delegate_pk, expires_at_ms, nonce) {
    const ptr0 = passArray8ToWasm0(issuer_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(mailbox, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(delegate_pk, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(nonce, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.signDelegationBlob(ptr0, len0, ptr1, len1, ptr2, len2, expires_at_ms, ptr3, len3);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v5 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v5;
}

/**
 * ADR-0081 amendment — the DeviceGroup self-removal proof: a device that
 * lost its DeviceGroup state signs, with its OLD leaf key (`oldIdentity`,
 * the device identity it still holds), that in the DeviceGroup `groupId` its
 * leaf may be replaced by the KeyPackage `replacementKeyPackage` in the
 * commit that leaves `epoch` (the journal's latest epoch, from a knock
 * probe). A fixed 137-byte blob carried with the knock; the admitting
 * sibling puts it in the removal-authorisation slot of the replacement
 * commit's envelope.
 * @param {Identity} old_identity
 * @param {Uint8Array} group_id
 * @param {bigint} epoch
 * @param {Uint8Array} replacement_key_package
 * @returns {Uint8Array}
 */
export function signDeviceGroupSelfRemoval(old_identity, group_id, epoch, replacement_key_package) {
    _assertClass(old_identity, Identity);
    const ptr0 = passArray8ToWasm0(group_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(replacement_key_package, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.signDeviceGroupSelfRemoval(old_identity.__wbg_ptr, ptr0, len0, epoch, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Sign the statement that removes one device of the account
 * (`POST /account/devices/revoke` at auth, and the
 * `accountStatementHeaderName()` header of discovery's
 * `DELETE /discovery/keypackage/device/{id}`): kind 3 over the 32-byte
 * `deviceId`. The same wire form serves both requests while it is inside the
 * two-minute window.
 *
 * # Errors
 *
 * Throws `"<code>: <reason>"`: `bad-device-id-length`, or as
 * `signAccountStatement`.
 * @param {Uint8Array} account_seed
 * @param {Uint8Array} account_id
 * @param {Uint8Array} device_id
 * @returns {Uint8Array}
 */
export function signDeviceRevokeStatement(account_seed, account_id, device_id) {
    const ptr0 = passArray8ToWasm0(account_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(device_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.signDeviceRevokeStatement(ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Owner-side: sign a group-invite revocation (ADR-0028 revoke). Returns the
 * 64-byte Ed25519 signature over the group-invite revoke canonical bytes (the
 * `DELETE /discovery/group-invite/{blob_id}` body). `issuerSigningSeed` is the
 * 32-byte Ed25519 seed that minted the link; `blobId` is the 6-byte id the
 * discovery upload returned.
 * @param {Uint8Array} issuer_signing_seed
 * @param {Uint8Array} blob_id
 * @param {bigint} signed_at_ms
 * @returns {Uint8Array}
 */
export function signGroupInviteRevocation(issuer_signing_seed, blob_id, signed_at_ms) {
    const ptr0 = passArray8ToWasm0(issuer_signing_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.signGroupInviteRevocation(ptr0, len0, ptr1, len1, signed_at_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * A claim statement (`POST /discovery/handle/claim`), standard base64.
 *
 * # Errors
 *
 * Throws `handleStatement.badLength: ...`.
 * @param {Uint8Array} handle_seed
 * @param {Uint8Array} lookup_key
 * @param {boolean} _public
 * @param {Uint8Array} content_hash
 * @returns {string}
 */
export function signHandleClaim(handle_seed, lookup_key, _public, content_hash) {
    let deferred5_0;
    let deferred5_1;
    try {
        const ptr0 = passArray8ToWasm0(handle_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(lookup_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(content_hash, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.signHandleClaim(ptr0, len0, ptr1, len1, _public, ptr2, len2);
        var ptr4 = ret[0];
        var len4 = ret[1];
        if (ret[3]) {
            ptr4 = 0; len4 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred5_0 = ptr4;
        deferred5_1 = len4;
        return getStringFromWasm0(ptr4, len4);
    } finally {
        wasm.__wbindgen_free(deferred5_0, deferred5_1, 1);
    }
}

/**
 * A rekey statement (`POST /discovery/handle/rekey`), standard base64.
 *
 * # Errors
 *
 * Throws `handleStatement.badLength: ...`.
 * @param {Uint8Array} handle_seed
 * @param {Uint8Array} lookup_key
 * @param {Uint8Array} new_write_pk
 * @returns {string}
 */
export function signHandleRekey(handle_seed, lookup_key, new_write_pk) {
    let deferred5_0;
    let deferred5_1;
    try {
        const ptr0 = passArray8ToWasm0(handle_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(lookup_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(new_write_pk, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.signHandleRekey(ptr0, len0, ptr1, len1, ptr2, len2);
        var ptr4 = ret[0];
        var len4 = ret[1];
        if (ret[3]) {
            ptr4 = 0; len4 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred5_0 = ptr4;
        deferred5_1 = len4;
        return getStringFromWasm0(ptr4, len4);
    } finally {
        wasm.__wbindgen_free(deferred5_0, deferred5_1, 1);
    }
}

/**
 * A replace statement (`POST /discovery/handle/replace`), standard base64.
 *
 * # Errors
 *
 * Throws `handleStatement.badLength: ...`.
 * @param {Uint8Array} handle_seed
 * @param {Uint8Array} lookup_key
 * @param {Uint8Array} new_lookup_key
 * @param {boolean} _public
 * @param {Uint8Array} content_hash
 * @returns {string}
 */
export function signHandleReplace(handle_seed, lookup_key, new_lookup_key, _public, content_hash) {
    let deferred6_0;
    let deferred6_1;
    try {
        const ptr0 = passArray8ToWasm0(handle_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(lookup_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(new_lookup_key, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ptr3 = passArray8ToWasm0(content_hash, wasm.__wbindgen_malloc);
        const len3 = WASM_VECTOR_LEN;
        const ret = wasm.signHandleReplace(ptr0, len0, ptr1, len1, ptr2, len2, _public, ptr3, len3);
        var ptr5 = ret[0];
        var len5 = ret[1];
        if (ret[3]) {
            ptr5 = 0; len5 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred6_0 = ptr5;
        deferred6_1 = len5;
        return getStringFromWasm0(ptr5, len5);
    } finally {
        wasm.__wbindgen_free(deferred6_0, deferred6_1, 1);
    }
}

/**
 * A retire statement (`DELETE /discovery/handle`), standard base64.
 *
 * # Errors
 *
 * Throws `handleStatement.badLength: ...`.
 * @param {Uint8Array} handle_seed
 * @param {Uint8Array} lookup_key
 * @returns {string}
 */
export function signHandleRetire(handle_seed, lookup_key) {
    let deferred4_0;
    let deferred4_1;
    try {
        const ptr0 = passArray8ToWasm0(handle_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(lookup_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.signHandleRetire(ptr0, len0, ptr1, len1);
        var ptr3 = ret[0];
        var len3 = ret[1];
        if (ret[3]) {
            ptr3 = 0; len3 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred4_0 = ptr3;
        deferred4_1 = len3;
        return getStringFromWasm0(ptr3, len3);
    } finally {
        wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
    }
}

/**
 * An update statement (`PUT /discovery/handle`), standard base64.
 *
 * # Errors
 *
 * Throws `handleStatement.badLength: ...`.
 * @param {Uint8Array} handle_seed
 * @param {Uint8Array} lookup_key
 * @param {boolean} _public
 * @param {Uint8Array} content_hash
 * @returns {string}
 */
export function signHandleUpdate(handle_seed, lookup_key, _public, content_hash) {
    let deferred5_0;
    let deferred5_1;
    try {
        const ptr0 = passArray8ToWasm0(handle_seed, wasm.__wbindgen_malloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passArray8ToWasm0(lookup_key, wasm.__wbindgen_malloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passArray8ToWasm0(content_hash, wasm.__wbindgen_malloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.signHandleUpdate(ptr0, len0, ptr1, len1, _public, ptr2, len2);
        var ptr4 = ret[0];
        var len4 = ret[1];
        if (ret[3]) {
            ptr4 = 0; len4 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred5_0 = ptr4;
        deferred5_1 = len4;
        return getStringFromWasm0(ptr4, len4);
    } finally {
        wasm.__wbindgen_free(deferred5_0, deferred5_1, 1);
    }
}

/**
 * Sign an invite-link revocation request (Phase 1.18d2b-web).
 *
 * The web client calls this just before posting
 * `DELETE /discovery/invite/{blob_id}` so the body's
 * `signature` field carries proof that the revocation came
 * from the same Ed25519 key that signed the capability. The
 * canonical bytes the signature covers — domain separator +
 * `blob_id` + big-endian `signed_at_ms` — live in
 * [`lakko_contacts::invite::revoke_canonical_bytes`]; both the
 * server (`services/discovery`) and this helper drive
 * sign / verify against the same layout, so consolidating it
 * in `lakko-contacts` is what keeps the two sides in sync.
 *
 * Returns the 64-byte raw Ed25519 signature. Caller
 * base64-encodes for the JSON body.
 *
 * # Errors
 *
 * `JsError` on any input-shape failure (32-byte signing seed,
 * 6-byte blob_id required).
 * @param {Uint8Array} signing_seed
 * @param {Uint8Array} blob_id
 * @param {bigint} signed_at_ms
 * @returns {Uint8Array}
 */
export function signInviteRevocation(signing_seed, blob_id, signed_at_ms) {
    const ptr0 = passArray8ToWasm0(signing_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(blob_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.signInviteRevocation(ptr0, len0, ptr1, len1, signed_at_ms);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Sign a genesis / fresh-start KT-log leaf's account-to-key binding with the
 * leaf's OWN identity key (G-30 #2, ADR-0019). Used for the first-ever publish
 * and a lost-everything fresh-start mint (a new key with NO supersession
 * proof). Pass the identity's `ed25519Seed`; the leaf's pubkey is derived from
 * it internally so the self-signature always matches the embedded key. The
 * signature is version-INDEPENDENT (the version is operator-assigned and
 * unknown to a fresh-start minter). Returns the 64-byte signature to carry as
 * the leaf's `leaf_auth`.
 *
 * Throws if the seed is not 32 bytes or `usernameHash` is not 32 bytes.
 * @param {Uint8Array} ed25519_seed
 * @param {Uint8Array} username_hash
 * @returns {Uint8Array}
 */
export function signLeafGenesis(ed25519_seed, username_hash) {
    const ptr0 = passArray8ToWasm0(ed25519_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(username_hash, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.signLeafGenesis(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * The 97-byte mailbox binding of the account whose identity is
 * `accountIdentity`: its mailbox key signed by its identity key.
 * Deterministic, so every device produces the same bytes.
 * @param {Identity} account_identity
 * @returns {Uint8Array}
 */
export function signMailboxBinding(account_identity) {
    _assertClass(account_identity, Identity);
    const ret = wasm.signMailboxBinding(account_identity.__wbg_ptr);
    var v1 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v1;
}

/**
 * Sign the statement `POST /account/recall/start` requires (ADR-0074 "T3 as
 * built"): kind 5 over the anchor's `anchorSeq` (from
 * `POST /account/recall/status`) and the OPAQUE login request, given as the
 * same standard-base64 string the request body carries
 * (`OpaqueLogin.requestB64`). `accountSeed` is the OLD account identity's
 * 32-byte Ed25519 seed kept in the recall record.
 *
 * # Errors
 *
 * Throws `"<code>: <reason>"`: `bad-base64`, or as `signAccountStatement`.
 * @param {Uint8Array} account_seed
 * @param {Uint8Array} account_id
 * @param {bigint} anchor_seq
 * @param {string} login_request_b64
 * @returns {Uint8Array}
 */
export function signRecallStartStatement(account_seed, account_id, anchor_seq, login_request_b64) {
    const ptr0 = passArray8ToWasm0(account_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(login_request_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.signRecallStartStatement(ptr0, len0, ptr1, len1, anchor_seq, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Sign the statement `POST /account/recall/finish` requires: kind 2 over
 * the 32-byte `serverNonce` the start returned, the NEW identity's 32-byte
 * signature public key, and the two OPAQUE registration uploads (the new
 * password's and the new recovery credential's), given as the same
 * standard-base64 strings the request body carries
 * (`OpaqueRegisterOutput.uploadB64`). `accountSeed` is the OLD account
 * identity's seed.
 *
 * # Errors
 *
 * Throws `"<code>: <reason>"`: `bad-server-nonce-length`,
 * `bad-pubkey-length`, `bad-base64`, or as `signAccountStatement`.
 * @param {Uint8Array} account_seed
 * @param {Uint8Array} account_id
 * @param {Uint8Array} server_nonce
 * @param {Uint8Array} new_signature_pubkey
 * @param {string} password_upload_b64
 * @param {string} recovery_upload_b64
 * @returns {Uint8Array}
 */
export function signRecallStatement(account_seed, account_id, server_nonce, new_signature_pubkey, password_upload_b64, recovery_upload_b64) {
    const ptr0 = passArray8ToWasm0(account_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(server_nonce, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(new_signature_pubkey, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passStringToWasm0(password_upload_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len4 = WASM_VECTOR_LEN;
    const ptr5 = passStringToWasm0(recovery_upload_b64, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len5 = WASM_VECTOR_LEN;
    const ret = wasm.signRecallStatement(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, ptr5, len5);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v7 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v7;
}

/**
 * Sign the statement that upgrades a kept device's session after a key
 * rotation (ADR-0074 section 3.3): kind 4 over `BLAKE3(refreshToken)`. Sent
 * as `key_statement_b64` (base64url, no padding) beside `refresh_token` on
 * `POST /auth/session/refresh`, once the device has ADOPTED the new account
 * seed. `accountSeed` is the NEW account identity's 32-byte Ed25519 seed;
 * `refreshToken` is the very `r2` credential the request carries, of the
 * previous key generation.
 *
 * A device that holds only the old account key cannot make a statement the
 * server accepts: it stays signed out (`key-generation-superseded`).
 *
 * # Errors
 *
 * As `signAccountStatement`.
 * @param {Uint8Array} account_seed
 * @param {Uint8Array} account_id
 * @param {string} refresh_token
 * @returns {Uint8Array}
 */
export function signSessionUpgradeStatement(account_seed, account_id, refresh_token) {
    const ptr0 = passArray8ToWasm0(account_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(refresh_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.signSessionUpgradeStatement(ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v4 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v4;
}

/**
 * Sign an identity supersession proof (G-4): the OLD identity authorises the
 * NEW identity as its successor, valid in `[issuedAtUnixSecs, expiresAtUnixSecs]`
 * (the verifier additionally caps the window at 30 days). Pass each identity's
 * `ed25519Seed`; both public keys are derived internally so the proof's
 * embedded old key always matches the signing key. Returns the wire-form proof
 * to publish alongside the rotation.
 * @param {Uint8Array} old_ed25519_seed
 * @param {Uint8Array} new_ed25519_seed
 * @param {bigint} issued_at_unix_secs
 * @param {bigint} expires_at_unix_secs
 * @returns {Uint8Array}
 */
export function signSupersessionProof(old_ed25519_seed, new_ed25519_seed, issued_at_unix_secs, expires_at_unix_secs) {
    const ptr0 = passArray8ToWasm0(old_ed25519_seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(new_ed25519_seed, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.signSupersessionProof(ptr0, len0, ptr1, len1, issued_at_unix_secs, expires_at_unix_secs);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Continuous speech for this long takes the floor, milliseconds. Chosen,
 * not measured.
 * @returns {number}
 */
export function speakingFloorOnsetMs() {
    const ret = wasm.speakingFloorOnsetMs();
    return ret >>> 0;
}

/**
 * The threshold a person starts with, before anything is known about the
 * noise around their microphone. Chosen, not measured.
 * @returns {number}
 */
export function speakingLevel() {
    const ret = wasm.speakingLevel();
    return ret;
}

/**
 * The highest a person's threshold goes. Chosen, not measured.
 * @returns {number}
 */
export function speakingLevelMax() {
    const ret = wasm.speakingLevelMax();
    return ret;
}

/**
 * The lowest a person's threshold goes. Chosen, not measured.
 * @returns {number}
 */
export function speakingLevelMin() {
    const ret = wasm.speakingLevelMin();
    return ret;
}

/**
 * Silence for this long turns the ring off, milliseconds. Chosen, not
 * measured.
 * @returns {number}
 */
export function speakingRingOffMs() {
    const ret = wasm.speakingRingOffMs();
    return ret >>> 0;
}

/**
 * Sound for this long turns the ring on, milliseconds. Chosen, not
 * measured.
 * @returns {number}
 */
export function speakingRingOnMs() {
    const ret = wasm.speakingRingOnMs();
    return ret >>> 0;
}

/**
 * A message's text as pieces, in order. Joining every piece's text gives the
 * input back. Draw a link piece as a link; decide nothing else about it here.
 * @param {string} text
 * @returns {MessageTextPiece[]}
 */
export function splitMessageLinks(text) {
    const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.splitMessageLinks(ptr0, len0);
    var v2 = getArrayJsValueFromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * The request header a storage proof travels in (`x-lakko-storage-proof`).
 * @returns {string}
 */
export function storageProofHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.storageProofHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * How far from the server's clock a storage proof's time may lie, either
 * way, in milliseconds (120 000). A `bigint`.
 * @returns {bigint}
 */
export function storageProofWindowMs() {
    const ret = wasm.storageProofWindowMs();
    return BigInt.asUintN(64, ret);
}

/**
 * Whether a stored translation or transcript still describes the message.
 * @param {bigint} stored_last_op_id
 * @param {bigint} current_last_op_id
 * @param {string} stored_target
 * @param {string} current_target
 * @returns {boolean}
 */
export function storedResultIsCurrent(stored_last_op_id, current_last_op_id, stored_target, current_target) {
    const ptr0 = passStringToWasm0(stored_target, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(current_target, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.storedResultIsCurrent(stored_last_op_id, current_last_op_id, ptr0, len0, ptr1, len1);
    return ret !== 0;
}

/**
 * The `@name` to prefill for a new group (`kind` `"group"`) or channel
 * (`"channel"`) named `name`; `random` is the caller's randomness (a
 * `bigint`).
 *
 * # Errors
 *
 * Throws `handle.badKind` for another kind.
 * @param {string} name
 * @param {string} kind
 * @param {boolean} _private
 * @param {bigint} random
 * @returns {string}
 */
export function suggestHandle(name, kind, _private, random) {
    let deferred4_0;
    let deferred4_1;
    try {
        const ptr0 = passStringToWasm0(name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.suggestHandle(ptr0, len0, ptr1, len1, _private, random);
        var ptr3 = ret[0];
        var len3 = ret[1];
        if (ret[3]) {
            ptr3 = 0; len3 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred4_0 = ptr3;
        deferred4_1 = len3;
        return getStringFromWasm0(ptr3, len3);
    } finally {
        wasm.__wbindgen_free(deferred4_0, deferred4_1, 1);
    }
}

/**
 * A tile narrower than this, in physical pixels, asks for the light track
 * alone. Chosen, not measured.
 * @returns {number}
 */
export function tileLightMaxWidthPx() {
    const ret = wasm.tileLightMaxWidthPx();
    return ret >>> 0;
}

/**
 * A pinned or stage tile at least this wide, in physical pixels, asks for
 * the main track. Chosen, not measured.
 * @returns {number}
 */
export function tileMainMinWidthPx() {
    const ret = wasm.tileMainMinWidthPx();
    return ret >>> 0;
}

/**
 * Which track a tile asks for. `people` is everyone in the call, this
 * device's own person included; `tile_width_px` is the width the tile is
 * drawn at in **physical** pixels (CSS pixels times `devicePixelRatio`);
 * `pinned_or_stage` says the tile is the pinned person or the stage.
 *
 * A call of two: `Main`. A pinned or stage tile at least
 * {@link tileMainMinWidthPx} wide: `Main`. Otherwise a tile narrower than
 * {@link tileLightMaxWidthPx}: `Light`. Otherwise: `Middle`.
 * @param {number} people
 * @param {number} tile_width_px
 * @param {boolean} pinned_or_stage
 * @returns {TileVideoQuality}
 */
export function tileVideoQuality(people, tile_width_px, pinned_or_stage) {
    const ret = wasm.tileVideoQuality(people, tile_width_px, pinned_or_stage);
    return ret;
}

/**
 * The name a `TileVideoQuality` has in the SFU's `video-wants` frame:
 * `main`, `middle` or `light` (what `composeSfuVideoWants` takes).
 * @param {TileVideoQuality} quality
 * @returns {string}
 */
export function tileVideoQualityName(quality) {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.tileVideoQualityName(quality);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Whether a voice or video message gets the transcript button.
 * @param {boolean} enabled
 * @param {boolean} has_audio
 * @param {TranslationEngineState} engine
 * @returns {boolean}
 */
export function transcriptOffered(enabled, has_audio, engine) {
    const ret = wasm.transcriptOffered(enabled, has_audio, engine);
    return ret !== 0;
}

/**
 * How many confident detections the bar needs before it says anything.
 * @returns {number}
 */
export function translationBarMinConfident() {
    const ret = wasm.translationBarMinConfident();
    return ret >>> 0;
}

/**
 * How many of the newest incoming messages the conversation bar reads.
 * @returns {number}
 */
export function translationBarWindow() {
    const ret = wasm.translationBarWindow();
    return ret >>> 0;
}

/**
 * Detections below this confidence, in thousandths, are guesses.
 * @returns {number}
 */
export function translationDetectionConfidentPermille() {
    const ret = wasm.translationDetectionConfidentPermille();
    return ret;
}

/**
 * Whether the message menu offers `Translate`.
 *
 * `detectedTag` empty means nothing was detected; `detectedPermille` is then
 * ignored.
 * @param {boolean} enabled
 * @param {boolean} has_text
 * @param {boolean} is_own
 * @param {string} detected_tag
 * @param {number} detected_permille
 * @param {TranslationEngineState} engine_for_detected
 * @param {boolean} any_engine
 * @param {string[]} reading_languages
 * @returns {TranslateOffer}
 */
export function translationOffered(enabled, has_text, is_own, detected_tag, detected_permille, engine_for_detected, any_engine, reading_languages) {
    const ptr0 = passStringToWasm0(detected_tag, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArrayJsValueToWasm0(reading_languages, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.translationOffered(enabled, has_text, is_own, ptr0, len0, detected_permille, engine_for_detected, any_engine, ptr1, len1);
    return ret;
}

/**
 * Derive the transparency-log account-key (the leaf's opaque
 * `username_hash`) for an `account_id`:
 * `BLAKE3("lakko/transparency/account/v1" || account_id)`.
 *
 * The web client needs this to (a) query
 * `GET /transparency/latest/:account_key` and (b) pass the exact
 * `entry_username_hash` into [`verify_log_inclusion`]. We expose it
 * from the audited Rust core rather than bundle a JS BLAKE3 — the
 * derivation must match discovery's `account_key(...)` byte-for-byte,
 * and re-implementing it in TS would be a second, drift-prone copy of
 * a security-relevant primitive.
 *
 * Mirrors the un-keyed `lakko_crypto::hash::blake3` over the
 * domain-prefixed `account_id`. `account_id` is public, so the result
 * reveals nothing new; the domain separator only stops a collision
 * with any other BLAKE3 use of the same bytes.
 * @param {Uint8Array} account_id
 * @returns {Uint8Array}
 */
export function transparencyAccountKey(account_id) {
    const ptr0 = passArray8ToWasm0(account_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.transparencyAccountKey(ptr0, len0);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Try to unwrap a call-KP-forward envelope from a HPKE-
 * decrypted plaintext (Phase 3c3-kp-forward).
 *
 * Returns:
 *   * `Uint8Array` — `plaintext` started with the
 *     call-KP-forward domain separator AND the trailing
 *     payload is at least `CALL_KP_FORWARD_MIN_PAYLOAD_LEN`
 *     bytes long. Caller drives `Group::add_member_from_wire(payload)`
 *     against the call group and ferries the resulting
 *     Welcome back via `wrapCallWelcome`.
 *   * `null` — `plaintext` is not a KP-forward envelope;
 *     caller falls through to `tryUnwrapCallWelcome` and
 *     then to the intro-message rendering path.
 *
 * # Errors
 *
 * `JsError` ONLY when the domain separator matched but the
 * payload is truncated (a malformed / hostile envelope;
 * log + drop on the caller side).
 * @param {Uint8Array} plaintext
 * @returns {Uint8Array | undefined}
 */
export function tryUnwrapCallKeyPackage(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.tryUnwrapCallKeyPackage(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    let v2;
    if (ret[0] !== 0) {
        v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * Try to unwrap a call-welcome envelope from a HPKE-decrypted
 * plaintext (Phase 3c3-welcome-ferry).
 *
 * Returns:
 *   * `Uint8Array` with the MLS Welcome bytes — `plaintext`
 *     started with the call-welcome domain separator AND
 *     the trailing payload is at least
 *     `CALL_WELCOME_MIN_PAYLOAD_LEN` bytes long. Caller
 *     drives `Group::join_from_welcome(payload)`.
 *   * `null` — `plaintext` is a plain intro message; caller
 *     renders it as a free-text ContactRequest.
 *
 * # Errors
 *
 * `JsError` ONLY when the domain separator matched but the
 * payload is truncated (a malformed / hostile envelope; log
 * + drop on the caller side).
 * @param {Uint8Array} plaintext
 * @returns {Uint8Array | undefined}
 */
export function tryUnwrapCallWelcome(plaintext) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.tryUnwrapCallWelcome(ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    let v2;
    if (ret[0] !== 0) {
        v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v2;
}

/**
 * The request header an unsubscribe carries its signature in
 * (`x-lakko-unsubscribe-sig`).
 * @returns {string}
 */
export function unsubscribeSigHeaderName() {
    let deferred1_0;
    let deferred1_1;
    try {
        const ret = wasm.unsubscribeSigHeaderName();
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * Parse an SFrame wire frame.
 *
 * # Errors
 *
 * `JsError` on header decode failure or an oversize
 * `kid` (must fit in u32). Any 64-bit `ctr` is accepted
 * (ADR-0069).
 * @param {Uint8Array} wire
 * @returns {UnwrappedFrame}
 */
export function unwrapSframeFrame(wire) {
    const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.unwrapSframeFrame(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return UnwrappedFrame.__wrap(ret[0]);
}

/**
 * Wire length of a username certificate: 153.
 * @returns {number}
 */
export function usernameCertificateLen() {
    const ret = wasm.usernameCertificateLen();
    return ret >>> 0;
}

/**
 * Validate an inbound server frame off the `lakko-sfu-v1`
 * WebSocket and return its `kind` discriminator (kebab-case:
 * `"welcome"`, `"peer-joined"`, `"peer-left"`, `"signal-relay"`,
 * `"error"`, `"pong"`, `"sdp-answer"`, `"sdp-offer"`, `"video-given"`,
 * `"video-needed"`, `"publish-budget"`).
 * A `publish-budget` frame carries two required integers, `main_kbps`
 * and `middle_kbps`: the ceiling the SFU sets for this publisher's own
 * main and middle camera senders (`parseSfuPublishBudget`).
 * A `video-needed` frame carries two required booleans, `main` and
 * `middle`: which of this publisher's own camera senders somebody is
 * being sent (`parseSfuVideoNeeded`).
 * A `video-given` frame (ADR-0070 step 6) carries `given`, a list of
 * `{ peer, quality }` with `quality` one of `"main"`, `"middle"`,
 * `"light"`: what this subscriber is being sent of each publisher's
 * video, the whole set, validated here against the protocol's bounds.
 * Throws on a
 * malformed / unknown-kind / oversize frame. The web reads the
 * frame's fields off the original JSON with a TypeScript type.
 * @param {string} json
 * @returns {string}
 */
export function validateSfuServerFrame(json) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passStringToWasm0(json, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.validateSfuServerFrame(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * Normalise `input` and require the ADR-0048 §4 username rule — ASCII
 * `[a-z0-9_]`, 3 to 32 characters. Returns the normalised form.
 *
 * For signup and search fields as the user types: show the reason, and
 * never send a lookup for a name that fails.
 *
 * # Errors
 *
 * Throws an `Error` whose `usernameError` property is `"empty"`,
 * `"tooShort"`, `"tooLong"` or `"invalidCharacters"`.
 * @param {string} input
 * @returns {string}
 */
export function validateUsername(input) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passStringToWasm0(input, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.validateUsername(ptr0, len0);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * The vault keys of `accountIdentity`'s account.
 *
 * `accountIdentity` is the ACCOUNT identity (the ADR-0017 phrase-seeded
 * one, the same on every device of the account), not a device identity:
 * its Ed25519 seed is the derivation input, exactly as for
 * `dsJournalRootSigner`. A device identity here would name vaults no other
 * device of the account can find.
 * @param {Identity} account_identity
 * @returns {VaultKeys}
 */
export function vaultKeys(account_identity) {
    _assertClass(account_identity, Identity);
    const ret = wasm.vaultKeys(account_identity.__wbg_ptr);
    return VaultKeys.__wrap(ret);
}

/**
 * Most messages in one chunk (512).
 * @returns {number}
 */
export function vaultMaxChunkMessages() {
    const ret = wasm.vaultMaxChunkMessages();
    return ret >>> 0;
}

/**
 * Longest saved GroupInfo, in bytes (1 MiB).
 * @returns {number}
 */
export function vaultMaxGroupInfoBytes() {
    const ret = wasm.vaultMaxGroupInfoBytes();
    return ret >>> 0;
}

/**
 * Most people in one head (4096).
 * @returns {number}
 */
export function vaultMaxPeople() {
    const ret = wasm.vaultMaxPeople();
    return ret >>> 0;
}

/**
 * Most saved epochs of re-entry material in a head (2: the two newest).
 * @returns {number}
 */
export function vaultMaxReentryEpochs() {
    const ret = wasm.vaultMaxReentryEpochs();
    return ret >>> 0;
}

/**
 * Most messages in a head's tail (512).
 * @returns {number}
 */
export function vaultMaxTailMessages() {
    const ret = wasm.vaultMaxTailMessages();
    return ret >>> 0;
}

/**
 * The sealed size of an object carrying `plaintextLen` bytes - what the
 * storage service will see - or `undefined` when that is more than one
 * object can carry. For a client that budgets a vault's bytes. `bigint` in
 * and out.
 * @param {bigint} plaintext_len
 * @returns {bigint | undefined}
 */
export function vaultSealedLen(plaintext_len) {
    const ret = wasm.vaultSealedLen(plaintext_len);
    return ret[0] === 0 ? undefined : BigInt.asUintN(64, ret[1]);
}

/**
 * Verify the post's Ed25519 signature + AEAD-open the
 * ciphertext. Returns the recovered plaintext bytes.
 *
 * Throws with `BadSignature` if the signature does not
 * validate, `AeadOpen` if the ciphertext is tampered or
 * the seed is wrong, `BadSenderKey` if the sender pubkey
 * does not parse as Ed25519.
 * @param {Uint8Array} seed
 * @param {Uint8Array} channel_id
 * @param {ChannelPost} post
 * @returns {Uint8Array}
 */
export function verifyAndDecryptChannelPost(seed, channel_id, post) {
    const ptr0 = passArray8ToWasm0(seed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(channel_id, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    _assertClass(post, ChannelPost);
    const ret = wasm.verifyAndDecryptChannelPost(ptr0, len0, ptr1, len1, post.__wbg_ptr);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Verify a backup head's signature, `accountId` binding, `envelopeHash`
 * binding, AND freshness — the full check the web client applies on restore
 * (ADR-0037 §6). Returns the parsed [`VerifiedBackupHead`] so the caller reads
 * `backupVersion` for its own monotonic-floor comparison against its persisted
 * `lastKnownVersion`.
 *
 * `head` is the raw backup-head bytes (the container prefix of the served
 * backup blob). `attestVerifyingKey` is the 32-byte client-pinned auth attest
 * key. `expectedAccountId` is the client's authenticated 16-byte account id;
 * `expectedEnvelopeHash` is the 32-byte [`backup_envelope_hash`] of the served
 * envelope. `nowMillis` and `maxStalenessMillis` are Unix milliseconds.
 *
 * Throws a coded `JsError`: `stale: …` when the head verified authentically but
 * is outside the freshness window (the caller may offer a user-acknowledged
 * override); `invalid: …` for every hard fail-closed cause (a malformed /
 * wrong-length / wrong-version head, a signature that does not verify, an
 * `accountId` / `envelopeHash` mismatch, or a bad key / wrong-length
 * `expectedAccountId` / `expectedEnvelopeHash`). Branch on the code prefix.
 * @param {Uint8Array} head
 * @param {Uint8Array} attest_verifying_key
 * @param {Uint8Array} expected_account_id
 * @param {Uint8Array} expected_envelope_hash
 * @param {bigint} now_millis
 * @param {bigint} max_staleness_millis
 * @returns {VerifiedBackupHead}
 */
export function verifyBackupHead(head, attest_verifying_key, expected_account_id, expected_envelope_hash, now_millis, max_staleness_millis) {
    const ptr0 = passArray8ToWasm0(head, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(attest_verifying_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(expected_account_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(expected_envelope_hash, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.verifyBackupHead(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, now_millis, max_staleness_millis);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VerifiedBackupHead.__wrap(ret[0]);
}

/**
 * Verify a backup head's signature and `accountId` binding ONLY — no
 * `envelopeHash` or freshness check (ADR-0037 §6). For a caller that applies
 * its own freshness and monotonic-floor policy (e.g. one that wants the parsed
 * `backupVersion` before it has hashed the served envelope). Returns the parsed
 * [`VerifiedBackupHead`].
 *
 * Throws a coded `invalid: …` `JsError` on a malformed / wrong-length /
 * wrong-version head, a signature that does not verify, an `accountId`
 * mismatch, or a bad key / wrong-length `expectedAccountId`. This entry point
 * performs no freshness check, so it never throws the `stale: …` code.
 * @param {Uint8Array} head
 * @param {Uint8Array} attest_verifying_key
 * @param {Uint8Array} expected_account_id
 * @returns {VerifiedBackupHead}
 */
export function verifyBackupHeadBinding(head, attest_verifying_key, expected_account_id) {
    const ptr0 = passArray8ToWasm0(head, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(attest_verifying_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(expected_account_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.verifyBackupHeadBinding(ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VerifiedBackupHead.__wrap(ret[0]);
}

/**
 * Verify a JWT against a JWKS document the client fetched
 * from `GET /.well-known/jwks.json`. Returns the typed
 * claims on success.
 *
 * `nowUnixSecs` is injected so the platform controls the
 * clock source — pass `Math.floor(Date.now() / 1000)`.
 * @param {Uint8Array} jwks_doc_bytes
 * @param {string} jwt
 * @param {bigint} now_unix_secs
 * @returns {ParsedCallJwt}
 */
export function verifyCallJwtWithJwks(jwks_doc_bytes, jwt, now_unix_secs) {
    const ptr0 = passArray8ToWasm0(jwks_doc_bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(jwt, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.verifyCallJwtWithJwks(ptr0, len0, ptr1, len1, now_unix_secs);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ParsedCallJwt.__wrap(ret[0]);
}

/**
 * Verify a peer announcement against `group` (the call's MLS group).
 * `relayFrom` is the `from` the SFU stamped on the relay frame.
 *
 * # Errors
 *
 * Any refusal (`wrongCall`, `wrongPeer`, `notAMember`, `noAccount`,
 * `badSignature`, a decode code, `callIdLength`): drop the announcement.
 * @param {Group} group
 * @param {Uint8Array} call_id
 * @param {string} relay_from
 * @param {Uint8Array} announcement
 * @returns {VerifiedCallPeer}
 */
export function verifyCallPeerAnnouncement(group, call_id, relay_from, announcement) {
    _assertClass(group, Group);
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(relay_from, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(announcement, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.verifyCallPeerAnnouncement(group.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VerifiedCallPeer.__wrap(ret[0]);
}

/**
 * Verify a screen-share state. `peerId` and `peerSignerKey` are what the
 * caller has bound to the sender: in a group call the relay `from` and the
 * signature key of that peer's verified announcement; in a one-to-one call
 * `callScreenShareOneToOnePeerId()` and the MLS sender's leaf key.
 *
 * # Errors
 *
 * Any refusal (`wrongCall`, `wrongPeer`, `notAMember`, `noAccount`,
 * `badSignature`, a decode code, `callIdLength`): drop the state.
 * @param {Group} group
 * @param {Uint8Array} call_id
 * @param {string} peer_id
 * @param {Uint8Array} peer_signer_key
 * @param {Uint8Array} state
 * @returns {VerifiedCallScreenShare}
 */
export function verifyCallScreenShare(group, call_id, peer_id, peer_signer_key, state) {
    _assertClass(group, Group);
    const ptr0 = passArray8ToWasm0(call_id, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(peer_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(peer_signer_key, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(state, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.verifyCallScreenShare(group.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VerifiedCallScreenShare.__wrap(ret[0]);
}

/**
 * Verify a conversation link against the key the caller left pinned for the
 * account and return the 32-byte conversation key it names, which the caller
 * records as a proven predecessor of that pin.
 *
 * Throws if `trustedCurrentKey` is not 32 bytes, or the link is malformed,
 * names another current key, or either signature does not verify. A caller
 * ignores a refused link: it changes nothing.
 * @param {Uint8Array} link
 * @param {Uint8Array} trusted_current_key
 * @returns {Uint8Array}
 */
export function verifyConversationLink(link, trusted_current_key) {
    const ptr0 = passArray8ToWasm0(link, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(trusted_current_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.verifyConversationLink(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Verify an auth-attested genesis attestation's full account / usernameHash /
 * pubkey binding against the CLIENT-PINNED auth attest verifying key (ADR-0036,
 * closes the G-30 finding-2 squatting residual). A pass proves the party that
 * OPAQUE-authenticated as `expectedAccountId` presented `expectedPubkey` as its
 * genesis identity key (bridged to the KT-log leaf by `expectedUsernameHash`) —
 * so only the account holder could have minted it.
 *
 * This is the CLIENT first-observation path: it checks the signature plus all
 * four bindings but deliberately does NOT enforce freshness (the attestation is
 * a permanent historical artifact). Verify beside the ADR-0019 self-signature.
 *
 * Throws if `attestVerifyingKey` is not a valid Ed25519 key, `expectedAccountId`
 * is not 16 bytes, `expectedUsernameHash` / `expectedPubkey` are not 32 bytes,
 * the attestation is malformed / the wrong length or version, the signature does
 * not verify, or any of the account / usernameHash / pubkey bindings mismatch.
 * @param {Uint8Array} attestation
 * @param {Uint8Array} attest_verifying_key
 * @param {Uint8Array} expected_account_id
 * @param {Uint8Array} expected_username_hash
 * @param {Uint8Array} expected_pubkey
 */
export function verifyGenesisAttestation(attestation, attest_verifying_key, expected_account_id, expected_username_hash, expected_pubkey) {
    const ptr0 = passArray8ToWasm0(attestation, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(attest_verifying_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(expected_account_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(expected_username_hash, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray8ToWasm0(expected_pubkey, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    const ret = wasm.verifyGenesisAttestation(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

/**
 * Verify a handle certificate under the pinned discovery key and its
 * binding to `expectedLookupKey` (the lookup key of the OPRF output over
 * the SHOWN name).
 *
 * # Errors
 *
 * Throws `"<code>: ..."` with `badLength` / `badVersion` / `badSignature`
 * / `nameMismatch`, or `badVerifyingKey` / `lookupKeyLength`.
 * @param {Uint8Array} certificate
 * @param {Uint8Array} verifying_key
 * @param {Uint8Array} expected_lookup_key
 * @returns {HandleCertificate}
 */
export function verifyHandleBinding(certificate, verifying_key, expected_lookup_key) {
    const ptr0 = passArray8ToWasm0(certificate, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(verifying_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(expected_lookup_key, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.verifyHandleBinding(ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return HandleCertificate.__wrap(ret[0]);
}

/**
 * Verify a key-reset attestation's signature and its binding to
 * `expectedAccountId` (through the derived `account_ref`) and to
 * `expectedPubkey`, with **no** freshness check (ADR-0073
 * section 2.2). Returns the authenticated generation and mint time.
 *
 * `attestation` is the raw 145-byte blob (`key_reset_attest_b64` decoded);
 * `attestVerifyingKey` the 32-byte client-pinned auth attest key.
 *
 * Throws an `invalid: ...` `JsError` on a bad verifying key, a wrong-length
 * account id or pubkey, a malformed / wrong-length / wrong-version blob, a
 * signature that does not verify, or a binding mismatch. Never `stale:`.
 * @param {Uint8Array} attestation
 * @param {Uint8Array} attest_verifying_key
 * @param {Uint8Array} expected_account_id
 * @param {Uint8Array} expected_pubkey
 * @returns {VerifiedKeyResetAttestation}
 */
export function verifyKeyResetAttestation(attestation, attest_verifying_key, expected_account_id, expected_pubkey) {
    const ptr0 = passArray8ToWasm0(attestation, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(attest_verifying_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(expected_account_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(expected_pubkey, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.verifyKeyResetAttestation(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VerifiedKeyResetAttestation.__wrap(ret[0]);
}

/**
 * `verifyKeyResetAttestation` plus a freshness bound: `signed_at` must lie
 * within `maxAgeMillis` of `nowMillis` (both BigInt, Unix milliseconds), and
 * no further in the future than a five-minute skew. The entry point for a
 * client applying its own reset from persisted `publish-owed` state
 * (ADR-0073 section 4.2, 30 days chosen there).
 *
 * Throws `stale: ...` when the attestation verified but is outside the
 * window, otherwise as `verifyKeyResetAttestation`.
 * @param {Uint8Array} attestation
 * @param {Uint8Array} attest_verifying_key
 * @param {Uint8Array} expected_account_id
 * @param {Uint8Array} expected_pubkey
 * @param {bigint} now_millis
 * @param {bigint} max_age_millis
 * @returns {VerifiedKeyResetAttestation}
 */
export function verifyKeyResetAttestationFresh(attestation, attest_verifying_key, expected_account_id, expected_pubkey, now_millis, max_age_millis) {
    const ptr0 = passArray8ToWasm0(attestation, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(attest_verifying_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(expected_account_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(expected_pubkey, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.verifyKeyResetAttestationFresh(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, now_millis, max_age_millis);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VerifiedKeyResetAttestation.__wrap(ret[0]);
}

/**
 * The three checks a kept device makes before it stores an envelope as
 * pending (ADR-0074 section 3.3), in one call so no client re-derives them:
 *
 * 1. the supersession proof is signed by `heldAccountPubkey` (the account
 *    identity key THIS device holds), has a validity window the format
 *    allows, and has not expired at `nowUnixSecs` (a `bigint`);
 * 2. the account identity derived from the entropy (the phrase, its 64-byte
 *    seed, `Identity.fromSeed`) has exactly the key the proof authorises;
 * 3. the new key is not the held one.
 *
 * On success the device stores the result as PENDING, keyed on
 * `newSignaturePubkey`, and changes nothing. It adopts when it sees the
 * server-side commit: the published bundle carries that key, or its own
 * refresh is answered `key-generation-superseded`.
 *
 * # Errors
 *
 * Throws the envelope's own codes, `bad-key-length`, `proof-refused`,
 * `entropy-mismatch`, `same-key`.
 * @param {Uint8Array} plaintext
 * @param {Uint8Array} held_account_pubkey
 * @param {bigint} now_unix_secs
 * @returns {VerifiedKeyRotation}
 */
export function verifyKeyRotationEnvelope(plaintext, held_account_pubkey, now_unix_secs) {
    const ptr0 = passArray8ToWasm0(plaintext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(held_account_pubkey, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.verifyKeyRotationEnvelope(ptr0, len0, ptr1, len1, now_unix_secs);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VerifiedKeyRotation.__wrap(ret[0]);
}

/**
 * Verify a genesis self-signature against the embedded `identityPubkey`
 * (G-30 #2, ADR-0019). A pass proves the holder of `identityPubkey` authorised
 * this `(usernameHash, pubkey)` account-to-key binding.
 *
 * Throws if `usernameHash` is not 32 bytes, the embedded `identityPubkey` is
 * not a valid Ed25519 key, `sig` is not 64 bytes, or the signature does not
 * verify.
 * @param {Uint8Array} sig
 * @param {Uint8Array} username_hash
 * @param {Uint8Array} identity_pubkey
 */
export function verifyLeafGenesis(sig, username_hash, identity_pubkey) {
    const ptr0 = passArray8ToWasm0(sig, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(username_hash, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(identity_pubkey, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.verifyLeafGenesis(ptr0, len0, ptr1, len1, ptr2, len2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

/**
 * `true` when `wire` is a refusal of THIS account's request
 * (`ownAccountKey`) through the link `expectedLinkId`, signed by
 * `inviterKey` (the account key the invite link named). Anything else,
 * inputs of the wrong length included, is `false`.
 * @param {Uint8Array} wire
 * @param {Uint8Array} inviter_key
 * @param {Uint8Array} expected_link_id
 * @param {Uint8Array} own_account_key
 * @returns {boolean}
 */
export function verifyLinkRefusal(wire, inviter_key, expected_link_id, own_account_key) {
    const ptr0 = passArray8ToWasm0(wire, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(inviter_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(expected_link_id, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(own_account_key, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.verifyLinkRefusal(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    return ret !== 0;
}

/**
 * Verify that `entryIdentityPubkey` (at `entryVersion`) for the
 * account-key `entryUsernameHash` is committed by the operator-signed
 * transparency log (KT-log S3b). The decomposed fields mirror the JSON
 * a client decodes from `/transparency/{root,latest,proof}`.
 *
 * Runs two gates and returns successfully only if BOTH pass:
 *
 * 1. `signedRoot.verify(operator)` — the root is signed by the
 *    client-pinned operator key (a forged root fails here).
 * 2. `verify_inclusion(entry, proof, signedRoot)` — this exact
 *    `(account_key, pubkey, version)` leaf is at `leafIndex` of that
 *    root's tree (a substituted identity / tampered proof fails here).
 *
 * The no-rollback (append-only) check is the CALLER's job: compare the
 * fetched `logSize` against the last-seen monotone value before
 * trusting the result. This function is stateless and does not persist
 * or compare any last-seen size.
 *
 * # Errors
 *
 * Throws a `JsError` if:
 * - any fixed-length field is the wrong length (`treeRoot`/`entryUsernameHash`
 *   != 32, `rootSignature` != 64, `auditPath` length not a multiple of 32),
 * - `operatorPubkey` is not 1..=64 bytes or not a valid Ed25519 key,
 * - the root signature does not verify under `operatorPubkey`,
 * - the inclusion proof does not bind the entry to the root.
 * @param {Uint8Array} operator_pubkey
 * @param {Uint8Array} tree_root
 * @param {bigint} log_size
 * @param {bigint} timestamp_unix
 * @param {Uint8Array} root_signature
 * @param {bigint} leaf_index
 * @param {Uint8Array} audit_path
 * @param {Uint8Array} entry_username_hash
 * @param {Uint8Array} entry_identity_pubkey
 * @param {bigint} entry_version
 */
export function verifyLogInclusion(operator_pubkey, tree_root, log_size, timestamp_unix, root_signature, leaf_index, audit_path, entry_username_hash, entry_identity_pubkey, entry_version) {
    const ptr0 = passArray8ToWasm0(operator_pubkey, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(tree_root, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(root_signature, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(audit_path, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray8ToWasm0(entry_username_hash, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    const ptr5 = passArray8ToWasm0(entry_identity_pubkey, wasm.__wbindgen_malloc);
    const len5 = WASM_VECTOR_LEN;
    const ret = wasm.verifyLogInclusion(ptr0, len0, ptr1, len1, log_size, timestamp_unix, ptr2, len2, leaf_index, ptr3, len3, ptr4, len4, ptr5, len5, entry_version);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

/**
 * Verify a 97-byte binding against the recipient's 32-byte account key as
 * the writer holds it pinned, and return the 32-byte mailbox key it names.
 * A writer runs this before it seals or writes anything; on a throw it
 * writes nothing.
 *
 * # Errors
 *
 * Throws with `mailboxError` `badLength` / `unknownVersion`, `badBinding`,
 * `badKey`, `keyLength`.
 * @param {Uint8Array} binding
 * @param {Uint8Array} account_pk
 * @returns {Uint8Array}
 */
export function verifyMailboxBinding(binding, account_pk) {
    const ptr0 = passArray8ToWasm0(binding, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(account_pk, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.verifyMailboxBinding(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Parse a release manifest's wire bytes and verify the Ed25519 signature against
 * `verifyingKey` (the client-pinned publisher release key) AND the
 * freshness window — the full check the web client applies before rendering an
 * update notice (ADR-0038). Returns the authenticated [`VerifiedReleaseManifest`].
 *
 * `manifest` is the raw fetched manifest bytes. `verifyingKey` is the 32-byte
 * pinned publisher release verifying key. `nowMillis` and `maxStalenessMillis`
 * are Unix milliseconds (BigInt on the JS side); pass
 * `maxStalenessMillis === (2n ** 63n - 1n)` (`i64::MAX`) to disable the freshness
 * gate entirely (a stale manifest can only *suppress* an update notice, never
 * forge one — a LOW-threat residual, ADR-0038).
 *
 * Throws a `release:`-coded `JsError` on ANY failure — a malformed / wrong-length
 * / wrong-version manifest, a signature that does not verify, a stale manifest,
 * or a bad `verifyingKey`. There is no discriminable stale-vs-invalid split
 * (ADR-0038): the caller catches the throw and treats the app as up-to-date, so
 * an unverified manifest never drives an update nag.
 * @param {Uint8Array} manifest
 * @param {Uint8Array} verifying_key
 * @param {bigint} now_millis
 * @param {bigint} max_staleness_millis
 * @returns {VerifiedReleaseManifest}
 */
export function verifyReleaseManifest(manifest, verifying_key, now_millis, max_staleness_millis) {
    const ptr0 = passArray8ToWasm0(manifest, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(verifying_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.verifyReleaseManifest(ptr0, len0, ptr1, len1, now_millis, max_staleness_millis);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return VerifiedReleaseManifest.__wrap(ret[0]);
}

/**
 * Verify an identity supersession proof (G-4) against the old key the caller
 * already pins for this account. Returns the authorised NEW public key
 * (32 bytes) the caller should re-pin on success.
 *
 * Throws if `trustedOldPubkey` is not 32 bytes, or the proof is malformed /
 * not signed by the trusted old key / carries an out-of-range (>30-day)
 * window / has expired relative to `nowUnixSecs`.
 * @param {Uint8Array} proof_bytes
 * @param {Uint8Array} trusted_old_pubkey
 * @param {bigint} now_unix_secs
 * @returns {Uint8Array}
 */
export function verifySupersession(proof_bytes, trusted_old_pubkey, now_unix_secs) {
    const ptr0 = passArray8ToWasm0(proof_bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(trusted_old_pubkey, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.verifySupersession(ptr0, len0, ptr1, len1, now_unix_secs);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}

/**
 * Verify a complete identity-rotation chain against the KT log (G-30 #2,
 * ADR-0019). Unlike `verifySupersession` — which checks only the bundle's
 * standalone proof against the local pin — this anchors the rotation to the
 * operator-signed, append-only log: a rotation is bound to a SINGLE committed
 * version under a no-rollback root, which makes a leaked-old-key equivocation
 * DETECTABLE (two distinct successors cannot both be log-confirmed at one
 * version; the second peer to fetch sees a version-floor or inclusion failure).
 *
 * The decomposed fields mirror the JSON a client gathers from
 * `/transparency/{root,latest,proof}` plus the `supersession_proof` carried in
 * the peer's identity bundle and what the peer currently pins. On success the
 * caller re-pins to the returned NEW public key (32 bytes) at `entryVersion`.
 *
 * `lastSeenLogSize` is the client's persisted monotone high-water mark; the
 * chain's no-rollback check reads only the size, so the stale root's other
 * fields are reconstructed as zero (never inspected).
 *
 * # Errors
 *
 * Throws a `JsError` if any fixed-length field is the wrong length, the
 * operator key is not a valid Ed25519 key, or any chain gate fails (forged
 * root, rollback, inclusion mismatch, wrong account, non-newer version, or a
 * supersession proof that is absent / not signed by the pinned old key /
 * out-of-window / expired / not authorising exactly the entry's new key).
 * @param {Uint8Array} operator_pubkey
 * @param {Uint8Array} new_tree_root
 * @param {bigint} new_log_size
 * @param {bigint} new_timestamp_unix
 * @param {Uint8Array} new_root_signature
 * @param {Uint8Array} entry_username_hash
 * @param {Uint8Array} entry_identity_pubkey
 * @param {bigint} entry_version
 * @param {bigint} inclusion_leaf_index
 * @param {Uint8Array} inclusion_audit_path
 * @param {Uint8Array} supersession_proof
 * @param {Uint8Array} pinned_old_pubkey
 * @param {bigint} pinned_version
 * @param {bigint} last_seen_log_size
 * @param {bigint} now_unix_secs
 * @returns {Uint8Array}
 */
export function verifySupersessionChain(operator_pubkey, new_tree_root, new_log_size, new_timestamp_unix, new_root_signature, entry_username_hash, entry_identity_pubkey, entry_version, inclusion_leaf_index, inclusion_audit_path, supersession_proof, pinned_old_pubkey, pinned_version, last_seen_log_size, now_unix_secs) {
    const ptr0 = passArray8ToWasm0(operator_pubkey, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(new_tree_root, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(new_root_signature, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(entry_username_hash, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray8ToWasm0(entry_identity_pubkey, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    const ptr5 = passArray8ToWasm0(inclusion_audit_path, wasm.__wbindgen_malloc);
    const len5 = WASM_VECTOR_LEN;
    const ptr6 = passArray8ToWasm0(supersession_proof, wasm.__wbindgen_malloc);
    const len6 = WASM_VECTOR_LEN;
    const ptr7 = passArray8ToWasm0(pinned_old_pubkey, wasm.__wbindgen_malloc);
    const len7 = WASM_VECTOR_LEN;
    const ret = wasm.verifySupersessionChain(ptr0, len0, ptr1, len1, new_log_size, new_timestamp_unix, ptr2, len2, ptr3, len3, ptr4, len4, entry_version, inclusion_leaf_index, ptr5, len5, ptr6, len6, ptr7, len7, pinned_version, last_seen_log_size, now_unix_secs);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v9 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v9;
}

/**
 * Verify a claimed username: the certificate's signature under the pinned
 * discovery key and its binding to `expectedOprfHash` (the `lookupKey` of
 * the OPRF round over the CLAIMED username, never its `output`) and
 * `expectedAccountId`. No freshness check. Show
 * the username only on success.
 *
 * # Errors
 *
 * Throws with `usernameCertificateError` (`badLength`, `badVersion`,
 * `badSignature`, `usernameMismatch`, `accountMismatch`,
 * `badVerifyingKey`, `oprfHashLength`, `accountIdLength`).
 * @param {Uint8Array} certificate
 * @param {Uint8Array} verifying_key
 * @param {Uint8Array} expected_oprf_hash
 * @param {Uint8Array} expected_account_id
 * @returns {UsernameCertificate}
 */
export function verifyUsernameBinding(certificate, verifying_key, expected_oprf_hash, expected_account_id) {
    const ptr0 = passArray8ToWasm0(certificate, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(verifying_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(expected_oprf_hash, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray8ToWasm0(expected_account_id, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ret = wasm.verifyUsernameBinding(ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return UsernameCertificate.__wrap(ret[0]);
}

/**
 * Verify a witness co-signature (ADR-0020) over a transparency root's
 * `(treeRoot, logSize)` against the CLIENT-PINNED `witnessKey` — the defence
 * against an operator split-view (G-30 #1). A client requires this AND the
 * operator's own root signature before trusting a root. `cosig` is the
 * self-describing wire blob served alongside the root.
 *
 * Throws if `expectedTreeRoot` is not 32 bytes, the co-sig is the wrong length
 * / version, its embedded root differs from `expectedTreeRoot` /
 * `expectedLogSize`, or the signature does not verify under `witnessKey`.
 * @param {Uint8Array} cosig
 * @param {Uint8Array} witness_key
 * @param {Uint8Array} expected_tree_root
 * @param {bigint} expected_log_size
 */
export function verifyWitnessCosig(cosig, witness_key, expected_tree_root, expected_log_size) {
    const ptr0 = passArray8ToWasm0(cosig, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(witness_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(expected_tree_root, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.verifyWitnessCosig(ptr0, len0, ptr1, len1, ptr2, len2, expected_log_size);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

/**
 * Verify a K-of-N witness QUORUM (ADR-0039, G-30 finding-1) over a transparency
 * root's `(treeRoot, logSize)`: at least `threshold` DISTINCT client-pinned
 * witnesses must each have co-signed the SAME root. The K-of-N generalisation of
 * `verifyWitnessCosig` — the client pins `N` witness keys and requires `K` of
 * them, so an operator can no longer fork the log by colluding with a single
 * witness (it must corrupt `K`).
 *
 * The two lists cross the boundary FLAT-CONCATENATED (the role-table FFI
 * convention): `cosigsFlat` is `N` co-signature blobs each exactly
 * `WITNESS_COSIG_LEN` (105) bytes, `pinnedKeysFlat` is `M` witness verifying keys
 * each 32 bytes; both are sliced into fixed-length chunks on the Rust side. The
 * quorum counts DISTINCT pinned keys, never co-signatures, so a replayed
 * co-signature cannot inflate the tally (`cosigsFlat` is attacker-controlled).
 *
 * Throws if `expectedTreeRoot` is not 32 bytes, `cosigsFlat` is not a multiple of
 * 105 bytes, `pinnedKeysFlat` is not a multiple of 32 bytes, `threshold` is `0`
 * (rejected fail-closed — invariant #4), or fewer than `threshold` distinct
 * pinned keys produced a valid co-signature over the expected root.
 * @param {Uint8Array} cosigs_flat
 * @param {Uint8Array} pinned_keys_flat
 * @param {number} threshold
 * @param {Uint8Array} expected_tree_root
 * @param {bigint} expected_log_size
 */
export function verifyWitnessQuorum(cosigs_flat, pinned_keys_flat, threshold, expected_tree_root, expected_log_size) {
    const ptr0 = passArray8ToWasm0(cosigs_flat, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(pinned_keys_flat, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(expected_tree_root, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.verifyWitnessQuorum(ptr0, len0, ptr1, len1, threshold, ptr2, len2, expected_log_size);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

/**
 * `true` when a setting stamped `incomingUpdatedAtMs` must replace the
 * stored one. Strictly greater wins.
 * @param {bigint} incoming_updated_at_ms
 * @param {bigint} stored_updated_at_ms
 * @returns {boolean}
 */
export function visibilityIsNewer(incoming_updated_at_ms, stored_updated_at_ms) {
    const ret = wasm.visibilityIsNewer(incoming_updated_at_ms, stored_updated_at_ms);
    return ret !== 0;
}

/**
 * Whether a VP8 frame is a key frame by its own frame tag (the low bit of
 * the first byte is 0). `undefined` for an empty frame. Only the first byte
 * is read, so a caller may pass a one-byte view.
 * @param {Uint8Array} frame
 * @returns {boolean | undefined}
 */
export function vp8IsKeyFrame(frame) {
    const ptr0 = passArray8ToWasm0(frame, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.vp8IsKeyFrame(ptr0, len0);
    return ret === 0xFFFFFF ? undefined : ret !== 0;
}

/**
 * Decode a group-journal entry as a rendezvous envelope and, if it is
 * addressed to `mySignatureKey`, return the inner Welcome (ADR-0027 Phase 3).
 * Returns `undefined` when the entry is not a rendezvous, is malformed, or is
 * addressed to someone else — so the joiner can run it over **every** journal
 * entry without throwing on the non-matching ones.
 * @param {Uint8Array} bytes
 * @param {Uint8Array} my_signature_key
 * @returns {Uint8Array | undefined}
 */
export function welcomeRendezvousFor(bytes, my_signature_key) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(my_signature_key, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.welcomeRendezvousFor(ptr0, len0, ptr1, len1);
    let v3;
    if (ret[0] !== 0) {
        v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
        wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    }
    return v3;
}

/**
 * Wrap a fresh MLS KeyPackage in the call-KP-forward
 * envelope's domain separator (Phase 3c3-kp-forward).
 *
 * The subsequent joiner (3rd, 4th, … participant in a
 * multi-party link call) hands the output to
 * `ContactRequest.compose` as the intro-message argument;
 * the existing HPKE-seal + stranger-inbox transport carries
 * the wrapped KP to the call group's current committer, who
 * runs an MLS Add proposal + commit and ferries a Welcome
 * back via `wrapCallWelcome`.
 *
 * See [`lakko_contacts::call_kp_forward`] for the
 * threat-model rationale (reuse the contact-invite envelope
 * confidentiality + authenticity + anti-abuse contracts;
 * sibling of `wrapCallWelcome`).
 * @param {Uint8Array} kp_bytes
 * @returns {Uint8Array}
 */
export function wrapCallKeyPackage(kp_bytes) {
    const ptr0 = passArray8ToWasm0(kp_bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.wrapCallKeyPackage(ptr0, len0);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Wrap MLS Welcome bytes in the call-welcome envelope's
 * domain separator (Phase 3c3-welcome-ferry). The joiner
 * hands the output to `ContactRequest.compose` as the intro-
 * message argument; the existing HPKE-seal + stranger-inbox
 * transport carries the wrapped bytes to the owner.
 *
 * See [`lakko_contacts::call_welcome`] for the threat-model
 * rationale (reuse the contact-invite envelope's
 * confidentiality + authenticity + anti-abuse contracts
 * rather than spin up a fresh `/discovery/call-welcome`
 * endpoint).
 * @param {Uint8Array} welcome_bytes
 * @returns {Uint8Array}
 */
export function wrapCallWelcome(welcome_bytes) {
    const ptr0 = passArray8ToWasm0(welcome_bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.wrapCallWelcome(ptr0, len0);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Encode an SFrame wire frame: `header(kid=sender_leaf,
 * ctr=counter) || ciphertext`. Mirror of the UniFFI
 * helper at `crates/lakko-ffi-uniffi/src/sframe.rs`. `counter` is 64-bit
 * (a `bigint` in JS).
 * @param {number} sender_leaf
 * @param {bigint} counter
 * @param {Uint8Array} ciphertext
 * @returns {Uint8Array}
 */
export function wrapSframeFrame(sender_leaf, counter, ciphertext) {
    const ptr0 = passArray8ToWasm0(ciphertext, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.wrapSframeFrame(sender_leaf, counter, ptr0, len0);
    var v2 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v2;
}

/**
 * Wrap an added member's Welcome into a rendezvous envelope the sweeping
 * device posts to the group journal (ADR-0027 Phase 3, G-3).
 * `joinerSignatureKey` is the added member's signature key; `welcome` is the
 * `StagedSweep.welcomeBytes` the ADR-0045 sweep produced (or, on a
 * `PublisherGroup`, the `AddMember.welcomeBytes` from its direct
 * `approveExternalJoin`).
 *
 * **A sweep cannot tell which of its adds came from a `/g/` link** — that is
 * what makes an approval indistinguishable from an ordinary add, and it is
 * deliberate — so the caller wraps one envelope per added key, origin-blind.
 * @param {Uint8Array} joiner_signature_key
 * @param {Uint8Array} welcome
 * @returns {Uint8Array}
 */
export function wrapWelcomeRendezvous(joiner_signature_key, welcome) {
    const ptr0 = passArray8ToWasm0(joiner_signature_key, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(welcome, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.wrapWelcomeRendezvous(ptr0, len0, ptr1, len1);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v3 = getArrayU8FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 1, 1);
    return v3;
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg_BigInt_ae200e93cacbd2b3: function(arg0) {
            const ret = BigInt(arg0);
            return ret;
        },
        __wbg_Error_3639a60ed15f87e7: function(arg0, arg1) {
            const ret = Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg___wbindgen_bigint_get_as_i64_3af6d4ca77193a4b: function(arg0, arg1) {
            const v = arg1;
            const ret = typeof(v) === 'bigint' ? v : undefined;
            getDataViewMemory0().setBigInt64(arg0 + 8 * 1, isLikeNone(ret) ? BigInt(0) : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_debug_string_07cb72cfcc952e2b: function(arg0, arg1) {
            const ret = debugString(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_is_bigint_d6a8167cac401b95: function(arg0) {
            const ret = typeof(arg0) === 'bigint';
            return ret;
        },
        __wbg___wbindgen_is_function_2f0fd7ceb86e64c5: function(arg0) {
            const ret = typeof(arg0) === 'function';
            return ret;
        },
        __wbg___wbindgen_is_object_5b22ff2418063a9c: function(arg0) {
            const val = arg0;
            const ret = typeof(val) === 'object' && val !== null;
            return ret;
        },
        __wbg___wbindgen_is_string_eddc07a3efad52e6: function(arg0) {
            const ret = typeof(arg0) === 'string';
            return ret;
        },
        __wbg___wbindgen_is_undefined_244a92c34d3b6ec0: function(arg0) {
            const ret = arg0 === undefined;
            return ret;
        },
        __wbg___wbindgen_jsval_eq_403eaa3610500a25: function(arg0, arg1) {
            const ret = arg0 === arg1;
            return ret;
        },
        __wbg___wbindgen_lt_c483cc694de67c3e: function(arg0, arg1) {
            const ret = arg0 < arg1;
            return ret;
        },
        __wbg___wbindgen_neg_9b4d71823e3bc513: function(arg0) {
            const ret = -arg0;
            return ret;
        },
        __wbg___wbindgen_number_get_dd6d69a6079f26f1: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'number' ? obj : undefined;
            getDataViewMemory0().setFloat64(arg0 + 8 * 1, isLikeNone(ret) ? 0 : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_string_get_965592073e5d848c: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'string' ? obj : undefined;
            var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            var len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_throw_9c75d47bf9e7731e: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbg_anontoken_new: function(arg0) {
            const ret = AnonToken.__wrap(arg0);
            return ret;
        },
        __wbg_backupcontact_new: function(arg0) {
            const ret = BackupContact.__wrap(arg0);
            return ret;
        },
        __wbg_backupcontact_unwrap: function(arg0) {
            const ret = BackupContact.__unwrap(arg0);
            return ret;
        },
        __wbg_backupreceivedprofile_new: function(arg0) {
            const ret = BackupReceivedProfile.__wrap(arg0);
            return ret;
        },
        __wbg_backupreceivedprofile_unwrap: function(arg0) {
            const ret = BackupReceivedProfile.__unwrap(arg0);
            return ret;
        },
        __wbg_beaconobservation_unwrap: function(arg0) {
            const ret = BeaconObservation.__unwrap(arg0);
            return ret;
        },
        __wbg_call_a41d6421b30a32c5: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = arg0.call(arg1, arg2);
            return ret;
        }, arguments); },
        __wbg_chipsource_new: function(arg0) {
            const ret = ChipSource.__wrap(arg0);
            return ret;
        },
        __wbg_chipsource_unwrap: function(arg0) {
            const ret = ChipSource.__unwrap(arg0);
            return ret;
        },
        __wbg_conversationendregister_new: function(arg0) {
            const ret = ConversationEndRegister.__wrap(arg0);
            return ret;
        },
        __wbg_conversationrow_unwrap: function(arg0) {
            const ret = ConversationRow.__unwrap(arg0);
            return ret;
        },
        __wbg_crypto_38df2bab126b63dc: function(arg0) {
            const ret = arg0.crypto;
            return ret;
        },
        __wbg_delegation_new: function(arg0) {
            const ret = Delegation.__wrap(arg0);
            return ret;
        },
        __wbg_draftregister_new: function(arg0) {
            const ret = DraftRegister.__wrap(arg0);
            return ret;
        },
        __wbg_entries_bb9843ba73dc70d6: function(arg0) {
            const ret = Object.entries(arg0);
            return ret;
        },
        __wbg_feedcandidate_unwrap: function(arg0) {
            const ret = FeedCandidate.__unwrap(arg0);
            return ret;
        },
        __wbg_feedconversation_unwrap: function(arg0) {
            const ret = FeedConversation.__unwrap(arg0);
            return ret;
        },
        __wbg_feedentry_new: function(arg0) {
            const ret = FeedEntry.__wrap(arg0);
            return ret;
        },
        __wbg_flagsregister_new: function(arg0) {
            const ret = FlagsRegister.__wrap(arg0);
            return ret;
        },
        __wbg_folderconversationref_new: function(arg0) {
            const ret = FolderConversationRef.__wrap(arg0);
            return ret;
        },
        __wbg_folderview_new: function(arg0) {
            const ret = FolderView.__wrap(arg0);
            return ret;
        },
        __wbg_from_ff141b1e4c69b979: function(arg0) {
            const ret = Array.from(arg0);
            return ret;
        },
        __wbg_getRandomValues_c44a50d8cfdaebeb: function() { return handleError(function (arg0, arg1) {
            arg0.getRandomValues(arg1);
        }, arguments); },
        __wbg_get_41476db20fef99a8: function() { return handleError(function (arg0, arg1) {
            const ret = Reflect.get(arg0, arg1);
            return ret;
        }, arguments); },
        __wbg_get_652f640b3b0b6e3e: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_get_unchecked_be562b1421656321: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_groupmember_new: function(arg0) {
            const ret = GroupMember.__wrap(arg0);
            return ret;
        },
        __wbg_heldreaction_new: function(arg0) {
            const ret = HeldReaction.__wrap(arg0);
            return ret;
        },
        __wbg_heldreaction_unwrap: function(arg0) {
            const ret = HeldReaction.__unwrap(arg0);
            return ret;
        },
        __wbg_historyattachmentinput_unwrap: function(arg0) {
            const ret = HistoryAttachmentInput.__unwrap(arg0);
            return ret;
        },
        __wbg_historygroupinput_unwrap: function(arg0) {
            const ret = HistoryGroupInput.__unwrap(arg0);
            return ret;
        },
        __wbg_historymessageinput_unwrap: function(arg0) {
            const ret = HistoryMessageInput.__unwrap(arg0);
            return ret;
        },
        __wbg_instanceof_Uint8Array_57d77acd50e4c44d: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Uint8Array;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_languageinfo_new: function(arg0) {
            const ret = LanguageInfo.__wrap(arg0);
            return ret;
        },
        __wbg_length_0a6ce016dc1460b0: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_length_ba3c032602efe310: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_lineageregister_new: function(arg0) {
            const ret = LineageRegister.__wrap(arg0);
            return ret;
        },
        __wbg_linkrecord_new: function(arg0) {
            const ret = LinkRecord.__wrap(arg0);
            return ret;
        },
        __wbg_localmessage_new: function(arg0) {
            const ret = LocalMessage.__wrap(arg0);
            return ret;
        },
        __wbg_messagetextpiece_new: function(arg0) {
            const ret = MessageTextPiece.__wrap(arg0);
            return ret;
        },
        __wbg_msCrypto_bd5a034af96bcba6: function(arg0) {
            const ret = arg0.msCrypto;
            return ret;
        },
        __wbg_new_2fad8ca02fd00684: function() {
            const ret = new Object();
            return ret;
        },
        __wbg_new_3baa8d9866155c79: function() {
            const ret = new Array();
            return ret;
        },
        __wbg_new_c9ea13ea803a692e: function(arg0, arg1) {
            const ret = new Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_new_from_slice_5a173c243af2e823: function(arg0, arg1) {
            const ret = new Uint8Array(getArrayU8FromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_new_with_length_9011f5da794bf5d9: function(arg0) {
            const ret = new Uint8Array(arg0 >>> 0);
            return ret;
        },
        __wbg_new_with_length_95e51bab415f3ca8: function(arg0) {
            const ret = new Array(arg0 >>> 0);
            return ret;
        },
        __wbg_node_84ea875411254db1: function(arg0) {
            const ret = arg0.node;
            return ret;
        },
        __wbg_now_4f457f10f864aec5: function() {
            const ret = Date.now();
            return ret;
        },
        __wbg_openedhistoryattachment_new: function(arg0) {
            const ret = OpenedHistoryAttachment.__wrap(arg0);
            return ret;
        },
        __wbg_openedhistorygroup_new: function(arg0) {
            const ret = OpenedHistoryGroup.__wrap(arg0);
            return ret;
        },
        __wbg_openedhistorymessage_new: function(arg0) {
            const ret = OpenedHistoryMessage.__wrap(arg0);
            return ret;
        },
        __wbg_process_44c7a14e11e9f69e: function(arg0) {
            const ret = arg0.process;
            return ret;
        },
        __wbg_proposalrecord_new: function(arg0) {
            const ret = ProposalRecord.__wrap(arg0);
            return ret;
        },
        __wbg_prototypesetcall_fd4050e806e1d519: function(arg0, arg1, arg2) {
            Uint8Array.prototype.set.call(getArrayU8FromWasm0(arg0, arg1), arg2);
        },
        __wbg_push_60a5366c0bb22a7d: function(arg0, arg1) {
            const ret = arg0.push(arg1);
            return ret;
        },
        __wbg_randomFillSync_6c25eac9869eb53c: function() { return handleError(function (arg0, arg1) {
            arg0.randomFillSync(arg1);
        }, arguments); },
        __wbg_reachgrant_new: function(arg0) {
            const ret = ReachGrant.__wrap(arg0);
            return ret;
        },
        __wbg_reachgrant_unwrap: function(arg0) {
            const ret = ReachGrant.__unwrap(arg0);
            return ret;
        },
        __wbg_reachstateentry_new: function(arg0) {
            const ret = ReachStateEntry.__wrap(arg0);
            return ret;
        },
        __wbg_reachstateentry_unwrap: function(arg0) {
            const ret = ReachStateEntry.__unwrap(arg0);
            return ret;
        },
        __wbg_reactionchip_new: function(arg0) {
            const ret = ReactionChip.__wrap(arg0);
            return ret;
        },
        __wbg_reactionop_unwrap: function(arg0) {
            const ret = ReactionOp.__unwrap(arg0);
            return ret;
        },
        __wbg_readcursorregister_new: function(arg0) {
            const ret = ReadCursorRegister.__wrap(arg0);
            return ret;
        },
        __wbg_require_b4edbdcf3e2a1ef0: function() { return handleError(function () {
            const ret = module.require;
            return ret;
        }, arguments); },
        __wbg_savedmessageview_new: function(arg0) {
            const ret = SavedMessageView.__wrap(arg0);
            return ret;
        },
        __wbg_scopeid_new: function(arg0) {
            const ret = ScopeId.__wrap(arg0);
            return ret;
        },
        __wbg_scopeid_unwrap: function(arg0) {
            const ret = ScopeId.__unwrap(arg0);
            return ret;
        },
        __wbg_set_5337f8ac82364a3f: function() { return handleError(function (arg0, arg1, arg2) {
            const ret = Reflect.set(arg0, arg1, arg2);
            return ret;
        }, arguments); },
        __wbg_set_f614f6a0608d1d1d: function(arg0, arg1, arg2) {
            arg0[arg1 >>> 0] = arg2;
        },
        __wbg_static_accessor_GLOBAL_THIS_1c7f1bd6c6941fdb: function() {
            const ret = typeof globalThis === 'undefined' ? null : globalThis;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_static_accessor_GLOBAL_e039bc914f83e74e: function() {
            const ret = typeof global === 'undefined' ? null : global;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_static_accessor_SELF_8bf8c48c28420ad5: function() {
            const ret = typeof self === 'undefined' ? null : self;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_static_accessor_WINDOW_6aeee9b51652ee0f: function() {
            const ret = typeof window === 'undefined' ? null : window;
            return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
        },
        __wbg_subarray_fbe3cef290e1fa43: function(arg0, arg1, arg2) {
            const ret = arg0.subarray(arg1 >>> 0, arg2 >>> 0);
            return ret;
        },
        __wbg_syncedsnapshotpart_new: function(arg0) {
            const ret = SyncedSnapshotPart.__wrap(arg0);
            return ret;
        },
        __wbg_toString_15656af8d8e71f16: function(arg0, arg1, arg2) {
            const ret = arg1.toString(arg2);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg_vaultattachment_new: function(arg0) {
            const ret = VaultAttachment.__wrap(arg0);
            return ret;
        },
        __wbg_vaultattachment_unwrap: function(arg0) {
            const ret = VaultAttachment.__unwrap(arg0);
            return ret;
        },
        __wbg_vaultchunkentry_new: function(arg0) {
            const ret = VaultChunkEntry.__wrap(arg0);
            return ret;
        },
        __wbg_vaultchunkentry_unwrap: function(arg0) {
            const ret = VaultChunkEntry.__unwrap(arg0);
            return ret;
        },
        __wbg_vaultindexentry_new: function(arg0) {
            const ret = VaultIndexEntry.__wrap(arg0);
            return ret;
        },
        __wbg_vaultindexentry_unwrap: function(arg0) {
            const ret = VaultIndexEntry.__unwrap(arg0);
            return ret;
        },
        __wbg_vaultmessage_new: function(arg0) {
            const ret = VaultMessage.__wrap(arg0);
            return ret;
        },
        __wbg_vaultmessage_unwrap: function(arg0) {
            const ret = VaultMessage.__unwrap(arg0);
            return ret;
        },
        __wbg_vaultperson_new: function(arg0) {
            const ret = VaultPerson.__wrap(arg0);
            return ret;
        },
        __wbg_vaultperson_unwrap: function(arg0) {
            const ret = VaultPerson.__unwrap(arg0);
            return ret;
        },
        __wbg_vaultpersonalpin_new: function(arg0) {
            const ret = VaultPersonalPin.__wrap(arg0);
            return ret;
        },
        __wbg_vaultpersonalpin_unwrap: function(arg0) {
            const ret = VaultPersonalPin.__unwrap(arg0);
            return ret;
        },
        __wbg_vaultreaction_new: function(arg0) {
            const ret = VaultReaction.__wrap(arg0);
            return ret;
        },
        __wbg_vaultreentryepoch_new: function(arg0) {
            const ret = VaultReentryEpoch.__wrap(arg0);
            return ret;
        },
        __wbg_vaultreentryepoch_unwrap: function(arg0) {
            const ret = VaultReentryEpoch.__unwrap(arg0);
            return ret;
        },
        __wbg_vaultsharedpin_new: function(arg0) {
            const ret = VaultSharedPin.__wrap(arg0);
            return ret;
        },
        __wbg_vaultsharedpin_unwrap: function(arg0) {
            const ret = VaultSharedPin.__unwrap(arg0);
            return ret;
        },
        __wbg_versions_276b2795b1c6a219: function(arg0) {
            const ret = arg0.versions;
            return ret;
        },
        __wbindgen_cast_0000000000000001: function(arg0) {
            // Cast intrinsic for `F64 -> Externref`.
            const ret = arg0;
            return ret;
        },
        __wbindgen_cast_0000000000000002: function(arg0, arg1) {
            // Cast intrinsic for `Ref(Slice(U8)) -> NamedExternref("Uint8Array")`.
            const ret = getArrayU8FromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_cast_0000000000000003: function(arg0, arg1) {
            // Cast intrinsic for `Ref(String) -> Externref`.
            const ret = getStringFromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_cast_0000000000000004: function(arg0) {
            // Cast intrinsic for `U64 -> Externref`.
            const ret = BigInt.asUintN(64, arg0);
            return ret;
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./lakko_ffi_wasm_bg.js": import0,
    };
}

const AcceptedCallInviteFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_acceptedcallinvite_free(ptr, 1));
const ActingCommitterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_actingcommitter_free(ptr, 1));
const AddMemberFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_addmember_free(ptr, 1));
const AnonTokenFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_anontoken_free(ptr, 1));
const AnonTokenRequestFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_anontokenrequest_free(ptr, 1));
const AttachmentRefFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_attachmentref_free(ptr, 1));
const AvAlignFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_avalign_free(ptr, 1));
const AvatarRefFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_avatarref_free(ptr, 1));
const BackupContactFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_backupcontact_free(ptr, 1));
const BackupReceivedProfileFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_backupreceivedprofile_free(ptr, 1));
const BeaconObservationFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_beaconobservation_free(ptr, 1));
const CallCurrentScreenShareFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_callcurrentscreenshare_free(ptr, 1));
const CallGridFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_callgrid_free(ptr, 1));
const CallInviteCapabilityFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_callinvitecapability_free(ptr, 1));
const CallQualitySamplerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_callqualitysampler_free(ptr, 1));
const CallQualityScoreFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_callqualityscore_free(ptr, 1));
const CallRelayKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_callrelaykey_free(ptr, 1));
const CallRelayPayloadFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_callrelaypayload_free(ptr, 1));
const CallScreenShareStateFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_callscreensharestate_free(ptr, 1));
const ChannelCoPublisherBindingFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelcopublisherbinding_free(ptr, 1));
const ChannelCoPublisherSignerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelcopublishersigner_free(ptr, 1));
const ChannelCommentWriteKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelcommentwritekey_free(ptr, 1));
const ChannelInfoCapabilitiesFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelinfocapabilities_free(ptr, 1));
const ChannelInviteSignerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelinvitesigner_free(ptr, 1));
const ChannelListingRecordFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channellistingrecord_free(ptr, 1));
const ChannelOwnerSignerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelownersigner_free(ptr, 1));
const ChannelReactionKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelreactionkey_free(ptr, 1));
const ChannelReactionWriteKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelreactionwritekey_free(ptr, 1));
const ChipRowFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_chiprow_free(ptr, 1));
const ChipSourceFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_chipsource_free(ptr, 1));
const CircleEpochKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_circleepochkey_free(ptr, 1));
const CircleFilterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_circlefilter_free(ptr, 1));
const CircleGrantFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_circlegrant_free(ptr, 1));
const CircleGrantInfoFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_circlegrantinfo_free(ptr, 1));
const CircleHeadFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_circlehead_free(ptr, 1));
const CircleWriteFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_circlewrite_free(ptr, 1));
const CircleWriterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_circlewriter_free(ptr, 1));
const CommitterDelegationEnvelopeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_committerdelegationenvelope_free(ptr, 1));
const ContactAckFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_contactack_free(ptr, 1));
const ContactRequestFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_contactrequest_free(ptr, 1));
const ConversationEndRegisterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_conversationendregister_free(ptr, 1));
const ConversationOrderRegisterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_conversationorderregister_free(ptr, 1));
const ConversationRowFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_conversationrow_free(ptr, 1));
const DecodedPairingQrFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_decodedpairingqr_free(ptr, 1));
const DeviceGroupFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_devicegroup_free(ptr, 1));
const DeviceLinkAcceptedFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_devicelinkaccepted_free(ptr, 1));
const DeviceLinkInitiatorFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_devicelinkinitiator_free(ptr, 1));
const DeviceLinkResponderFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_devicelinkresponder_free(ptr, 1));
const DeviceLinkSealedFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_devicelinksealed_free(ptr, 1));
const DraftFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_draft_free(ptr, 1));
const DraftRegisterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_draftregister_free(ptr, 1));
const DsProofSignerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_dsproofsigner_free(ptr, 1));
const DsStatementRequestFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_dsstatementrequest_free(ptr, 1));
const DsStatementSignerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_dsstatementsigner_free(ptr, 1));
const EditChainFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_editchain_free(ptr, 1));
const EditOpFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_editop_free(ptr, 1));
const EditResolvedFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_editresolved_free(ptr, 1));
const EncryptedAttachmentFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_encryptedattachment_free(ptr, 1));
const EncryptedStoreFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_encryptedstore_free(ptr, 1));
const EnvelopeBuilderFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_envelopebuilder_free(ptr, 1));
const FeedCandidateFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_feedcandidate_free(ptr, 1));
const FeedConversationFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_feedconversation_free(ptr, 1));
const FeedEntryFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_feedentry_free(ptr, 1));
const FeedRuleFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_feedrule_free(ptr, 1));
const FlagsRegisterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_flagsregister_free(ptr, 1));
const FolderChangeSetFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_folderchangeset_free(ptr, 1));
const FolderConversationRefFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_folderconversationref_free(ptr, 1));
const FolderRuleFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_folderrule_free(ptr, 1));
const FolderViewFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_folderview_free(ptr, 1));
const GroupFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_group_free(ptr, 1));
const GroupInfoCapabilitiesFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_groupinfocapabilities_free(ptr, 1));
const GroupInviteCapabilityFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_groupinvitecapability_free(ptr, 1));
const GroupJoinAttributionFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_groupjoinattribution_free(ptr, 1));
const GroupMemberFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_groupmember_free(ptr, 1));
const GroupStateStoreFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_groupstatestore_free(ptr, 1));
const HeldReactionFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_heldreaction_free(ptr, 1));
const HistoryAttachmentInputFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_historyattachmentinput_free(ptr, 1));
const HistoryGroupInputFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_historygroupinput_free(ptr, 1));
const HistoryMessageInputFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_historymessageinput_free(ptr, 1));
const HlcFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_hlc_free(ptr, 1));
const HybridLogicalClockFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_hybridlogicalclock_free(ptr, 1));
const IdentityFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_identity_free(ptr, 1));
const IdentityPseudonymFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_identitypseudonym_free(ptr, 1));
const InMemoryStoreFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_inmemorystore_free(ptr, 1));
const IntroductionPayloadFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_introductionpayload_free(ptr, 1));
const IntroductionsBundleFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_introductionsbundle_free(ptr, 1));
const InviteCapabilityFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_invitecapability_free(ptr, 1));
const JoinRequestFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_joinrequest_free(ptr, 1));
const L10nCatalogFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_l10ncatalog_free(ptr, 1));
const L10nSourceFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_l10nsource_free(ptr, 1));
const LanguageInfoFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_languageinfo_free(ptr, 1));
const LayerDisplayFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_layerdisplay_free(ptr, 1));
const LineageRegisterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_lineageregister_free(ptr, 1));
const LinkClaimDecisionFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_linkclaimdecision_free(ptr, 1));
const LinkRecordFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_linkrecord_free(ptr, 1));
const LocalMessageFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_localmessage_free(ptr, 1));
const MailboxKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_mailboxkey_free(ptr, 1));
const MessageStoreFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_messagestore_free(ptr, 1));
const MessageTextPieceFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_messagetextpiece_free(ptr, 1));
const OpaqueLoginFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_opaquelogin_free(ptr, 1));
const OpaqueLoginOutputFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_opaqueloginoutput_free(ptr, 1));
const OpaqueRegisterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_opaqueregister_free(ptr, 1));
const OpaqueRegisterOutputFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_opaqueregisteroutput_free(ptr, 1));
const OpenedContactRequestFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedcontactrequest_free(ptr, 1));
const OpenedHistoryAttachmentFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedhistoryattachment_free(ptr, 1));
const OpenedHistoryBundleFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedhistorybundle_free(ptr, 1));
const OpenedHistoryGroupFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedhistorygroup_free(ptr, 1));
const OpenedHistoryMessageFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedhistorymessage_free(ptr, 1));
const OpenedHistoryPlaceholderFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedhistoryplaceholder_free(ptr, 1));
const OpenedHistoryThumbFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedhistorythumb_free(ptr, 1));
const OpenedMailboxRowFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedmailboxrow_free(ptr, 1));
const OpenedMemberCardFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedmembercard_free(ptr, 1));
const OprfBlinderFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_oprfblinder_free(ptr, 1));
const OprfResultFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_oprfresult_free(ptr, 1));
const OwnUsernameRegisterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_ownusernameregister_free(ptr, 1));
const ParsedBackupBlobFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_parsedbackupblob_free(ptr, 1));
const ParsedBackupPayloadFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_parsedbackuppayload_free(ptr, 1));
const ParsedBodyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_parsedbody_free(ptr, 1));
const ParsedCallJwtFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_parsedcalljwt_free(ptr, 1));
const ParsedCallSignalingFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_parsedcallsignaling_free(ptr, 1));
const ParsedDeviceGroupPayloadFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_parseddevicegrouppayload_free(ptr, 1));
const ParsedEnvelopeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_parsedenvelope_free(ptr, 1));
const PerRelationshipFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_perrelationship_free(ptr, 1));
const PersonInfoCapabilitiesFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_personinfocapabilities_free(ptr, 1));
const PowProofFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_powproof_free(ptr, 1));
const PowSolverFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_powsolver_free(ptr, 1));
const PresenceTokenFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_presencetoken_free(ptr, 1));
const PresenceTrackerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_presencetracker_free(ptr, 1));
const ProcessedMessageFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_processedmessage_free(ptr, 1));
const ProfileAvatarFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_profileavatar_free(ptr, 1));
const ProposalCommitFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_proposalcommit_free(ptr, 1));
const ProposalRecordFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_proposalrecord_free(ptr, 1));
const PublisherGroupFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_publishergroup_free(ptr, 1));
const PushPayloadFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_pushpayload_free(ptr, 1));
const PushTokenListFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_pushtokenlist_free(ptr, 1));
const ReachGrantFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_reachgrant_free(ptr, 1));
const ReachStateFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_reachstate_free(ptr, 1));
const ReachStateEntryFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_reachstateentry_free(ptr, 1));
const ReachStateMergeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_reachstatemerge_free(ptr, 1));
const ReactionChipFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_reactionchip_free(ptr, 1));
const ReactionOpFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_reactionop_free(ptr, 1));
const ReadCursorRegisterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_readcursorregister_free(ptr, 1));
const RecoveryEmailRegisterFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_recoveryemailregister_free(ptr, 1));
const RecoveryMnemonicFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_recoverymnemonic_free(ptr, 1));
const RegistrarSeedEnvelopeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_registrarseedenvelope_free(ptr, 1));
const SavedMessageViewFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_savedmessageview_free(ptr, 1));
const ScopeIdFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_scopeid_free(ptr, 1));
const SframeParticipantKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_sframeparticipantkey_free(ptr, 1));
const SpeakingTrackerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_speakingtracker_free(ptr, 1));
const StagedSweepFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_stagedsweep_free(ptr, 1));
const StrangerInboxFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_strangerinbox_free(ptr, 1));
const SyncedSnapshotPartFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_syncedsnapshotpart_free(ptr, 1));
const SyncedStateFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_syncedstate_free(ptr, 1));
const ThumbRefFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_thumbref_free(ptr, 1));
const UsernameCertificateFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_usernamecertificate_free(ptr, 1));
const VaultAttachmentFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultattachment_free(ptr, 1));
const VaultChunkEntryFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultchunkentry_free(ptr, 1));
const VaultConversationHeadFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultconversationhead_free(ptr, 1));
const VaultIndexEntryFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultindexentry_free(ptr, 1));
const VaultKeysFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultkeys_free(ptr, 1));
const VaultMessageFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultmessage_free(ptr, 1));
const VaultPersonFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultperson_free(ptr, 1));
const VaultPersonalPinFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultpersonalpin_free(ptr, 1));
const VaultReactionFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultreaction_free(ptr, 1));
const VaultReentryEpochFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultreentryepoch_free(ptr, 1));
const VaultRefFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultref_free(ptr, 1));
const VaultSharedPinFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultsharedpin_free(ptr, 1));
const VaultThumbFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_vaultthumb_free(ptr, 1));
const VerifiedBackupHeadFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_verifiedbackuphead_free(ptr, 1));
const VerifiedCallPeerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_verifiedcallpeer_free(ptr, 1));
const VerifiedCallScreenShareFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_verifiedcallscreenshare_free(ptr, 1));
const VerifiedKeyResetAttestationFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_verifiedkeyresetattestation_free(ptr, 1));
const VerifiedReleaseManifestFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_verifiedreleasemanifest_free(ptr, 1));
const VisibilitySettingFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_visibilitysetting_free(ptr, 1));
const AdmissionSeedEnvelopeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_admissionseedenvelope_free(ptr, 1));
const AllowedReactionsFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_allowedreactions_free(ptr, 1));
const BanListFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_banlist_free(ptr, 1));
const CallKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_callkey_free(ptr, 1));
const CallKeyRingFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_callkeyring_free(ptr, 1));
const ChannelAccessFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelaccess_free(ptr, 1));
const ChannelPostFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelpost_free(ptr, 1));
const ChannelPostContentFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelpostcontent_free(ptr, 1));
const ChannelSigningKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelsigningkey_free(ptr, 1));
const ChannelSubscriberKeypairFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_channelsubscriberkeypair_free(ptr, 1));
const ConversationHandleFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_conversationhandle_free(ptr, 1));
const DelegationFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_delegation_free(ptr, 1));
const DeviceCardFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_devicecard_free(ptr, 1));
const GroupClosedFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_groupclosed_free(ptr, 1));
const GroupInvitePreviewFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_groupinvitepreview_free(ptr, 1));
const GroupNameFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_groupname_free(ptr, 1));
const GroupPhotoFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_groupphoto_free(ptr, 1));
const GroupReactionsFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_groupreactions_free(ptr, 1));
const HandleCertificateFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_handlecertificate_free(ptr, 1));
const HandleRecordPayloadFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_handlerecordpayload_free(ptr, 1));
const IncomingEnvelopeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_incomingenvelope_free(ptr, 1));
const InviteLinkMintFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_invitelinkmint_free(ptr, 1));
const JournalEpochsFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_journalepochs_free(ptr, 1));
const KeyRotationFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_keyrotation_free(ptr, 1));
const KnockRequestFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_knockrequest_free(ptr, 1));
const KnockSeedEnvelopeFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_knockseedenvelope_free(ptr, 1));
const LinkClaimFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_linkclaim_free(ptr, 1));
const LinkFoundedFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_linkfounded_free(ptr, 1));
const MediaFrameInfoFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_mediaframeinfo_free(ptr, 1));
const OpenedChannelInviteCardFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedchannelinvitecard_free(ptr, 1));
const OpenedCommentFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_openedcomment_free(ptr, 1));
const ParticipantKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_participantkey_free(ptr, 1));
const PinOpFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_pinop_free(ptr, 1));
const ProfileFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_profile_free(ptr, 1));
const PushRoutingSecretFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_pushroutingsecret_free(ptr, 1));
const SealedFrameFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_sealedframe_free(ptr, 1));
const SearchRecordFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_searchrecord_free(ptr, 1));
const SharedChannelsFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_sharedchannels_free(ptr, 1));
const SubscriberInviteFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_subscriberinvite_free(ptr, 1));
const UnwrappedFrameFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_unwrappedframe_free(ptr, 1));
const VerifiedKeyRotationFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_verifiedkeyrotation_free(ptr, 1));

function addToExternrefTable0(obj) {
    const idx = wasm.__externref_table_alloc();
    wasm.__wbindgen_externrefs.set(idx, obj);
    return idx;
}

function _assertClass(instance, klass) {
    if (!(instance instanceof klass)) {
        throw new Error(`expected instance of ${klass.name}`);
    }
}

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function getArrayF64FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getFloat64ArrayMemory0().subarray(ptr / 8, ptr / 8 + len);
}

function getArrayJsValueFromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    const mem = getDataViewMemory0();
    const result = [];
    for (let i = ptr; i < ptr + 4 * len; i += 4) {
        result.push(wasm.__wbindgen_externrefs.get(mem.getUint32(i, true)));
    }
    wasm.__externref_drop_slice(ptr, len);
    return result;
}

function getArrayU32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU64FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getBigUint64ArrayMemory0().subarray(ptr / 8, ptr / 8 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedBigUint64ArrayMemory0 = null;
function getBigUint64ArrayMemory0() {
    if (cachedBigUint64ArrayMemory0 === null || cachedBigUint64ArrayMemory0.byteLength === 0) {
        cachedBigUint64ArrayMemory0 = new BigUint64Array(wasm.memory.buffer);
    }
    return cachedBigUint64ArrayMemory0;
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

let cachedFloat64ArrayMemory0 = null;
function getFloat64ArrayMemory0() {
    if (cachedFloat64ArrayMemory0 === null || cachedFloat64ArrayMemory0.byteLength === 0) {
        cachedFloat64ArrayMemory0 = new Float64Array(wasm.memory.buffer);
    }
    return cachedFloat64ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint16ArrayMemory0 = null;
function getUint16ArrayMemory0() {
    if (cachedUint16ArrayMemory0 === null || cachedUint16ArrayMemory0.byteLength === 0) {
        cachedUint16ArrayMemory0 = new Uint16Array(wasm.memory.buffer);
    }
    return cachedUint16ArrayMemory0;
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function handleError(f, args) {
    try {
        return f.apply(this, args);
    } catch (e) {
        const idx = addToExternrefTable0(e);
        wasm.__wbindgen_exn_store(idx);
    }
}

function isLikeNone(x) {
    return x === undefined || x === null;
}

function passArray16ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 2, 2) >>> 0;
    getUint16ArrayMemory0().set(arg, ptr / 2);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArray32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getUint32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArray64ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 8, 8) >>> 0;
    getBigUint64ArrayMemory0().set(arg, ptr / 8);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayF64ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 8, 8) >>> 0;
    getFloat64ArrayMemory0().set(arg, ptr / 8);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArrayJsValueToWasm0(array, malloc) {
    const ptr = malloc(array.length * 4, 4) >>> 0;
    for (let i = 0; i < array.length; i++) {
        const add = addToExternrefTable0(array[i]);
        getDataViewMemory0().setUint32(ptr + 4 * i, add, true);
    }
    WASM_VECTOR_LEN = array.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedBigUint64ArrayMemory0 = null;
    cachedDataViewMemory0 = null;
    cachedFloat64ArrayMemory0 = null;
    cachedUint16ArrayMemory0 = null;
    cachedUint32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = module.ok && expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('lakko_ffi_wasm_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
